/* ===================================================================
   CONFIGURATOR FLOORPLAN LIB
   assets/configurator-floorplan-lib.js

   Renderer + drawer controller for the floorplan library.

   Pulls floorplan definitions from window.FloorplanData and:
     - Mounts a side-drawer with filter UI + thumbnail grid
     - Renders each floorplan as inline SVG (no PNGs -- the SVG IS
       the source of truth, used for thumbnail, 2D overlay, and as
       a feed for the 3D extrude pass)
     - Applies the selected floorplan to the live configurator:
         - Updates BaseEngine STATE.polygon to match the footprint
         - Pushes the floorplan onto a stack for undo
         - Adds an opacity-controlled SVG overlay above the 2D floor
     - Exposes a stable API on window.FloorplanLib

   Coordinate convention (mirrors configurator-floorplan-data.js):
     - Origin (0, 0) = top-left, x -> right, y -> down, units in meters
     - SVG viewBox is built as "0 0 (lengthx100) (depthx100)"
       so 1 SVG unit = 1 cm -- easy to read & precise enough.

   The drawer borrows Shopify Dawn's <side-panel> conventions so it
   matches the existing asset drawer's open/close transitions and
   backdrop behaviour. CSS lives in configurator-floorplan-drawer.css
   and only handles the inner layout.
   =================================================================== */

(function (global) {
  'use strict';

  /* the version is assigned at the end of the file, with the last zone, so a
     delivery from the zone banner down carries its own version */
  var VERSION;
  var DRAWER_ID  = 'Configurator-Floorplan-Drawer';
  var OVERLAY_ID = 'cf-floorplan-2d-overlay';

  /* ---------------------------------------------------------------
     STATE
     --------------------------------------------------------------- */
  var _initialized      = false;
  var _selectedHandle   = null;   /* current floorplan applied */
  var _previousPolygon  = null;   /* snapshot for undo          */
  var _opacity          = 0.5;    /* overlay opacity (0..1)     */
  var _filters          = { bedrooms: null, storeys: null, category: null };
  var _locale           = 'en';   /* set by init()              */

  /* Default i18n strings -- overridable via init(opts.labels). */
  var L = {
    drawer_title:        'Floorplan library',
    drawer_subtitle:     'Pick a layout and the foundation will adapt to fit.',
    filter_bedrooms:     'Bedrooms',
    filter_storeys:      'Storeys',
    filter_category:     'Type',
    filter_any:          'Any',
    category_house:      'House',
    category_tiny:       'Tiny',
    category_garden:     'Garden',
    category_garage:     'Garage',
    no_results:          'No floorplans match your filters.',
    apply:               'Apply',
    applied:             'Applied',
    undo:                'Undo',
    floorplan_opacity:   'Floorplan opacity',
    summary_template:    '{bedrooms} BR - {storeys}F - {length}x{depth} m - {area} m2'
  };

  /* ---------------------------------------------------------------
     UTILITIES
     --------------------------------------------------------------- */
  function _log() {
    if (global.Configurator && global.Configurator.log) {
      var args = ['[floorplan-lib]'].concat(Array.prototype.slice.call(arguments));
      global.Configurator.log.log.apply(global.Configurator.log, args);
    }
  }

  function _fmtNum(n, decimals) {
    if (typeof n !== 'number') return '';
    var d = (decimals == null) ? 1 : decimals;
    return n.toFixed(d).replace(/\.0+$/, '');
  }

  function _summaryLine(fp) {
    return L.summary_template
      .replace('{bedrooms}', fp.bedrooms)
      .replace('{storeys}',  fp.storeys)
      .replace('{length}',   _fmtNum(fp.footprint_m.length))
      .replace('{depth}',    _fmtNum(fp.footprint_m.depth))
      .replace('{area}',     _fmtNum(fp.area_m2, 0));
  }

  function _localized(fp, room, field) {
    var k = field + '_' + _locale;
    if (room[k]) return room[k];
    if (room[field + '_en']) return room[field + '_en'];
    return '';
  }

  function _palette() {
    return (global.FloorplanData && global.FloorplanData.palette) || {};
  }

  function _colorFor(roomType) {
    var p = _palette();
    return p[roomType] || p.other || '#7f8c8d';
  }

  /* ---------------------------------------------------------------
     SVG RENDERING

     One generator that draws the floorplan into a fresh SVG node.
     Three render modes change the styling subtly:

       'thumb'   -- drawer grid card (small, no labels except totals)
       'overlay' -- 2D configurator overlay (transparent fill, walls
                   visible, room labels + areas)
       'full'    -- large preview, used by 'Apply' confirmation if
                   we add one later

     Returns an SVG DOM element ready to insert. --------------------- */
  function _renderSvg(fp, mode) {
    var SVG_NS = 'http://www.w3.org/2000/svg';
    var svg    = document.createElementNS(SVG_NS, 'svg');
    var w      = fp.footprint_m.length * 100;  /* 1 m = 100 units */
    var h      = fp.footprint_m.depth  * 100;
    svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
    svg.setAttribute('xmlns', SVG_NS);
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.setAttribute('data-handle', fp.handle);
    svg.classList.add('cf-fp-svg');
    svg.classList.add('cf-fp-svg--' + (mode || 'thumb'));

    /* Layer order: room fills -> walls -> openings -> labels.
       Each as its own <g> so style + interactivity is per-layer. */
    var gRooms   = document.createElementNS(SVG_NS, 'g');
    var gWalls   = document.createElementNS(SVG_NS, 'g');
    var gLabels  = document.createElementNS(SVG_NS, 'g');
    gRooms.setAttribute('class',  'cf-fp-rooms');
    gWalls.setAttribute('class',  'cf-fp-walls');
    gLabels.setAttribute('class', 'cf-fp-labels');

    /* Room fills.  Each room is an axis-aligned rect with the
       palette colour at low opacity so walls + labels remain
       readable.  Stroke 0 here -- wall outlines are drawn as a
       separate layer once. */
    for (var i = 0; i < fp.rooms.length; i++) {
      var r = fp.rooms[i];
      if (r.w <= 0 || r.h <= 0) continue;
      var rect = document.createElementNS(SVG_NS, 'rect');
      rect.setAttribute('x',      r.x * 100);
      rect.setAttribute('y',      r.y * 100);
      rect.setAttribute('width',  r.w * 100);
      rect.setAttribute('height', r.h * 100);
      rect.setAttribute('fill',   _colorFor(r.type));
      rect.setAttribute('fill-opacity', (mode === 'thumb') ? '0.85' : '0.55');
      rect.setAttribute('stroke', 'none');
      rect.setAttribute('data-room-id',   r.id);
      rect.setAttribute('data-room-type', r.type);
      gRooms.appendChild(rect);
    }

    /* Exterior walls -- the footprint outline.  Thickness in real
       units, drawn as a stroke around the bounding rectangle. */
    var ext = fp.walls && fp.walls.exterior_thickness_m
            ? fp.walls.exterior_thickness_m * 100 : 30;
    var extRect = document.createElementNS(SVG_NS, 'rect');
    extRect.setAttribute('x', ext / 2);
    extRect.setAttribute('y', ext / 2);
    extRect.setAttribute('width',  w - ext);
    extRect.setAttribute('height', h - ext);
    extRect.setAttribute('fill', 'none');
    extRect.setAttribute('stroke', '#1A1C1D');
    extRect.setAttribute('stroke-width', ext);
    gWalls.appendChild(extRect);

    /* Interior partitions -- drawn between adjacent rooms.  We use
       the room rectangles as a coarse approximation: shared edges
       (within a small tolerance) get a partition line at the
       interior thickness.  Cheap but accurate enough for a
       rectangular layout like this. */
    var intT = fp.walls && fp.walls.interior_thickness_m
             ? fp.walls.interior_thickness_m * 100 : 15;
    var TOL = 2;  /* svg units (= 2 cm tolerance) */
    var seen = {};
    for (var a = 0; a < fp.rooms.length; a++) {
      for (var b = a + 1; b < fp.rooms.length; b++) {
        var rA = fp.rooms[a], rB = fp.rooms[b];
        if (rA.type === 'terrace' || rB.type === 'terrace') continue;
        var ax = rA.x * 100, ay = rA.y * 100, aw = rA.w * 100, ah = rA.h * 100;
        var bx = rB.x * 100, by = rB.y * 100, bw = rB.w * 100, bh = rB.h * 100;
        /* Vertical shared edge? */
        if (Math.abs((ax + aw) - bx) < TOL || Math.abs((bx + bw) - ax) < TOL) {
          var x = Math.abs((ax + aw) - bx) < TOL ? (ax + aw) : (bx + bw);
          var y0 = Math.max(ay, by);
          var y1 = Math.min(ay + ah, by + bh);
          if (y1 > y0) {
            var key = 'v:' + x + ':' + y0 + ':' + y1;
            if (!seen[key]) {
              seen[key] = 1;
              var ln = document.createElementNS(SVG_NS, 'line');
              ln.setAttribute('x1', x); ln.setAttribute('y1', y0);
              ln.setAttribute('x2', x); ln.setAttribute('y2', y1);
              ln.setAttribute('stroke', '#1A1C1D');
              ln.setAttribute('stroke-width', intT);
              ln.setAttribute('stroke-linecap', 'butt');
              gWalls.appendChild(ln);
            }
          }
        }
        /* Horizontal shared edge? */
        if (Math.abs((ay + ah) - by) < TOL || Math.abs((by + bh) - ay) < TOL) {
          var y2 = Math.abs((ay + ah) - by) < TOL ? (ay + ah) : (by + bh);
          var x0 = Math.max(ax, bx);
          var x1 = Math.min(ax + aw, bx + bw);
          if (x1 > x0) {
            var keyH = 'h:' + y2 + ':' + x0 + ':' + x1;
            if (!seen[keyH]) {
              seen[keyH] = 1;
              var lnH = document.createElementNS(SVG_NS, 'line');
              lnH.setAttribute('x1', x0); lnH.setAttribute('y1', y2);
              lnH.setAttribute('x2', x1); lnH.setAttribute('y2', y2);
              lnH.setAttribute('stroke', '#1A1C1D');
              lnH.setAttribute('stroke-width', intT);
              lnH.setAttribute('stroke-linecap', 'butt');
              gWalls.appendChild(lnH);
            }
          }
        }
      }
    }

    /* Room labels -- name on top, area on bottom.  Suppressed in
       thumb mode to keep the card tidy. */
    if (mode !== 'thumb') {
      for (var li = 0; li < fp.rooms.length; li++) {
        var rm = fp.rooms[li];
        if (rm.w * rm.h < 1.0) continue;  /* skip tiny rooms */
        var name = _localized(fp, rm, 'label');
        if (!name) continue;
        var cx = (rm.x + rm.w / 2) * 100;
        var cy = (rm.y + rm.h / 2) * 100;
        var tn = document.createElementNS(SVG_NS, 'text');
        tn.setAttribute('x', cx);
        tn.setAttribute('y', cy - 8);
        tn.setAttribute('text-anchor', 'middle');
        tn.setAttribute('class', 'cf-fp-room-name');
        tn.textContent = name;
        gLabels.appendChild(tn);
        var ta = document.createElementNS(SVG_NS, 'text');
        ta.setAttribute('x', cx);
        ta.setAttribute('y', cy + 18);
        ta.setAttribute('text-anchor', 'middle');
        ta.setAttribute('class', 'cf-fp-room-area');
        ta.textContent = _fmtNum(rm.w * rm.h, 1) + ' m2';
        gLabels.appendChild(ta);
      }
    }

    svg.appendChild(gRooms);
    svg.appendChild(gWalls);
    svg.appendChild(gLabels);
    return svg;
  }

  /* ---------------------------------------------------------------
     DRAWER DOM

     Built once on init().  We use Dawn's <side-panel> + .active
     toggle pattern that the asset drawer already uses, so opening
     / closing animations + backdrop are consistent across drawers.
     --------------------------------------------------------------- */
  function _buildDrawer() {
    if (document.getElementById(DRAWER_ID)) return;

    /* Mirror the asset-drawer DOM shape exactly: a plain <div> with
       class="side-panel" (NOT the <side-panel> custom element), so
       the Dawn theme CSS that handles slide-in animation + backdrop
       picks it up automatically. The .active class on this div +
       .open-cc on <body> are what Dawn watches for. */
    var wrap = document.createElement('div');
    wrap.id  = DRAWER_ID;
    wrap.className = 'side-panel configurator-floorplan-drawer';
    wrap.setAttribute('tabindex', '-1');
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-modal', 'true');
    wrap.setAttribute('aria-labelledby', 'cf-fp-drawer-title');
    wrap.setAttribute('aria-hidden', 'true');

    wrap.innerHTML =
      '<div class="side-panel-inner">' +
        '<div class="side-panel-header">' +
          '<div>' +
            '<h2 class="cf-fp-title" id="cf-fp-drawer-title">' + L.drawer_title + '</h2>' +
            '<p class="cf-fp-subtitle">' + L.drawer_subtitle + '</p>' +
            '<side-panel-close class="side-panel-close cf-fp-close" data-floorplan-drawer-close aria-label="Close">' +
              '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
                '<path d="M6 6l12 12M18 6L6 18"/>' +
              '</svg>' +
            '</side-panel-close>' +
          '</div>' +
        '</div>' +
        '<div class="side-panel-content">' +
          '<div class="cf-fp-body">' +
            '<div class="cf-fp-filters" id="cf-fp-filters"></div>' +
            '<div class="cf-fp-results" id="cf-fp-results"></div>' +
          '</div>' +
        '</div>' +
      '</div>';

    document.body.appendChild(wrap);

    /* Build filter UI and grid. */
    _buildFilters();
    _renderResults();

    /* In Dawn theme, <side-panel-close> handles its own click -> drawer
       close, but if Dawn isn't there we still want a working close
       button. Wire a delegate handler on the wrap. */
    wrap.addEventListener('click', function (e) {
      if (e.target.closest('[data-floorplan-drawer-close]')) {
        close();
      }
    });

    /* Mirror the asset-drawer mutation-observer pattern: listen
       for '.active' toggling on the side-panel and fire close
       cleanup once removed. */
    var mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].attributeName === 'class') {
          var wasOpen = wrap.classList.contains('active');
          if (!wasOpen) _onDrawerClosed();
        }
      }
    });
    mo.observe(wrap, { attributes: true });
  }

  function _buildFilters() {
    var host = document.getElementById('cf-fp-filters');
    if (!host) return;
    var fps = (global.FloorplanData && global.FloorplanData.floorplans) || [];

    /* Derive option sets from the data -- keeps the filter UI in
       sync if floorplans get added without touching this code. */
    var bedroomsSet = {};
    var storeysSet  = {};
    var categoriesSet = {};
    fps.forEach(function (fp) {
      bedroomsSet[fp.bedrooms] = true;
      storeysSet[fp.storeys]   = true;
      categoriesSet[fp.category] = true;
    });

    var bedroomsOpts   = Object.keys(bedroomsSet).map(Number).sort(function (a, b) { return a - b; });
    var storeysOpts    = Object.keys(storeysSet).map(Number).sort(function (a, b) { return a - b; });
    var categoriesOpts = Object.keys(categoriesSet).sort();

    function pillHtml(label, value, isActive, group) {
      return '<button type="button" class="cf-fp-pill' + (isActive ? ' is-active' : '') +
             '" data-filter-group="' + group + '" data-filter-value="' + (value == null ? '' : value) + '">' +
             label + '</button>';
    }

    var html = '';

    html += '<div class="cf-fp-filter-row"><span class="cf-fp-filter-label">' + L.filter_bedrooms + '</span><div class="cf-fp-pills">';
    html += pillHtml(L.filter_any, '', _filters.bedrooms == null, 'bedrooms');
    bedroomsOpts.forEach(function (n) {
      html += pillHtml(String(n), n, _filters.bedrooms === n, 'bedrooms');
    });
    html += '</div></div>';

    html += '<div class="cf-fp-filter-row"><span class="cf-fp-filter-label">' + L.filter_storeys + '</span><div class="cf-fp-pills">';
    html += pillHtml(L.filter_any, '', _filters.storeys == null, 'storeys');
    storeysOpts.forEach(function (n) {
      html += pillHtml(String(n), n, _filters.storeys === n, 'storeys');
    });
    html += '</div></div>';

    html += '<div class="cf-fp-filter-row"><span class="cf-fp-filter-label">' + L.filter_category + '</span><div class="cf-fp-pills">';
    html += pillHtml(L.filter_any, '', _filters.category == null, 'category');
    categoriesOpts.forEach(function (c) {
      var lab = L['category_' + c] || c;
      html += pillHtml(lab, c, _filters.category === c, 'category');
    });
    html += '</div></div>';

    host.innerHTML = html;

    host.addEventListener('click', function (e) {
      var pill = e.target.closest('.cf-fp-pill');
      if (!pill) return;
      var group = pill.getAttribute('data-filter-group');
      var raw   = pill.getAttribute('data-filter-value');
      var value;
      if (raw === '') {
        value = null;
      } else if (group === 'category') {
        value = raw;
      } else {
        value = parseInt(raw, 10);
        if (isNaN(value)) value = null;
      }
      _filters[group] = value;
      _buildFilters();   /* re-render to reflect active state */
      _renderResults();
    });
  }

  function _matches(fp) {
    if (_filters.bedrooms != null && fp.bedrooms !== _filters.bedrooms) return false;
    if (_filters.storeys  != null && fp.storeys  !== _filters.storeys)  return false;
    if (_filters.category != null && fp.category !== _filters.category) return false;
    return true;
  }

  function _renderResults() {
    var host = document.getElementById('cf-fp-results');
    if (!host) return;
    var fps = (global.FloorplanData && global.FloorplanData.floorplans) || [];
    var matched = fps.filter(_matches);
    if (matched.length === 0) {
      host.innerHTML = '<p class="cf-fp-empty">' + L.no_results + '</p>';
      return;
    }
    host.innerHTML = '';
    var grid = document.createElement('div');
    grid.className = 'cf-fp-grid';
    for (var i = 0; i < matched.length; i++) {
      grid.appendChild(_buildCard(matched[i]));
    }
    host.appendChild(grid);
  }

  function _buildCard(fp) {
    var card = document.createElement('button');
    card.type      = 'button';
    card.className = 'cf-fp-card' + (_selectedHandle === fp.handle ? ' is-selected' : '');
    card.setAttribute('data-handle', fp.handle);

    var thumb = document.createElement('div');
    thumb.className = 'cf-fp-card__thumb';
    thumb.appendChild(_renderSvg(fp, 'thumb'));
    card.appendChild(thumb);

    var meta = document.createElement('div');
    meta.className = 'cf-fp-card__meta';
    meta.innerHTML =
      '<div class="cf-fp-card__summary">' + _summaryLine(fp) + '</div>' +
      '<div class="cf-fp-card__action">' +
        (_selectedHandle === fp.handle
          ? ('<span class="cf-fp-applied-badge">ok ' + L.applied + '</span>')
          : ('<span class="cf-fp-apply-cta">' + L.apply + ' -></span>')) +
      '</div>';
    card.appendChild(meta);

    card.addEventListener('click', function () {
      _applyFloorplan(fp.handle);
    });
    return card;
  }

  /* ---------------------------------------------------------------
     APPLY / UNDO

     Hooks into BaseEngine + BaseRenderer to swap the foundation
     polygon and re-render the scene.  We snapshot the previous
     polygon so a single-level undo brings the user back to where
     they were before they clicked a floorplan. --------------------- */
  function _findFloorplan(handle) {
    var fps = (global.FloorplanData && global.FloorplanData.floorplans) || [];
    for (var i = 0; i < fps.length; i++) {
      if (fps[i].handle === handle) return fps[i];
    }
    return null;
  }

  function _engineState() {
    /* BaseEngine exposes STATE on itself but it's also reachable
       via a couple of getters depending on which configurator we
       are in. We try the most common path first. */
    var e = global.BaseEngine;
    return (e && e.STATE) ? e.STATE : null;
  }

  /* ---------------------------------------------------------------
     EDITABLE FLOORPLAN STATE

     Once a floorplan is applied we keep a deep-copied, EDITABLE
     version of its rooms + wall thicknesses. All drag operations
     mutate _floorplanState (not the source data in
     FloorplanData.floorplans, which stays pristine).

     The footprint polygon is DERIVED from the editable state on
     every change -- the user no longer drags foundation corners
     directly. Foundations follow the outermost room rectangles
     plus the exterior wall thickness.
     --------------------------------------------------------------- */
  var _floorplanState = null;   /* { handle, rooms[], walls, history[] } */
  var _editMode       = false;  /* true while a floorplan is active */
  var _editHandles    = [];     /* HTML pin elements tracking world points */
  var _handlesHost    = null;   /* container for the pin elements */
  var _xButton        = null;   /* "remove floorplan" red X button */
  var _dragActive     = false;  /* true while a drag gesture is in progress */

  function _seedEditableState(fp) {
    return {
      handle:  fp.handle,
      walls: {
        exterior_thickness_m: (fp.walls && fp.walls.exterior_thickness_m) || 0.30,
        interior_thickness_m: (fp.walls && fp.walls.interior_thickness_m) || 0.15
      },
      rooms:   fp.rooms.map(function (r) { return Object.assign({}, r); }),
      history: []
    };
  }

  function _pushFloorplanHistory() {
    if (!_floorplanState) return;
    var snap = {
      rooms: _floorplanState.rooms.map(function (r) { return Object.assign({}, r); })
    };
    _floorplanState.history.push(snap);
    /* Bound the stack so a wild drag session doesn't bloat memory. */
    if (_floorplanState.history.length > 50) _floorplanState.history.shift();
  }

  function _popFloorplanHistory() {
    if (!_floorplanState || !_floorplanState.history.length) return false;
    var snap = _floorplanState.history.pop();
    _floorplanState.rooms = snap.rooms.map(function (r) { return Object.assign({}, r); });
    return true;
  }

  /* Compute the footprint rectangle that wraps the editable rooms,
     adding HALF the exterior wall thickness on every side so the
     foundation finishes flush with the exterior wall midline. */
  function _computeFootprintFromRooms() {
    if (!_floorplanState || !_floorplanState.rooms.length) return null;
    var xMin =  Infinity, yMin =  Infinity;
    var xMax = -Infinity, yMax = -Infinity;
    var rooms = _floorplanState.rooms;
    for (var i = 0; i < rooms.length; i++) {
      var r = rooms[i];
      if (r.x < xMin) xMin = r.x;
      if (r.y < yMin) yMin = r.y;
      if (r.x + r.w > xMax) xMax = r.x + r.w;
      if (r.y + r.h > yMax) yMax = r.y + r.h;
    }
    var L = xMax - xMin;
    var D = yMax - yMin;
    var halfL = L / 2;
    var halfD = D / 2;
    /* Convert top-left origin -> centre origin to match the
       foundation polygon convention. */
    return {
      length_m: L,
      depth_m:  D,
      polygon: [
        [-halfL, -halfD],
        [ halfL, -halfD],
        [ halfL,  halfD],
        [-halfL,  halfD]
      ],
      offset_x: xMin,   /* shift to apply when drawing the overlay */
      offset_y: yMin
    };
  }

  function _applyFootprintFromEdit() {
    var eng = global.BaseEngine;
    if (!eng || typeof eng.setPolygon !== 'function') return;
    var fp = _computeFootprintFromRooms();
    if (!fp) return;
    eng.setPolygon(fp.polygon);
  }

  function _applyFloorplan(handle) {
    var fp = _findFloorplan(handle);
    if (!fp) return;
    var eng = global.BaseEngine;
    if (!eng || typeof eng.setPolygon !== 'function') {
      _log('cannot apply -- BaseEngine.setPolygon not available');
      return;
    }

    /* Snapshot the existing polygon so the X button (remove) can
       restore the configurator to its pre-floorplan state. */
    if (typeof eng.getPolygon === 'function') {
      _previousPolygon = eng.getPolygon();
    }

    _selectedHandle  = handle;
    _floorplanState  = _seedEditableState(fp);
    _editMode        = true;
    _log('applied floorplan handle=' + handle);

    _applyFootprintFromEdit();
    _enterEditMode();
    _refreshOverlay();
    _showOpacitySlider(true);
    _renderResults();
    _renderUndoControl();
  }

  function _removeFloorplan() {
    if (!_floorplanState) return;
    var eng = global.BaseEngine;
    _floorplanState = null;
    _selectedHandle = null;
    _editMode       = false;
    _exitEditMode();
    _refreshOverlay();
    _showOpacitySlider(false);
    if (eng && typeof eng.setPolygon === 'function' && _previousPolygon) {
      eng.setPolygon(_previousPolygon);
    }
    _previousPolygon = null;
    _renderResults();
    _renderUndoControl();
    _log('floorplan removed, restored previous polygon');
  }

  function _undo() {
    /* If we have floorplan edit history, pop one step instead of
       fully removing the floorplan. Only roll back to the
       pre-floorplan polygon if the floorplan is fresh (no edits
       yet). */
    if (_floorplanState && _floorplanState.history.length > 0) {
      _popFloorplanHistory();
      _applyFootprintFromEdit();
      _refreshOverlay();
      _log('undo: stepped back one floorplan edit');
      return;
    }
    if (!_previousPolygon) return;
    _removeFloorplan();
  }

  /* ---------------------------------------------------------------
     OVERLAY (Three.js floor overlay, 2D view only)

     We draw the floorplan directly into the Three.js scene as a
     dedicated group of meshes -- NOT as an HTML SVG.  Doing it in
     scene-space means the overlay perfectly aligns with the
     foundation polygon under all camera transforms (pan, zoom,
     map sync) without manual world-to-screen math.

     The group is attached to BaseEngine.scene (NOT sceneGroup),
     so BaseRenderer.build() -- which only wipes sceneGroup -- leaves
     it alone.  We toggle visibility per view: visible in 2D,
     hidden in 3D, until a later iteration adds extruded walls.

     Coordinates in the floorplan data are in meters, top-left
     origin (x -> right, y -> down).  Three.js scene-space uses
     x -> right, z -> forward (with y = up).  We map:
         scene.x =  fp.x - halfLength
         scene.z =  fp.y - halfDepth
     ...so the floorplan is centred on the origin, matching the
     polygon's coordinate system exactly.
     --------------------------------------------------------------- */

  var _overlayGroup = null;   /* THREE.Group inside scene */
  /* AN UPLOADED PLAN IS NEVER SQUARE TO THE MODEL. Scanned, photographed or
     exported, it comes in at whatever angle the paper was -- so the underlay
     has to turn. Opacity, scale and position were already here; this is the one
     that was missing, and without it a plan can be sized and placed correctly
     and still sit crooked under the drawing (Marek). */
  var _overlayYaw = 0;        /* radians, applied to the whole underlay */
  var _overlayY     = 0.02;   /* lift slightly above the slab to avoid z-fight */

  function _hexToColor(hex, T) {
    return new T.Color(hex);
  }

  function _renderSceneOverlay(fp) {
    var T   = global.THREE;
    var eng = global.BaseEngine;
    if (!T || !eng || !eng.scene) return;

    _clearSceneOverlay();

    /* Use the editable copy of the rooms if available, otherwise
       fall back to the source data -- keeps rendering working for
       any code path that calls this before _floorplanState is
       seeded. */
    var rooms = (_floorplanState && _floorplanState.rooms)
              ? _floorplanState.rooms
              : fp.rooms;
    var walls = (_floorplanState && _floorplanState.walls)
              ? _floorplanState.walls
              : fp.walls;

    /* Footprint dimensions derived from the editable rooms so the
       overlay tracks any wall drag immediately. */
    var fpd = _computeFootprintFromRooms() || {
      length_m: fp.footprint_m.length,
      depth_m:  fp.footprint_m.depth,
      offset_x: 0, offset_y: 0
    };
    var halfL = fpd.length_m / 2;
    var halfD = fpd.depth_m  / 2;
    var oxOff = fpd.offset_x;
    var oyOff = fpd.offset_y;

    var group = new T.Group();
    group.name = 'cf-floorplan-overlay';
    group.userData = { isFloorplanOverlay: true };

    var pal = _palette();

    /* Room floor tiles. */
    for (var i = 0; i < rooms.length; i++) {
      var r = rooms[i];
      if (r.w <= 0 || r.h <= 0) continue;
      var color = pal[r.type] || pal.other || '#7f8c8d';
      var mat = new T.MeshBasicMaterial({
        color:        _hexToColor(color, T),
        transparent:  true,
        opacity:      _opacity * 0.85,
        depthWrite:   false,
        side:         T.DoubleSide
      });
      var geo  = new T.PlaneGeometry(r.w, r.h);
      var mesh = new T.Mesh(geo, mat);
      mesh.rotation.x = -Math.PI / 2;
      var cx = (r.x + r.w / 2) - oxOff - halfL;
      var cz = (r.y + r.h / 2) - oyOff - halfD;
      mesh.position.set(cx, _overlayY, cz);
      mesh.userData = { roomId: r.id, roomType: r.type };
      group.add(mesh);
    }

    /* Walls -- exterior + interior partitions. */
    var extT = walls.exterior_thickness_m;
    var intT = walls.interior_thickness_m;
    var wallMat = new T.MeshBasicMaterial({
      color:       _hexToColor('#1A1C1D', T),
      transparent: true,
      opacity:     _opacity,
      depthWrite:  false,
      side:        T.DoubleSide
    });

    function addWall(cx, cz, w, h) {
      var g = new T.PlaneGeometry(w, h);
      var m = new T.Mesh(g, wallMat);
      m.rotation.x = -Math.PI / 2;
      m.position.set(cx, _overlayY + 0.001, cz);
      group.add(m);
    }
    var Lf = fpd.length_m, Df = fpd.depth_m;
    addWall(0,           -halfD + extT/2, Lf, extT);
    addWall(0,            halfD - extT/2, Lf, extT);
    addWall(-halfL+extT/2, 0,             extT, Df);
    addWall( halfL-extT/2, 0,             extT, Df);

    /* Interior partitions from shared edges. */
    var TOL = 0.02;
    var seen = {};
    for (var a = 0; a < rooms.length; a++) {
      for (var b = a + 1; b < rooms.length; b++) {
        var rA = rooms[a], rB = rooms[b];
        if (rA.type === 'terrace' || rB.type === 'terrace') continue;
        var aL = rA.x, aR = rA.x + rA.w, aT = rA.y, aB = rA.y + rA.h;
        var bL = rB.x, bR = rB.x + rB.w, bT = rB.y, bB = rB.y + rB.h;
        if (Math.abs(aR - bL) < TOL || Math.abs(bR - aL) < TOL) {
          var x = Math.abs(aR - bL) < TOL ? aR : bR;
          var y0 = Math.max(aT, bT);
          var y1 = Math.min(aB, bB);
          if (y1 > y0) {
            var keyV = 'v:' + x.toFixed(3) + ':' + y0.toFixed(3) + ':' + y1.toFixed(3);
            if (!seen[keyV]) {
              seen[keyV] = 1;
              addWall((x - oxOff) - halfL, (((y0 + y1) / 2) - oyOff) - halfD, intT, (y1 - y0));
            }
          }
        }
        if (Math.abs(aB - bT) < TOL || Math.abs(bB - aT) < TOL) {
          var y2 = Math.abs(aB - bT) < TOL ? aB : bB;
          var x0 = Math.max(aL, bL);
          var x1 = Math.min(aR, bR);
          if (x1 > x0) {
            var keyH = 'h:' + y2.toFixed(3) + ':' + x0.toFixed(3) + ':' + x1.toFixed(3);
            if (!seen[keyH]) {
              seen[keyH] = 1;
              addWall((((x0 + x1) / 2) - oxOff) - halfL, (y2 - oyOff) - halfD, (x1 - x0), intT);
            }
          }
        }
      }
    }

    var st = (typeof eng.getState === 'function') ? eng.getState() : null;
    group.visible = !st || st.view !== '3d';

    group.rotation.y = _overlayYaw;      /* keep the turn across rebuilds */
    eng.scene.add(group);
    _overlayGroup = group;

    /* If we're in edit mode the drag pins need to be repositioned
       to match the new wall layout. Defer until next animation
       frame so the camera has applied any pending updates. We
       SKIP the rebuild while an active drag is in progress --
       see _attachHandleDrag for why. */
    if (_editMode && !_dragActive) {
      requestAnimationFrame(_rebuildEditHandles);
    }
  }

  /* Variant of _renderSceneOverlay that intentionally skips the
     handle rebuild step. Called during an active drag (see
     pointermove) so the pin element we're listening on doesn't
     get destroyed and recreated mid-gesture. */
  function _renderSceneOverlayNoHandles(fp) {
    var wasDragActive = _dragActive;
    _dragActive = true;     /* suppress handle rebuild */
    _renderSceneOverlay(fp);
    _dragActive = wasDragActive;
  }

  function _clearSceneOverlay() {
    var eng = global.BaseEngine;
    if (!eng || !eng.scene) return;
    if (_overlayGroup) {
      _disposeGroup(_overlayGroup);
      eng.scene.remove(_overlayGroup);
      _overlayGroup = null;
    }
    /* Defensive sweep -- earlier versions added meshes directly to
       eng.scene without grouping. Wipe anything tagged. */
    var killList = [];
    eng.scene.traverse(function (obj) {
      if (obj.userData && obj.userData.isFloorplanOverlay && obj !== _overlayGroup) {
        killList.push(obj);
      }
    });
    for (var k = 0; k < killList.length; k++) {
      _disposeGroup(killList[k]);
      if (killList[k].parent) killList[k].parent.remove(killList[k]);
    }
  }

  function _disposeGroup(group) {
    group.traverse(function (obj) {
      if (obj.geometry && obj.geometry.dispose) obj.geometry.dispose();
      if (obj.material) {
        if (Array.isArray(obj.material)) {
          obj.material.forEach(function (m) { if (m.dispose) m.dispose(); });
        } else if (obj.material.dispose) {
          obj.material.dispose();
        }
      }
    });
  }

  /* turn it: degrees, absolute, and it survives the next rebuild */
  function _setOverlayRotation(deg) {
    _overlayYaw = (Number(deg) || 0) * Math.PI / 180;
    if (_overlayGroup) _overlayGroup.rotation.y = _overlayYaw;
    var sl = document.querySelector('.cf-fp-rotate-slider');
    if (sl) sl.value = Math.round(_overlayYaw * 180 / Math.PI);
  }
  function _getOverlayRotation() { return Math.round(_overlayYaw * 180 / Math.PI); }
  /* and nudge it, which is what a person does with a crooked scan */
  function _nudgeOverlayRotation(dDeg) { _setOverlayRotation(_getOverlayRotation() + (Number(dDeg) || 0)); }

  function _setOverlayOpacity(v) {
    _opacity = Math.max(0, Math.min(1, Number(v) || 0));
    if (!_overlayGroup) return;
    _overlayGroup.traverse(function (obj) {
      if (obj.isMesh && obj.material) {
        var isWall = !obj.userData || !obj.userData.roomId;
        obj.material.opacity = isWall ? _opacity : (_opacity * 0.85);
      }
    });
    var sl = document.querySelector('.cf-fp-opacity-slider');
    if (sl) sl.value = Math.round(_opacity * 100);
  }

  function _onViewChanged() {
    if (!_overlayGroup) return;
    var eng = global.BaseEngine;
    var st = (eng && typeof eng.getState === 'function') ? eng.getState() : null;
    _overlayGroup.visible = !st || st.view !== '3d';
    /* Edit handles only make sense in the 2D plan view. */
    if (_handlesHost) _handlesHost.style.display = (!st || st.view !== '3d') ? '' : 'none';
    if (_xButton)     _xButton.style.display     = (!st || st.view !== '3d') ? '' : 'none';
  }

  /* ---------------------------------------------------------------
     EDIT MODE -- wall + corner drag handles, X (remove) button

     Each interior partition + exterior wall gets a single MIDDLE
     handle that drags the wall perpendicular to its run. Each
     wall also gets two END handles, one at either end, that drag
     the corresponding corner of the rooms it separates.

     Handles are HTML pins layered over the Three.js canvas. On
     every animation frame _onFrame() projects their world points
     back to screen and updates the pins' CSS positions. This is
     the same pattern base-lib already uses for the foundation
     corner / edge handles.

     The X button lives in scene space too -- pinned to the top
     outer corner of the floorplan envelope.
     --------------------------------------------------------------- */
  /* ---------------------------------------------------------------
     EDIT MODE -- room-based selection + edge drag

     New (v6) model: drag operates on ROOMS, not walls.
     - Pointer hovers a room floor -> soft highlight (CSS border).
     - Click -> selects the room (full highlight + 4 mid pins
       on its edges).
     - Drag pin -> grows/shrinks the room from that side; the
       neighbour rooms sharing the moved boundary contract /
       expand by the same delta so the layout stays gap-free.
     - First-neighbour cascade only -- distant rooms aren't
       touched, which prevents the "everything wars with everything"
       problem the wall-based model had when a partition was only
       partially shared between adjacent rooms.
     - Min room dimension 0.60 m. Hitting it stops the drag.
     - Click outside any room (= on the canvas but not on a room
       floor) deselects.
     --------------------------------------------------------------- */

  var MIN_ROOM_DIM    = 0.60;   /* meters -- 600 mm minimum wall offset */
  var _selectedRoomId = null;
  var _hoverRoomId    = null;
  var _hoverHostEl    = null;  /* invisible host catching room hover */
  var _hoverHitAreas  = [];    /* one rect per room for picking */
  var _selectionMeshes = [];   /* THREE.Mesh outlines for selected/hover */
  var _deselectHandler = null;

  function _enterEditMode() {
    var eng = global.BaseEngine;
    if (!eng) return;

    if (typeof eng.setFoundationsHandlesVisible === 'function') {
      eng.setFoundationsHandlesVisible(false);
    }

    if (!_handlesHost) {
      _handlesHost = document.createElement('div');
      _handlesHost.id = 'cf-floorplan-handles';
      _handlesHost.className = 'cf-fp-handles-host';
      var slot = document.querySelector('#cf-wrap-base') || document.body;
      slot.appendChild(_handlesHost);
    }
    _handlesHost.style.display = '';

    if (!_hoverHostEl) {
      _hoverHostEl = document.createElement('div');
      _hoverHostEl.id = 'cf-floorplan-hover';
      _hoverHostEl.className = 'cf-fp-hover-host';
      var slot1 = document.querySelector('#cf-wrap-base') || document.body;
      slot1.appendChild(_hoverHostEl);
    }
    _hoverHostEl.style.display = '';

    if (!_xButton) {
      _xButton = document.createElement('button');
      _xButton.type = 'button';
      _xButton.className = 'cf-fp-remove-btn';
      _xButton.setAttribute('aria-label', 'Remove floorplan');
      _xButton.innerHTML =
        '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>' +
        '</svg>';
      _xButton.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        _removeFloorplan();
      });
      var slot2 = document.querySelector('#cf-wrap-base') || document.body;
      slot2.appendChild(_xButton);
    }
    _xButton.style.display = '';

    /* Outside-click deselect -- attached to the wrap container so
       only clicks inside the configurator pane count, not random
       clicks elsewhere on the page. */
    if (!_deselectHandler) {
      var wrap = document.querySelector('#cf-wrap-base') || document.body;
      _deselectHandler = function (e) {
        if (!_editMode) return;
        /* Ignore clicks on handles or on the floorplan itself. */
        if (e.target.closest('.cf-fp-handle')) return;
        if (e.target.closest('.cf-fp-remove-btn')) return;
        if (e.target.closest('.cf-fp-hover-hit')) return;
        if (_selectedRoomId) {
          _selectedRoomId = null;
          _renderSelectionUI();
        }
      };
      wrap.addEventListener('click', _deselectHandler, true);
    }

    _rebuildEditHandles();
  }

  function _exitEditMode() {
    _editMode = false;
    var eng = global.BaseEngine;
    if (eng && typeof eng.setFoundationsHandlesVisible === 'function') {
      eng.setFoundationsHandlesVisible(true);
    }
    if (_handlesHost) {
      _handlesHost.style.display = 'none';
      _handlesHost.innerHTML = '';
    }
    if (_hoverHostEl) {
      _hoverHostEl.style.display = 'none';
      _hoverHostEl.innerHTML = '';
    }
    if (_xButton) _xButton.style.display = 'none';
    _editHandles    = [];
    _hoverHitAreas  = [];
    _selectedRoomId = null;
    _hoverRoomId    = null;
    _clearSelectionMeshes();
    if (_deselectHandler) {
      var wrap = document.querySelector('#cf-wrap-base');
      if (wrap) wrap.removeEventListener('click', _deselectHandler, true);
      _deselectHandler = null;
    }
  }

  /* Build the four mid-edge pins for the currently selected
     room. We also rebuild the per-room invisible hit areas so a
     click anywhere on a room's floor selects it, and add four
     "envelope" pins on the exterior perimeter that grow / shrink
     the whole footprint at once. */
  function _rebuildEditHandles() {
    if (!_editMode || !_handlesHost) return;
    _handlesHost.innerHTML = '';
    if (_hoverHostEl) _hoverHostEl.innerHTML = '';
    _editHandles   = [];
    _hoverHitAreas = [];

    if (!_floorplanState) {
      _renderSelectionUI();
      return;
    }

    /* Per-room hit areas -- click = select that room. */
    var rooms = _floorplanState.rooms;
    for (var i = 0; i < rooms.length; i++) {
      var r = rooms[i];
      if (r.type === 'terrace') continue;
      var hit = _makeRoomHitArea(r);
      _hoverHitAreas.push(hit);
    }

    /* Per-room edge pins -- only for the selected room. */
    if (_selectedRoomId) {
      var sel = _findRoom(_selectedRoomId);
      if (sel) _buildRoomEdgeHandles(sel);
    }

    /* Envelope pins -- four mid-edge pins on the OUTER perimeter
       that the user can drag to grow / shrink the whole house at
       once. These are always visible (no selection required) so
       there's a quick way to resize the footprint without picking
       any single room. */
    _buildEnvelopeHandles();

    _renderSelectionUI();
    _onFrame();
  }

  function _buildEnvelopeHandles() {
    var fpd = _computeFootprintFromRooms();
    if (!fpd) return;
    var halfL = fpd.length_m / 2;
    var halfD = fpd.depth_m  / 2;
    var sides = [
      { side: 'left',   orient: 'v', worldX: -halfL, worldZ: 0 },
      { side: 'right',  orient: 'v', worldX:  halfL, worldZ: 0 },
      { side: 'top',    orient: 'h', worldX: 0,      worldZ: -halfD },
      { side: 'bottom', orient: 'h', worldX: 0,      worldZ:  halfD }
    ];
    for (var i = 0; i < sides.length; i++) {
      var s = sides[i];
      var h = {
        type:   'envelope',
        side:   s.side,
        orient: s.orient,
        worldX: s.worldX,
        worldZ: s.worldZ
      };
      _editHandles.push(_makeHandleElement(h));
    }
  }

  function _findRoom(id) {
    if (!_floorplanState) return null;
    for (var i = 0; i < _floorplanState.rooms.length; i++) {
      if (_floorplanState.rooms[i].id === id) return _floorplanState.rooms[i];
    }
    return null;
  }

  function _makeRoomHitArea(room) {
    var div = document.createElement('div');
    div.className = 'cf-fp-hover-hit';
    div.setAttribute('data-room-id', room.id);
    div.addEventListener('pointerenter', function () {
      if (_dragActive) return;
      _hoverRoomId = room.id;
      _renderSelectionUI();
    });
    div.addEventListener('pointerleave', function () {
      if (_dragActive) return;
      if (_hoverRoomId === room.id) {
        _hoverRoomId = null;
        _renderSelectionUI();
      }
    });
    div.addEventListener('click', function (e) {
      if (_dragActive) return;
      e.stopPropagation();
      _selectedRoomId = (_selectedRoomId === room.id) ? null : room.id;
      _rebuildEditHandles();
    });
    _hoverHostEl.appendChild(div);
    return { el: div, roomId: room.id };
  }

  function _buildRoomEdgeHandles(room) {
    /* 4 mid-edge pins: left, right, top, bottom of the room in
       floorplan-local coords. We translate to scene-space when
       projecting (see _onFrame). */
    var fpd = _computeFootprintFromRooms();
    if (!fpd) return;
    var halfL = fpd.length_m / 2;
    var halfD = fpd.depth_m  / 2;
    var oxOff = fpd.offset_x;
    var oyOff = fpd.offset_y;
    function sx(x) { return (x - oxOff) - halfL; }
    function sz(y) { return (y - oyOff) - halfD; }

    var midY = sz(room.y + room.h / 2);
    var midX = sx(room.x + room.w / 2);
    var rL = sx(room.x);
    var rR = sx(room.x + room.w);
    var rT = sz(room.y);
    var rB = sz(room.y + room.h);

    var edges = [
      { side: 'left',   orient: 'v', worldX: rL, worldZ: midY },
      { side: 'right',  orient: 'v', worldX: rR, worldZ: midY },
      { side: 'top',    orient: 'h', worldX: midX, worldZ: rT },
      { side: 'bottom', orient: 'h', worldX: midX, worldZ: rB }
    ];
    for (var i = 0; i < edges.length; i++) {
      var h = {
        type:   'room-edge',
        roomId: room.id,
        side:   edges[i].side,
        orient: edges[i].orient,
        worldX: edges[i].worldX,
        worldZ: edges[i].worldZ
      };
      _editHandles.push(_makeHandleElement(h));
    }
  }

  function _makeHandleElement(handle) {
    var el = document.createElement('div');
    el.className = 'cf-fp-handle cf-fp-handle--' + handle.type;
    el.setAttribute('data-orient', handle.orient || '');
    el.setAttribute('data-side',   handle.side   || '');
    _handlesHost.appendChild(el);
    handle.el = el;
    _attachHandleDrag(handle);
    return handle;
  }

  function _renderSelectionUI() {
    _clearSelectionMeshes();
    if (!_floorplanState) return;
    var T = global.THREE;
    var eng = global.BaseEngine;
    if (!T || !eng || !eng.scene) return;

    var fpd = _computeFootprintFromRooms();
    if (!fpd) return;
    var halfL = fpd.length_m / 2;
    var halfD = fpd.depth_m  / 2;
    var oxOff = fpd.offset_x;
    var oyOff = fpd.offset_y;

    function makeOutline(room, color, lineWidth) {
      var x = (room.x + room.w / 2 - oxOff) - halfL;
      var z = (room.y + room.h / 2 - oyOff) - halfD;
      /* Use line segments at the room perimeter, slightly raised
         so they read above the floor fill. */
      var hw = room.w / 2;
      var hd = room.h / 2;
      var y  = _overlayY + 0.01;
      var pts = [
        new T.Vector3(x - hw, y, z - hd),
        new T.Vector3(x + hw, y, z - hd),
        new T.Vector3(x + hw, y, z - hd),
        new T.Vector3(x + hw, y, z + hd),
        new T.Vector3(x + hw, y, z + hd),
        new T.Vector3(x - hw, y, z + hd),
        new T.Vector3(x - hw, y, z + hd),
        new T.Vector3(x - hw, y, z - hd)
      ];
      var geo  = new T.BufferGeometry().setFromPoints(pts);
      var mat  = new T.LineBasicMaterial({ color: color, linewidth: lineWidth || 1 });
      var line = new T.LineSegments(geo, mat);
      line.userData = { isFloorplanSelectionMesh: true };
      eng.scene.add(line);
      _selectionMeshes.push(line);
    }

    if (_selectedRoomId) {
      var sel = _findRoom(_selectedRoomId);
      if (sel) makeOutline(sel, '#463529');
    }
    if (_hoverRoomId && _hoverRoomId !== _selectedRoomId) {
      var hv = _findRoom(_hoverRoomId);
      if (hv) makeOutline(hv, '#D2A878');
    }
  }

  function _clearSelectionMeshes() {
    var eng = global.BaseEngine;
    if (!eng || !eng.scene) { _selectionMeshes = []; return; }
    for (var i = 0; i < _selectionMeshes.length; i++) {
      var m = _selectionMeshes[i];
      if (m.geometry && m.geometry.dispose) m.geometry.dispose();
      if (m.material && m.material.dispose) m.material.dispose();
      eng.scene.remove(m);
    }
    _selectionMeshes = [];
  }

  /* ---------------------------------------------------------------
     DRAG implementation -- cascade to first neighbours only

     When the user drags one of the four edge pins of the selected
     room, the room grows or shrinks on that side. Any neighbour
     room flush with the moved edge in the orthogonal range of the
     room being dragged grows / shrinks the opposite way so the
     layout stays gap-free.

     If applying the drag would shrink any participating room
     below MIN_ROOM_DIM, we clamp the delta and stop. The pin
     stays where the clamp landed.
     --------------------------------------------------------------- */
  function _attachHandleDrag(handle) {
    var startWorld = null;
    var startRoom  = null;
    var startRooms = null;

    function onMove(e) {
      if (!_dragActive || !startWorld) return;
      e.preventDefault();
      var eng = global.BaseEngine;
      var p = eng.screenToWorld(e.clientX, e.clientY);
      if (!p) return;
      var dx = p.x - startWorld.x;
      var dz = p.z - startWorld.z;
      _applyRoomEdgeDrag(handle, dx, dz, startRoom, startRooms);
      _redrawSceneOverlay();
      _renderSelectionUI();
      _updateDraggedHandleWorldPos(handle);
      _applyFootprintFromEdit();
    }

    function onEnd(e) {
      if (!_dragActive) return;
      _dragActive = false;
      try { if (e && handle.el.releasePointerCapture) handle.el.releasePointerCapture(e.pointerId); } catch (_) {}
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup',     onEnd);
      document.removeEventListener('pointercancel', onEnd);
      var eng = global.BaseEngine;
      if (eng && eng.controls) eng.controls.enabled = true;
      _rebuildEditHandles();
    }

    handle.el.addEventListener('pointerdown', function (e) {
      e.preventDefault();
      e.stopPropagation();
      _dragActive = true;
      try { handle.el.setPointerCapture(e.pointerId); } catch (_) {}
      var eng = global.BaseEngine;
      if (eng && eng.controls) eng.controls.enabled = false;
      startWorld = eng.screenToWorld(e.clientX, e.clientY);
      startRoom  = handle.roomId ? Object.assign({}, _findRoom(handle.roomId)) : null;
      startRooms = _floorplanState.rooms.map(function (r) { return Object.assign({}, r); });
      _pushFloorplanHistory();
      document.addEventListener('pointermove', onMove, { passive: false });
      document.addEventListener('pointerup',     onEnd);
      document.addEventListener('pointercancel', onEnd);
    });
  }

  /* Apply a room-edge drag with first-neighbour cascade.

     "handle" describes which edge (left/right/top/bottom) of
     which room is being moved. "startRoom" is the snapshot of
     the dragged room at gesture start; "startRooms" is the
     snapshot of every room. "dx" / "dz" are world deltas.

     We apply the move to the LIVE rooms array, clamping the
     delta whenever any participating room would shrink below
     MIN_ROOM_DIM. */
  function _applyRoomEdgeDrag(handle, dx, dz, startRoom, startRooms) {
    if (!_floorplanState) return;
    var TOL = 0.02;

    /* Envelope drag -- move every exterior-flush room on that side
       so the whole footprint resizes uniformly. Doesn't need a
       selected room, doesn't cascade interior boundaries. */
    if (handle.type === 'envelope') {
      _applyEnvelopeDrag(handle, dx, dz, startRooms);
      return;
    }

    var rooms = _floorplanState.rooms;

    /* Convert the world delta into an axis delta in floorplan
       coordinates. Floorplan x grows right in scene-x; floorplan
       y grows DOWN in scene-z (since rooms live on the XZ plane). */
    var delta = (handle.orient === 'v') ? dx : dz;

    /* Determine which boundary in floorplan coords we're moving,
       and which rooms share it. Right / bottom = increasing
       coord; left / top = decreasing. */
    var side = handle.side;
    var oldBoundary;
    if      (side === 'left')   oldBoundary = startRoom.x;
    else if (side === 'right')  oldBoundary = startRoom.x + startRoom.w;
    else if (side === 'top')    oldBoundary = startRoom.y;
    else if (side === 'bottom') oldBoundary = startRoom.y + startRoom.h;

    var newBoundary = oldBoundary + delta;

    /* Clamp against MIN_ROOM_DIM on:
       1) the dragged room itself,
       2) any first-neighbour rooms that share the boundary in the
          orthogonal range of the dragged room. */
    function clampNewBoundary(nb) {
      var sr = startRoom;
      if (side === 'left' && (sr.x + sr.w - nb) < MIN_ROOM_DIM) {
        nb = sr.x + sr.w - MIN_ROOM_DIM;
      }
      if (side === 'right' && (nb - sr.x) < MIN_ROOM_DIM) {
        nb = sr.x + MIN_ROOM_DIM;
      }
      if (side === 'top' && (sr.y + sr.h - nb) < MIN_ROOM_DIM) {
        nb = sr.y + sr.h - MIN_ROOM_DIM;
      }
      if (side === 'bottom' && (nb - sr.y) < MIN_ROOM_DIM) {
        nb = sr.y + MIN_ROOM_DIM;
      }
      /* Neighbour-side clamp -- collect first neighbours touching
         this boundary in the orthogonal range, check their width
         after the proposed move, clamp if any would go below
         MIN_ROOM_DIM. */
      for (var i = 0; i < startRooms.length; i++) {
        var sn = startRooms[i];
        if (sn.id === sr.id) continue;
        if (side === 'left' || side === 'right') {
          /* Vertical boundary -- neighbour shares an x coordinate
             with our boundary AND overlaps in y range. */
          var oy0 = Math.max(sn.y, sr.y);
          var oy1 = Math.min(sn.y + sn.h, sr.y + sr.h);
          if (oy1 - oy0 < TOL) continue;
          if (side === 'left' && Math.abs((sn.x + sn.w) - sr.x) < TOL) {
            /* Neighbour is to the left, sharing right=sr.x */
            var nw = nb - sn.x;
            if (nw < MIN_ROOM_DIM) nb = sn.x + MIN_ROOM_DIM;
          }
          if (side === 'right' && Math.abs(sn.x - (sr.x + sr.w)) < TOL) {
            /* Neighbour is to the right, sharing left=sr.x+sr.w */
            var nw2 = (sn.x + sn.w) - nb;
            if (nw2 < MIN_ROOM_DIM) nb = (sn.x + sn.w) - MIN_ROOM_DIM;
          }
        } else {
          var ox0 = Math.max(sn.x, sr.x);
          var ox1 = Math.min(sn.x + sn.w, sr.x + sr.w);
          if (ox1 - ox0 < TOL) continue;
          if (side === 'top' && Math.abs((sn.y + sn.h) - sr.y) < TOL) {
            var nh = nb - sn.y;
            if (nh < MIN_ROOM_DIM) nb = sn.y + MIN_ROOM_DIM;
          }
          if (side === 'bottom' && Math.abs(sn.y - (sr.y + sr.h)) < TOL) {
            var nh2 = (sn.y + sn.h) - nb;
            if (nh2 < MIN_ROOM_DIM) nb = (sn.y + sn.h) - MIN_ROOM_DIM;
          }
        }
      }
      return nb;
    }

    newBoundary = clampNewBoundary(newBoundary);

    /* Apply to the live dragged room. */
    var live = _findRoom(handle.roomId);
    if (!live) return;
    if (side === 'left') {
      live.w = (startRoom.x + startRoom.w) - newBoundary;
      live.x = newBoundary;
    } else if (side === 'right') {
      live.w = newBoundary - startRoom.x;
    } else if (side === 'top') {
      live.h = (startRoom.y + startRoom.h) - newBoundary;
      live.y = newBoundary;
    } else if (side === 'bottom') {
      live.h = newBoundary - startRoom.y;
    }

    /* Apply to first-neighbour rooms. We look at the SNAPSHOT
       to identify neighbours (= rooms flush with oldBoundary
       in the orthogonal range), then mutate the LIVE ones. */
    for (var j = 0; j < startRooms.length; j++) {
      var sn = startRooms[j];
      if (sn.id === handle.roomId) continue;
      var liveSn = _findRoom(sn.id);
      if (!liveSn) continue;

      if (side === 'left' || side === 'right') {
        var oy0 = Math.max(sn.y, startRoom.y);
        var oy1 = Math.min(sn.y + sn.h, startRoom.y + startRoom.h);
        if (oy1 - oy0 < TOL) continue;
        if (side === 'left' && Math.abs((sn.x + sn.w) - oldBoundary) < TOL) {
          /* Neighbour on the left: extend its width to meet new boundary. */
          liveSn.w = newBoundary - sn.x;
        }
        if (side === 'right' && Math.abs(sn.x - oldBoundary) < TOL) {
          /* Neighbour on the right: shift its left edge + adjust width. */
          liveSn.x = newBoundary;
          liveSn.w = (sn.x + sn.w) - newBoundary;
        }
      } else {
        var ox0 = Math.max(sn.x, startRoom.x);
        var ox1 = Math.min(sn.x + sn.w, startRoom.x + startRoom.w);
        if (ox1 - ox0 < TOL) continue;
        if (side === 'top' && Math.abs((sn.y + sn.h) - oldBoundary) < TOL) {
          liveSn.h = newBoundary - sn.y;
        }
        if (side === 'bottom' && Math.abs(sn.y - oldBoundary) < TOL) {
          liveSn.y = newBoundary;
          liveSn.h = (sn.y + sn.h) - newBoundary;
        }
      }
    }
  }

  /* Envelope drag -- pull the entire exterior on one side and
     extend / contract every room flush with that edge. Skips
     interior boundaries so the room subdivision pattern stays
     intact, just stretched. Clamps if any participating room
     would drop below MIN_ROOM_DIM. */
  function _applyEnvelopeDrag(handle, dx, dz, startRooms) {
    if (!_floorplanState) return;
    var TOL = 0.02;
    var side  = handle.side;
    var delta = (handle.orient === 'v') ? dx : dz;

    /* Identify the outer boundary value in floorplan-space + the
       set of rooms flush with it. */
    var outer;
    if (side === 'left')   outer = Math.min.apply(null, startRooms.map(function (r) { return r.x; }));
    if (side === 'right')  outer = Math.max.apply(null, startRooms.map(function (r) { return r.x + r.w; }));
    if (side === 'top')    outer = Math.min.apply(null, startRooms.map(function (r) { return r.y; }));
    if (side === 'bottom') outer = Math.max.apply(null, startRooms.map(function (r) { return r.y + r.h; }));

    var matched = [];
    for (var i = 0; i < startRooms.length; i++) {
      var sr = startRooms[i];
      if (side === 'left'   && Math.abs(sr.x - outer) < TOL)             matched.push(i);
      if (side === 'right'  && Math.abs((sr.x + sr.w) - outer) < TOL)    matched.push(i);
      if (side === 'top'    && Math.abs(sr.y - outer) < TOL)             matched.push(i);
      if (side === 'bottom' && Math.abs((sr.y + sr.h) - outer) < TOL)    matched.push(i);
    }
    if (!matched.length) return;

    /* Clamp delta so the smallest matched room stays >= MIN_ROOM_DIM. */
    var clampedDelta = delta;
    for (var k = 0; k < matched.length; k++) {
      var src = startRooms[matched[k]];
      if (side === 'left') {
        /* Outer left moving right by delta shrinks room width. */
        if (src.w - clampedDelta < MIN_ROOM_DIM) clampedDelta = src.w - MIN_ROOM_DIM;
      }
      if (side === 'right') {
        if (src.w + clampedDelta < MIN_ROOM_DIM) clampedDelta = MIN_ROOM_DIM - src.w;
      }
      if (side === 'top') {
        if (src.h - clampedDelta < MIN_ROOM_DIM) clampedDelta = src.h - MIN_ROOM_DIM;
      }
      if (side === 'bottom') {
        if (src.h + clampedDelta < MIN_ROOM_DIM) clampedDelta = MIN_ROOM_DIM - src.h;
      }
    }

    /* Apply to the matching live rooms. */
    var rooms = _floorplanState.rooms;
    for (var m = 0; m < matched.length; m++) {
      var idx = matched[m];
      var src2 = startRooms[idx];
      var live = rooms[idx];
      if (side === 'left') {
        live.x = src2.x + clampedDelta;
        live.w = src2.w - clampedDelta;
      } else if (side === 'right') {
        live.w = src2.w + clampedDelta;
      } else if (side === 'top') {
        live.y = src2.y + clampedDelta;
        live.h = src2.h - clampedDelta;
      } else if (side === 'bottom') {
        live.h = src2.h + clampedDelta;
      }
    }
  }

  /* Update the dragged handle's world point after the room's
     geometry has changed, so the pin follows the moving edge. */
  function _updateDraggedHandleWorldPos(handle) {
    if (handle.type === 'envelope') {
      var fpd = _computeFootprintFromRooms();
      if (!fpd) return;
      var hL = fpd.length_m / 2;
      var hD = fpd.depth_m  / 2;
      if (handle.side === 'left')   { handle.worldX = -hL; handle.worldZ = 0; }
      if (handle.side === 'right')  { handle.worldX =  hL; handle.worldZ = 0; }
      if (handle.side === 'top')    { handle.worldX = 0;   handle.worldZ = -hD; }
      if (handle.side === 'bottom') { handle.worldX = 0;   handle.worldZ =  hD; }
      return;
    }
    if (handle.type !== 'room-edge') return;
    var room = _findRoom(handle.roomId);
    if (!room) return;
    var fpd = _computeFootprintFromRooms();
    if (!fpd) return;
    var halfL = fpd.length_m / 2;
    var halfD = fpd.depth_m  / 2;
    var oxOff = fpd.offset_x;
    var oyOff = fpd.offset_y;
    function sx(x) { return (x - oxOff) - halfL; }
    function sz(y) { return (y - oyOff) - halfD; }
    var midY = sz(room.y + room.h / 2);
    var midX = sx(room.x + room.w / 2);
    if (handle.side === 'left')   { handle.worldX = sx(room.x);             handle.worldZ = midY; }
    if (handle.side === 'right')  { handle.worldX = sx(room.x + room.w);    handle.worldZ = midY; }
    if (handle.side === 'top')    { handle.worldX = midX;                   handle.worldZ = sz(room.y); }
    if (handle.side === 'bottom') { handle.worldX = midX;                   handle.worldZ = sz(room.y + room.h); }
  }

  /* Per-frame projection of all handle world points back to
     screen-space so the HTML pins ride on top of the live camera.
     Also re-positions the X (remove) button to the floorplan's
     top-right corner, and the invisible room-hit-area divs so
     hover + click stay accurate as the camera moves. Called by
     BaseEngine's animation loop. */
  function _onFrame() {
    if (!_editMode) return;
    var eng = global.BaseEngine;
    if (!eng || typeof eng.worldToScreen !== 'function') return;

    /* Resize the hover host to the bounding box of the current
       footprint so pointer events outside the floorplan can
       reach the base configurator (foundation drag handles,
       canvas pan, etc) untouched.  Without this clamp, the
       hover host would cover the whole #cf-wrap-base and pointer-
       events:none on the host should be enough -- but in practice
       some browsers leak hover/focus styles through anyway, and
       sizing to the footprint guarantees there's literally
       nothing in the way outside the building outline. */
    var fpdHost = _computeFootprintFromRooms();
    if (_hoverHostEl && fpdHost) {
      var p1H = eng.worldToScreen(-fpdHost.length_m / 2, -fpdHost.depth_m / 2);
      var p2H = eng.worldToScreen( fpdHost.length_m / 2,  fpdHost.depth_m / 2);
      if (p1H && p2H) {
        var hostLeft = Math.min(p1H.left, p2H.left);
        var hostTop  = Math.min(p1H.top,  p2H.top);
        var hostW    = Math.abs(p2H.left - p1H.left);
        var hostH    = Math.abs(p2H.top  - p1H.top);
        _hoverHostEl.style.left   = hostLeft + 'px';
        _hoverHostEl.style.top    = hostTop  + 'px';
        _hoverHostEl.style.width  = hostW    + 'px';
        _hoverHostEl.style.height = hostH    + 'px';
      }
    }

    /* Pin elements (per-selected-room + 4 envelope pins). */
    for (var i = 0; i < _editHandles.length; i++) {
      var h = _editHandles[i];
      var s = eng.worldToScreen(h.worldX, h.worldZ);
      if (!s || !h.el) continue;
      h.el.style.left = (s.left - 7) + 'px';
      h.el.style.top  = (s.top  - 7) + 'px';
    }

    /* Room hit areas -- one transparent rect per room, used for
       hover preview + click-to-select. Positions are computed
       relative to the hover host (NOT cf-wrap-base) since each
       hit div is a child of _hoverHostEl. */
    if (_floorplanState && _hoverHitAreas.length && fpdHost) {
      var halfL = fpdHost.length_m / 2;
      var halfD = fpdHost.depth_m  / 2;
      var oxOff = fpdHost.offset_x;
      var oyOff = fpdHost.offset_y;
      /* Project room corners to wrap-space then offset by the
         host's wrap-space position so we get host-local pixels. */
      var hostRect = _hoverHostEl.getBoundingClientRect();
      var wrapEl   = document.getElementById('cf-wrap-base');
      var wrapRect = wrapEl ? wrapEl.getBoundingClientRect() : { left: 0, top: 0 };
      var hostOffsetLeft = hostRect.left - wrapRect.left;
      var hostOffsetTop  = hostRect.top  - wrapRect.top;
      for (var k = 0; k < _hoverHitAreas.length; k++) {
        var hit  = _hoverHitAreas[k];
        var room = _findRoom(hit.roomId);
        if (!room) continue;
        var sL = (room.x         - oxOff) - halfL;
        var sR = (room.x + room.w - oxOff) - halfL;
        var sT = (room.y         - oyOff) - halfD;
        var sB = (room.y + room.h - oyOff) - halfD;
        var p1 = eng.worldToScreen(sL, sT);
        var p2 = eng.worldToScreen(sR, sB);
        if (!p1 || !p2) continue;
        var left   = Math.min(p1.left, p2.left) - hostOffsetLeft;
        var top    = Math.min(p1.top,  p2.top)  - hostOffsetTop;
        var width  = Math.abs(p2.left - p1.left);
        var height = Math.abs(p2.top  - p1.top);
        hit.el.style.left   = left   + 'px';
        hit.el.style.top    = top    + 'px';
        hit.el.style.width  = width  + 'px';
        hit.el.style.height = height + 'px';
      }
    }

    if (_xButton && _floorplanState && fpdHost) {
      /* Sit at the outside of the top-right corner of the
         envelope so it doesn't fight the wall pins. */
      var xs = eng.worldToScreen(fpdHost.length_m / 2, -fpdHost.depth_m / 2);
      if (xs) {
        _xButton.style.left = (xs.left + 6) + 'px';
        _xButton.style.top  = (xs.top  - 30) + 'px';
      }
    }
  }

  /* Patch BaseEngine.toggleView so the overlay's visibility flips
     when the user switches between 2D and 3D. We wrap rather than
     subscribe because base-lib doesn't expose an event bus. */
  function _patchViewToggle() {
    var eng = global.BaseEngine;
    if (!eng || !eng.toggleView || eng._floorplanPatched) return;
    var orig = eng.toggleView.bind(eng);
    eng.toggleView = function () {
      orig();
      _onViewChanged();
    };
    eng._floorplanPatched = true;
  }

  function _findCanvasHost() {
    return document.querySelector('#cf-wrap-base .cf-stage')
        || document.querySelector('#cf-wrap-base');
  }

  function _refreshOverlay() {
    /* Remove any stale HTML overlay from earlier versions. */
    var existing = document.getElementById(OVERLAY_ID);
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

    if (!_selectedHandle) {
      _clearSceneOverlay();
      return;
    }
    var fp = _findFloorplan(_selectedHandle);
    if (!fp) return;
    _patchViewToggle();
    _renderSceneOverlay(fp);
  }

  function _mountOpacitySlider() {
    if (document.getElementById('cf-fp-opacity-control')) return;
    var slot = document.querySelector('#cf-wrap-base') || document.body;
    var box = document.createElement('div');
    box.id = 'cf-fp-opacity-control';
    box.className = 'cf-fp-opacity-control';
    box.style.display = 'none';
    box.innerHTML =
      '<label class="cf-fp-opacity-label">' + L.floorplan_opacity + '</label>' +
      '<input type="range" min="0" max="100" value="' + Math.round(_opacity * 100) + '" class="cf-fp-opacity-slider" />';
    slot.appendChild(box);
    var sl = box.querySelector('input');
    sl.addEventListener('input', function () {
      _setOverlayOpacity(sl.value / 100);
    });
  }

  function _showOpacitySlider(show) {
    var box = document.getElementById('cf-fp-opacity-control');
    if (box) box.style.display = show ? '' : 'none';
  }

  function _renderUndoControl() {
    /* Small floating undo button next to the floorplan opacity
       slider.  Visible only when a floorplan has been applied. */
    var el = document.getElementById('cf-fp-undo');
    if (!_selectedHandle) {
      if (el) el.style.display = 'none';
      return;
    }
    if (!el) {
      el = document.createElement('button');
      el.id = 'cf-fp-undo';
      el.type = 'button';
      el.className = 'cf-fp-undo-btn';
      el.innerHTML =
        '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<path d="M9 14L4 9l5-5"/><path d="M4 9h11a5 5 0 0 1 0 10h-4"/>' +
        '</svg> ' + L.undo;
      el.addEventListener('click', _undo);
      var slot = document.querySelector('#cf-wrap-base') || document.body;
      slot.appendChild(el);
    }
    el.style.display = '';
  }

  function _mountOpacitySlider() {
    if (document.getElementById('cf-fp-opacity-control')) return;
    var slot = document.querySelector('#cf-wrap-base') || document.body;
    var box = document.createElement('div');
    box.id = 'cf-fp-opacity-control';
    box.className = 'cf-fp-opacity-control';
    box.style.display = 'none';
    box.innerHTML =
      '<label class="cf-fp-opacity-label">' + L.floorplan_opacity + '</label>' +
      '<input type="range" min="0" max="100" value="' + Math.round(_opacity * 100) + '" class="cf-fp-opacity-slider" />';
    slot.appendChild(box);
    var sl = box.querySelector('input');
    sl.addEventListener('input', function () {
      _opacity = Math.max(0, Math.min(1, sl.value / 100));
      var ov = document.getElementById(OVERLAY_ID);
      if (ov) ov.style.opacity = String(_opacity);
    });
  }

  /* ---------------------------------------------------------------
     PUBLIC API
     --------------------------------------------------------------- */
  function open() {
    var w = document.getElementById(DRAWER_ID);
    if (!w) return;
    w.classList.add('active');
    document.body.classList.add('open-cc');
    w.setAttribute('aria-hidden', 'false');
  }

  function close() {
    var w = document.getElementById(DRAWER_ID);
    if (!w) return;
    w.classList.remove('active');
    document.body.classList.remove('open-cc');
    w.setAttribute('aria-hidden', 'true');
  }

  function _onDrawerClosed() {
    /* Nothing to do for now -- keep the overlay + slider visible
       so the user can keep tweaking after closing the picker. */
  }

  function init(opts) {
    if (_initialized) return;
    opts = opts || {};
    if (opts.locale)  _locale = String(opts.locale).toLowerCase().indexOf('cs') === 0 ? 'cs' : 'en';
    if (opts.labels)  for (var k in opts.labels) { if (opts.labels[k]) L[k] = opts.labels[k]; }
    /* Debug mode -- visible hit area outlines so we can sanity
       check the world->screen projection. Toggled via URL param. */
    try {
      var qs = (global.location && global.location.search) || '';
      if (qs.indexOf('fpdebug=1') !== -1) {
        var sty = document.createElement('style');
        sty.id = 'cf-fp-debug-style';
        sty.textContent =
          '.cf-fp-hover-hit{outline:2px dashed rgba(255,0,128,0.6);outline-offset:-2px;background:rgba(255,0,128,0.05)!important}' +
          '.cf-fp-hover-host{outline:2px solid rgba(0,200,255,0.7);background:rgba(0,200,255,0.06)!important}';
        document.head.appendChild(sty);
        _log('debug mode ON');
      }
    } catch (_) {}
    _buildDrawer();
    _mountOpacitySlider();
    _initialized = true;
    _log('initialised -- version=' + VERSION + ', floorplans=' +
         ((global.FloorplanData && global.FloorplanData.floorplans.length) || 0));
  }


  /* ---------- FITOUT CATALOGUE ------------------------------------------
     The furniture a room can be given, kept here rather than inside each
     product's library. Every configurator carried its own copy, so adding a
     bookshelf meant editing it in as many places as there are products -- and
     they had already drifted apart.

     Each item names the catalogue code it is bought under, so a sofa dropped
     into a plan can become a line in the bill without anybody maintaining a
     second table mapping one to the other. Geometry is in metres in the item's
     own frame: plan is the 2D symbol, parts are the boxes drawn in 3D.

     A product need not offer all of it -- FloorplanLib.fitouts(keys) hands back
     only the categories it asks for. */
  var CATALOG = {
    kitchen: [
      { id: 'kit_counter', boq: 'FKI00001', plan: [[-1.2,0.1,1.2,0.1],[-0.5,-0.3,-0.5,0.3],[0.5,-0.3,0.5,0.3],[-0.25,-0.14,-0.25,0.14],[0.05,-0.14,0.05,0.14]], i18n: 'fit_kit_1', def: 'Kitchen counter', parts: [{ w: 2.40, d: 0.60, h: 0.90, x: 0, z: 0, m: 'wood' }] },
      { id: 'kit_island', boq: 'FKI00002', plan: [[-0.7,0.1,0.7,0.1],[-0.2,-0.2,0.2,-0.2],[-0.2,-0.2,-0.2,0.2],[0.2,-0.2,0.2,0.2],[-0.2,0.2,0.2,0.2]], i18n: 'fit_kit_2', def: 'Kitchen island', parts: [{ w: 1.40, d: 0.80, h: 0.90, x: 0, z: 0, m: 'wood' }] },
      { id: 'kit_stools', boq: 'FKI00003', plan: [[-0.18,-0.18,0.18,-0.18],[0.18,-0.18,0.18,0.18],[0.18,0.18,-0.18,0.18],[-0.18,0.18,-0.18,-0.18]], i18n: 'fit_kit_4', def: 'Bar stools', parts: [{ w: 0.36, d: 0.36, h: 0.75, x: 0, z: 0, m: 'wood' }] },
      { id: 'kit_pantry', boq: 'FKI00004', plan: [[-0.3,-0.3,0.3,-0.3],[0.3,-0.3,0.3,0.3],[0.3,0.3,-0.3,0.3],[-0.3,0.3,-0.3,-0.3]], i18n: 'fit_kit_5', def: 'Pantry cabinet', parts: [{ w: 0.60, d: 0.60, h: 2.00, x: 0, z: 0, m: 'wood' }] },
      { id: 'kit_fridge', boq: 'FKI00005', plan: [[-0.35,0.2,0.35,0.2],[0.2,0.2,0.2,0.35]], i18n: 'fit_kit_3', def: 'Fridge', parts: [{ w: 0.70, d: 0.70, h: 1.90, x: 0, z: 0, m: 'metal' }] }
    ],
    living: [
      { id: 'liv_sofa', boq: 'FLI00001', plan: [[-0.95,-0.28,0.95,-0.28],[-0.32,-0.28,-0.32,0.45],[0.32,-0.28,0.32,0.45],[-0.95,-0.28,-0.95,0.45],[0.95,-0.28,0.95,0.45]], i18n: 'bench_v2', def: 'Sofa', parts: [{ w: 1.90, d: 0.90, h: 0.80, x: 0, z: 0, m: 'fabric' }] },
      { id: 'liv_sofaL', boq: 'FLI00002', plan: [[-0.95,-0.78,0.95,-0.78],[-0.32,-0.78,-0.32,-0.05],[0.32,-0.78,0.32,-0.05],[-0.72,-0.05,-0.72,0.95],[-0.05,-0.05,-0.05,0.95]], i18n: 'fit_liv_2', def: 'L sofa', parts: [{ w: 1.90, d: 0.90, h: 0.80, x: 0, z: -0.50, m: 'fabric' }, { w: 0.90, d: 1.40, h: 0.80, x: -0.50, z: 0.25, m: 'fabric' }] },
      { id: 'liv_chair', boq: 'FLI00003', plan: [[-0.42,-0.2,0.42,-0.2],[-0.28,-0.2,-0.28,0.42],[0.28,-0.2,0.28,0.42]], i18n: 'fit_liv_3', def: 'Armchair', parts: [{ w: 0.85, d: 0.85, h: 0.80, x: 0, z: 0, m: 'fabric' }] },
      { id: 'liv_table', boq: 'FLI00004', plan: [[-0.35,-0.14,0.35,-0.14],[-0.35,0.14,0.35,0.14]], i18n: 'fit_liv_4', def: 'Coffee table', parts: [{ w: 1.00, d: 0.50, h: 0.40, x: 0, z: 0, m: 'wood' }] },
      { id: 'liv_side', boq: 'FLI00005', plan: [[-0.25,-0.25,0.25,-0.25],[0.25,-0.25,0.25,0.25],[0.25,0.25,-0.25,0.25],[-0.25,0.25,-0.25,-0.25]], i18n: 'fit_liv_6', def: 'Side table', parts: [{ w: 0.50, d: 0.50, h: 0.50, x: 0, z: 0, m: 'wood' }] },
      { id: 'liv_shelf', boq: 'FLI00008', plan: [[-0.45,-0.15,0.45,-0.15],[0.45,-0.15,0.45,0.15],[0.45,0.15,-0.45,0.15],[-0.45,0.15,-0.45,-0.15]], i18n: 'fit_liv_7', def: 'Bookshelf', parts: [{ w: 0.90, d: 0.30, h: 1.90, x: 0, z: 0, m: 'wood' }] },
      { id: 'liv_tv', boq: 'FLI00011', plan: [[-0.8,0.0,0.8,0.0],[-0.3,-0.2,-0.3,0.2],[0.3,-0.2,0.3,0.2]], i18n: 'fit_liv_5', def: 'TV unit', parts: [{ w: 1.60, d: 0.40, h: 0.50, x: 0, z: 0, m: 'wood' }] }
    ],
    bedroom: [
      { id: 'bed_double', boq: 'FMA00001', plan: [[-0.8,-0.55,0.8,-0.55],[-0.72,-0.9,-0.05,-0.62],[0.05,-0.9,0.72,-0.62],[-0.72,-0.9,-0.72,-0.62],[-0.05,-0.9,-0.05,-0.62],[0.05,-0.9,0.05,-0.62],[0.72,-0.9,0.72,-0.62],[-0.72,-0.62,-0.05,-0.62],[0.05,-0.62,0.72,-0.62]], i18n: 'fit_bed_1', def: 'Double bed', parts: [{ w: 1.60, d: 2.00, h: 0.50, x: 0, z: 0, m: 'mattress' }, { w: 1.60, d: 0.10, h: 0.90, x: 0, z: -1.00, m: 'wood' }] },
      { id: 'bed_single', boq: 'FKB00001', plan: [[-0.45,-0.55,0.45,-0.55],[-0.38,-0.9,0.38,-0.62],[-0.38,-0.9,-0.38,-0.62],[0.38,-0.9,0.38,-0.62],[-0.38,-0.62,0.38,-0.62]], i18n: 'fit_bed_2', def: 'Single bed', parts: [{ w: 0.90, d: 2.00, h: 0.50, x: 0, z: 0, m: 'mattress' }, { w: 0.90, d: 0.10, h: 0.90, x: 0, z: -1.00, m: 'wood' }] },
      { id: 'bed_wardrobe', boq: 'FMA00004', plan: [[-0.6,0.3,0.6,0.3],[-0.6,-0.3,0.6,0.3]], i18n: 'fit_bed_3', def: 'Wardrobe', parts: [{ w: 1.20, d: 0.60, h: 2.00, x: 0, z: 0, m: 'wood' }] },
      { id: 'bed_nightstand', boq: 'FMA00003', plan: [[-0.25,0.05,0.25,0.05]], i18n: 'fit_bed_4', def: 'Nightstand', parts: [{ w: 0.50, d: 0.40, h: 0.50, x: 0, z: 0, m: 'wood' }] }
    ],
    bath: [
      { id: 'bath_toilet', boq: 'SBT05501', plan: [[-0.2,-0.3,0.2,-0.3],[-0.2,-0.3,-0.2,-0.18],[0.2,-0.3,0.2,-0.18],[-0.2,-0.18,0.2,-0.18],[-0.14,-0.05,0.14,-0.05],[-0.14,-0.05,-0.16,0.2],[0.14,-0.05,0.16,0.2],[-0.16,0.2,0.16,0.2]], i18n: 'fit_bath_1', def: 'Toilet', parts: [{ w: 0.40, d: 0.60, h: 0.42, x: 0, z: 0, m: 'white' }] },
      { id: 'bath_sink', boq: 'SBS08001', plan: [[-0.2,-0.14,0.2,-0.14],[-0.2,-0.14,-0.2,0.12],[0.2,-0.14,0.2,0.12],[-0.2,0.12,0.2,0.12],[-0.04,0.16,0.04,0.16]], i18n: 'fit_bath_2', def: 'Sink', parts: [{ w: 0.55, d: 0.45, h: 0.85, x: 0, z: 0, m: 'white' }] },
      { id: 'bath_shower', boq: 'SBS08002', plan: [[-0.45,-0.45,0.45,0.45],[0.45,-0.45,-0.45,0.45],[-0.05,0.0,0.05,0.0],[0.0,-0.05,0.0,0.05]], i18n: 'fit_bath_3', def: 'Shower', parts: [{ w: 0.90, d: 0.90, h: 0.06, x: 0, z: 0, m: 'white' }] },
      { id: 'bath_tub', boq: 'SBB08001', plan: [[-0.78,-0.3,0.6,-0.3],[-0.78,0.3,0.6,0.3],[-0.78,-0.3,-0.78,0.3],[0.6,-0.3,0.6,0.3],[0.68,-0.04,0.72,0.04]], i18n: 'fit_bath_4', def: 'Bathtub', parts: [{ w: 1.70, d: 0.75, h: 0.55, x: 0, z: 0, m: 'white' }] }
    ],
    kids: [
      { id: 'kids_bed', boq: 'FKB00001', plan: [[-0.45,-0.55,0.45,-0.55],[-0.38,-0.9,0.38,-0.62],[-0.38,-0.9,-0.38,-0.62],[0.38,-0.9,0.38,-0.62],[-0.38,-0.62,0.38,-0.62]], i18n: 'fit_kids_1', def: 'Kids bed', parts: [
      { w: 0.90, d: 2.00, h: 0.45, x: 0, z: 0, m: 'mattress' },
      { w: 0.90, d: 0.10, h: 0.80, x: 0, z: -1.00, m: 'wood' }
      ] },
      { id: 'kids_bunk', boq: 'FKB00002', plan: [[-0.45,-0.55,0.45,-0.55],[-0.45,0.55,0.45,0.55],[-0.2,-0.9,0.2,-0.9],[-0.2,0.9,0.2,0.9],[-0.45,-0.2,0.45,-0.2],[-0.45,0.2,0.45,0.2]], i18n: 'fit_kids_2', def: 'Bunk bed', parts: [
      { w: 0.90, d: 2.00, h: 0.16, x: 0, z: 0, y: 0.35, m: 'mattress' },
      { w: 0.90, d: 2.00, h: 0.16, x: 0, z: 0, y: 1.35, m: 'mattress' },
      { w: 0.07, d: 0.07, h: 1.65, x: -0.44, z: -0.96, m: 'wood' },
      { w: 0.07, d: 0.07, h: 1.65, x: 0.44, z: -0.96, m: 'wood' },
      { w: 0.07, d: 0.07, h: 1.65, x: -0.44, z: 0.96, m: 'wood' },
      { w: 0.07, d: 0.07, h: 1.65, x: 0.44, z: 0.96, m: 'wood' }
      ] },
      { id: 'kids_desk', boq: 'FKB00003', plan: [[-0.6,0.1,0.6,0.1],[-0.2,-0.3,-0.2,0.1],[0.2,-0.3,0.2,0.1]], i18n: 'fit_kids_3', def: 'Desk', parts: [
      { w: 1.20, d: 0.60, h: 0.74, x: 0, z: 0, m: 'wood' },
      { w: 0.42, d: 0.42, h: 0.45, x: 0, z: 0.52, m: 'fabric' }
      ] },
      { id: 'kids_wardrobe', boq: 'FKB00005', plan: [[-0.5,0.3,0.5,0.3],[-0.5,-0.3,0.5,0.3]], i18n: 'fit_kids_4', def: 'Wardrobe', parts: [{ w: 1.00, d: 0.60, h: 2.00, x: 0, z: 0, m: 'wood' }] }
    ],
    dining: [
      { id: 'din_table', boq: 'FDI00001', plan: [[-0.8,-0.45,0.8,-0.45],[0.8,-0.45,0.8,0.45],[0.8,0.45,-0.8,0.45],[-0.8,0.45,-0.8,-0.45]], i18n: 'fit_din_1', def: 'Dining table', parts: [{ w: 1.60, d: 0.90, h: 0.75, x: 0, z: 0, m: 'wood' }] },
      { id: 'din_cab', boq: 'FDI00005', plan: [[-0.5,-0.2,0.5,-0.2],[0.5,-0.2,0.5,0.2],[0.5,0.2,-0.5,0.2],[-0.5,0.2,-0.5,-0.2]], i18n: 'fit_din_2', def: 'Display cabinet', parts: [{ w: 1.00, d: 0.40, h: 1.80, x: 0, z: 0, m: 'wood' }] }
    ],
    office: [
      { id: 'off_desk', boq: 'FHO00001', plan: [[-0.6,-0.3,0.6,-0.3],[0.6,-0.3,0.6,0.3],[0.6,0.3,-0.6,0.3],[-0.6,0.3,-0.6,-0.3]], i18n: 'fit_off_1', def: 'Desk', parts: [{ w: 1.20, d: 0.60, h: 0.75, x: 0, z: 0, m: 'wood' }] },
      { id: 'off_shelf', boq: 'FHO00003', plan: [[-0.4,-0.15,0.4,-0.15],[0.4,-0.15,0.4,0.15],[0.4,0.15,-0.4,0.15],[-0.4,0.15,-0.4,-0.15]], i18n: 'fit_off_2', def: 'Bookshelf', parts: [{ w: 0.80, d: 0.30, h: 1.90, x: 0, z: 0, m: 'wood' }] }
    ],
    hall: [
      { id: 'hal_shoe', boq: 'FHA00002', plan: [[-0.45,-0.17,0.45,-0.17],[0.45,-0.17,0.45,0.17],[0.45,0.17,-0.45,0.17],[-0.45,0.17,-0.45,-0.17]], i18n: 'fit_hal_1', def: 'Shoe cabinet', parts: [{ w: 0.90, d: 0.35, h: 1.00, x: 0, z: 0, m: 'wood' }] },
      { id: 'hal_ward', boq: 'FHA00004', plan: [[-0.5,-0.3,0.5,-0.3],[0.5,-0.3,0.5,0.3],[0.5,0.3,-0.5,0.3],[-0.5,0.3,-0.5,-0.3]], i18n: 'fit_hal_2', def: 'Wardrobe cabinet', parts: [{ w: 1.00, d: 0.60, h: 2.00, x: 0, z: 0, m: 'wood' }] }
    ],
    outdoor: [
      { id: 'out_sofa', boq: 'FOU00001', plan: [[-0.9,-0.4,0.9,-0.4],[0.9,-0.4,0.9,0.4],[0.9,0.4,-0.9,0.4],[-0.9,0.4,-0.9,-0.4]], i18n: 'fit_out_1', def: 'Outdoor sofa', parts: [{ w: 1.80, d: 0.80, h: 0.75, x: 0, z: 0, m: 'wood' }] },
      { id: 'out_table', boq: 'FOU00003', plan: [[-0.75,-0.45,0.75,-0.45],[0.75,-0.45,0.75,0.45],[0.75,0.45,-0.75,0.45],[-0.75,0.45,-0.75,-0.45]], i18n: 'fit_out_2', def: 'Outdoor dining table', parts: [{ w: 1.50, d: 0.90, h: 0.75, x: 0, z: 0, m: 'wood' }] },
      { id: 'out_daybed', boq: 'FOU00006', plan: [[-1.0,-0.4,1.0,-0.4],[1.0,-0.4,1.0,0.4],[1.0,0.4,-1.0,0.4],[-1.0,0.4,-1.0,-0.4]], i18n: 'fit_out_3', def: 'Daybed', parts: [{ w: 2.00, d: 0.80, h: 0.40, x: 0, z: 0, m: 'wood' }] }
    ]
  };

  function fitoutCategories() {
    var out = [];
    for (var k in CATALOG) if (Object.prototype.hasOwnProperty.call(CATALOG, k)) out.push(k);
    return out;
  }
  function fitoutsFor(keys) {
    if (!keys || !keys.length) return CATALOG;
    var out = {};
    for (var i = 0; i < keys.length; i++) if (CATALOG[keys[i]]) out[keys[i]] = CATALOG[keys[i]];
    return out;
  }
  function fitoutItem(catKey, id) {
    var list = CATALOG[catKey] || [];
    for (var i = 0; i < list.length; i++) if (list[i].id === id) return list[i];
    return null;
  }
  function fitoutFind(id) {
    for (var k in CATALOG) {
      if (!Object.prototype.hasOwnProperty.call(CATALOG, k)) continue;
      for (var i = 0; i < CATALOG[k].length; i++) if (CATALOG[k][i].id === id) return CATALOG[k][i];
    }
    return null;
  }

  /* ===================================================================
     THE SHARED PLAN LOOK -- FloorplanLib.draw
     ===================================================================
     Every configurator drew its plan with its own colours, its own line
     weights, its own door symbol and its own way of writing a number, and the
     result across the site reads as several products by several people. None
     of this depends on how a product stores its geometry, which is what kept
     the shared partition editor from ever being switched on: these are the
     PRIMITIVES, and a product keeps its own model and simply stops inventing
     its own appearance.
     Lifted out of the tiny house, which is the look we settled on. Anything
     that moves here must behave identically in the product it came from --
     that is the test of a clean move (Marek). */
  var DRAW = {
    /* --- the numbers -------------------------------------------------- */
    IN: 0.0254,
    fmtImp: function (m) {
      var inT = m / this.IN;
      if (inT < 12) return (Math.round(inT * 10) / 10) + '\u2033';
      var ft = Math.floor(inT / 12), inch = Math.round(inT - ft * 12);
      if (inch === 12) { ft++; inch = 0; }
      return ft + '\u2032-' + inch + '\u2033';
    },
    fmtLen: function (m, isMetric) {
      return isMetric ? (Math.round(m * 1000) + ' mm') : this.fmtImp(m);
    },
    /* a door is bought by the inch and written that way */
    fmtDoor: function (m, isMetric) {
      return isMetric ? (Math.round(m * 1000) + ' mm') : (Math.round(m / this.IN) + '\u2033');
    },

    /* --- what things look like ---------------------------------------- */
    WALL_COL: '#8A8F94',      /* the shell, grey enough to sit back        */
    PART_COL: '#374151',      /* interior walls                            */
    LINE_COL: '#1f2937',      /* symbols: door leaves, glazing             */
    NOTE_COL: '#4b5563',      /* the dot and leader of a label             */
    TEXT_COL: '#1f2937',
    DIM_BG:   '#ffffff',
    DIM_TXT:  '#1A1C1D',
    /* one palette and one set of names, so a bathroom is the same room in
       every product; keyed together, so a name change carries its colour */
    /* THE ROOMS AND THEIR COLOURS COME FROM THE UX PAGE (section 12), which is
       where they are decided and published. Two things changed here.
       THE COLOURS ARE MUTED, and that is not taste: the fill sits at 40 percent
       UNDER the plan and the room name has to stay readable on top of it. The
       old set was picked before that rule and reads through the linework.
       AND THERE ARE SIXTEEN, not seven -- a tiny house has a loft and a pantry,
       a garden building has a workshop, and the page carries them all so that a
       BATHROOM IS THE SAME BLUE IN EVERY PRODUCT. Name and colour are keyed
       together; that is the whole point of the list living in one place.
       THE FIRST SEVEN KEEP THEIR ORDER AND THEIR i18n KEYS. The names are
       stored as an INDEX into this table, so reordering would silently rename
       every room anybody has ever saved -- the new nine are appended, and the
       container_room_ prefix stays on the old ones because that is what the
       translations are filed under (Marek). */
    /* THE ROOM WORDS LIVE IN THE LOCALE'S OWN BRANCH, NOT IN A PRODUCT'S.
       This table asked for container_room_1..16 and FOURTEEN of those sixteen
       keys have never existed in any locale file -- so every product drawing a
       plan showed its rooms in English, in every language, and no amount of
       translating could change it. The locale has carried rooms.livingroom,
       rooms.kitchen, rooms.bathroom and the rest all along.
       TECHNICAL and KIDS ROOM keep their container_room keys because those two
       DO exist and are translated, and moving them would orphan a good key to
       gain nothing. HALLWAY, PANTRY, LOFT and WORKSHOP are four words that had
       no key anywhere; they get one here rather than staying untranslatable.
       A product wanting a different word still answers roomTable() (Marek). */
    ROOMS: [
      { i18n: 'rooms.livingroom',  def: 'LIVING ROOM',    col: '#B7C7A6' },
      { i18n: 'rooms.kitchen',  def: 'KITCHEN',        col: '#E3C49E' },
      { i18n: 'rooms.bedroom',  def: 'BEDROOM',        col: '#AEC2D4' },
      { i18n: 'rooms.bathroom',  def: 'BATHROOM',       col: '#A8C7CA' },
      { i18n: 'rooms.hall',  def: 'HALL',           col: '#C4B8AC' },
      { i18n: 'container_room_6',  def: 'TECHNICAL',      col: '#BBAEC4' },
      { i18n: 'container_room_7',  def: 'KIDS ROOM',      col: '#D8B4BC' },
      { i18n: 'rooms.sauna',  def: 'SAUNA',          col: '#D5BCA1' },
      { i18n: 'rooms.office',  def: 'OFFICE',         col: '#B1BDC2' },
      { i18n: 'rooms.storage', def: 'STORAGE',        col: '#C7C2B6' },
      { i18n: 'rooms.laundry', def: 'LAUNDRY',        col: '#B7C4BE' },
      { i18n: 'rooms.terrace', def: 'TERRACE',        col: '#C6CBB9' },
      { i18n: 'rooms.hallway', def: 'HALLWAY / ENTRY', col: '#CBBFB2' },
      { i18n: 'rooms.pantry', def: 'PANTRY',         col: '#D2C8AE' },
      { i18n: 'rooms.loft', def: 'LOFT',           col: '#BFC6D0' },
      { i18n: 'rooms.workshop', def: 'WORKSHOP',       col: '#B6B9BE' },
      /* v105 (Marek: DINING, WC, WALK-IN CLOSET, GARAGE + MUDROOM). Appended,
         never inserted: a saved name is an index into this list. The keys
         are the locale's own -- four were there already, rooms.mudroom is
         new -- and the colours sit with their neighbours: dining between
         the living room and the kitchen, the WC beside the bathroom. */
      { i18n: 'rooms.diningroom', def: 'DINING ROOM', col: '#CFC3A2' },
      { i18n: 'rooms.wc', def: 'WC',             col: '#B3C9CF' },
      { i18n: 'rooms.wardrobe', def: 'WARDROBE',  col: '#C9C0B0' },
      { i18n: 'rooms.garage', def: 'GARAGE',      col: '#BDBDB8' },
      { i18n: 'rooms.mudroom', def: 'MUDROOM',    col: '#C3B9A9' }
    ],
    /* AND A PRODUCT MAY BRING ITS OWN VOCABULARY. A sauna has no kitchen and a
       garage has no bedroom, so the list above is the DEFAULT and not the law:
       a contract that answers roomTable() supplies its own names and colours,
       in the same shape, and everything downstream -- the fill, the label, the
       cycle, the pin -- follows it without knowing which product it is drawing
       (Marek). */
    /* A PRODUCT MAY ALSO TAKE A SUBSET, WITHOUT COPYING ANYTHING. A tiny house
       has no sauna and a garden building has no pantry, but a product that
       answers roomTable() with its own rows carries its own COLOURS with them --
       and then a bathroom is only the same blue for as long as nobody edits one
       of the copies. roomKeys() names the rows it wants and the colours stay
       here, which is the whole reason this list is in the asset (Marek). */
    roomTable: function () {
      var H = _PH, q, k, r, out;
      var t = (H && typeof H.roomTable === 'function') ? H.roomTable() : null;
      if (t && t.length) return t;
      var keys = (H && typeof H.roomKeys === 'function') ? H.roomKeys() : null;
      if (keys && keys.length) {
        out = [];
        for (q = 0; q < keys.length; q++) {
          for (k = 0; k < this.ROOMS.length; k++) {
            r = this.ROOMS[k];
            if (r.i18n === keys[q] || r.def === keys[q]) { out.push(r); break; }
          }
        }
        if (out.length) return out;
      }
      return this.ROOMS;
    },
    roomFill: function (i) {
      var R = this.roomTable(), n = R.length, t = R[(((i | 0) % n) + n) % n];
      return (t ? t.col : '#9CCC9E') + '66';
    },
    roomName: function (i, t) {
      var R = this.roomTable(), n = R.length, r = R[(((i | 0) % n) + n) % n];
      if (!r) return '';
      return (typeof t === 'function') ? t(r.i18n, r.def) : r.def;
    },

    /* --- the marks ---------------------------------------------------- */
    /* A KOT IS A NUMBER. No dimension line, no leaders, no arrowheads: a
       white pill with dark text, placed halfway between the two points it
       measures and stepped off by dx, dy. */
    kot: function (ctx, p1, p2, dx, dy, text, vert, max) {
      var cx = (p1.x + p2.x) / 2 + dx, cy = (p1.y + p2.y) / 2 + dy;
      ctx.save();
      ctx.translate(cx, cy);
      if (vert) ctx.rotate(-Math.PI / 2);
      var bw = text.length * 7.2 + 14, r = 9, x0 = -bw / 2, y0 = -9, w = bw, h = 18;
      ctx.fillStyle = this.DIM_BG;
      ctx.beginPath();
      ctx.moveTo(x0 + r, y0);
      ctx.lineTo(x0 + w - r, y0); ctx.quadraticCurveTo(x0 + w, y0, x0 + w, y0 + r);
      ctx.lineTo(x0 + w, y0 + h - r); ctx.quadraticCurveTo(x0 + w, y0 + h, x0 + w - r, y0 + h);
      ctx.lineTo(x0 + r, y0 + h); ctx.quadraticCurveTo(x0, y0 + h, x0, y0 + h - r);
      ctx.lineTo(x0, y0 + r); ctx.quadraticCurveTo(x0, y0, x0 + r, y0);
      ctx.closePath();
      ctx.fill();
      /* AT ITS LIMIT THE NUMBER IS RED AND SAYS SO: the same kot, the same
         pill and the same turn, the text in red and the word MAX hung under
         it in the same face -- so a plan dragged to its maximum reads as one
         message rather than a plain number (Marek, ADU: width 8000). */
      ctx.fillStyle = max ? '#C8102E' : this.DIM_TXT;
      ctx.font = '600 12px system-ui,-apple-system,sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, 0, 0);
      if (max) ctx.fillText('MAX', 0, h);
      ctx.restore();
    },
    /* A NOTE ABOUT ONE THING, not a measurement between two: a dot on it, a
       thin leader out to the side, the text plain with nothing behind it. */
    note: function (ctx, at, to, text) {
      ctx.save();
      ctx.strokeStyle = this.NOTE_COL;
      ctx.fillStyle = this.NOTE_COL;
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.arc(at.x, at.y, 3, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.moveTo(at.x, at.y); ctx.lineTo(to.x, to.y); ctx.stroke();
      ctx.fillStyle = this.TEXT_COL;
      ctx.font = '600 12px Montserrat, Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, to.x, to.y);
      ctx.restore();
    },
    /* A GRIP. Something to put a finger on, drawn where a dimension can be
       taken hold of. White so it reads over any room colour, red so it says it
       is a control and not a part of the building. */
    GRIP_COL: '#D2352C',
    GRIP_R: 7,
    grip: function (ctx, x, y) {
      ctx.save();
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = this.GRIP_COL;
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(x, y, this.GRIP_R, 0, Math.PI * 2);
      ctx.fill(); ctx.stroke();
      ctx.fillStyle = this.GRIP_COL;
      ctx.beginPath(); ctx.arc(x, y, 2.6, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    },
    /* AN OPENING IN AN OUTER WALL -- SIX SYMBOLS, ONE ROUTINE. The poche is
       broken and the symbol drawn over the break, and every type gets the jamb
       ticks that say where the wall stops. The set and its weights are the UX
       page's: single door, double door, sliding or portal, garage door, window,
       fixed glazing. One drawing for every product, so the same opening reads
       the same here, in the other configurators and on the sheet (Marek).

       kind is one of the SYM names. A BOOLEAN IS STILL ACCEPTED because that is
       what the callers before this version pass -- true a door, false a window.

       into says which side of the wall the building is on, so a leaf swings
       INTO the room rather than out over the garden. Without it every door on
       the far wall opened outwards, which is not a plan mistake anyone reads
       past. */
    /* PASS is a way through with no leaf in it -- jambs and nothing else. It
       is what an interior door becomes at the end of its cycle, and it is a
       real thing to build rather than a door that has gone missing (Marek). */
    SYM: { DOOR: 'door', DOOR2: 'door2', SLIDE: 'slide', SLIDE2: 'slide2', GARAGE: 'garage', WINDOW: 'window', FIXED: 'fixed', PASS: 'pass' },
    /* WHAT A PRODUCT'S OWN WORD MEANS AS A SYMBOL. Each configurator keeps its
       own vocabulary for an opening -- doorhg, glazed, serve -- and this is the
       one place it is translated, so a new product adds a line here instead of
       a second set of symbols. */
    symbolOf: function (op) {
      var k = (op && op.kind) ? String(op.kind).toLowerCase() : '';
      /* A LIGHT THAT DOES NOT OPEN IS FIXED GLAZING whatever else it is: the
         product may call it a window and auto-fit it to the wall, but on a plan
         the mark is the one that says there is no sash (Marek). */
      if (op && op.fix) return this.SYM.FIXED;
      if (op && (op.full || op.halfW || op.fullW)) return this.SYM.SLIDE;
      /* 'doorhg' is NOT a garage door. It reads like one and I mapped it that
         way, but in the sauna it is the EXTERIOR GLAZED DOOR (EXTG0101) -- so
         an entrance was being drawn with a garage door's symbol. A garage door
         says 'garage' (Marek). */
      if (k === 'garage') return this.SYM.GARAGE;
      if (k === 'doorhg') return this.SYM.DOOR;
      /* A GLAZED DOOR AS WHAT IT IS (v131): the catalogue's sliders slide, a
         single French door is one leaf, a pair is a pair -- one mark for the
         whole group drew the 39 inch French door as a pair */
      if (k === 'french') {
        var GF = null, iF, rowF = null;
        try { GF = (global.ConfiguratorUI && typeof global.ConfiguratorUI.openingGroup === 'function') ? global.ConfiguratorUI.openingGroup('french') : null; } catch (eGF) { GF = null; }
        for (iF = 0; GF && GF.items && iF < GF.items.length; iF++) if (GF.items[iF].code === op.code) { rowF = GF.items[iF]; break; }
        if (rowF && /slid/i.test(String(rowF.name || ''))) return this.SYM.SLIDE;
        if ((op.leaves || (rowF && rowF.leaves) || 0) === 1 || (op.w || 0) < 1.2) return this.SYM.DOOR;
        return this.SYM.DOOR2;
      }
      if (k === 'double' || k === 'door2') return this.SYM.DOOR2;
      if (k === 'slide' || k === 'slider' || k === 'hs' || k === 'portal' || k === 'glazed') return this.SYM.SLIDE;
      if (k === 'fixed' || k === 'glass') return this.SYM.FIXED;
      /* AN ENTRY DOOR OF TWO LEAVES IS A PAIR (v149, Marek: vstup do storage
         je prevedeny jako jednokridle dvere). The catalogue's double door
         came out as one leaf 1.5 m long sweeping half the room. */
      if (k === 'door' && op && (op.leaves | 0) >= 2) return this.SYM.DOOR2;
      if (k === 'door' || k === 'doorl' || k === 'doorr') return this.SYM.DOOR;
      if (k === 'window' || k === 'serve') return this.SYM.WINDOW;
      /* nothing said: a sill on the floor is a way through, anything else is
         something to look out of */
      return (op && (op.sill || 0) < 0.03) ? this.SYM.DOOR : this.SYM.WINDOW;
    },
    isDoorSym: function (s) { return s === this.SYM.DOOR || s === this.SYM.DOOR2 || s === this.SYM.SLIDE || s === this.SYM.SLIDE2 || s === this.SYM.GARAGE; },
    /* flip mirrors a one-leaf slider end for end, which is the only difference
       between one that parks left and one that parks right. It is the last
       argument on purpose: every caller written before it passes nothing and
       gets exactly what it got before. */
    opening: function (ctx, cx, cy, along, gap, kind, rotate, into, flip) {
      var K = (kind === true) ? this.SYM.DOOR : ((kind === false || !kind) ? this.SYM.WINDOW : kind);
      var s = (into < 0) ? -1 : 1;
      var half = along / 2, hg = gap / 2;
      ctx.save();
      ctx.translate(cx, cy);
      if (rotate) ctx.rotate(Math.PI / 2);
      /* the break, over the full thickness so nothing of the poche shows. A
         PASS is the exception: the wall it stands in is already drawn with the
         hole in it, so painting the break again would put a grey patch over
         the room colour showing through the doorway. */
      if (K !== this.SYM.PASS) {
        ctx.fillStyle = '#f5f5f5';
        ctx.fillRect(-half, -hg - 1, along, gap + 2);
      }
      ctx.strokeStyle = this.LINE_COL;
      ctx.fillStyle = this.LINE_COL;
      ctx.lineWidth = 1.4;
      /* A LEAF ON ITS HINGE AND THE ARC IT SWEEPS. dir is the jamb it swings
         back to: +1 the one at +x, -1 the one at -x. The sweep runs from the
         leaf standing in the room to that jamb, and which way round that is
         depends on both the hinge and the side the room is on. */
      function leaf(hx, dir, len) {
        var hy = s * (hg - 1);
        ctx.beginPath();
        ctx.moveTo(hx, hy); ctx.lineTo(hx, hy + s * len);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(hx, hy, len, (s > 0) ? Math.PI / 2 : -Math.PI / 2,
                (dir > 0) ? 0 : Math.PI, (s > 0) === (dir > 0));
        ctx.stroke();
      }
      function arrow(x0, y0, x1, y1) {
        var dx = x1 - x0, dy = y1 - y0, L = Math.hypot(dx, dy) || 1;
        var ax = dx / L, ay = dy / L, h = Math.min(7, L * 0.45);
        ctx.beginPath();
        ctx.moveTo(x0, y0); ctx.lineTo(x1, y1);
        ctx.moveTo(x1, y1); ctx.lineTo(x1 - ax * h - ay * h * 0.55, y1 - ay * h + ax * h * 0.55);
        ctx.moveTo(x1, y1); ctx.lineTo(x1 - ax * h + ay * h * 0.55, y1 - ay * h - ax * h * 0.55);
        ctx.stroke();
      }
      if (K === this.SYM.DOOR) {
        leaf(-half, 1, along);
      } else if (K === this.SYM.DOOR2) {
        leaf(-half, 1, half);
        leaf(half, -1, half);
      } else if (K === this.SYM.SLIDE) {
        /* two panels, one passing the other -- they are told apart by sitting
           at different depths in the wall, which is what makes it a slider and
           not a pair of doors */
        var t = Math.max(2, gap * 0.26);
        ctx.save();
        if (flip) ctx.scale(-1, 1);
        /* the depths are the wall's own, not the room's -- taking them off s
           made the two panels overlap on one side of the building and not the
           other, which is the same slider drawn two ways */
        ctx.fillRect(-half + 1, -gap * 0.06 - t, half + 2, t);
        ctx.fillRect(-2, gap * 0.06, half + 1, t);
        arrow(along * 0.10, s * (hg + 9), along * 0.42, s * (hg + 9));
        ctx.restore();
      } else if (K === this.SYM.SLIDE2) {
        /* TWO LEAVES PARTING FROM THE MIDDLE. Same idea as the one-leaf
           slider -- the panels are told apart by their depth in the wall --
           but each covers half the opening and travels the other way, so the
           mark reads as a portal rather than a door. */
        var t2 = Math.max(2, gap * 0.26);
        ctx.fillRect(-half + 1, -gap * 0.06 - t2, half - 1, t2);
        ctx.fillRect(1, gap * 0.06, half - 1, t2);
        arrow(-along * 0.10, s * (hg + 9), -along * 0.42, s * (hg + 9));
        arrow(along * 0.10, s * (hg + 9), along * 0.42, s * (hg + 9));
      } else if (K === this.SYM.GARAGE) {
        ctx.strokeRect(-half + 2, -hg + 1, along - 4, gap - 2);
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(-half + 2, -gap * 0.12); ctx.lineTo(half - 2, -gap * 0.12);
        ctx.moveTo(-half + 2, gap * 0.12); ctx.lineTo(half - 2, gap * 0.12);
        ctx.stroke();
        ctx.lineWidth = 1.4;
        arrow(0, s * (hg + 5), 0, s * (hg + 5 + Math.max(16, gap * 1.6)));
      } else if (K === this.SYM.FIXED) {
        ctx.strokeRect(-half, -hg + 1, along, gap - 2);
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(-half, -hg + 1); ctx.lineTo(half, hg - 1);
        ctx.stroke();
        ctx.lineWidth = 1.4;
      } else {
        ctx.beginPath();
        ctx.moveTo(-half, -gap * 0.22); ctx.lineTo(half, -gap * 0.22);
        ctx.moveTo(-half, gap * 0.22); ctx.lineTo(half, gap * 0.22);
        ctx.stroke();
      }
      /* the jambs, every type */
      ctx.beginPath();
      ctx.moveTo(-half, -hg); ctx.lineTo(-half, hg);
      ctx.moveTo(half, -hg); ctx.lineTo(half, hg);
      ctx.stroke();
      ctx.restore();
    },
    /* THE DOOR. The opening is painted out of the wall and the leaf and its
       swing drawn over the break: sw picks the jamb it hangs on (bit 0) and
       the side it opens to (bit 1). ppm is pixels per metre; along and gap are
       already in pixels so a caller in any model can use it. */
    /* THE CLOSET DOORS (v106, Marek: skrine dvojite dvere). Same contract as
       door(): along and gap in pixels, sw bit 1 the side it opens to, bit 0
       the jamb a single bifold stacks at; rotate for a wall along z. */
    door2: function (ctx, cx, cy, along, gap, sw, rotate) {
      /* two leaves, one on each jamb, meeting in the middle */
      ctx.save();
      ctx.translate(cx, cy);
      if (rotate) ctx.rotate(Math.PI / 2);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(-along / 2, -gap / 2, along, gap);
      ctx.strokeStyle = this.LINE_COL;
      ctx.lineWidth = 1.4;
      var L = along / 2, sy = ((sw | 0) & 2) ? -1 : 1, hy = sy * (gap / 2 - 1), j;
      for (j = 0; j < 2; j++) {
        var right = (j === 1), hx = right ? along / 2 : -along / 2;
        ctx.beginPath(); ctx.moveTo(hx, hy); ctx.lineTo(hx, hy - sy * L); ctx.stroke();
        ctx.beginPath();
        if (sy > 0) ctx.arc(hx, hy, L, -Math.PI / 2, right ? -Math.PI : 0, right);
        else ctx.arc(hx, hy, L, Math.PI / 2, right ? Math.PI : 0, !right);
        ctx.stroke();
      }
      ctx.restore();
    },
    bifold: function (ctx, cx, cy, along, gap, sw, rotate) {
      /* panels folding into the room as a zigzag: one unit stacked at one
         jamb, two units (an opening over 1.1 m, passed as wide) one at each */
      ctx.save();
      ctx.translate(cx, cy);
      if (rotate) ctx.rotate(Math.PI / 2);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(-along / 2, -gap / 2, along, gap);
      ctx.strokeStyle = this.LINE_COL;
      ctx.lineWidth = 1.4;
      var sy = ((sw | 0) & 2) ? -1 : 1, hy = sy * (gap / 2 - 1), dbl = !!((sw | 0) & 4), th = 50 * Math.PI / 180;
      var units = dbl ? [[-along / 2, 1], [along / 2, -1]] : [[((sw | 0) & 1) ? along / 2 : -along / 2, ((sw | 0) & 1) ? -1 : 1]];
      var pnl = dbl ? along / 4 : along / 2, u;
      for (u = 0; u < units.length; u++) {
        var x0 = units[u][0], dir = units[u][1];
        var x1 = x0 + dir * pnl * Math.cos(th), y1 = hy - sy * pnl * Math.sin(th), x2 = x1 + dir * pnl * Math.cos(th);
        ctx.beginPath(); ctx.moveTo(x0, hy); ctx.lineTo(x1, y1); ctx.lineTo(x2, hy); ctx.stroke();
      }
      ctx.restore();
    },
    bypass: function (ctx, cx, cy, along, gap, rotate) {
      /* two leaves at two depths in the opening, overlapping in the middle --
         nothing swings and nothing leaves the opening */
      ctx.save();
      ctx.translate(cx, cy);
      if (rotate) ctx.rotate(Math.PI / 2);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(-along / 2, -gap / 2, along, gap);
      ctx.fillStyle = this.LINE_COL;
      var t = Math.max(2.5, gap * 0.32), ov = along * 0.06;
      ctx.fillRect(-along / 2, -gap * 0.08 - t, along / 2 + ov, t);
      ctx.fillRect(-ov, gap * 0.08, along / 2 + ov, t);
      ctx.strokeStyle = this.LINE_COL;
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(-along / 2, -gap / 2); ctx.lineTo(-along / 2, gap / 2);
      ctx.moveTo(along / 2, -gap / 2); ctx.lineTo(along / 2, gap / 2);
      ctx.stroke();
      ctx.restore();
    },
    door: function (ctx, cx, cy, along, gap, sw, rotate) {
      ctx.save();
      ctx.translate(cx, cy);
      if (rotate) ctx.rotate(Math.PI / 2);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(-along / 2, -gap / 2, along, gap);
      ctx.strokeStyle = this.LINE_COL;
      ctx.lineWidth = 1.4;
      var hx = ((sw | 0) & 1) ? along / 2 : -along / 2;
      var sy = ((sw | 0) & 2) ? -1 : 1;
      var hy = sy * (gap / 2 - 1);
      ctx.beginPath();
      ctx.moveTo(hx, hy); ctx.lineTo(hx, hy - sy * along);
      ctx.stroke();
      ctx.beginPath();
      if (sy > 0) ctx.arc(hx, hy, along, -Math.PI / 2, ((sw | 0) & 1) ? -Math.PI : 0, !!((sw | 0) & 1));
      else ctx.arc(hx, hy, along, Math.PI / 2, ((sw | 0) & 1) ? Math.PI : 0, !((sw | 0) & 1));
      ctx.stroke();
      ctx.restore();
    }
  };


  /* ===================================================================
     THE UNDERLAY -- lifted from configurator-floorplan.js
     ===================================================================
     Upload a JPG or PNG, place it, calibrate its scale and trace over it.
     The logic is not new and is not rewritten: it is the object the main
     house has been using, copied across as it stands. What kept it there was
     never the logic -- it was that its panel hangs off configurator-master
     .liquid, so any product on another template got a module that loaded and
     then had nowhere to put its panel.
     It needs three things from its host and nothing else: a logger, a 3D
     flush and a translator. Those are below, and this asset is not bound to
     any template, so every configurator can have it (Marek).
     STEP ONE OF THE MOVE: the object is HERE and exported, and nothing is
     wired to it yet -- a pure addition, so no product that already loads this
     asset behaves any differently today. */
  /* WHERE THE PANEL LIVES. The whole reason this worked in one product and
     nowhere else: it looks for #cf-wrap-base, which configurator-master.liquid
     provides and no other template does, and falls back to document.body --
     where it lands outside the configurator, unpositioned and effectively
     invisible. Asking for the configurator's own root first makes it at home on
     any template, and the old id still wins where it exists so the product that
     has been using it all along sees no change (Marek). */
  /* WHAT THE UNDERLAY ASKS ITS HOST. In the file it came from it read a global
     STATE that belonged to that product; here there is none, and a shared asset
     has no business reaching into a product's variables anyway. Four fields is
     all it wants -- how many floors, which view, metric or not, and which
     structural variant -- so the host says them, and each has an answer that is
     true for a product that does not have the field at all (Marek). */
  var _ulHostState = {
    floors: function () { try { return (_H && _H.getFloors) ? (_H.getFloors() | 0) : 1; } catch (e) { return 1; } },
    view: function () { try { return (_H && _H.getView) ? _H.getView() : '3d'; } catch (e) { return '3d'; } },
    isMetric: function () { try { return (_H && _H.isMetric) ? !!_H.isMetric() : true; } catch (e) { return true; } },
    variant: function () { try { return (_H && _H.getVariant) ? _H.getVariant() : null; } catch (e) { return null; } }
  };
  /* the object below was written against STATE.<field>; this reads the same way
     and costs the port no edits inside its 812 lines */
  var STATE = {
    get floors() { return _ulHostState.floors(); },
    get view() { return _ulHostState.view(); },
    get isMetric() { return _ulHostState.isMetric(); },
    get structureVariant() { return _ulHostState.variant(); }
  };
  function _ulHost() {
    return document.getElementById('cf-wrap-base')
        || document.getElementById('cf-root')
        || document.body;
  }
  function _ulLog(msg) {
    try {
      if (global.Configurator && global.Configurator.log && global.Configurator.log.log) {
        global.Configurator.log.log('[fp-underlay] ' + msg);
      }
    } catch (e) {}
  }
  function _ulFlush3D() {
    try { if (_H && typeof _H.flush3D === 'function') return _H.flush3D(); } catch (e) {}
  }
  function _ulT(k, f) {
    try { if (_H && typeof _H.t === 'function') return _H.t(k, f); } catch (e) {}
    return f;
  }
  var _fl = _ulLog, _forceRender3DFlush = _ulFlush3D, _optt = _ulT;

  var _Floorplan = {
    _bound: false, _img: null, _tex: null, _opacity: 0.30, _grp: null,
    _iconEl: null, _panelEl: null,
    _cx: null, _cz: null, _w: 0, _d: 0, _stage: 0,         /* transform + stage: 0 edit - 1 locked - 2 hidden */
    _gridGrp: null, _gridSig: null,                        /* scale grid overlay (editing stage) */
    _activeFloor: 0, _floors: {},                          /* per-floor plans (tied to STATE.floors) */

    _ensure: function () {
      if (this._bound) return;
      this._bound = true;
      this._injectIcon();
      this._bindDrag();
    },

    _injectIcon: function () {
      if (this._iconEl) return;
      var self = this;
      var winIcon = document.getElementById('cf-openings-icon');   /* sit ABOVE windows/doors */
      var anchor = winIcon ||
                   document.getElementById('cf-btn-share') ||
                   document.getElementById('cf-btn-ar') ||
                   document.getElementById('cf-btn-view') ||
                   document.getElementById('cf-btn-pdf');
      if (anchor && anchor.parentNode) {
        var cs = global.getComputedStyle ? global.getComputedStyle(anchor) : null;
        var b = document.createElement('button');
        b.type = 'button'; b.id = 'cf-floorplan-icon'; b.title = 'Floor plan underlay';
        var sz = anchor.offsetWidth || 44, hz = anchor.offsetHeight || 44;
        b.style.cssText =
          'width:' + sz + 'px;height:' + hz + 'px;padding:0;cursor:pointer;' +
          'display:flex;align-items:center;justify-content:center;color:#fff;' +     /* highlighted: black bg + white icon */
          (cs ? ('border-radius:' + cs.borderRadius + ';background:#2c2d2e;margin:' + cs.margin + ';box-shadow:' + (cs.boxShadow !== 'none' ? cs.boxShadow : '0 1px 4px rgba(0,0,0,.15)') + ';border:none;')
              : 'border-radius:12px;background:#2c2d2e;margin:6px 0;box-shadow:0 1px 4px rgba(0,0,0,.15);border:none;');
        b.innerHTML = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="1"/><line x1="3" y1="10" x2="14" y2="10"/><line x1="14" y1="3" x2="14" y2="21"/><line x1="14" y1="15" x2="21" y2="15"/></svg>';
        b.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); self._onIconClick(); });
        if (winIcon) anchor.parentNode.insertBefore(b, winIcon);
        else anchor.parentNode.insertBefore(b, anchor.nextSibling);
        this._iconEl = b;
        return;
      }
      var fb = document.createElement('button');
      fb.id = 'cf-floorplan-icon'; fb.title = 'Floor plan underlay';
      fb.style.cssText = 'position:fixed;right:18px;top:50%;transform:translateY(60px);z-index:9100;width:48px;height:48px;border-radius:50%;border:none;background:#3a3f45;color:#fff;box-shadow:0 3px 10px rgba(0,0,0,.3);cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:20px;';
      fb.textContent = '\u25A6';
      fb.addEventListener('click', function (e) { e.stopPropagation(); self._onIconClick(); });
      document.body.appendChild(fb);
      this._iconEl = fb;
    },

    _onIconClick: function () {
      /* With no underlay, open straight into the PARTITIONS stage so the whole
         floor-plan can be drawn from scratch -- the UPLOAD button still shows in
         every stage, so adding a tracing image stays optional. Only nudge when
         opening (not closing) and only from the default/upload stage. */
      var opening = !(this._panelEl && this._panelEl.style.display !== 'none');
      if (opening && !this._img && (this._stage | 0) === 0) this._stage = 1;
      if (opening) this._ensureFloorplan2D(true);             /* opening the panel -> 2D plan of floor 1 */
      this._togglePanel();                                    /* open the panel -- uploading an underlay is optional, partitions work without one */
    },

    _pickFile: function () {
      var self = this;
      var inp = document.createElement('input');
      inp.type = 'file';
      inp.accept = 'image/png,image/jpeg';
      inp.style.display = 'none';
      inp.addEventListener('change', function () {
        var f = inp.files && inp.files[0];
        if (f) {
          var rd = new FileReader();
          rd.onload = function () { self._loadImage(rd.result); };
          rd.readAsDataURL(f);
        }
        if (inp.parentNode) inp.parentNode.removeChild(inp);
      });
      document.body.appendChild(inp);
      inp.click();
    },

    _loadImage: function (dataUrl) {
      var self = this, T = global.THREE;
      _fl('loadImage: ' + (dataUrl ? (dataUrl.slice(0, 24) + '... ' + Math.round(dataUrl.length / 1024) + ' kB') : 'NO DATA'));
      var im = new Image();
      im.onerror = function () { _fl('loadImage: the browser refused the image'); };
      im.onload = function () {
        _fl('loadImage: decoded ' + im.width + 'x' + im.height);
        self._img = im;
        if (self._tex) { try { self._tex.dispose(); } catch (e) {} }
        self._tex = new T.Texture(im);
        if (T.sRGBEncoding !== undefined) self._tex.encoding = T.sRGBEncoding;
        self._tex.needsUpdate = true;
        self._cx = null; self._cz = null; self._stage = 0;                /* new image re-centres, editable */
        if (STATE.view !== '2d') { try { toggleView(); } catch (e) {} }   /* a plan needs the top view */
        try { self.render(); _fl('loadImage: render done, grp=' + (self._grp ? self._grp.children.length + ' children' : 'NO GROUP')); }
        catch (e) { _fl('loadImage: render THREW ' + (e && e.message ? e.message : e)); }
        try { self._showPanel(); } catch (e2) { _fl('loadImage: showPanel THREW ' + (e2 && e2.message ? e2.message : e2)); }
        try { self._save(); } catch (e3) { _fl('loadImage: save THREW ' + (e3 && e3.message ? e3.message : e3)); }
        if (typeof _forceRender3DFlush === 'function') _forceRender3DFlush(2);
        _fl('loadImage: done, stage=' + self._stage + ' w=' + self._w.toFixed(2) + ' d=' + self._d.toFixed(2) +
            ' cx=' + self._cx + ' cz=' + self._cz);
      };
      im.src = dataUrl;
    },

    /* ---- per-floor plans: the flat fields above hold the active floor's live
       state; switching floors saves the current one and loads the target. The
       floor switcher in the panel is tied to STATE.floors so each storey gets
       its own underlay. ---- */
    _saveActiveFloor: function () {
      this._floors[this._activeFloor] = {
        img: this._img, tex: this._tex,
        cx: this._cx, cz: this._cz, w: this._w, d: this._d, stage: this._stage
      };
    },
    _applyFloorState: function (idx) {
      var f = this._floors[idx];
      if (f) {
        this._img = f.img; this._tex = f.tex;
        this._cx = f.cx; this._cz = f.cz; this._w = f.w; this._d = f.d;
        /* _stage stays GLOBAL -- switching floors keeps the current stage (e.g. CUT)
           so a cut/partition can be placed on any floor without dropping to stage 0 */
      } else {
        this._img = null; this._tex = null;
        this._cx = null; this._cz = null; this._w = 0; this._d = 0;
      }
    },
    _setFloor: function (idx) {
      var flN = Math.max(1, STATE.floors | 0);
      idx = Math.max(0, Math.min(flN - 1, idx | 0));
      if (idx === this._activeFloor) return;
      this._saveActiveFloor();
      this._activeFloor = idx;
      this._applyFloorState(idx);
      this.render();
      try { _Partitions.render(); } catch (e) {}                 /* swap partitions/plan to the new floor -- otherwise the previous floor's walls linger */
      try { _Balcony.render(); } catch (e) {}                    /* swap the balcony ghost to the new floor too */
      try { _Balcony.buildWalls(); } catch (e) {}                /* rebuild the 3D balcony walls */
      this._showPanel();
      this._save();
      if (typeof _forceRender3DFlush === 'function') _forceRender3DFlush(2);
    },

    /* every entry into the floor-plan lands on the 2D top-down view. Only the
       toolbar icon (opening the panel) also resets to floor 1; the banner stage
       icon keeps the current floor so a cut can be cycled into on floor 2/3. */
    _ensureFloorplan2D: function (forceFloor1) {
      if (STATE.view !== '2d' && typeof toggleView === 'function') { try { toggleView(); } catch (e) {} }
      if (forceFloor1 && (this._activeFloor | 0) !== 0) { try { this._setFloor(0); } catch (e) {} }
    },
    _initTransform: function () {
      var poly = (typeof getFootprintPolygon === 'function') ? getFootprintPolygon() : null;
      var minX = -5, maxX = 5, minZ = -5, maxZ = 5;
      if (poly && poly.length >= 3) {
        minX = Infinity; maxX = -Infinity; minZ = Infinity; maxZ = -Infinity;
        for (var i = 0; i < poly.length; i++) {
          var p = poly[i];
          if (p[0] < minX) minX = p[0]; if (p[0] > maxX) maxX = p[0];
          if (p[1] < minZ) minZ = p[1]; if (p[1] > maxZ) maxZ = p[1];
        }
      }
      var bw = Math.max(2, maxX - minX), bd = Math.max(2, maxZ - minZ);
      var iw = this._img.naturalWidth || this._img.width || 1;
      var ih = this._img.naturalHeight || this._img.height || 1;
      var ar = iw / ih, bar = bw / bd;
      if (ar > bar) { this._w = bw; this._d = bw / ar; } else { this._d = bd; this._w = bd * ar; }
      this._cx = (minX + maxX) / 2; this._cz = (minZ + maxZ) / 2;
    },

    _corners: function () {
      var hw = this._w / 2, hd = this._d / 2;
      return [
        [this._cx - hw, this._cz - hd],
        [this._cx + hw, this._cz - hd],
        [this._cx + hw, this._cz + hd],
        [this._cx - hw, this._cz + hd]
      ];
    },
    _cornerAt: function (g) {
      var cs = this._corners(), r = Math.max(0.45, Math.min(this._w, this._d) * 0.12);
      for (var k = 0; k < 4; k++) {
        var dx = g.x - cs[k][0], dz = g.z - cs[k][1];
        if (dx * dx + dz * dz <= r * r) return k;
      }
      return -1;
    },
    /* the stage icon sits exactly on the coordinate origin (0,0) -- its centre on both axes */
    _iconPos: function () { return [0, 0]; },
    _hitLock: function (g) {
      var ip = this._iconPos(), dx = g.x - ip[0], dz = g.z - ip[1], r = 0.62;
      return (dx * dx + dz * dz) <= r * r;
    },
    /* centre icon, three stages, styled like #cf-btn-dispo-lock (rounded-square
       button + white glyph): 0 edit = black bg + padlock - 1 locked = red bg +
       padlock - 2 hidden = blue bg + eye. */
    _stageIcon: function (stage) {
      var T = global.THREE;
      var sz = 128, cv = document.createElement('canvas'); cv.width = cv.height = sz;
      var ctx = cv.getContext('2d');
      function rr(x, y, w, h, r) {
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + w, y, x + w, y + h, r);
        ctx.arcTo(x + w, y + h, x, y + h, r);
        ctx.arcTo(x, y + h, x, y, r);
        ctx.arcTo(x, y, x + w, y, r);
        ctx.closePath();
      }
      /* rounded-square button background (44px / radius 11px) */
      rr(2, 2, sz - 4, sz - 4, sz * (11 / 44));
      ctx.fillStyle = stage === 1 ? '#16a34a' : stage === 2 ? '#c2410c' : stage === 3 ? '#2f6fed' : '#2c2d2e';
      ctx.fill();
      /* white glyph, 22px icon centred in the 44px button */
      var span = sz * (22 / 44), s = span / 24, ox = (sz - span) / 2, oy = (sz - span) / 2;
      function X(v) { return ox + v * s; }
      function Y(v) { return oy + v * s; }
      ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 2 * s; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      if (stage === 3) {
        /* eye -- finished VIEW (floor-plan picture hidden, walls + rooms shown); tap cycles back to unlock */
        ctx.beginPath();
        ctx.moveTo(X(2), Y(12));
        ctx.quadraticCurveTo(X(12), Y(5), X(22), Y(12));
        ctx.quadraticCurveTo(X(12), Y(19), X(2), Y(12));
        ctx.closePath(); ctx.stroke();
        ctx.beginPath(); ctx.arc(X(12), Y(12), 3.1 * s, 0, 2 * Math.PI); ctx.stroke();
      } else if (stage === 2) {
        /* door swing symbol -- DOORS stage (place interior doors on the finished plan) */
        ctx.beginPath(); ctx.moveTo(X(5), Y(20)); ctx.lineTo(X(5), Y(7)); ctx.stroke();   /* jamb */
        ctx.beginPath(); ctx.moveTo(X(5), Y(7)); ctx.lineTo(X(16), Y(7)); ctx.stroke();    /* leaf (open) */
        ctx.beginPath(); ctx.arc(X(5), Y(7), 11 * s, 0, Math.PI / 2); ctx.stroke();        /* swing arc */
      } else if (stage === 1) {
        /* pencil -- EDIT partitions */
        ctx.beginPath();
        ctx.moveTo(X(14), Y(4));
        ctx.lineTo(X(20), Y(10));
        ctx.lineTo(X(9), Y(21));
        ctx.lineTo(X(3), Y(15));
        ctx.closePath(); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(X(12), Y(6)); ctx.lineTo(X(18), Y(12)); ctx.stroke();   /* eraser separator */
      } else {
        /* open padlock -- UNLOCK stage (position the underlay); shackle's right leg lifted */
        rr(X(5), Y(11), 14 * s, 10 * s, 2 * s); ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(X(8), Y(11));
        ctx.lineTo(X(8), Y(7));
        ctx.arc(X(12), Y(7), 4 * s, Math.PI, 2 * Math.PI);
        ctx.stroke();
      }
      var tex = new T.CanvasTexture(cv);
      tex.minFilter = T.LinearFilter; tex.magFilter = T.LinearFilter;
      if ('sRGBEncoding' in T) tex.encoding = T.sRGBEncoding;
      var mat = new T.SpriteMaterial({ map: tex, transparent: true, depthTest: false, depthWrite: false });
      var spr = new T.Sprite(mat);
      spr.renderOrder = 16;
      spr.scale.set(1.2, 1.2, 1);       /* bigger than before */
      spr.userData = { floorplanLock: true };
      return spr;
    },

    /* (re)build the underlay plane + corner handles -- 2D top view only (it's a
       plan, hidden in 3D). Called on every scene build so it survives rebuilds. */
    render: function () {
      var T = global.THREE, eng = global.BaseEngine;
      if (!T || !eng || !eng.sceneGroup) {
        _fl('render bailed: THREE=' + (!!T) + ' engine=' + (!!eng) +
            ' sceneGroup=' + (!!(eng && eng.sceneGroup)) + '  -- nothing will draw');
        return;
      }
      if (this._grp) { if (this._grp.parent) this._grp.parent.remove(this._grp); this._grp = null; }
      if (this._activeFloor >= Math.max(1, STATE.floors | 0)) this._activeFloor = Math.max(0, (STATE.floors | 0) - 1);
      /* with the lock stage gone the underlay is locked in edit + view anyway, so hide the thin house lines whenever a plan is loaded */
      this._setHouse2DLinesHidden(!!this._img && !!this._tex && STATE.view === '2d');
      this._ensureGrid();
      if (!this._img || !this._tex || STATE.view !== '2d') return;
      if (this._cx == null) this._initTransform();
      var grp = new T.Group();
      /* the plan image -- visible in unlock (0) / edit (1), hidden in doors (2) + view (3) where the finished plan shows */
      if (this._stage < 2) {
        var mat = new T.MeshBasicMaterial({ map: this._tex, transparent: true, opacity: this._opacity, side: T.DoubleSide, depthWrite: false });
        var plane = new T.Mesh(new T.PlaneGeometry(this._w, this._d), mat);
        plane.rotation.x = -Math.PI / 2;
        plane.position.set(this._cx, 0.07, this._cz);
        plane.renderOrder = 1;
        plane.userData = { floorplan: true };
        grp.add(plane);
      }
      /* corner resize handles -- small red dots, only while editing (stage 0) */
      if (this._stage === 0) {
        var hr = Math.max(0.12, Math.min(0.25, Math.min(this._w, this._d) * 0.025));
        var cs = this._corners();
        for (var k = 0; k < 4; k++) {
          var hmat = new T.MeshBasicMaterial({ color: 0xe5392e, transparent: true, opacity: 0.95, side: T.DoubleSide, depthWrite: false, depthTest: false });
          var h = new T.Mesh(new T.CircleGeometry(hr, 24), hmat);
          h.rotation.x = -Math.PI / 2;
          h.position.set(cs[k][0], 0.09, cs[k][1]);
          h.renderOrder = 14;
          h.userData = { floorplanHandle: k };
          grp.add(h);
        }
      }
      eng.sceneGroup.add(grp);
      this._grp = grp;
    },

    /* move only -- nudge plane + corner dots + lock without a full rebuild */
    _applyTransform: function () {
      if (this._grp) {
        var cs = this._corners(), cx = this._cx, cz = this._cz;
        this._grp.children.forEach(function (o) {
          var u = o.userData || {};
          if (u.floorplan) o.position.set(cx, 0.07, cz);
          else if (typeof u.floorplanHandle === 'number') o.position.set(cs[u.floorplanHandle][0], 0.09, cs[u.floorplanHandle][1]);
          else if (u.floorplanLock) o.position.set(cx, 0.12, cz);
        });
      }
      if (typeof _forceRender3DFlush === 'function') _forceRender3DFlush(0);
    },

    /* ---- scale grid overlay (editing stage): bolder lines + numeric labels so
       the user can size the underlay to real proportions. Stable ruler keyed to
       the footprint (not the layer), so it doesn't jump while dragging. Metric:
       every 2 m -> 0,2,4..m. Imperial: every 5 ft -> 0,5,10..ft. ---- */
    _wallThk: function () { return (global.BASE_BOOT && +global.BASE_BOOT.wallThickness) || 0.30; },
    /* perimeter wall thickness for the frame: masonry follows the actual block
       width (mc:grid.brick.w), other structure types use the master wall thk.
       Facade / insulation layers are intentionally ignored here. */
    _perimThk: function () {
      if ((STATE.structureVariant || 'masonry') === 'masonry') {
        try {
          var g = JSON.parse(localStorage.getItem('mc:grid') || 'null');
          if (g && g.brick && g.brick.w > 0) {
            var t = g.metric ? (g.brick.w / 1000) : (g.brick.w * 0.0254);
            if (t > 0) return t;
          }
        } catch (e) {}
      }
      return this._wallThk();
    },
    _gridSignature: function () {
      var poly = (typeof getFootprintPolygon === 'function') ? getFootprintPolygon() : null;
      var s = (STATE.isMetric !== false ? 'm' : 'i') + '|' + (STATE.structureVariant || 'masonry') + '|t' + Math.round(this._perimThk() * 1000);
      if (poly && poly.length >= 3) {
        for (var i = 0; i < poly.length; i++) s += '|' + Math.round(poly[i][0] * 20) + ',' + Math.round(poly[i][1] * 20);
      }
      return s;
    },
    /* offset the footprint polygon by d (>0 = outward) -- per-edge shift along the
       outward normal (point-in-polygon test, robust for concave U/L/T/H), with
       mitred corners from intersecting adjacent offset edges. */
    _offsetPoly: function (poly, d) {
      var n = poly.length;
      if (n < 3) return poly.slice();
      function inside(px, pz) {
        var c = false;
        for (var i = 0, j = n - 1; i < n; j = i++) {
          var xi = poly[i][0], zi = poly[i][1], xj = poly[j][0], zj = poly[j][1];
          if (((zi > pz) !== (zj > pz)) && (px < (xj - xi) * (pz - zi) / ((zj - zi) || 1e-9) + xi)) c = !c;
        }
        return c;
      }
      var ed = [];
      for (var e = 0; e < n; e++) {
        var a = poly[e], b = poly[(e + 1) % n];
        var dx = b[0] - a[0], dz = b[1] - a[1], L = Math.hypot(dx, dz) || 1e-9;
        var ux = dx / L, uz = dz / L, nx = -uz, nz = ux;
        var mx = (a[0] + b[0]) / 2, mz = (a[1] + b[1]) / 2;
        if (inside(mx + nx * 0.05, mz + nz * 0.05)) { nx = -nx; nz = -nz; }
        ed.push({ px: a[0] + nx * d, pz: a[1] + nz * d, ux: ux, uz: uz });
      }
      var out = [];
      for (var v = 0; v < n; v++) {
        var e0 = ed[(v - 1 + n) % n], e1 = ed[v];
        var den = e0.ux * (-e1.uz) - e0.uz * (-e1.ux);
        if (Math.abs(den) < 1e-9) { out.push([poly[v][0], poly[v][1]]); continue; }   /* collinear -> keep */
        var t = ((e1.px - e0.px) * (-e1.uz) - (e1.pz - e0.pz) * (-e1.ux)) / den;
        out.push([e0.px + e0.ux * t, e0.pz + e0.uz * t]);
      }
      return out;
    },

    _removeGrid: function () {
      if (this._gridGrp) {
        if (this._gridGrp.parent) this._gridGrp.parent.remove(this._gridGrp);
        this._gridGrp = null;
      }
      this._gridSig = null;
    },

    _ensureGrid: function () {
      var eng = global.BaseEngine;
      if (!eng || !eng.sceneGroup) return;
      if (!this._img || STATE.view !== '2d' || (this._stage !== 0 && this._stage !== 1)) { this._removeGrid(); return; }
      var sig = this._gridSignature();
      if (this._gridGrp && this._gridGrp.parent && sig === this._gridSig) return;   /* up to date */
      this._removeGrid();
      this._gridGrp = this._buildGrid();
      this._gridSig = sig;
      if (this._gridGrp) eng.sceneGroup.add(this._gridGrp);
    },
    /* while editing the floor plan the house's own 2D outline + roof ridges are
       hidden, so the only outlines on screen are the clean overlay: dashed roof
       boundary + the wall-thickness frame (no confusing third centreline). */
    _setHouse2DLinesHidden: function (hide) {
      var eng = global.BaseEngine;
      if (!eng || !eng.sceneGroup) return;
      eng.sceneGroup.traverse(function (o) {
        if (o.userData && o.userData.house2DLine) o.visible = !hide;
      });
    },

    _textSprite: function (text) {
      var T = global.THREE;
      var fs = 40, pad = 8;
      var cv = document.createElement('canvas');
      var ctx = cv.getContext('2d');
      ctx.font = '700 ' + fs + 'px Montserrat, Arial, sans-serif';
      var tw = Math.ceil(ctx.measureText(text).width);
      cv.width = tw + pad * 2; cv.height = fs + pad * 2;
      ctx = cv.getContext('2d');
      ctx.font = '700 ' + fs + 'px Montserrat, Arial, sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.lineJoin = 'round'; ctx.lineWidth = 7; ctx.strokeStyle = 'rgba(255,255,255,0.92)';
      ctx.strokeText(text, cv.width / 2, cv.height / 2);
      ctx.fillStyle = '#374151';
      ctx.fillText(text, cv.width / 2, cv.height / 2);
      var tex = new T.CanvasTexture(cv);
      tex.minFilter = T.LinearFilter; tex.magFilter = T.LinearFilter;
      if ('sRGBEncoding' in T) tex.encoding = T.sRGBEncoding;
      var mat = new T.SpriteMaterial({ map: tex, transparent: true, depthTest: false, depthWrite: false });
      var spr = new T.Sprite(mat);
      spr.renderOrder = 13;
      var hM = 0.65;
      spr.scale.set(hM * (cv.width / cv.height), hM, 1);
      return spr;
    },

    _buildGrid: function () {
      var T = global.THREE, eng = global.BaseEngine, self = this;
      if (!T || !eng) return null;
      var metric = STATE.isMetric !== false;
      var stepW = metric ? 2.0 : 5 * FT;      /* world spacing between lines */
      var unitStep = metric ? 2 : 5;          /* numeric increment per step */
      var suffix = metric ? 'm' : 'ft';
      var poly = (typeof getFootprintPolygon === 'function') ? getFootprintPolygon() : null;
      var minX = -5, maxX = 5, minZ = -5, maxZ = 5;
      if (poly && poly.length >= 3) {
        minX = Infinity; maxX = -Infinity; minZ = Infinity; maxZ = -Infinity;
        for (var i = 0; i < poly.length; i++) {
          var p = poly[i];
          if (p[0] < minX) minX = p[0]; if (p[0] > maxX) maxX = p[0];
          if (p[1] < minZ) minZ = p[1]; if (p[1] > maxZ) maxZ = p[1];
        }
      }
      /* origin (0,0) = WORLD origin, exactly like the base GridHelper -- so the
         lines coincide with it and never shift when the footprint changes. The
         extent just reaches the farthest footprint corner from origin + margin
         (more lines appear at the edges, but every line stays put). */
      var cx0 = 0, cz0 = 0;
      var maxAbs = Math.max(Math.abs(minX), Math.abs(maxX), Math.abs(minZ), Math.abs(maxZ), 6);
      var n = Math.max(6, Math.ceil((maxAbs + 4) / stepW));   /* steps each side */
      var ext = n * stepW;
      var gMinX = -ext, gMaxX = ext, gMinZ = -ext, gMaxZ = ext;
      var grp = new T.Group();
      var y = 0.08, ix, iz, x, z;
      /* regular grid lines (lighter); central axes collected separately (bolder) */
      var pts = [], cpts = [];
      for (ix = -n; ix <= n; ix++) {
        x = cx0 + ix * stepW;
        if (ix === 0) cpts.push(x, y, gMinZ, x, y, gMaxZ); else pts.push(x, y, gMinZ, x, y, gMaxZ);
      }
      for (iz = -n; iz <= n; iz++) {
        z = cz0 + iz * stepW;
        if (iz === 0) cpts.push(gMinX, y, z, gMaxX, y, z); else pts.push(gMinX, y, z, gMaxX, y, z);
      }
      var geo = new T.BufferGeometry();
      geo.setAttribute('position', new T.Float32BufferAttribute(pts, 3));
      var lines = new T.LineSegments(geo, new T.LineBasicMaterial({ color: 0x9aa1ab, transparent: true, opacity: 0.7, depthTest: false, depthWrite: false }));
      lines.renderOrder = 11; grp.add(lines);
      var cgeo = new T.BufferGeometry();
      cgeo.setAttribute('position', new T.Float32BufferAttribute(cpts, 3));
      var caxes = new T.LineSegments(cgeo, new T.LineBasicMaterial({ color: 0x374151, transparent: true, opacity: 0.95, depthTest: false, depthWrite: false }));
      caxes.renderOrder = 12; grp.add(caxes);
      /* labels sit on the central axes: X ticks just below the horizontal axis,
         Z ticks just left of the vertical axis, "0" at the centre. */
      function label(text, wx, wz) {
        var spr = self._textSprite(text);
        if (spr) { spr.position.set(wx, 0.10, wz); grp.add(spr); }
      }
      var off = Math.min(0.6, stepW * 0.32);
      for (ix = -n; ix <= n; ix++) {
        if (ix !== 0) label(Math.abs(ix) * unitStep + suffix, cx0 + ix * stepW, cz0 - off);
      }
      for (iz = -n; iz <= n; iz++) {
        if (iz !== 0) label(Math.abs(iz) * unitStep + suffix, cx0 - off, cz0 + iz * stepW);
      }
      label('0', cx0 - off, cz0 - off);
      /* perimeter wall frame -- outer + inner face of the exterior walls (the
         footprint is their centreline). Partitions snap to the INNER face, so it
         is highlighted; a faint band shows the wall body. */
      var fpoly = (typeof getFootprintPolygon === 'function') ? getFootprintPolygon() : null;
      if (fpoly && fpoly.length >= 3) {
        var halfW = self._perimThk() / 2;
        var nn = fpoly.length, yf = 0.085;
        var loop = function (pts, col, ro, dashed) {
          var seg = [];
          for (var i = 0; i < pts.length; i++) {
            var a = pts[i], b = pts[(i + 1) % pts.length];
            seg.push(a[0], yf, a[1], b[0], yf, b[1]);
          }
          var g = new T.BufferGeometry();
          g.setAttribute('position', new T.Float32BufferAttribute(seg, 3));
          var m = dashed
            ? new T.LineDashedMaterial({ color: col, transparent: true, opacity: 0.9, dashSize: 0.4, gapSize: 0.28, depthTest: false, depthWrite: false })
            : new T.LineBasicMaterial({ color: col, transparent: true, opacity: 0.95, depthTest: false, depthWrite: false });
          var ln = new T.LineSegments(g, m);
          if (dashed) ln.computeLineDistances();
          ln.renderOrder = ro; grp.add(ln);
        };
        /* roof max boundary (eave + overhang) -- dashed, the outermost outline */
        var ov = (SHELTER_SPECS && SHELTER_SPECS.carport && SHELTER_SPECS.carport.overhangM) || 0.35;
        loop(self._offsetPoly(fpoly, halfW + ov), 0x8a7c68, 12, true);
        /* wall thickness frame -- outer + inner face + faint wall body */
        var outer = self._offsetPoly(fpoly, halfW), inner = self._offsetPoly(fpoly, -halfW);
        var fpos = [];
        for (var fi = 0; fi < nn; fi++) {
          var fj = (fi + 1) % nn, o0 = outer[fi], o1 = outer[fj], i0 = inner[fi], i1 = inner[fj];
          fpos.push(o0[0], 0.078, o0[1], o1[0], 0.078, o1[1], i1[0], 0.078, i1[1]);
          fpos.push(o0[0], 0.078, o0[1], i1[0], 0.078, i1[1], i0[0], 0.078, i0[1]);
        }
        var fg = new T.BufferGeometry();
        fg.setAttribute('position', new T.Float32BufferAttribute(fpos, 3));
        var fmesh = new T.Mesh(fg, new T.MeshBasicMaterial({ color: 0x5b6470, transparent: true, opacity: 0.20, side: T.DoubleSide, depthWrite: false, depthTest: false }));
        fmesh.renderOrder = 10; grp.add(fmesh);
        loop(outer, 0x4b5563, 13, false);     /* outer wall face -- grey */
        loop(inner, 0xE08A2B, 14, false);     /* inner wall face -- amber (partition snap target) */
      }
      return grp;
    },

    /* drag the layer (2D only, unlocked): grab a corner -> resize, grab the body
       -> move. Capture phase + orbit disabled so it doesn't fight pan/zoom. */
    _bindDrag: function () {
      var self = this, eng = global.BaseEngine;
      var canvas = eng && eng.renderer && eng.renderer.domElement;
      if (!canvas) { setTimeout(function () { self._bindDrag(); }, 300); return; }
      var T = global.THREE;
      var mode = null;                                                  /* 'move' | 'resize' */
      var downGx = 0, downGz = 0, baseCx = 0, baseCz = 0;               /* move */
      var fx = 0, fz = 0, w0 = 1, d0 = 1, asp = 1, sgnX = 1, sgnZ = 1;  /* resize */
      function ground(cx, cy) {
        var rect = canvas.getBoundingClientRect();
        var nx = ((cx - rect.left) / rect.width) * 2 - 1;
        var ny = -((cy - rect.top) / rect.height) * 2 + 1;
        var ray = new T.Raycaster();
        ray.setFromCamera({ x: nx, y: ny }, eng.camera);
        var pt = new T.Vector3();
        return ray.ray.intersectPlane(new T.Plane(new T.Vector3(0, 1, 0), -0.07), pt) ? pt : null;
      }
      canvas.addEventListener('pointerdown', function (ev) {
        if (STATE.view !== '2d' || !self._img) return;
        var g = ground(ev.clientX, ev.clientY); if (!g) return;
        if (self._stage !== 0) return;                                 /* only the unlock stage moves/resizes the underlay */
        var corner = self._cornerAt(g);
        if (corner >= 0) {                                             /* grab a corner -> resize */
          var cs = self._corners(), opp = cs[(corner + 2) % 4];
          fx = opp[0]; fz = opp[1]; w0 = self._w; d0 = self._d; asp = w0 / d0;
          sgnX = (cs[corner][0] >= fx) ? 1 : -1;
          sgnZ = (cs[corner][1] >= fz) ? 1 : -1;
          mode = 'resize';
          if (eng.controls) eng.controls.enabled = false;
          ev.stopPropagation();
          return;
        }
        if (Math.abs(g.x - self._cx) <= self._w / 2 && Math.abs(g.z - self._cz) <= self._d / 2) {
          mode = 'move'; downGx = g.x; downGz = g.z; baseCx = self._cx; baseCz = self._cz;
          if (eng.controls) eng.controls.enabled = false;
          ev.stopPropagation();
        }
      }, true);
      canvas.addEventListener('pointermove', function (ev) {
        if (!mode) return;
        var g = ground(ev.clientX, ev.clientY); if (!g) return;
        if (mode === 'move') {
          self._cx = baseCx + (g.x - downGx);
          self._cz = baseCz + (g.z - downGz);
          self._applyTransform();
        } else {                                                       /* uniform resize, opposite corner fixed */
          var dw = Math.abs(g.x - fx), dd = Math.abs(g.z - fz);
          var s = Math.max(dw / w0, dd / d0);
          var nw = w0 * s, nd = d0 * s;
          if (nw < 1) { nw = 1; nd = nw / asp; }
          self._w = nw; self._d = nd;
          self._cx = fx + sgnX * nw / 2;
          self._cz = fz + sgnZ * nd / 2;
          self.render();
        }
        ev.stopPropagation();
      }, true);
      function end() { if (mode) { mode = null; if (eng.controls) eng.controls.enabled = true; self._save(); } }
      canvas.addEventListener('pointerup', function () { end(); }, true);
      canvas.addEventListener('pointercancel', function () { end(); }, true);
    },

    remove: function () {
      this._img = null;
      if (this._tex) { try { this._tex.dispose(); } catch (e) {} this._tex = null; }
      if (this._grp) { if (this._grp.parent) this._grp.parent.remove(this._grp); this._grp = null; }
      this._removeGrid();
      this._cx = null; this._cz = null; this._stage = 0;   /* fresh upload re-centres */
      delete this._floors[this._activeFloor];                   /* drop this floor's plan */
      if (typeof _forceRender3DFlush === 'function') _forceRender3DFlush(2);
      if (Math.max(1, STATE.floors | 0) > 1) this._showPanel(); /* keep panel for floor switching */
      else this._hidePanel();
      this._save();
    },

    /* ---- persistence: per-floor underlay (image data-URL + transform + stage),
       saved per project+customer via ProjectStore so it survives refresh / device ---- */
    _save: function () {
      var self = this;
      if (this._saveTimer) clearTimeout(this._saveTimer);
      this._saveTimer = setTimeout(function () { self._saveNow(); }, 500);
    },
    _saveNow: function () {
      if (typeof _ProjectStore === 'undefined' || !_ProjectStore._initialized) return;
      this._saveActiveFloor();                                   /* flush live fields into _floors */
      var out = {}, k;
      for (k in this._floors) {
        var f = this._floors[k]; if (!f) continue;
        out[k] = { src: (f.img && f.img.src) ? f.img.src : null, cx: f.cx, cz: f.cz, w: f.w, d: f.d, stage: f.stage || 0 };
      }
      _ProjectStore.writeKey('house-floorplan', { v: _ProjectStore.SCHEMA_VERSION, floors: out, activeFloor: this._activeFloor | 0, updatedAt: new Date().toISOString() });
    },
    _restore: function () {
      if (typeof _ProjectStore === 'undefined' || !_ProjectStore._initialized) return;
      var data = _ProjectStore.read('house-floorplan');
      if (!data || !data.floors) return;
      var self = this, T = global.THREE, pending = 0, any = false;
      this._floors = this._floors || {};
      function mk(idx, rec) {
        var im = new Image(); pending++;
        im.onload = function () {
          var tex = new T.Texture(im);
          if (T.sRGBEncoding !== undefined) tex.encoding = T.sRGBEncoding;
          tex.needsUpdate = true;
          self._floors[idx] = { img: im, tex: tex, cx: rec.cx, cz: rec.cz, w: rec.w, d: rec.d, stage: rec.stage || 0 };
          if (--pending === 0) self._afterRestore(data.activeFloor | 0);
        };
        im.onerror = function () { if (--pending === 0) self._afterRestore(data.activeFloor | 0); };
        im.src = rec.src;
      }
      for (var k in data.floors) { var rec = data.floors[k]; if (rec && rec.src) { any = true; mk(k | 0, rec); } }
      if (!any) return;
    },
    _afterRestore: function (activeFloor) {
      var flN = Math.max(1, STATE.floors | 0);
      this._activeFloor = Math.max(0, Math.min(flN - 1, activeFloor | 0));
      this._applyFloorState(this._activeFloor);
      this.render();
      try { _Partitions.render(); } catch (e) {}
      try { _Balcony.render(); } catch (e) {}
      try { this._showPanel(); } catch (e) {}
      if (typeof _forceRender3DFlush === 'function') _forceRender3DFlush(2);
    },

    /* coloured glyph for the stage button in the banner (matches the old centre icon) */
    _stageGlyph: function (stage) {
      var bg = stage === 1 ? '#16a34a' : stage === 2 ? '#c2410c' : stage === 3 ? '#2f6fed' : stage === 4 ? '#0d9488' : '#7c3aed', g;
      if (stage === 1) g = '<path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>';                                  /* pencil */
      else if (stage === 2) g = '<path d="M4 21h16"/><path d="M6 21V4a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v17"/><path d="M15 21a9 9 0 0 0-3-7"/>'; /* door + swing */
      else if (stage === 3) g = '<path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/>';            /* eye */
      else if (stage === 4) g = '<path d="M3 8h18"/><path d="M3 8v12M21 8v12"/><path d="M3 20h18"/><path d="M8 8v12M13 8v12"/>';            /* balcony railing */
      else g = '<rect x="3" y="11" width="18" height="10" rx="2"/><path d="M7 11V7a5 5 0 0 1 9.5-2"/>';                                    /* open padlock */
      return { bg: bg, svg: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' + g + '</svg>' };
    },
    /* advance the underlay stage from the banner button: unlock -> partitions -> doors -> view -> balcony -> unlock */
    _cycleStage: function () {
      this._ensureFloorplan2D(false);                         /* force 2D, but KEEP the current floor (no jump back to floor 1) */
      this._stage = (this._stage + 1) % 5;
      this.render();
      try { _Partitions.render(); } catch (e) {}
      try { _Balcony.render(); } catch (e) {}
      this._showPanel();
      this._save();
      if (typeof _forceRender3DFlush === 'function') _forceRender3DFlush(0);
    },

    /* The panel container lives in configurator-master.liquid, absolutely
       positioned inside #cf-wrap-base exactly like the white size-bar -- so it
       shares the banner's layer and never scrolls or hides. We only set its top
       to sit 15px below the size-bar; offsets are relative to #cf-wrap-base, so
       they stay constant during scroll. */
    _positionPanel: function (p) {
      if (!p) return;
      /* under the size bar where there is one; otherwise a fixed step down from
         the top of whatever it was hung on, so it is on screen either way */
      var bar = document.querySelector('#cf-wrap-base .cf-size-bar')
             || document.querySelector('.cf-size-bar');
      if (bar) { p.style.top = (bar.offsetTop + bar.offsetHeight + 15) + 'px'; return; }
      var host = _ulHost();
      if (host === document.body) { p.style.position = 'fixed'; p.style.top = '90px'; }
      else { p.style.top = '90px'; }
    },

    _showPanel: function () {
      var self = this;
      var flN = Math.max(1, STATE.floors | 0);
      if (this._activeFloor >= flN) this._activeFloor = flN - 1;
      var hasImg = !!this._img;
      /* the container lives in configurator-master.liquid (same absolute layer as
         the white size-bar, so it never scrolls/hides). Fall back to building it
         inside #cf-wrap-base if an older liquid is still deployed. */
      var p = document.getElementById('cf-floorplan-panel');
      if (!p) {
        p = document.createElement('div');
        p.id = 'cf-floorplan-panel';
        p.style.cssText = 'position:absolute;top:112px;left:50%;transform:translateX(-50%);z-index:12;flex-wrap:wrap;align-items:center;justify-content:center;gap:8px;background:#2c2d2e;color:#fff;padding:7px 10px;border-radius:14px;box-shadow:0 4px 16px rgba(0,0,0,.28);font:700 12px Montserrat,sans-serif;max-width:calc(100vw - 40px);';
        _ulHost().appendChild(p);
      }
      p.innerHTML = '';

      /* stage icon (moved here from the scene) -- click cycles the stages */
      var ic = document.createElement('button'); ic.type = 'button'; ic.id = 'cf-fp-stage';
      var gly = this._stageGlyph(this._stage);
      ic.style.cssText = 'flex:0 0 auto;width:34px;height:34px;margin-right:8px;border:none;border-radius:10px;display:flex;align-items:center;justify-content:center;cursor:pointer;background:' + gly.bg + ';box-shadow:0 1px 4px rgba(0,0,0,.25);';
      ic.innerHTML = gly.svg;
      ic.addEventListener('click', function () { self._cycleStage(); });                  /* always step to the next stage -- an underlay is optional */
      p.appendChild(ic);

      /* short UPPER-CASE label per stage (locale keys, EN fallbacks) */
      var L4 = [_optt('mcFpDrag', 'DRAG TO MOVE'), _optt('mcFpEdit', 'PARTITIONS'), _optt('mcFpDoors', 'DOORS'), _optt('mcFpView', 'VIEW'), _optt('mcFpCut', 'CUT')];
      var lab = document.createElement('span');
      lab.textContent = (this._stage === 0 && !hasImg) ? _optt('mcFpUpload', 'UPLOAD') : (L4[this._stage] || L4[0]);
      lab.style.cssText = 'flex:0 0 auto;letter-spacing:.04em;color:#fff;';
      p.appendChild(lab);

      /* floor switcher. Outside the CUT stage it switches the active storey. In
         the CUT stage it sets the SCOPE of the active (selected) cut -- a storey,
         or ALL 1 / ALL 2 -- so each cut can behave differently. */
      if (flN > 1) {
        var inCut = (this._stage === 4);
        var ac = null; try { ac = inCut && _Balcony ? _Balcony._activeCut() : null; } catch (eAC) {}
        var acScope = ac ? (ac.mode === 'floor' ? (ac.floor | 0) : ac.mode) : null;
        var fr = document.createElement('span');
        fr.style.cssText = 'flex:0 0 auto;display:flex;gap:4px;background:rgba(255,255,255,.10);padding:3px;border-radius:999px;';
        for (var i = 0; i < flN; i++) {
          (function (idx) {
            var on = inCut ? (acScope === idx) : (idx === self._activeFloor);
            var has = (idx === self._activeFloor) ? hasImg : !!(self._floors[idx] && self._floors[idx].img);
            var b = document.createElement('button'); b.type = 'button';
            b.textContent = (idx + 1) + (has ? ' \u25cf' : '');
            b.style.cssText = 'border:none;border-radius:999px;padding:5px 10px;cursor:pointer;font:700 12px Montserrat,sans-serif;background:' + (on ? '#D2A878' : 'transparent') + ';color:' + (on ? '#1c1c1c' : '#fff') + ';';
            b.addEventListener('click', function () {
              if (inCut) { try { _Balcony._setActiveScope('floor', idx); } catch (e) {} }   /* active cut -> this storey */
              else self._setFloor(idx);
            });
            fr.appendChild(b);
          })(i);
        }
        if (inCut) {
          var mkAll = function (m, label) {
            var onA = (acScope === m);
            var ab = document.createElement('button'); ab.type = 'button';
            ab.textContent = label;
            ab.style.cssText = 'border:none;border-radius:999px;padding:5px 9px;cursor:pointer;font:700 11px Montserrat,sans-serif;letter-spacing:.02em;background:' + (onA ? (m === 'all2' ? '#f97316' : '#3b82f6') : 'transparent') + ';color:' + (onA ? '#fff' : '#fff') + ';';
            ab.addEventListener('click', function () { try { _Balcony._setActiveScope(m); } catch (e) {} });   /* active cut -> ALL 1 / ALL 2 */
            fr.appendChild(ab);
          };
          mkAll('all1', _optt('mcCutAll1', 'ALL 1'));
          mkAll('all2', _optt('mcCutAll2', 'ALL 2'));
        }
        p.appendChild(fr);
      }

      /* image actions: UPLOAD when empty; REPLACE + REMOVE only in the unlock stage */
      if (!hasImg) {
        var up = document.createElement('button'); up.type = 'button';
        up.textContent = _optt('mcFpUpload', 'UPLOAD');
        up.style.cssText = 'flex:0 0 auto;border:none;background:rgba(255,255,255,.16);color:#fff;border-radius:999px;padding:6px 11px;font:700 12px Montserrat,sans-serif;cursor:pointer;';
        up.addEventListener('click', function () { self._pickFile(); });
        p.appendChild(up);
      } else if (this._stage === 0) {
        var rep = document.createElement('button'); rep.type = 'button';
        rep.textContent = _optt('mcFpReplace', 'REPLACE');
        rep.style.cssText = 'flex:0 0 auto;border:none;background:rgba(255,255,255,.16);color:#fff;border-radius:999px;padding:6px 11px;font:700 12px Montserrat,sans-serif;cursor:pointer;';
        rep.addEventListener('click', function () { self._pickFile(); });
        p.appendChild(rep);
        var rem = document.createElement('button'); rem.type = 'button';
        rem.textContent = _optt('mcFpRemove', 'REMOVE');
        rem.style.cssText = 'flex:0 0 auto;border:none;background:#b5483a;color:#fff;border-radius:999px;padding:6px 11px;font:700 12px Montserrat,sans-serif;cursor:pointer;';
        rem.addEventListener('click', function () { self.remove(); });
        p.appendChild(rem);
      }

      /* dismiss (reopen via the floor-plan rail icon) */
      var cl = document.createElement('button'); cl.type = 'button'; cl.textContent = '\u2715';
      cl.style.cssText = 'flex:0 0 auto;border:none;background:transparent;color:#fff;font-size:15px;cursor:pointer;padding:2px 4px;';
      cl.addEventListener('click', function () { self._hidePanel(); });
      p.appendChild(cl);

      this._panelEl = p;
      p.style.display = 'flex';
      this._positionPanel(p);                              /* 15px below the white size-bar, in its own layer */
      try { _Partitions.render(); } catch (e) {}            /* show partition templates/walls if the panel opens on that stage */
      try { _Balcony.render(); } catch (e) {}              /* show the balcony ghost if we're on its stage */
      try { _Balcony.buildWalls(); } catch (e) {}          /* keep the 3D balcony walls in sync (covers stage entry) */
      if (typeof _forceRender3DFlush === 'function') { try { _forceRender3DFlush(0); } catch (e) {} }
      if (!this._panelResizeBound) {
        this._panelResizeBound = true;
        var fp = this;
        window.addEventListener('resize', function () { if (fp._panelEl) fp._positionPanel(fp._panelEl); });
      }
      setTimeout(function () { if (self._panelEl) self._positionPanel(self._panelEl); }, 200);
    },
    _hidePanel: function () { if (this._panelEl) this._panelEl.style.display = 'none'; try { _Balcony.render(); } catch (e) {} },
    _togglePanel: function () {
      if (this._panelEl && this._panelEl.style.display !== 'none') this._hidePanel();
      else this._showPanel();
    }
  };

  /* ===================================================================
     THE TRACING IMAGE -- no panel, no drawer, no second UI
     ===================================================================
     The ported underlay came with a whole panel of its own: stages, sliders,
     buttons. That is a second interface over a plan that already has one, and
     it is not what anybody wants -- you tap the button, you pick a file, and
     the picture is THERE, half transparent, with handles on it. Everything you
     do to it you do on the drawing itself.
     This owns the state and knows how to draw it and what is under a finger;
     the product draws it into its own plan and feeds it pointers, because the
     product is the one that knows where its plan is on screen (Marek). */
  var TRACE = {
    /* ARMED OR NOT, and it decides everything about who gets the finger.
       ARMED: the handles are drawn and they answer first -- the picture is
       being placed, and while that is what you are doing the walls can wait.
       LOCKED: the picture is scenery. No handles, nothing to hit, every touch
       goes to the plan as if there were no picture at all.
       It arms itself when it arrives and the product locks it again shortly
       after the last time it was touched, so there is never a moment where it
       is unclear which of the two has the gesture -- and no second button for
       a mode (Marek). */
    armed: false,
    arm: function (on) { this.armed = !!on && !!this.img; return this.armed; },
    img: null, cx: 0, cz: 0, w: 8, angle: 0, opacity: 0.10, gx: 0, gz: 0,
    /* THE HANDLES ARE THUMB-SIZED, ALWAYS. Sized in metres they grew with the
       picture and shrank with it -- huge on a big plan, unhittable on a small
       one, and the gap between them went the same way. They are placed and
       measured in PIXELS now; the scale of the last paint is remembered so a
       touch can be turned back into the same pixels (Marek). */
    HANDLE_R: 18,        /* drawn radius, px    */
    HANDLE_GAP: 30,      /* centre to centre, px */
    _ppm: 60,
    /* pick a file and take its aspect from the image itself */
    pick: function (onReady, opts) {
      var self = this;
      var inp = document.createElement('input');
      inp.type = 'file';
      inp.accept = 'image/*';
      if (opts && opts.camera) inp.setAttribute('capture', 'environment');
      inp.style.display = 'none';
      inp.addEventListener('change', function () {
        var f = inp.files && inp.files[0];
        if (!f) return;
        var rd = new FileReader();
        rd.onload = function (e) {
          var im = new Image();
          im.onload = function () {
            self.img = im;
            self.ar = (im.naturalHeight || 1) / (im.naturalWidth || 1);
            self.armed = true;          /* it arrives to be placed, so it arrives awake */
            if (typeof onReady === 'function') onReady();
          };
          im.src = e.target.result;
        };
        rd.readAsDataURL(f);
      });
      document.body.appendChild(inp);
      inp.click();
      setTimeout(function () { try { document.body.removeChild(inp); } catch (e) {} }, 0);
    },
    clear: function () { this.img = null; this.armed = false; },
    has: function () { return !!this.img; },
    depth: function () { return this.w * (this.ar || 0.75); },
    /* the four corners in plan coordinates, turned with the image */
    corners: function () {
      var hw = this.w / 2, hd = this.depth() / 2, c = Math.cos(this.angle), s2 = Math.sin(this.angle);
      var out = [], q, pts = [[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]];
      for (q = 0; q < 4; q++) {
        out.push([this.cx + pts[q][0] * c - pts[q][1] * s2,
                  this.cz + pts[q][0] * s2 + pts[q][1] * c]);
      }
      return out;
    },
    /* draw it under everything else; X and Y map plan metres to canvas pixels */
    draw: function (ctx, ppm, X, Y) {
      if (!this.img) return;
      this._ppm = ppm || 60;
      ctx.save();
      ctx.globalAlpha = this.opacity;
      ctx.translate(X(this.cx), Y(this.cz));
      ctx.rotate(this.angle);
      var wpx = this.w * ppm, hpx = this.depth() * ppm;
      ctx.drawImage(this.img, -wpx / 2, -hpx / 2, wpx, hpx);
      ctx.restore();
      /* WHERE THE PICTURE LANDS ON THE SCREEN, measured while the mapping is in
         hand. The opacity slider hangs off the bottom of it and travels with it
         through every pan and zoom, so it needs the picture's box in canvas
         pixels -- and this is the only place that knows it. The box is the
         AXIS-ALIGNED bounds of the four turned corners: a control that tilted
         with the picture would be a control nobody can read (Marek). */
      var cS = this.corners(), qS, sx0 = 1e9, sx1 = -1e9, sy1 = -1e9;
      for (qS = 0; qS < cS.length; qS++) {
        var pxS = X(cS[qS][0]), pyS = Y(cS[qS][1]);
        if (pxS < sx0) sx0 = pxS;
        if (pxS > sx1) sx1 = pxS;
        if (pyS > sy1) sy1 = pyS;
      }
      this._scr = { cx: (sx0 + sx1) / 2, bottom: sy1 };
      /* locked: the picture and nothing else -- no handles to argue with the
         drawing over it */
      if (!this.armed) return;
      /* the handles: a dot on each corner for size, and the turn button where
         a person looks for it -- top middle, above the picture */
      var cs = this.corners(), q;
      ctx.save();
      ctx.globalAlpha = 1;
      for (q = 0; q < 4; q++) {
        ctx.beginPath();
        ctx.arc(X(cs[q][0]), Y(cs[q][1]), 9, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        ctx.strokeStyle = '#1A1C1D';
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
      /* MOVE AND TURN SIT TOGETHER IN THE MIDDLE. Dragging anywhere inside the
         picture meant the picture answered every touch that landed on it -- and
         a plan is something you work ON TOP OF, so the walls under it could
         never be reached. It moves by its own handle now and by nothing else,
         which also means it can simply be left alone and traced over (Marek). */
      var mp = this.binPoint(), rp = this.rotPoint(), fp = this.freezePoint(), R = this.HANDLE_R;
      function chip(px, pz, col) {
        ctx.beginPath();
        ctx.arc(X(px), Y(pz), R, 0, Math.PI * 2);
        ctx.fillStyle = col || '#D2352C';
        ctx.fill();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.stroke();
      }
      /* NO MOVE HANDLE. The picture is moved BY THE PICTURE -- a finger
         anywhere on it, which is what a finger on a photograph does everywhere
         else. The handle was there because the plan underneath was competing
         for the same touch; while the picture is armed nothing else is
         listening, so there is nothing to compete with (Marek).
         THE BIN TOOK THAT SLOT, beside the turn button. It rode at the top of
         the screen for a version, the way a phone does it, and that was one
         floating thing too many for a plan: here it is part of the same little
         cluster, it appears and disappears with the rest of the handles, and it
         is only ever reachable while the picture is the thing being worked on.
         RED, and the other two black -- that is the way round this site reads:
         red is the one thing that cannot be undone, and turn and freeze are
         ordinary controls (Marek). */
      chip(mp[0], mp[1], '#D2352C');
      var bx = X(mp[0]), by = Y(mp[1]);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.8;
      ctx.beginPath();
      ctx.moveTo(bx - 6, by - 4); ctx.lineTo(bx + 6, by - 4);          /* the rim   */
      ctx.moveTo(bx - 2, by - 7); ctx.lineTo(bx + 2, by - 7);          /* the lid   */
      ctx.moveTo(bx - 4.5, by - 4); ctx.lineTo(bx - 3.6, by + 6.5);    /* the sides */
      ctx.lineTo(bx + 3.6, by + 6.5); ctx.lineTo(bx + 4.5, by - 4);
      ctx.moveTo(bx - 1.4, by - 1); ctx.lineTo(bx - 1.4, by + 4);      /* the slots */
      ctx.moveTo(bx + 1.4, by - 1); ctx.lineTo(bx + 1.4, by + 4);
      ctx.stroke();
      /* AND FREEZE, ON TOP OF THE TRIANGLE. The same switch as the one in the
         icon row, put where the hand already is: one tap and the picture is
         parked and the plan has its gestures back, without going up to the row
         for it. It only ever LOCKS -- unlocking is the row's button, because a
         frozen picture draws nothing to tap (Marek). */
      /* AND THE LOCK, ON TOP OF THE TRIANGLE. It was a snowflake and a
         snowflake is a metaphor -- a padlock is the thing itself, and everyone
         has already learned it. Closed, because it is an ACTION button sitting
         next to a bin and a turn arrow: pressing it LOCKS the picture. The one
         in the icon row shows the STATE instead, open while the picture can be
         moved, and it is the only way back (Marek). */
      chip(fp[0], fp[1], '#1A1C1D');
      var fx = X(fp[0]), fy = Y(fp[1]);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      /* the shackle, closed */
      ctx.arc(fx, fy - 1.5, 3.6, Math.PI, 0);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(fx - 3.6, fy - 1.5); ctx.lineTo(fx - 3.6, fy + 0.5);
      ctx.moveTo(fx + 3.6, fy - 1.5); ctx.lineTo(fx + 3.6, fy + 0.5);
      ctx.stroke();
      /* and the body */
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(fx - 5.4, fy + 0.5);
      ctx.lineTo(fx + 5.4, fy + 0.5);
      ctx.lineTo(fx + 5.4, fy + 6.5);
      ctx.lineTo(fx - 5.4, fy + 6.5);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      chip(rp[0], rp[1], '#1A1C1D');
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(X(rp[0]), Y(rp[1]), 6, -Math.PI * 0.8, Math.PI * 0.55);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(X(rp[0]) + 2, Y(rp[1]) - 8);
      ctx.lineTo(X(rp[0]) - 4, Y(rp[1]) - 6);
      ctx.lineTo(X(rp[0]) - 1, Y(rp[1]) - 1);
      ctx.stroke();
      ctx.restore();
    },
    /* the turn button sits above the top edge, in the middle of it */
    /* both handles in the middle, side by side, a fixed number of pixels apart */
    handleGap: function () { return this.HANDLE_GAP / (this._ppm || 60); },
    /* THE THREE BUTTONS SIT IN A TRIANGLE in the middle of the picture: FREEZE
       on top, throw it away at the lower left, turn it at the lower right. They
       were a row of two; the third one made a row too wide to take in at a
       glance, and a triangle reads as one cluster however big the picture is
       scaled (Marek). The radius is the same handle gap as before, so the chips
       sit further from each other than they did side by side. */
    freezePoint: function () { return [this.cx, this.cz - this.handleGap()]; },
    binPoint: function () { return [this.cx - this.handleGap() * 0.87, this.cz + this.handleGap() * 0.5]; },
    movePoint: function () { return this.binPoint(); },
    rotPoint: function () { return [this.cx + this.handleGap() * 0.87, this.cz + this.handleGap() * 0.5]; },
    /* what is under the finger: 'rot90' on the turn button, 'scale' on a
       corner, 'move' anywhere inside, null outside */
    /* ONLY THE HANDLES ANSWER. Everywhere else on the picture the touch passes
       through to whatever is drawn over it -- which is the whole point of a
       tracing image. */
    /* ---------- THE RULE ACROSS THE PLAN ----------------------------------
       A scale that rides on the picture turns and stretches with it, which is
       exactly what a scale must not do: the thing being measured cannot also be
       the ruler. So it is nailed to the CANVAS -- two axes out of the top left
       corner, across the whole viewport, still while the picture is pulled
       about underneath. Lay a kota from the scan against it, drag a corner
       until the two agree, and the underlay is to scale (Marek).

       It measures LENGTH, not position: both axes read zero at the corner. It
       belongs to the underlay, so it comes and goes with the handles, and it is
       drawn from the overlay rather than from drawFloor -- the sheet is drawn by
       the same call and a ruler has no business on a sheet.

       GRADUATION, and it is a real rule, not a row of identical marks: numbers
       on whole units only, a taller mark at the half, and the small ones at a
       tenth of a metre or an inch of a foot. When the zoom crowds them they are
       dropped a level at a time rather than all at once -- halves only, then
       plain units, then every fifth unit numbered. */
    /* HOW FAR IN THE CORNER SITS, and the two are not the same number. Hard
       against the edge the rule ran through the map and satellite chips along
       the top and through the whole left rail -- share, download, the gear.
       The left side carries more furniture than the top, so it is pushed in
       further (Marek). */
    /* ---------- HOW STRONGLY THE PICTURE SHOWS ----------------------------
       A scan is worth tracing at half strength, but not every scan: a pale old
       blueprint wants to come up, a dark photograph wants to go down until the
       walls drawn over it can be read.
       THE CONTROL IS THE ASSET'S OWN, markup and all -- the same way the cut
       handles are. A product that loads this file gets it by loading this file;
       there is nothing to wire, nothing in the liquid, and no second copy to
       drift (Marek). It appears with the handles and goes with them. */
    SLIDER_ID: 'cf-trace-opacity',
    /* THE SLIDER STAYS IN THE FLOORPLAN STEP (v116, Marek: posuvnik opacity v
       pudorysu, user si ho muze uplne zneviditelnit). It came only with the
       handles, after Trace was tapped, and ran 10 to 90 %; a picture laid
       under an imported plan could not be faded out of the way at all. It is
       there now whenever a picture lies under the plan and the Floorplan step
       is open, and it runs 0 to 100 % -- at 0 the picture is gone, the slider
       stays to bring it back. */
    _inPlanStep: function () {
      try { var C = global.HouseLib; return !!(C && typeof C.editing === 'function' && C.editing('layout')); } catch (e) { return false; }
    },
    ACCENT: '#c79a4a',
    TRACK:  '#E6E1D8',
    /* IT IS THE SAME SLIDER AS THE ROOF PITCH, and that is the whole
       requirement: same place, same track, same little dark bubble riding over
       the thumb with the number in it. Mine arrived as a white pill at the top
       of the screen -- a second kind of slider on one page, which is a thing a
       person has to learn twice (Marek).
       So the geometry is copied exactly from the pitch control: centred, 190
       wide, 216 px off the floor -- the row the pitch slider vacates on this
       step -- and the bubble sits above the thumb, moved with the value. */
    SLIDER_BOTTOM: 216,
    SLIDER_W: 190,
    _slPaint: function (inp, bub, root) {
      var lo = 0, hi = 100, v = parseFloat(inp.value);
      if (!isFinite(v)) v = 50;
      var f = (v - lo) / (hi - lo);
      inp.style.background = 'linear-gradient(to right,' + this.ACCENT + ' 0%,' + this.ACCENT + ' ' +
        (f * 100) + '%,' + this.TRACK + ' ' + (f * 100) + '%,' + this.TRACK + ' 100%)';
      if (!bub || !root) return;
      bub.textContent = Math.round(v) + '%';
      try {
        var rr = inp.getBoundingClientRect(), rt = root.getBoundingClientRect();
        if (!rr.width) return;
        bub.style.left = (rr.left - rt.left + rr.width * f) + 'px';
        bub.style.top = (rr.top - rt.top - 8) + 'px';
      } catch (e) {}
    },
    slider: function (root) {
      var doc = global.document, self = this;
      if (!doc) return;
      var rowId = this.SLIDER_ID + '-row', bubId = this.SLIDER_ID + '-bubble';
      var row = doc.getElementById(rowId), bub = doc.getElementById(bubId);
      if (!this.has() || !(this.armed || this._inPlanStep())) {
        if (row) row.style.display = 'none';
        if (bub) bub.style.display = 'none';
        return;
      }
      if (!root) return;
      if (!row) {
        row = doc.createElement('div');
        row.id = rowId;
        row.style.cssText = 'position:absolute;left:50%;transform:translateX(-50%);bottom:' +
          this.SLIDER_BOTTOM + 'px;width:' + this.SLIDER_W + 'px;display:flex;align-items:center;' +
          'justify-content:center;z-index:9;pointer-events:auto;';
        var inp = doc.createElement('input');
        inp.type = 'range'; inp.id = this.SLIDER_ID;
        inp.min = '0'; inp.max = '100'; inp.step = '5';
        inp.value = String(Math.round(this.opacity * 100));
        inp.className = 'cfg-ui-slider';
        inp.style.cssText = 'width:100%;margin:0;';
        row.appendChild(inp);
        root.appendChild(row);
        bub = doc.createElement('div');
        bub.id = bubId;
        bub.style.cssText = 'position:absolute;z-index:41;pointer-events:none;transform:translate(-50%,-100%);' +
          'background:#1c1d1f;color:#fff;font:700 12px/1 Montserrat,system-ui,sans-serif;' +
          'padding:6px 9px;border-radius:9px;box-shadow:0 3px 10px rgba(0,0,0,0.28);white-space:nowrap;';
        root.appendChild(bub);
        inp.addEventListener('input', function () {
          var v0 = parseFloat(inp.value);
          self.opacity = (isFinite(v0) ? v0 : 50) / 100;
          self._slPaint(inp, doc.getElementById(bubId), root);
          OVERLAY.repaint();
        });
        /* the plan underneath is listening for drags: a gesture that starts on
           this control belongs to this control */
        var stop = function (e) { e.stopPropagation(); };
        inp.addEventListener('pointerdown', stop);
        inp.addEventListener('touchstart', stop);
        row.addEventListener('pointerdown', stop);
      }
      row.style.display = 'flex';
      if (bub) bub.style.display = '';
      /* AND IT HANGS OFF THE PICTURE, 30 px under its bottom edge and centred
         on it -- so the control for how strongly the picture shows is beside
         the picture, and stays there while it is dragged and zoomed. It used
         to sit where the roof pitch slider sits, which is the right place for
         a control that belongs to the BUILDING; this one belongs to the
         underlay (Marek).
         Clamped to the viewport, because a picture pulled to the bottom of the
         screen would otherwise take its slider off the edge. */
      var box = this._scr;
      if (box && root) {
        var rw = root.clientWidth || 0, rh = root.clientHeight || 0;
        var lx = box.cx, ty = box.bottom + 30;
        if (rw) lx = Math.max(this.SLIDER_W / 2 + 12, Math.min(rw - this.SLIDER_W / 2 - 12, lx));
        if (rh) ty = Math.max(40, Math.min(rh - 44, ty));
        row.style.left = Math.round(lx) + 'px';
        row.style.top = Math.round(ty) + 'px';
        row.style.bottom = 'auto';
      }
      var live = doc.getElementById(this.SLIDER_ID);
      if (live) {
        if (doc.activeElement !== live) {
          var want = String(Math.round(this.opacity * 100));
          if (live.value !== want) live.value = want;
        }
        this._slPaint(live, bub, root);
      }
    },
    RULE_PAD_X: 126,
    RULE_PAD_Y: 76,
    RULE_COL: '#1A1C1D',
    rule: function (ctx, ppm, w, h) {
      if (!this.img || !this.armed || !ppm || !w || !h) return;
      var H = _PH;
      var metric = (H && typeof H.isMetric === 'function') ? !!H.isMetric() : true;
      var unit = metric ? 1 : 0.3048;
      var unitPx = unit * ppm;
      if (!(unitPx > 3)) return;
      var minorN = metric ? 10 : 12;               /* 10 cm, or one inch */
      var minors = (unitPx / minorN) >= 4.5;
      var halves = unitPx >= 24;
      var every  = (unitPx >= 24) ? 1 : Math.max(1, Math.ceil(30 / unitPx));
      var PX = this.RULE_PAD_X, PY = this.RULE_PAD_Y, self = this;
      ctx.save();
      ctx.lineCap = 'butt';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      /* every mark is laid down twice, white underneath, so the rule reads over
         a dark scan as well as over an empty plot */
      function line(x0, y0, x1, y1, wide) {
        ctx.strokeStyle = '#ffffff'; ctx.lineWidth = wide + 2.5;
        ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
        ctx.strokeStyle = self.RULE_COL; ctx.lineWidth = wide;
        ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
      }
      function label(t, x, y) {
        ctx.font = '600 10px Montserrat, Arial';
        ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 3;
        ctx.strokeText(t, x, y);
        ctx.fillStyle = self.RULE_COL;
        ctx.fillText(t, x, y);
      }
      /* the two rules themselves, out of the corner and off the edges */
      line(PX, PY, w - 6, PY, 1.4);
      line(PX, PY, PX, h - 6, 1.4);
      var i, u, at;
      /* ACROSS */
      for (i = 0; (at = PX + i * unitPx) <= w - 6; i++) {
        line(at, PY, at, PY + 12, 1.4);
        if (i % every === 0) label(String(i), at + 3, PY + 13);
        if (halves && PX + (i + 0.5) * unitPx <= w - 6) line(PX + (i + 0.5) * unitPx, PY, PX + (i + 0.5) * unitPx, PY + 8, 1.2);
        if (minors) {
          for (u = 1; u < minorN; u++) {
            if (u === minorN / 2) continue;
            var mx = PX + (i + u / minorN) * unitPx;
            if (mx <= w - 6) line(mx, PY, mx, PY + 4, 1);
          }
        }
      }
      /* AND DOWN, the same rule turned a quarter */
      for (i = 0; (at = PY + i * unitPx) <= h - 6; i++) {
        line(PX, at, PX + 12, at, 1.4);
        if (i % every === 0 && i > 0) label(String(i), PX + 13, at + 2);
        if (halves && PY + (i + 0.5) * unitPx <= h - 6) line(PX, PY + (i + 0.5) * unitPx, PX + 8, PY + (i + 0.5) * unitPx, 1.2);
        if (minors) {
          for (u = 1; u < minorN; u++) {
            if (u === minorN / 2) continue;
            var my = PY + (i + u / minorN) * unitPx;
            if (my <= h - 6) line(PX, my, PX + 4, my, 1);
          }
        }
      }
      /* the unit, once, in the corner both axes start from */
      ctx.font = '700 11px Montserrat, Arial';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';
      ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 3;
      ctx.strokeText(metric ? 'm' : 'ft', PX - 4, PY - 2);
      ctx.fillStyle = this.RULE_COL;
      ctx.fillText(metric ? 'm' : 'ft', PX - 4, PY - 2);
      ctx.restore();
    },
    /* is the point on the picture itself, turned as it is */
    inside: function (x, z) {
      if (!this.img) return false;
      var c = Math.cos(-this.angle), s2 = Math.sin(-this.angle);
      var dx = x - this.cx, dz = z - this.cz;
      var u = dx * c - dz * s2, v = dx * s2 + dz * c;
      return Math.abs(u) <= this.w / 2 && Math.abs(v) <= this.depth() / 2;
    },
    at: function (x, z) {
      if (!this.img || !this.armed) return null;
      /* the reach is a thumb, in pixels, whatever the picture is scaled to */
      var grab = (this.HANDLE_R + 6) / (this._ppm || 60);
      var rp = this.rotPoint(), bp = this.binPoint(), fp = this.freezePoint();
      if (Math.hypot(x - fp[0], z - fp[1]) <= grab) return 'freeze';
      if (Math.hypot(x - rp[0], z - rp[1]) <= grab) return 'rot90';
      if (Math.hypot(x - bp[0], z - bp[1]) <= grab) return 'bin';
      var cs = this.corners(), q;
      for (q = 0; q < 4; q++) {
        if (Math.hypot(x - cs[q][0], z - cs[q][1]) <= grab) return 'scale';
      }
      /* and anywhere on the picture is the picture */
      return this.inside(x, z) ? 'move' : null;
    },
    /* GRABBED WHERE THE FINGER LANDED. Setting the centre to the touch point
       made the picture jump to the finger on every tap -- the same fault the
       openings had. The distance from the touch to the centre is remembered
       when it is grabbed and carried while it moves (Marek). */
    grab: function (x, z) { this.gx = this.cx - x; this.gz = this.cz - z; },
    moveTo: function (x, z) { this.cx = x + this.gx; this.cz = z + this.gz; },
    /* A QUARTER AT A TIME. Everything in these plans is rectilinear, so a free
       angle is a way to end up almost square and never quite -- four steps and
       it always lines up (Marek). */
    turn90: function () { this.angle = (this.angle + Math.PI / 2) % (Math.PI * 2); },
    scaleTo: function (x, z) {
      var d = Math.hypot(x - this.cx, z - this.cz);
      var full = Math.hypot(this.w / 2, this.depth() / 2) || 1;
      this.w = Math.max(0.5, this.w * (d / full));
    },
    rotateTo: function () {}
  };

  /* ===================================================================
     THE SHARED UI -- glyphs and the button, once, for every configurator
     ===================================================================
     He is right and I kept getting this wrong: I built the icon, its glyphs and
     the swap-to-a-bin in the tiny house's own lib, which is exactly how the
     products drifted apart in the first place. A configurator should say WHICH
     button it is and nothing about how it looks.
     So the glyphs live here, the colours live here, and syncTraceButton() is
     the whole of what a product calls. Give it the button's title and it paints
     the right thing: red with an upload arrow while there is no picture, BLACK
     with a white cross once there is -- black because the change has to be
     visible at a glance, which a same-coloured swap is not (Marek). */
  var UI = {
    ICON_UPLOAD: '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>',
    ICON_CLEAR:  '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>',
    ICON_ROTATE: '<polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>',
    /* THE HANDLES SWITCH AND THE SCISSORS, ONCE. Each product kept its own
       copy of these two glyphs, which is how one control ends up looking
       different on two pages of the same site (Marek). */
    ICON_HANDLES: '<circle cx="6" cy="6" r="2"/><circle cx="18" cy="6" r="2"/><circle cx="6" cy="18" r="2"/><circle cx="18" cy="18" r="2"/><path d="M8 6h8M8 18h8M6 8v8M18 8v8"/>',
    ICON_SCISSORS: '<circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M20 4L8.12 15.88M14.47 14.48L20 20M8.12 8.12L12 12"/>',
    BG_ACTION: '#D2352C',      /* something to do        */
    BG_CLEAR:  '#1A1C1D',      /* something to undo      */
    svg: function (body) {
      return '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
             'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' + body + '</svg>';
    },
    /* the tracing button, painted for whatever state the picture is in */
    BG_IDLE:   '#9AA0A6',      /* something asleep       */
    /* ONE BUTTON, THREE STATES, and the colour is the whole of the language:
       RED with an upload arrow -- there is no picture, tap to fetch one.
       BLACK with a cross -- the picture is ARMED: its handles are live, and
         this tap throws it away.
       GREY with a cross -- the picture is locked: it lies under the drawing,
         answers nothing, and this tap wakes it up again.
       So a delete is never one stray tap away: the picture has to be woken
       first, which is the same tap you would make to move it anyway (Marek). */
    syncTraceButton: function (title) {
      try {
        var has = TRACE.has(), armed = has && TRACE.armed;
        var bs = document.querySelectorAll('button[title="' + title + '"]'), i;
        for (i = 0; i < bs.length; i++) {
          bs[i].innerHTML = this.svg(has ? this.ICON_CLEAR : this.ICON_UPLOAD);
          bs[i].style.background = has ? (armed ? this.BG_CLEAR : this.BG_IDLE) : this.BG_ACTION;
          bs[i].style.color = '#ffffff';
        }
      } catch (e) {}
    }
  };


  /* ===================================================================
     THE PLAN ITSELF -- FloorplanLib.plan
     ===================================================================
     The drawing moves here whole. It was 194 lines in one product and there
     are two more products with their own 194, all slightly different, which is
     the entire reason a sauna plan and a tiny plan never looked alike.
     It reads NOTHING about a product directly. Everything it needs -- the
     outline, the walls, the partitions, the doors, the rooms, the thicknesses,
     the units -- arrives through a host contract, so the asset never has to
     know what shape of building it is drawing. That was the thing that stopped
     the shared partition editor from ever being switched on, and it is avoided
     here by asking instead of reaching (Marek).

     Moving it does NOT make the codebase smaller today: the lines leave one
     product and arrive here. It gets smaller when the second and third product
     drop their own copies -- that is where the 2500 lines go. */
  var _PH = null;                       /* the plan host contract */
  function PLANSTEP(ppm) { return Math.max(16, Math.min(30, (ppm || 60) * 0.28)); }
  function INSET(P, d) {
    var out = [], n = P.length, i, H = _PH;
    for (i = 0; i < n; i++) {
      var pv = P[(i + n - 1) % n], cu = P[i], nx2 = P[(i + 1) % n];
      var e0x = cu[0] - pv[0], e0z = cu[1] - pv[1], l0 = Math.hypot(e0x, e0z) || 1;
      var e1x = nx2[0] - cu[0], e1z = nx2[1] - cu[1], l1 = Math.hypot(e1x, e1z) || 1;
      var n0x = -e0z / l0, n0z = e0x / l0, n1x = -e1z / l1, n1z = e1x / l1;
      if (!H.inPoly((pv[0] + cu[0]) / 2 + n0x * 0.02, (pv[1] + cu[1]) / 2 + n0z * 0.02, P)) { n0x = -n0x; n0z = -n0z; }
      if (!H.inPoly((cu[0] + nx2[0]) / 2 + n1x * 0.02, (cu[1] + nx2[1]) / 2 + n1z * 0.02, P)) { n1x = -n1x; n1z = -n1z; }
      out.push([cu[0] + (n0x + n1x) * d, cu[1] + (n0z + n1z) * d]);
    }
    return out;
  }
  function drawFloor(ctx, lvl, ppm, ox, oy) {
      var H = _PH; if (!H) return;
    var P = H.poly();
    if (!ctx || !P || P.length < 3 || !(ppm > 0)) return;
    /* THE WALL IS DRAWN AT ITS REAL THICKNESS, not at a drawing weight. A plan
       that shows 90 where the wall is 120 is a plan you cannot measure off, and
       the partitions had to guess where its face was. H.wallThk() is the one
       place that answers it, so when the facade layers arrive -- ventilated
       carries a batten cavity and its cladding, the contact build-up does not --
       the band grows OUTWARD from here and everything else follows (Marek). */
    var WALL = H.wallThk();
    /* AND WHAT LIES PAST THE OUTLINE (v168, core-v580 planOut; RANCH block
       house: the outline is the wall's AXIS and wallThk the half block inside
       it). The poche, the openings' breaks and the outline's koty are drawn
       that much further out, so the plan shows the whole wall and measures
       its outer faces; the rooms, the openings' places and everything the
       plan edits still go by the outline. 0 for every other product. */
    var OUT = 0;
    try { OUT = (H.planOut ? (+H.planOut() || 0) : 0); } catch (ePO) { OUT = 0; }
    if (!(OUT > 0 && OUT < 1)) OUT = 0;
    var mods = H.modules ? (H.modules() || []) : [];
    var m0 = mods[0] || {}, mx0 = m0.x || 0, mz0 = m0.z || 0;
    function X(v) { return ox + (v + mx0) * ppm; }
    function Y(v) { return oy + (v + mz0) * ppm; }
    var i, q;

    /* THE PICTURE GOES OVER THE PLAN (v131, Marek: obrazek by mel byt nad
       pudorysem, aby se s opacity sliderem dal zobrazit na 100 %; default
       opacity staci 10 %). It was drawn first, under everything, so at 100 %
       the rooms and walls still covered it. It is drawn last now, at the end
       of this function, faint by default and opaque at the slider's top.
       ON THE SCREEN ONLY (v132): not here at all any more -- drawFloor also
       draws the sheet and the PDF; the overlay draws the picture after it. */

    /* ---------- THE OUTDOOR AREAS -----------------------------------------
       A terrace, a deck, a porch: floor a person stands on, part of the plan
       and part of what is being bought, but with NO PERIMETER WALL. They read
       exactly like a room -- their own colour, their name, their area -- which
       is the whole point: on a plan the eye should not have to be told that the
       terrace is also a place (Marek).
       They are drawn FIRST so the building's poche lands on top of them where
       they meet, and they take no koty: their size is set by dragging their own
       frame, not read off the drawing. Every product hands them over the same
       way -- areas() in the contract, rects in the module's own frame. */
    var areas = (H.areas ? (H.areas() || []) : []), ai, aj;
    for (ai = 0; ai < areas.length; ai++) {
      var ar = areas[ai], arc = ar.rects || [];
      ctx.fillStyle = (ar.col || '#D2A878') + '66';
      for (aj = 0; aj < arc.length; aj++) {
        var ac = arc[aj];
        ctx.fillRect(X(ac.x0), Y(ac.z0), (ac.x1 - ac.x0) * ppm, (ac.z1 - ac.z0) * ppm);
      }
    }

    /* the rooms, one fill per ROOM -- an L with nothing in it is one room and
       gets one colour, which is what the seam in the decomposition was wrongly
       showing as two */
    var rooms = H.rooms(), rj, WG = [], wq9;
    /* THE CORNER PAST A SLANTED WALL IS THE ROOM IT OPENS INTO (v129): a
       rectangle a slanted wall cuts the corner off is filled without that
       corner, and the corner in its own room's colour after */
    try { WG = _impWedges(rooms) || []; } catch (eWG) { WG = []; }
    function wPath(Pg) {
      ctx.moveTo(X(Pg[0][0]), Y(Pg[0][1]));
      for (var j9 = 1; j9 < Pg.length; j9++) ctx.lineTo(X(Pg[j9][0]), Y(Pg[j9][1]));
      ctx.closePath();
    }
    for (i = 0; i < rooms.length; i++) {
      ctx.fillStyle = rooms[i].closet ? CLOSET_FILL : DRAW.roomFill(H.roomNameIdx(rooms[i], i));
      for (rj = 0; rj < rooms[i].rects.length; rj++) {
        var rc = rooms[i].rects[rj], cutW = [];
        for (wq9 = 0; wq9 < WG.length; wq9++) if (WG[wq9].to >= 0 && WG[wq9].from === i && WG[wq9].rect === rj) cutW.push(WG[wq9]);
        if (!cutW.length) { ctx.fillRect(X(rc.x0), Y(rc.z0), (rc.x1 - rc.x0) * ppm, (rc.z1 - rc.z0) * ppm); continue; }
        ctx.beginPath();
        ctx.rect(X(rc.x0), Y(rc.z0), (rc.x1 - rc.x0) * ppm, (rc.z1 - rc.z0) * ppm);
        for (wq9 = 0; wq9 < cutW.length; wq9++) wPath(cutW[wq9].poly);
        ctx.fill('evenodd');
      }
    }
    for (wq9 = 0; wq9 < WG.length; wq9++) {
      var wg9 = WG[wq9];
      if (wg9.to < 0 || !rooms[wg9.to]) continue;
      ctx.fillStyle = rooms[wg9.to].closet ? CLOSET_FILL : DRAW.roomFill(H.roomNameIdx(rooms[wg9.to], wg9.to));
      ctx.beginPath(); wPath(wg9.poly); ctx.fill();
    }
    /* VOIDS (v87, Marek: v miste kde je otvor nechej prazdny prostor): a
       product that sets PLAN.voids(fn) gets the rectangles fn() returns cut
       out of the room fills -- a stairwell on the storey above is empty
       floor, not a room and not a partition -- with a thin dashed edge */
    var VD = [];
    try { VD = _voidHook ? (_voidHook() || []) : []; } catch (eV) { VD = []; }
    for (i = 0; i < VD.length; i++) {
      var vd = VD[i];
      if (!vd || !(vd.x1 > vd.x0) || !(vd.z1 > vd.z0)) continue;
      ctx.clearRect(X(vd.x0), Y(vd.z0), (vd.x1 - vd.x0) * ppm, (vd.z1 - vd.z0) * ppm);
      ctx.save();
      ctx.strokeStyle = '#3B3B3B'; ctx.lineWidth = 1;
      if (ctx.setLineDash) ctx.setLineDash([5, 4]);
      ctx.strokeRect(X(vd.x0), Y(vd.z0), (vd.x1 - vd.x0) * ppm, (vd.z1 - vd.z0) * ppm);
      ctx.restore();
    }

    /* THE POCHE, GREY. Nearly black read as a border round the drawing rather
       than as a wall with a thickness; a mid grey sits back and lets the rooms
       and the partitions carry the plan. The band is stroked on the outline
       pulled HALF A THICKNESS INWARD, because the outline is the OUTER face --
       stroking the outline itself would put half the wall outside the building. */
    var innerLine = INSET(P, (WALL - OUT) / 2);
    ctx.strokeStyle = '#8A8F94';
    ctx.lineWidth = (WALL + OUT) * ppm;
    ctx.lineJoin = 'miter';
    ctx.beginPath();
    ctx.moveTo(X(innerLine[0][0]), Y(innerLine[0][1]));
    for (i = 1; i < innerLine.length; i++) ctx.lineTo(X(innerLine[i][0]), Y(innerLine[i][1]));
    ctx.closePath();
    ctx.stroke();

    /* the exterior openings, drawn by the shared asset -- one window symbol and
       one door symbol for every product on the site */
    var Do = DRAW, segs = H.wallSegs(), si, oi;
    for (si = 0; si < segs.length; si++) {
      var sg = segs[si], here = H.opsOn(sg.i);
      for (oi = 0; oi < here.length; oi++) {
        var op = here[oi];
        var cxw = sg.a[0] + sg.ux * op.off, czw = sg.a[1] + sg.uz * op.off;
        var vertO = Math.abs(sg.uz) > 0.5;
        var symO = Do.symbolOf(op);
        /* WHICH SIDE THE BUILDING IS ON. The symbol knows nothing about the
           plan, so it is told: step off the wall by a hand's width and ask the
           outline. On a vertical wall the symbol's own +y points at -x once it
           is turned, which is why the two cases probe different ways. */
        var intoO = vertO ? (H.inPoly(cxw - 0.15, czw, P) ? 1 : -1)
                          : (H.inPoly(cxw, czw + 0.15, P) ? 1 : -1);
        /* AND A DOOR THAT OPENS OUT HAS ITS LEAVES OUTSIDE (v149): the core
           marks it out (core-v567); its label goes in, away from the arcs */
        var swO = (op.out && Do && Do.isDoorSym(symO)) ? -intoO : intoO;
        /* v168: with a wall past the outline, the break spans the whole of it,
           centred on its middle (the building lies -x on a vertical wall when
           intoO is 1, +z on a horizontal one) */
        var cxB = cxw, czB = czw;
        if (OUT) { var shB = (WALL - OUT) / 2; if (vertO) cxB = cxw - intoO * shB; else czB = czw + intoO * shB; }
        if (Do) Do.opening(ctx, X(cxB), Y(czB), op.w * ppm, (WALL + OUT) * ppm + 2,
                           symO, vertO, swO);
        /* A DOOR IS LABELLED, NEVER KOTED -- a dot on the opening, a leader,
           the width, nothing behind it. It is stepped OUT of the building,
           because inside is where the leaf and its arc are (Marek's UX page). */
        if (Do && Do.isDoorSym(symO)) {
          /* the two axes take opposite signs: turning the symbol maps its own
             +y onto -x, so "away from the room" is +x on a vertical wall and
             -z on a horizontal one */
          var lxO = vertO ? (cxw + 0.55 * swO) : cxw;
          var lzO = vertO ? czw : (czw - 0.55 * swO);
          Do.note(ctx, { x: X(cxw), y: Y(czw) }, { x: X(lxO), y: Y(lzO) },
                  Do.fmtDoor(op.w, H.isMetric()));
        }
      }
    }

    /* the partitions, the sauna's weight and colour, clipped to the outline */
    var ps = H.partitions();
    ctx.strokeStyle = '#374151';
    ctx.lineWidth = Math.max(2, 0.10 * ppm);
    ctx.fillStyle = '#374151';
    for (i = 0; i < ps.length; i++) {
      var sp2 = H.partSpan(ps[i]);
      if (!sp2) continue;
      /* A ROOM LINE (v105): two rooms with no wall between them. It parts
         them on the plan -- each keeps its name, its colour and its area --
         and is drawn as what it is, a thin dashed line with nothing built */
      /* v129: a room line inside one room -- the corner past a slanted wall
         and the room it opens into -- is not drawn */
      if (ps[i].sep && _impSepInside(ps[i], sp2, rooms, WG)) continue;
      if (ps[i].sep) {
        ctx.save();
        if (ctx.setLineDash) ctx.setLineDash([Math.max(5, 0.14 * ppm), Math.max(4, 0.09 * ppm)]);
        ctx.strokeStyle = '#6B7280';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        if (ps[i].o === 'v') { ctx.moveTo(X(ps[i].c), Y(sp2[0])); ctx.lineTo(X(ps[i].c), Y(sp2[1])); }
        else { ctx.moveTo(X(sp2[0]), Y(ps[i].c)); ctx.lineTo(X(sp2[1]), Y(ps[i].c)); }
        ctx.stroke();
        ctx.restore();
        continue;
      }
      /* filled at its own thickness, so two crossing partitions overlap and the
         corner comes out solid without anything mitring it -- and INTERRUPTED
         at every door, because a doorway is a hole in a wall, not a mark on it */
      var gaps = H.doorGaps(ps[i]), cur = sp2[0], gi;
      /* v213: a wall of its own thickness (core-v815, DUPLEX's party wall) */
      var tkP = (typeof ps[i].t === 'number' && ps[i].t > 0) ? ps[i].t : H.partThk();
      function pslab(a, b) {
        if (b - a < 0.01) return;
        if (ps[i].o === 'v') ctx.fillRect(X(ps[i].c - tkP / 2), Y(a), tkP * ppm, (b - a) * ppm);
        else ctx.fillRect(X(a), Y(ps[i].c - tkP / 2), (b - a) * ppm, tkP * ppm);
      }
      for (gi = 0; gi < gaps.length; gi++) { pslab(cur, gaps[gi][0]); cur = Math.max(cur, gaps[gi][1]); }
      pslab(cur, sp2[1]);
    }
    /* the door symbol is the asset's -- one drawing for every product */
    var Dw = DRAW, DL = H.doorsLive(), dq;
    for (dq = 0; dq < DL.length; dq++) {
      var d2 = DL[dq].d, pw2 = DL[dq].p, dw2 = d2.w || H.doorW();
      var cxD = (pw2.o === 'v') ? pw2.c : d2.u;
      var czD = (pw2.o === 'v') ? d2.u : pw2.c;
      if (!Dw) continue;
      /* a doorway is jambs and nothing else -- there is no leaf to swing */
      if (d2.pass) {
        Dw.opening(ctx, X(cxD), Y(czD), dw2 * ppm, H.partThk() * ppm + 2,
                   Dw.SYM.PASS, pw2.o === 'v', 1);
      } else if (d2.kind === 'pair' && Dw.door2) {
        Dw.door2(ctx, X(cxD), Y(czD), dw2 * ppm, H.partThk() * ppm + 2, d2.sw | 0, pw2.o === 'v');
      } else if (d2.kind === 'bifold' && Dw.bifold) {
        Dw.bifold(ctx, X(cxD), Y(czD), dw2 * ppm, H.partThk() * ppm + 2, (d2.sw | 0) | ((dw2 > 1.1) ? 4 : 0), pw2.o === 'v');
      } else if (d2.kind === 'bypass' && Dw.bypass) {
        Dw.bypass(ctx, X(cxD), Y(czD), dw2 * ppm, H.partThk() * ppm + 2, pw2.o === 'v');
      } else if (d2.slide) {
        /* the slider marks are the ones the shell openings already use, so an
           interior slider and an exterior one are the same drawing */
        Dw.opening(ctx, X(cxD), Y(czD), dw2 * ppm, H.partThk() * ppm + 2,
                   (d2.slide === 3) ? Dw.SYM.SLIDE2 : Dw.SYM.SLIDE,
                   pw2.o === 'v', 1, (d2.slide === 2) ? 1 : 0);
      } else {
        Dw.door(ctx, X(cxD), Y(czD), dw2 * ppm, H.partThk() * ppm + 2, d2.sw | 0, pw2.o === 'v');
      }
    }
    /* AND THE GRIPS ON A DOORWAY, while the handles are up. Only a doorway
       gets them: a door's widths are the ones it is bought in, and those are
       walked with a tap rather than pulled to any size (Marek). */
    if (_handles()) {
      var GD = H.doorsLive(), gq;
      for (gq = 0; gq < GD.length; gq++) {
        var dG = GD[gq].d, pG = GD[gq].p;
        if (!dG.pass) continue;
        var wG2 = dG.w || H.doorW(), vG2 = (pG.o === 'v');
        var e0 = dG.u - wG2 / 2, e1 = dG.u + wG2 / 2;
        DRAW.grip(ctx, X(vG2 ? pG.c : e0), Y(vG2 ? e0 : pG.c));
        DRAW.grip(ctx, X(vG2 ? pG.c : e1), Y(vG2 ? e1 : pG.c));
      }
    }

    /* the fixtures, as their own drawings from above, inside the rooms */
    try { _drawFixtures(ctx, X, Y, ppm); } catch (eFx) {}
    /* and a product's own flights over them (v210) */
    try { _drawFlights(ctx, X, Y, ppm); } catch (eFl) {}

    /* THE OUTLINE'S OWN KOTY, drawn here at last. They were DOM chips with
       their own font, their own box and their own number format, floating over
       the canvas -- the last place in this product with a look of its own. Same
       routine as every other kot now, so the sheet and the screen and the other
       products all say it the same way. The step outward is the drawing's own,
       so they scale with the zoom instead of drifting (Marek). */
    var GAPo = PLANSTEP(ppm), eo;
    /* which of the outline's sizes stand on their limit -- the host knows its
       own maxima; without the hook nothing is ever red */
    var MX = null;
    try { MX = (H.maxDims ? H.maxDims() : null) || null; } catch (eMx) { MX = null; }
    /* ONE DIMENSION PER DIRECTION ON A RECTANGLE: the top edge for the width
       and the right edge for the length, not the same number on all four
       sides (Marek). Any other outline keeps a dimension on every edge. */
    /* v168: over the outer faces where the wall reaches past the outline */
    var PK = OUT ? INSET(P, -OUT) : P;
    var rectP = (PK.length === 4), topZ = null, rightX = null;
    if (rectP) {
      for (eo = 0; eo < 4; eo++) {
        var mZ = (PK[eo][1] + PK[(eo + 1) % 4][1]) / 2, mX = (PK[eo][0] + PK[(eo + 1) % 4][0]) / 2;
        if (topZ === null || mZ < topZ) topZ = mZ;
        if (rightX === null || mX > rightX) rightX = mX;
      }
    }
    for (eo = 0; eo < PK.length; eo++) {
      var aE = PK[eo], bE = PK[(eo + 1) % PK.length];
      var lenE = Math.hypot(bE[0] - aE[0], bE[1] - aE[1]);
      if (lenE < 0.05) continue;
      var horizE = Math.abs(bE[1] - aE[1]) < 1e-6;
      if (rectP) {
        var midZ = (aE[1] + bE[1]) / 2, midX = (aE[0] + bE[0]) / 2;
        if (horizE && Math.abs(midZ - topZ) > 1e-6) continue;
        if (!horizE && Math.abs(midX - rightX) > 1e-6) continue;
      }
      var nxE = horizE ? 0 : 1, nzE = horizE ? 1 : 0;
      var mxE = (aE[0] + bE[0]) / 2, mzE = (aE[1] + bE[1]) / 2;
      if (H.inPoly(mxE + nxE * 0.08, mzE + nzE * 0.08, PK)) { nxE = -nxE; nzE = -nzE; }
      var maxE = !!(MX && (horizE ? MX.w : MX.l));
      DRAW.kot(ctx, { x: X(aE[0]), y: Y(aE[1]) }, { x: X(bE[0]), y: Y(bE[1]) },
               nxE * GAPo * 1.6, nzE * GAPo * 1.6, DRAW.fmtLen(lenE, H.isMetric()), !horizE, maxE);
    }

    /* the koty of the wall in the hand, at both of its feet */
    if (H.dragPart() >= 0 && ps[H.dragPart()]) {
      var pD = ps[H.dragPart()], spD = H.partSpan(pD);
      if (spD) {
        var ends = [spD[0], spD[1]], q5;
        for (q5 = 0; q5 < ends.length; q5++) {
          /* INSIDE THE ROOMS, NOT OUT BY THE CORNER. The kot is stepped off the
             foot INTO the space it describes -- at the near end along the wall,
             at the far end back along it -- so each number sits in the room it
             belongs to instead of out beyond the building (Marek). */
          var at = ends[q5], ob = H.footObstacles(pD, at);
          var vD = (pD.o === 'v');
          var fLo = pD.c - H.partThk() / 2, fHi = pD.c + H.partThk() / 2;
          var into = (q5 === 0) ? 26 : -26;
          function ptD(across) { return vD ? { x: X(across), y: Y(at) } : { x: X(at), y: Y(across) }; }
          if (fLo - ob.lo > 0.05) {
            DRAW.kot(ctx, ptD(ob.lo), ptD(fLo), vD ? 0 : into, vD ? into : 0, DRAW.fmtLen(fLo - ob.lo, H.isMetric()), !vD);
          }
          if (ob.hi - fHi > 0.05) {
            DRAW.kot(ctx, ptD(fHi), ptD(ob.hi), vD ? 0 : into, vD ? into : 0, DRAW.fmtLen(ob.hi - fHi, H.isMetric()), !vD);
          }
        }
      }
    }

    /* THE DOOR'S OWN SIZE, WHILE DOORS ARE WHAT YOU ARE DOING. The container
       measures an opening the same way it measures anything else -- a kot
       across it, stepped off the wall, in the same grey and the same numbers --
       rather than a label stuck on the leaf. It shows while the door tool is
       up, which is when the width is the thing being decided (Marek). */
    /* and the label is the asset's too: a dot on the opening, a leader out to
       the side away from the swing, and the width in inches, nothing behind it */
    var DW = H.doorsLive(), wq;
    for (wq = 0; wq < DW.length; wq++) {
      var dW = DW[wq].d, pW = DW[wq].p, wWid = dW.w || H.doorW(), vW = (pW.o === 'v');
      var ccx = vW ? pW.c : dW.u, ccz = vW ? dW.u : pW.c;
      var openSide = ((dW.sw | 0) & 2) ? -1 : 1;   /* a doorway has no swing, so 0 reads as +1 */
      var lpx = vW ? (ccx - openSide * 0.55) : ccx;
      var lpz = vW ? ccz : (ccz + openSide * 0.55);
      if (Dw) Dw.note(ctx, { x: X(ccx), y: Y(ccz) }, { x: X(lpx), y: Y(lpz) },
                      Dw.fmtDoor(wWid, H.isMetric()));
    }

    /* AND THE SAME FOR A DOOR IN THE HAND. Moving a door, the question is not
       how wide it is -- that was chosen -- but how much wall is left on either
       side of it. Both gaps are measured to the ends of the stretch it is
       sliding in, so it can be set against a corner or centred by eye without
       anyone counting squares (Marek). */
    if (H.dragDoor()) {
      var pwG = null, gk;
      for (gk = 0; gk < ps.length; gk++) if (ps[gk].id === H.dragDoor().pid) pwG = ps[gk];
      if (pwG) {
        var segG = H.doorSeg(H.dragDoor().pid, H.dragDoor().u);
        if (segG) {
          var freeG = H.doorFree(H.dragDoor().pid, segG, H.dragDoor()), slotG = null;
          for (gk = 0; gk < freeG.length; gk++) {
            if (H.dragDoor().u >= freeG[gk][0] - 0.01 && H.dragDoor().u <= freeG[gk][1] + 0.01) { slotG = freeG[gk]; break; }
          }
          if (slotG) {
            var wG = H.dragDoor().w || H.doorW(), vG = (pwG.o === 'v');
            var a0G = H.dragDoor().u - wG / 2, a1G = H.dragDoor().u + wG / 2;
            function ptG(at) { return vG ? { x: X(pwG.c), y: Y(at) } : { x: X(at), y: Y(pwG.c) }; }
            var pushG = 18;
            if (a0G - slotG[0] > 0.02) DRAW.kot(ctx, ptG(slotG[0]), ptG(a0G), vG ? pushG : 0, vG ? 0 : -pushG, DRAW.fmtLen(a0G - slotG[0], H.isMetric()), vG);
            if (slotG[1] - a1G > 0.02) DRAW.kot(ctx, ptG(a1G), ptG(slotG[1]), vG ? pushG : 0, vG ? 0 : -pushG, DRAW.fmtLen(slotG[1] - a1G, H.isMetric()), vG);
          }
        }
      }
    }

    /* the name and the area, the sauna's two lines under each other */
    ctx.textAlign = 'center';
    ctx.fillStyle = '#111827';
    _nameBoxes = [];
    /* v143: each room by its own floor, the walls taken out */
    var NETA = [];
    try { NETA = _impNetAreas(rooms, WG) || []; } catch (eNA) { NETA = []; }
    for (i = 0; i < rooms.length; i++) {
      /* v129: with the corners past slanted walls that are its own, and
         without its own that are another room's */
      var arW = rooms[i].area + _impWedgeDelta(WG, i);
      var arG = arW;
      if (NETA[i] != null) arW = NETA[i];
      /* v143 (Marek: u nekterych mist nemame room name, ale mela by tam byt
         spon vymera): a closet, or a room too small for its name, by its
         area alone */
      if (arG < 0.8 || rooms[i].closet) { try { _impAreaOnly(ctx, rooms[i], arW, X, Y, ppm); } catch (eAO) {} ctx.textAlign = 'center'; ctx.fillStyle = '#111827'; continue; }
      var fsN = Math.max(11, ppm * 0.16);
      ctx.font = '700 ' + fsN + 'px Montserrat, Arial';
      var nmTxt = H.roomName(rooms[i], i);
      var spN = _impLabelSpot(rooms[i], ctx.measureText(nmTxt).width, ppm * 0.24 + fsN, ppm);
      var lcx = spN ? spN.x : rooms[i].cx, lcz = spN ? spN.z : rooms[i].cz;
      var lx = X(lcx), ly = Y(lcz);
      /* THE NAME ONLY WHERE IT FITS (v186, Marek: room name zobrazovat jen
         kdyz se do dane room vleze text (80%) -- pri ZOOM in se objevi, po
         zoom out zmizi). The text keeps a smallest size, so zoomed out it
         ran over the walls into the next rooms. The name and its area are
         drawn only when they take no more than 80 % of the room's width and
         height where they stand; otherwise the area alone, where it fits
         (v143), or nothing. The plan is painted again at every zoom, so the
         names come back as it is zoomed in. Renaming takes a name on screen. */
      if (!spN) {
        try { _impAreaOnly(ctx, rooms[i], arW, X, Y, ppm); } catch (eAO2) {}
        ctx.textAlign = 'center'; ctx.fillStyle = '#111827';
        continue;
      }
      ctx.fillText(nmTxt, lx, ly - ppm * 0.10);
      ctx.font = '600 ' + Math.max(10, ppm * 0.14) + 'px Montserrat, Arial';
      ctx.fillText(H.fmtArea(arW), lx, ly + ppm * 0.14);
      /* the two lines' box, in plan metres, with a finger's margin: this is
         what a tap has to land on to rename the room (Marek) */
      var wPx = Math.max(ctx.measureText(nmTxt).width, ppm * 0.6) + 24;
      var hw = (wPx / 2) / ppm, hh = (ppm * 0.34 + 24) / 2 / ppm;
      _nameBoxes.push({ x0: lcx - hw, x1: lcx + hw, z0: lcz - hh, z1: lcz + hh });
    }
    /* AND THE OUTDOOR AREAS, IN THE SAME HAND. Same two lines, same weights,
       same colour -- an area that is written differently reads as a note about
       the drawing rather than as part of it. The label goes in the middle of
       the area's BIGGEST rectangle, because the centre of an L-shaped terrace
       that wraps a building is not on the terrace at all. */
    for (ai = 0; ai < areas.length; ai++) {
      var arA = areas[ai], rcA = arA.rects || [], big = null, bA = -1, tot = 0;
      for (aj = 0; aj < rcA.length; aj++) {
        var cA = rcA[aj], aA = (cA.x1 - cA.x0) * (cA.z1 - cA.z0);
        tot += aA;
        if (aA > bA) { bA = aA; big = cA; }
      }
      if (!big || tot < 0.8) continue;
      var axL = X((big.x0 + big.x1) / 2), azL = Y((big.z0 + big.z1) / 2);
      /* v186: the same 80 % rule outside -- the name and the area where both
         fit, the area alone where it does, else nothing */
      var fsA = Math.max(11, ppm * 0.16), fsB = Math.max(10, ppm * 0.14), arTxt = H.fmtArea(arA.area != null ? arA.area : tot);
      var fitA = IMP_LABEL_FIT || 0.8, wB = (big.x1 - big.x0) * ppm * fitA, hB = (big.z1 - big.z0) * ppm * fitA;
      ctx.font = '700 ' + fsA + 'px Montserrat, Arial';
      var nmOk = ctx.measureText(arA.name || '').width <= wB && ppm * 0.24 + fsA <= hB;
      ctx.font = '600 ' + fsB + 'px Montserrat, Arial';
      var arOk = ctx.measureText(arTxt).width <= wB && fsB <= hB;
      if (nmOk && arOk) {
        ctx.font = '700 ' + fsA + 'px Montserrat, Arial';
        ctx.fillText(arA.name || '', axL, azL - ppm * 0.10);
        ctx.font = '600 ' + fsB + 'px Montserrat, Arial';
        ctx.fillText(arTxt, axL, azL + ppm * 0.14);
      } else if (arOk) {
        ctx.fillText(arTxt, axL, azL + fsB * 0.35);
      }
    }
  }




  /* ---------- THE INTERIOR DOORS ---------------------------------------
     The container's model, moved once more: 800 by 2000 by default, three
     widths, four swings, and ONE TAP walks the lot -- widths, then swings,
     then gone. A wall takes as many doors as fit with a reveal between them,
     a door slides only in the free stretch it stands in, and a wall dragged
     across one pushes it ahead rather than through it.
     Fourth piece to move, 178 lines. It asks the host for the doors and the
     walls; it decides everything about how a door behaves (Marek). */
  var DOOR_W = 0.80, DOOR_H = 2.00;
  var DOOR_SET_M = [0.70, 0.80, 0.90], DOOR_SET_I = [28, 30, 32];
  var DOOR_REVEAL = 0.10;
  /* the narrowest way through worth building, and what a doorway may not go
     under when it is pulled by its ends */
  var PASS_MIN = 0.40;

  /* ---------- THE HANDLES SWITCH ---------------------------------------
     A plan is a drawing first. The scissors on every stretch of every wall and
     the grips on a doorway are CONTROLS, and they were only ever on while
     something was being dragged -- which meant the one moment you could not
     see them was before you reached for one. One switch turns the lot on and
     off, and it lives here so every product answers it the same way (Marek).
     A product that already has its own switch passes handlesOn() in the
     contract and keeps it; anything else uses the asset's own. */
  var _handlesOn = false;
  function _handles() {
    var H = _PH;
    if (H && typeof H.handlesOn === 'function') return !!H.handlesOn();
    return _handlesOn;
  }
  /* AND A PRODUCT SAYS WHEN IT IS READY FOR ONE. A doorway is not only a
     drawing: the model has to build a hole with no leaf in it and the grips
     have to be reachable. A product that has not done that half yet would
     otherwise get a doorway it cannot show or resize, so the step is offered
     only where the contract asks for it -- passDoors: true. Everything else
     keeps the old cycle, where the last tap takes the door away (Marek). */
  function _passOk() { var H = _PH; return !!(H && H.passDoors); }
  var HANDLES = {
    on: _handles,
    set: function (v) {
      var H = _PH;
      if (H && typeof H.setHandles === 'function') H.setHandles(!!v);
      else _handlesOn = !!v;
    },
    toggle: function () {
      var H = _PH;
      this.set(!_handles());
      if (H && H.rebuild) H.rebuild();
      if (H && H.save) H.save();
      return _handles();
    }
  };

  function _doorSet() {
    var H = _PH; if (!H) return [];
    if (H.isMetric()) return DOOR_SET_M.slice();
    var out = [], i;
    for (i = 0; i < DOOR_SET_I.length; i++) out.push(DOOR_SET_I[i] * DRAW.IN);
    return out;
  }
  function _doors() {
    var H = _PH; if (!H) return []; return (H.doorsRef() && H.doorsRef().length) ? H.doorsRef() : []; }
  function _doorWidthIdx(d) {
    var H = _PH; if (!H) return 0;
    var set = _doorSet(), cur = d.w || DOOR_W, bi = 0, bd = Infinity, i;
    for (i = 0; i < set.length; i++) { var df = Math.abs(set[i] - cur); if (df < bd) { bd = df; bi = i; } }
    return bi;
  }
  function _doorSeg(pid, u) {
    var H = _PH; if (!H) return null;
    var segs = _partSegs(), ps = H.partitions(), i;
    for (i = 0; i < segs.length; i++) {
      var pp = ps[segs[i].pi];
      if (!pp || pp.id !== pid) continue;
      if (u >= segs[i].s - 0.12 && u <= segs[i].e + 0.12) return [segs[i].s, segs[i].e];
    }
    return null;
  }
  function _doorFree(pid, seg, except) {
    var H = _PH; if (!H) return [];
    var L = _doors(), free = [[seg[0], seg[1]]], i, f;
    for (i = 0; i < L.length; i++) {
      if (L[i].pid !== pid || L[i] === except) continue;
      var w = L[i].w || DOOR_W;
      var a = L[i].u - w / 2 - DOOR_REVEAL, b = L[i].u + w / 2 + DOOR_REVEAL;
      var out = [];
      for (f = 0; f < free.length; f++) {
        var s0 = free[f][0], s1 = free[f][1];
        if (b <= s0 || a >= s1) { out.push([s0, s1]); continue; }
        if (a > s0) out.push([s0, a]);
        if (b < s1) out.push([b, s1]);
      }
      free = out;
    }
    return free;
  }
  function _doorAt(x, z) {
    var H = _PH; if (!H) return null;
    var L = _doors(), ps = H.partitions(), best = null, bd = Infinity, i, k;
    for (i = 0; i < L.length; i++) {
      var d = L[i], pw = null;
      for (k = 0; k < ps.length; k++) if (ps[k].id === d.pid) pw = ps[k];
      if (!pw) continue;
      var w = d.w || DOOR_W;
      var along = (pw.o === 'v') ? Math.abs(z - d.u) : Math.abs(x - d.u);
      var perp = (pw.o === 'v') ? Math.abs(x - pw.c) : Math.abs(z - pw.c);
      if (along <= w / 2 + 0.35 && perp <= w + 0.35) {
        var sc = along + perp;
        if (sc < bd) { bd = sc; best = d; }
      }
    }
    return best;
  }
  function _doorsLive() {
    var H = _PH; if (!H) return [];
    var L = _doors(), ps = H.partitions(), out = [], i, k;
    for (i = 0; i < L.length; i++) {
      for (k = 0; k < ps.length; k++) {
        if (ps[k].id !== L[i].pid) continue;
        if (ps[k].sep) break;            /* a room line has no doors (v105) */
        var sp = _partSpan(ps[k]), w = L[i].w || DOOR_W;
        if (!sp) break;
        if (L[i].u - w / 2 < sp[0] - 0.01 || L[i].u + w / 2 > sp[1] + 0.01) break;
        out.push({ d: L[i], p: ps[k] });
        break;
      }
    }
    return out;
  }
  function _doorGaps(pw) {
    var H = _PH; if (!H) return [];
    var L = _doors(), out = [], sp = _partSpan(pw), i;
    if (!sp) return out;
    for (i = 0; i < L.length; i++) {
      if (L[i].pid !== pw.id) continue;
      var w = L[i].w || DOOR_W;
      /* only where the wall actually is: a door left outside its wall's run --
         after the wall was dragged or trimmed away from under it -- is not a
         hole in anything, and drawing it as one put a swing in open air */
      if (L[i].u - w / 2 < sp[0] - 0.01 || L[i].u + w / 2 > sp[1] + 0.01) continue;
      out.push([L[i].u - w / 2, L[i].u + w / 2]);
    }
    out.sort(function (a, b) { return a[0] - b[0]; });
    return out;
  }
  /* ONE TAP WALKS THE WHOLE LOT: the widths, the four swings, THEN A DOORWAY
     WITH NO LEAF, and only then is it gone. The doorway was the step that was
     missing -- a way through between two rooms is the commonest thing in a
     small building and there was no way to ask for one, so people were leaving
     a door where they wanted an opening. It keeps the width it had and can be
     pulled to any other by its ends (Marek). */
  /* WHAT A DOOR BECOMES AT THE END OF ITS WALK.

     It used to be: widths and swings, then a doorway with nothing in it, then
     gone. A hinged leaf is not the only door a house has, and the three that
     were missing are the ones a small house uses most -- a slider parked to one
     side, the same parked to the other, and a pair parting in the middle. They
     go where the doorway used to be, and the doorway follows them, so the walk
     ends the way it always did (Marek).

     d.slide carries it: 0 hinged, 1 sliding left, 2 sliding right, 3 two
     leaves. The width does not change -- a slider is the width it was, and it
     is set back to a hinged door by walking the whole way round. */
  var SLIDE_LAST = 3;

  function _doorRemove(d) {
    var H = _PH, ix = H ? H.doorsRef().indexOf(d) : -1;
    if (ix >= 0) H.doorsRef().splice(ix, 1);
  }

  /* THE CLOSET DOORS IN THE WALK (v106, Marek: idealne to rozlisit podle
     pudorysu, aby model vedel a rozpoznal skrine). A door that leads into a
     closet -- a space marked one, or any room under 3 m2 -- walks on past the
     sliders through the doors a closet has: a hinged pair, a bifold, a
     bypass, each in the widths the catalogue sells that fit the free
     stretch it stands in, and each opening OUT of the closet. Any other door
     walks the way it always did. d.kind carries it: 'pair', 'bifold' or
     'bypass'; slide and pass are off while it is set. */
  var CLOSET_DOORS = {
    pair:   { m: [1.22, 1.52, 1.83], i: [48, 60, 72] },              /* ODI12001 / ODI16001 / ODI18001 */
    bifold: { m: [0.60, 0.80, 0.90, 1.20, 1.60, 1.80], i: [24, 30, 36, 48, 60, 72] },  /* ODI06007-09007, two units over 0.9 */
    bypass: { m: [1.22, 1.52, 1.83], i: [48, 60, 72] }               /* ODI12002 / ODI16002 / ODI18002 */
  };
  var CLOSET_ORDER = ['pair', 'bifold', 'bypass'];
  function _closetKindWidths(kind, metric) {
    var K = CLOSET_DOORS[kind], H = _PH, i, out = [];
    if (!K) return out;
    if (metric == null) metric = !(H && H.isMetric && !H.isMetric());
    if (metric) return K.m.slice();
    for (i = 0; i < K.i.length; i++) out.push(K.i[i] * DRAW.IN);
    return out;
  }
  /* which side of its wall the closet is: -1 the lower x or z, +1 the
     higher, 0 neither side is a closet */
  function _doorClosetSide(d) {
    var H = _PH; if (!H || !d) return 0;
    var ps = H.partitions(), pw = null, k, sgn, side = 0, best = 1e9;
    for (k = 0; k < ps.length; k++) if (ps[k] && ps[k].id === d.pid) { pw = ps[k]; break; }
    if (!pw) return 0;
    var R = _roomsTracked();
    for (sgn = -1; sgn <= 1; sgn += 2) {
      var x = (pw.o === 'v') ? pw.c + sgn * 0.25 : d.u, z = (pw.o === 'v') ? d.u : pw.c + sgn * 0.25;
      for (k = 0; k < R.length; k++) {
        if (!_roomHolds(R[k], x, z)) continue;
        var a = R[k].closet ? 0 : _closetNet(R[k]);
        if ((R[k].closet || a < CLOSET_MAX_M2) && a < best) { best = a; side = sgn; }
        break;
      }
    }
    return side;
  }
  /* sw for a leaf that opens away from the closet: bit 1 sends it to +z on
     a wall along x and to -x on a wall along z (DRAW.door's convention) */
  function _closetSwing(d) {
    var H = _PH, ps = H ? H.partitions() : [], pw = null, k, side = _doorClosetSide(d);
    for (k = 0; k < ps.length; k++) if (ps[k] && ps[k].id === d.pid) { pw = ps[k]; break; }
    if (!pw || !side) return d.sw | 0;
    var away = -side, bit1 = (pw.o === 'v') ? (away < 0) : (away > 0);
    return bit1 ? 2 : 0;
  }
  function _closetSteps(d) {
    if (!_doorClosetSide(d)) return [];
    var seg = _doorSeg(d.pid, d.u); if (!seg) return [];
    var free = _doorFree(d.pid, seg, d), slot = null, fi, q, j, out = [];
    for (fi = 0; fi < free.length; fi++) if (d.u >= free[fi][0] - 0.01 && d.u <= free[fi][1] + 0.01) { slot = free[fi]; break; }
    if (!slot) return [];
    var avail = slot[1] - slot[0] - DOOR_REVEAL, sw = _closetSwing(d);
    for (q = 0; q < CLOSET_ORDER.length; q++) {
      var W = _closetKindWidths(CLOSET_ORDER[q]);
      for (j = 0; j < W.length; j++) if (W[j] <= avail + 0.005) out.push({ kind: CLOSET_ORDER[q], w: W[j], sw: (CLOSET_ORDER[q] === 'bypass') ? 0 : sw, slot: slot });
    }
    return out;
  }
  function _closetApply(d, st) {
    d.kind = st.kind; d.w = st.w; d.sw = st.sw;
    delete d.slide; delete d.pass;
    d.u = Math.max(st.slot[0] + st.w / 2, Math.min(st.slot[1] - st.w / 2, d.u));
  }
  function stepDoor(d) {
    var H = _PH; if (!H) return false;
    if (!d) return;
    var set = _doorSet(), total = set.length * 4;
    if (d.pass) {                      /* the doorway is the last thing before out */
      _doorRemove(d);
      return;
    }
    if (d.kind) {
      var cs = _closetSteps(d), i0 = -1, ci;
      for (ci = 0; ci < cs.length; ci++) if (cs[ci].kind === d.kind && Math.abs(cs[ci].w - (d.w || 0)) < 0.01) { i0 = ci; break; }
      if (i0 >= 0 && i0 + 1 < cs.length) { _closetApply(d, cs[i0 + 1]); return; }
      delete d.kind;
      if (_passOk()) { d.pass = true; d.slide = 0; d.sw = 0; return; }
      _doorRemove(d);
      return;
    }
    if (d.slide) {
      if (d.slide < SLIDE_LAST) { d.slide++; return; }
      var cs0 = _closetSteps(d);
      if (cs0.length) { _closetApply(d, cs0[0]); return; }
      if (_passOk()) { d.pass = true; d.slide = 0; d.sw = 0; return; }
      _doorRemove(d);
      return;
    }
    var step = _doorWidthIdx(d) * 4 + ((d.sw | 0) & 3) + 1;
    if (step >= total) {
      d.slide = 1; d.sw = 0;
      return;
    }
    d.sw = step % 4;
    var want = set[Math.floor(step / 4)];
    var seg = _doorSeg(d.pid, d.u);
    if (seg) {
      /* it may only grow into wall that is free -- its neighbours keep theirs */
      var free = _doorFree(d.pid, seg, d), slot = null, fi;
      for (fi = 0; fi < free.length; fi++) {
        if (d.u >= free[fi][0] - 0.01 && d.u <= free[fi][1] + 0.01) { slot = free[fi]; break; }
      }
      if (slot) {
        var avail = slot[1] - slot[0] - DOOR_REVEAL;
        if (want > avail) want = Math.max(0.40, avail);
        d.u = Math.max(slot[0] + want / 2, Math.min(slot[1] - want / 2, d.u));
      }
    }
    d.w = want;
  }
  function addDoorAt(x, z) {
    var H = _PH; if (!H) return null;
    var ps = H.partitions(), best = null, bd = 0.9, ba = 0, i;
    for (i = 0; i < ps.length; i++) {
      var sp = _partSpan(ps[i]);
      if (!sp) continue;
      var perp = (ps[i].o === 'v') ? Math.abs(x - ps[i].c) : Math.abs(z - ps[i].c);
      var along = (ps[i].o === 'v') ? z : x;
      if (along < sp[0] - 0.12 || along > sp[1] + 0.12) continue;
      if (perp < bd) { bd = perp; best = ps[i]; ba = H.clamp(along, sp[0], sp[1]); }
    }
    if (!best) return null;
    /* a room line takes no door (v105): the finger was on the line, so no
       wall beside it gets one either */
    if (best.sep) { try { if (H.log) H.log('door: a room line has no wall to put a door in'); } catch (eSL) {} return null; }
    /* v213: nor a wall another layer builds (core-v815, DUPLEX's party wall) */
    if (best.own) { try { if (H.log) H.log('door: ' + best.own + ' builds that wall -- no door through it'); } catch (eOW) {} return null; }
    var seg = _doorSeg(best.id, ba);
    if (!seg) return null;
    /* the free stretch the finger landed in, not the whole segment */
    var free = _doorFree(best.id, seg, null), slot = null;
    for (i = 0; i < free.length; i++) {
      if (ba >= free[i][0] - 0.01 && ba <= free[i][1] + 0.01) { slot = free[i]; break; }
      if (!slot || (free[i][1] - free[i][0]) > (slot[1] - slot[0])) slot = free[i];
    }
    if (!slot) return null;
    var room = slot[1] - slot[0];
    var w = _doorSet()[0];
    if (room < w + DOOR_REVEAL) w = Math.max(0.40, room - DOOR_REVEAL);
    if (w < 0.40 || room < 0.50) return null;
    var u = Math.max(slot[0] + w / 2, Math.min(slot[1] - w / 2, ba));
    H.setDoors(_doors().slice());
    H.doorsRef().push({ pid: best.id, u: u, w: w, sw: 0 });
    return H.doorsRef()[H.doorsRef().length - 1];
  }
  function doorTapAt(x, z) {
    var H = _PH; if (!H) return false;
    var d = _doorAt(x, z);
    if (d) { stepDoor(d); H.rebuild(); H.save(); return true; }
    if (addDoorAt(x, z)) { H.rebuild(); H.save(); return true; }
    return false;
  }
  function moveDoorTo(d, x, z) {
    var H = _PH; if (!H) return false;
    var ps = H.partitions(), pw = null, i;
    for (i = 0; i < ps.length; i++) if (ps[i].id === d.pid) pw = ps[i];
    if (!pw) return;
    var want = (pw.o === 'v') ? z : x;
    var seg = _doorSeg(d.pid, d.u);
    if (!seg) return;
    var free = _doorFree(d.pid, seg, d), slot = null, w = d.w || DOOR_W;
    for (i = 0; i < free.length; i++) {
      if (d.u >= free[i][0] - 0.01 && d.u <= free[i][1] + 0.01) { slot = free[i]; break; }
    }
    if (!slot || slot[1] - slot[0] < w) return;
    d.u = H.clamp(want, slot[0] + w / 2, slot[1] - w / 2);
  }

  /* ---------- A DOORWAY TAKEN BY ITS END --------------------------------
     A door is a bought thing and its widths are the ones on the shelf, so it
     is walked through them. A DOORWAY is a hole in a wall and can be any size,
     so it is set the way any dimension in this plan is set -- by taking hold
     of it. The end in the hand moves and THE OTHER END STAYS PUT, because a
     doorway is usually set against something (Marek). */
  function doorEndAt(x, z) {
    var H = _PH; if (!H) return null;
    if (!_handles()) return null;
    var DL = _doorsLive(), i;
    for (i = 0; i < DL.length; i++) {
      var d = DL[i].d, p = DL[i].p;
      if (!d.pass) continue;
      var w = d.w || H.doorW();
      var along = (p.o === 'v') ? z : x, across = (p.o === 'v') ? x : z;
      if (Math.abs(across - p.c) > 0.30) continue;
      /* the reach never spills over the middle, or a narrow doorway would have
         two grips answering to one finger */
      var grab = Math.min(0.20, w * 0.45);
      if (Math.abs(along - (d.u - w / 2)) <= grab) return { d: d, p: p, end: -1 };
      if (Math.abs(along - (d.u + w / 2)) <= grab) return { d: d, p: p, end: 1 };
    }
    return null;
  }
  function resizeDoorTo(d, end, x, z) {
    var H = _PH; if (!H || !d) return false;
    var ps = H.partitions(), pw = null, i;
    for (i = 0; i < ps.length; i++) if (ps[i].id === d.pid) pw = ps[i];
    if (!pw) return;
    var e = (end < 0) ? -1 : 1;
    var w0 = d.w || H.doorW();
    var fixed = d.u - e * w0 / 2;
    var want = (pw.o === 'v') ? z : x;
    /* it may only grow into wall that is free: its neighbours keep theirs, and
       the stretch it stands in is the one the FIXED end is in */
    var seg = _doorSeg(d.pid, fixed) || _partSpan(pw), slot = null;
    if (seg) {
      var free = _doorFree(d.pid, seg, d), fi;
      for (fi = 0; fi < free.length; fi++) {
        if (fixed >= free[fi][0] - 0.01 && fixed <= free[fi][1] + 0.01) { slot = free[fi]; break; }
      }
    }
    if (slot) want = H.clamp(want, slot[0], slot[1]);
    var w = Math.abs(want - fixed);
    if (slot) {
      var lim = (e > 0) ? (slot[1] - fixed) : (fixed - slot[0]);
      if (w > lim) w = lim;
    }
    if (w < PASS_MIN) w = PASS_MIN;
    d.w = w;
    d.u = fixed + e * w / 2;
  }

  /* ---------- THE SCISSORS OVER THE DRAWING ----------------------------
     A plan drawn on a canvas cannot be tapped, so the cut handles are real
     buttons placed over it by the same projection the drawing uses. Every
     product had its own copy of this and its own red circle; it is one
     routine now, and it answers the handles switch like everything else
     (Marek). */
  var _iconBtns = [];
  function planIcons(hostEl, m, mx0, mz0, adapter) {
    var doc = global.document, i;
    if (!hostEl || !doc || !m || (adapter ? !adapter.enabled : !_handles())) {
      for (i = 0; i < _iconBtns.length; i++) _iconBtns[i].remove();
      _iconBtns = [];
      return _iconBtns;
    }
    // Keep target nodes alive through camera paints. Replacing every button
    // on every frame detaches the target between pointerdown and a touch tap.
    var segs = adapter ? adapter.segments : _partSegs(), ox = mx0 || 0, oz = mz0 || 0;
    for (i = 0; i < segs.length; i++) {
      (function (sg, idx) {
        var b = _iconBtns[idx];
        if (!b || b.parentNode !== hostEl) {
          if (b) b.remove();
          b = doc.createElement('button');
        b.type = 'button'; b.setAttribute('data-plan-cut', String(idx));
        b.setAttribute('aria-label', adapter && adapter.label || 'Cut partition');
        b.setAttribute('data-cf-nopinch', '1');
        b.style.cssText = 'position:absolute;width:34px;height:34px;margin:-17px 0 0 -17px;border:none;' +
          'border-radius:50%;background:' + UI.BG_ACTION + ';color:#fff;cursor:pointer;pointer-events:auto;' +
          'display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,.30);';
        b.innerHTML = UI.svg(UI.ICON_SCISSORS);
        /* A MOUSE NEVER GOT THROUGH on the version this came from: the canvas
           under these takes the pointer on pointerdown and captures it, so the
           click that would have followed never arrived. Acting on pointerdown,
           and stopping it there, is what the rest of the plan does. */
        b.addEventListener('pointerdown', function (ev) {
          ev.preventDefault(); ev.stopPropagation();
          if (b.__cut) b.__cut();
        });
        b.addEventListener('click', function (ev) { ev.preventDefault(); ev.stopPropagation(); });
        hostEl.appendChild(b);
        _iconBtns[idx] = b;
        }
        b.__cut = function () { if (adapter) adapter.cut(sg); else cutPartSeg(idx); };
        b.style.left = (m.ox + (sg.mx + ox) * m.ppm) + 'px';
        b.style.top = (m.oy + (sg.mz + oz) * m.ppm) + 'px';
      })(segs[i], i);
    }
    while (_iconBtns.length > segs.length) _iconBtns.pop().remove();
    return _iconBtns;
  }
  function _pushDoors() {
    var H = _PH; if (!H) return false;
    var L = _doors(), ps = H.partitions(), i, k;
    for (i = 0; i < L.length; i++) {
      var d = L[i], pw = null;
      for (k = 0; k < ps.length; k++) if (ps[k].id === d.pid) pw = ps[k];
      if (!pw) continue;
      var sp = _partSpan(pw);
      if (!sp) continue;
      var w = d.w || DOOR_W, half = w / 2 + H.partThk() / 2 + DOOR_REVEAL;
      for (k = 0; k < ps.length; k++) {
        if (ps[k].o === pw.o) continue;
        var osp = _partSpan(ps[k]);
        if (!osp || pw.c < osp[0] - 0.02 || pw.c > osp[1] + 0.02) continue;
        var cc = ps[k].c;
        if (Math.abs(d.u - cc) >= half) continue;
        var want = (d.u >= cc) ? (cc + half) : (cc - half);
        d.u = H.clamp(want, sp[0] + w / 2, sp[1] - w / 2);
      }
    }
  }

  /* ---------- THE PARTITIONS -------------------------------------------
     How far an interior wall reaches, where it is cut, what it may be dragged
     between, and how a corner where two of them meet stays solid. Third piece
     to move, 219 lines. All the rules that took a day to get right -- an end
     trimmed TO A WALL and not to a number, the far-face reach that fills the
     missing quarter of a corner, the free slide when a bay will not fit --
     live once now, so a product that adopts this gets them all at once
     instead of rediscovering them (Marek). */
  var _partSeq = 0;
  /* HOW NARROW A ROOM MAY GET. The figure was 0.60 buried twice inside the
     clamp, which is where the niches come from: 600 mm is not a room, it is a
     recess, and nothing stopped a wall being parked there. It is one named
     number now so it can be argued about in one place -- and the two sides use
     the SAME one, which they did not visibly do while it was written out twice
     (Marek). */
  var MIN_ROOM = 0.30;
  function _partId() {
    var H = _PH; if (!H) return null;
    var ps = H.partitions(), i;
    for (i = 0; i < ps.length; i++) if ((ps[i].id | 0) > _partSeq) _partSeq = ps[i].id | 0;
    return ++_partSeq;
  }
  function _partById(id) {
    var H = _PH; if (!H) return null;
    if (id == null) return null;
    var ps = H.partitions(), i;
    for (i = 0; i < ps.length; i++) if (ps[i].id === id) return ps[i];
    return null;
  }
  function _meetsWall(p, at) {
    var H = _PH; if (!H) return null;
    var ps = H.partitions(), k;
    for (k = 0; k < ps.length; k++) {
      if (ps[k] === p || ps[k].o === p.o) continue;
      if (Math.abs(ps[k].c - at) > 0.02) continue;
      var runs = (ps[k].o === 'v') ? H.clipRuns(H.poly(), ps[k].c, 0, 0, 1)
                                   : H.clipRuns(H.poly(), 0, ps[k].c, 1, 0);
      var q;
      for (q = 0; q < runs.length; q++) {
        var b0 = runs[q].t0 + H.wallThk(), b1 = runs[q].t1 - H.wallThk();
        if (ps[k].s != null) b0 = Math.max(b0, ps[k].s);
        if (ps[k].e != null) b1 = Math.min(b1, ps[k].e);
        if (p.c >= b0 - H.partThk() && p.c <= b1 + H.partThk()) return true;
      }
    }
    return false;
  }
  function _partSpan(p) {
    var H = _PH; if (!H) return null;
    var P = H.poly();
    var runs = (p.o === 'v') ? H.clipRuns(P, p.c, 0, 0, 1) : H.clipRuns(P, 0, p.c, 1, 0);
    var best = null, q;
    for (q = 0; q < runs.length; q++) {
      /* WALL TO WALL: the outline is the OUTER face, so a partition runs to the
         inner lining, not to the outline. A trimmed end stops at the CENTRELINE
         of the wall it was trimmed to, half a thickness past its face, so the
         two make a solid corner instead of leaving a hairline of nothing where
         they meet (Marek). */
      var a0 = runs[q].t0 + H.wallThk(), a1 = runs[q].t1 - H.wallThk();
      if (a1 - a0 < 0.20) continue;
      /* WHY A TRIMMED CORNER KEPT COMING APART. Cut back to another wall's
         CENTRELINE, each of the two covers only the half of the corner square
         on its own side -- and the quarter beyond both centrelines is covered
         by neither. That missing quarter is the gap, and it is there whether
         the ends stop at the centreline or short of it.
         An end that lands on another wall therefore runs to that wall's FAR
         FACE, half a thickness past its middle, and the quarter is filled. It
         is done only where an end really meets a wall -- an end at the shell,
         or a free end, stops where it was cut, so nothing sticks out anywhere
         else. That was the fault in both directions: too little and it gapped,
         everywhere and it stuck through (Marek). */
      /* an end trimmed to a wall follows that wall; one trimmed to nothing
         stays where it was cut. Reaching HALF A THICKNESS past the other wall's
         middle fills the quarter of the corner square neither would cover. */
      var wS = _partById(p.sTo), wE = _partById(p.eTo);
      var vS = wS ? wS.c : p.s, vE = wE ? wE.c : p.e;
      if (vS != null) a0 = Math.max(a0, vS - ((wS || _meetsWall(p, vS)) ? H.partThk() / 2 : 0));
      if (vE != null) a1 = Math.min(a1, vE + ((wE || _meetsWall(p, vE)) ? H.partThk() / 2 : 0));
      if (a1 - a0 < 0.20) continue;
      /* THE INNER CORNER MUST NOT DECIDE WHICH ARM THE WALL IS IN. A line drawn
         across an L can leave the outline and come back, so it arrives here as
         TWO runs -- and taking the longer of them moved the wall into whichever
         arm happened to be bigger, which reads exactly like the corner blocking
         it. The run that CONTAINS the wall wins; the longest is only the
         fallback for a wall that is not in any of them yet.
         The other orientation never showed it because its runs usually come
         back as one (Marek). */
      var mid = (p.s != null && p.e != null) ? (p.s + p.e) / 2 : null;
      if (mid != null && mid >= a0 - 0.02 && mid <= a1 + 0.02) return [a0, a1];
      if (!best || (a1 - a0) > (best[1] - best[0])) best = [a0, a1];
    }
    return best;
  }
  function _partSegs() {
    var H = _PH; if (!H) return null;
    var ps = H.partitions(), out = [], i, k;
    for (i = 0; i < ps.length; i++) {
      var sp = _partSpan(ps[i]);
      if (!sp) continue;
      var o = ps[i].o, c = ps[i].c, cuts = [{ at: sp[0], id: null }];
      for (k = 0; k < ps.length; k++) {
        if (k === i || ps[k].o === o) continue;
        var osp = _partSpan(ps[k]);
        /* only a partition that really reaches this line divides it */
        if (!osp || c < osp[0] - 0.02 || c > osp[1] + 0.02) continue;
        if (ps[k].c > sp[0] + 0.10 && ps[k].c < sp[1] - 0.10) cuts.push({ at: ps[k].c, id: ps[k].id });
      }
      cuts.push({ at: sp[1], id: null });
      cuts.sort(function (m, n) { return m.at - n.at; });
      for (k = 0; k + 1 < cuts.length; k++) {
        if (cuts[k + 1].at - cuts[k].at < 0.20) continue;
        out.push({ pi: i, o: o, c: c, s: cuts[k].at, e: cuts[k + 1].at,
                   sTo: cuts[k].id, eTo: cuts[k + 1].id,
                   mx: (o === 'v') ? c : (cuts[k].at + cuts[k + 1].at) / 2,
                   mz: (o === 'v') ? (cuts[k].at + cuts[k + 1].at) / 2 : c });
      }
    }
    return out;
  }
  function cutPartSeg(idx) {
    var H = _PH; if (!H) return null;
    var segs = _partSegs();
    if (idx < 0 || idx >= segs.length) return;
    var sg = segs[idx], ps = H.partitions().slice(), p = ps[sg.pi];
    var full = _partSpan(p);
    if (!full) return;
    var atStart = Math.abs(sg.s - full[0]) < 0.02, atEnd = Math.abs(sg.e - full[1]) < 0.02;
    if (atStart && atEnd) ps.splice(sg.pi, 1);
    else if (atStart) ps[sg.pi] = { id: p.id, o: p.o, c: p.c, s: sg.e, sTo: sg.eTo, e: p.e, eTo: p.eTo };
    else if (atEnd) ps[sg.pi] = { id: p.id, o: p.o, c: p.c, s: p.s, sTo: p.sTo, e: sg.s, eTo: sg.sTo };
    else {
      /* THE FAR HALF BECOMES A NEW WALL, AND THE DOORS STANDING IN IT HAVE TO
         GO WITH IT. Left pointing at the old id they kept being drawn against a
         wall that no longer reaches them -- a door beside its own hole, which
         is exactly what he is seeing (Marek). */
      var newId = _partId();
      ps[sg.pi] = { id: p.id, o: p.o, c: p.c, s: p.s, sTo: p.sTo, e: sg.s, eTo: sg.sTo };
      ps.push({ id: newId, o: p.o, c: p.c, s: sg.e, sTo: sg.eTo, e: p.e, eTo: p.eTo });
      var DL2 = H.doors(), dz;
      for (dz = 0; dz < DL2.length; dz++) if (DL2[dz].pid === p.id && DL2[dz].u >= sg.e) DL2[dz].pid = newId;
    }
    if (p.sep) { for (var zq = 0; zq < ps.length; zq++) if (ps[zq] && (ps[zq].id === p.id || ps[zq].id === newId)) ps[zq].sep = true; }
    H.setParts(ps);
    H.rebuild(); H.save();
    H.log('partition trimmed ' + sg.o + '@' + sg.c.toFixed(2) +
         ' [' + sg.s.toFixed(2) + '..' + sg.e.toFixed(2) + '] -> ' + ps.length + ' walls');
  }
  /* WALL OR ROOM LINE (v105, Marek: delici cara -- ano). The same partition
     either way: it keeps its id, its ends and its storey. Made a line, the
     doors in it go, because two rooms open to each other have none; a
     load-bearing wall stays a wall. sep is written false rather than left
     out, so the core does not put back a flag it remembers. */
  function toggleSep(pi) {
    var H = _PH; if (!H) return null;
    var ps = H.partitions().slice(), p = ps[pi], k;
    if (!p) return null;
    if (p.bear && !p.sep) { try { if (H.toast) H.toast('A load-bearing wall stays a wall'); } catch (eT) {} return false; }
    /* v213: a wall another layer builds stays a wall (core-v815) */
    if (p.own) { try { if (H.log) H.log('partition #' + p.id + ' is built by ' + p.own + ' -- it stays a wall'); } catch (eO) {} return false; }
    var q = {};
    for (k in p) if (p.hasOwnProperty(k)) q[k] = p[k];
    q.sep = !p.sep;
    ps[pi] = q;
    var gone = 0;
    if (q.sep) {
      var DL = H.doorsRef();
      for (k = DL.length - 1; k >= 0; k--) if (DL[k] && DL[k].pid === p.id) { DL.splice(k, 1); gone++; }
    }
    H.setParts(ps);
    H.rebuild(); H.save();
    H.log('partition #' + p.id + ' is now a ' + (q.sep ? 'room line (no wall, nothing built)' : 'wall') + (gone ? (', ' + gone + ' door(s) in it removed') : ''));
    return q.sep;
  }
  function movePartTo(pi, c) {
    var H = _PH; if (!H) return null;
    var ps = H.partitions().slice(), p = ps[pi];
    if (!p) return;
    var P = H.poly(), bx0 = 1e9, bx1 = -1e9, bz0 = 1e9, bz1 = -1e9, i;
    for (i = 0; i < P.length; i++) {
      bx0 = Math.min(bx0, P[i][0]); bx1 = Math.max(bx1, P[i][0]);
      bz0 = Math.min(bz0, P[i][1]); bz1 = Math.max(bz1, P[i][1]);
    }
    /* WHAT WAS REALLY STOPPING IT. His snapshot has a vertical wall at x=1.82
       on a plan 5 m wide: with MIN_ROOM at 900 the limit was 2.50 - 0.12 - 0.90
       = 1.48, so every drag pulled it back to 1.48 and it could not be taken to
       the right-hand wall. It reads exactly like the corner blocking it, and it
       was my own clearance figure -- one I invented, and set too high.
       The horizontal walls never showed it because the plan is longer than it
       is wide, so their limit was far away. Not an orientation bug at all: a
       number that happened to bite on the short axis first (Marek). */
    var lo = ((p.o === 'v') ? bx0 : bz0) + H.wallThk() + MIN_ROOM;
    var hi = ((p.o === 'v') ? bx1 : bz1) - H.wallThk() - MIN_ROOM;
    /* IT WAS NEVER THE CORNER. His plan has two vertical walls, at 0.84 and at
       1.22 -- and 0.84 + 0.08 + 0.30 is exactly 1.22. The one being dragged was
       stopping dead against the OTHER ONE, at the clearance I had put between
       parallel walls. The horizontal direction had only a single wall, so
       nothing was there to stop it, and the two behaved differently for that
       reason alone.
       Two walls only have to not occupy the same place. What size a room ought
       to be is the person's business, not this function's (Marek). */
    for (i = 0; i < ps.length; i++) {
      if (i === pi || ps[i].o !== p.o) continue;
      if (ps[i].c < p.c) lo = Math.max(lo, ps[i].c + H.partThk());
      else hi = Math.min(hi, ps[i].c - H.partThk());
    }
    /* A WALL DOES NOT RUN INTO A DOORWAY (v209, Marek: pokud tahnu pricku,
       mela by pred sebou tlacit i interierove dvere -- nemela by pricka
       zajizdet do otvoru pro dvere; plati pro vsechny projekty). A door in a
       wall this one meets goes AHEAD of it, on the side it stood -- decided
       by where this wall was before the move, so a fast drag cannot hop over
       it -- and once that door has reached the far end of its own wall, this
       wall stops at the door's reveal instead of going on into the opening.
       A room line (sep) builds nothing and pushes nothing. */
    var push = [], DL = p.sep ? [] : _doors(), thk = H.partThk(), mine = p.sep ? null : _partSpan(p), k, q;
    for (k = 0; mine && k < DL.length; k++) {
      var d = DL[k];
      if (!d) continue;
      q = null;
      for (i = 0; i < ps.length; i++) if (i !== pi && ps[i].id === d.pid) q = ps[i];
      if (!q || q.o === p.o || q.sep) continue;
      if (q.c < mine[0] - 0.05 || q.c > mine[1] + 0.05) continue;
      var qs = _partSpan(q);
      if (!qs) continue;
      var dw = d.w || DOOR_W, half = dw / 2 + thk / 2 + DOOR_REVEAL, up = (d.u >= p.c);
      if (up) { var h2 = qs[1] - dw / 2 - half; if (h2 >= p.c - 1e-6) hi = Math.min(hi, h2); }
      else { var l2 = qs[0] + dw / 2 + half; if (l2 <= p.c + 1e-6) lo = Math.max(lo, l2); }
      push.push({ d: d, q: q.id, up: up, half: half, w: dw });
    }
    if (hi < lo) return;
    ps[pi] = { id: p.id, o: p.o, c: H.clamp(c, lo, hi), s: p.s, sTo: p.sTo, e: p.e, eTo: p.eTo };
    if (p.sep) ps[pi].sep = true;
    H.setParts(ps);
    var nc = ps[pi].c, moved = 0;
    for (k = 0; k < push.length; k++) {
      var P0 = push[k], qq = _partById(P0.q), sp2 = qq ? _partSpan(qq) : null;
      if (!sp2) continue;
      var u0 = P0.d.u, want = P0.up ? Math.max(u0, nc + P0.half) : Math.min(u0, nc - P0.half);
      P0.d.u = H.clamp(want, sp2[0] + P0.w / 2, sp2[1] - P0.w / 2);
      if (Math.abs(P0.d.u - u0) > 1e-6) moved++;
    }
    if (moved) H.log('partition #' + p.id + ' pushed ' + moved + ' door(s) ahead of it (v209)');
    H.pushDoors();          /* the doors it meets travel with it */
    H.rebuild();
  }
  function _partAt(x, z, grabIn) {
    var H = _PH; if (!H) return null;
    /* the grab can be given by the caller in metres -- a finger's reach at
       the plan's current zoom -- so a partition is as easy to take as a piece */
    var ps = H.partitions(), grab = Math.max(H.partThk() / 2 + 0.12, Number(grabIn) || 0), i;
    for (i = 0; i < ps.length; i++) {
      var sp = _partSpan(ps[i]);
      if (!sp) continue;
      var d = (ps[i].o === 'v') ? Math.abs(x - ps[i].c) : Math.abs(z - ps[i].c);
      var along = (ps[i].o === 'v') ? z : x;
      if (d <= grab && along >= sp[0] - grab && along <= sp[1] + grab) return i;
    }
    return -1;
  }
  function _footObstacles(p, at) {
    var H = _PH; if (!H) return null;
    var ps = H.partitions(), P = H.poly(), i;
    /* THE SHELL ON THIS LINE, NOT THE CORNER OF THE PLOT. The bounding box was
       standing in for the outer walls -- and on an L the box covers the notch,
       which is not building at all, so the kot ran out past the short arm to an
       edge that is not there. Walking out along the line the kot is measured on
       and asking where it leaves the OUTLINE answers for this room, whatever
       shape the plan is (Marek). */
    var runsF = (p.o === 'v') ? H.clipRuns(P, 0, at, 1, 0) : H.clipRuns(P, at, 0, 0, 1);
    var bx0 = 1e9, bx1 = -1e9, bz0 = 1e9, bz1 = -1e9, rq;
    for (rq = 0; rq < runsF.length; rq++) {
      if (p.c < runsF[rq].t0 - 0.02 || p.c > runsF[rq].t1 + 0.02) continue;
      if (p.o === 'v') { bx0 = runsF[rq].t0; bx1 = runsF[rq].t1; bz0 = bz1 = at; }
      else { bz0 = runsF[rq].t0; bz1 = runsF[rq].t1; bx0 = bx1 = at; }
      break;
    }
    if (bx0 > 1e8) {
      for (i = 0; i < P.length; i++) {
        bx0 = Math.min(bx0, P[i][0]); bx1 = Math.max(bx1, P[i][0]);
        bz0 = Math.min(bz0, P[i][1]); bz1 = Math.max(bz1, P[i][1]);
      }
    }
    /* FACE TO FACE, WHICH IS WHAT THE ROOM ACTUALLY MEASURES. Centreline to
       centreline answers a draughtsman's question; the person moving the wall
       wants to know how much ROOM is left, so both ends are taken at the FACE
       of what is there -- the lining of the shell, or the near side of the wall
       opposite (Marek). */
    var lo = ((p.o === 'v') ? bx0 : bz0) + H.wallThk();
    var hi = ((p.o === 'v') ? bx1 : bz1) - H.wallThk();
    for (i = 0; i < ps.length; i++) {
      if (ps[i] === p || ps[i].o !== p.o) continue;
      var sp2 = _partSpan(ps[i]);
      if (!sp2 || at < sp2[0] - 0.02 || at > sp2[1] + 0.02) continue;   /* it must reach the foot */
      if (ps[i].c < p.c) lo = Math.max(lo, ps[i].c + H.partThk() / 2);
      else hi = Math.min(hi, ps[i].c - H.partThk() / 2);
    }
    return { lo: lo, hi: hi };
  }
  function _insetPoly(poly, d) {
    var H = _PH; if (!H) return null;
    var N = poly.length, i, A2 = 0;
    if (N < 3) return poly;
    for (i = 0; i < N; i++) { var pA = poly[i], pB = poly[(i + 1) % N]; A2 += pA[0] * pB[1] - pB[0] * pA[1]; }
    var wnd = (A2 >= 0) ? 1 : -1, nr = [];
    for (i = 0; i < N; i++) {
      var a = poly[i], b = poly[(i + 1) % N];
      var dx = b[0] - a[0], dz = b[1] - a[1], L = Math.hypot(dx, dz) || 1;
      nr.push([-wnd * dz / L, wnd * dx / L]);
    }
    var out = [];
    for (i = 0; i < N; i++) {
      var iP = (i + N - 1) % N;
      var hH = Math.abs(poly[i][1] - poly[(i + 1) % N][1]) < 1e-6;
      var nH = hH ? nr[i] : nr[iP], nV = hH ? nr[iP] : nr[i];
      out.push([poly[i][0] + nV[0] * d, poly[i][1] + nH[1] * d]);
    }
    return out;
  }

  /* ===== ZONE A -- replace from here to the END of the file ============ */

  /* ---------- THE ROOMS ------------------------------------------------
     What a room IS, what it is called and how it keeps its name while it
     changes shape. Second piece to move: it was 182 lines in one product and
     the sauna and the container each carry their own answer to the same
     question, which is why a room could be one thing here and another there.
     Like the drawing, it reaches for nothing -- the outline, the partitions
     and the saved names all arrive through the host (Marek). */
  var _roomsLastBy = {};   /* v200: per storey (H.storey()), was one _roomsLast */
  /* A CLOSET IS NOT A ROOM (v102, Marek: vestavene skrine, ktere se tvari
     jako mistnosti -- mensi nez 3 m2, negenerovat mistnost, jen pricky; and
     his call on how to tell one: the plan names it a closet, a store, a
     pantry or a laundry AND it is under 3 m2). The plan import marks such a
     space with a pin of its own key. It keeps its walls, its floor and its
     area in every sum, and the list of rooms still has it, flagged closet;
     what it does not get is a name, a room's colour, a tap to rename it or
     furniture. A small bathroom, a WC or a sauna carries no such pin and
     stays a room, and a marked space that grows to 3 m2 becomes a room. */
  var CLOSET_KEY = 'leva.closet', CLOSET_MAX_M2 = 3.0, CLOSET_FILL = '#EEF0F2';
  function _closetPin(p) { return !!(p && p.k === CLOSET_KEY); }
  /* the floor INSIDE the walls: a room's cells run to the outline and to
     the partitions' lines, so a 2.3 m2 closet in a corner measured over 3 */
  function _closetNet(r) {
    var H = _PH, WALL = (H && H.wallThk) ? H.wallThk() : 0.16, PT = (H && H.partThk) ? H.partThk() : 0.1, a = 0, j, rc;
    function inset(x, z) { return _nearOutline(x, z) ? WALL : PT / 2; }
    for (j = 0; j < r.rects.length; j++) {
      rc = r.rects[j];
      var mx = (rc.x0 + rc.x1) / 2, mz = (rc.z0 + rc.z1) / 2;
      var w = (rc.x1 - inset(rc.x1, mz)) - (rc.x0 + inset(rc.x0, mz)), d = (rc.z1 - inset(mx, rc.z1)) - (rc.z0 + inset(mx, rc.z0));
      if (w > 0 && d > 0) a += w * d;
    }
    return a;
  }
  function _isCloset(r) {
    if (!r || !r.rects) return false;
    var P = _roomPins(), k, pinned = false;
    if (!P) return false;
    for (k = 0; k < P.length; k++) {
      if (_closetPin(P[k]) && typeof P[k].x === 'number' && _roomHolds(r, P[k].x, P[k].z)) { pinned = true; break; }
    }
    return pinned && _closetNet(r) < CLOSET_MAX_M2;
  }
  function _overlapArea(a, b) {
    var t = 0, i, j;
    for (i = 0; i < a.rects.length; i++) {
      for (j = 0; j < b.rects.length; j++) {
        var ra = a.rects[i], rb = b.rects[j];
        var dx = Math.min(ra.x1, rb.x1) - Math.max(ra.x0, rb.x0);
        var dz = Math.min(ra.z1, rb.z1) - Math.max(ra.z0, rb.z0);
        if (dx > 0 && dz > 0) t += dx * dz;
      }
    }
    return t;
  }
  /* THE ROOMS ARE WORKED OUT ONCE FOR ONE SET OF WALLS (v183, Marek: zamrzlo
     to tady -- plan 1000076007, the 88 x 34 ft ranch with its garage, 112
     partitions and 46 spaces once written). rooms() split the floor at every
     partition and tested every shared edge against every partition's span on
     EVERY call, and a product asks for it at every face of every partition
     (the ADU's _wetAt, RANCH's), so that plan took about nine minutes and the
     page hung. The geometry is kept for the same outline, partitions and wall
     thickness; the names are still tracked on every call, on fresh copies. */
  var _roomsMemo = { key: '', out: null, hit: 0, miss: 0 };
  function _roomsGeo() {
    var H = _PH; if (!H) return [];
    var key = '';
    try { key = JSON.stringify(H.poly()) + '|' + JSON.stringify(H.partitions()) + '|' + H.wallThk(); } catch (eK) { key = ''; }
    if (!key || key !== _roomsMemo.key || !_roomsMemo.out) { _roomsMemo.out = _rooms(); _roomsMemo.key = key; _roomsMemo.miss++; }
    else _roomsMemo.hit++;
    var src = _roomsMemo.out, cp = [], i;
    for (i = 0; i < src.length; i++) cp.push({ rects: src[i].rects.map(function (r9) { return { x0: r9.x0, x1: r9.x1, z0: r9.z0, z1: r9.z1 }; }), area: src[i].area, mnx: src[i].mnx, mnz: src[i].mnz, cx: src[i].cx, cz: src[i].cz });
    return cp;
  }
  /* and the names they were given (v183): asked again for the same walls,
     the same pins and the same names table, a room keeps what it got --
     matching every room against the last set was the other half of the time */
  var _roomsTr = { key: '', out: null };
  function _roomsTracked() {
    var H = _PH; if (!H) return [];
    var R = _roomsGeo(), used = {}, taken = {}, i, k;
    var tk = '';
    try { tk = _roomsMemo.key ? (_roomsMemo.key + '|' + JSON.stringify(_roomPins() || null) + '|' + _roomNamesTable().length) : ''; } catch (eTk) { tk = ''; }
    if (tk && tk === _roomsTr.key && _roomsTr.out && _roomsTr.out.length === R.length) {
      for (i = 0; i < R.length; i++) { R[i].closet = _roomsTr.out[i].closet; R[i].nameIdx = _roomsTr.out[i].nameIdx; }
      return R;
    }
    /* EACH STOREY INHERITS FROM ITS OWN LAST ROOMS (v200, barndo loft): the
       loft's room took the ground floor's name when the build read the ground
       floor right after the loft's plan was painted, and the other way round */
    var svT = 0;
    try { svT = (H && H.storey) ? (H.storey() | 0) : 0; } catch (eSvT) { svT = 0; }
    var _roomsLast = _roomsLastBy[svT] || [];
    /* the closets first: they take no name and leave every name alone */
    for (i = 0; i < R.length; i++) R[i].closet = _isCloset(R[i]);
    /* ONE PREDECESSOR, ONE HEIR. Split a room in two and BOTH halves overlap
       the room they came from, so both were claiming its name and the plan
       showed the same room twice. The bigger piece keeps it -- it is the one a
       person still recognises -- and the other is a new room and gets a new
       name (Marek). */
    var claims = [];
    for (i = 0; i < R.length; i++) {
      for (k = 0; k < _roomsLast.length; k++) {
        if (R[i].closet || _roomsLast[k].closet) continue;
        var ov = _overlapArea(R[i], _roomsLast[k]);
        if (ov > 0) claims.push({ r: i, p: k, ov: ov });
      }
      R[i].nameIdx = null;
    }
    claims.sort(function (a, b) { return b.ov - a.ov; });
    for (i = 0; i < claims.length; i++) {
      var cl = claims[i];
      if (R[cl.r].nameIdx != null || taken[cl.p]) continue;
      R[cl.r].nameIdx = _roomsLast[cl.p].nameIdx;
      taken[cl.p] = 1;
    }
    for (i = 0; i < R.length; i++) if (R[i].nameIdx != null) used[R[i].nameIdx] = 1;
    /* AND A ROOM WITH NO PREDECESSOR ASKS THE PINS BEFORE IT TAKES A NUMBER.
       This is why a saved name did not survive a reload, and it had nothing to
       do with the saving: on a fresh boot _roomsLast is empty, so no room
       inherits, and every one of them was stamped with the first unused index
       -- a name BY SCAN ORDER, the very thing the pins exist to prevent. And
       because _roomNameIdx returns r.nameIdx before it looks at anything else,
       the pins were then never read at all. The store was right the whole time
       and this overwrote it three lines earlier.
       Inheritance still wins where there IS a predecessor: that is a live drag,
       and the pin agrees with it anyway (Marek). */
    var PP = _roomPins(), pk, byK;
    for (i = 0; i < R.length; i++) {
      if (R[i].nameIdx != null || !PP || R[i].closet) continue;
      pk = _roomPinAt(R[i]);
      if (pk < 0) continue;
      byK = _roomIdxOfKey(PP[pk].k);
      R[i].nameIdx = (byK >= 0) ? byK : (PP[pk].n | 0);
      used[R[i].nameIdx] = 1;
    }
    var next = 0;
    for (i = 0; i < R.length; i++) {
      if (R[i].nameIdx != null || R[i].closet) continue;
      while (used[next]) next++;
      R[i].nameIdx = next % _roomNamesTable().length;
      used[R[i].nameIdx] = 1;
    }
    _roomsLastBy[svT] = R;
    _roomsTr.key = tk;
    _roomsTr.out = R.map(function (r9) { return { closet: r9.closet, nameIdx: r9.nameIdx }; });
    return R;
  }
  function _rooms() {
    var H = _PH; if (!H) return [];
    var base = H.rects(H.poly()), ps = H.partitions(), cells = [], i, q, w, j;
    for (i = 0; i < base.length; i++) {
      var cur = [base[i]];
      for (q = 0; q < ps.length; q++) {
        var nxt = [];
        for (w = 0; w < cur.length; w++) {
          var r = cur[w];
          if (ps[q].o === 'v' && ps[q].c > r.x0 + 0.05 && ps[q].c < r.x1 - 0.05) {
            nxt.push({ x0: r.x0, x1: ps[q].c, z0: r.z0, z1: r.z1 });
            nxt.push({ x0: ps[q].c, x1: r.x1, z0: r.z0, z1: r.z1 });
          } else if (ps[q].o === 'h' && ps[q].c > r.z0 + 0.05 && ps[q].c < r.z1 - 0.05) {
            nxt.push({ x0: r.x0, x1: r.x1, z0: r.z0, z1: ps[q].c });
            nxt.push({ x0: r.x0, x1: r.x1, z0: ps[q].c, z1: r.z1 });
          } else nxt.push(r);
        }
        cur = nxt;
      }
      for (q = 0; q < cur.length; q++) cells.push(cur[q]);
    }
    var par = [];
    for (i = 0; i < cells.length; i++) par.push(i);
    function find(a) { while (par[a] !== a) { par[a] = par[par[a]]; a = par[a]; } return a; }
    function join(a, b) { a = find(a); b = find(b); if (a !== b) par[b] = a; }
    /* is every millimetre of [lo,hi] on the line o=c covered by wall? */
    /* NOBODY WALKS THROUGH A SLOT. A gap counts as a way between two rooms only
       if a person could use it; anything narrower is two walls meeting, however
       many millimetres of arithmetic sit between them. And the ENDS of a shared
       edge are inside the outer walls, where there is no way through either --
       measuring them as open was why everything collapsed into one room. */
    var WALK = 0.30;
    function sealed(o, c, lo, hi) {
      /* THE INSET BELONGS TO THE SHELL, NOT TO EVERY EDGE. Both ends of a
         shared edge were pulled in by a wall thickness on the assumption they
         ran into the outer walls -- true for an edge that crosses the whole
         building, wrong for one that starts at another partition. Pulled in
         where there is no wall, the leftover slot fell under the width a person
         walks through, the edge counted as SEALED and the two halves stayed
         apart... or the reverse, and a wall that really does close a room was
         read as leaving a way round it, so the new wall made no new room at
         all. The inset is applied only where the edge actually meets the
         outline (Marek). */
      var P4 = H.poly();
      var pLo = (o === 'v') ? [c, lo] : [lo, c];
      var pHi = (o === 'v') ? [c, hi] : [hi, c];
      if (!H.inPoly(pLo[0], pLo[1] + (o === 'v' ? 0.02 : 0), P4) ||
          _nearOutline(pLo[0], pLo[1])) lo += H.wallThk();
      if (!H.inPoly(pHi[0], pHi[1] - (o === 'v' ? 0.02 : 0), P4) ||
          _nearOutline(pHi[0], pHi[1])) hi -= H.wallThk();
      if (hi - lo <= WALK) return true;
      var free = [[lo, hi]], k, f;
      for (k = 0; k < ps.length; k++) {
        /* A LINE TOO NEAR ANOTHER TO CUT ITS OWN CELL STILL CLOSES THE EDGE
           (v110). Cells are cut only where a line is more than 0.05 m from
           the cell's side, so the room line between the kitchen and the
           living room, 0.05 m from the line of the closet wall above, cut
           nothing -- and the edge it should have closed was looked for at
           0.02 m, found open, and the two rooms came out as one (Marek:
           delici cara nedeli living a kitchen). A line counts for an edge
           as near as it could have failed to cut there. */
        if (ps[k].o !== o || Math.abs(ps[k].c - c) > 0.055) continue;
        var sp = H.partSpan(ps[k]);
        if (!sp) continue;
        var out = [];
        for (f = 0; f < free.length; f++) {
          var a = free[f][0], b = free[f][1];
          if (sp[1] <= a || sp[0] >= b) { out.push([a, b]); continue; }
          if (sp[0] > a) out.push([a, sp[0]]);
          if (sp[1] < b) out.push([sp[1], b]);
        }
        free = out;
      }
      for (f = 0; f < free.length; f++) if (free[f][1] - free[f][0] > WALK) return false;
      return true;
    }
    for (i = 0; i < cells.length; i++) {
      for (j = i + 1; j < cells.length; j++) {
        var A = cells[i], B = cells[j];
        var ovZ = Math.min(A.z1, B.z1) - Math.max(A.z0, B.z0);
        var ovX = Math.min(A.x1, B.x1) - Math.max(A.x0, B.x0);
        if (ovZ > 0.05 && (Math.abs(A.x1 - B.x0) < 0.02 || Math.abs(B.x1 - A.x0) < 0.02)) {
          var cx = (Math.abs(A.x1 - B.x0) < 0.02) ? A.x1 : B.x1;
          if (!sealed('v', cx, Math.max(A.z0, B.z0), Math.min(A.z1, B.z1))) join(i, j);
        }
        if (ovX > 0.05 && (Math.abs(A.z1 - B.z0) < 0.02 || Math.abs(B.z1 - A.z0) < 0.02)) {
          var cz = (Math.abs(A.z1 - B.z0) < 0.02) ? A.z1 : B.z1;
          if (!sealed('h', cz, Math.max(A.x0, B.x0), Math.min(A.x1, B.x1))) join(i, j);
        }
      }
    }
    /* A SLIVER IS NOT A ROOM (v108). The lines of walls elsewhere in the
       house cut the open floor into strips, and a strip under WALK both ways
       had every edge counted as a slot nobody walks through -- so a scrap of
       0.02 m2, where the room line under the hall met the one between the
       kitchen and the living room, stood as a room of its own with a + of
       its own (Marek: porad je tam moc mistnosti). A scrap under 0.15 m2
       with NO wall at all along one of its edges is floor of the room across
       that edge: it joins the one it shares the longest bare edge with, and
       only that one, so a scrap never joins two rooms into one. */
    (function () {
      var P5 = H.poly(), grp = {}, a, b, k, g;
      function bare(o, c, lo, hi) {
        var mx = (o === 'v') ? c : (lo + hi) / 2, mz = (o === 'v') ? (lo + hi) / 2 : c, n;
        if (!H.inPoly(mx, mz, P5) || _nearOutline(mx, mz)) return false;
        for (n = 0; n < ps.length; n++) {
          if (ps[n].o !== o || Math.abs(ps[n].c - c) > 0.055) continue;
          var sp5 = H.partSpan(ps[n]);
          if (sp5 && Math.min(sp5[1], hi) - Math.max(sp5[0], lo) > 0.01) return false;
        }
        return true;
      }
      for (a = 0; a < cells.length; a++) (grp[find(a)] = grp[find(a)] || []).push(a);
      for (g in grp) {
        if (!Object.prototype.hasOwnProperty.call(grp, g)) continue;
        var ix5 = grp[g], ar5 = 0, bestJ = -1, bestL = 0.02;
        for (k = 0; k < ix5.length; k++) ar5 += (cells[ix5[k]].x1 - cells[ix5[k]].x0) * (cells[ix5[k]].z1 - cells[ix5[k]].z0);
        if (ar5 >= 0.15) continue;
        for (k = 0; k < ix5.length; k++) {
          var A5 = cells[ix5[k]];
          for (b = 0; b < cells.length; b++) {
            if (find(b) === find(ix5[0])) continue;
            var B5 = cells[b], o5 = '', c5 = 0, lo5 = 0, hi5 = 0;
            if (Math.abs(A5.x1 - B5.x0) < 0.02 || Math.abs(B5.x1 - A5.x0) < 0.02) {
              o5 = 'v'; c5 = (Math.abs(A5.x1 - B5.x0) < 0.02) ? A5.x1 : B5.x1; lo5 = Math.max(A5.z0, B5.z0); hi5 = Math.min(A5.z1, B5.z1);
            } else if (Math.abs(A5.z1 - B5.z0) < 0.02 || Math.abs(B5.z1 - A5.z0) < 0.02) {
              o5 = 'h'; c5 = (Math.abs(A5.z1 - B5.z0) < 0.02) ? A5.z1 : B5.z1; lo5 = Math.max(A5.x0, B5.x0); hi5 = Math.min(A5.x1, B5.x1);
            } else continue;
            if (hi5 - lo5 > bestL && bare(o5, c5, lo5, hi5)) { bestL = hi5 - lo5; bestJ = b; }
          }
        }
        if (bestJ >= 0) join(bestJ, ix5[0]);
      }
    })();
    var byRoot = {}, out2 = [];
    for (i = 0; i < cells.length; i++) {
      var rt = find(i);
      (byRoot[rt] = byRoot[rt] || []).push(cells[i]);
    }
    for (var kk in byRoot) {
      if (!Object.prototype.hasOwnProperty.call(byRoot, kk)) continue;
      var rs = byRoot[kk], area = 0, big = rs[0], mnx = 1e9, mnz = 1e9;
      for (i = 0; i < rs.length; i++) {
        var a2 = (rs[i].x1 - rs[i].x0) * (rs[i].z1 - rs[i].z0);
        area += a2;
        mnx = Math.min(mnx, rs[i].x0); mnz = Math.min(mnz, rs[i].z0);
        if (a2 > (big.x1 - big.x0) * (big.z1 - big.z0)) big = rs[i];
      }
      /* THE NAME GOES IN THE MIDDLE OF THE ROOM, not in the middle of whichever
         rectangle happens to be biggest. On an L-shaped room those are two
         different places and the label sat off to one side. The centre of area
         is the answer -- and where that falls outside the room, which an L can
         do, the biggest piece takes it back (Marek). */
      var ccx = 0, ccz = 0;
      for (i = 0; i < rs.length; i++) {
        var aw = (rs[i].x1 - rs[i].x0) * (rs[i].z1 - rs[i].z0);
        ccx += ((rs[i].x0 + rs[i].x1) / 2) * aw;
        ccz += ((rs[i].z0 + rs[i].z1) / 2) * aw;
      }
      ccx = area ? (ccx / area) : (big.x0 + big.x1) / 2;
      ccz = area ? (ccz / area) : (big.z0 + big.z1) / 2;
      var inside = false;
      for (i = 0; i < rs.length; i++) {
        if (ccx >= rs[i].x0 && ccx <= rs[i].x1 && ccz >= rs[i].z0 && ccz <= rs[i].z1) { inside = true; break; }
      }
      if (!inside) { ccx = (big.x0 + big.x1) / 2; ccz = (big.z0 + big.z1) / 2; }
      out2.push({ rects: rs, area: area, mnx: mnx, mnz: mnz, cx: ccx, cz: ccz });
    }
    /* THE SAME ROOM HAS TO STAY THE SAME ROOM. Colour and name are taken by
       POSITION in this list, and the list came out of an object, whose order is
       whatever the engine feels like -- so a room changed its name and colour
       every time a wall moved. Ordered by its top-left corner, a room keeps its
       place while it only changes size, which is what a person watching a wall
       move expects to see (Marek). */
    out2.sort(function (a, b) { return (a.mnz - b.mnz) || (a.mnx - b.mnx); });
    return out2;
  }
  function _nearOutline(x, z) {
    var H = _PH; if (!H) return false;
    var P = H.poly(), i;
    for (i = 0; i < P.length; i++) {
      var a = P[i], b = P[(i + 1) % P.length];
      var dx = b[0] - a[0], dz = b[1] - a[1], L2 = dx * dx + dz * dz;
      if (L2 < 1e-9) continue;
      var t = H.clamp(((x - a[0]) * dx + (z - a[1]) * dz) / L2, 0, 1);
      if (Math.hypot(x - (a[0] + dx * t), z - (a[1] + dz * t)) < 0.05) return true;
    }
    return false;
  }
  /* A ROOM NAME IS PINNED TO A POINT, NOT TO A CENTRE. The key here was the
     room's own centre rounded to 100 mm -- and a centre MOVES the moment a
     partition is dragged, so the key missed, the lookup fell through to
     i % table.length, and the room came back named by the order the fill
     happened to hand it out. Marek's rule: dragging a wall must not rename a
     room. A point that does not move is the one the person TAPPED when they
     named it, and the room that owns it is simply the room that CONTAINS it --
     which is the same test the tap itself already uses.
     A SECOND FAULT WENT WITH IT. The store was written as a MAP, host.roomNames()
     [key] = idx, and at least one host hands back an ARRAY: string keys on an
     Array survive in memory and vanish through JSON.stringify, so those names
     were never saved at all. Pins are ARRAY ELEMENTS -- { x, z, n } -- which
     serialize.
     NOTHING IS LOST AND NOTHING BREAKS FOR THE OTHER PRODUCTS: the old key is
     still READ, so existing names keep working, and a host that still hands back
     an object keeps the old behaviour end to end until it moves to a list. */
  function _roomKey(r) {
    var H = _PH; if (!H) return ''; return r.cx.toFixed(1) + '|' + r.cz.toFixed(1); }
  function _roomPins() {
    var H = _PH, v = H && H.roomNames();
    return Array.isArray(v) ? v : null;
  }
  function _roomHolds(r, x, z) {
    var j, rc;
    if (!r || !r.rects) return false;
    for (j = 0; j < r.rects.length; j++) {
      rc = r.rects[j];
      if (x >= rc.x0 && x <= rc.x1 && z >= rc.z0 && z <= rc.z1) return true;
    }
    return false;
  }
  /* ONE ROOM, TWO NAMES OF THE OPEN KIND (v169, plan 1000075648, Marek:
     misto living room kitchen se mistnost naimportovala jako Hall). A hall
     with no wall to the kitchen / living room is one room with two pins,
     and the first pin named it: HALL, a kitchen in a hall, the plan kept out
     of the library, no sofa set, no heating head. Where every name in the
     room is of the open kind -- the living room, the kitchen, the dining
     room, a hall, an entry, a mudroom -- the main one names it: the living
     room before the kitchen before the dining room before the mudroom
     before a hall. Two names where one should have a wall (a bedroom and a
     hall) stay as they were: the first, and the audit says a wall is missing. */
  var PIN_OPEN = { 'rooms.livingroom': 5, 'rooms.kitchen': 4, 'rooms.diningroom': 3, 'rooms.mudroom': 2, 'rooms.hallway': 1, 'rooms.hall': 1 };
  function _pinBest(P, idx) {
    var best = -1, bR = -1, q;
    if (!idx.length) return -1;
    for (q = 0; q < idx.length; q++) {
      var rk = PIN_OPEN[String(P[idx[q]].k || '')];
      if (rk == null) return idx[0];
      if (rk > bR) { bR = rk; best = idx[q]; }
    }
    return best;
  }
  /* the non-closet pins inside this room */
  function _roomPinsIn(r, P) {
    var out = [], k, H = _PH, sv = 0;
    /* A PIN WITH st NAMES A ROOM ON THAT STOREY ONLY (v200, barndo loft); one
       without it names on every storey, as before. A host without storey()
       is one storey. */
    try { sv = (H && H.storey) ? (H.storey() | 0) : 0; } catch (eSv) { sv = 0; }
    for (k = 0; P && k < P.length; k++) {
      if (!P[k] || (P[k].st != null && (P[k].st | 0) !== sv)) continue;
      if (!_closetPin(P[k]) && typeof P[k].x === 'number' && _roomHolds(r, P[k].x, P[k].z)) out.push(k);
    }
    return out;
  }
  /* THE STOREY BELOW, FAINT, BEHIND THE PLAN ON SHOW (v200, Marek: kontext
     prvniho podlazi 30% opacity). UL = { alpha, poly, wall, lines: [{ a, b,
     t }] } in plan metres, from the host's underlay(). Painted AFTER the
     storey on show with destination-over, so it lies behind it: the floor of
     the storey on show hides it, the open part shows the storey below -- and
     every hit list drawFloor keeps (names, icons) stays the shown storey's. */
  function _paintUnder(ctx, UL, ppm, ox, oy) {
    var H = _PH, mods = (H && H.modules) ? (H.modules() || []) : [], m0 = mods[0] || {}, mx0 = m0.x || 0, mz0 = m0.z || 0, i;
    function X(v) { return ox + (v + mx0) * ppm; }
    function Y(v) { return oy + (v + mz0) * ppm; }
    var P = UL.poly, W = (UL.wall > 0) ? UL.wall : 0.15;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.globalAlpha = (UL.alpha > 0 && UL.alpha <= 1) ? UL.alpha : 0.3;
    /* destination-over: what is drawn first lies on top, so the partitions,
       then the outer walls, then the floor */
    ctx.strokeStyle = '#6B7075';
    ctx.lineCap = 'butt';
    for (i = 0; UL.lines && i < UL.lines.length; i++) {
      var L = UL.lines[i];
      if (!L || !L.a || !L.b) continue;
      ctx.lineWidth = Math.max(1, (L.t > 0 ? L.t : 0.10) * ppm);
      ctx.beginPath(); ctx.moveTo(X(L.a[0]), Y(L.a[1])); ctx.lineTo(X(L.b[0]), Y(L.b[1])); ctx.stroke();
    }
    var IN = INSET(P, W / 2);
    ctx.lineWidth = W * ppm; ctx.lineJoin = 'miter';
    ctx.beginPath(); ctx.moveTo(X(IN[0][0]), Y(IN[0][1]));
    for (i = 1; i < IN.length; i++) ctx.lineTo(X(IN[i][0]), Y(IN[i][1]));
    ctx.closePath(); ctx.stroke();
    ctx.fillStyle = '#EDEBE6';
    ctx.beginPath(); ctx.moveTo(X(P[0][0]), Y(P[0][1]));
    for (i = 1; i < P.length; i++) ctx.lineTo(X(P[i][0]), Y(P[i][1]));
    ctx.closePath(); ctx.fill();
    ctx.restore();
  }
  /* the pin this room contains, or -1 */
  function _roomPinAt(r) {
    var P = _roomPins();
    if (!P) return -1;
    return _pinBest(P, _roomPinsIn(r, P));
  }
  /* WHICH ROW IS THIS KEY, in whatever table is in force */
  function _roomIdxOfKey(k) {
    var T = _roomNamesTable(), q;
    if (!k) return -1;
    for (q = 0; q < T.length; q++) if (T[q] && T[q].i18n === k) return q;
    return -1;
  }
  function _roomNameIdx(r, i) {
    var H = _PH; if (!H) return false;
    var P0 = _roomPins(), k0 = (P0 ? _roomPinAt(r) : -1);
    if (k0 >= 0 && P0[k0] && P0[k0].k) {
      var byKey0 = _roomIdxOfKey(P0[k0].k);
      if (byKey0 >= 0) return byKey0;
    }
    if (r && r.nameIdx != null) return r.nameIdx | 0;
    var P = _roomPins(), k = (P ? _roomPinAt(r) : -1);
    /* A PIN REMEMBERS THE ROOM'S KEY, not its row number. The index alone is a
       position in a list -- and the list changes the moment a product takes a
       subset of it, or the day another row is added in the middle. Then every
       saved name silently becomes a different room. The key does not move; the
       index is kept only as the fallback for pins written before this. */
    if (k >= 0) {
      var byKey = _roomIdxOfKey(P[k].k);
      return (byKey >= 0) ? byKey : (P[k].n | 0);
    }
    /* the retired centre key, still read so nobody's names disappear */
    var v = (H.roomNames() || {})[_roomKey(r)];
    return (v == null) ? (i % _roomNamesTable().length) : (v | 0);
  }
  function _roomName(r, i) {
    var H = _PH; if (!H) return '';
    var D = DRAW;
    if (D) return D.roomName(_roomNameIdx(r, i), H.t);
    var t = DRAW.roomTable()[_roomNameIdx(r, i) % DRAW.roomTable().length];
    return t ? H.t(t.i18n, t.def) : '';
  }
  function _roomNamesTable() {
    var H = _PH; if (!H) return []; return DRAW.roomTable(); }
  function cycleRoomNameAt(x, z) {
    var H = _PH; if (!H) return false;
    var R = _roomsTracked(), i, j;
    for (i = 0; i < R.length; i++) {
      if (R[i].closet) continue;
      for (j = 0; j < R[i].rects.length; j++) {
        var rc = R[i].rects[j];
        if (x < rc.x0 || x > rc.x1 || z < rc.z0 || z > rc.z1) continue;
        R[i].nameIdx = (_roomNameIdx(R[i], i) + 1) % _roomNamesTable().length;
        /* the pin goes in AT THE TAP, which is the point that will still be in
           this room after a wall moves */
        var PN = _roomPins(), pk, TB = _roomNamesTable();
        var kk = (TB[R[i].nameIdx] || {}).i18n || '';
        if (PN) {
          pk = _roomPinAt(R[i]);
          if (pk >= 0) {
            /* v169: the tapped name is the room's only one -- another pin
               in the room would take the name back by its rank */
            var oth = _roomPinsIn(R[i], PN), q9;
            for (q9 = oth.length - 1; q9 >= 0; q9--) if (oth[q9] !== pk) { PN.splice(oth[q9], 1); if (oth[q9] < pk) pk--; }
            PN[pk].n = R[i].nameIdx; PN[pk].k = kk; PN[pk].x = x; PN[pk].z = z;
          }
          else PN.push({ x: x, z: z, n: R[i].nameIdx, k: kk });
        } else {
          H.roomNames()[_roomKey(R[i])] = R[i].nameIdx;   /* host still on the map */
        }
        H.rebuild(); H.save();
        H.toast(_roomName(R[i], i));
        return true;
      }
    }
    return false;
  }

  /* ---------- THE OVERLAY -----------------------------------------------
     The canvas the plan is drawn on and the mapping from metres to pixels.
     Last piece of the move, and the half that is pure: size a canvas over the
     viewport, read the scale off the live camera by projecting the origin and
     one metre, hand the context to the drawing. No product's variables in it.
     The POINTER half -- what a finger on a wall or a door does -- stays with
     the product for now, because it is bound to that product's canvas and its
     engine; it is the next thing to come across, and it is the one that will
     make the sauna and the container behave identically rather than merely
     look it (Marek). */
  var OVERLAY = {
    el: null,
    ensure: function () {
      if (this.el) return this.el;
      if (typeof document === 'undefined') return null;
      var root = document.getElementById('cf-root');
      if (!root) return null;
      var cv = document.createElement('canvas');
      cv.id = 'cf-plan2d';
      cv.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%;' +
                         'pointer-events:none;display:none;z-index:6;';
      root.appendChild(cv);
      this.el = cv;
      return cv;
    },
    show: function () { var c = this.ensure(); if (c) c.style.display = 'block'; },
    hide: function () {
      if (this.el) this.el.style.display = 'none';
      try { TRACE.slider(null); } catch (e) {}    /* the control goes with the drawing */
    },
    /* the last engine painted with, so anything the asset owns can ask for a
       repaint without being handed one */
    _eng: null,
    repaint: function () { try { if (this._eng) this.paint(this._eng); } catch (e) {} },
    /* metres to pixels, taken from the camera itself so it follows every zoom */
    map: function (eng) {
      var T = global.THREE;
      if (!eng || !eng.camera || !eng.renderer || !T) return null;
      var el = eng.renderer.domElement;
      var w = el.clientWidth || el.width, h = el.clientHeight || el.height;
      if (!w || !h) return null;
      function pr(x, z) {
        var v = new T.Vector3(x, 0, z).project(eng.camera);
        return { x: (v.x * 0.5 + 0.5) * w, y: (-v.y * 0.5 + 0.5) * h };
      }
      var o = pr(0, 0), ux = pr(1, 0), sx = ux.x - o.x;
      if (!isFinite(sx) || Math.abs(sx) < 1e-6) return null;
      return { ppm: sx, ox: o.x, oy: o.y, w: w, h: h };
    },
    /* clear it, size it to the display and draw the floor into it */
    paint: function (eng) {
      try {
        var cv = this.ensure(); if (!cv) return null;
        var m = this.map(eng); if (!m) return null;
        var dpr = global.devicePixelRatio || 1;
        if (cv.width !== Math.round(m.w * dpr) || cv.height !== Math.round(m.h * dpr)) {
          cv.width = Math.round(m.w * dpr); cv.height = Math.round(m.h * dpr);
        }
        var ctx = cv.getContext('2d'); if (!ctx) return null;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, m.w, m.h);
        drawFloor(ctx, 0, m.ppm, m.ox, m.oy);
        try {
          var HU = _PH, UL = (HU && HU.underlay) ? HU.underlay() : null;
          if (UL && UL.poly && UL.poly.length >= 3) _paintUnder(ctx, UL, m.ppm, m.ox, m.oy);
        } catch (eUL) {}
        /* THE PICTURE ON THE SCREEN ONLY (v132, Marek: obrazek rozhodne jen pro
           vlastnika; nesmi se dostat ven v ramci sdileni rozpoctu). v131 drew it
           last inside drawFloor -- and drawFloor is also what draws the sheet
           and the PDF, so the buyer's picture could ride out with them. It is
           drawn here now, over the plan on this screen and nowhere else, at the
           same place: the plan's own mapping, module 0 at the origin. */
        try {
          var HT = _PH, mdT = (HT && HT.modules) ? (HT.modules() || []) : [], m0T = mdT[0] || {}, mxT = m0T.x || 0, mzT = m0T.z || 0;
          var ppT = m.ppm, oxT = m.ox, oyT = m.oy;
          if (HT && TRACE.img) TRACE.draw(ctx, ppT, function (v) { return oxT + (v + mxT) * ppT; }, function (v) { return oyT + (v + mzT) * ppT; });
        } catch (eTR) {}
        /* the underlay's rule is nailed to the VIEWPORT, so it is drawn here
           where the viewport is known -- and not by drawFloor, which is also
           what draws the sheet */
        try { TRACE.rule(ctx, m.ppm, m.w, m.h); } catch (eR) {}
        try { TRACE.slider(cv.parentNode); } catch (eS) {}
        this._eng = eng;
        return m;
      } catch (e) { return null; }
    }
  };


  /* =========================================================================
     FIXTURES -- what a room is furnished with, the big pieces only. The asset
     owns the catalogue (sizes, the plan drawing, the catalogue rows), the
     drawing in the plan and the verbs a product's pointer handler calls; the
     product owns the list itself (H.fixturesRef()), its 3D blocks and its
     bill. A fixture is { id, kind, x, z, rot } -- x, z its centre in plan
     metres, rot 0..3 quarter turns; w along its own x, d along its own z.
     ========================================================================= */
  var FIX = {
    /* v206 (Marek 2026-10-12: Rozpocet: sporak, mycka s pripojovaci sadou,
       digestor -- nic mensiho nestavet, hranice / obecne pravidlo): every
       kitchen run carries its appliances with its sink -- FKI00013 an
       electric range, FKI00014 a dishwasher with FKI00015 its installation
       kit, FKI00016 a range hood -- billed with the run by every product
       that bills a piece's extras (ADU, RANCH, GARAGE APT, BARNDO,
       CONTAINER). Nothing smaller is billed. */
    kitchen24: { grp: 'kitchen', w: 2.4, d: 0.6, h: 0.9,  wall: true, code: 'FKI00001', extra: ['SKS08401', 'FKI00013', 'FKI00014', 'FKI00015', 'FKI00016'], i18n: 'container_fit_kit_1', def: 'Kitchen counter', sub: '2400' },
    kitchen30: { grp: 'kitchen', w: 3.0, d: 0.6, h: 0.9,  wall: true, code: 'FKI00001', extra: ['SKS08401', 'FKI00013', 'FKI00014', 'FKI00015', 'FKI00016'], i18n: 'container_fit_kit_1', def: 'Kitchen counter', sub: '3000' },
    kitchen36: { grp: 'kitchen', w: 3.6, d: 0.6, h: 0.9,  wall: true, code: 'FKI00001', extra: ['SKS08401', 'FKI00013', 'FKI00014', 'FKI00015', 'FKI00016'], i18n: 'container_fit_kit_1', def: 'Kitchen counter', sub: '3600' },
    kitchenL:  { grp: 'kitchen', w: 2.4, d: 1.8, h: 0.9,  wall: true, code: 'FKI00008', extra: ['SKS08401', 'FKI00013', 'FKI00014', 'FKI00015', 'FKI00016'], i18n: 'fit_kit_l', def: 'L kitchen', sub: 'L', shape: 'L' },
    kitchenU:  { grp: 'kitchen', w: 3.0, d: 2.4, h: 0.9,  wall: true, code: 'FKI00009', extra: ['SKS08401', 'FKI00013', 'FKI00014', 'FKI00015', 'FKI00016'], i18n: 'fit_kit_u', def: 'U kitchen', sub: 'U', shape: 'U' },
    island:    { grp: 'kitchen', w: 1.2, d: 0.8, h: 0.9,  wall: false, code: 'FKI00002', i18n: 'container_fit_kit_2', def: 'Kitchen island' },
    bath18:    { grp: 'bathroom', w: 1.8, d: 0.8, h: 0.55, wall: true, code: 'SBB08001', extra: ['SFA00002'], i18n: 'container_fit_bath_4', def: 'Bathtub', sub: '1800' },
    bath15:    { grp: 'bathroom', w: 1.52, d: 0.76, h: 0.55, wall: true, code: 'SBB07601', extra: ['SFA00002'], i18n: 'container_fit_bath_4', def: 'Bathtub', sub: '1520' },
    shower90:  { grp: 'bathroom', w: 0.9, d: 0.9, h: 2.0,  wall: true, code: 'SBS09001', extra: ['SFA00003'], i18n: 'rooms.shower', def: 'Shower', sub: '900', tiled: true },
    shower120: { grp: 'bathroom', w: 1.2, d: 0.9, h: 2.0,  wall: true, code: 'SBS12001', extra: ['SFA00003'], i18n: 'rooms.shower', def: 'Shower', sub: '1200', tiled: true },
    basin50:   { grp: 'bathroom', w: 0.5, d: 0.3, h: 0.85, wall: true, code: 'SBS05001', extra: ['SFA00001'], i18n: 'container_fit_bath_2', def: 'Sink', sub: '500' },
    basin80:   { grp: 'bathroom', w: 0.8, d: 0.4, h: 0.85, wall: true, code: 'SBS08001', extra: ['SFA00001'], i18n: 'container_fit_bath_2', def: 'Sink', sub: '800' },
    wc:        { grp: 'bathroom', w: 0.375, d: 0.55, h: 0.4, wall: true, code: 'SBT05501', i18n: 'container_fit_bath_1', def: 'Toilet' },
    bedDouble: { grp: 'bedroom', w: 1.6, d: 2.0, h: 0.5,  wall: true, code: 'FMA00001', extra: ['FMA00002'], i18n: 'container_fit_bed_1', def: 'Double bed' },
    bedSingle: { grp: 'bedroom', w: 0.9, d: 2.0, h: 0.5,  wall: true, code: 'FKB00001', i18n: 'container_fit_bed_2', def: 'Single bed' },
    bunk:      { grp: 'bedroom', w: 0.9, d: 2.0, h: 1.6,  wall: true, code: 'FKB00002', i18n: 'container_fit_kids_2', def: 'Bunk bed' },
    bedside:   { grp: 'bedroom', w: 0.45, d: 0.4, h: 0.5, wall: true, code: 'FMA00003', i18n: 'container_fit_bed_4', def: 'Nightstand' },
    wardrobe:  { grp: 'bedroom', w: 1.2, d: 0.6, h: 2.1,  wall: true, code: 'FMA00004', i18n: 'fit_hal_2', def: 'Wardrobe cabinet' },
    desk:      { grp: 'bedroom', w: 1.2, d: 0.6, h: 0.75, wall: true, code: 'FKB00003', extra: ['FKB00004'], i18n: 'container_fit_kids_3', def: 'Desk' },
    sofa2:     { grp: 'living', w: 1.6, d: 0.9, h: 0.8,   wall: true, code: 'FLI00001', i18n: 'bench_v2', def: 'Sofa', sub: '2' },
    sofa3:     { grp: 'living', w: 2.1, d: 0.9, h: 0.8,   wall: true, code: 'FLI00001', i18n: 'bench_v2', def: 'Sofa', sub: '3' },
    sofa4:     { grp: 'living', w: 2.6, d: 0.9, h: 0.8,   wall: true, code: 'FLI00001', i18n: 'bench_v2', def: 'Sofa', sub: '4' },
    sofaL:     { grp: 'living', w: 2.6, d: 1.8, h: 0.8,   wall: true, code: 'FLI00002', i18n: 'container_fit_liv_2', def: 'L sofa', sub: 'L', shape: 'L' },
    sofaU:     { grp: 'living', w: 3.0, d: 2.4, h: 0.8,   wall: true, code: 'FLI00002', i18n: 'fit_liv_u', def: 'U sofa', sub: 'U', shape: 'U' },
    armchair:  { grp: 'living', w: 0.8, d: 0.85, h: 0.8,  wall: false, code: 'FLI00003', i18n: 'container_fit_liv_3', def: 'Armchair' },
    coffee:    { grp: 'living', w: 0.9, d: 0.6, h: 0.45,  wall: false, code: 'FLI00004', i18n: 'container_fit_liv_4', def: 'Coffee table' },
    tvStand:   { grp: 'living', w: 1.6, d: 0.45, h: 0.5,  wall: true, code: 'FLI00006', i18n: 'container_fit_liv_5', def: 'TV unit' },
    /* a dining set is the table AND its chairs: w x d is the whole set,
       tw x td the top, so plan, picker and model agree on the size */
    table:     { grp: 'living', w: 1.4, d: 1.7, tw: 1.4, td: 0.8, h: 0.75,  wall: false, code: 'FDI00001', extra: ['FDI00002', 'FDI00002', 'FDI00002', 'FDI00002'], i18n: 'fit_din_1', def: 'Dining table', sub: '4' },
    table2:    { grp: 'living', w: 0.8, d: 1.7, tw: 0.8, td: 0.8, h: 0.75,  wall: false, code: 'FDI00001', extra: ['FDI00002', 'FDI00002'], i18n: 'fit_din_1', def: 'Dining table', sub: '2' },
    table2r:   { grp: 'living', w: 0.9, d: 1.8, tw: 0.9, td: 0.9, h: 0.75,  wall: false, code: 'FDI00001', extra: ['FDI00002', 'FDI00002'], i18n: 'fit_din_1', def: 'Dining table', sub: '2 round', round: true },
    /* v150: three a side; and three a side with one at each end (6+2) */
    table6:    { grp: 'living', w: 1.8, d: 1.8, tw: 1.8, td: 0.9, h: 0.75,  wall: false, code: 'FDI00001', extra: ['FDI00002', 'FDI00002', 'FDI00002', 'FDI00002', 'FDI00002', 'FDI00002'], i18n: 'fit_din_1', def: 'Dining table', sub: '6', side: 3 },
    table8:    { grp: 'living', w: 2.9, d: 1.8, tw: 2.0, td: 0.9, h: 0.75,  wall: false, code: 'FDI00001', extra: ['FDI00002', 'FDI00002', 'FDI00002', 'FDI00002', 'FDI00002', 'FDI00002', 'FDI00002', 'FDI00002'], i18n: 'fit_din_1', def: 'Dining table', sub: '8', side: 3, ends: 1 },
    barTable:  { grp: 'kitchen', w: 1.2, d: 0.95, tw: 1.2, td: 0.6, h: 1.05, wall: false, code: 'FKI00002', extra: ['FKI00003', 'FKI00003'], i18n: 'fit_bar', def: 'Bar table', sub: '2' },
    /* v150: an island with its stools in front -- w x d the whole set, tw x td
       the counter at the back, a stool to every 0.6 m */
    island18:  { grp: 'kitchen', w: 1.8, d: 1.35, tw: 1.8, td: 0.9, h: 0.9, wall: false, code: 'FKI00002', extra: ['FKI00003', 'FKI00003', 'FKI00003'], i18n: 'container_fit_kit_2', def: 'Kitchen island', sub: '1800', stools: 3 },
    island24:  { grp: 'kitchen', w: 2.4, d: 1.35, tw: 2.4, td: 0.9, h: 0.9, wall: false, code: 'FKI00002', extra: ['FKI00003', 'FKI00003', 'FKI00003', 'FKI00003'], i18n: 'container_fit_kit_2', def: 'Kitchen island', sub: '2400', stools: 4 },
    /* v151: THE APPLIANCES ARE BILLED (Marek, appliances.tsv: FKI00010 a
       refrigerator, FKI00011 a washing machine, FKI00012 a clothes dryer). An
       imperial page draws and builds them at the US stock size the catalogue
       searches for -- wi x di, hi high: a 36 in fridge, a 27 in washer and
       dryer -- and a metric one at 600 mm, as before. appliance: stands free,
       nothing fixes it (no screws, no silicone) */
    fridge:    { grp: 'kitchen', w: 0.6, d: 0.65, h: 1.8, wi: 0.914, di: 0.76, hi: 1.78, wall: true, code: 'FKI00010', appliance: true, i18n: 'container_fit_kit_3', def: 'Fridge' },
    washer:    { grp: 'bathroom', w: 0.6, d: 0.6, h: 0.85, wi: 0.686, di: 0.76, hi: 0.99, wall: true, code: 'FKI00011', appliance: true, i18n: 'fit_washer', def: 'Washing machine' },
    dryer:     { grp: 'bathroom', w: 0.6, d: 0.6, h: 0.85, wi: 0.686, di: 0.76, hi: 0.99, wall: true, code: 'FKI00012', appliance: true, i18n: 'fit_dryer', def: 'Dryer' },
    wardrobe80: { grp: 'bedroom', w: 0.8, d: 0.6, h: 2.1, wall: true, code: 'FMA00004', i18n: 'fit_hal_2', def: 'Wardrobe cabinet', sub: '800' },
    wardrobe100: { grp: 'bedroom', w: 1.0, d: 0.6, h: 2.1, wall: true, code: 'FMA00004', i18n: 'fit_hal_2', def: 'Wardrobe cabinet', sub: '1000' },
    /* STAIRS (v85): a flight from the storey it is placed on to the one
       above. The size here is only a start -- the product that builds it
       gives the real run and width through the size hook, from the storey
       height and the kit. Not in FIX_ORDER, so a product offers them only
       when it asks for the 'stairs' group; the product builds and bills them.
       The flight climbs along its own +x. */
    stairStraight: { grp: 'stairs', w: 3.6, d: 0.9, h: 3.0, wall: true, stair: 'straight', code: null, i18n: 'pool_stairs', def: 'Stairs', subKey: 'sh_rect', subDef: 'Straight' },
    stairLoft:     { grp: 'stairs', w: 2.8, d: 0.7, h: 3.0, wall: true, stair: 'loft', code: null, i18n: 'pool_stairs', def: 'Stairs', subKey: 'rooms.loft', subDef: 'Loft' },
    /* v86: the L with a landing, the L with winders and the spiral. On a
       flight the mirror is the side the L turns to -- it flips the drawing
       across, not along (the arrow still ends at the top) */
    stairLLanding: { grp: 'stairs', w: 3.0, d: 2.0, h: 3.0, wall: true, stair: 'lLanding', code: null, i18n: 'pool_stairs', def: 'Stairs', subKey: 'base_shape_l', subDef: 'L shape', subKey2: 'stair_landing', subDef2: 'Landing' },
    stairLWinder:  { grp: 'stairs', w: 3.0, d: 2.0, h: 3.0, wall: true, stair: 'lWinder', code: null, i18n: 'pool_stairs', def: 'Stairs', subKey: 'base_shape_l', subDef: 'L shape', subKey2: 'stair_winder', subDef2: 'Winder' },
    stairSpiral:   { grp: 'stairs', w: 1.6, d: 1.6, h: 3.0, wall: false, stair: 'spiral', code: null, i18n: 'pool_stairs', def: 'Stairs', subKey: 'stair_spiral', subDef: 'Spiral' }
  };
  /* the picker's order: room by room -- kitchen, bathroom, bedroom, living
     and dining -- and inside a room from the biggest piece to the smallest */
  var FIX_ORDER = ['kitchen24', 'kitchen30', 'kitchen36', 'kitchenL', 'kitchenU', 'island', 'island18', 'island24', 'barTable', 'fridge',
                   'bath18', 'bath15', 'shower90', 'shower120', 'basin50', 'basin80', 'wc', 'washer', 'dryer',
                   'bedDouble', 'bedSingle', 'bunk', 'wardrobe80', 'wardrobe100', 'wardrobe', 'desk', 'bedside',
                   'sofa2', 'sofa3', 'sofa4', 'sofaL', 'sofaU', 'armchair', 'coffee', 'tvStand',
                   'table8', 'table6', 'table', 'table2', 'table2r'];

  /* the drawing of each piece from above, on a box of the piece's own
     proportions (vb = "w h" in drawing units), so a counter's hob rings stay
     round and a sofa reads as a sofa: back and arms, seat cushions, not a
     bare rectangle (Marek) */
  var FIX_SVG = {
    stairsL:  { vb: '48 40', svg:
      '<path d="M1 1h46v14H15v24H1z"/><path d="M1 15h14V1"/>' +
      '<path d="M19 1v14M23 1v14M27 1v14M31 1v14M35 1v14M39 1v14M43 1v14"/>' +
      '<path d="M1 19h14M1 23h14M1 27h14M1 31h14M1 35h14"/>' +
      '<path d="M8 37V8h36M40 5l4 3l-4 3"/>' },
    stairsLW: { vb: '48 40', svg:
      '<path d="M1 1h46v14H15v24H1z"/><path d="M15 15L1 1M15 15L8 1M15 15L1 8"/>' +
      '<path d="M19 1v14M23 1v14M27 1v14M31 1v14M35 1v14M39 1v14M43 1v14"/>' +
      '<path d="M1 19h14M1 23h14M1 27h14M1 31h14M1 35h14"/>' +
      '<path d="M8 37V8h36M40 5l4 3l-4 3"/>' },
    stairsSpiral: { vb: '24 24', svg:
      '<circle cx="12" cy="12" r="11"/><circle cx="12" cy="12" r="1.5"/>' +
      '<path d="M12 12L23 12M12 12L21.5 17.5M12 12L17.5 21.5M12 12L12 23M12 12L6.5 21.5M12 12L2.5 17.5M12 12L1 12M12 12L2.5 6.5M12 12L6.5 2.5M12 12L12 1M12 12L17.5 2.5"/>' +
      '<path d="M18 15.5A7 7 0 1 1 18.5 9M16 7.5l2.5 1.5l0.5-3"/>' },
    stairs:   { vb: '72 18', svg:
      '<rect x="1" y="1" width="70" height="16"/>' +
      '<path d="M7 1v16M13 1v16M19 1v16M25 1v16M31 1v16M37 1v16M43 1v16M49 1v16M55 1v16M61 1v16"/>' +
      '<path d="M4 9h62M62 6l4 3l-4 3"/>' },
    kitchen:  { vb: '96 24', svg:
      '<rect x="1" y="1" width="94" height="22" rx="1.5"/>' +
      '<rect x="6" y="5" width="16" height="14" rx="2"/><circle cx="14" cy="3.5" r="0.9"/>' +
      '<circle cx="76" cy="8" r="2.6"/><circle cx="84" cy="8" r="2.6"/><circle cx="76" cy="16" r="2.6"/><circle cx="84" cy="16" r="2.6"/>' +
      '<path d="M30 1v22M46 1v22"/>' },
    kitchenL: { vb: '32 24', svg:
      '<path d="M1 1h30v8H9v14H1z"/><rect x="13" y="2.5" width="9" height="5" rx="1.5"/><path d="M15 2.5v5M20 2.5v5"/>' +
      '<circle cx="4" cy="14" r="1.4"/><circle cx="6.5" cy="17.5" r="1.4"/><circle cx="4" cy="21" r="1.2"/><rect x="24" y="2.5" width="6" height="5" rx="1"/>' },
    kitchenU: { vb: '30 24', svg:
      '<path d="M1 1h28v22h-7V8H8v15H1z"/><rect x="11" y="2.5" width="8" height="4.5" rx="1.5"/>' +
      '<circle cx="4.2" cy="13" r="1.3"/><circle cx="4.2" cy="17" r="1.3"/><circle cx="25.8" cy="13" r="1.3"/><circle cx="25.8" cy="17" r="1.3"/><path d="M22 8v15"/>' },
    island:   { vb: '36 24', svg:
      '<rect x="1" y="1" width="34" height="22" rx="2"/><rect x="5" y="6" width="12" height="12" rx="2"/><path d="M11 6v12"/><path d="M22 4v16M28 4v16"/>' },
    bath:     { vb: '54 24', svg:
      '<rect x="1" y="1" width="52" height="22" rx="4"/><rect x="4" y="4" width="46" height="16" rx="7"/><circle cx="9" cy="12" r="1.2"/><path d="M6 8h4"/>' },
    shower:   { vb: '24 24', svg:
      '<rect x="1" y="1" width="22" height="22" rx="2"/><rect x="3" y="3" width="18" height="18" rx="1"/><path d="M3 3l18 18"/><circle cx="12" cy="12" r="1.5"/><path d="M4 21l4-4M21 4l-4 4"/>' },
    basin:    { vb: '40 24', svg:
      '<rect x="1" y="1" width="38" height="22" rx="3"/><ellipse cx="20" cy="12.5" rx="12" ry="7.5"/><ellipse cx="20" cy="12.5" rx="2" ry="1.3"/><circle cx="20" cy="4" r="1.4"/>' },
    wc:       { vb: '24 34', svg:
      '<rect x="2" y="1" width="20" height="9" rx="2"/><path d="M6 5.5h12"/><ellipse cx="12" cy="22" rx="8.5" ry="10.5"/><ellipse cx="12" cy="22.5" rx="5.5" ry="7.5"/>' },
    bed:      { vb: '32 40', svg:
      '<rect x="1" y="1" width="30" height="38" rx="2"/><rect x="3.5" y="3.5" width="11" height="6.5" rx="2"/><rect x="17.5" y="3.5" width="11" height="6.5" rx="2"/>' +
      '<path d="M1 13h30"/><path d="M3 20c6-2 8 2 13 0s7-2 13 0"/><rect x="3" y="30" width="26" height="7" rx="1.5"/>' },
    bedSingle:{ vb: '18 40', svg:
      '<rect x="1" y="1" width="16" height="38" rx="2"/><rect x="3.5" y="3.5" width="11" height="6.5" rx="2"/><path d="M1 13h16"/><path d="M3 20c3-2 5 2 8 0s4-2 4 0"/><rect x="3" y="30" width="12" height="7" rx="1.5"/>' },
    bunk:     { vb: '18 40', svg:
      '<rect x="1" y="1" width="16" height="38" rx="2"/><rect x="3.5" y="3.5" width="11" height="5" rx="1.5"/><path d="M1 11h16"/><path d="M4 14l10 22M14 14L4 36"/><rect x="3" y="30" width="12" height="6" rx="1.5"/>' },
    bedside:  { vb: '24 22', svg:
      '<rect x="1.5" y="1.5" width="21" height="19" rx="2"/><circle cx="12" cy="11" r="4"/><circle cx="12" cy="11" r="1.2"/>' },
    wardrobe: { vb: '48 24', svg:
      '<rect x="1" y="1" width="46" height="22" rx="1.5"/><path d="M24 1v22"/><path d="M4 12h17M27 12h17"/><path d="M6 8l4 0M6 16l4 0M38 8h4M38 16h4"/>' },
    desk:     { vb: '48 24', svg:
      '<rect x="1" y="1" width="46" height="15" rx="1.5"/><rect x="27" y="4" width="12" height="8" rx="1"/><path d="M33 12v2"/><rect x="18" y="17" width="12" height="6" rx="3"/>' },
    sofa:     { vb: '48 24', svg:
      '<rect x="1" y="1" width="46" height="22" rx="4"/><path d="M1 7h46"/><path d="M7 7v16M41 7v16"/><path d="M24 7v16"/>' },
    sofaL:    { vb: '36 24', svg:
      '<path d="M4 1h28a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H15v7a3 3 0 0 1-3 3H4a3 3 0 0 1-3-3V4a3 3 0 0 1 3-3z"/>' +
      '<path d="M1 5h34"/><path d="M5 5v18"/><path d="M15 5v8M25 5v8"/>' },
    sofaU:    { vb: '36 30', svg:
      '<path d="M4 1h28a3 3 0 0 1 3 3v22a3 3 0 0 1-3 3h-7a3 3 0 0 1-3-3V13H14v13a3 3 0 0 1-3 3H4a3 3 0 0 1-3-3V4a3 3 0 0 1 3-3z"/>' +
      '<path d="M1 5h34"/><path d="M5 5v24M31 5v24"/><path d="M14 5v8M22 5v8"/>' },
    armchair: { vb: '24 24', svg:
      '<rect x="1" y="1" width="22" height="22" rx="5"/><rect x="1" y="1" width="22" height="6" rx="3"/><rect x="1" y="7" width="5" height="16" rx="2.5"/><rect x="18" y="7" width="5" height="16" rx="2.5"/><rect x="7" y="8" width="10" height="14" rx="2"/>' },
    coffee:   { vb: '36 24', svg:
      '<rect x="1" y="1" width="34" height="22" rx="4"/><rect x="5" y="5" width="26" height="14" rx="3"/><path d="M12 12h12"/>' },
    tvStand:  { vb: '54 16', svg:
      '<rect x="1" y="1" width="52" height="14" rx="1.5"/><path d="M14 1v14M40 1v14"/><rect x="18" y="4" width="18" height="3" rx="1"/><path d="M4 8h6M44 8h6"/>' },
    table2:   { vb: '16 34', svg:
      '<rect x="0.5" y="9" width="15" height="16" rx="1"/><rect x="3" y="1.5" width="10" height="6" rx="1.5"/><rect x="3" y="26.5" width="10" height="6" rx="1.5"/>' },
    table2r:  { vb: '18 36', svg:
      '<circle cx="9" cy="18" r="8.5"/><rect x="4" y="1.5" width="10" height="6" rx="1.5"/><rect x="4" y="28.5" width="10" height="6" rx="1.5"/>' },
    barTable: { vb: '48 38', svg:
      '<rect x="0.5" y="0.5" width="47" height="23" rx="2"/><circle cx="14" cy="31" r="5"/><circle cx="34" cy="31" r="5"/>' },
    fridge:   { vb: '24 26', svg:
      '<rect x="2" y="2" width="20" height="22" rx="1.5"/><path d="M2 13h20"/><path d="M5 5v5M5 16v5"/>' },
    /* v150: the dryer -- the washer's drum without its door ring, the lint
       filter across the top */
    dryer:    { vb: '24 24', svg:
      '<rect x="2" y="2" width="20" height="20" rx="1.5"/><circle cx="12" cy="13.5" r="6"/><path d="M5 5h14"/><path d="M9 13.5h6"/>' },
    /* the islands with their stools in front: 0.05 m a unit */
    island18: { vb: '36 27', svg:
      '<rect x="0.5" y="0.5" width="35" height="17" rx="2"/><path d="M4 4h28"/><circle cx="6" cy="22.5" r="4"/><circle cx="18" cy="22.5" r="4"/><circle cx="30" cy="22.5" r="4"/>' },
    island24: { vb: '48 27', svg:
      '<rect x="0.5" y="0.5" width="47" height="17" rx="2"/><path d="M4 4h40"/><circle cx="6" cy="22.5" r="4"/><circle cx="18" cy="22.5" r="4"/><circle cx="30" cy="22.5" r="4"/><circle cx="42" cy="22.5" r="4"/>' },
    /* the tables of six and eight, 0.05 m a unit: three chairs a side, and
       one at each end of the eight */
    table6:   { vb: '36 36', svg:
      '<rect x="0.5" y="9" width="35" height="18" rx="1"/>' +
      '<rect x="2" y="1.5" width="8" height="6" rx="1.5"/><rect x="14" y="1.5" width="8" height="6" rx="1.5"/><rect x="26" y="1.5" width="8" height="6" rx="1.5"/>' +
      '<rect x="2" y="28.5" width="8" height="6" rx="1.5"/><rect x="14" y="28.5" width="8" height="6" rx="1.5"/><rect x="26" y="28.5" width="8" height="6" rx="1.5"/>' },
    table8:   { vb: '58 36', svg:
      '<rect x="9" y="9" width="40" height="18" rx="1"/>' +
      '<rect x="11.5" y="1.5" width="8" height="6" rx="1.5"/><rect x="25" y="1.5" width="8" height="6" rx="1.5"/><rect x="38.5" y="1.5" width="8" height="6" rx="1.5"/>' +
      '<rect x="11.5" y="28.5" width="8" height="6" rx="1.5"/><rect x="25" y="28.5" width="8" height="6" rx="1.5"/><rect x="38.5" y="28.5" width="8" height="6" rx="1.5"/>' +
      '<rect x="1.5" y="14" width="6" height="8" rx="1.5"/><rect x="50.5" y="14" width="6" height="8" rx="1.5"/>' },
    washer:   { vb: '24 24', svg:
      '<rect x="2" y="2" width="20" height="20" rx="1.5"/><circle cx="12" cy="13" r="6"/><circle cx="12" cy="13" r="2.5"/><path d="M5 5h5"/>' },
    table:    { vb: '28 34', svg:
      '<rect x="0.5" y="9" width="27" height="16" rx="1"/>' +
      '<rect x="3" y="1.5" width="9" height="6" rx="1.5"/><rect x="16" y="1.5" width="9" height="6" rx="1.5"/><rect x="3" y="26.5" width="9" height="6" rx="1.5"/><rect x="16" y="26.5" width="9" height="6" rx="1.5"/>' }
  };
  function _fixGlyphKey(kind) {
    if (/^kitchen(24|30|36)$/.test(kind)) return 'kitchen';
    if (/^bath/.test(kind)) return 'bath';
    if (/^shower/.test(kind)) return 'shower';
    if (/^basin/.test(kind)) return 'basin';
    if (/^sofa[234]$/.test(kind)) return 'sofa';
    if (kind === 'bedDouble') return 'bed';
    if (kind === 'stairLLanding') return 'stairsL';
    if (kind === 'stairLWinder') return 'stairsLW';
    if (kind === 'stairSpiral') return 'stairsSpiral';
    if (/^stair/.test(kind)) return 'stairs';
    if (/^wardrobe/.test(kind)) return 'wardrobe';
    return FIX_SVG[kind] ? kind : 'sofa';
  }
  /* VARIANTS GET THEIR OWN DRAWING. A 3600 counter drawn from the 2400
     picture came out with oval hob rings; a four-seat sofa with two long
     cushions. These build the drawing for the piece's own proportions: the
     counter with a unit per 600 and round rings, the sofa with a cushion per
     seat, the wardrobe with a door per 600, and the sanitary sizes on a box
     of their true ratio. */
  function _glyphFor(kind) {
    var D = FIX[kind]; if (!D) return null;
    var r = D.w / D.d, H = 24, W = Math.round(H * r * 10) / 10, s;
    if (/^kitchen(24|30|36)$/.test(kind)) {
      var n = Math.round(D.w / 0.6), u = W / n, i;
      s = '<rect x="1" y="1" width="' + (W - 2) + '" height="22" rx="1.5"/>';
      for (i = 1; i < n; i++) s += '<path d="M' + (u * i).toFixed(1) + ' 1v22"/>';
      /* the sink over the second unit, the hob over the last but one */
      var sx = u * 1.5, hx = u * (n - 1.5);
      s += '<rect x="' + (sx - u * 0.36).toFixed(1) + '" y="5" width="' + (u * 0.72).toFixed(1) + '" height="14" rx="2"/><circle cx="' + sx.toFixed(1) + '" cy="3.5" r="0.9"/>';
      s += '<circle cx="' + (hx - 4).toFixed(1) + '" cy="8" r="2.6"/><circle cx="' + (hx + 4).toFixed(1) + '" cy="8" r="2.6"/><circle cx="' + (hx - 4).toFixed(1) + '" cy="16" r="2.6"/><circle cx="' + (hx + 4).toFixed(1) + '" cy="16" r="2.6"/>';
      return { vb: W + ' ' + H, svg: s };
    }
    if (/^sofa[234]$/.test(kind)) {
      var seats = parseInt(kind.slice(4), 10), arm = 3, inner = W - 2 - 2 * arm, cw = inner / seats, q;
      s = '<rect x="1" y="1" width="' + (W - 2) + '" height="22" rx="4"/><path d="M1 7h' + (W - 2) + '"/><path d="M' + (1 + arm) + ' 7v16M' + (W - 1 - arm) + ' 7v16"/>';
      for (q = 1; q < seats; q++) s += '<path d="M' + (1 + arm + cw * q).toFixed(1) + ' 7v16"/>';
      return { vb: W + ' ' + H, svg: s };
    }
    if (/^wardrobe/.test(kind)) {
      var doors = Math.max(1, Math.round(D.w / 0.5)), dw = (W - 2) / doors, k;
      s = '<rect x="1" y="4" width="' + (W - 2) + '" height="16" rx="1.5"/><path d="M1 12h' + (W - 2) + '"/>';
      for (k = 1; k < doors; k++) s += '<path d="M' + (1 + dw * k).toFixed(1) + ' 4v16"/>';
      return { vb: W + ' ' + H, svg: s };
    }
    if (/^shower/.test(kind)) {
      return { vb: W + ' ' + H, svg: '<rect x="1" y="1" width="' + (W - 2) + '" height="22" rx="2"/><rect x="3" y="3" width="' + (W - 6) + '" height="18" rx="1"/><path d="M3 3L' + (W - 3) + ' 21"/><circle cx="' + (W / 2).toFixed(1) + '" cy="12" r="1.5"/>' };
    }
    if (/^basin/.test(kind)) {
      return { vb: W + ' ' + H, svg: '<rect x="1" y="1" width="' + (W - 2) + '" height="22" rx="3"/><ellipse cx="' + (W / 2).toFixed(1) + '" cy="12.5" rx="' + (W / 2 - 6).toFixed(1) + '" ry="7.5"/><ellipse cx="' + (W / 2).toFixed(1) + '" cy="12.5" rx="2" ry="1.3"/><circle cx="' + (W / 2).toFixed(1) + '" cy="4" r="1.4"/>' };
    }
    if (/^bath/.test(kind)) {
      return { vb: W + ' ' + H, svg: '<rect x="1" y="1" width="' + (W - 2) + '" height="22" rx="4"/><rect x="4" y="4" width="' + (W - 8) + '" height="16" rx="7"/><circle cx="9" cy="12" r="1.2"/><path d="M6 8h4"/>' };
    }
    return null;
  }
  function _fixGlyph(kind) { return _glyphFor(kind) || FIX_SVG[_fixGlyphKey(kind)] || FIX_SVG.sofa; }
  /* THE GLYPH AS PATHS, NOT AS A PICTURE. Drawn as an image the strokes
     scaled with the piece -- a 3 m counter had thick lines, a toilet thin
     ones. The elements are turned into path data once, and at draw time the
     path is transformed into place and stroked at one pixel, so every piece
     has the same line whatever its size (Marek). */
  var _fixPath = {};
  function _svgToPaths(svg) {
    var out = [], re = /<(rect|circle|ellipse|path)\s([^>]*)\/>/g, m;
    function attr(s, k) { var r = new RegExp('\\b' + k + '="([^"]*)"').exec(s); return r ? parseFloat(r[1]) : 0; }
    while ((m = re.exec(svg))) {
      var tag = m[1], a = m[2];
      if (tag === 'path') {
        var d = /\bd="([^"]*)"/.exec(a);
        if (d) out.push(d[1]);
      } else if (tag === 'rect') {
        var x = attr(a, 'x'), y = attr(a, 'y'), w = attr(a, 'width'), h = attr(a, 'height'), r = attr(a, 'rx');
        r = Math.min(r, w / 2, h / 2);
        if (r > 0) {
          out.push('M' + (x + r) + ' ' + y + 'H' + (x + w - r) + 'A' + r + ' ' + r + ' 0 0 1 ' + (x + w) + ' ' + (y + r) +
                   'V' + (y + h - r) + 'A' + r + ' ' + r + ' 0 0 1 ' + (x + w - r) + ' ' + (y + h) +
                   'H' + (x + r) + 'A' + r + ' ' + r + ' 0 0 1 ' + x + ' ' + (y + h - r) +
                   'V' + (y + r) + 'A' + r + ' ' + r + ' 0 0 1 ' + (x + r) + ' ' + y + 'Z');
        } else out.push('M' + x + ' ' + y + 'h' + w + 'v' + h + 'h' + (-w) + 'Z');
      } else if (tag === 'circle' || tag === 'ellipse') {
        var cx = attr(a, 'cx'), cy = attr(a, 'cy');
        var rx = tag === 'circle' ? attr(a, 'r') : attr(a, 'rx'), ry = tag === 'circle' ? rx : attr(a, 'ry');
        out.push('M' + (cx - rx) + ' ' + cy + 'A' + rx + ' ' + ry + ' 0 1 0 ' + (cx + rx) + ' ' + cy +
                 'A' + rx + ' ' + ry + ' 0 1 0 ' + (cx - rx) + ' ' + cy + 'Z');
      }
    }
    return out;
  }
  /* v167: an L or a U kitchen at its own size is drawn from its arms, 0.6 m
     deep at 0.075 m a unit (the catalogue glyph's own scale), so a shorter or
     a longer counter keeps its real depth instead of being stretched */
  function _fixKitGlyph(kind, w, d) {
    var U = 0.075, VW = Math.round(w / U * 100) / 100, VH = Math.round(d / U * 100) / 100, G8 = 8, svg;
    if (kind === 'kitchenL') {
      svg = '<path d="M1 1H' + (VW - 1) + 'v' + G8 + 'H' + (1 + G8) + 'V' + (VH - 1) + 'H1z"/>' +
            '<rect x="13" y="2.5" width="9" height="5" rx="1.5"/><path d="M15 2.5v5M20 2.5v5"/>' +
            (VW > 31 ? ('<rect x="' + (VW - 8) + '" y="2.5" width="6" height="5" rx="1"/>') : '') +
            (VH >= 20 ? ('<circle cx="4" cy="' + (VH - 10) + '" r="1.4"/><circle cx="6.5" cy="' + (VH - 6.5) + '" r="1.4"/><circle cx="4" cy="' + (VH - 3) + '" r="1.2"/>')
                      : ('<circle cx="5" cy="' + ((G8 + VH) / 2) + '" r="1.4"/>'));
    } else {
      svg = '<path d="M1 1H' + (VW - 1) + 'V' + (VH - 1) + 'H' + (VW - 1 - G8) + 'V' + (1 + G8) + 'H' + (1 + G8) + 'V' + (VH - 1) + 'H1z"/>' +
            '<rect x="' + (VW / 2 - 4.5) + '" y="2.5" width="9" height="4.5" rx="1.5"/>' +
            (VH >= 20 ? ('<circle cx="4.5" cy="' + (VH - 10) + '" r="1.3"/><circle cx="4.5" cy="' + (VH - 6) + '" r="1.3"/>') : ('<circle cx="4.5" cy="' + ((G8 + VH) / 2) + '" r="1.3"/>'));
    }
    return { vb: VW + ' ' + VH, svg: svg };
  }
  function _fixPaths(kind, f) {
    if (f && (kind === 'kitchenL' || kind === 'kitchenU') && f.sizeW > 0 && f.sizeD > 0) {
      var kk = kind + '@' + f.sizeW + 'x' + f.sizeD;
      if (_fixPath[kk]) return _fixPath[kk];
      var GK = _fixKitGlyph(kind, f.sizeW, f.sizeD), vk = GK.vb.split(' '), pk = _svgToPaths(GK.svg), PK = [], ik;
      try { for (ik = 0; ik < pk.length; ik++) PK.push(new global.Path2D(pk[ik])); } catch (eK) { PK = []; }
      _fixPath[kk] = { paths: PK, vw: parseFloat(vk[0]) || 24, vh: parseFloat(vk[1]) || 24 };
      return _fixPath[kk];
    }
    var gk = _glyphFor(kind) ? kind : _fixGlyphKey(kind);
    if (_fixPath[gk]) return _fixPath[gk];
    var G = _fixGlyph(kind), vb = (G.vb || '24 24').split(' ');
    var ps = _svgToPaths(G.svg), i, P = [];
    try { for (i = 0; i < ps.length; i++) P.push(new global.Path2D(ps[i])); } catch (e) { P = []; }
    _fixPath[gk] = { paths: P, vw: parseFloat(vb[0]) || 24, vh: parseFloat(vb[1]) || 24 };
    return _fixPath[gk];
  }
  function _fixDef(kind) { return FIX[kind] || null; }
  /* THE PRODUCT'S HOOKS (v85). size(f, D) -> { w, d } gives a piece its
     real size where the catalogue's is only a start (a flight's run comes
     from the storey height); storey() -> the storey the plan shows, so a
     piece belongs to the storey it was placed on. A product that sets
     neither sees no change. */
  var _fixHooks = { size: null, storey: null };
  function _fixSetHooks(o) {
    o = o || {};
    if (o.size !== undefined) _fixHooks.size = (typeof o.size === 'function') ? o.size : null;
    if (o.storey !== undefined) _fixHooks.storey = (typeof o.storey === 'function') ? o.storey : null;
  }
  function _fixWD(f) {
    var D = _fixDef(f.kind); if (!D) return { w: 0.6, d: 0.6 };
    var S = null;
    if (_fixHooks.size) { try { S = _fixHooks.size(f, D); } catch (eS) { S = null; } }
    if (S && S.w > 0 && S.d > 0) return { w: S.w, d: S.d };
    /* v167: an L or a U kitchen at the size the plan gave it (sizeW along
       its back arm, sizeD along its side arm); the counter stays 0.6 deep */
    if ((f.kind === 'kitchenL' || f.kind === 'kitchenU') && f.sizeW > 0 && f.sizeD > 0) return { w: f.sizeW, d: f.sizeD };
    /* v151: an appliance at its US stock size on an imperial page */
    if (D.wi > 0 && _fixImperial() && !(f && f.compact)) return { w: D.wi, d: D.di || D.d };
    return { w: D.w, d: D.d };
  }
  /* the page counts in feet and inches (the plan host says so) */
  function _fixImperial() {
    var H = _PH;
    try { return !!(H && typeof H.isMetric === 'function' && H.isMetric() === false); } catch (e) { return false; }
  }
  /* a piece's height, the US one for an appliance on an imperial page (v151) */
  function _fixHt(f) {
    var D = _fixDef(f && f.kind); if (!D) return 0;
    return (D.hi > 0 && _fixImperial() && !f.compact) ? D.hi : (D.h || 0);
  }
  function _fixStorey() {
    if (!_fixHooks.storey) return null;
    try { return _fixHooks.storey() | 0; } catch (e) { return null; }
  }
  /* on the storey the plan shows: its own storey; a flight shows on the
     storey above too (drawn dashed), but is only grabbed on its own */
  function _fixOn(f, forHit) {
    var s = _fixStorey();
    if (s == null || f.st == null) return true;
    if ((f.st | 0) === s) return true;
    var D = _fixDef(f.kind);
    return !forHit && !!(D && D.stair) && s === (f.st | 0) + 1;
  }
  function _fixDims(f) {
    var m = _fixWD(f);
    return ((f.rot | 0) % 2) ? { w: m.d, d: m.w } : { w: m.w, d: m.d };
  }
  function _fixRect(f) {
    var m = _fixDims(f);
    return { x0: f.x - m.w / 2, x1: f.x + m.w / 2, z0: f.z - m.d / 2, z1: f.z + m.d / 2 };
  }
  function _fixturesRef() { var H = _PH; return (H && H.fixturesRef) ? (H.fixturesRef() || []) : []; }
  function _fixtureAt(x, z) {
    var L = _fixturesRef(), i;
    for (i = L.length - 1; i >= 0; i--) {
      if (!_fixOn(L[i], true)) continue;
      var r = _fixRect(L[i]);
      if (x >= r.x0 && x <= r.x1 && z >= r.z0 && z <= r.z1) return L[i];
    }
    return null;
  }
  function _fixOverlaps(f, skipId) {
    var L = _fixturesRef(), i, r = _fixRect(f);
    for (i = 0; i < L.length; i++) {
      if (L[i].id === skipId) continue;
      if ((L[i].st == null ? null : (L[i].st | 0)) !== (f.st == null ? null : (f.st | 0))) continue;
      var q = _fixRect(L[i]);
      if (r.x0 < q.x1 - 0.01 && r.x1 > q.x0 + 0.01 && r.z0 < q.z1 - 0.01 && r.z1 > q.z0 + 0.01) return true;
    }
    return false;
  }
  /* which room a kind belongs in, by the room's name */
  function _fixRoomFor(kind) {
    var D = _fixDef(kind), R = _roomsTracked(), i, best = null, bestA = -1;
    var want = D ? D.grp : 'living';
    var match = {
      kitchen: /kitchen|dining/i, bathroom: /bath|shower|toilet|wc|utility|laundry/i,
      bedroom: /bed|kids|nursery|children/i, living: /living|lounge|hall|office|dining/i
    }[want] || /./;
    for (i = 0; i < R.length; i++) {
      var nm = _roomName(R[i], i) || '', area = 0, j;
      for (j = 0; j < R[i].rects.length; j++) {
        var rc = R[i].rects[j]; area += (rc.x1 - rc.x0) * (rc.z1 - rc.z0);
      }
      var score = (match.test(nm) ? 1000 : 0) + area;
      if (score > bestA) { bestA = score; best = R[i]; }
    }
    return best;
  }
  /* FIRST PLACE: against the longest wall of the room, back to the wall,
     slid along it until it does not sit on another piece; free-standing
     pieces go to the middle. */
  function fixtureAdd(kind, atX, atZ, roomX, roomZ) {
    var H = _PH, D = _fixDef(kind); if (!H || !D || !H.fixturesRef) return null;
    var room = null, rc = null, i;
    /* the room asked for (the plus that was tapped), else the room the kind
       belongs in */
    if (typeof roomX === 'number' && typeof roomZ === 'number') {
      var R0 = _roomsTracked(), q0, j0;
      for (q0 = 0; q0 < R0.length && !room; q0++) for (j0 = 0; j0 < R0[q0].rects.length; j0++) {
        var rr = R0[q0].rects[j0];
        if (roomX >= rr.x0 && roomX <= rr.x1 && roomZ >= rr.z0 && roomZ <= rr.z1) { room = R0[q0]; break; }
      }
    }
    if (!room) room = _fixRoomFor(kind);
    if (room) {
      var bA = -1;
      for (i = 0; i < room.rects.length; i++) {
        var r = room.rects[i], a = (r.x1 - r.x0) * (r.z1 - r.z0);
        if (a > bA) { bA = a; rc = r; }
      }
    }
    if (!rc) { var P = H.poly(), B = H.rects(P); rc = B[0] || { x0: 0, x1: 4, z0: 0, z1: 4 }; }
    var Ff = _roomFaces((rc.x0 + rc.x1) / 2, (rc.z0 + rc.z1) / 2) || rc;
    var ix0 = Ff.x0, ix1 = Ff.x1, iz0 = Ff.z0, iz1 = Ff.z1;
    var f = { id: 'f' + Date.now().toString(36) + Math.floor(Math.random() * 1000).toString(36), kind: kind, x: 0, z: 0, rot: 0 };
    var stQ = _fixStorey(); if (stQ != null) f.st = stQ;
    if (typeof atX === 'number' && typeof atZ === 'number') {
      f.x = atX; f.z = atZ;
    } else if (!D.wall) {
      f.x = (ix0 + ix1) / 2; f.z = (iz0 + iz1) / 2;
    } else {
      var alongX = (ix1 - ix0) >= (iz1 - iz0);
      f.rot = alongX ? 0 : 1;
      var m = _fixDims(f), tries = [], t;
      if (alongX) {
        for (t = ix0 + m.w / 2; t <= ix1 - m.w / 2 + 1e-6; t += 0.2) tries.push({ x: t, z: iz0 + m.d / 2, rot: 0 });
        for (t = ix0 + m.w / 2; t <= ix1 - m.w / 2 + 1e-6; t += 0.2) tries.push({ x: t, z: iz1 - m.d / 2, rot: 2 });
      } else {
        /* ALONG A WALL THAT RUNS IN Z (v88). f.rot is already a quarter turn
           here, so _fixDims has swapped the sizes: m.d is the run along the
           wall and m.w the depth off it -- the two were used the other way
           round, so a long piece stood its own length away from the wall and
           ran past the end of the room (the container's 3.6 m flight came out
           half outside its box). And a quarter turn puts the back on +x, three
           quarters on -x (as _fixBackAtWall reads them), so turn 3 goes
           against the -x wall and turn 1 against the +x wall -- they faced
           into the room. The -x wall is tried first, as the old order did. */
        for (t = iz0 + m.d / 2; t <= iz1 - m.d / 2 + 1e-6; t += 0.2) tries.push({ x: ix0 + m.w / 2, z: t, rot: 3 });
        for (t = iz0 + m.d / 2; t <= iz1 - m.d / 2 + 1e-6; t += 0.2) tries.push({ x: ix1 - m.w / 2, z: t, rot: 1 });
      }
      var placed = false;
      for (i = 0; i < tries.length; i++) {
        f.x = tries[i].x; f.z = tries[i].z; f.rot = tries[i].rot;
        if (!_fixOverlaps(f, f.id)) { placed = true; break; }
      }
      if (!placed) { f.x = (ix0 + ix1) / 2; f.z = (iz0 + iz1) / 2; f.rot = alongX ? 0 : 1; }
    }
    H.fixturesRef().push(f);
    return f;
  }
  /* the inner faces of the room the point is in: the outline is the OUTER
     face, so its edges come in by a wall; a partition edge comes in by half
     a partition, since the rects split on the partition's centre line */
  function _roomFaces(x, z) {
    var H = _PH; if (!H) return null;
    var R = _roomsTracked(), P = H.poly(), WALL = H.wallThk ? H.wallThk() : 0.16, PT = H.partThk ? H.partThk() : 0.1;
    var i, j;
    for (i = 0; i < R.length; i++) for (j = 0; j < R[i].rects.length; j++) {
      var rc = R[i].rects[j];
      if (x < rc.x0 || x > rc.x1 || z < rc.z0 || z > rc.z1) continue;
      /* THE ROOM, NOT THE CELL. A room is cut into cells by every partition
         line in the plan, including the lines of partitions that do not
         reach it, so a cell can be a metre wide inside a four metre room --
         and a piece wider than the cell was centred in it (Marek: the
         kitchen jumped half a metre on the drop). The box grows through the
         neighbouring cells of the same room that lie flush with it and are
         not parted by a partition, to the largest rectangle round the point. */
      var out = { x0: rc.x0, x1: rc.x1, z0: rc.z0, z1: rc.z1 }, grew = true, cells = R[i].rects, q;
      var ps = H.partitions ? (H.partitions() || []) : [];
      function parted(o, at, a, b) {
        /* a real partition on the line 'at' (orientation o) across [a, b]? */
        var k;
        for (k = 0; k < ps.length; k++) {
          var p = ps[k]; if (p.o !== o || Math.abs(p.c - at) > 0.01) continue;
          var sp = _partSpan(p), lo = Math.min(sp[0], sp[1]), hi = Math.max(sp[0], sp[1]);
          /* NOT THE CORNER POKE (v91): a trimmed end runs half a partition
             past the wall it meets, into the next cell, and that alone
             was reading as a partition between two cells of one room --
             the box stopped short and the picker centred pieces in a
             strip. More than a partition's thickness is needed now. */
          var TOLp = Math.max(0.12, (H.partThk ? H.partThk() : 0.1) + 0.02);
          if (lo < b - TOLp && hi > a + TOLp) return true;
        }
        return false;
      }
      while (grew) {
        grew = false;
        for (q = 0; q < cells.length; q++) {
          var c = cells[q];
          /* flush on the left / right, covering the box's z run, no partition between */
          if (Math.abs(c.x1 - out.x0) < 0.005 && c.z0 <= out.z0 + 0.005 && c.z1 >= out.z1 - 0.005 && !parted('v', out.x0, out.z0, out.z1)) { out.x0 = c.x0; grew = true; }
          else if (Math.abs(c.x0 - out.x1) < 0.005 && c.z0 <= out.z0 + 0.005 && c.z1 >= out.z1 - 0.005 && !parted('v', out.x1, out.z0, out.z1)) { out.x1 = c.x1; grew = true; }
          else if (Math.abs(c.z1 - out.z0) < 0.005 && c.x0 <= out.x0 + 0.005 && c.x1 >= out.x1 - 0.005 && !parted('h', out.z0, out.x0, out.x1)) { out.z0 = c.z0; grew = true; }
          else if (Math.abs(c.z0 - out.z1) < 0.005 && c.x0 <= out.x0 + 0.005 && c.x1 >= out.x1 - 0.005 && !parted('h', out.z1, out.x0, out.x1)) { out.z1 = c.z1; grew = true; }
        }
      }
      rc = { x0: out.x0, x1: out.x1, z0: out.z0, z1: out.z1 };
      /* an edge on the outline is a wall face; any other edge is a partition centre */
      function onOutline(v, axis) {
        var q;
        for (q = 0; q < P.length; q++) if (Math.abs(P[q][axis] - v) < 0.01) return true;
        return false;
      }
      out.x0 += onOutline(rc.x0, 0) ? WALL : PT / 2;
      out.x1 -= onOutline(rc.x1, 0) ? WALL : PT / 2;
      out.z0 += onOutline(rc.z0, 1) ? WALL : PT / 2;
      out.z1 -= onOutline(rc.z1, 1) ? WALL : PT / 2;
      return out;
    }
    return null;
  }
  /* THE DRAG, PLAIN. The piece goes exactly where the finger puts it, both
     axes, no snapping and no walls in the way -- every limit that was here
     made it hard to move or pinned it to one axis (Marek). The one rule left
     is confine(), which the product runs when walls or partitions move: a
     piece a wall has moved over, or that is outside, is pushed back into its
     room then. Snapping to faces comes back later, on its own. */
  function _put(f, x, z) { f.x = Math.round(x * 1000) / 1000; f.z = Math.round(z * 1000) / 1000; return true; }
  function fixtureBeginDrag(f) { return !!f; }
  function fixtureDragTo(f, x, z) { return f ? _put(f, x, z) : false; }
  function fixtureEndDrag(f, x, z) { return f ? _put(f, x, z) : false; }
  function fixtureMoveTo(f, x, z) { return f ? _put(f, x, z) : false; }
  function fixtureRotate(f) { if (!f) return false; f.rot = ((f.rot | 0) + 1) % 4; return true; }
  function fixtureMirror(f) { if (!f) return false; f.mirror = !f.mirror; return true; }
  function fixtureRemove(f) {
    var L = _fixturesRef(), i = L.indexOf(f);
    if (i >= 0) L.splice(i, 1);
    return i >= 0;
  }
  /* FLIGHTS (v210, the garage apartment's stair, Marek 2026-10-13: the
     flight drawn on card 1's plan). A product that works its stair out
     itself (not a stair piece of the fitout) sets PLAN.flights(fn); fn()
     returns flights as { on: the storey whose plan shows it, x0, z0, x1, z1
     (its footprint), axis 'x' | 'z' (the way it climbs), risers: [the
     world coordinate of each riser along that axis], cut: the coordinate of
     the plan's cut (4 ft over the floor) or null, arrow 'up' | 'down' }.
     Drawn as a plan draws a stair: the outline, a line at every riser, the
     walking line from a dot at the foot to an arrow at the head ('down': the
     arrow at the foot, the flight seen from the storey above); past the cut
     a break line and the rest dashed. Nothing to grab, nothing billed. */
  var _flightHook = null;
  function _setFlights(fn) { _flightHook = (typeof fn === 'function') ? fn : null; }
  function _drawFlights(ctx, X, Y, ppm) {
    var FL = [];
    try { FL = _flightHook ? (_flightHook() || []) : []; } catch (eF) { FL = []; }
    if (!FL.length) return;
    var s = _fixStorey(); if (s == null) s = 0;
    var i, k;
    for (i = 0; i < FL.length; i++) {
      var f = FL[i];
      if (!f || (f.on | 0) !== s || !(f.x1 > f.x0) || !(f.z1 > f.z0)) continue;
      var ax = (f.axis === 'x'), lo = ax ? f.x0 : f.z0, hi = ax ? f.x1 : f.z1, c0 = ax ? f.z0 : f.x0, c1 = ax ? f.z1 : f.x1, cm = (c0 + c1) / 2;
      var R = (f.risers || []).slice().sort(function (p, q) { return p - q; });
      var up = (f.arrow !== 'down'), foot = (R.length > 1 && Math.abs(R[0] - lo) > Math.abs(R[R.length - 1] - lo)) ? hi : lo;   /* the foot: the end the risers start from */
      if (f.foot != null) foot = f.foot;
      var head = (foot === lo) ? hi : lo, sgn = (head > foot) ? 1 : -1;
      var cut = (f.cut != null && isFinite(f.cut)) ? f.cut : null;
      function P2(a, c) { return ax ? [X(a), Y(c)] : [X(c), Y(a)]; }
      function past(a) { return cut != null && (a - cut) * sgn > 0; }
      function seg(a0, ca, a1, cb, dash) {
        var p0 = P2(a0, ca), p1 = P2(a1, cb);
        ctx.save();
        if (dash && ctx.setLineDash) ctx.setLineDash([4, 3]);
        ctx.beginPath(); ctx.moveTo(p0[0], p0[1]); ctx.lineTo(p1[0], p1[1]); ctx.stroke();
        ctx.restore();
      }
      ctx.save();
      ctx.strokeStyle = '#3B3B3B'; ctx.fillStyle = '#3B3B3B'; ctx.lineWidth = 1; ctx.lineCap = 'round';
      /* the outline: its long sides solid to the cut, dashed past it */
      if (cut != null && cut > lo && cut < hi) {
        var cA = (sgn > 0) ? cut : lo, cB = (sgn > 0) ? hi : cut, sA = (sgn > 0) ? lo : cut, sB = (sgn > 0) ? cut : hi;
        seg(sA, c0, sB, c0, false); seg(sA, c1, sB, c1, false);
        seg(cA, c0, cB, c0, true); seg(cA, c1, cB, c1, true);
      } else { seg(lo, c0, hi, c0, false); seg(lo, c1, hi, c1, false); }
      seg(foot, c0, foot, c1, false);
      seg(head, c0, head, c1, cut != null && past(head));
      /* a line at every riser */
      for (k = 0; k < R.length; k++) {
        if (R[k] <= lo + 0.01 || R[k] >= hi - 0.01) continue;
        seg(R[k], c0, R[k], c1, past(R[k]));
      }
      /* the break line at the cut */
      if (cut != null && cut > lo && cut < hi) seg(cut - 0.12 * sgn, c0, cut + 0.12 * sgn, c1, false);
      /* the walking line, a dot at its start, an arrow at its end */
      var aS = up ? foot : head, aE = up ? head : foot, dirW = (aE > aS) ? 1 : -1, ah = Math.min(0.25, Math.abs(aE - aS) / 4);
      if (cut != null && up && cut > lo && cut < hi) { seg(aS + 0.10 * dirW, cm, cut, cm, false); seg(cut, cm, aE - 0.02 * dirW, cm, true); }
      else seg(aS + 0.10 * dirW, cm, aE - 0.02 * dirW, cm, !up);
      var pd = P2(aS + 0.05 * dirW, cm);
      ctx.beginPath(); ctx.arc(pd[0], pd[1], Math.max(2, 0.04 * ppm), 0, Math.PI * 2); ctx.fill();
      var pa = P2(aE, cm), pb = P2(aE - ah * dirW, cm - ah * 0.45), pc = P2(aE - ah * dirW, cm + ah * 0.45);
      ctx.beginPath(); ctx.moveTo(pa[0], pa[1]); ctx.lineTo(pb[0], pb[1]); ctx.lineTo(pc[0], pc[1]); ctx.closePath(); ctx.fill();
      ctx.restore();
    }
  }
  function _drawFixtures(ctx, X, Y, ppm) {
    try { _fit.draws = (_fit.draws | 0) + 1; } catch (eCnt) {}
    var L = _fixturesRef(), i, k;
    var haveM = (typeof global.DOMMatrix === 'function');
    for (i = 0; i < L.length; i++) {
      var f = L[i], D = _fixDef(f.kind); if (!D) continue;
      if (!_fixOn(f, false)) continue;
      var G = _fixPaths(f.kind, f), WD = _fixWD(f);
      var w = WD.w * ppm, d = WD.d * ppm;
      var sQ = _fixStorey(), above = (sQ != null && f.st != null && (f.st | 0) !== sQ);
      var sx = w / G.vw, sy = d / G.vh;
      ctx.save();
      ctx.strokeStyle = '#3B3B3B';
      ctx.lineWidth = 1;
      ctx.lineJoin = 'round'; ctx.lineCap = 'round';
      if (above && ctx.setLineDash) ctx.setLineDash([4, 3]);
      if (haveM && G.paths.length) {
        /* the path carried into place: translate to the centre, turn, scale
           to the piece, shift by half the drawing -- and stroked at 1 px */
        var flipAcross = !!(f.mirror && D.stair);
        var M = new global.DOMMatrix().translate(X(f.x), Y(f.z)).rotate(((f.rot | 0) * 90))
                  .scale((f.mirror && !D.stair) ? -sx : sx, flipAcross ? -sy : sy).translate(-G.vw / 2, -G.vh / 2);
        for (k = 0; k < G.paths.length; k++) {
          var p2 = new global.Path2D();
          p2.addPath(G.paths[k], M);
          ctx.stroke(p2);
        }
      } else {
        ctx.translate(X(f.x), Y(f.z));
        ctx.rotate((f.rot | 0) * Math.PI / 2);
        ctx.strokeRect(-w / 2, -d / 2, w, d);
      }
      ctx.restore();
    }
    /* and, under ?fitTest=1 for an admin, what the placer would do */
    try { _fitTestDraw(ctx, X, Y, ppm); } catch (eFT) {}
    /* and the plan being imported, in violet, while it is only a preview */
    try { _impDraw(ctx, X, Y, ppm); } catch (eIM) {}
  }
  /* does the piece stand with its back (its -z side, turned by rot) on a
     wall face -- within 200 mm */
  function _fixBackAtWall(f) {
    var F = _roomFaces(f.x, f.z); if (!F) return false;
    var m = _fixDims(f), rot = (f.rot | 0) % 4, SN = 0.2;
    if (rot === 0) return Math.abs((f.z - m.d / 2) - F.z0) < SN;
    if (rot === 2) return Math.abs((f.z + m.d / 2) - F.z1) < SN;
    if (rot === 1) return Math.abs((f.x + m.w / 2) - F.x1) < SN;
    return Math.abs((f.x - m.w / 2) - F.x0) < SN;
  }
  /* KEEP EVERY PIECE INSIDE THE HOUSE -- and nothing more. Rooms, cells and
     partitions had every version of this pushing furniture about (to the
     middle of a room, off a wall); the one thing that has to hold is that a
     wall dragged inward cannot leave a piece outside the building. So the
     box is the outline's inner faces, the piece is clamped into it, and a
     piece that fits is left exactly where it was (Marek). */
  function _fixConfine() {
    var H = _PH; if (!H) return 0;
    var L = _fixturesRef(); if (!L.length) return 0;
    var P = H.poly(), WALL = H.wallThk ? H.wallThk() : 0.16, q;
    var bx0 = 1e9, bx1 = -1e9, bz0 = 1e9, bz1 = -1e9;
    for (q = 0; q < P.length; q++) { bx0 = Math.min(bx0, P[q][0]); bx1 = Math.max(bx1, P[q][0]); bz0 = Math.min(bz0, P[q][1]); bz1 = Math.max(bz1, P[q][1]); }
    var F = { x0: bx0 + WALL, x1: bx1 - WALL, z0: bz0 + WALL, z1: bz1 - WALL };
    var k, moved = 0;
    for (k = 0; k < L.length; k++) {
      var f = L[k], m = _fixDims(f), nx = f.x, nz = f.z;
      if (m.w >= (F.x1 - F.x0)) nx = (F.x0 + F.x1) / 2;
      else nx = Math.max(F.x0 + m.w / 2, Math.min(F.x1 - m.w / 2, f.x));
      if (m.d >= (F.z1 - F.z0)) nz = (F.z0 + F.z1) / 2;
      else nz = Math.max(F.z0 + m.d / 2, Math.min(F.z1 - m.d / 2, f.z));
      if (Math.abs(nx - f.x) > 0.004 || Math.abs(nz - f.z) > 0.004) {
        f.x = Math.round(nx * 1000) / 1000; f.z = Math.round(nz * 1000) / 1000;
        moved++;
      }
    }
    return moved;
  }
  /* =========================================================================
     THE PLACER (v89). A piece asked for in words -- the bed against the top
     wall of the bedroom -- has to land somewhere without a finger putting it
     there, and whatever turns the words into a request must never count
     metres. So the request names only WHAT, WHICH ROOM, WHICH WALL and WHICH
     END, and this works out x, z and the turn. It says no, with the reason,
     where the piece would stand in a doorway or in a door's swing, in front
     of a window it is taller than, on another piece or over a stairwell.

     Nothing here moves anything. place() only answers; commit() is the one
     call that puts an answer into the product's list, and the product then
     rebuilds and saves exactly as it does after a tap. It asks the host for
     nothing new: rooms, partitions, doors, wall openings and voids are all
     read through the contract the plan already uses, so every product on
     this asset gets it the day it is uploaded (Marek: model nesmi pocitat
     souradnice; postavit tak, aby se to snadno nasadilo na ostatni config).

     spec = { kind, room, wall, align, st, id }
       kind   a key of the catalogue above (bedDouble, kitchen30 ...)
       room   a room's number in rooms(), a word its key or its English name
              holds (rooms.bedroom, kitchen), a point { x, z }, or nothing --
              then the room the piece belongs in, the emptiest of them
       wall   top | right | bottom | left, as the 2D plan shows them, free
              (standing in the room, off the walls), or auto
       align  start | center | end along that wall -- start is the left end
              of a top or bottom wall and the top end of a left or right
              one -- or auto
       id     the piece being MOVED: it is left out of its own way, and the
              answer carries its id so commit() moves it instead of adding
     The answer: { ok, reason, detail, f, room, wall, align, shift, alt }.
     f is the piece ready to commit; reason is kind, room, size, overlap,
     door, window, void or open (no wall on that side); shift is how far along the wall it had to slide
     from the point asked for; alt is the nearest place that IS free when the
     wall asked for is not.
     ========================================================================= */
  var FIT_STEP = 0.05;          /* how finely a wall is walked */
  var FIT_GRID = 0.10;          /* and the floor, for a piece off the walls */
  var FIT_PASS = 0.60;          /* the way through a doorway, either side */
  var FIT_WIN_REACH = 0.35;     /* how far off a window a taller piece still blocks it */
  var FIT_SIDE_ROT = { top: 0, right: 1, bottom: 2, left: 3 };
  /* WHAT KIND OF ROOM IT IS, read off the room's KEY and its English name
     rather than off the word on the plan -- that word is in the page's
     language, and a test written in English found no kitchen on a Czech
     page. First match wins, so the order is part of the rule. */
  var FIT_CLASS = [
    ['bathroom', /bath|shower|toilet|wc/i],
    ['laundry', /laundry|utility/i],
    ['kitchen', /kitchen|pantry/i],
    ['kids', /kids|nursery|children/i],
    ['bedroom', /bed|loft/i],
    ['office', /office|study|workshop/i],
    ['hall', /hall|entry/i],
    ['living', /living|lounge|dining/i]
  ];
  /* which rooms a catalogue group goes to, best first */
  var FIT_GRP_ROOMS = {
    kitchen: ['kitchen', 'living'],
    bathroom: ['bathroom', 'laundry'],
    bedroom: ['bedroom', 'kids', 'office'],
    living: ['living', 'office', 'kitchen', 'hall'],
    stairs: ['hall', 'living']
  };
  /* the pieces that sit in the middle of their wall unless told otherwise;
     everything else goes into a corner first */
  var FIT_CENTRED = /^(bedDouble|sofa[234]|tvStand)$/;

  function _fitClassOf(rm) {
    var s = (rm.key || '') + ' ' + (rm.def || ''), k;
    for (k = 0; k < FIT_CLASS.length; k++) if (FIT_CLASS[k][1].test(s)) return FIT_CLASS[k][0];
    return '';
  }
  function _fitStOf(f) { return (f.st == null) ? null : (f.st | 0); }
  function _fitHit(r, q, tol) {
    return r.x0 < q.x1 - tol && r.x1 > q.x0 + tol && r.z0 < q.z1 - tol && r.z1 > q.z0 + tol;
  }

  /* THE ROOM'S BOX (v91): the LARGEST RECTANGLE that fits in the room, not
     the cell the room's middle happens to fall in grown outward. The grown
     box stopped at every line a partition was drawn on, and a partition that
     ends half a thickness past the wall it meets -- which every trimmed end
     does, to fill the corner -- reached five centimetres into the next room
     and cut it in two: the living room came out 1.24 m wide in a 4 m house
     (Marek's FIT test). Here the room's cells are laid on a grid, every
     rectangle of whole cells is tried, and one is refused only if a
     partition really runs through it, more than a partition's thickness.
     Each side then says what it is: wall (the outline), part (a partition)
     or open (the room goes on past it, an L) -- and only a side that is a
     wall or a partition takes a piece's back. */
  function _fitRoomBox(r) {
    var H = _PH;
    if (!H || !r || !r.rects || !r.rects.length) return null;
    var P = H.poly() || [], WALL = H.wallThk ? H.wallThk() : 0.16, PT = H.partThk ? H.partThk() : 0.1;
    var TOL = Math.max(0.12, PT + 0.02), xs = [], zs = [], i, a, b, c, d;
    function addU(A, v) { for (var q = 0; q < A.length; q++) if (Math.abs(A[q] - v) < 0.005) return; A.push(v); }
    for (i = 0; i < r.rects.length; i++) {
      addU(xs, r.rects[i].x0); addU(xs, r.rects[i].x1); addU(zs, r.rects[i].z0); addU(zs, r.rects[i].z1);
    }
    var SP = [], ps = H.partitions ? (H.partitions() || []) : [];
    for (i = 0; i < ps.length; i++) {
      var sp = null;
      try { sp = _partSpan(ps[i]); } catch (e0) { sp = null; }
      if (sp) SP.push({ o: ps[i].o, c: ps[i].c, lo: Math.min(sp[0], sp[1]), hi: Math.max(sp[0], sp[1]) });
    }
    /* AND A LINE WHERE A PARTITION STOPS (v93): the cells are cut only on a
       partition's own line, so the end of a stub was no line at all and the
       room beside its end -- the floor past a shower wall -- could not be
       made a rectangle of */
    var mnx = Math.min.apply(null, xs), mxx = Math.max.apply(null, xs), mnz = Math.min.apply(null, zs), mxz = Math.max.apply(null, zs);
    for (i = 0; i < SP.length; i++) {
      var ends = [SP[i].lo, SP[i].hi], e;
      for (e = 0; e < 2; e++) {
        if (SP[i].o === 'v' && ends[e] > mnz + 0.05 && ends[e] < mxz - 0.05 && SP[i].c >= mnx - 0.01 && SP[i].c <= mxx + 0.01) addU(zs, ends[e]);
        if (SP[i].o === 'h' && ends[e] > mnx + 0.05 && ends[e] < mxx - 0.05 && SP[i].c >= mnz - 0.01 && SP[i].c <= mxz + 0.01) addU(xs, ends[e]);
      }
    }
    xs.sort(function (p, q) { return p - q; }); zs.sort(function (p, q) { return p - q; });
    var nx = xs.length - 1, nz = zs.length - 1;
    if (nx < 1 || nz < 1) return null;
    /* the grid, and a running sum over it so a rectangle is checked at once */
    var S = [];
    for (a = 0; a <= nx; a++) { S.push([]); for (c = 0; c <= nz; c++) S[a].push(0); }
    for (a = 0; a < nx; a++) for (c = 0; c < nz; c++) {
      var inn = _roomHolds(r, (xs[a] + xs[a + 1]) / 2, (zs[c] + zs[c + 1]) / 2) ? 1 : 0;
      S[a + 1][c + 1] = inn + S[a][c + 1] + S[a + 1][c] - S[a][c];
    }
    function crosses(x0, x1, z0, z1) {
      for (var q = 0; q < SP.length; q++) {
        var t = SP[q];
        if (t.o === 'v' && t.c > x0 + 0.01 && t.c < x1 - 0.01 && Math.min(t.hi, z1) - Math.max(t.lo, z0) > TOL) return true;
        if (t.o === 'h' && t.c > z0 + 0.01 && t.c < z1 - 0.01 && Math.min(t.hi, x1) - Math.max(t.lo, x0) > TOL) return true;
      }
      return false;
    }
    /* EVERY RECTANGLE THAT CANNOT GROW (v93), not only the biggest. A room
       with a notch or a shower wall standing in it has several -- the
       biggest of Marek's bedroom was a strip 0.81 m wide beside the shower,
       and a single bed that fits below the shower was never tried. The five
       largest are kept, biggest first, and a piece goes into the first one
       that takes it. */
    function valid(a0, b0, c0, d0) {
      if (a0 < 0 || c0 < 0 || b0 >= nx || d0 >= nz || a0 > b0 || c0 > d0) return false;
      var cnt = S[b0 + 1][d0 + 1] - S[a0][d0 + 1] - S[b0 + 1][c0] + S[a0][c0];
      if (cnt !== (b0 - a0 + 1) * (d0 - c0 + 1)) return false;
      return !crosses(xs[a0], xs[b0 + 1], zs[c0], zs[d0 + 1]);
    }
    var cand = [];
    for (a = 0; a < nx; a++) for (b = a; b < nx; b++) for (c = 0; c < nz; c++) for (d = c; d < nz; d++) {
      if (!valid(a, b, c, d)) continue;
      if (valid(a - 1, b, c, d) || valid(a, b + 1, c, d) || valid(a, b, c - 1, d) || valid(a, b, c, d + 1)) continue;
      var cw = xs[b + 1] - xs[a], cd = zs[d + 1] - zs[c];
      if (cw < 0.4 || cd < 0.4) continue;
      cand.push({ x0: xs[a], x1: xs[b + 1], z0: zs[c], z1: zs[d + 1], ar: cw * cd });
    }
    if (!cand.length) return null;
    cand.sort(function (p, q) { return q.ar - p.ar; });
    if (cand.length > 5) cand.length = 5;
    /* what each side is: on the outline a wall, on a partition's line a
       partition, otherwise open -- and WHERE along it there is something to
       stand against, because a partition that stops halfway leaves the rest
       of that side open (an L) */
    function mk(best) {
    var sup = {};
    function kind(nm, o, v, lo, hi) {
      var q, iv = [], k = 'open';
      for (q = 0; q < P.length; q++) {
        var A = P[q], B = P[(q + 1) % P.length], a0, a1;
        if (o === 'v' && Math.abs(A[0] - v) < 0.01 && Math.abs(B[0] - v) < 0.01) { a0 = Math.min(A[1], B[1]); a1 = Math.max(A[1], B[1]); }
        else if (o === 'h' && Math.abs(A[1] - v) < 0.01 && Math.abs(B[1] - v) < 0.01) { a0 = Math.min(A[0], B[0]); a1 = Math.max(A[0], B[0]); }
        else continue;
        if (Math.min(a1, hi) - Math.max(a0, lo) > 0.1) { iv.push([Math.max(a0, lo), Math.min(a1, hi)]); k = 'wall'; }
      }
      for (q = 0; q < SP.length; q++) {
        if (SP[q].o !== o || Math.abs(SP[q].c - v) > 0.01) continue;
        if (Math.min(SP[q].hi, hi) - Math.max(SP[q].lo, lo) > 0.2) {
          iv.push([Math.max(SP[q].lo, lo), Math.min(SP[q].hi, hi)]);
          if (k === 'open') k = 'part';
        }
      }
      iv.sort(function (m, n) { return m[0] - n[0]; });
      var mg = [];
      /* ONE WALL ACROSS A T (v92). Two partitions meeting a third one from
         either side leave a gap of the third one's thickness between their
         ends on this line, and the back of a sofa was being refused across
         that gap: a 3.68 m wall read as two short ones (Marek's FIT test). A
         gap up to a partition's thickness and a little is wall. */
      var JOIN = PT + 0.06;
      for (q = 0; q < iv.length; q++) {
        if (mg.length && iv[q][0] <= mg[mg.length - 1][1] + JOIN) mg[mg.length - 1][1] = Math.max(mg[mg.length - 1][1], iv[q][1]);
        else mg.push([iv[q][0], iv[q][1]]);
      }
      sup[nm] = mg;
      return k;
    }
    var side = {
      top: kind('top', 'h', best.z0, best.x0, best.x1), bottom: kind('bottom', 'h', best.z1, best.x0, best.x1),
      left: kind('left', 'v', best.x0, best.z0, best.z1), right: kind('right', 'v', best.x1, best.z0, best.z1)
    };
    function ins(k) { return (k === 'wall') ? WALL : ((k === 'part') ? PT / 2 : 0); }
    return { x0: best.x0 + ins(side.left), x1: best.x1 - ins(side.right),
             z0: best.z0 + ins(side.top), z1: best.z1 - ins(side.bottom), side: side, sup: sup };
    }
    /* WHY THE BOX IS SMALLER THAN THE ROOM, when it is: the room's whole
       extent, and every partition running through it that is not one of its
       edges -- a stub that does not close the room off still splits it, and
       the box has to stay on one side of it */
    var bb = { x0: xs[0], x1: xs[nx], z0: zs[0], z1: zs[nz] }, cuts = [];
    for (i = 0; i < SP.length; i++) {
      var tq = SP[i];
      if (tq.o === 'v' && tq.c > bb.x0 + 0.01 && tq.c < bb.x1 - 0.01 && Math.min(tq.hi, bb.z1) - Math.max(tq.lo, bb.z0) > TOL) {
        cuts.push('v x' + tq.c.toFixed(2) + ' z' + Math.max(tq.lo, bb.z0).toFixed(2) + '..' + Math.min(tq.hi, bb.z1).toFixed(2));
      }
      if (tq.o === 'h' && tq.c > bb.z0 + 0.01 && tq.c < bb.z1 - 0.01 && Math.min(tq.hi, bb.x1) - Math.max(tq.lo, bb.x0) > TOL) {
        cuts.push('h z' + tq.c.toFixed(2) + ' x' + Math.max(tq.lo, bb.x0).toFixed(2) + '..' + Math.min(tq.hi, bb.x1).toFixed(2));
      }
    }
    var boxes = [];
    for (i = 0; i < cand.length; i++) {
      var bx = mk(cand[i]);
      if (bx.x1 - bx.x0 > 0.3 && bx.z1 - bx.z0 > 0.3) boxes.push(bx);
    }
    if (!boxes.length) return null;
    var F = boxes[0];
    F.boxes = boxes; F.bbox = bb; F.cuts = cuts;
    return F;
  }

  /* THE ROOMS AS THE PLACER SEES THEM: number, key, English name, the word
     on the plan, the area, and the box above */
  function _fitRooms() {
    var R = _roomsTracked(), T = _roomNamesTable(), out = [], i, j;
    for (i = 0; i < R.length; i++) {
      /* a closet is furnished by nobody (v102) */
      if (R[i].closet) continue;
      var big = null, bA = -1, area = 0;
      for (j = 0; j < R[i].rects.length; j++) {
        var rc = R[i].rects[j], a = (rc.x1 - rc.x0) * (rc.z1 - rc.z0);
        area += a;
        if (a > bA) { bA = a; big = rc; }
      }
      if (!big) continue;
      var F = _fitRoomBox(R[i]);
      if (!F || !(F.x1 > F.x0) || !(F.z1 > F.z0)) continue;
      var ni = _roomNameIdx(R[i], i) | 0, n = T.length;
      var row = n ? T[((ni % n) + n) % n] : null;
      var rm = { i: i, r: R[i], key: row ? (row.i18n || '') : '', def: row ? (row.def || '') : '',
                 name: _roomName(R[i], i), area: area, F: F };
      rm.cls = _fitClassOf(rm);
      out.push(rm);
    }
    return out;
  }
  /* floor left in a room: its faces less the pieces standing in them */
  function _fitFree(rm, pend) {
    var F = rm.F, L = _fixturesRef().concat(pend || []), a = (F.x1 - F.x0) * (F.z1 - F.z0), i;
    for (i = 0; i < L.length; i++) {
      if (L[i].x < F.x0 || L[i].x > F.x1 || L[i].z < F.z0 || L[i].z > F.z1) continue;
      var m = _fixDims(L[i]);
      a -= m.w * m.d;
    }
    return a;
  }
  function _fitEmptiest(list, pend) {
    var best = null, bf = -1e9, i;
    for (i = 0; i < list.length; i++) {
      var fr = _fitFree(list[i], pend);
      if (fr > bf) { bf = fr; best = list[i]; }
    }
    return best;
  }
  function _fitPickRoom(spec, rooms, D, pend) {
    var q = spec.room, i;
    if (q == null && spec.id) {
      var L = _fixturesRef();
      for (i = 0; i < L.length; i++) if (L[i].id === spec.id) { q = { x: L[i].x, z: L[i].z }; break; }
    }
    if (typeof q === 'number') {
      for (i = 0; i < rooms.length; i++) if (rooms[i].i === (q | 0)) return rooms[i];
      return null;
    }
    if (q && typeof q === 'object' && typeof q.x === 'number' && typeof q.z === 'number') {
      for (i = 0; i < rooms.length; i++) if (_roomHolds(rooms[i].r, q.x, q.z)) return rooms[i];
      return null;
    }
    if (typeof q === 'string' && q) {
      var s = q.toLowerCase(), hits = [];
      for (i = 0; i < rooms.length; i++) {
        var hay = (rooms[i].key + ' ' + rooms[i].def + ' ' + rooms[i].name).toLowerCase();
        if (rooms[i].cls === s || hay.indexOf(s) >= 0) hits.push(rooms[i]);
      }
      return _fitEmptiest(hits, pend);
    }
    /* nothing asked: the rooms the piece's group goes to, best kind first,
       and of those the emptiest -- two bedrooms get a bed each */
    var want = FIT_GRP_ROOMS[D.grp] || [], rank = 99, pool = [];
    for (i = 0; i < rooms.length; i++) {
      var ci = want.indexOf(rooms[i].cls);
      if (ci < 0) continue;
      if (ci < rank) { rank = ci; pool = []; }
      if (ci === rank) pool.push(rooms[i]);
    }
    return _fitEmptiest(pool.length ? pool : rooms, pend);
  }

  /* WHERE NOTHING MAY STAND. A door in a partition keeps the quarter its
     leaf sweeps on the side it opens to and a way through on the other; a
     slider and a doorway keep the way through on both. A door in the outer
     wall keeps its swing inside the house. A window keeps a strip in front
     of it, which only a piece TALLER than its sill is stopped by -- a bed
     goes under a window, a wardrobe does not. And a void is a stairwell. */
  function _fitZones() {
    var H = _PH, Z = [];
    if (!H) return Z;
    var P = H.poly() || [], WALL = H.wallThk ? H.wallThk() : 0.16, PT = H.partThk ? H.partThk() : 0.1, i, k;
    var DL = [];
    try { DL = _doorsLive(); } catch (e0) { DL = []; }
    for (i = 0; i < DL.length; i++) {
      var d = DL[i].d, p = DL[i].p, w = d.w || DOOR_W;
      var a0 = d.u - w / 2 - 0.05, a1 = d.u + w / 2 + 0.05;
      /* v106: a bypass has no leaf; a pair swings two half leaves, a bifold
         its panels -- half the opening for one unit, a quarter for two */
      var leaf = !(d.slide || d.pass || d.kind === 'bypass');
      var leafL = (d.kind === 'pair') ? w / 2 : ((d.kind === 'bifold') ? ((w > 1.1) ? w / 4 : w / 2) : w);
      /* the side the leaf opens to, as DRAW.door draws it: on a wall along x
         the second bit of sw sends it to +z, on a wall along z to -x */
      var s = (p.o === 'v') ? (((d.sw | 0) & 2) ? -1 : 1) : (((d.sw | 0) & 2) ? 1 : -1);
      var dA = PT / 2 + (leaf ? Math.max(leafL, FIT_PASS) + 0.05 : FIT_PASS);
      var dB = PT / 2 + FIT_PASS;
      var lo = (s > 0) ? p.c - dB : p.c - dA, hi = (s > 0) ? p.c + dA : p.c + dB;
      if (p.o === 'v') Z.push({ t: 'door', why: 'interior', x0: lo, x1: hi, z0: a0, z1: a1 });
      else Z.push({ t: 'door', why: 'interior', x0: a0, x1: a1, z0: lo, z1: hi });
    }
    var segs = [];
    try { segs = H.wallSegs ? (H.wallSegs() || []) : []; } catch (e1) { segs = []; }
    for (i = 0; i < segs.length; i++) {
      var sg = segs[i];
      if (!sg || !sg.a || typeof sg.ux !== 'number' || typeof sg.uz !== 'number') continue;
      var horiz = Math.abs(sg.uz) < 0.2, vert = Math.abs(sg.ux) < 0.2;
      if (!horiz && !vert) continue;
      var ops = [];
      try { ops = H.opsOn ? (H.opsOn(sg.i) || []) : []; } catch (e2) { ops = []; }
      for (k = 0; k < ops.length; k++) {
        var op = ops[k], ow = Number(op.w) || 0;
        if (!(ow > 0)) continue;
        var cx = sg.a[0] + sg.ux * (Number(op.off) || 0), cz = sg.a[1] + sg.uz * (Number(op.off) || 0);
        var inS = horiz ? (H.inPoly(cx, cz + 0.15, P) ? 1 : -1) : (H.inPoly(cx + 0.15, cz, P) ? 1 : -1);
        var sym = DRAW.symbolOf(op), isDoor = DRAW.isDoorSym(sym), reach, zn;
        if (isDoor) {
          /* v149: a door opening out sweeps nothing inside -- the way in only */
          reach = op.out ? FIT_PASS : (sym === DRAW.SYM.DOOR) ? Math.max(ow, FIT_PASS) + 0.05
                : ((sym === DRAW.SYM.DOOR2) ? Math.max(ow / 2, FIT_PASS) + 0.05 : FIT_PASS);
          zn = { t: 'door', why: 'exterior' };
        } else {
          reach = FIT_WIN_REACH;
          var glass = !!(op.full || op.fullW || op.halfW || op.fix);
          zn = { t: 'window', why: '', sill: (op.sill != null && !isNaN(Number(op.sill))) ? Number(op.sill) : (glass ? 0 : 0.9) };
        }
        var pad = isDoor ? 0.05 : 0, deep = WALL + reach;
        if (horiz) {
          zn.x0 = cx - ow / 2 - pad; zn.x1 = cx + ow / 2 + pad;
          zn.z0 = Math.min(cz, cz + inS * deep); zn.z1 = Math.max(cz, cz + inS * deep);
        } else {
          zn.z0 = cz - ow / 2 - pad; zn.z1 = cz + ow / 2 + pad;
          zn.x0 = Math.min(cx, cx + inS * deep); zn.x1 = Math.max(cx, cx + inS * deep);
        }
        Z.push(zn);
      }
    }
    var VD = [];
    try { VD = _voidHook ? (_voidHook() || []) : []; } catch (e3) { VD = []; }
    for (i = 0; i < VD.length; i++) {
      var v = VD[i];
      if (v && v.x1 > v.x0 && v.z1 > v.z0) Z.push({ t: 'void', why: '', x0: v.x0, x1: v.x1, z0: v.z0, z1: v.z1 });
    }
    return Z;
  }

  /* what is in the way of a piece standing as f, or null */
  function _fitClash(f, F, Z, pend, skipId) {
    var r = _fixRect(f), D = _fixDef(f.kind), h = D ? _fixHt(f) : 0, i;
    if (F && (r.x0 < F.x0 - 0.005 || r.x1 > F.x1 + 0.005 || r.z0 < F.z0 - 0.005 || r.z1 > F.z1 + 0.005)) return { reason: 'size', detail: '' };
    for (i = 0; i < Z.length; i++) {
      var z = Z[i];
      if (!_fitHit(r, z, 0.005)) continue;
      if (z.t === 'window' && h <= z.sill + 0.01) continue;
      return { reason: z.t, detail: z.why || (z.t === 'window' ? ('sill ' + z.sill.toFixed(2)) : '') };
    }
    /* v127: the slanted walls, their doors' swing, and the corner each cuts off this room */
    var cS = _fitSlantClash(r, F, f);
    if (cS) return cS;
    var L = _fixturesRef().concat(pend || []);
    for (i = 0; i < L.length; i++) {
      var o = L[i];
      if (o === f || (skipId && o.id === skipId)) continue;
      if (_fitStOf(o) !== _fitStOf(f)) continue;
      /* v143: an L or a U sofa by its shape, not its box */
      if (_fitHit(r, _fixRect(o), 0.01) && _impShapeHit(f, o)) return { reason: 'overlap', detail: o.kind };
    }
    return null;
  }

  /* ---------- THE SLANTED WALLS IN THE WAY (v127) ------------------------
     A slanted wall (product.diagWalls, floorplan v125) is not a square
     partition, so neither the rooms nor the zones knew it: a piece could be
     put straight across it, in the swing of its door, or in the corner it
     cuts off -- a toilet standing behind the 45 degree wall of a bathroom,
     in the wedge between that wall and the corner. Three shapes are asked
     here, each a convex polygon tested against the piece's rectangle:
       the wall itself, its thickness and a hand either side;
       the swing of each door in it, as a square door's zone is made;
       the wedge it cuts off the room's box, on the side away from the
       middle of the room. */
  function _fitPolyHit(A, B, tol) {
    var polys = [A, B], s, i, k;
    for (s = 0; s < 2; s++) {
      var P = polys[s];
      for (i = 0; i < P.length; i++) {
        var p0 = P[i], p1 = P[(i + 1) % P.length], ax = -(p1[1] - p0[1]), az = p1[0] - p0[0], al = Math.hypot(ax, az);
        if (!(al > 1e-9)) continue;
        ax /= al; az /= al;
        var a0 = 1e9, a1 = -1e9, b0 = 1e9, b1 = -1e9;
        for (k = 0; k < A.length; k++) { var pa = A[k][0] * ax + A[k][1] * az; a0 = Math.min(a0, pa); a1 = Math.max(a1, pa); }
        for (k = 0; k < B.length; k++) { var pb = B[k][0] * ax + B[k][1] * az; b0 = Math.min(b0, pb); b1 = Math.max(b1, pb); }
        if (a1 <= b0 + tol || b1 <= a0 + tol) return false;
      }
    }
    return true;
  }
  /* the part of the wall's line inside the box, or null */
  function _fitClipSeg(g, F) {
    var t0 = 0, t1 = g.len, P = [[-g.ux, g.a[0] - F.x0], [g.ux, F.x1 - g.a[0]], [-g.uz, g.a[1] - F.z0], [g.uz, F.z1 - g.a[1]]], i;
    for (i = 0; i < 4; i++) {
      var pp = P[i][0], qq = P[i][1];
      if (Math.abs(pp) < 1e-9) { if (qq < 0) return null; continue; }
      var t = qq / pp;
      if (pp < 0) { if (t > t1) return null; if (t > t0) t0 = t; }
      else { if (t < t0) return null; if (t < t1) t1 = t; }
    }
    return (t1 - t0 > 0.05) ? [t0, t1] : null;
  }
  function _fitSlantClash(r, F, f) {
    var st = _fitStOf(f), L = [];
    if (st == null) { try { st = _fixStorey(); } catch (eS) { st = null; } }
    try { L = (typeof _impDiags === 'function') ? (_impDiags(st) || []) : []; } catch (eD) { L = []; }
    if (!L.length) return null;
    var PT = (_PH && _PH.partThk) ? _PH.partThk() : 0.1, R = [[r.x0, r.z0], [r.x1, r.z0], [r.x1, r.z1], [r.x0, r.z1]], i, k;
    for (i = 0; i < L.length; i++) {
      var g = L[i], nx = -g.uz, nz = g.ux;
      var Pt = function (al, ac) { return [g.a[0] + g.ux * al + nx * ac, g.a[1] + g.uz * al + nz * ac]; };
      var hw = PT / 2 + 0.02;
      if (_fitPolyHit(R, [Pt(0, -hw), Pt(g.len, -hw), Pt(g.len, hw), Pt(0, hw)], 0.005)) return { reason: 'slant', detail: 'across the slanted wall ' + g.id };
      for (k = 0; k < (g.doors || []).length; k++) {
        var d = g.doors[k], w = d.w || DOOR_W, kd = String(d.kind || 'hinged');
        var leaf = !(kd === 'slide' || kd === 'pass' || kd === 'bypass');
        var leafL = (kd === 'pair') ? w / 2 : ((kd === 'bifold') ? ((w > 1.1) ? w / 4 : w / 2) : w);
        var s = ((d.sw | 0) & 2) ? 1 : -1;
        var dA = PT / 2 + (leaf ? Math.max(leafL, FIT_PASS) + 0.05 : FIT_PASS), dB = PT / 2 + FIT_PASS;
        var c0 = (s > 0) ? -dB : -dA, c1 = (s > 0) ? dA : dB, u0 = d.u - w / 2 - 0.05, u1 = d.u + w / 2 + 0.05;
        if (_fitPolyHit(R, [Pt(u0, c0), Pt(u1, c0), Pt(u1, c1), Pt(u0, c1)], 0.005)) return { reason: 'door', detail: 'slanted wall ' + g.id };
      }
      if (!F) continue;
      var cl = _fitClipSeg(g, F);
      if (!cl) continue;
      var cx = (F.x0 + F.x1) / 2, cz = (F.z0 + F.z1) / 2, sc = (cx - g.a[0]) * nx + (cz - g.a[1]) * nz;
      if (Math.abs(sc) < 0.05) continue;
      var CR = [[F.x0, F.z0], [F.x1, F.z0], [F.x1, F.z1], [F.x0, F.z1]], W = [Pt(cl[0], 0), Pt(cl[1], 0)];
      /* a wedge only where the wall closes it: both its ends on the box's
         edges. A wall standing free in the room cuts nothing off. */
      var onEdge = function (q) { return Math.min(Math.abs(q[0] - F.x0), Math.abs(q[0] - F.x1), Math.abs(q[1] - F.z0), Math.abs(q[1] - F.z1)) < 0.12; };
      if (!onEdge(W[0]) || !onEdge(W[1])) continue;
      for (k = 0; k < 4; k++) {
        var rx = CR[k][0] - g.a[0], rz = CR[k][1] - g.a[1], sd = rx * nx + rz * nz, al2 = rx * g.ux + rz * g.uz;
        if (sd * sc < 0 && al2 > -0.05 && al2 < g.len + 0.05) W.push(CR[k]);
      }
      if (W.length < 3) continue;
      /* in order round their middle, so the wedge is one convex polygon */
      var mx = 0, mz = 0;
      for (k = 0; k < W.length; k++) { mx += W[k][0]; mz += W[k][1]; }
      mx /= W.length; mz /= W.length;
      W.sort(function (p1, p2) { return Math.atan2(p1[1] - mz, p1[0] - mx) - Math.atan2(p2[1] - mz, p2[0] - mx); });
      if (_fitPolyHit(R, W, 0.005)) return { reason: 'slant', detail: 'behind the slanted wall ' + g.id };
    }
    return null;
  }

  function _fitSidesByLength(F) {
    var w = F.x1 - F.x0, d = F.z1 - F.z0, out = [], i;
    var S = [{ s: 'top', l: w, o: 0 }, { s: 'right', l: d, o: 1 }, { s: 'bottom', l: w, o: 2 }, { s: 'left', l: d, o: 3 }];
    S.sort(function (a, b) { return (b.l - a.l) || (a.o - b.o); });
    for (i = 0; i < S.length; i++) if (!(F.side && F.side[S[i].s] === 'open')) out.push(S[i].s);
    return out;
  }

  /* THE POSES ALONG ONE WALL, the one asked for first and then outward from
     it, so a corner blocked by a door gives the place nearest the corner */
  function _fitWallPoses(kind, F, side, align, st) {
    var f = { kind: kind, x: 0, z: 0, rot: FIT_SIDE_ROT[side] };
    if (st != null) f.st = st;
    var m = _fixDims(f), out = [];
    var alongX = (side === 'top' || side === 'bottom');
    if (alongX ? (m.d > (F.z1 - F.z0) + 1e-6) : (m.w > (F.x1 - F.x0) + 1e-6)) return out;
    var lo = alongX ? F.x0 + m.w / 2 : F.z0 + m.d / 2, hi = alongX ? F.x1 - m.w / 2 : F.z1 - m.d / 2;
    if (hi < lo - 1e-6) return out;
    if (hi < lo) hi = lo;
    var mid = (lo + hi) / 2;
    var a = (align === 'auto') ? (FIT_CENTRED.test(kind) ? 'center' : 'start') : align;
    var t0 = (a === 'start') ? lo : ((a === 'end') ? hi : mid);
    var fix = (side === 'top') ? F.z0 + m.d / 2 : ((side === 'bottom') ? F.z1 - m.d / 2
            : ((side === 'left') ? F.x0 + m.w / 2 : F.x1 - m.w / 2));
    var ts = [lo, hi, mid], t;
    for (t = lo + FIT_STEP; t < hi - 1e-6; t += FIT_STEP) ts.push(t);
    ts.sort(function (p, q) { return Math.abs(p - t0) - Math.abs(q - t0); });
    var seen = {}, i, run = alongX ? m.w : m.d, SU = (F.sup && F.sup[side]) || null;
    function backed(tv) {
      if (!SU) return true;
      for (var q = 0; q < SU.length; q++) if (tv - run / 2 >= SU[q][0] - 0.02 && tv + run / 2 <= SU[q][1] + 0.02) return true;
      return false;
    }
    for (i = 0; i < ts.length; i++) {
      var tv = Math.round(ts[i] * 1000) / 1000, key = String(tv);
      if (seen[key]) continue;
      seen[key] = 1;
      if (!backed(tv)) continue;
      var lab = (Math.abs(tv - lo) < 0.005) ? 'start' : ((Math.abs(tv - hi) < 0.005) ? 'end' : ((Math.abs(tv - mid) < 0.005) ? 'center' : 'along'));
      out.push({ x: alongX ? tv : fix, z: alongX ? fix : tv, rot: f.rot, side: side, align: lab,
                 shift: Math.round(Math.abs(tv - t0) * 1000) / 1000, along: tv });
    }
    return out;
  }
  /* AND OFF THE WALLS: the middle of the room first, the long side of the
     piece along the long side of the room, then outward on a grid */
  function _fitFreePoses(kind, F, st) {
    var D = _fixDef(kind), out = [], i;
    var roomLongX = (F.x1 - F.x0) >= (F.z1 - F.z0), pieceLongX = D.w >= D.d;
    var rots = (roomLongX === pieceLongX) ? [0, 1] : [1, 0];
    var cx = (F.x0 + F.x1) / 2, cz = (F.z0 + F.z1) / 2, r;
    for (r = 0; r < rots.length; r++) {
      var f = { kind: kind, x: 0, z: 0, rot: rots[r] };
      if (st != null) f.st = st;
      var m = _fixDims(f);
      var x0 = F.x0 + m.w / 2, x1 = F.x1 - m.w / 2, z0 = F.z0 + m.d / 2, z1 = F.z1 - m.d / 2;
      if (x1 < x0 - 1e-6 || z1 < z0 - 1e-6) continue;
      var pts = [{ x: Math.max(x0, Math.min(x1, cx)), z: Math.max(z0, Math.min(z1, cz)) }], gx, gz;
      for (gx = x0; gx <= x1 + 1e-6; gx += FIT_GRID) for (gz = z0; gz <= z1 + 1e-6; gz += FIT_GRID) pts.push({ x: gx, z: gz });
      pts.sort(function (p, q) { return (Math.hypot(p.x - cx, p.z - cz) - Math.hypot(q.x - cx, q.z - cz)); });
      for (i = 0; i < pts.length; i++) {
        out.push({ x: Math.round(pts[i].x * 1000) / 1000, z: Math.round(pts[i].z * 1000) / 1000, rot: rots[r],
                   side: 'free', align: (i === 0) ? 'center' : 'along',
                   shift: Math.round(Math.hypot(pts[i].x - cx, pts[i].z - cz) * 1000) / 1000 });
      }
    }
    return out;
  }
  /* walk the sides in order; the first free pose wins, and the first pose
     tried is kept so a refusal can say where it wanted to go and why */
  function _fitTry(kind, F, sides, align, st, Z, pend, skipId) {
    var first = null, i, j;
    for (i = 0; i < sides.length; i++) {
      var poses = (sides[i] === 'free') ? _fitFreePoses(kind, F, st) : _fitWallPoses(kind, F, sides[i], align, st);
      for (j = 0; j < poses.length; j++) {
        var ps = poses[j], f = { kind: kind, x: ps.x, z: ps.z, rot: ps.rot };
        if (st != null) f.st = st;
        var c = _fitClash(f, F, Z, pend, skipId);
        if (!c) return { hit: { f: f, side: ps.side, align: ps.align, shift: ps.shift }, first: first };
        if (!first) first = { f: f, side: ps.side, align: ps.align, clash: c };
      }
    }
    return { hit: null, first: first };
  }

  /* one test run asks the same plan the same questions dozens of times; the
     rooms and the zones are worked out once for the run */
  var _fitCache = null;
  function _fitPlace(spec, pend) {
    spec = spec || {};
    var res = { ok: false, reason: '', detail: '', kind: String(spec.kind || ''), f: null, room: null,
                wall: '', align: '', shift: 0, alt: null };
    var D = _fixDef(spec.kind);
    if (!D) { res.reason = 'kind'; return res; }
    if (!_PH) { res.reason = 'room'; res.detail = 'no plan'; return res; }
    var st = (spec.st != null) ? (spec.st | 0) : _fixStorey();
    var rooms = _fitCache ? _fitCache.rooms : _fitRooms(), rm = _fitPickRoom(spec, rooms, D, pend);
    if (!rm) { res.reason = 'room'; res.detail = (spec.room == null) ? 'none' : String(spec.room); return res; }
    res.room = { i: rm.i, key: rm.key, cls: rm.cls, name: rm.name };
    var Z = _fitCache ? _fitCache.zones : _fitZones(), P = pend || [];
    var wall = FIT_SIDE_ROT.hasOwnProperty(spec.wall) ? spec.wall : ((spec.wall === 'free') ? 'free' : 'auto');
    var align = (spec.align === 'start' || spec.align === 'end' || spec.align === 'center') ? spec.align : 'auto';
    var boxes = (rm.F.boxes && rm.F.boxes.length) ? rm.F.boxes : [rm.F], bi;
    var named = (wall !== 'auto' && wall !== 'free'), openAsked = named;
    var T = { hit: null, first: null };
    for (bi = 0; bi < boxes.length && !T.hit; bi++) {
      var BX = boxes[bi];
      if (named && BX.side && BX.side[wall] === 'open') continue;
      openAsked = false;
      var sides = named || wall === 'free' ? [wall] : (D.wall ? _fitSidesByLength(BX) : ['free']);
      var TB = _fitTry(spec.kind, BX, sides, align, st, Z, P, spec.id);
      if (TB.hit) T.hit = TB.hit;
      if (!T.first && TB.first) T.first = TB.first;
    }
    if (T.hit) {
      res.ok = true;
      res.f = T.hit.f; res.wall = T.hit.side; res.align = T.hit.align; res.shift = T.hit.shift;
    } else {
      res.reason = openAsked ? 'open' : (T.first ? T.first.clash.reason : 'size');
      res.detail = T.first ? T.first.clash.detail : '';
      if (T.first) { res.f = T.first.f; res.wall = T.first.side; res.align = T.first.align; }
      else res.wall = wall;
      /* the wall asked for will not take it: say where it WOULD go */
      if (wall !== 'auto') {
        for (bi = 0; bi < boxes.length && !res.alt; bi++) {
          var rest = [], all = D.wall ? _fitSidesByLength(boxes[bi]) : ['free'], k;
          for (k = 0; k < all.length; k++) if (all[k] !== wall) rest.push(all[k]);
          if (D.wall && wall !== 'free') rest.push('free');
          var A = _fitTry(spec.kind, boxes[bi], rest, 'auto', st, Z, P, spec.id);
          if (A.hit) res.alt = { f: A.hit.f, wall: A.hit.side, align: A.hit.align };
        }
      }
    }
    if (res.f && spec.id) res.f.id = spec.id;
    return res;
  }
  /* PUT AN ANSWER INTO THE PLAN: a new piece is added, a moved one (the
     answer carries its id) takes its new place. Rebuild and save are the
     caller's, after the last of a batch. */
  function _fitCommit(res) {
    var H = _PH;
    if (!H || !H.fixturesRef || !res || !res.ok || !res.f) return null;
    var L = H.fixturesRef(), f = res.f, i;
    if (f.id) {
      for (i = 0; i < L.length; i++) if (L[i].id === f.id) { L[i].x = f.x; L[i].z = f.z; L[i].rot = f.rot; return L[i]; }
    }
    var n = { id: 'f' + Date.now().toString(36) + Math.floor(Math.random() * 1000).toString(36), kind: f.kind, x: f.x, z: f.z, rot: f.rot };
    if (f.st != null) n.st = f.st;
    L.push(n);
    return n;
  }
  /* the rooms for whoever turns words into a request: no geometry beyond
     the faces, and the key that is the same in every language */
  function _fitRoomsInfo() {
    var R = _fitRooms(), out = [], i;
    for (i = 0; i < R.length; i++) {
      out.push({ i: R[i].i, key: R[i].key, def: R[i].def, name: R[i].name, cls: R[i].cls,
                 area: Math.round(R[i].area * 100) / 100,
                 faces: { x0: R[i].F.x0, x1: R[i].F.x1, z0: R[i].F.z0, z1: R[i].F.z1 } });
    }
    return out;
  }

  /* ---------- THE TEST, ?fitTest=1, ADMIN ONLY ---------------------------
     Every room is furnished on paper with the set its kind of room gets, and
     the answer is DRAWN, not placed: green where the placer would put a
     piece, orange where it wanted to and could not, the door swings and
     passages red and the strips in front of windows blue. Nothing goes into
     the project and nothing is saved. Each room also gets its first piece
     tried against every wall in turn, which is the line that shows the
     refusals. The lines go to the log under FIT and into a small box with a
     COPY button, because the snapshot filter may not be printing FIT. */
  var FIT_SETS = {
    kitchen:  ['kitchen36|kitchen30|kitchen24', 'fridge'],
    bathroom: ['shower90|bath15', 'basin50', 'wc'],
    laundry:  ['washer'],
    bedroom:  ['bedDouble|bedSingle', 'wardrobe|wardrobe100|wardrobe80', 'bedside'],
    kids:     ['bedSingle', 'desk', 'wardrobe80'],
    office:   ['desk', 'wardrobe80'],
    hall:     ['wardrobe80'],
    living:   ['sofa3|sofa2', 'tvStand', 'coffee']
  };
  var _fit = { on: null, sig: '', out: null, timer: 0, logged: '', box: null, pre: null, drawn: false, noted: false };
  /* THE SWITCH IS READ WHEN THIS FILE LOADS (v90), not when the plan is first
     drawn. The configurator writes the project into the address after it
     boots, and a switch read later could find the address already rewritten
     -- the box then never came and nothing said why. Read now as well, so a
     switch added by hand to a loaded page still counts; fitTest=0 wins. */
  var _FIT_AT_LOAD = '';
  try { _FIT_AT_LOAD = String(((global.location && global.location.search) || '') + ((global.location && global.location.hash) || '')); } catch (eFL) { _FIT_AT_LOAD = ''; }
  function _fitUrlNow() {
    try { return String(((global.location && global.location.search) || '') + ((global.location && global.location.hash) || '')); } catch (e) { return ''; }
  }
  function _fitParam() {
    var now = _fitUrlNow();
    if (/[?&#]fitTest=0/.test(now)) return false;
    return /[?&#]fitTest=1/.test(_FIT_AT_LOAD) || /[?&#]fitTest=1/.test(now);
  }
  function _fitNote(s) {
    try {
      if (global.Configurator && global.Configurator.log && global.Configurator.log.log) global.Configurator.log.log('[floorplan-lib] ' + s);
    } catch (e) {}
  }
  function _fitTestOn() {
    if (_fit.on != null) return _fit.on;
    if (!_fitParam()) { _fit.on = false; return false; }
    var LP = global.LEVA_PRODUCT;
    if (!LP) return false;
    _fit.on = (LP.isAdmin === true || LP.isAdmin === 'true');
    if (!_fit.on && !_fit.noted) {
      _fit.noted = true;
      _fitNote('FIT switch is in the address but this visitor is not admin (isAdmin=' + LP.isAdmin + ') -- nothing is shown');
    }
    return _fit.on;
  }
  function _fitN(v) { return (Math.round(v * 100) / 100).toFixed(2); }
  function _fitSig() {
    var H = _PH;
    if (!H) return '';
    var s = '', i, k, P = H.poly() || [], ps = H.partitions() || [], D = _doors(), L = _fixturesRef();
    for (i = 0; i < P.length; i++) s += _fitN(P[i][0]) + ',' + _fitN(P[i][1]) + ';';
    s += '|';
    for (i = 0; i < ps.length; i++) {
      var sp = null;
      try { sp = _partSpan(ps[i]); } catch (e0) { sp = null; }
      s += ps[i].o + _fitN(ps[i].c) + (sp ? (':' + _fitN(sp[0]) + ':' + _fitN(sp[1])) : '') + ';';
    }
    s += '|';
    for (i = 0; i < D.length; i++) s += D[i].pid + ':' + _fitN(D[i].u) + ':' + (D[i].w || 0) + ':' + (D[i].sw | 0) + ':' + (D[i].slide | 0) + ':' + (D[i].pass ? 1 : 0) + ':' + (D[i].kind || '') + ';';
    s += '|';
    for (i = 0; i < L.length; i++) s += L[i].kind + ':' + _fitN(L[i].x) + ':' + _fitN(L[i].z) + ':' + (L[i].rot | 0) + ':' + _fitStOf(L[i]) + ';';
    s += '|';
    try {
      var sg = H.wallSegs ? (H.wallSegs() || []) : [];
      for (i = 0; i < sg.length; i++) {
        var ops = H.opsOn ? (H.opsOn(sg[i].i) || []) : [];
        for (k = 0; k < ops.length; k++) s += sg[i].i + ':' + _fitN(Number(ops[k].off) || 0) + ':' + _fitN(Number(ops[k].w) || 0) + ':' + (ops[k].sill || 0) + ':' + (ops[k].kind || '') + ';';
      }
    } catch (e1) {}
    s += '|' + _fixStorey() + '|';
    try { s += JSON.stringify(H.roomNames ? H.roomNames() : ''); } catch (e2) {}
    return s;
  }
  /* what stands on each side of a room: D a door, W a window, V a void */
  function _fitSideMarks(F, Z) {
    var S = {
      top: { x0: F.x0, x1: F.x1, z0: F.z0 - 0.3, z1: F.z0 + 0.3 },
      right: { x0: F.x1 - 0.3, x1: F.x1 + 0.3, z0: F.z0, z1: F.z1 },
      bottom: { x0: F.x0, x1: F.x1, z0: F.z1 - 0.3, z1: F.z1 + 0.3 },
      left: { x0: F.x0 - 0.3, x1: F.x0 + 0.3, z0: F.z0, z1: F.z1 }
    };
    var out = [], k, i;
    var ks = ['top', 'right', 'bottom', 'left'];
    for (k = 0; k < ks.length; k++) {
      var m = '', st = S[ks[k]];
      for (i = 0; i < Z.length; i++) {
        if (!_fitHit(st, Z[i], 0.01)) continue;
        var c = (Z[i].t === 'door') ? 'D' : ((Z[i].t === 'window') ? 'W' : 'V');
        if (m.indexOf(c) < 0) m += c;
      }
      var len = (ks[k] === 'top' || ks[k] === 'bottom') ? (F.x1 - F.x0) : (F.z1 - F.z0);
      out.push(ks[k] + ' ' + _fitN(len) + ' ' + ((F.side && F.side[ks[k]]) || '?') + (m ? (' ' + m) : ''));
    }
    return out.join(' | ');
  }
  /* a piece of one of these kinds already standing in the room */
  function _fitHas(rm, kinds) {
    var L = _fixturesRef(), st = _fixStorey(), i;
    for (i = 0; i < L.length; i++) {
      if (kinds.indexOf(L[i].kind) < 0) continue;
      if (st != null && L[i].st != null && (L[i].st | 0) !== st) continue;
      if (_roomHolds(rm.r, L[i].x, L[i].z)) return L[i].kind;
    }
    return '';
  }
  function _fitPad(s, n) { s = String(s); while (s.length < n) s += ' '; return s; }
  function _fitRunTest() {
    var rooms = _fitRooms(), Z = _fitZones(), pend = [], lines = [], ghosts = [], i, k, v, nD = 0, nW = 0, nV = 0;
    _fitCache = { rooms: rooms, zones: Z };
    try {
    for (i = 0; i < Z.length; i++) { if (Z[i].t === 'door') nD++; else if (Z[i].t === 'window') nW++; else nV++; }
    lines.push('FIT test ' + VERSION + ' | rooms ' + rooms.length + ' | pieces ' + _fixturesRef().length +
               ' | zones: doors ' + nD + ', windows ' + nW + ', voids ' + nV + ' | storey ' + _fixStorey());
    for (i = 0; i < rooms.length; i++) {
      var rm = rooms[i], set = FIT_SETS[rm.cls] || [];
      lines.push('FIT room ' + rm.i + ' ' + (rm.key || '?') + ' (' + (rm.cls || 'no set') + ') ' +
                 _fitN(rm.F.x1 - rm.F.x0) + ' x ' + _fitN(rm.F.z1 - rm.F.z0) + ' | ' + _fitSideMarks(rm.F, Z));
      if (rm.F.bbox) {
        var BB = rm.F.bbox;
        lines.push('FIT   extent x' + _fitN(BB.x0) + '..' + _fitN(BB.x1) + ' z' + _fitN(BB.z0) + '..' + _fitN(BB.z1) +
                   ' (' + _fitN(BB.x1 - BB.x0) + ' x ' + _fitN(BB.z1 - BB.z0) + ' to the partition centres)' +
                   ' | box x' + _fitN(rm.F.x0) + '..' + _fitN(rm.F.x1) + ' z' + _fitN(rm.F.z0) + '..' + _fitN(rm.F.z1) +
                   ' | cut by ' + (rm.F.cuts && rm.F.cuts.length ? rm.F.cuts.slice(0, 3).join(', ') : 'nothing') +
                   ' | boxes ' + ((rm.F.boxes && rm.F.boxes.length) || 1) +
                   (rm.F.boxes && rm.F.boxes.length > 1 ? (' (next ' + _fitN(rm.F.boxes[1].x1 - rm.F.boxes[1].x0) + ' x ' + _fitN(rm.F.boxes[1].z1 - rm.F.boxes[1].z0) + ')') : ''));
      }
      if (set.length) {
        var k0 = set[0].split('|')[0], wl = [], s4 = ['top', 'right', 'bottom', 'left'];
        for (k = 0; k < s4.length; k++) {
          var pr = _fitPlace({ kind: k0, room: rm.i, wall: s4[k] }, pend);
          wl.push(s4[k] + ' ' + (pr.ok ? 'OK' : ('NO ' + pr.reason + (pr.detail ? ' ' + pr.detail : '') +
                  (pr.alt ? (' -> ' + pr.alt.wall) : ''))));
        }
        lines.push('FIT   walls for ' + k0 + ': ' + wl.join(', '));
      }
      for (k = 0; k < set.length; k++) {
        var alts = set[k].split('|'), res = null, has = _fitHas(rm, alts);
        if (has) { lines.push('FIT   ' + _fitPad(has, 11) + 'HAS one already, not added'); continue; }
        for (v = 0; v < alts.length; v++) {
          res = _fitPlace({ kind: alts[v], room: rm.i }, pend);
          if (res.ok) break;
        }
        if (res.ok) pend.push(res.f);
        ghosts.push(res);
        lines.push('FIT   ' + _fitPad(res.kind, 11) + (res.ok
          ? ('OK ' + res.wall + ' ' + res.align + (res.shift > 0.004 ? (' +' + _fitN(res.shift)) : '') +
             ' at ' + _fitN(res.f.x) + ',' + _fitN(res.f.z) + ' r' + res.f.rot)
          : ('NO ' + res.reason + (res.detail ? ' ' + res.detail : '') + (res.wall ? (' (wanted ' + res.wall + ')') : ''))));
      }
    }
    } finally { _fitCache = null; }
    return { lines: lines, ghosts: ghosts, zones: Z, rooms: rooms };
  }
  function _fitBox(txt) {
    if (typeof document === 'undefined' || !document.body) return;
    if (!_fit.box) {
      var b = document.createElement('div');
      b.id = 'cf-fit-test';
      b.style.cssText = 'position:fixed;left:8px;top:8px;z-index:2147483646;max-width:92vw;' +
        'background:rgba(255,255,255,0.95);border:1px solid #999;border-radius:6px;color:#111;' +
        'font:10px/1.35 ui-monospace,Menlo,monospace;box-shadow:0 2px 8px rgba(0,0,0,0.25)';
      var bar = document.createElement('div');
      bar.style.cssText = 'display:flex;align-items:center;gap:6px;padding:4px 6px;border-bottom:1px solid #ddd';
      var ttl = document.createElement('span');
      ttl.textContent = 'TEST';
      ttl.style.cssText = 'flex:1;font-weight:700';
      var bs = 'font:inherit;font-weight:700;padding:3px 8px;border:1px solid #888;border-radius:4px;background:#f2f2f2;color:#111';
      var cp = document.createElement('button');
      cp.type = 'button'; cp.textContent = 'COPY'; cp.style.cssText = bs;
      var hd = document.createElement('button');
      hd.type = 'button'; hd.textContent = 'HIDE'; hd.style.cssText = bs;
      var pre = document.createElement('pre');
      pre.style.cssText = 'margin:0;padding:6px;max-height:32vh;overflow:auto;white-space:pre-wrap';
      cp.addEventListener('click', function () {
        var t = pre.textContent || '';
        function done() { cp.textContent = 'COPIED'; global.setTimeout(function () { cp.textContent = 'COPY'; }, 1200); }
        function fallback() {
          try {
            var ta = document.createElement('textarea');
            ta.value = t; ta.style.cssText = 'position:fixed;left:-9999px;top:0';
            document.body.appendChild(ta); ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            done();
          } catch (e) {}
        }
        try {
          if (global.navigator && global.navigator.clipboard && global.navigator.clipboard.writeText) {
            global.navigator.clipboard.writeText(t).then(done, fallback);
          } else fallback();
        } catch (e2) { fallback(); }
      });
      hd.addEventListener('click', function () {
        var shut = pre.style.display !== 'none';
        pre.style.display = shut ? 'none' : 'block';
        hd.textContent = shut ? 'SHOW' : 'HIDE';
      });
      bar.appendChild(ttl); bar.appendChild(cp); bar.appendChild(hd);
      b.appendChild(bar); b.appendChild(pre);
      document.body.appendChild(b);
      _fit.box = b; _fit.pre = pre;
      /* the bar, so a test can put a button of its own beside COPY (v95) */
      _fit.bar = bar; _fit.cp = cp;
    }
    _fit.pre.textContent = txt;
  }
  /* THE BOX SAYS WHAT IT IS WAITING FOR (v90). It used to be made by the
     plan's own drawing, so with the plan not up there was no box and no word
     why (Marek: nevidim zadny copy log button). Now it is up as soon as the
     switch and the admin are known, and its first line names each condition:
     the switch in the address at load and now, the admin, the plan host and
     whether the 2D plan has drawn the marks yet. */
  function _fitStatus() {
    var LP = global.LEVA_PRODUCT;
    return 'FIT ' + VERSION +
      ' | address at load ' + (/[?&#]fitTest=1/.test(_FIT_AT_LOAD) ? 'fitTest=1' : 'no switch') +
      ', now ' + (/[?&#]fitTest=1/.test(_fitUrlNow()) ? 'fitTest=1' : 'no switch') +
      ' | admin ' + (LP ? String(LP.isAdmin) : 'not known yet') +
      ' | plan host ' + (_PH ? 'yes' : 'NOT YET (configurator still booting)') +
      ' | 2D marks ' + (_fit.drawn ? 'drawn' : 'not drawn yet -- open Floorplan and switch to 2D') +
      ' | plan drawings ' + (_fit.draws | 0);
  }
  /* ONE BOX FOR EVERY TEST (v94): the FIT lines when fitTest is on, the
     PLAN lines when planTest is on, both when both are */
  /* v153: THE ADMIN'S BOX, AND THE BAR'S WHOLE ROAD IN IT. Since v144 every
     step of a reading from the canvas bar refreshed this box, and with no
     switch in the address it held one line, the FIT status -- for anyone: a
     buyer reading a plan got a COPY button over the canvas, and the admin
     got a line with nothing in it (Marek: chtel jsem ti zaslat log z
     textoveho zadani ale skoro nic tam neni). The box is the admin's alone
     now; for him it carries the PLAN lines of the bar's reading, design or
     change, and the FIT status only when fitTest asks for it. */
  function _fitShow() {
    var parts = [], O = _fit.out;
    if (_dbgAdmin() !== true) { _fitBoxAway(); return; }
    if (_fit.on === true) { parts.push(_fitStatus()); if (O) parts.push(O.lines.join('\n')); }
    if (_impParam() || (_imp.user && _imp.lines && _imp.lines.length)) { parts.push(_impStatus()); if (_imp.lines) parts.push(_imp.lines.join('\n')); }
    if (!parts.length) {
      /* a switch in the address with nothing to show yet: what it waits for */
      if (!(_fitParam() || _impParam())) return;
      parts.push(_fitStatus());
    }
    try { _fitBox(parts.join('\n')); } catch (e) {}
    /* and the plan test's own button beside COPY, once the bar is there */
    try { _impButtons(); } catch (eB) {}
  }
  function _fitBoxAway() {
    if (!_fit.box) return;
    try { if (_fit.box.parentNode) _fit.box.parentNode.removeChild(_fit.box); } catch (e) {}
    _fit.box = null; _fit.pre = null; _fit.bar = null; _fit.cp = null; _fit.strip = false;
    try { if (_imp.btns) _imp.btns = {}; _imp.btnFor = null; } catch (e2) {}
  }
  function _dbgAdmin() {
    var LP = global.LEVA_PRODUCT;
    if (!LP) return null;
    return (LP.isAdmin === true || LP.isAdmin === 'true');
  }
  /* the log is written once the plan has stood still for a moment, not on
     every frame of a drag */
  function _fitSchedule() {
    try { if (_fit.timer) global.clearTimeout(_fit.timer); } catch (e) {}
    _fit.timer = global.setTimeout(function () {
      _fit.timer = 0;
      var O = _fit.out;
      if (!O) return;
      _fitShow();
      var txt = O.lines.join('\n');
      if (txt === _fit.logged) return;
      _fit.logged = txt;
      try {
        if (global.Configurator && global.Configurator.log && global.Configurator.log.log) {
          for (var i = 0; i < O.lines.length; i++) global.Configurator.log.log('[floorplan-lib] ' + O.lines[i]);
        }
      } catch (e1) {}
    }, 400);
  }
  /* AND THE TEST RUNS WITHOUT THE PLAN BEING UP: the placer reads the host,
     not the drawing, so the lines are there in 3D as well. Once a second;
     the 2D drawing still reruns it at once while something is dragged. */
  function _fitTick() {
    if (!_PH) { _fitShow(); return; }
    var sig = _fitSig();
    if (sig !== _fit.sig || !_fit.out) {
      _fit.sig = sig;
      try { _fit.out = _fitRunTest(); } catch (e) { _fit.out = { lines: ['FIT test threw: ' + ((e && e.message) || e)], ghosts: [], zones: [] }; }
      _fitSchedule();
    }
    _fitShow();
  }
  (function () {
    if (!(_fitParam() || _impParam()) || typeof global.setInterval !== 'function') return;
    var n = 0, iv = global.setInterval(function () {
      n++;
      var adm = _dbgAdmin();
      if (adm === false) {
        global.clearInterval(iv);
        _fitNote('test switch is in the address but this visitor is not admin -- nothing is shown');
        return;
      }
      if (adm !== true) {
        if (n > 90) { global.clearInterval(iv); _fitNote('test switch is in the address but LEVA_PRODUCT never came -- admin unknown, nothing shown'); }
        return;
      }
      if (_fitParam()) { _fitTestOn(); try { _fitTick(); } catch (e) {} }
      if (_impParam()) { try { _impTick(); } catch (e2) {} }
      _fitShow();
    }, 1000);
  })();
  function _fitGhost(ctx, f, X, Y, ppm, col) {
    var D = _fixDef(f.kind);
    if (!D) return;
    var G = _fixPaths(f.kind, f), WD = _fixWD(f), w = WD.w * ppm, d = WD.d * ppm, k;
    ctx.save();
    ctx.strokeStyle = col;
    ctx.lineWidth = 1.5;
    ctx.lineJoin = 'round'; ctx.lineCap = 'round';
    if (ctx.setLineDash) ctx.setLineDash([4, 3]);
    if (typeof global.DOMMatrix === 'function' && G.paths.length) {
      var M = new global.DOMMatrix().translate(X(f.x), Y(f.z)).rotate((f.rot | 0) * 90)
                .scale(w / G.vw, d / G.vh).translate(-G.vw / 2, -G.vh / 2);
      for (k = 0; k < G.paths.length; k++) {
        var p2 = new global.Path2D();
        p2.addPath(G.paths[k], M);
        ctx.stroke(p2);
      }
    } else {
      ctx.translate(X(f.x), Y(f.z));
      ctx.rotate((f.rot | 0) * Math.PI / 2);
      ctx.strokeRect(-w / 2, -d / 2, w, d);
    }
    ctx.restore();
  }
  function _fitTestDraw(ctx, X, Y, ppm) {
    if (!_fitTestOn() || !_PH) return;
    if (!_fit.drawn) { _fit.drawn = true; try { _fitShow(); } catch (eD) {} }
    var sig = _fitSig();
    if (sig !== _fit.sig || !_fit.out) {
      _fit.sig = sig;
      _fit.out = _fitRunTest();
      _fitSchedule();
    }
    var O = _fit.out, i;
    ctx.save();
    for (i = 0; i < O.zones.length; i++) {
      var z = O.zones[i];
      ctx.fillStyle = (z.t === 'window') ? 'rgba(30,136,229,0.22)' : ((z.t === 'void') ? 'rgba(97,97,97,0.22)' : 'rgba(229,57,53,0.20)');
      ctx.fillRect(X(z.x0), Y(z.z0), (z.x1 - z.x0) * ppm, (z.z1 - z.z0) * ppm);
    }
    /* the box each room is furnished in, thin and dashed, so a wrong room
       is seen before anything is put in it */
    ctx.strokeStyle = 'rgba(55,71,79,0.85)';
    ctx.lineWidth = 1;
    if (ctx.setLineDash) ctx.setLineDash([6, 4]);
    for (i = 0; O.rooms && i < O.rooms.length; i++) {
      var RBs = O.rooms[i].F.boxes || [O.rooms[i].F], rb;
      for (rb = 0; rb < RBs.length; rb++) {
        var RB = RBs[rb];
        ctx.strokeStyle = rb ? 'rgba(55,71,79,0.40)' : 'rgba(55,71,79,0.85)';
        ctx.strokeRect(X(RB.x0), Y(RB.z0), (RB.x1 - RB.x0) * ppm, (RB.z1 - RB.z0) * ppm);
      }
    }
    ctx.restore();
    for (i = 0; i < O.ghosts.length; i++) {
      var g = O.ghosts[i];
      if (g.f) _fitGhost(ctx, g.f, X, Y, ppm, g.ok ? '#2E7D32' : '#E65100');
    }
  }

  /* ===== ZONE B -- replace from here to the END of the file ============ */

  /* =========================================================================
     THE PLAN IMPORT, PART 1 (v94): a plan read off a picture, as DATA,
     straightened and shown -- nothing applied yet.

     Whatever reads the picture (the model, in part 3) only has to say what it
     SEES, in the picture's own pixels: the outline, the partitions, where the
     doors and windows are and how wide, the room names, and the dimension
     lines with the numbers written on them. Everything that has to be exact
     is done here, by code, the same way every time (Marek: model jen cte,
     zbytek je nas kod):

       the scale       from the dimension lines -- the middle one of them, so
                       one misread number does not move the whole house
       the turn        a scan that came in a degree crooked is turned back
       the corners     every wall square, every line on a 5 cm step
       the partitions  ends taken to the wall or partition they nearly meet
       the doors       widths from the doors this site sells (0.70 / 0.80 /
                       0.90, or 28 / 30 / 32 in), each on the wall it is near
       the rooms       a name read off the plan becomes one of our room keys

     What it changed it says, line by line, so nothing is corrected silently.

     raw = {
       units: 'px' | 'm',
       outline: [[x, y], ...]                 outer face, in order round
       dims: [{ a: [x, y], b: [x, y], m }]    a dimension line and its length in m
       scale: { m_per_px }                    instead of dims, if known
       partitions: [{ a: [x, y], b: [x, y] }] centre lines
       doors: [{ at: [x, y], w }]             w in the same units as the points
       windows: [{ at: [x, y], w, sill }]     sill in m
       rooms: [{ at: [x, y], label, key }]
     }
     opts = { longSide: m }  the one question asked when there is no dimension
     ========================================================================= */
  var IMP_SNAP = 0.05;
  var IMP_REACH = 0.45;          /* how near a wall a door or window must be */
  /* v114: what is outdoors -- a room with such a name is not a room */
  var IMP_OUTDOOR = /patio|porch|deck|terrace|balcon|veranda|lanai|outside|outdoor|yard|garden|courtyard/i;
  /* v105: the five new rooms (DINING ROOM, WC, WARDROBE, GARAGE, MUDROOM)
     come BEFORE the rows that used to catch them, and every row a label
     matches is tried in order -- so a WC is a WC where the product offers
     one and a bathroom where it does not */
  var IMP_ROOMS = [
    ['rooms.wc', /\bwc\b|\bw\.c\.|powder|half[ -]?bath|toilet|restroom/i],
    ['rooms.bathroom', /bath|shower|\bwc\b|toilet|restroom|powder|en ?suite/i],
    ['rooms.mudroom', /mud/i],
    ['rooms.laundry', /laundry|utility|\bw\/d\b/i],
    ['rooms.pantry', /pantry/i],
    ['rooms.kitchen', /kitchen/i],
    ['container_room_7', /kid|child|nursery/i],
    ['rooms.bedroom', /bed|master|guest/i],
    ['rooms.office', /office|study|\bden\b/i],
    ['rooms.garage', /garage/i],
    /* v108: the hall in the middle of a house is a HALL, the way the room is
       an ENTRY only where the house is entered (Marek: uprostred domu, kde je
       spojovaci cast, mas hallway / entry, coz je nesmysl) -- each where the
       product offers it, the old row behind them for one that does not */
    ['rooms.hallway', /entry|foyer|vestibule/i],
    ['rooms.hall', /hall|corridor|passage/i],
    ['rooms.hallway', /hall|corridor|entry|foyer|mud/i],
    ['rooms.wardrobe', /walk-?in|\bwic\b|wardrobe|dressing/i],
    ['rooms.storage', /storage|closet|store|walk-in|\bclo\b|linen/i],
    ['rooms.loft', /loft/i],
    ['rooms.diningroom', /dining|nook|breakfast/i],
    ['rooms.livingroom', /living|lounge|family|great|dining/i]
  ];
  /* A PLAN IS ITS OUTER FACES; A BLOCK HOUSE IS BUILT ON ITS WALL AXIS (v170,
     RANCH; core-v580 planOut). Where the product's drawn wall reaches past the
     outline it builds on, the reader works in the OUTER frame -- the outline
     moved out by planOut, the wall that much thicker -- so a plan's 10.19 m
     becomes a 9.75 m axis under a 440 block instead of a 10.19 m axis (the
     house one block too big); what it writes back is taken to the axis. With
     no planOut (every other product) nothing changes. */
  function _impOut() {
    var H = _PH, h = 0;
    try { h = (H && typeof H.planOut === 'function') ? (+H.planOut() || 0) : 0; } catch (e) { h = 0; }
    return (h > 0.001 && h < 1) ? h : 0;
  }
  var _imp = { out: null, lines: null, prev: null, drawn: false, logged: '' };
  function _impSnap(v) { return Math.round(v / IMP_SNAP) * IMP_SNAP; }
  function _impR(v) { return Math.round(v * 1000) / 1000; }
  function _impN(v) { return (Math.round(v * 100) / 100).toFixed(2); }
  /* v144: a point on a side the rectangle took out goes out with it */
  function _impRMove(L, p) {
    for (var q = 0; L && q < L.length; q++) {
      var mv = L[q], al = mv.o === 'h' ? p[0] : p[1], ac = mv.o === 'h' ? p[1] : p[0];
      if (Math.abs(ac - mv.from) < 0.3 && al >= mv.lo - 0.1 && al <= mv.hi + 0.1) { if (mv.o === 'h') p[1] = mv.to; else p[0] = mv.to; return true; }
    }
    return false;
  }
  function _impInPoly(x, z, P) {
    var c = false, i, j;
    for (i = 0, j = P.length - 1; i < P.length; j = i++) {
      if (((P[i][1] > z) !== (P[j][1] > z)) && (x < (P[j][0] - P[i][0]) * (z - P[i][1]) / (P[j][1] - P[i][1]) + P[i][0])) c = !c;
    }
    return c;
  }
  /* key is one key or a list tried in order (v105): the reader's type gives
     the room and what stands in for it where the product does not offer it */
  function _impKey(label, key) {
    var T = DRAW.roomTable(), i, first = '', keys = Array.isArray(key) ? key : (key ? [key] : []);
    function has(kk) { for (var q = 0; q < T.length; q++) if (T[q] && T[q].i18n === kk) return true; return false; }
    for (i = 0; i < keys.length; i++) {
      if (!has(keys[i])) continue;
      return { key: keys[i], note: i ? ('room "' + label + '" is ' + keys[0] + ', which this product does not offer -- named ' + keys[i]) : '' };
    }
    for (i = 0; i < IMP_ROOMS.length; i++) {
      if (!IMP_ROOMS[i][1].test(String(label || ''))) continue;
      if (!first) first = IMP_ROOMS[i][0];
      if (has(IMP_ROOMS[i][0])) {
        return { key: IMP_ROOMS[i][0], note: (IMP_ROOMS[i][0] !== first) ? ('room "' + label + '" is ' + first + ', which this product does not offer -- named ' + IMP_ROOMS[i][0]) : '' };
      }
    }
    if (!first && !keys.length) return { key: '', note: 'room "' + label + '" matches none of our room names' };
    return { key: '', note: 'room "' + label + '" is ' + (keys[0] || first) + ', which this product does not offer' };
  }

  /* A DOOR ON A CUT CORNER (v187, plan 1000076007, Marek: je tam i jedno
     misto kde by se spravne mely implementovat zase dve sikme dvere /
     pricky pri pruchodu do dvou loznic). The hall ends in a little lobby
     whose two far corners are cut at an angle, a door in each -- into the
     office and into the bedroom. The reader read both doors, hinge and
     middle on the slant, but not the two short slanted walls they hang in:
     the office door stood on no wall and was left out, the bedroom door
     went onto the wall between the office and the bedroom, and the hall,
     the bedroom and its closet came out one room. A door read on a slant
     (25 to 65 deg from hinge to middle) with no wall within 0.4 of its
     width gets its wall: along the door, two such walls that nearly meet
     are joined where their lines cross, and a free end runs on to the
     first square wall within 1.5 door widths. Built by the slanted-wall
     road (v125), the door in it. Works on the reader's own units. */
  function _impCutCornerDoors(raw) {
    if (!raw || !raw.doors || !raw.doors.length) return null;
    var SQ = [], DG = [], i, j, k, said = [];
    function addSeg(a, b) {
      if (!a || !b) return;
      var dx = b[0] - a[0], dy = b[1] - a[1], ang = Math.atan2(Math.abs(dy), Math.abs(dx)) * 180 / Math.PI;
      if (ang > 20 && ang < 70) { DG.push([a, b]); return; }
      if (Math.abs(dx) >= Math.abs(dy)) SQ.push({ o: 'h', c: (a[1] + b[1]) / 2, lo: Math.min(a[0], b[0]), hi: Math.max(a[0], b[0]) });
      else SQ.push({ o: 'v', c: (a[0] + b[0]) / 2, lo: Math.min(a[1], b[1]), hi: Math.max(a[1], b[1]) });
    }
    var OL = raw.outline || [];
    for (i = 0; i < OL.length; i++) addSeg(OL[i], OL[(i + 1) % OL.length]);
    for (i = 0; raw.partitions && i < raw.partitions.length; i++) addSeg(raw.partitions[i].a, raw.partitions[i].b);
    function dSeg(p, a, b) {
      var dx = b[0] - a[0], dy = b[1] - a[1], L2 = dx * dx + dy * dy, t = L2 > 0 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2)) : 0;
      return Math.hypot(p[0] - a[0] - dx * t, p[1] - a[1] - dy * t);
    }
    function nearWall(p) {
      var best = 1e9, q;
      for (q = 0; q < SQ.length; q++) best = Math.min(best, dSeg(p, SQ[q].o === 'h' ? [SQ[q].lo, SQ[q].c] : [SQ[q].c, SQ[q].lo], SQ[q].o === 'h' ? [SQ[q].hi, SQ[q].c] : [SQ[q].c, SQ[q].hi]));
      for (q = 0; q < DG.length; q++) best = Math.min(best, dSeg(p, DG[q][0], DG[q][1]));
      return best;
    }
    var C = [];
    for (i = 0; i < raw.doors.length; i++) {
      var d = raw.doors[i], w = Number(d.w) || 0, Hn = d.hinge, A = d.at;
      if (!(w > 0) || !Hn || !A || !isFinite(Hn[0]) || !isFinite(A[0])) continue;
      if ((d.rooms || []).some(function (t) { return /^(outside|outdoors?|exterior|garden|yard|porch|deck|patio)$/i.test(String(t).trim()); })) continue;
      var ux = A[0] - Hn[0], uy = A[1] - Hn[1], L = Math.hypot(ux, uy);
      if (L < 0.3 * w) continue;
      var ang = Math.atan2(Math.abs(uy), Math.abs(ux)) * 180 / Math.PI;
      if (ang < 25 || ang > 65) continue;
      if (nearWall(A) <= 0.4 * w) continue;
      ux /= L; uy /= L;
      C.push({ i: i, w: w, h: [Hn[0], Hn[1]], e: [ux, uy], t0: 0, t1: w, f0: false, f1: false, nm: (d.rooms || []).join(' / ') });
    }
    if (!C.length) return null;
    /* two that nearly meet: joined where their lines cross */
    for (i = 0; i < C.length; i++) for (j = i + 1; j < C.length; j++) {
      var P = C[i], Q = C[j], den = P.e[0] * Q.e[1] - P.e[1] * Q.e[0];
      if (Math.abs(den) < 0.2) continue;
      var rx = Q.h[0] - P.h[0], ry = Q.h[1] - P.h[1];
      var tp = (rx * Q.e[1] - ry * Q.e[0]) / den, tq = (rx * P.e[1] - ry * P.e[0]) / den;
      var gp = tp >= P.w ? tp - P.w : (tp <= 0 ? -tp : -1), gq = tq >= Q.w ? tq - Q.w : (tq <= 0 ? -tq : -1);
      if (gp < 0 || gq < 0 || gp > P.w || gq > Q.w) continue;
      if (tp >= P.w) { P.t1 = tp; P.f1 = true; } else { P.t0 = tp; P.f0 = true; }
      if (tq >= Q.w) { Q.t1 = tq; Q.f1 = true; } else { Q.t0 = tq; Q.f0 = true; }
    }
    /* a free end runs on to the first square wall */
    function runOn(c, dir) {
      var from = dir > 0 ? c.t1 : c.t0, best = null, q, sl = 0.5 * c.w;
      for (q = 0; q < SQ.length; q++) {
        var S = SQ[q], ec = S.o === 'h' ? c.e[1] : c.e[0];
        if (Math.abs(ec) < 1e-6) continue;
        var t = ((S.c - (S.o === 'h' ? c.h[1] : c.h[0])) / ec);
        if (dir > 0 ? (t < from - 1e-6 || t > from + 1.5 * c.w) : (t > from + 1e-6 || t < from - 1.5 * c.w)) continue;
        var al = S.o === 'h' ? c.h[0] + c.e[0] * t : c.h[1] + c.e[1] * t;
        if (al < S.lo - sl || al > S.hi + sl) continue;
        if (best == null || Math.abs(t - from) < Math.abs(best - from)) best = t;
      }
      return best;
    }
    /* only a pair: the lobby's two cut corners. One door alone on a slant is
       more often an open leaf read for the door (the owners' suite door of
       the same plan, in a square wall, was read so) -- left as it was */
    var lone = [];
    C = C.filter(function (c9) { if (c9.f0 || c9.f1) return true; lone.push('"' + c9.nm + '"'); return false; });
    if (!C.length) return lone.length ? { raw: raw, n: 0, said: [], lone: lone } : null;
    var R = JSON.parse(JSON.stringify(raw));
    R.partitions = R.partitions || [];
    /* a square wall that stops short of a slanted wall's end, in line with
       it (a hand away, a stub of it between): run on to that end */
    function reach(E, wq) {
      var q, P2, n2 = 0, bestL2 = -1, bestC2 = null, bestH2 = false;
      for (q = 0; q < R.partitions.length; q++) {
        P2 = R.partitions[q];
        if (!P2 || !P2.a || !P2.b || P2.cut) continue;
        var dx = P2.b[0] - P2.a[0], dy = P2.b[1] - P2.a[1], ho = Math.abs(dx) >= Math.abs(dy);
        if (Math.atan2(Math.min(Math.abs(dx), Math.abs(dy)), Math.max(Math.abs(dx), Math.abs(dy))) > 0.2) continue;
        var cc = ho ? (P2.a[1] + P2.b[1]) / 2 : (P2.a[0] + P2.b[0]) / 2, ea = ho ? E[0] : E[1];
        if (Math.abs(cc - (ho ? E[1] : E[0])) > 0.3 * wq) continue;
        var ia = ho ? 0 : 1, lo2 = Math.min(P2.a[ia], P2.b[ia]), hi2 = Math.max(P2.a[ia], P2.b[ia]);
        if (ea >= lo2 - 1e-6 && ea <= hi2 + 1e-6) continue;
        var hit = false;
        if (ea > hi2 && ea - hi2 <= 2 * wq) { if (P2.a[ia] >= P2.b[ia]) P2.a[ia] = ea; else P2.b[ia] = ea; hit = true; }
        else if (ea < lo2 && lo2 - ea <= 2 * wq) { if (P2.a[ia] <= P2.b[ia]) P2.a[ia] = ea; else P2.b[ia] = ea; hit = true; }
        /* and the slanted wall's end goes onto the longest such wall's line, so the two are one corner */
        if (hit) { n2++; if (hi2 - lo2 > bestL2) { bestL2 = hi2 - lo2; bestC2 = cc; bestH2 = ho; } }
      }
      if (bestC2 != null) { if (bestH2) E[1] = bestC2; else E[0] = bestC2; }
      return n2;
    }
    /* a joint near the end of a square wall goes onto that end */
    function snapEnd(J, wq) {
      var best = null, bd = 0.6 * wq, q;
      for (q = 0; q < SQ.length; q++) {
        var S = SQ[q], e1 = S.o === 'h' ? [S.lo, S.c] : [S.c, S.lo], e2 = S.o === 'h' ? [S.hi, S.c] : [S.c, S.hi];
        var d1 = Math.hypot(J[0] - e1[0], J[1] - e1[1]), d2 = Math.hypot(J[0] - e2[0], J[1] - e2[1]);
        if (d1 < bd) { bd = d1; best = e1; }
        if (d2 < bd) { bd = d2; best = e2; }
      }
      return best;
    }
    var JO = [];
    for (k = 0; k < C.length; k++) {
      var c = C[k], t;
      if (!c.f0) { t = runOn(c, -1); if (t != null) c.t0 = t; }
      if (!c.f1) { t = runOn(c, 1); if (t != null) c.t1 = t; }
      var a = [c.h[0] + c.e[0] * c.t0, c.h[1] + c.e[1] * c.t0], b = [c.h[0] + c.e[0] * c.t1, c.h[1] + c.e[1] * c.t1];
      var sa = c.f0 ? snapEnd(a, c.w) : null, sb = c.f1 ? snapEnd(b, c.w) : null;
      if (sa) a = [sa[0], sa[1]];
      if (sb) b = [sb[0], sb[1]];
      if (!c.f0) reach(a, c.w);
      if (!c.f1) reach(b, c.w);
      R.partitions.push({ a: a, b: b, cut: true });
      said.push('"' + c.nm + '" door ' + Math.round(Math.atan2(Math.abs(c.e[1]), Math.abs(c.e[0])) * 180 / Math.PI) + ' deg -> a slanted wall ' +
                (Math.round((c.t1 - c.t0) / c.w * 10) / 10) + ' door widths long' + ((c.f0 || c.f1) ? ', joined to the next one' : ''));
    }
    return { raw: R, n: C.length, said: said, lone: lone };
  }
  /* the scale the sizes written in the rooms give (v99's rule, v192 as a
     function): each room both ways, the way its two sides agree; the middle
     and those within 5 % of it. Three rooms at least, or null. */
  function _impRoomScale(raw) {
    var RS = [], i;
    for (i = 0; raw && raw.rooms && i < raw.rooms.length; i++) {
      var rm = raw.rooms[i];
      if (!rm || !rm.size || !rm.box || !rm.box[0] || !rm.box[1]) continue;
      var bw = Math.abs(rm.box[1][0] - rm.box[0][0]), bh = Math.abs(rm.box[1][1] - rm.box[0][1]), SA = rm.size[0], SB = rm.size[1];
      if (!(bw > 4 && bh > 4 && SA > 0.5 && SB > 0.5)) continue;
      var p1a = SA / bw, p1b = SB / bh, p2a = SB / bw, p2b = SA / bh;
      var e1 = Math.abs(p1a - p1b) / (p1a + p1b), e2 = Math.abs(p2a - p2b) / (p2a + p2b);
      if (Math.min(e1, e2) < 0.08) RS.push((e1 <= e2) ? (p1a + p1b) / 2 : (p2a + p2b) / 2);
    }
    if (RS.length < 3) return null;
    RS.sort(function (a, b) { return a - b; });
    var m = Math.floor(RS.length / 2), med = (RS.length % 2) ? RS[m] : (RS[m - 1] + RS[m]) / 2, ag = [], sum = 0, k;
    for (k = 0; k < RS.length; k++) if (Math.abs(RS[k] - med) / med <= 0.05) { ag.push(RS[k]); sum += RS[k]; }
    return ag.length >= 2 ? { s: sum / ag.length, n: ag.length } : null;
  }
  /* v203: the two inner walls of an L's corner (box frame, the corner as
     { x0, x1, z0, z1 }): the line that is not on the box's edge, each way --
     a point within tol of one of them, inside its stretch, comes back on it */
  function _impLInner(LN) {
    return { z: (LN.z0 > 0.01) ? LN.z0 : LN.z1, x: (LN.x0 > 0.01) ? LN.x0 : LN.x1 };
  }
  function _impOnLNotch(LN, p, tol) {
    if (!LN || !p) return null;
    var I = _impLInner(LN);
    if (Math.abs(p[1] - I.z) <= tol && p[0] >= LN.x0 - 0.05 && p[0] <= LN.x1 + 0.05) return [p[0], I.z];
    if (Math.abs(p[0] - I.x) <= tol && p[1] >= LN.z0 - 0.05 && p[1] <= LN.z1 + 0.05) return [I.x, p[1]];
    return null;
  }
  function _impNormalize(raw, opts) {
    opts = opts || {};
    var H = _PH, out = { ok: false, why: '', scale: 0, scaleFrom: '', spread: 0, turn: 0, outline: [], w: 0, d: 0,
                         partitions: [], doors: [], openings: [], rooms: [], notes: [] };
    var CCD = null;
    try { CCD = _impCutCornerDoors(raw); } catch (eCC) { CCD = null; }
    if (CCD && CCD.n) { raw = CCD.raw; out.notes.push('CUT CORNER (v187): ' + CCD.n + ' door(s) read on a slant with no wall there got their slanted wall: ' + CCD.said.join(', ')); }
    if (CCD && CCD.lone && CCD.lone.length) out.notes.push('CUT CORNER (v187): ' + CCD.lone.length + ' door(s) read alone on a slant, no wall there -- no slanted wall made, read as a square door: ' + CCD.lone.join(', '));
    var WALL = (H && H.wallThk) ? H.wallThk() : 0.16, i, q;
    /* NO OUTLINE, STILL A HOUSE (v114, Marek: i kdyby byl problem precist
       nektere prvky, porad by se mel objevit pudorys, sedet obvodove steny a
       co nejvic pricek). A reader that gave no outline but did see walls,
       rooms or windows gets the rectangle round all of them as the house. */
    if (raw && (!raw.outline || raw.outline.length < 3)) {
      var ex0 = 1e9, ex1 = -1e9, ez0 = 1e9, ez1 = -1e9, nEx = 0;
      function exP(p) { if (p && isFinite(p[0]) && isFinite(p[1])) { ex0 = Math.min(ex0, p[0]); ex1 = Math.max(ex1, p[0]); ez0 = Math.min(ez0, p[1]); ez1 = Math.max(ez1, p[1]); nEx++; } }
      for (i = 0; raw.partitions && i < raw.partitions.length; i++) { exP(raw.partitions[i].a); exP(raw.partitions[i].b); }
      for (i = 0; raw.rooms && i < raw.rooms.length; i++) { if (raw.rooms[i].box) { exP(raw.rooms[i].box[0]); exP(raw.rooms[i].box[1]); } }
      for (i = 0; raw.windows && i < raw.windows.length; i++) exP(raw.windows[i].at);
      for (i = 0; raw.doors && i < raw.doors.length; i++) exP(raw.doors[i].at);
      if (nEx >= 4 && ex1 - ex0 > 1 && ez1 - ez0 > 1) {
        raw.outline = [[ex0, ez0], [ex1, ez0], [ex1, ez1], [ex0, ez1]];
        out.notes.push('no outline was read -- the rectangle round the walls, rooms and windows read is taken as the house');
      }
    }
    if (!raw || !raw.outline || raw.outline.length < 3) { out.why = 'outline'; out.notes.push('no outline was read'); return out; }

    /* THE SCALE: metres per pixel, from the dimension lines -- the middle
       one, and the spread between them reported */
    var s = 0;
    if (raw.units === 'm') { s = 1; out.scaleFrom = 'metres'; }
    else if (raw.scale && raw.scale.m_per_px > 0) { s = Number(raw.scale.m_per_px); out.scaleFrom = 'given'; }
    else if (raw.dims && raw.dims.length) {
      var R = [];
      for (i = 0; i < raw.dims.length; i++) {
        var dm = raw.dims[i], px = Math.hypot(dm.b[0] - dm.a[0], dm.b[1] - dm.a[1]);
        if (px > 1 && Number(dm.m) > 0) R.push(Number(dm.m) / px);
      }
      if (R.length) {
        R.sort(function (a, b) { return a - b; });
        var mid = Math.floor(R.length / 2), med = (R.length % 2) ? R[mid] : (R[mid - 1] + R[mid]) / 2;
        /* the middle one decides which agree; those that agree within 1.5%
           are averaged, so the wobble of any one line is shared out */
        var agree = [], sum = 0, k0;
        for (k0 = 0; k0 < R.length; k0++) if (Math.abs(R[k0] - med) / med <= 0.015) { agree.push(R[k0]); sum += R[k0]; }
        s = agree.length ? sum / agree.length : med;
        out.spread = (R[R.length - 1] - R[0]) / s;
        out.scaleFrom = agree.length + ' of ' + R.length + ' dimension(s)';
        if (agree.length < R.length) out.notes.push((R.length - agree.length) + ' dimension(s) disagreed with the rest by more than 1.5% and were not used');
        /* THE DIMENSION ON THE OUTLINE DECIDES (v185, plan 1000076007, the
           RANCH thread: 88' lay exactly over the outline, 110-1120 px; the
           reader put 57' -- the patio's top to the porch's foot -- over
           243-782 px where the house is 243-662, the two met at their
           middle, "0 of 2", and the house came out 11 % too big). When the
           dimensions do not all agree, the ones whose line is as long as the
           outline is across or along it, or as one of its sides parallel to
           them (within 2 %), are the scale; a line that fits nothing is
           left out. None fits: as before. */
        if (agree.length < R.length && raw.dims.length >= 2) {
          var OLs = raw.outline, oX0 = 1e9, oX1 = -1e9, oY0 = 1e9, oY1 = -1e9, kO, ON = [], offD = [];
          for (kO = 0; kO < OLs.length; kO++) { oX0 = Math.min(oX0, OLs[kO][0]); oX1 = Math.max(oX1, OLs[kO][0]); oY0 = Math.min(oY0, OLs[kO][1]); oY1 = Math.max(oY1, OLs[kO][1]); }
          for (k0 = 0; k0 < raw.dims.length; k0++) {
            var dO = raw.dims[k0], dx = Math.abs(dO.b[0] - dO.a[0]), dy = Math.abs(dO.b[1] - dO.a[1]), dpx = Math.hypot(dx, dy);
            if (!(dpx > 1) || !(Number(dO.m) > 0)) continue;
            var horD = dx >= dy, cand = [horD ? (oX1 - oX0) : (oY1 - oY0)], hit = false;
            for (kO = 0; kO < OLs.length; kO++) {
              var pA = OLs[kO], pB = OLs[(kO + 1) % OLs.length], ex = Math.abs(pB[0] - pA[0]), ey = Math.abs(pB[1] - pA[1]);
              if ((horD && ex > 3 * ey) || (!horD && ey > 3 * ex)) cand.push(Math.hypot(ex, ey));
            }
            for (kO = 0; kO < cand.length && !hit; kO++) if (cand[kO] > 1 && Math.abs(dpx - cand[kO]) / cand[kO] <= 0.02) hit = true;
            if (hit) ON.push({ r: Number(dO.m) / dpx, t: _impN(Number(dO.m)) + ' m' }); else offD.push(_impN(Number(dO.m)) + ' m');
          }
          if (ON.length && offD.length) {
            var sO = 0;
            for (kO = 0; kO < ON.length; kO++) sO += ON[kO].r;
            s = sO / ON.length;
            out.scaleFrom = ON.length + ' of ' + R.length + ' dimension(s), on the outline';
            out.notes.push('DIMS ON THE OUTLINE (v185): ' + ON.map(function (o9) { return o9.t; }).join(', ') + ' as long as the outline -- the scale; left out, its line as long as nothing on the outline: ' + offD.join(', '));
          }
          /* THE ROOM SIZES DECIDE (v192, plan 1000076007 again, D1 #89, Marek:
             je to jeste horsi). The reader put 57' over the house alone (243-641
             px) where it runs from the patio's top to the porch's foot; both
             lines then lay as long as the outline, nothing told them apart,
             and their mean was taken: 0.0351 m/px, the house 35.4 x 14.0 m
             instead of 26.8 x 10.6, everything after it wrong. When the
             outline cannot tell, the sizes written in the rooms (22' x 34',
             12' x 14'10" ...) can: the dimensions within 4 % of their scale
             are kept; none is, and the room sizes are the scale. Never the
             mean of two that disagree. */
          else {
            var RSD = _impRoomScale(raw), keepD = [], sumD = 0;
            if (RSD) {
              for (k0 = 0; k0 < R.length; k0++) if (Math.abs(R[k0] - RSD.s) / RSD.s <= 0.04) { keepD.push(R[k0]); sumD += R[k0]; }
              /* only when they decide: some agree and some do not. None agreeing
                 says the room sizes are the uncertain ones (ADU 1000075604: its
                 rooms' boxes are not what their sizes measure) -- as before */
              if (keepD.length && keepD.length < R.length) {
                s = sumD / keepD.length;
                out.scaleFrom = keepD.length + ' of ' + R.length + ' dimension(s), the one(s) the room sizes agree with';
                out.notes.push('ROOM SIZES DECIDE (v192): the dimensions disagree and the outline cannot tell them apart -- ' + keepD.length + ' of them within 4 % of the scale of ' +
                               RSD.n + ' room size(s) kept, ' + (R.length - keepD.length) + ' left out');
              } else out.notes.push('ROOM SIZES (v192): ' + (keepD.length ? 'all' : 'none') + ' of the dimensions within 4 % of the scale of ' + RSD.n + ' room size(s) -- they do not decide, the dimensions as before');
            }
          }
        }
      }
    }
    if (!(s > 0) && opts.longSide > 0) {
      /* MEASURED ACROSS THE STRAIGHTENED OUTLINE (v99): a picture lying a
         degree crooked is wider than the house in it, and a long side laid
         over the crooked width came out a few centimetres short */
      var lsW = 0, lsA = 0, bx0 = 1e9, bx1 = -1e9, bz0 = 1e9, bz1 = -1e9;
      for (i = 0; i < raw.outline.length; i++) {
        var la = raw.outline[i], lb = raw.outline[(i + 1) % raw.outline.length], lL = Math.hypot(lb[0] - la[0], lb[1] - la[1]);
        if (!(lL > 0)) continue;
        var lth = Math.atan2(lb[1] - la[1], lb[0] - la[0]);
        lsA += (lth - Math.round(lth / (Math.PI / 2)) * (Math.PI / 2)) * lL; lsW += lL;
      }
      var lt = lsW ? -lsA / lsW : 0, lc = Math.cos(lt), ls = Math.sin(lt);
      for (i = 0; i < raw.outline.length; i++) {
        var lx = raw.outline[i][0] * lc - raw.outline[i][1] * ls, lz = raw.outline[i][0] * ls + raw.outline[i][1] * lc;
        bx0 = Math.min(bx0, lx); bx1 = Math.max(bx1, lx); bz0 = Math.min(bz0, lz); bz1 = Math.max(bz1, lz);
      }
      s = Number(opts.longSide) / Math.max(bx1 - bx0, bz1 - bz0);
      out.scaleFrom = 'the long side given as ' + _impN(Number(opts.longSide)) + ' m';
    }
    /* THE ROOM SIZES, WHEN THERE IS NO DIMENSION LINE (v99). A sales plan
       often has none and writes the size into each room instead (16/0 X
       12/0, 4,20 x 3,60). The reader hands each such room its size and its
       box -- the inner faces of its walls, in pixels -- and every room gives
       a scale. Which of the two numbers runs across the picture is not
       written anywhere, so both ways are tried and the one whose two sides
       agree is kept; then the same middle-and-agreeing rule as for the
       dimensions, only looser, because a room size is rounded and a box is
       only as good as the reader's eye. */
    if (!(s > 0) && raw.rooms && raw.rooms.length) {
      var RS = [], nRm = 0;
      for (i = 0; i < raw.rooms.length; i++) {
        var rm = raw.rooms[i];
        if (!rm || !rm.size || !rm.box || !rm.box[0] || !rm.box[1]) continue;
        nRm++;
        var bw = Math.abs(rm.box[1][0] - rm.box[0][0]), bh = Math.abs(rm.box[1][1] - rm.box[0][1]), SA = rm.size[0], SB = rm.size[1];
        if (!(bw > 4 && bh > 4 && SA > 0.5 && SB > 0.5)) continue;
        var p1a = SA / bw, p1b = SB / bh, p2a = SB / bw, p2b = SA / bh;
        var e1 = Math.abs(p1a - p1b) / (p1a + p1b), e2 = Math.abs(p2a - p2b) / (p2a + p2b);
        if (Math.min(e1, e2) < 0.08) RS.push((e1 <= e2) ? (p1a + p1b) / 2 : (p2a + p2b) / 2);
      }
      if (RS.length) {
        RS.sort(function (a, b) { return a - b; });
        var mR = Math.floor(RS.length / 2), medR = (RS.length % 2) ? RS[mR] : (RS[mR - 1] + RS[mR]) / 2, agR = [], sumR = 0, kR;
        for (kR = 0; kR < RS.length; kR++) if (Math.abs(RS[kR] - medR) / medR <= 0.05) { agR.push(RS[kR]); sumR += RS[kR]; }
        s = agR.length ? sumR / agR.length : medR;
        out.spread = (RS[RS.length - 1] - RS[0]) / s;
        out.scaleFrom = agR.length + ' of ' + nRm + ' room size(s)';
        out.notes.push('no dimension line was read -- the scale comes from the sizes written in the rooms, which is less exact; SIZE gives the long side instead');
        if (agR.length < RS.length) out.notes.push((RS.length - agR.length) + ' room size(s) disagreed with the rest by more than 5% and were not used');
      } else if (nRm) out.notes.push(nRm + ' room size(s) were read, but none of them fitted its room on the picture');
    }
    /* AND THE AREAS WRITTEN IN THE ROOMS (v171): 165 SQ. FT over the box of
       the room's walls gives a scale, the square root of the two. Two at
       least, the middle of them, and those within 6 % of it -- a written
       area is rounded and a box is only as good as the reader's eye (the
       dining area of 1000075647 was boxed 14 % short and is left out). */
    if (!(s > 0) && raw.rooms && raw.rooms.length) {
      var RA = [], nRa = 0;
      for (i = 0; i < raw.rooms.length; i++) {
        var ra9 = raw.rooms[i];
        if (!ra9 || !(ra9.areaW > 0.5) || !ra9.box || !ra9.box[0] || !ra9.box[1]) continue;
        nRa++;
        var aw9 = Math.abs(ra9.box[1][0] - ra9.box[0][0]), ah9 = Math.abs(ra9.box[1][1] - ra9.box[0][1]);
        if (aw9 > 4 && ah9 > 4) RA.push(Math.sqrt(ra9.areaW / (aw9 * ah9)));
      }
      if (RA.length >= 2) {
        RA.sort(function (a, b) { return a - b; });
        var mA9 = Math.floor(RA.length / 2), medA = (RA.length % 2) ? RA[mA9] : (RA[mA9 - 1] + RA[mA9]) / 2, agA = [], sumA = 0, kA;
        for (kA = 0; kA < RA.length; kA++) if (Math.abs(RA[kA] - medA) / medA <= 0.06) { agA.push(RA[kA]); sumA += RA[kA]; }
        if (agA.length >= 2) {
          s = sumA / agA.length;
          out.spread = (RA[RA.length - 1] - RA[0]) / s;
          out.scaleFrom = agA.length + ' of ' + nRa + ' room size(s) written as areas';
          out.notes.push('no dimension line was read -- the scale comes from the areas written in the rooms (v171), which is less exact; SIZE gives the long side instead');
          if (agA.length < RA.length) out.notes.push((RA.length - agA.length) + ' room area(s) disagreed with the rest by more than 6% and were not used');
        } else out.notes.push(RA.length + ' room area(s) were read, but they disagree with each other -- not used for the scale');
      } else if (nRa) out.notes.push(nRa + ' room area(s) were read, too few to give the scale');
    }
    /* THE DOORS GIVE A SCALE, LAST OF ALL (v114): a door labelled 2668 is
       0.76 m wide whatever the picture's size; with no label a drawn leaf is
       taken as 0.80 m. The middle of them decides, and the line says SIZE
       corrects it. */
    /* AND THE FURNITURE BEFORE THE DOORS (v132, plan 3D render: no text at
       all, the doors taken as 0.80 m made the house 6.15 x 4.65 m -- the bed
       in it says 7.7 x 5.8). A door is anything from 0.61 to 0.91 m, and in a
       picture drawn in 3D its leaf is a slab nobody can measure; a bed is 1.91
       to 2.03 m long whatever the plan, a tub 1.52, a WC 0.70 deep, a counter
       0.61 deep. With two pieces or more the furniture decides -- the middle
       of them, a bed counting three times, a tub or a counter twice -- and
       the doors only check it: drawn at that scale they must come out 0.55 to
       1.15 m, or the doors join the vote. A door label (2668) still beats
       everything, being written. Pieces the reader only suggested are not
       measured. */
    if (!(s > 0) && ((raw.doors && raw.doors.length) || (raw.fixtures && raw.fixtures.length))) {
      var DS = [], dLab = 0, FV = [], DV = [], fWhat = [];
      for (i = 0; raw.doors && i < raw.doors.length; i++) {
        var dd9 = raw.doors[i], lw9 = _impDoorLabelW(dd9.label);
        if (!(dd9.w > 4)) continue;
        if (lw9) { DS.push(lw9 / dd9.w); dLab++; }
      }
      if (DS.length) {
        DS.sort(function (a, b) { return a - b; });
        s = DS[Math.floor(DS.length / 2)];
        out.scaleFrom = DS.length + ' door label(s)';
        out.notes.push('no dimension and no room size was read -- the scale comes from ' + dLab + ' door label(s) -- SIZE corrects it');
      } else {
        for (i = 0; raw.doors && i < raw.doors.length; i++) {
          var dd8 = raw.doors[i];
          if (dd8.w > 4 && (!dd8.kind || dd8.kind === 'hinged')) DV.push({ v: 0.80 / dd8.w, w: 1 });
        }
        for (i = 0; raw.fixtures && i < raw.fixtures.length; i++) {
          var fq = raw.fixtures[i];
          if (!fq || fq.sug || !fq.a || !fq.b) continue;
          var fdx = Math.abs(fq.b[0] - fq.a[0]), fdz = Math.abs(fq.b[1] - fq.a[1]), fL = Math.max(fdx, fdz), fS = Math.min(fdx, fdz);
          if (!(fL > 4 && fS > 2)) continue;
          var ft = String(fq.type || '').toLowerCase(), fl = String(fq.label || '').toUpperCase(), fr = fL / fS, fm = 0, fw = 0, fn = '';
          if (/bed/.test(ft) && !/sofa/.test(ft) && fr >= 1.0 && fr <= 2.3) {
            fm = /CAL/.test(fl) ? 2.13 : (/KING|QUEEN/.test(fl) ? 2.03 : (/FULL|DOUBLE|TWIN|SINGLE/.test(fl) ? 1.91 : 2.0)); fw = 3; fn = 'bed ' + fm.toFixed(2) + ' m long';
          } else if (/tub/.test(ft) && fr >= 1.6 && fr <= 3.2) {
            fm = /6\s*'|6\s*FT|72/.test(fl) ? 1.83 : (/5\s*'\s*6|66/.test(fl) ? 1.68 : 1.52); fw = 2; fn = 'tub ' + fm.toFixed(2);
          } else if (/^(toilet|wc)$/.test(ft) && fr >= 1.1 && fr <= 2.3) {
            fm = 0.70; fw = 1; fn = 'WC 0.70 deep';
          } else if (/^(washer|dryer)$/.test(ft) && fr <= 1.45) {
            fm = 0.74; fw = 1; fn = ft + ' 0.74';
          } else if (/fridge|refrigerator/.test(ft) && fr <= 1.6) {
            fm = 0.80; fw = 1; fn = 'fridge 0.80';
          } else if (ft === 'kitchen' && fr >= 2.5) {
            fm = 0.61; fw = 2; fn = 'counter 0.61 deep';
            FV.push({ v: fm / fS, w: fw }); fWhat.push(fn);
            continue;
          }
          if (fm > 0) { FV.push({ v: fm / fL, w: fw }); fWhat.push(fn); }
        }
        var wmed = function (A) {
          var B = A.slice().sort(function (p0, p1) { return p0.v - p1.v; }), tot = 0, acc = 0, k9;
          for (k9 = 0; k9 < B.length; k9++) tot += B[k9].w;
          for (k9 = 0; k9 < B.length; k9++) { acc += B[k9].w; if (acc >= tot / 2) return B[k9].v; }
          return B.length ? B[B.length - 1].v : 0;
        };
        var sF = (FV.length >= 2) ? wmed(FV) : 0, dAt = '';
        if (sF > 0 && DV.length) {
          var DW = DV.map(function (q9) { return 0.80 / q9.v * sF; }).sort(function (p0, p1) { return p0 - p1; }), dMid = DW[Math.floor(DW.length / 2)];
          dAt = ' -- the doors come out ' + _impN(dMid) + ' m at it';
          if (dMid < 0.55 || dMid > 1.15) { sF = 0; dAt = ' -- but the doors would come out ' + _impN(dMid) + ' m at it, so the doors vote too'; }
        }
        if (sF > 0) {
          s = sF;
          out.scaleFrom = FV.length + ' piece(s) of furniture';
          /* v198: three pieces or more and the drawn doors a door's width
             at their scale -- two measures agreeing, enough for the library */
          if (FV.length >= 3 && dAt && DV.length) {
            var DW2 = DV.map(function (q9) { return 0.80 / q9.v * sF; }).sort(function (p0, p1) { return p0 - p1; }), dM2 = DW2[Math.floor(DW2.length / 2)];
            if (dM2 >= 0.70 && dM2 <= 0.95) out.scaleDoors = _impR(dM2);
          }
          out.notes.push('no dimension and no room size was read -- the scale comes from the furniture (' + fWhat.join(', ') + ')' + dAt + ' -- SIZE corrects it');
        } else if (FV.length + DV.length) {
          s = wmed(FV.concat(DV));
          out.scaleFrom = (FV.length ? (FV.length + ' piece(s) of furniture and ') : '') + DV.length + ' door(s)';
          out.notes.push('no dimension and no room size was read -- the scale is guessed from ' + (FV.length ? ('the furniture (' + fWhat.join(', ') + ') and ') : '') +
                         DV.length + ' drawn door(s) taken as 0.80 m' + dAt + ' -- SIZE corrects it');
        }
      }
    }
    if (!(s > 0)) { out.why = 'scale'; out.notes.push('no scale: no dimension and no room size was read and no length was given -- tap SIZE and give the long side'); return out; }
    out.scale = s;

    /* THE TURN: the edges of a square plan all lie on two directions; a scan
       that came in crooked has them all off by the same angle. The mean of
       that angle, weighted by length, is turned back about the middle. */
    var P0 = [];
    for (i = 0; i < raw.outline.length; i++) P0.push([raw.outline[i][0] * s, raw.outline[i][1] * s]);
    var sw = 0, sa = 0, cx = 0, cz = 0;
    for (i = 0; i < P0.length; i++) {
      var A0 = P0[i], B0 = P0[(i + 1) % P0.length], L0 = Math.hypot(B0[0] - A0[0], B0[1] - A0[1]);
      if (L0 < 0.05) continue;
      var th = Math.atan2(B0[1] - A0[1], B0[0] - A0[0]);
      th = th - Math.round(th / (Math.PI / 2)) * (Math.PI / 2);
      sa += th * L0; sw += L0; cx += (A0[0] + B0[0]) / 2 * L0; cz += (A0[1] + B0[1]) / 2 * L0;
    }
    var turn = sw ? sa / sw : 0;
    cx = sw ? cx / sw : 0; cz = sw ? cz / sw : 0;
    if (Math.abs(turn) < 0.3 * Math.PI / 180) turn = 0;
    out.turn = turn * 180 / Math.PI;
    if (turn) out.notes.push('the drawing lay ' + Math.abs(out.turn).toFixed(1) + ' deg crooked and was turned straight');
    var cs = Math.cos(-turn), sn = Math.sin(-turn);
    function M(p) {
      var x = p[0] * s - cx, z = p[1] * s - cz;
      return [x * cs - z * sn + cx, x * sn + z * cs + cz];
    }

    /* THE CORNERS: each edge is horizontal or vertical, pieces of one wall
       are one wall, and the corners are where they meet */
    var pts = [];
    for (i = 0; i < raw.outline.length; i++) {
      var m = M(raw.outline[i]);
      if (!pts.length || Math.hypot(m[0] - pts[pts.length - 1][0], m[1] - pts[pts.length - 1][1]) > 0.05) pts.push(m);
    }
    if (pts.length > 2 && Math.hypot(pts[0][0] - pts[pts.length - 1][0], pts[0][1] - pts[pts.length - 1][1]) < 0.05) pts.pop();
    var E = [];
    for (i = 0; i < pts.length; i++) {
      var A = pts[i], B = pts[(i + 1) % pts.length], dx = B[0] - A[0], dz = B[1] - A[1];
      var o = (Math.abs(dx) >= Math.abs(dz)) ? 'h' : 'v', len = Math.hypot(dx, dz);
      var dev = Math.atan2(o === 'h' ? Math.abs(dz) : Math.abs(dx), o === 'h' ? Math.abs(dx) : Math.abs(dz)) * 180 / Math.PI;
      E.push({ o: o, c: (o === 'h') ? (A[1] + B[1]) / 2 : (A[0] + B[0]) / 2, len: len, dev: dev });
    }
    var G = [];
    for (i = 0; i < E.length; i++) {
      var g = G.length ? G[G.length - 1] : null;
      if (g && g.o === E[i].o) { g.c = (g.c * g.len + E[i].c * E[i].len) / (g.len + E[i].len); g.len += E[i].len; g.dev = Math.max(g.dev, E[i].dev); }
      else G.push({ o: E[i].o, c: E[i].c, len: E[i].len, dev: E[i].dev });
    }
    if (G.length > 1 && G[0].o === G[G.length - 1].o) {
      var gl = G.pop();
      G[0].c = (G[0].c * G[0].len + gl.c * gl.len) / (G[0].len + gl.len); G[0].len += gl.len; G[0].dev = Math.max(G[0].dev, gl.dev);
    }
    /* NOT SQUARE, STILL A HOUSE (v114): an outline with a slanted corner or
       a side the reader broke oddly was refused; the box round it is taken
       now, and the walls inside are read as usual */
    if (G.length < 4 || G.length % 2) {
      var qx0 = 1e9, qx1 = -1e9, qz0 = 1e9, qz1 = -1e9;
      for (i = 0; i < pts.length; i++) { qx0 = Math.min(qx0, pts[i][0]); qx1 = Math.max(qx1, pts[i][0]); qz0 = Math.min(qz0, pts[i][1]); qz1 = Math.max(qz1, pts[i][1]); }
      if (pts.length < 3 || qx1 - qx0 < 1 || qz1 - qz0 < 1) { out.why = 'outline'; out.notes.push('the outline is not made of square corners (' + G.length + ' sides) and too small to take its box'); return out; }
      out.notes.push('the outline is not made of square corners (' + G.length + ' sides) -- the rectangle round it is taken as the house');
      G = [{ o: 'h', c: qz0, len: qx1 - qx0, dev: 0 }, { o: 'v', c: qx1, len: qz1 - qz0, dev: 0 }, { o: 'h', c: qz1, len: qx1 - qx0, dev: 0 }, { o: 'v', c: qx0, len: qz1 - qz0, dev: 0 }];
    }
    /* THE STEP IS MEASURED FROM THE HOUSE'S OWN CORNER, so it is the
       LENGTHS that land on 5 cm. Snapping each wall where it lay put both
       ends of a 9.00 m house a step apart and it came out 8.95. */
    var ox0 = 1e9, oz0 = 1e9, bent = 0;
    for (i = 0; i < G.length; i++) { if (G[i].o === 'v') ox0 = Math.min(ox0, G[i].c); else oz0 = Math.min(oz0, G[i].c); }
    for (i = 0; i < G.length; i++) { G[i].c = _impSnap(G[i].c - (G[i].o === 'v' ? ox0 : oz0)); if (G[i].dev > 1.0) bent++; }
    if (bent) out.notes.push(bent + ' outline side(s) more than 1 deg off square were straightened');
    var V = [];
    for (i = 0; i < G.length; i++) {
      var e1 = G[i], e2 = G[(i + 1) % G.length];
      V.push(e1.o === 'h' ? [e2.c, e1.c] : [e1.c, e2.c]);
    }
    /* CLOCKWISE, LIKE EVERY OUTLINE IN THE LINE (v95): x right, z down. A
       reader may go round either way; the walls' numbering and which side
       is outside both depend on it, so it is settled here */
    var sgA = 0;
    for (i = 0; i < V.length; i++) { var vA = V[i], vB = V[(i + 1) % V.length]; sgA += vA[0] * vB[1] - vB[0] * vA[1]; }
    if (sgA < 0) V.reverse();
    /* ONE RECTANGLE, AS THE PRODUCT BUILDS (v113, Marek: nahral jsem tento
       pudorys a neobjevuje se tlacitko apply). The small ADU's outline came
       with eight corners -- the house, a water heater closet and a storage
       reached from outside, and the covered entry cut in between them -- and
       the fit refused anything but four. A product that builds a rectangle
       takes the rectangle the plan comes nearest to: the whole box when the
       corners cut out of it are under 5 % of it (a hand that did not quite
       close a corner), otherwise the biggest rectangle inside the outline --
       the main body -- when it holds at least 70 % of the house. What lies
       outside it is named here and left out by the usual rules (a wall, a
       window or a door on no wall, a room or a fitting outside the house). */
    var RECTPS = [], RECESS = [], RMOVED = [];
    if (V.length > 4 && opts.rectOnly !== false) {
      var xsR = [], zsR = [], kR, aR, bR, cR, dR;
      for (kR = 0; kR < V.length; kR++) { if (xsR.indexOf(V[kR][0]) < 0) xsR.push(V[kR][0]); if (zsR.indexOf(V[kR][1]) < 0) zsR.push(V[kR][1]); }
      xsR.sort(function (p1, p2) { return p1 - p2; }); zsR.sort(function (p1, p2) { return p1 - p2; });
      var Apol = Math.abs(sgA) / 2, BX0 = xsR[0], BX1 = xsR[xsR.length - 1], BZ0 = zsR[0], BZ1 = zsR[zsR.length - 1];
      var Abox = (BX1 - BX0) * (BZ1 - BZ0), RB = null, how = '';
      if (Abox > 0 && Apol / Abox >= 0.95) { RB = { x0: BX0, x1: BX1, z0: BZ0, z1: BZ1 }; how = 'box'; }
      /* AN L THE PRODUCT BUILDS AS AN L (v203, RANCH thread -- the plan
         library thread's request on plans 1000076006 / 1000076007, Marek:
         "L tvar pudorysu ale se strechou pres cely objekt bez lomeni ... vyklenek
         pod strechou pred garazi"). A product that answers HouseLib.importNotch
         (RANCH) takes one corner out of its rectangle as a covered porch under
         the whole roof. An outline of six square corners with one inside corner
         is then not squared nor cut to its main body: the box round it is the
         house, the corner the box adds is handed over as out.lnotch (box frame),
         its two walls stand as partitions until the product has made the L
         (v203 apply clips them away), and no niche is walled off in it. A
         corner under 1 m either way is squared as before. */
      var LN = null;
      if (V.length === 6) {
        var CLn = _impCore();
        if (CLn && typeof CLn.importNotch === 'function') {
          var nRefL = 0, rIdxL = -1, kL;
          for (kL = 0; kL < 6; kL++) {
            var lA = V[(kL + 5) % 6], lB = V[kL], lC = V[(kL + 1) % 6];
            var crL = (lB[0] - lA[0]) * (lC[1] - lB[1]) - (lB[1] - lA[1]) * (lC[0] - lB[0]);
            if (crL < 0) { nRefL++; rIdxL = kL; }
          }
          if (nRefL === 1) {
            var rvL = V[rIdxL], cxL = (Math.abs(rvL[0] - BX0) < Math.abs(rvL[0] - BX1)) ? BX0 : BX1, czL = (Math.abs(rvL[1] - BZ0) < Math.abs(rvL[1] - BZ1)) ? BZ0 : BZ1;
            /* v208 (RANCH thread: a 30 x 68 ft L with its garage set back
               under the roof, the corner 20 ft wide): the corner the box adds
               is the box corner no vertex of the outline stands on -- the
               nearest box side to the inside corner picked the other side
               when the corner is wider than half the house */
            var bcL = [[BX0, BZ0], [BX1, BZ0], [BX1, BZ1], [BX0, BZ1]], qL, vL2, hitL;
            for (qL = 0; qL < 4; qL++) {
              hitL = false;
              for (vL2 = 0; vL2 < 6; vL2++) if (Math.abs(V[vL2][0] - bcL[qL][0]) < 1e-6 && Math.abs(V[vL2][1] - bcL[qL][1]) < 1e-6) { hitL = true; break; }
              if (!hitL) { cxL = bcL[qL][0]; czL = bcL[qL][1]; break; }
            }
            var lx0 = Math.min(cxL, rvL[0]), lx1 = Math.max(cxL, rvL[0]), lz0 = Math.min(czL, rvL[1]), lz1 = Math.max(czL, rvL[1]);
            if (lx1 - lx0 >= 1.0 && lz1 - lz0 >= 1.0) {
              LN = { x0: _impR(lx0 - BX0), x1: _impR(lx1 - BX0), z0: _impR(lz0 - BZ0), z1: _impR(lz1 - BZ0) };
              RB = { x0: BX0, x1: BX1, z0: BZ0, z1: BZ1 }; how = 'lnotch';
            }
          }
        }
      }
      /* THE LINE MOST OF A SIDE STANDS ON (v187, plan 1000076007 on RANCH,
         Marek: neaplikoval se mezi ostatni). The box took the living room's
         bay, 0.56 m out of the front, for the whole front: the 21 m of front
         wall on one line became partitions 0.55 m inside, the strips between
         them and the box were walled off as niches, and the four windows on
         that wall (the garage, the dining room, both bedrooms) were on no
         wall and left out -- a bedroom with no window, so the plan was not
         for the library. A side of the box goes onto the line most of the
         outline's side stands on, when that line holds 60 % of the side or
         more and lies no more than 0.90 m inside it: the bay is cut off and
         its window goes onto the wall, a recess under 0.45 m is taken out
         to it with its door. */
      var MAJ = {}, MAJN = [];
      function majOut(ho, c) {
        var ks = ho ? ['z0', 'z1'] : ['x0', 'x1'], q, mj, ou;
        for (q = 0; q < 2; q++) {
          mj = MAJ[ks[q]];
          if (!mj) continue;
          ou = (q === 0) ? (RB[ks[q]] - c) : (c - RB[ks[q]]);
          if (ou > 1e-6 && ou <= mj.dep + 0.05) return true;
        }
        return false;
      }
      if (how === 'lnotch') { /* v203: the box, nothing squared */ }
      else if (how === 'box') {
        var SDS = [{ k: 'z0', o: 'h', c: BZ0, inw: 1, L: BX1 - BX0 }, { k: 'z1', o: 'h', c: BZ1, inw: -1, L: BX1 - BX0 },
                   { k: 'x0', o: 'v', c: BX0, inw: 1, L: BZ1 - BZ0 }, { k: 'x1', o: 'v', c: BX1, inw: -1, L: BZ1 - BZ0 }], sM, kM;
        for (sM = 0; sM < 4; sM++) {
          var SM = SDS[sM], byC = {};
          for (kM = 0; kM < V.length; kM++) {
            var mA = V[kM], mB = V[(kM + 1) % V.length], hoM = Math.abs(mB[1] - mA[1]) < 1e-6;
            if ((hoM ? 'h' : 'v') !== SM.o) continue;
            var cM = hoM ? mA[1] : mA[0], inM = (cM - SM.c) * SM.inw;
            if (inM < -1e-6 || inM > 0.90) continue;
            var keyM = String(_impR(cM));
            byC[keyM] = (byC[keyM] || 0) + (hoM ? Math.abs(mB[0] - mA[0]) : Math.abs(mB[1] - mA[1]));
          }
          var bestC = null, bestL = 0, atSide = byC[String(_impR(SM.c))] || 0, cK;
          for (cK in byC) if (byC.hasOwnProperty(cK) && byC[cK] > bestL) { bestL = byC[cK]; bestC = Number(cK); }
          if (bestC != null && Math.abs(bestC - SM.c) > 0.02 && bestL >= 0.6 * SM.L && bestL > atSide) {
            MAJ[SM.k] = { dep: Math.abs(bestC - SM.c) };
            RB[SM.k] = bestC;
            MAJN.push(_impN(bestL) + ' m of the ' + (SM.o === 'h' ? 'long' : 'short') + ' side on one line ' + _impN(Math.abs(bestC - SM.c)) + ' m inside the box, ' + _impN(atSide) + ' m out on the box');
          }
        }
      }
      else {
        var bestA = 0;
        for (aR = 0; aR < xsR.length; aR++) for (bR = aR + 1; bR < xsR.length; bR++) {
          for (cR = 0; cR < zsR.length; cR++) for (dR = cR + 1; dR < zsR.length; dR++) {
            var arR = (xsR[bR] - xsR[aR]) * (zsR[dR] - zsR[cR]);
            if (arR <= bestA) continue;
            var okR = true, iR, jR;
            for (iR = aR; iR < bR && okR; iR++) for (jR = cR; jR < dR && okR; jR++) {
              if (!_impInPoly((xsR[iR] + xsR[iR + 1]) / 2, (zsR[jR] + zsR[jR + 1]) / 2, V)) okR = false;
            }
            if (okR) { bestA = arR; RB = { x0: xsR[aR], x1: xsR[bR], z0: zsR[cR], z1: zsR[dR] }; }
          }
        }
        if (RB && bestA >= 0.7 * Apol) how = 'body';
        /* never refused (v114): with no rectangle holding 70 % of the house,
           the box round it is built, its outer walls on every side */
        else { RB = { x0: BX0, x1: BX1, z0: BZ0, z1: BZ1 }; how = 'boxAll'; }
      }
      /* the rooms a rectangle leaves out, and which of them are outdoors */
      function outOf(RBq) {
        var o = { inN: [], outdoor: [] };
        for (var kq = 0; raw.rooms && kq < raw.rooms.length; kq++) {
          var mq = M(raw.rooms[kq].at), rxq = mq[0] - ox0, rzq = mq[1] - oz0;
          if (rxq >= RBq.x0 && rxq <= RBq.x1 && rzq >= RBq.z0 && rzq <= RBq.z1) continue;
          var nmq = String(raw.rooms[kq].label || raw.rooms[kq].type || 'room');
          if (IMP_OUTDOOR.test(nmq) || IMP_OUTDOOR.test(String(raw.rooms[kq].type || ''))) o.outdoor.push('"' + nmq + '"'); else o.inN.push('"' + nmq + '"');
        }
        return o;
      }
      /* THE NICHES KEPT (v114, Marek: nereflektuji se vyklenky; delici
         pricky by tam byt mohly). The main body left the water heater
         closet and the store beside the covered entry out of the house.
         When the body would leave out a room or a closet (not a patio or a
         porch -- those are outdoors anyway) and the box round the house is
         no more than a fifth bigger than the house, the box is built and
         the niches' walls stand inside it as partitions. */
      var keptN = [];
      if (how === 'body' && Abox > 0 && Apol / Abox >= 0.80) {
        var oB = outOf(RB);
        if (oB.inN.length) { RB = { x0: BX0, x1: BX1, z0: BZ0, z1: BZ1 }; how = 'keep'; keptN = oB.inN; }
      }
      if (RB) {
        var oN = outOf(RB), outN = oN.inN.concat(oN.outdoor);
        if (oN.outdoor.length) out.outdoor = oN.outdoor;
        var wR = _impR(RB.x1 - RB.x0), dRb = _impR(RB.z1 - RB.z0);
        /* THE OUTER WALLS OF A CORNER CUT OUT STAY, AS PARTITIONS (v114,
           Marek: vyklenky dole -- delici pricky by tam byt mohly). When the
           rectangle takes in a recess -- a covered entry between a closet and
           a store -- the walls round the recess were the house's outer walls;
           they now stand inside it, so they become partitions and the closet,
           the entry and the store stay rooms of their own. */
        RECTPS = [];
        if (how !== 'body') {
          for (kR = 0; kR < V.length; kR++) {
            var vA = V[kR], vB = V[(kR + 1) % V.length], hoR = Math.abs(vB[1] - vA[1]) < 1e-6;
            var cR2 = hoR ? vA[1] : vA[0], loR = hoR ? Math.min(vA[0], vB[0]) : Math.min(vA[1], vB[1]), hiR = hoR ? Math.max(vA[0], vB[0]) : Math.max(vA[1], vB[1]);
            /* a corner within 45 cm of the box's side is that side drawn a
               little short -- the room runs on to the wall, not a sliver */
            var onB = hoR ? (Math.abs(cR2 - RB.z0) < 0.45 || Math.abs(cR2 - RB.z1) < 0.45) : (Math.abs(cR2 - RB.x0) < 0.45 || Math.abs(cR2 - RB.x1) < 0.45);
            /* v187: past a side moved onto its main line, within the step -- the bay's face, moved in */
            if (!onB && MAJN.length && majOut(hoR, cR2)) onB = true;
            /* v187: and a face across such a side, cut to the box, is no wall when nothing of it is left
               or it stands within the 0.45 m step that is taken out */
            if (!onB && MAJN.length) {
              var kA = hoR ? ['x0', 'x1'] : ['z0', 'z1'], cl0 = Math.max(loR, RB[kA[0]]), cl1 = Math.min(hiR, RB[kA[1]]);
              if (cl1 - cl0 < 0.4) continue;
              if ((MAJ[kA[0]] && cl1 - RB[kA[0]] < 0.45 + 0.01) || (MAJ[kA[1]] && RB[kA[1]] - cl0 < 0.45 + 0.01)) continue;
              loR = cl0; hiR = cl1;
            }
            /* v144 (plan 1000073628, Marek: v loznici okno chybi -- v puvodnim
               planku jsou pritom posuvne dvere na covered porch): the bedroom's
               outer wall stood 0.44 m short of the rest, the rectangle took it
               out to the box and the room with it -- but its window stayed on
               the old line, on no wall, and was left out. What stands on such a
               side, a window or an outer door, goes out with it */
            if (onB && hiR - loR >= 0.3) {
              var toS = hoR ? ((Math.abs(cR2 - RB.z0) < Math.abs(cR2 - RB.z1)) ? RB.z0 : RB.z1) : ((Math.abs(cR2 - RB.x0) < Math.abs(cR2 - RB.x1)) ? RB.x0 : RB.x1);
              if (Math.abs(toS - cR2) > 0.02) RMOVED.push({ o: hoR ? 'h' : 'v', from: _impR(cR2 - (hoR ? RB.z0 : RB.x0)), to: _impR(toS - (hoR ? RB.z0 : RB.x0)),
                                                         lo: _impR(loR - (hoR ? RB.x0 : RB.z0)), hi: _impR(hiR - (hoR ? RB.x0 : RB.z0)) });
            }
            if (onB || hiR - loR < 0.4) continue;
            RECTPS.push({ o: hoR ? 'h' : 'v', c: _impR(cR2 - (hoR ? RB.z0 : RB.x0)), lo: _impR(loR - (hoR ? RB.x0 : RB.z0)), hi: _impR(hiR - (hoR ? RB.x0 : RB.z0)) });
          }
        }
        /* THE MOUTHS OF THE NICHES (v114): where the box's side runs on no
           wall of the plan, a niche opens to the outside there -- an entry
           under the roof, as a rule. Kept to give the niche its outer door
           and its name once the doors are read. (v203: an L's corner is the
           product's porch, not a niche.) */
        if (how !== 'body' && how !== 'lnotch') {
          var SIDES = [{ o: 'h', c: RB.z0, lo: RB.x0, hi: RB.x1, inw: 1 }, { o: 'v', c: RB.x1, lo: RB.z0, hi: RB.z1, inw: -1 },
                       { o: 'h', c: RB.z1, lo: RB.x0, hi: RB.x1, inw: -1 }, { o: 'v', c: RB.x0, lo: RB.z0, hi: RB.z1, inw: 1 }], sR;
          for (sR = 0; sR < 4; sR++) {
            var SR = SIDES[sR], cov = [];
            for (kR = 0; kR < V.length; kR++) {
              var wA = V[kR], wB = V[(kR + 1) % V.length], hoW = Math.abs(wB[1] - wA[1]) < 1e-6;
              if ((hoW ? 'h' : 'v') !== SR.o) continue;
              if (Math.abs((hoW ? wA[1] : wA[0]) - SR.c) >= 0.45 && !(MAJN.length && majOut(hoW, hoW ? wA[1] : wA[0]))) continue;
              cov.push(hoW ? [Math.min(wA[0], wB[0]), Math.max(wA[0], wB[0])] : [Math.min(wA[1], wB[1]), Math.max(wA[1], wB[1])]);
            }
            cov.sort(function (c1, c2) { return c1[0] - c2[0]; });
            var at0 = SR.lo, gaps = [];
            for (kR = 0; kR < cov.length; kR++) { if (cov[kR][0] - at0 > 0.8) gaps.push([at0, cov[kR][0]]); at0 = Math.max(at0, cov[kR][1]); }
            if (SR.hi - at0 > 0.8) gaps.push([at0, SR.hi]);
            for (kR = 0; kR < gaps.length; kR++) {
              /* how deep: to the first wall of the plan across the gap's middle */
              var mid = (gaps[kR][0] + gaps[kR][1]) / 2, dep = 1e9, jW;
              for (jW = 0; jW < V.length; jW++) {
                var jA = V[jW], jB = V[(jW + 1) % V.length], hoJ = Math.abs(jB[1] - jA[1]) < 1e-6;
                if ((hoJ ? 'h' : 'v') !== SR.o) continue;
                var jl = hoJ ? Math.min(jA[0], jB[0]) : Math.min(jA[1], jB[1]), jh = hoJ ? Math.max(jA[0], jB[0]) : Math.max(jA[1], jB[1]);
                if (mid < jl || mid > jh) continue;
                var dd2 = ((hoJ ? jA[1] : jA[0]) - SR.c) * SR.inw;
                if (dd2 > 0.45 && dd2 < dep) dep = dd2;
              }
              if (dep < 1e8) RECESS.push({ side: sR, o: SR.o, c: SR.c - (SR.o === 'h' ? RB.z0 : RB.x0), inw: SR.inw,
                                           lo: gaps[kR][0] - (SR.o === 'h' ? RB.x0 : RB.z0), hi: gaps[kR][1] - (SR.o === 'h' ? RB.x0 : RB.z0), depth: dep });
            }
          }
        }
        if (how === 'lnotch') {
          out.lnotch = LN;
          out.notes.push('L PLAN (v203): the outline is an L -- the whole ' + _impN(wR) + ' x ' + _impN(dRb) + ' m is the house and its roof, the corner ' +
                         _impN(LN.x1 - LN.x0) + ' x ' + _impN(LN.z1 - LN.z0) + ' m at ' + _impN(LN.x0) + ',' + _impN(LN.z0) + ' goes to the product as its L corner (a covered porch); its ' +
                         RECTPS.length + ' wall(s) stand as partitions until then');
        } else
        out.notes.push('RECT: the outline has ' + V.length + ' corners and this product builds a rectangle -- ' +
                       ((how === 'box') ? ('the corners cut out of it are under 5 % of it, so the whole ' + _impN(wR) + ' x ' + _impN(dRb) + ' m is built')
                        : (how === 'keep') ? ('the main body would leave out ' + keptN.join(', ') + ', so the whole ' + _impN(wR) + ' x ' + _impN(dRb) +
                                              ' m round it is built, ' + _impN(wR * dRb - Apol) + ' m2 more than drawn')
                        : (how === 'boxAll') ? ('no rectangle inside holds 70 % of the house, so the whole ' + _impN(wR) + ' x ' + _impN(dRb) + ' m round it is built, ' + _impN(wR * dRb - Apol) + ' m2 more than drawn')
                        : ('the main body ' + _impN(wR) + ' x ' + _impN(dRb) + ' m is built, ' + _impN(Apol - wR * dRb) + ' m2 outside it is left out' +
                           (outN.length ? (': ' + outN.join(', ')) : ''))) +
                       (MAJN.length ? ('; ON THE MAIN LINE (v187): ' + MAJN.join('; ') + ' -- the side built on that line') : '') +
                       (RECTPS.length ? ('; ' + RECTPS.length + ' outer wall(s) of the corners taken in stand as partitions') : '') +
                       (RMOVED.length ? ('; ' + RMOVED.length + ' side(s) of a step under 0.45 m taken out to the new wall, the rooms with them, and the windows and outer doors on them (v144)') : ''));
        ox0 += RB.x0; oz0 += RB.z0;
        V = [[0, 0], [wR, 0], [wR, dRb], [0, dRb]];
      }
    }
    var Mx = -1e9, Mz = -1e9;
    for (i = 0; i < V.length; i++) { V[i] = [_impR(V[i][0]), _impR(V[i][1])]; Mx = Math.max(Mx, V[i][0]); Mz = Math.max(Mz, V[i][1]); }
    out.outline = V; out.w = _impR(Mx); out.d = _impR(Mz);
    /* the way from the picture's pixels to these metres, kept so the picture
       itself can be laid under the plan where the plan landed (v103) */
    var hx0 = 1e9, hx1 = -1e9, hz0 = 1e9, hz1 = -1e9;
    for (i = 0; i < raw.outline.length; i++) {
      hx0 = Math.min(hx0, raw.outline[i][0]); hx1 = Math.max(hx1, raw.outline[i][0]);
      hz0 = Math.min(hz0, raw.outline[i][1]); hz1 = Math.max(hz1, raw.outline[i][1]);
    }
    out.tf = { s: s, turn: turn, cx: cx, cz: cz, ox0: ox0, oz0: oz0, hx: (hx0 + hx1) / 2, hz: (hz0 + hz1) / 2 };
    function T(p) { var m2 = M(p); return [m2[0] - ox0, m2[1] - oz0]; }
    /* the outline's sides, each from one corner to the next */
    var SD = [];
    for (i = 0; i < V.length; i++) {
      var a1 = V[(i + V.length - 1) % V.length], b1 = V[i];
      var ho = Math.abs(b1[1] - a1[1]) < 1e-6;
      SD.push({ o: ho ? 'h' : 'v', c: ho ? a1[1] : a1[0], lo: ho ? Math.min(a1[0], b1[0]) : Math.min(a1[1], b1[1]),
                hi: ho ? Math.max(a1[0], b1[0]) : Math.max(a1[1], b1[1]), a: a1, b: b1 });
    }
    function inside(x, z) { return _impInPoly(x, z, V); }
    /* A SMALL SPACE WITH NO NAME AND NOTHING IN IT IS A CLOSET (v135, plan
       2BR 34 x 25: a 2.3 m2 space below the WC came in as a bathroom with a
       door of its own). A room the plan gives no name, under 3 m2, with no
       WC, basin, shower, tub, washer, dryer, water heater or counter in it,
       is storage. */
    (function () {
      var PLUMB = /toilet|wc|basin|sink|shower|tub|washer|dryer|water_heater|kitchen|fridge|range/, SMALLN = [], k, f;
      for (k = 0; raw.rooms && k < raw.rooms.length; k++) {
        var rs = raw.rooms[k];
        if (!rs || rs.label || !rs.box || !rs.box[0] || !rs.box[1] || /^(closet|storage|pantry|laundry|utility|wardrobe|walk_in_closet|hallway|hall|entry|mudroom|corridor|wc|stairs)$/.test(String(rs.type || ''))) continue;
        if (/^(patio|porch|deck|balcony|terrace)$/.test(String(rs.type || ''))) continue;
        var q0 = T(rs.box[0]), q1 = T(rs.box[1]), x0 = Math.min(q0[0], q1[0]), x1 = Math.max(q0[0], q1[0]), z0 = Math.min(q0[1], q1[1]), z1 = Math.max(q0[1], q1[1]);
        var ar = (x1 - x0) * (z1 - z0);
        if (!(ar < 3.0)) continue;
        var wet = false;
        for (f = 0; raw.fixtures && f < raw.fixtures.length && !wet; f++) {
          var fx = raw.fixtures[f];
          if (!fx || fx.sug || !PLUMB.test(String(fx.type || '').toLowerCase())) continue;
          var fa = T(fx.a), fb = T(fx.b), fcx = (fa[0] + fb[0]) / 2, fcz = (fa[1] + fb[1]) / 2;
          if (fcx > x0 - 0.05 && fcx < x1 + 0.05 && fcz > z0 - 0.05 && fcz < z1 + 0.05) wet = true;
        }
        if (wet) continue;
        SMALLN.push('a ' + (rs.type || 'room') + ' ' + _impN(ar) + ' m2');
        rs.typeRead = rs.typeRead || rs.type; rs.type = 'storage'; rs.key = IMP_TYPE_KEY.storage;
      }
      if (SMALLN.length) out.notes.push('SMALL: ' + SMALLN.length + ' space(s) with no name, under 3 m2 and with nothing plumbed in them became closets (v135): ' + SMALLN.join(', '));
    })();
    /* A CABINET, NOT A CLOSET (v139, Marek: od spodni hrany jakoby trci kus
       pricky, ktera konci v obyvaci casti). The plan draws a pantry cabinet,
       PAN, at the end of the kitchen run; the reader gave it the box of a
       pantry open to the living room, the open side became a wall, and that
       wall ran on in the line of the water heater closet's wall below, one
       wall standing 0.75 m out into the living room with nothing at its
       top. A store of a closet's kind -- a pantry, a closet, a store, a
       wardrobe -- no more than 0.80 m either way, with no door of its own,
       whose box lies inside the box of a room the reader named, is a tall
       cabinet standing in that room: no closet, and no wall and no room
       line on its open side. */
    (function () {
      var CABT = /^(pantry|closet|storage|wardrobe)$/, NOHOST = /^(closet|storage|pantry|laundry|utility|wardrobe|walk_in_closet|patio|porch|deck|balcony|terrace)$/;
      var WRHOST = /^(bedroom|kids|office|hallway|entry|mudroom|guest)$/;
      var gone = {}, said = [], k, j, nGone = 0;
      function bx(r) { var a = T(r.box[0]), b = T(r.box[1]); return { x0: Math.min(a[0], b[0]), x1: Math.max(a[0], b[0]), z0: Math.min(a[1], b[1]), z1: Math.max(a[1], b[1]) }; }
      function nm(t) { return String(t || '').toUpperCase().replace(/[^A-Z0-9]/g, ''); }
      var CABN = [];
      for (k = 0; raw.rooms && k < raw.rooms.length; k++) {
        var rc = raw.rooms[k];
        if (!rc || !rc.box || !rc.box[0] || !rc.box[1] || !CABT.test(String(rc.type || ''))) continue;
        var B = bx(rc);
        /* v163: up to 1.00 m along and 0.80 m deep -- the cabinet under the
           tub of plan 1000073631 was read 0.84 x 0.49 m */
        if (Math.max(B.x1 - B.x0, B.z1 - B.z0) > 1.00 || Math.min(B.x1 - B.x0, B.z1 - B.z0) > 0.80) continue;
        var names = [nm(rc.label), nm(rc.type)].filter(function (t) { return !!t; }), own = false, host = null;
        for (j = 0; raw.doors && j < raw.doors.length && !own; j++) {
          var dr = raw.doors[j].rooms || [], jj, hit = false;
          /* v142: an opening is its open front, not a door of its own */
          if (/^(opening|pass|passage)$/.test(String(raw.doors[j].kind || ''))) continue;
          for (jj = 0; jj < dr.length; jj++) if (names.indexOf(nm(dr[jj])) >= 0) hit = true;
          if (!hit) continue;
          /* v163: BY ITS PLACE, NOT ONLY ITS NAME. Every closet of that plan
             is called "closet", so the door of the hall's linen closet was
             taken for the cabinet's own and the cabinet stayed a closet nobody
             could get into. A door naming it is its own when it stands at its
             box. */
          var atD = raw.doors[j].at ? T(raw.doors[j].at) : null;
          if (!atD || (atD[0] >= B.x0 - 0.40 && atD[0] <= B.x1 + 0.40 && atD[1] >= B.z0 - 0.40 && atD[1] <= B.z1 + 0.40)) own = true;
        }
        if (own) continue;
        for (j = 0; raw.rooms && j < raw.rooms.length && !host; j++) {
          var rh = raw.rooms[j];
          if (j === k || !rh || !rh.box || !rh.box[0] || !rh.box[1] || NOHOST.test(String(rh.type || ''))) continue;
          var Hb = bx(rh);
          if ((Hb.x1 - Hb.x0) * (Hb.z1 - Hb.z0) < 2 * (B.x1 - B.x0) * (B.z1 - B.z0)) continue;
          /* v163: a wall's thickness over -- the cabinet's box ran to the far
             face of the wall it stands against */
          if (B.x0 >= Hb.x0 - 0.20 && B.x1 <= Hb.x1 + 0.20 && B.z0 >= Hb.z0 - 0.20 && B.z1 <= Hb.z1 + 0.20) host = rh;
        }
        if (!host) continue;
        gone[k] = 1; nGone++;
        CABN.push(names);
        /* v180: a closet's or a wardrobe's cabinet in a bedroom, a hall or
           an office is the wardrobe cabinet that stands there -- built and
           billed (a pantry's is the kitchen's and stays out) */
        var wrC = /^(closet|wardrobe|storage)$/.test(String(rc.type || '')) && WRHOST.test(String(host.type || ''));
        if (wrC) { raw.fixtures = raw.fixtures || []; raw.fixtures.push({ type: 'wardrobe', a: rc.box[0], b: rc.box[1], label: '' }); }
        said.push('"' + (rc.label || rc.type) + '" ' + _impN(B.x1 - B.x0) + ' x ' + _impN(B.z1 - B.z0) + ' m in "' + (host.label || host.type || 'room') + '"' + (wrC ? ' -- a wardrobe cabinet there' : ''));
      }
      /* A BUILT-IN WARDROBE, NOT A CLOSET (v180, plan 1000075604, Marek:
         dodelat do fitouts i vetsi satni skrine, ktere v projektech byvaji a
         my je nestavime, a zaroven je vykazovat). The bedroom of that plan has
         a wardrobe along its top wall and another down its side, drawn
         hatched with no wall in front: the reader gave each a closet's box,
         and the audit found "two rooms in one: bedroom + closet + closet".
         A closet, a wardrobe or a store no deeper than 0.80 m and longer than
         1.00 m, with no door of its own, whose box lies inside the box of a
         bedroom, a kids' room, an office or a hall, is a run of wardrobe
         cabinets along that wall: as many 1.20 / 1.00 / 0.80 m cabinets as
         fill the run best, centred on it -- no closet, no wall, each cabinet
         a piece the house bills (FMA00004). */
      var RUNS = [], RUNC = [];
      for (k = 0; raw.rooms && k < raw.rooms.length; k++) {
        var rw = raw.rooms[k];
        if (gone[k] || !rw || !rw.box || !rw.box[0] || !rw.box[1] || !/^(closet|wardrobe|storage)$/.test(String(rw.type || ''))) continue;
        var Bw = bx(rw), lnW = Math.max(Bw.x1 - Bw.x0, Bw.z1 - Bw.z0), dpW = Math.min(Bw.x1 - Bw.x0, Bw.z1 - Bw.z0);
        if (lnW <= 1.00 || dpW > 0.80 || lnW > 8) continue;
        var namesW = [nm(rw.label), nm(rw.type)].filter(function (t) { return !!t; }), ownW = false, hostW = null;
        for (j = 0; raw.doors && j < raw.doors.length && !ownW; j++) {
          var drW = raw.doors[j].rooms || [], jw, hitW = false;
          if (/^(opening|pass|passage)$/.test(String(raw.doors[j].kind || ''))) continue;
          for (jw = 0; jw < drW.length; jw++) if (namesW.indexOf(nm(drW[jw])) >= 0) hitW = true;
          if (!hitW) continue;
          var atW = raw.doors[j].at ? T(raw.doors[j].at) : null;
          if (!atW || (atW[0] >= Bw.x0 - 0.40 && atW[0] <= Bw.x1 + 0.40 && atW[1] >= Bw.z0 - 0.40 && atW[1] <= Bw.z1 + 0.40)) ownW = true;
        }
        if (ownW) continue;
        for (j = 0; raw.rooms && j < raw.rooms.length && !hostW; j++) {
          var rhW = raw.rooms[j];
          if (j === k || gone[j] || !rhW || !rhW.box || !rhW.box[0] || !rhW.box[1] || !WRHOST.test(String(rhW.type || ''))) continue;
          var HbW = bx(rhW);
          if ((HbW.x1 - HbW.x0) * (HbW.z1 - HbW.z0) < 2 * lnW * dpW) continue;
          if (Bw.x0 >= HbW.x0 - 0.20 && Bw.x1 <= HbW.x1 + 0.20 && Bw.z0 >= HbW.z0 - 0.20 && Bw.z1 <= HbW.z1 + 0.20) hostW = rhW;
        }
        if (!hostW) continue;
        /* in the picture's pixels: the run's axis, its ends, its depth and
           the side its back stands on (the host's edge it is nearer) */
        var pa0 = rw.box[0], pb0 = rw.box[1], ha = hostW.box[0], hb = hostW.box[1], alX = Math.abs(pb0[0] - pa0[0]) >= Math.abs(pb0[1] - pa0[1]);
        var ax = alX ? 0 : 1, cx9 = alX ? 1 : 0;
        var lo9 = Math.min(pa0[ax], pb0[ax]), hi9 = Math.max(pa0[ax], pb0[ax]), c0 = Math.min(pa0[cx9], pb0[cx9]), c1 = Math.max(pa0[cx9], pb0[cx9]);
        var hc0 = Math.min(ha[cx9], hb[cx9]), hc1 = Math.max(ha[cx9], hb[cx9]);
        RUNC.push({ k: k, rw: rw, names: namesW, host: hostW, lnW: lnW, dpW: dpW, alX: alX, lo: lo9, hi: hi9, c0: c0, c1: c1, ppm: (hi9 - lo9) / lnW,
                    back: (Math.abs(c0 - hc0) <= Math.abs(hc1 - c1)) ? -1 : 1 });
      }
      /* the longer run first: it keeps the corner where two runs meet, and
         the other starts past its cabinets (0.60 deep, a hand's gap) */
      RUNC.sort(function (x9, y9) { return y9.lnW - x9.lnW; });
      var FOOT = [];
      for (var rq = 0; rq < RUNC.length; rq++) {
        var RC = RUNC[rq], ppm = RC.ppm, wl = (RC.back < 0) ? RC.c0 : RC.c1, fc0 = Math.min(wl, wl - RC.back * 0.62 * ppm), fc1 = Math.max(wl, wl - RC.back * 0.62 * ppm);
        var lo = RC.lo, hi = RC.hi, ft;
        for (ft = 0; ft < FOOT.length; ft++) {
          var FP = FOOT[ft];
          if (FP.alX === RC.alX) continue;
          /* the earlier run's cabinets, in this run's axes */
          var fAlo = RC.alX ? FP.x0 : FP.y0, fAhi = RC.alX ? FP.x1 : FP.y1, fClo = RC.alX ? FP.y0 : FP.x0, fChi = RC.alX ? FP.y1 : FP.x1;
          if (fChi <= fc0 || fClo >= fc1) continue;
          if (fAlo <= lo + 0.30 * ppm && fAhi > lo) lo = Math.max(lo, fAhi + 0.02 * ppm);
          else if (fAhi >= hi - 0.30 * ppm && fAlo < hi) hi = Math.min(hi, fAlo - 0.02 * ppm);
        }
        /* NO CABINET IN FRONT OF A WINDOW (v182, Marek: zadny nabytek nemuze
           zastinovat okna -- to je zakladni pravidlo). The plan drew the
           bedroom's wardrobe along the wall with the window in it; the run
           leaves the window and a hand either side free and fills what is
           left on each side. */
        var GAPS = [], wq;
        for (wq = 0; raw.windows && wq < raw.windows.length; wq++) {
          var wn = raw.windows[wq];
          if (!wn || !wn.at || !(wn.w > 0)) continue;
          var wAl = RC.alX ? wn.at[0] : wn.at[1], wCr = RC.alX ? wn.at[1] : wn.at[0];
          if (Math.abs(wCr - wl) > 0.60 * ppm) continue;
          if (wAl + wn.w / 2 < lo || wAl - wn.w / 2 > hi) continue;
          GAPS.push([wAl - wn.w / 2 - 0.10 * ppm, wAl + wn.w / 2 + 0.10 * ppm]);
        }
        GAPS.sort(function (x9, y9) { return x9[0] - y9[0]; });
        var SEGW = [], cur = lo;
        for (wq = 0; wq < GAPS.length; wq++) {
          if (GAPS[wq][0] > cur) SEGW.push([cur, Math.min(hi, GAPS[wq][0])]);
          cur = Math.max(cur, GAPS[wq][1]);
        }
        if (cur < hi) SEGW.push([cur, hi]);
        var ALLC = [], sg9;
        for (sg9 = 0; sg9 < SEGW.length; sg9++) {
          var sLo = SEGW[sg9][0], sHi = SEGW[sg9][1], useM = (sHi - sLo) / ppm;
          /* the cabinets that fill it best (0.20 m units: 6, 5, 4), the
             fewest of them on a tie */
          var CAP = Math.floor((useM - 0.02) / 0.2 + 1e-6), BEST = [], bestS = 0, u, v, DPW = [[]];
          for (u = 1; u <= CAP; u++) {
            DPW[u] = null;
            [6, 5, 4].forEach(function (sz) {
              if (u - sz >= 0 && DPW[u - sz] && (!DPW[u] || DPW[u - sz].length + 1 < DPW[u].length)) DPW[u] = DPW[u - sz].concat([sz]);
            });
          }
          for (u = CAP; u >= 4; u--) if (DPW[u]) { BEST = DPW[u]; bestS = u; break; }
          if (!BEST.length) continue;
          /* against a window gap the cabinets stand at the far end, the
             gap stays whole; otherwise centred */
          var at9 = (GAPS.length && sg9 > 0 && sg9 === SEGW.length - 1) ? (sHi - bestS * 0.2 * ppm) : ((GAPS.length && sg9 === 0) ? sLo : (sLo + (useM - bestS * 0.2) / 2 * ppm)), g0 = at9;
          if (GAPS.length && sg9 > 0 && sg9 < SEGW.length - 1) at9 = g0 = sLo + (useM - bestS * 0.2) / 2 * ppm;
          raw.fixtures = raw.fixtures || [];
          BEST.sort(function (x9, y9) { return y9 - x9; });
          for (v = 0; v < BEST.length; v++) {
            var e9 = at9 + BEST[v] * 0.2 * ppm;
            raw.fixtures.push(RC.alX ? { type: 'wardrobe', a: [at9, RC.c0], b: [e9, RC.c1], label: '' } : { type: 'wardrobe', a: [RC.c0, at9], b: [RC.c1, e9], label: '' });
            at9 = e9;
          }
          FOOT.push(RC.alX ? { alX: true, x0: g0, x1: at9, y0: fc0, y1: fc1 } : { alX: false, x0: fc0, x1: fc1, y0: g0, y1: at9 });
          ALLC = ALLC.concat(BEST);
        }
        /* the closet goes even where no cabinet fits: it was furniture */
        gone[RC.k] = 1; nGone++;
        CABN.push(RC.names);
        RUNS.push('"' + (RC.rw.label || RC.rw.type) + '" ' + _impN(RC.lnW) + ' x ' + _impN(RC.dpW) + ' m in "' + (RC.host.label || RC.host.type || 'room') + '" -> ' +
                  (ALLC.length ? (ALLC.length + ' cabinet(s) ' + ALLC.map(function (x9) { return _impN(x9 * 0.2); }).join(' + ') + ' m') : 'no cabinet') +
                  (GAPS.length ? (', ' + GAPS.length + ' window(s) left free') : '') +
                  ((hi - lo) / ppm < RC.lnW - 0.05 ? (' (' + _impN(RC.lnW - (hi - lo) / ppm) + ' m left to the run across its corner)') : ''));
      }
      /* A LAUNDRY CORNER, NOT A CLOSET (v146, plan 1000073629: STACKED
         LAUNDRY is the washer and dryer standing one on the other in the
         corner of the bathroom, no wall round them -- the reader gave the
         corner a laundry's box, it became a closet with no wall, and the
         audit found "two rooms in one: bathroom + closet"). A laundry or a
         utility store no more than 1.25 m either way, with no door of its
         own, whose box lies inside the box of a room the reader named and
         holds a washer or a dryer the reader drew, is that room's corner:
         the washer stands in the room, no closet and no wall. */
      var NOOKN = [];
      for (k = 0; raw.rooms && k < raw.rooms.length; k++) {
        var rn = raw.rooms[k];
        if (gone[k] || !rn || !rn.box || !rn.box[0] || !rn.box[1] || !/^(laundry|utility)$/.test(String(rn.type || ''))) continue;
        var Bn = bx(rn);
        if (Bn.x1 - Bn.x0 > 1.25 || Bn.z1 - Bn.z0 > 1.25) continue;
        var namesN = [nm(rn.label), nm(rn.type)].filter(function (t) { return !!t; }), ownN = false, hostN = null, wash = false;
        for (j = 0; raw.doors && j < raw.doors.length && !ownN; j++) {
          var drN = raw.doors[j].rooms || [], jn;
          for (jn = 0; jn < drN.length; jn++) if (namesN.indexOf(nm(drN[jn])) >= 0) ownN = true;
        }
        if (ownN) continue;
        for (j = 0; raw.fixtures && j < raw.fixtures.length && !wash; j++) {
          var fxN = raw.fixtures[j];
          if (!fxN || fxN.sug || !fxN.a || !fxN.b || !/^(washer|dryer)$/.test(String(fxN.type || ''))) continue;
          var fa = T(fxN.a), fb = T(fxN.b), fcx = (fa[0] + fb[0]) / 2, fcz = (fa[1] + fb[1]) / 2;
          if (fcx >= Bn.x0 - 0.10 && fcx <= Bn.x1 + 0.10 && fcz >= Bn.z0 - 0.10 && fcz <= Bn.z1 + 0.10) wash = true;
        }
        if (!wash) continue;
        for (j = 0; raw.rooms && j < raw.rooms.length && !hostN; j++) {
          var rhN = raw.rooms[j];
          if (j === k || gone[j] || !rhN || !rhN.box || !rhN.box[0] || !rhN.box[1] || NOHOST.test(String(rhN.type || ''))) continue;
          var HbN = bx(rhN);
          if ((HbN.x1 - HbN.x0) * (HbN.z1 - HbN.z0) < 2 * (Bn.x1 - Bn.x0) * (Bn.z1 - Bn.z0)) continue;
          if (Bn.x0 >= HbN.x0 - 0.10 && Bn.x1 <= HbN.x1 + 0.10 && Bn.z0 >= HbN.z0 - 0.10 && Bn.z1 <= HbN.z1 + 0.10) hostN = rhN;
        }
        if (!hostN) continue;
        gone[k] = 1; nGone++;
        CABN.push(namesN);
        NOOKN.push('"' + (rn.label || rn.type) + '" ' + _impN(Bn.x1 - Bn.x0) + ' x ' + _impN(Bn.z1 - Bn.z0) + ' m in "' + (hostN.label || hostN.type || 'room') + '"');
      }
      if (!nGone) return;
      var R2 = {}, key, nOS = 0;
      for (key in raw) if (Object.prototype.hasOwnProperty.call(raw, key)) R2[key] = raw[key];
      R2.rooms = raw.rooms.filter(function (r, i9) { return !gone[i9]; });
      R2.open_sides = (raw.open_sides || []).filter(function (os) {
        var rr = os.rooms || [], i8, i7;
        for (i8 = 0; i8 < rr.length; i8++) for (i7 = 0; i7 < CABN.length; i7++) if (CABN[i7].indexOf(nm(rr[i8])) >= 0) { nOS++; return false; }
        return true;
      });
      raw = R2;
      if (said.length) out.notes.push('CABINET: ' + said.length + ' store(s) of a cabinet\'s size inside a room, with no door of their own, are a cabinet standing in that room -- no closet, no wall' +
                     (nOS ? (', and ' + nOS + ' open side(s) to them left open') : '') + ' (v139): ' + said.join(', '));
      if (RUNS.length) out.notes.push('WARDROBE RUN (v180): ' + RUNS.length + ' built-in wardrobe(s) read as a closet with no wall in front are wardrobe cabinets along the wall -- no closet, no wall: ' + RUNS.join(', '));
      if (NOOKN.length) out.notes.push('NOOK: ' + NOOKN.length + ' laundry corner(s) with the washer drawn in them, no door and no wall of their own, are a corner of the room round them -- the washer stands in that room, no closet (v146): ' + NOOKN.join(', '));
    })();

    /* THE PARTITIONS: square, on the step, ends taken to what they nearly
       meet, pieces of one line made one, anything outside left out */
    var PS = [];
    /* SLANTED WALLS ARE DRAWN, NOT BUILT (v114, Marek: pricky 45 stupnu u
       vchodu do koupelny -- alespon pri importu kreslit, i kdyz je v config
       nemame). A wall more than 20 deg off both directions was squared to
       whichever it was nearer and stood as a stray straight wall; it is kept
       aside now, drawn in the preview and named in the log, and the picture
       under the plan shows it after APPLY. */
    out.diag = [];
    for (i = 0; i < RECTPS.length; i++) PS.push(RECTPS[i]);
    for (i = 0; raw.partitions && i < raw.partitions.length; i++) {
      var pa = T(raw.partitions[i].a), pb = T(raw.partitions[i].b);
      var dgA = Math.atan2(Math.abs(pb[1] - pa[1]), Math.abs(pb[0] - pa[0])) * 180 / Math.PI, dgL = Math.hypot(pb[0] - pa[0], pb[1] - pa[1]);
      if (dgA > 20 && dgA < 70) {
        if (dgL >= 0.25) out.diag.push({ a: [_impR(pa[0]), _impR(pa[1])], b: [_impR(pb[0]), _impR(pb[1])], len: _impR(dgL), ang: Math.round(dgA) });
        continue;
      }
      var po = (Math.abs(pb[0] - pa[0]) >= Math.abs(pb[1] - pa[1])) ? 'h' : 'v';
      var pc = _impSnap(po === 'h' ? (pa[1] + pb[1]) / 2 : (pa[0] + pb[0]) / 2);
      var lo = po === 'h' ? Math.min(pa[0], pb[0]) : Math.min(pa[1], pb[1]);
      var hi = po === 'h' ? Math.max(pa[0], pb[0]) : Math.max(pa[1], pb[1]);
      PS.push({ o: po, c: pc, lo: lo, hi: hi, px: !!raw.partitions[i].px });
    }
    /* A PARTITION IN LINE WITH A WALL STAYS IN LINE: the outline and the
       partitions are snapped separately, and a line that is one line on the
       drawing could land on two neighbouring steps */
    for (i = 0; i < PS.length; i++) {
      for (q = 0; q < SD.length; q++) {
        if (SD[q].o === PS[i].o && Math.abs(SD[q].c - PS[i].c) < 0.08) {
          PS[i].c = SD[q].c;
          /* ON the outer wall, along most of its length, it IS the outer
             wall read twice (v100) -- not a partition */
          var ovl = Math.min(PS[i].hi, SD[q].hi) - Math.max(PS[i].lo, SD[q].lo);
          if (ovl > 0.5 * (PS[i].hi - PS[i].lo)) PS[i].onShell = true;
          break;
        }
      }
    }
    function snapEnd(p, v) {
      /* the faces and lines a partition end can reach: the inner face of an
         outline side across its path, or another partition's line */
      var best = v, bd = 0.35, k;
      for (k = 0; k < SD.length; k++) {
        if (SD[k].o === p.o) continue;
        if (p.c < SD[k].lo - 0.01 || p.c > SD[k].hi + 0.01) continue;
        /* the face that is inside the house, whichever side that is */
        var inPlus = (SD[k].o === 'h') ? inside(p.c, SD[k].c + 0.05) : inside(SD[k].c + 0.05, p.c);
        var ff = inPlus ? SD[k].c + WALL : SD[k].c - WALL;
        var dd = Math.min(Math.abs(v - ff), Math.abs(v - SD[k].c));
        if (dd < bd) { bd = dd; best = ff; }
      }
      for (k = 0; k < PS.length; k++) {
        var q2 = PS[k];
        if (q2 === p || q2.o === p.o) continue;
        if (p.c < q2.lo - 0.35 || p.c > q2.hi + 0.35) continue;
        var dq = Math.abs(v - q2.c);
        if (dq < bd) { bd = dq; best = q2.c; }
      }
      return best;
    }
    for (i = 0; i < PS.length; i++) { PS[i].lo = _impSnap(snapEnd(PS[i], PS[i].lo)); PS[i].hi = _impSnap(snapEnd(PS[i], PS[i].hi)); }
    var MP = [], SHORT = [];
    for (i = 0; i < PS.length; i++) {
      var p1 = PS[i], merged = false;
      if (p1.onShell) { out.notes.push('a partition lying on an outer wall was left out'); continue; }
      /* one note for all of them (v104): a stub whose two ends snapped onto
         the same line is 0.00 m, and four of them were four lines saying so */
      if (p1.hi - p1.lo < 0.4) { SHORT.push(_impN(p1.hi - p1.lo)); continue; }
      var midx = p1.o === 'h' ? (p1.lo + p1.hi) / 2 : p1.c, midz = p1.o === 'h' ? p1.c : (p1.lo + p1.hi) / 2;
      if (!inside(midx, midz)) { out.notes.push('a partition outside the house was left out'); continue; }
      for (q = 0; q < MP.length; q++) {
        if (MP[q].o === p1.o && Math.abs(MP[q].c - p1.c) <= 0.06 && p1.lo <= MP[q].hi + 0.1 && p1.hi >= MP[q].lo - 0.1) {
          MP[q].lo = Math.min(MP[q].lo, p1.lo); MP[q].hi = Math.max(MP[q].hi, p1.hi); if (p1.px) MP[q].px = true; merged = true; break;
        }
      }
      if (!merged) MP.push({ o: p1.o, c: p1.c, lo: p1.lo, hi: p1.hi, px: !!p1.px });
    }
    if (SHORT.length) out.notes.push(SHORT.length + ' partition(s) under 0.40 m long were left out (' + SHORT.join(', ') + ' m)');
    /* A LINE THROUGH THE MIDDLE OF A ROOM IS NOT A WALL (v105). Opus drew the
       edges of the shower and the tub in the right-hand bathroom as two
       partitions, and one bathroom became four rooms (Marek: Opus vygeneroval
       o dost vice mistnosti). Inside the box the reader gives a bathroom, a
       WC, a kitchen or a closet, a line more than 0.30 m from both sides of
       the box and no longer than it is a fixture's edge and goes -- unless a
       door stands in it, or a room's name is written on both sides of it,
       which is a real wall the box was drawn too big for. */
    var FIXR = { bathroom: 1, wc: 1, kitchen: 1, laundry: 1, closet: 1, utility: 1, pantry: 1, storage: 1, wardrobe: 1, walk_in_closet: 1 };
    var RBX = [], LPT = [], DPT = [], fixTxt = [];
    for (i = 0; raw.rooms && i < raw.rooms.length; i++) {
      var rr2 = raw.rooms[i];
      if (rr2 && rr2.at) LPT.push(T(rr2.at));
      if (!rr2 || !rr2.box || !rr2.box[0] || !rr2.box[1] || !FIXR[rr2.type]) continue;
      var fb0 = T(rr2.box[0]), fb1 = T(rr2.box[1]);
      RBX.push({ x0: Math.min(fb0[0], fb1[0]), x1: Math.max(fb0[0], fb1[0]), z0: Math.min(fb0[1], fb1[1]), z1: Math.max(fb0[1], fb1[1]),
                 name: String(rr2.label || rr2.type) });
    }
    for (i = 0; raw.doors && i < raw.doors.length; i++) DPT.push(T(raw.doors[i].at));
    MP = MP.filter(function (m) {
      /* a wall read off the picture is drawn thick: never a fixture's edge (v115) */
      if (m.px) return true;
      for (var b = 0; b < RBX.length; b++) {
        var B = RBX[b], vv = (m.o === 'v');
        var cLo = vv ? B.x0 : B.z0, cHi = vv ? B.x1 : B.z1, aLo = vv ? B.z0 : B.x0, aHi = vv ? B.z1 : B.x1, k, door = false, sA = false, sB = false;
        if (!(m.c > cLo + 0.30 && m.c < cHi - 0.30)) continue;
        if (m.lo < aLo - 0.25 || m.hi > aHi + 0.25) continue;
        for (k = 0; k < DPT.length; k++) {
          var pe = Math.abs((vv ? DPT[k][0] : DPT[k][1]) - m.c), al = vv ? DPT[k][1] : DPT[k][0];
          if (pe < 0.30 && al >= m.lo - 0.1 && al <= m.hi + 0.1) { door = true; break; }
        }
        if (door) continue;
        for (k = 0; k < LPT.length; k++) {
          var lx = LPT[k][0], lz = LPT[k][1];
          if (lx < B.x0 - 0.05 || lx > B.x1 + 0.05 || lz < B.z0 - 0.05 || lz > B.z1 + 0.05) continue;
          var cc = vv ? lx : lz;
          if (cc < m.c - 0.05) sA = true; else if (cc > m.c + 0.05) sB = true;
        }
        if (sA && sB) continue;
        fixTxt.push('"' + B.name + '" ' + m.o + ' ' + (vv ? 'x' : 'z') + _impN(m.c));
        return false;
      }
      return true;
    });
    if (fixTxt.length) out.notes.push(fixTxt.length + ' line(s) through the middle of a room were left out as the edge of a fixture, not a wall: ' + fixTxt.join(', '));
    /* THE PLAN TIDIED (v112, Marek: pokud jsou rovnobezne pricky vyosene do
       xx cm, udelej je jako soubezne -- a dalsi podobna pravidla, ktera budou
       mit za nasledek logicke zjednoduseni pudorysu; muze byt i hand draft).
       A drawing -- a hand sketch above all -- puts one wall a few centimetres
       off its own line on the far side of a door, and a room line a hand's
       breadth beside the wall it belongs in; each such slip made a sliver of
       a room with a + of its own. The rules, in this order:
         ALIGN   lines of one direction less than ALIGN_TOL apart, end to end
                 within 2.50 m or lying side by side, are ONE line -- placed
                 where the walls among them say (a wall counts three times a
                 room line), on the 5 cm step;
         CLIP    a room line lying along a wall is cut off it: a line is only
                 where there is no wall;
         CLOSE   two walls in one line with a gap under 0.50 m and no door
                 read in it are one wall -- no door fits a gap that narrow;
       and the ends of every line are then taken again to the line they
       nearly meet. Each rule says in the log what it did. */
    var ALIGN_TOL = 0.15;
    function alignLines(withSep) {
      var par = [], n = 0, a, b;
      for (a = 0; a < MP.length; a++) par.push(a);
      function fd(x) { while (par[x] !== x) { par[x] = par[par[x]]; x = par[x]; } return x; }
      for (a = 0; a < MP.length; a++) {
        for (b = a + 1; b < MP.length; b++) {
          var A = MP[a], B = MP[b];
          if (A.o !== B.o || (!withSep && (A.sep || B.sep))) continue;
          var dc = Math.abs(A.c - B.c);
          if (dc < 0.001 || dc > ALIGN_TOL + 1e-6) continue;
          var gap = Math.max(A.lo, B.lo) - Math.min(A.hi, B.hi);
          if (gap > 2.5) continue;
          par[fd(b)] = fd(a);
        }
      }
      var G = {}, k;
      for (a = 0; a < MP.length; a++) (G[fd(a)] = G[fd(a)] || []).push(MP[a]);
      for (k in G) {
        if (!G.hasOwnProperty(k) || G[k].length < 2) continue;
        var L = G[k], sw = 0, sc = 0, j, cmin = 1e9, cmax = -1e9;
        for (j = 0; j < L.length; j++) {
          var wgt = (L[j].hi - L[j].lo) * (L[j].sep ? 1 : 3);
          sw += wgt; sc += wgt * L[j].c; cmin = Math.min(cmin, L[j].c); cmax = Math.max(cmax, L[j].c);
        }
        if (cmax - cmin < 0.001) continue;
        var cN = _impSnap(sc / sw);
        for (j = 0; j < L.length; j++) if (Math.abs(L[j].c - cN) > 0.001) { L[j].c = cN; n++; }
      }
      return n;
    }
    function clipLines() {
      var keep = [], n = 0, a, b;
      for (a = 0; a < MP.length; a++) {
        var S0 = MP[a];
        if (!S0.sep) { keep.push(S0); continue; }
        var free = [[S0.lo, S0.hi]];
        for (b = 0; b < MP.length; b++) {
          var W0 = MP[b];
          if (W0.sep || W0.o !== S0.o || Math.abs(W0.c - S0.c) > 0.02) continue;
          var nf = [], f;
          for (f = 0; f < free.length; f++) {
            var fa = free[f][0], fb = free[f][1];
            if (W0.hi <= fa + 0.02 || W0.lo >= fb - 0.02) { nf.push([fa, fb]); continue; }
            if (W0.lo > fa) nf.push([fa, W0.lo]);
            if (W0.hi < fb) nf.push([W0.hi, fb]);
          }
          free = nf;
        }
        var tot = 0, f2;
        for (f2 = 0; f2 < free.length; f2++) tot += free[f2][1] - free[f2][0];
        if (Math.abs(tot - (S0.hi - S0.lo)) < 0.01) { keep.push(S0); continue; }
        n++;
        for (f2 = 0; f2 < free.length; f2++) if (free[f2][1] - free[f2][0] >= 0.3) keep.push({ o: S0.o, c: S0.c, lo: free[f2][0], hi: free[f2][1], sep: true, name: S0.name });
      }
      MP = keep;
      return n;
    }
    function closeGaps() {
      var n = 0, again = true, a, b;
      while (again) {
        again = false;
        for (a = 0; a < MP.length && !again; a++) {
          for (b = 0; b < MP.length; b++) {
            var A = MP[a], B = MP[b];
            if (a === b || A.sep || B.sep || A.o !== B.o || Math.abs(A.c - B.c) > 0.02) continue;
            var g = B.lo - A.hi;
            if (!(g > 0.001 && g < 0.5)) continue;
            var dr = false, k;
            for (k = 0; k < DPT.length; k++) {
              var al = (A.o === 'h') ? DPT[k][0] : DPT[k][1], pe = Math.abs(((A.o === 'h') ? DPT[k][1] : DPT[k][0]) - A.c);
              if (pe < 0.35 && al > A.hi - 0.1 && al < B.lo + 0.1) { dr = true; break; }
            }
            if (dr) continue;
            A.hi = Math.max(A.hi, B.hi); MP.splice(b, 1); n++; again = true; break;
          }
        }
      }
      return n;
    }
    if (out.diag.length) out.notes.push('DIAG: ' + out.diag.length + ' slanted wall(s), built as walls when the plan is written (v125): ' +
                                         out.diag.map(function (g) { return _impN(g.len) + ' m at ' + g.ang + ' deg'; }).join(', '));
    var nAl0 = alignLines(false);
    if (nAl0) { mergeMP(); out.notes.push('TIDY: ' + nAl0 + ' wall(s) less than ' + Math.round(ALIGN_TOL * 100) + ' cm off the line of another were put on it'); }
    /* STRIP (v114): a wall with no door that runs beside another wall, or
       beside an outer wall, less than 38 cm from it along most of its length
       makes a strip no one walks into -- a shelf, a counter or a line of the
       drawing, not a wall (a linen closet 45 cm deep is a closet, and stays). The shorter of the two goes; the store of the
       small ADU was cut into a 30 cm strip named KITCHEN and a closet. */
    var STRIPT = [];
    MP = MP.filter(function (A) {
      if (A.sep || A.px) return true;
      var len = A.hi - A.lo, k, B, ov, dd;
      if (len < 0.3) return true;
      for (k = 0; k < DPT.length; k++) {
        var peS = Math.abs(((A.o === 'h') ? DPT[k][1] : DPT[k][0]) - A.c), alS = (A.o === 'h') ? DPT[k][0] : DPT[k][1];
        if (peS < 0.30 && alS >= A.lo - 0.1 && alS <= A.hi + 0.1) return true;
      }
      for (k = 0; k < MP.length; k++) {
        B = MP[k];
        if (B === A || B.sep || B.o !== A.o || (B.hi - B.lo) < len) continue;
        dd = Math.abs(B.c - A.c); ov = Math.min(A.hi, B.hi) - Math.max(A.lo, B.lo);
        if (dd > ALIGN_TOL && dd < 0.38 && ov >= 0.8 * len) { STRIPT.push(A.o + ' ' + (A.o === 'v' ? 'x' : 'z') + _impN(A.c) + ' ' + _impN(dd) + ' m from a wall'); return false; }
      }
      for (k = 0; k < SD.length; k++) {
        B = SD[k];
        if (B.o !== A.o) continue;
        dd = Math.abs(B.c - A.c) - WALL; ov = Math.min(A.hi, B.hi) - Math.max(A.lo, B.lo);
        if (dd > 0.02 && dd < 0.35 && ov >= 0.8 * len) { STRIPT.push(A.o + ' ' + (A.o === 'v' ? 'x' : 'z') + _impN(A.c) + ' ' + _impN(dd) + ' m from an outer wall'); return false; }
      }
      return true;
    });
    if (STRIPT.length) out.notes.push('STRIP: ' + STRIPT.length + ' wall(s) with no door less than 38 cm beside another were left out -- a shelf or a line of the drawing: ' + STRIPT.join(', '));
    /* THE OPEN SIDES (v105, Marek: dve mistnosti, ktere nejsou oddelene
       prickou -- ano, delici cara). Where the reader says two rooms meet with
       no wall, a ROOM LINE goes there: it divides the rooms on the plan and
       builds nothing. A wall the reader also drew on that line comes off it. */
    /* A ROOM LINE ONLY BETWEEN THE OPEN ZONES (v116, Marek: deleni mistnosti
       prerusovanou carou ma byt opravdu jen vyjimecne -- treba zony v hlavni
       mistnosti, kitchen / living room -- jinak se prakticky nemaji
       vyskytovat). Living, dining, kitchen, hall, entry and mudroom may be
       parted by a line; a bathroom, a bedroom, a closet or a laundry is
       always behind a wall. */
    var OPEN_Z = { living: 1, dining: 1, kitchen: 1, hallway: 1, entry: 1, mudroom: 1 };
    function typeOfName(nm0) {
      var nz = String(nm0 || '').toUpperCase().replace(/[^A-Z0-9]/g, ''), kz;
      if (!nz) return '';
      for (kz = 0; raw.rooms && kz < raw.rooms.length; kz++) {
        if (String(raw.rooms[kz].label || '').toUpperCase().replace(/[^A-Z0-9]/g, '') === nz) return String(raw.rooms[kz].type || '');
      }
      var lw = String(nm0).toLowerCase().trim();
      if (/^(hall|hallway|corridor)$/.test(lw)) return 'hallway';
      if (/^(living|dining|kitchen|entry|mudroom|bedroom|kids|bathroom|wc|laundry|utility|pantry|office|storage|closet|wardrobe)$/.test(lw)) return lw;
      return '';
    }
    function openPair(nA, nB) {
      var tA = typeOfName(nA), tB = typeOfName(nB);
      if (!tA || !tB) return true;
      return !!(OPEN_Z[tA] && OPEN_Z[tB]);
    }
    /* A HALL WITH NO NAME IN FRONT OF THE DOORS IS THE LIVING ROOM (v148,
       Marek, plan 1000073678: nedava smysl pred koupelnou mit oddelenou
       plochu hall -- v puvodnim je living space, a to dava vetsi smysl). The
       reader gave the strip in front of the bedroom's and the bathroom's
       doors a hallway's box of its own, with no name -- the plan writes none
       there -- and a room line parted it from the living room it is open to.
       A hallway box with no name on the plan, no more than 1.2 m deep, whose
       long side runs along a living room for 60 % of its length or more, at
       most half that room's size, is part of the living room: no room line
       between them and no name of its own. A corridor open to the living
       room at its end only (1000073355) stays a hall. */
    var HALLIN = {}, HALLSAID = [], nHallLine = 0;
    (function () {
      function bx9(r) { var a = T(r.box[0]), b = T(r.box[1]); return { x0: Math.min(a[0], b[0]), x1: Math.max(a[0], b[0]), z0: Math.min(a[1], b[1]), z1: Math.max(a[1], b[1]) }; }
      var k9, j9;
      for (k9 = 0; raw.rooms && k9 < raw.rooms.length; k9++) {
        var h9 = raw.rooms[k9];
        if (!h9 || !h9.box || !h9.box[0] || !h9.box[1] || !/^(hallway|hall)$/.test(String(h9.type || '')) || String(h9.label || '').trim()) continue;
        var H9 = bx9(h9), hw9 = H9.x1 - H9.x0, hd9 = H9.z1 - H9.z0, ax9 = hw9 >= hd9, lg9 = Math.max(hw9, hd9), sh9 = Math.min(hw9, hd9);
        if (sh9 > 1.2 || lg9 < 1.0) continue;
        for (j9 = 0; raw.rooms && j9 < raw.rooms.length; j9++) {
          var l9 = raw.rooms[j9];
          if (j9 === k9 || !l9 || !l9.box || !l9.box[0] || !l9.box[1] || !/^(living|family|great_room)$/.test(String(l9.type || ''))) continue;
          var L9 = bx9(l9);
          if ((L9.x1 - L9.x0) * (L9.z1 - L9.z0) < 2 * hw9 * hd9) continue;
          var e9 = null;
          if (ax9) { if (Math.abs(H9.z0 - L9.z1) <= 0.35) e9 = H9.z0; else if (Math.abs(H9.z1 - L9.z0) <= 0.35) e9 = H9.z1; }
          else { if (Math.abs(H9.x0 - L9.x1) <= 0.35) e9 = H9.x0; else if (Math.abs(H9.x1 - L9.x0) <= 0.35) e9 = H9.x1; }
          var ov9 = ax9 ? (Math.min(H9.x1, L9.x1) - Math.max(H9.x0, L9.x0)) : (Math.min(H9.z1, L9.z1) - Math.max(H9.z0, L9.z0));
          if (e9 == null || ov9 < 0.6 * lg9) continue;
          HALLIN[k9] = { o: ax9 ? 'h' : 'v', c: e9, lo: ax9 ? H9.x0 : H9.z0, hi: ax9 ? H9.x1 : H9.z1 };
          HALLSAID.push('"' + (h9.label || h9.type) + '" ' + _impN(hw9) + ' x ' + _impN(hd9) + ' m along "' + (l9.label || l9.type) + '"');
          break;
        }
      }
    })();
    /* a line on the side such a hall shares with its living room */
    function hallLine(o9, c9, lo9, hi9) {
      for (var kh in HALLIN) {
        if (!HALLIN.hasOwnProperty(kh)) continue;
        var e = HALLIN[kh];
        if (e.o !== o9 || Math.abs(e.c - c9) > 0.35) continue;
        if (Math.min(hi9, e.hi) - Math.max(lo9, e.lo) >= 0.5 * Math.max(0.01, hi9 - lo9)) return true;
      }
      return false;
    }
    var OS = [], nClip = 0, OSW = [];
    for (i = 0; raw.open_sides && i < raw.open_sides.length; i++) {
      var osA = T(raw.open_sides[i].a), osB = T(raw.open_sides[i].b);
      var oo = (Math.abs(osB[0] - osA[0]) >= Math.abs(osB[1] - osA[1])) ? 'h' : 'v';
      var oc = _impSnap(oo === 'h' ? (osA[1] + osB[1]) / 2 : (osA[0] + osB[0]) / 2);
      var olo = (oo === 'h') ? Math.min(osA[0], osB[0]) : Math.min(osA[1], osB[1]);
      var ohi = (oo === 'h') ? Math.max(osA[0], osB[0]) : Math.max(osA[1], osB[1]);
      var onm = (raw.open_sides[i].rooms || []).join(' / ') || 'two rooms';
      if (ohi - olo < 0.4) { out.notes.push('an open side ' + _impN(ohi - olo) + ' m long was left out'); continue; }
      var onSh = false;
      for (q = 0; q < SD.length; q++) {
        if (SD[q].o === oo && Math.abs(SD[q].c - oc) < 0.15 && Math.min(ohi, SD[q].hi) - Math.max(olo, SD[q].lo) > 0.5 * (ohi - olo)) { onSh = true; break; }
      }
      if (onSh) { out.notes.push('an open side lying on an outer wall was left out'); continue; }
      if (!inside(oo === 'h' ? (olo + ohi) / 2 : oc, oo === 'h' ? oc : (olo + ohi) / 2)) { out.notes.push('an open side outside the house was left out'); continue; }
      if (hallLine(oo, oc, olo, ohi)) { nHallLine++; continue; }
      var os1 = { o: oo, c: oc, lo: olo, hi: ohi, sep: true, name: onm };
      os1.lo = _impSnap(snapTo(os1, os1.lo, 0.35, true)); os1.hi = _impSnap(snapTo(os1, os1.hi, 0.35, true));
      var osr = raw.open_sides[i].rooms || [];
      if (osr.length === 2 && !openPair(osr[0], osr[1])) {
        /* not two open zones: the wall the reader did not see */
        MP.push({ o: os1.o, c: os1.c, lo: os1.lo, hi: os1.hi });
        OSW.push(onm + ' ' + _impN(os1.hi - os1.lo) + ' m');
        continue;
      }
      OS.push(os1);
    }
    for (q = 0; q < OS.length; q++) {
      var keepMP = [], oq = OS[q];
      for (i = 0; i < MP.length; i++) {
        var m1 = MP[i];
        if (m1.sep || m1.o !== oq.o || Math.abs(m1.c - oq.c) > 0.12 || m1.hi <= oq.lo + 0.05 || m1.lo >= oq.hi - 0.05) { keepMP.push(m1); continue; }
        nClip++;
        if (oq.lo - m1.lo >= 0.4) keepMP.push({ o: m1.o, c: m1.c, lo: m1.lo, hi: oq.lo });
        if (m1.hi - oq.hi >= 0.4) keepMP.push({ o: m1.o, c: m1.c, lo: oq.hi, hi: m1.hi });
      }
      MP = keepMP;
    }
    for (q = 0; q < OS.length; q++) MP.push(OS[q]);
    if (OSW.length) out.notes.push(OSW.length + ' open side(s) between rooms that are not open zones became walls: ' + OSW.join(', '));
    if (OS.length) out.notes.push(OS.length + ' open side(s) became room lines -- no wall, nothing built: ' +
                                  OS.map(function (o2) { return o2.name + ' ' + o2.o + ' ' + _impN(o2.hi - o2.lo) + ' m'; }).join(', ') +
                                  (nClip ? ('; ' + nClip + ' wall(s) the plan also drew there came off it') : ''));
    /* DOORS THE READER LEFT OFF THE END OF A WALL (v100). A plan draws a
       wall up to the jamb and the door beside it, and the reader hands over
       the wall without the door: the door then stood on no wall and was left
       out, and the room it closes stayed open into the next (Marek's first
       real plan: the bathroom and the bedroom both opened into the living
       room). A door just past the free end of a partition, on its line,
       takes the partition on through it to its far jamb and on to the wall
       there, if one is near. */
    function nearSide(x, z) {
      for (var k = 0; k < SD.length; k++) {
        var sd = SD[k], al = sd.o === 'h' ? x : z, pe = Math.abs((sd.o === 'h' ? z : x) - sd.c);
        if (al >= sd.lo - 0.1 && al <= sd.hi + 0.1 && pe < IMP_REACH) return true;
      }
      return false;
    }
    function nearPart(x, z) {
      for (var k = 0; k < MP.length; k++) {
        var mp = MP[k], al = mp.o === 'h' ? x : z, pe = Math.abs((mp.o === 'h' ? z : x) - mp.c);
        if (al >= mp.lo - 0.1 && al <= mp.hi + 0.1 && pe < IMP_REACH) return true;
      }
      return false;
    }
    /* an end taken to the wall it nearly meets: the inner face of the shell,
       or the line of a partition that really runs across it */
    function snapTo(p, v, reach, shell) {
      var best = v, bd = reach, k;
      /* an end on the end of a slanted wall stays there (v128): the wall goes
         on at its angle from that point, and taking the end back to the next
         square line left a gap beside it */
      for (k = 0; out.diag && k < out.diag.length; k++) {
        var gE = out.diag[k], eX = p.o === 'h' ? v : p.c, eZ = p.o === 'h' ? p.c : v;
        if (Math.hypot(gE.a[0] - eX, gE.a[1] - eZ) <= 0.12 || Math.hypot(gE.b[0] - eX, gE.b[1] - eZ) <= 0.12) return v;
      }
      for (k = 0; shell && k < SD.length; k++) {
        if (SD[k].o === p.o || p.c < SD[k].lo - 0.01 || p.c > SD[k].hi + 0.01) continue;
        var inPlus = (SD[k].o === 'h') ? inside(p.c, SD[k].c + 0.05) : inside(SD[k].c + 0.05, p.c);
        var ff = inPlus ? SD[k].c + WALL : SD[k].c - WALL, dd = Math.min(Math.abs(v - ff), Math.abs(v - SD[k].c));
        if (dd < bd) { bd = dd; best = ff; }
      }
      for (k = 0; k < MP.length; k++) {
        var q3 = MP[k];
        if (q3 === p || q3.o === p.o || p.c < q3.lo - 0.05 || p.c > q3.hi + 0.05) continue;
        var dq3 = Math.abs(v - q3.c);
        if (dq3 < bd) { bd = dq3; best = q3.c; }
      }
      return best;
    }
    function mergeMP() {
      var again = true;
      while (again) {
        again = false;
        for (var a = 0; a < MP.length && !again; a++) {
          for (var b = a + 1; b < MP.length; b++) {
            if (MP[a].o === MP[b].o && !!MP[a].sep === !!MP[b].sep && Math.abs(MP[a].c - MP[b].c) <= 0.06 && MP[b].lo <= MP[a].hi + 0.15 && MP[b].hi >= MP[a].lo - 0.15) {
              MP[a].lo = Math.min(MP[a].lo, MP[b].lo); MP[a].hi = Math.max(MP[a].hi, MP[b].hi); if (MP[b].px) MP[a].px = true; if (MP[b].dw) MP[a].dw = true; MP.splice(b, 1); again = true; break;
            }
          }
        }
      }
    }
    var nThru = 0, nShort = 0, THRUX = [];
    /* NOT THROUGH THE MIDDLE OF A ROOM (v165, plan 1000075648, Marek: nechytila
       se pricka delici vestavene skrine). The bypass doors of the right-hand
       closet stood 0.40 m beside the line of the hall wall, within reach of
       it, and the hall wall was run on past its end through the closet to
       the outer wall -- the closet cut in two and the real wall between the
       two closets thrown out as too near. A wall is run on through a door
       only where the stretch it gains lies on the edge of the rooms the
       reader boxed: one passing 0.30 m and more inside a room's box over
       0.30 m of its length is not that door's wall. */
    var RBOXT = [];
    for (i = 0; raw.rooms && i < raw.rooms.length; i++) {
      var rT = raw.rooms[i];
      if (!rT || !rT.box || !rT.box[0] || !rT.box[1]) continue;
      var tb0 = T(rT.box[0]), tb1 = T(rT.box[1]);
      RBOXT.push({ x0: Math.min(tb0[0], tb1[0]), x1: Math.max(tb0[0], tb1[0]), z0: Math.min(tb0[1], tb1[1]), z1: Math.max(tb0[1], tb1[1]), name: String(rT.label || rT.type || 'room') });
    }
    function thruRoom(o, c, a0, a1) {
      for (var kT = 0; kT < RBOXT.length; kT++) {
        var BT = RBOXT[kT], vT = (o === 'v');
        var cLo = vT ? BT.x0 : BT.z0, cHi = vT ? BT.x1 : BT.z1, sLo = vT ? BT.z0 : BT.x0, sHi = vT ? BT.z1 : BT.x1;
        if (!(c > cLo + 0.30 && c < cHi - 0.30)) continue;
        if (Math.min(a1, sHi) - Math.max(a0, sLo) > 0.30) return BT.name;
      }
      return '';
    }
    for (i = 0; raw.doors && i < raw.doors.length; i++) {
      var dp0 = T(raw.doors[i].at), dw0 = units(raw.doors[i].w);
      if (!(dw0 > 0) || nearSide(dp0[0], dp0[1]) || nearPart(dp0[0], dp0[1])) continue;
      /* a door standing in a slanted wall is that wall's (v128): no square
         wall is run on through it -- that closed the hall off from the two
         doors of a 45 degree corner */
      if (out.diag && out.diag.length && _impDiagDoorAt(out.diag, dp0, null)) continue;
      var bestM = null, bestG = 1e9;
      for (q = 0; q < MP.length; q++) {
        var mq = MP[q], alq = mq.o === 'h' ? dp0[0] : dp0[1], peq = Math.abs((mq.o === 'h' ? dp0[1] : dp0[0]) - mq.c);
        if (peq >= IMP_REACH) continue;
        var gq = (alq > mq.hi) ? alq - mq.hi : ((alq < mq.lo) ? mq.lo - alq : 0);
        if (!(gq <= dw0 + 0.3 && gq < bestG)) continue;
        var eA = (alq > mq.hi) ? mq.hi : alq - dw0 / 2, eB = (alq > mq.hi) ? alq + dw0 / 2 : mq.lo, rmT = (eB > eA) ? thruRoom(mq.o, mq.c, eA, eB) : '';
        if (rmT) { THRUX.push(mq.o + ' ' + (mq.o === 'v' ? 'x' : 'z') + _impN(mq.c) + ' through "' + rmT + '"'); continue; }
        bestG = gq; bestM = { mp: mq, al: alq };
      }
      if (!bestM) continue;
      if (bestM.al > bestM.mp.hi) bestM.mp.hi = _impSnap(snapTo(bestM.mp, bestM.al + dw0 / 2, 0.35, true));
      else bestM.mp.lo = _impSnap(snapTo(bestM.mp, bestM.al - dw0 / 2, 0.35, true));
      nThru++;
    }
    if (nThru) { mergeMP(); out.notes.push(nThru + ' partition(s) were run on through a door their end stopped short of'); }
    if (THRUX.length) out.notes.push('THRU (v165): ' + THRUX.length + ' wall(s) not run on through a door, the stretch would cross the middle of a room: ' + THRUX.join(', '));
    /* AN END LEFT SHORT OF THE WALL IT MEETS: the reader often stops a wall
       a little before the next one. Within 0.70 m of a partition that really
       runs across its path, it is taken to it; further than that it is
       believed to be a free end. */
    function attached(p, v) {
      var k;
      for (k = 0; k < SD.length; k++) {
        if (SD[k].o === p.o || p.c < SD[k].lo - 0.01 || p.c > SD[k].hi + 0.01) continue;
        if (Math.abs(Math.abs(v - SD[k].c) - WALL) < 0.03 || Math.abs(v - SD[k].c) < 0.03) return true;
      }
      for (k = 0; k < MP.length; k++) {
        if (MP[k] === p || MP[k].o === p.o || p.c < MP[k].lo - 0.05 || p.c > MP[k].hi + 0.05) continue;
        if (Math.abs(v - MP[k].c) < 0.03) return true;
      }
      return false;
    }
    /* A WALL THE PICTURE DRAWS TO A DOOR ENDS THERE (v165, plan 1000075648).
       The wall under the two closets runs on 0.50 m past the hall wall to the
       jamb of the right-hand closet's bypass doors, and it was cut back to
       the hall wall here -- the closet left open at the bottom. An end of a
       wall read off the picture at the jamb of a door -- one in its own line
       or one across it -- is not cut back further than a wall's thickness;
       one meeting nothing still is. */
    var PXKEEP = 0.20;
    function doorAtEnd(p, v) {
      for (var kD = 0; raw.doors && kD < raw.doors.length; kD++) {
        var dD = raw.doors[kD]; if (!dD || !dD.at) continue;
        var pD = T(dD.at), wD = units(dD.w) || 0, alD = (p.o === 'h') ? pD[0] : pD[1], peD = (p.o === 'h') ? pD[1] : pD[0];
        /* in its own line: the end at one of the door's jambs */
        if (Math.abs(peD - p.c) < 0.25 && Math.abs(Math.abs(alD - v) - wD / 2) < 0.25) return true;
        /* across it: the door's line at the end, its span reaching the wall */
        if (Math.abs(alD - v) < 0.25 && Math.abs(peD - p.c) < wD / 2 + 0.25) return true;
      }
      return false;
    }
    var PXKEPT = [];
    for (i = 0; i < MP.length; i++) {
      var mpi = MP[i], v0;
      if (!attached(mpi, mpi.lo)) { v0 = _impSnap(snapTo(mpi, mpi.lo, 0.70, false)); if (v0 !== mpi.lo) { if (mpi.px && v0 - mpi.lo > PXKEEP && doorAtEnd(mpi, mpi.lo)) PXKEPT.push(mpi.o + ' ' + (mpi.o === 'v' ? 'x' : 'z') + _impN(mpi.c)); else { mpi.lo = v0; nShort++; } } }
      if (!attached(mpi, mpi.hi)) { v0 = _impSnap(snapTo(mpi, mpi.hi, 0.70, false)); if (v0 !== mpi.hi) { if (mpi.px && mpi.hi - v0 > PXKEEP && doorAtEnd(mpi, mpi.hi)) PXKEPT.push(mpi.o + ' ' + (mpi.o === 'v' ? 'x' : 'z') + _impN(mpi.c)); else { mpi.hi = v0; nShort++; } } }
    }
    if (PXKEPT.length) out.notes.push('JAMB (v165): ' + PXKEPT.length + ' wall end(s) drawn to the jamb of a door kept there, not cut back to the wall they pass: ' + PXKEPT.join(', '));
    if (nShort) { mergeMP(); out.notes.push(nShort + ' partition end(s) stopping short of a wall were taken to it'); }
    /* EVERY ROOM THE PLAN NAMES IS CLOSED (v101, Marek: musi se vsude
       umistovat do pricek dvere, aby mistnosti byly ohranicene, i kdyby dvere
       mely byt otvor). The reader gives each room the box of its walls. Along
       each edge of a box wall is looked for -- a partition or the shell within
       0.35 m; a stretch of an edge with none is closed by a partition with a
       doorway (a door with no leaf) over the whole stretch, unless the reader
       put a door there. A room open into the next stays open in use, and is a
       room of its own on the plan. A box is the reader's eye, so a wall it
       nearly finds counts, and nothing shorter than 0.40 m is added. */
    var CLIPC = [], CLOSE = [], OPENE = [], OVL = [], cRooms = [], ALLB = [], CLOSETS = { closet: 1, storage: 1, pantry: 1, laundry: 1, utility: 1, wardrobe: 1, walk_in_closet: 1 }, isCloset = {};
    /* THE OPEN SIDE OF A SLANTED WALL'S CORNER IS NOT A WALL (v130, Marek:
       pred dvermi je pricka navic s dvermi -- nesmysl). The bathroom's box
       and the hall's met under the V of the two doors at 45 degrees, no
       wall was drawn there, and it was closed -- a wall right across the V
       with a door of its own in front of the two doors. Every side of the
       box round a slanted wall is a leg of a corner it cuts: one where the
       rooms part with nothing built (the room line the corner gets below),
       or one the plan draws as a wall anyway. Neither is closed here. */
    function onDiagLeg(o, line, lo, hi) {
      var LG = [];
      for (var kD = 0; out.diag && kD < out.diag.length; kD++) {
        var gD = out.diag[kD], x0D = Math.min(gD.a[0], gD.b[0]), x1D = Math.max(gD.a[0], gD.b[0]), z0D = Math.min(gD.a[1], gD.b[1]), z1D = Math.max(gD.a[1], gD.b[1]);
        var onL = (o === 'h') ? (Math.abs(line - z0D) <= 0.35 || Math.abs(line - z1D) <= 0.35) : (Math.abs(line - x0D) <= 0.35 || Math.abs(line - x1D) <= 0.35);
        if (!onL) continue;
        var s0 = (o === 'h') ? x0D : z0D, s1 = (o === 'h') ? x1D : z1D;
        if (Math.min(hi, s1) - Math.max(lo, s0) > 0.5 * Math.min(hi - lo, s1 - s0)) { LG.push([s0, s1]); DLEGS[kD + ((o === 'h') ? ((Math.abs(line - z0D) <= 0.35) ? 'z0' : 'z1') : ((Math.abs(line - x0D) <= 0.35) ? 'x0' : 'x1'))] = 1; }
      }
      return LG;
    }
    /* THE DOORS THE PLAN DRAWS ARE ALL THE DOORS THERE ARE (v130, Marek:
       pokud jsou ve floorplanu evidentne znacene dvere klasickym symbolem,
       tak je nepridavat -- kolem mistnosti s pradlem W/D). A door is added
       where nothing is drawn only on a plan that draws no door leaves; on
       one that does -- a leaf found in the picture -- a gap with no leaf is
       a passage and stays open: a room line, nothing built. */
    var DRAWN_N = 0, DOORS_DRAWN = false, NODOOR = [];
    for (i = 0; raw.doors && i < raw.doors.length; i++) if (raw.doors[i] && raw.doors[i].pxLeaf) DRAWN_N++;
    DOORS_DRAWN = DRAWN_N >= 1;
    if (DOORS_DRAWN) out.notes.push('DOORS: ' + DRAWN_N + ' door leaf/leaves found in the picture -- the plan draws its doors, so none is added where it draws none (v130)');
    out.closets = [];
    /* A CLOSET INSIDE A BATHROOM IS ITS SHOWER (v161, plan 1000073631,
       Marek: kousek pricky misto zasteny sprchoveho koutu). The reader called
       the shower in the corner of BATH 1 a closet, its glass screen a
       partition and its glass door a door to it. A closet whose box lies
       inside a bathroom's or a WC's (seven tenths of it and more) is that
       room's shower: no closet and no room, its line no wall (the picture's
       thin line is left out below), and a shower piece in its place where
       the reader drew none. */
    var BATHB = [], SHOWERS = [];
    for (i = 0; raw.rooms && i < raw.rooms.length; i++) {
      var rbB = raw.rooms[i];
      if (!rbB || !rbB.box || !rbB.box[0] || !rbB.box[1] || !/^(bathroom|wc)$/.test(String(rbB.type || ''))) continue;
      var bb0 = T(rbB.box[0]), bb1 = T(rbB.box[1]);
      BATHB.push({ x0: Math.min(bb0[0], bb1[0]), x1: Math.max(bb0[0], bb1[0]), z0: Math.min(bb0[1], bb1[1]), z1: Math.max(bb0[1], bb1[1]), name: String(rbB.label || rbB.type) });
    }
    for (i = 0; raw.rooms && i < raw.rooms.length; i++) {
      var rb = raw.rooms[i];
      if (!rb || !rb.box || !rb.box[0] || !rb.box[1]) continue;
      var cb0 = T(rb.box[0]), cb1 = T(rb.box[1]);
      var bxR = { x0: Math.min(cb0[0], cb1[0]), x1: Math.max(cb0[0], cb1[0]), z0: Math.min(cb0[1], cb1[1]), z1: Math.max(cb0[1], cb1[1]),
                  name: String(rb.label || rb.type || 'room'), type: String(rb.type || ''), label: String(rb.label || '') };
      if (/^(closet|storage)$/.test(String(rb.type || ''))) {
        var arS = (bxR.x1 - bxR.x0) * (bxR.z1 - bxR.z0), inB = null, qB;
        for (qB = 0; qB < BATHB.length && arS > 0; qB++) {
          var BB = BATHB[qB], ovS = Math.max(0, Math.min(BB.x1, bxR.x1) - Math.max(BB.x0, bxR.x0)) * Math.max(0, Math.min(BB.z1, bxR.z1) - Math.max(BB.z0, bxR.z0));
          if (ovS >= 0.7 * arS) { inB = BB; break; }
        }
        /* not a closet with walls of its own, nor one the plan names (LIN.,
           LINEN, a linen closet of plan C) */
        if (inB && rb.label && !/^\s*(closet|cl\.?|storage)?\s*$/i.test(String(rb.label))) inB = null;
        if (inB) {
          var edgesW = 0, edgesN = 0, qE, qP;
          var EDG = [['h', bxR.z0, bxR.x0, bxR.x1, inB.z0], ['h', bxR.z1, bxR.x0, bxR.x1, inB.z1], ['v', bxR.x0, bxR.z0, bxR.z1, inB.x0], ['v', bxR.x1, bxR.z0, bxR.z1, inB.x1]];
          for (qE = 0; qE < EDG.length; qE++) {
            var EG = EDG[qE];
            if (Math.abs(EG[1] - EG[4]) <= 0.2) continue;
            edgesN++;
            for (qP = 0; raw.partitions && qP < raw.partitions.length; qP++) {
              var RP = raw.partitions[qP]; if (!RP || !RP.a || !RP.b) continue;
              var pA = T(RP.a), pB = T(RP.b), hzP = Math.abs(pB[1] - pA[1]) < Math.abs(pB[0] - pA[0]);
              if ((EG[0] === 'h') !== hzP) continue;
              var lnP = hzP ? (pA[1] + pB[1]) / 2 : (pA[0] + pB[0]) / 2, l0P = hzP ? Math.min(pA[0], pB[0]) : Math.min(pA[1], pB[1]), l1P = hzP ? Math.max(pA[0], pB[0]) : Math.max(pA[1], pB[1]);
              if (Math.abs(lnP - EG[1]) > 0.2) continue;
              if (Math.min(l1P, EG[3]) - Math.max(l0P, EG[2]) >= 0.5 * (EG[3] - EG[2])) { edgesW++; break; }
            }
          }
          if (edgesN && edgesW) inB = null;
        }
        if (inB) {
          var sw0 = bxR.x1 - bxR.x0, sd0 = bxR.z1 - bxR.z0, hasF = false, qF;
          for (qF = 0; raw.fixtures && qF < raw.fixtures.length; qF++) {
            var FQ = raw.fixtures[qF];
            if (!FQ || !/shower|bathtub/.test(String(FQ.type || '')) || !FQ.a || !FQ.b) continue;
            var fa = T(FQ.a), fb = T(FQ.b), fx0 = Math.min(fa[0], fb[0]), fx1 = Math.max(fa[0], fb[0]), fz0 = Math.min(fa[1], fb[1]), fz1 = Math.max(fa[1], fb[1]);
            if (Math.min(fx1, bxR.x1) - Math.max(fx0, bxR.x0) > 0.2 && Math.min(fz1, bxR.z1) - Math.max(fz0, bxR.z0) > 0.2) { hasF = true; break; }
          }
          var putF = !hasF && !rb.__shower && Math.min(sw0, sd0) >= 0.7 && Math.max(sw0, sd0) <= 1.7;
          if (putF) { raw.fixtures = raw.fixtures || []; raw.fixtures.push({ type: 'shower', a: rb.box[0], b: rb.box[1], label: '' }); rb.__shower = true; }
          SHOWERS.push('"' + bxR.name + '" ' + _impN(sw0) + ' x ' + _impN(sd0) + ' m inside "' + inB.name + '"' + (hasF ? ' (the shower drawn there already)' : (rb.__shower ? ' -- a shower piece in its place' : '')));
          bxR.type = 'shower'; bxR.name = 'shower'; bxR.label = '';
          ALLB.push(bxR);
          /* no name of a room either -- it is the bathroom's */
          isCloset[i] = true;
          continue;
        }
      }
      /* THE CLOSET TEST COMES BEFORE THE NARROW-BOX TEST (v105): a linen
         closet 0.45 m deep was thrown out as a box too narrow to close and
         named STORAGE like a room. Whether the box was right is settled
         after the plan is written, by the real cell (_impClosetsByCell). */
      var arR = (bxR.x1 - bxR.x0) * (bxR.z1 - bxR.z0);
      if (CLOSETS[rb.type] && arR < CLOSET_MAX_M2) {
        out.closets.push({ label: bxR.name, type: rb.type, area: arR, x: (bxR.x0 + bxR.x1) / 2, z: (bxR.z0 + bxR.z1) / 2, box: [bxR.x0, bxR.z0, bxR.x1, bxR.z1] });
        isCloset[i] = true;
      }
      ALLB.push(bxR);
      if (bxR.x1 - bxR.x0 < 0.5 || bxR.z1 - bxR.z0 < 0.5) continue;
      cRooms.push(bxR);
    }
    if (SHOWERS.length) out.notes.push('SHOWER (v161): ' + SHOWERS.length + ' closet(s) lying inside a bathroom are its shower, no closet: ' + SHOWERS.join(', '));
    var nDiagLeg = 0, DLEGS = {};
    function freeRuns(o, line, lo, hi) {
      var free = [[lo, hi]], k, f2;
      function cut(a0, a1) {
        var nf = [];
        for (f2 = 0; f2 < free.length; f2++) {
          var fa = free[f2][0], fb = free[f2][1];
          if (a1 <= fa || a0 >= fb) { nf.push([fa, fb]); continue; }
          if (a0 > fa) nf.push([fa, a0]);
          if (a1 < fb) nf.push([a1, fb]);
        }
        free = nf;
      }
      for (k = 0; k < SD.length; k++) if (SD[k].o === o && Math.abs(SD[k].c - line) <= WALL + 0.35) cut(SD[k].lo - 0.1, SD[k].hi + 0.1);
      for (k = 0; k < MP.length; k++) if (MP[k].o === o && Math.abs(MP[k].c - line) <= 0.35) cut(MP[k].lo - 0.05, MP[k].hi + 0.05);
      /* v130: nor along the box round a slanted wall */
      var dl = onDiagLeg(o, line, lo, hi), kl;
      for (kl = 0; kl < dl.length; kl++) { cut(dl[kl][0] - 0.05, dl[kl][1] + 0.05); nDiagLeg = Object.keys(DLEGS).length; }
      return free;
    }
    /* v172: the line of a door the reader put between rooms a and b, lying
       in the strip where a's edge runs into b's box and on no wall -- or null */
    var DOORW = [];
    function doorWall(a, b, ed, lo, hi) {
      var na = String(a.label || a.name || '').toLowerCase(), nb = String(b.label || b.name || '').toLowerCase(), q, k;
      var tb = String(a.type || '').toLowerCase(), tc = String(b.type || '').toLowerCase();
      var nearB = (ed.out > 0) ? ((ed.o === 'h') ? b.z0 : b.x0) : ((ed.o === 'h') ? b.z1 : b.x1);
      var c0 = Math.min(ed.line, nearB) - 0.15, c1 = Math.max(ed.line, nearB) + 0.15;
      for (q = 0; raw.doors && q < raw.doors.length; q++) {
        var D = raw.doors[q];
        if (!D || !D.at || !Array.isArray(D.rooms) || D.rooms.length < 2) continue;
        var RN = D.rooms.map(function (r) { return String(r).toLowerCase(); });
        var hasA = RN.indexOf(na) >= 0 || (tb && RN.indexOf(tb) >= 0), hasB = RN.indexOf(nb) >= 0 || (tc && RN.indexOf(tc) >= 0);
        if (!hasA || !hasB) continue;
        var P = T(D.at), c = (ed.o === 'h') ? P[1] : P[0], u = (ed.o === 'h') ? P[0] : P[1];
        if (c < c0 || c > c1 || u < lo - 0.1 || u > hi + 0.1) continue;
        var walled = false;
        for (k = 0; k < SD.length && !walled; k++) if (SD[k].o === ed.o && Math.abs(SD[k].c - c) <= WALL + 0.1 && u >= SD[k].lo - 0.1 && u <= SD[k].hi + 0.1) walled = true;
        for (k = 0; k < MP.length && !walled; k++) if (MP[k].o === ed.o && Math.abs(MP[k].c - c) <= 0.15 && u >= MP[k].lo - 0.1 && u <= MP[k].hi + 0.1) walled = true;
        if (walled) continue;
        return _impSnap(c);
      }
      return null;
    }
    for (i = 0; i < cRooms.length; i++) {
      var BX = cRooms[i];
      var E4 = [{ o: 'h', line: BX.z0, lo: BX.x0, hi: BX.x1, out: -1 }, { o: 'h', line: BX.z1, lo: BX.x0, hi: BX.x1, out: 1 },
                { o: 'v', line: BX.x0, lo: BX.z0, hi: BX.z1, out: -1 }, { o: 'v', line: BX.x1, lo: BX.z0, hi: BX.z1, out: 1 }];
      for (q = 0; q < 4; q++) {
        var ed = E4[q], fr = freeRuns(ed.o, ed.line, ed.lo + 0.15, ed.hi - 0.15), r2;
        for (r2 = 0; r2 < fr.length; r2++) {
          var a5 = fr[r2][0], b5 = fr[r2][1];
          if (b5 - a5 < 0.40) continue;
          /* ONLY WHERE ANOTHER ROOM IS ACROSS IT (v107, Marek: jedna mistnost se
             delila na x mensich). A box is the largest rectangle the reader saw,
             and in an L-shaped room -- a bedroom with its closet cut out of a
             corner -- one side of it runs through the middle of the room. Closing
             that side cut the room in two. A free stretch is closed only where
             another named room's box lies right across it; anywhere else the
             room simply goes on. */
          /* NOT WHERE THE NEXT BOX REACHES OVER THE EDGE (v108). The hall's box
             was the rectangle round the whole middle of the house, and its edge
             ran through the laundry closet beside it: closing it there cut the
             closet in two and made a second hall of the strip (Marek: porad je
             tam moc mistnosti). Two rooms meet along an edge only where the
             other box STARTS at it; one that reaches more than 0.15 m over it
             means a box was drawn too big, and the walls the plan does draw
             part those two rooms already. */
          var acrossR = null, overR = null, k6;
          for (k6 = 0; k6 < cRooms.length; k6++) {
            if (k6 === i) continue;
            var OB = cRooms[k6];
            var nearO = (ed.out > 0) ? ((ed.o === 'h') ? OB.z0 : OB.x0) : ((ed.o === 'h') ? OB.z1 : OB.x1);
            var ovO = (ed.o === 'h') ? (Math.min(OB.x1, b5) - Math.max(OB.x0, a5)) : (Math.min(OB.z1, b5) - Math.max(OB.z0, a5));
            var dO = (nearO - ed.line) * ed.out;
            if (ovO < 0.5 * (b5 - a5)) continue;
            if (dO > -0.15 && dO < 0.5) { acrossR = OB; break; }
            if (dO <= -0.15 && dO > -1.5 && !overR) overR = OB;
          }
          /* UNLESS A DOOR BETWEEN THE TWO STANDS THERE ON NO WALL (v172, plan
             1000076006, RANCH: the bedrooms' boxes ran 0.28 m into the hall's,
             no wall was drawn there, the doors from the hall stood on nothing
             and were left out -- both bedrooms opened into the hall and on into
             the great room and the kitchen, two rooms in one). The door the
             reader put between those two rooms says where their wall is: it is
             closed through the door, along the two boxes. */
          var FORCE = null;
          if (!acrossR && overR) {
            FORCE = doorWall(BX, overR, ed, a5, b5);
            if (FORCE == null) { OVL.push('"' + BX.name + ' / ' + overR.name + '" ' + _impN(b5 - a5) + ' m'); continue; }
            acrossR = overR;
            DOORW.push('"' + BX.name + ' / ' + overR.name + '" at ' + _impN(FORCE));
          }
          if (!acrossR) { OPENE.push('"' + BX.name + '" ' + _impN(b5 - a5) + ' m'); continue; }
          /* ONLY AS FAR AS THE ROOM ACROSS GOES (v165, plan 1000075648). The
             right-hand bedroom is an L: its box's left side runs past the
             closet across it on to the hall, where the bedroom goes on round
             the corner to its door. The whole side was closed, the corner
             shut off from the bedroom, its door opening into a cell of its
             own. A free stretch is closed only along the rooms across it;
             what runs on 0.40 m and more beyond them stays open. */
          var aR = (ed.o === 'h') ? acrossR.x0 : acrossR.z0, bR = (ed.o === 'h') ? acrossR.x1 : acrossR.z1, k7;
          for (k7 = 0; k7 < cRooms.length; k7++) {
            if (k7 === i || cRooms[k7] === acrossR) continue;
            var OB7 = cRooms[k7], near7 = (ed.out > 0) ? ((ed.o === 'h') ? OB7.z0 : OB7.x0) : ((ed.o === 'h') ? OB7.z1 : OB7.x1), d7 = (near7 - ed.line) * ed.out;
            var lo7 = (ed.o === 'h') ? OB7.x0 : OB7.z0, hi7 = (ed.o === 'h') ? OB7.x1 : OB7.z1;
            if (d7 > -0.15 && d7 < 0.5 && Math.min(hi7, b5) - Math.max(lo7, a5) > 0.2) { aR = Math.min(aR, lo7); bR = Math.max(bR, hi7); }
          }
          var ca5 = Math.max(a5, aR - 0.15), cb5 = Math.min(b5, bR + 0.15);
          if ((ca5 - a5 >= 0.40 || b5 - cb5 >= 0.40) && cb5 - ca5 >= 0.40) { CLIPC.push('"' + BX.name + ' / ' + acrossR.name + '" ' + _impN(b5 - a5) + ' to ' + _impN(cb5 - ca5) + ' m'); a5 = ca5; b5 = cb5; }
          /* v172: a wall put through a door goes only along the room the door
             leads to -- not on past it along a closet that has its own wall */
          /* and where the room across starts or ends inside the run, the wall
             steps back there to the line of the box edge (a bedroom's wall
             along the hall, its closet's front beside it) -- a short wall
             across, so the two lines meet */
          var CONN = [];
          if (FORCE != null) {
            var oLo9 = (ed.o === 'h') ? acrossR.x0 : acrossR.z0, oHi9 = (ed.o === 'h') ? acrossR.x1 : acrossR.z1;
            if (oLo9 > a5 + 0.05) { a5 = oLo9; CONN.push(a5); }
            if (oHi9 < b5 - 0.05) { b5 = oHi9; CONN.push(b5); }
            if (b5 - a5 < 0.40) { DOORW.pop(); OVL.push('"' + BX.name + ' / ' + acrossR.name + '" ' + _impN(b5 - a5) + ' m'); continue; }
          }
          var cl = (FORCE != null) ? FORCE : null, k5;
          for (k5 = 0; cl == null && k5 < MP.length; k5++) if (MP[k5].o === ed.o && Math.abs(MP[k5].c - ed.line) <= 0.35) { cl = MP[k5].c; break; }
          if (cl == null) cl = _impSnap(ed.line + ed.out * 0.05);
          var mx2 = (ed.o === 'h') ? (a5 + b5) / 2 : cl, mz2 = (ed.o === 'h') ? cl : (a5 + b5) / 2;
          if (!inside(mx2, mz2)) continue;
          /* AND HOW IT IS CLOSED (v107). Two rooms of the open part of a house
             -- living, dining, kitchen, hall, entry, mudroom -- meeting where the
             plan draws no wall are open to each other: a ROOM LINE, nothing
             built. Anywhere a closet, a bathroom, a bedroom or an office is on
             either side, the plan has a wall the reader missed, and it gets a
             partition with a doorway as before (Marek: i kdyby dvere mely byt
             otvor). A tap turns either into the other. */
          var OPENT = { living: 1, dining: 1, kitchen: 1, hallway: 1, entry: 1, mudroom: 1 };
          var asLine = !!(OPENT[BX.type] && OPENT[acrossR.type]);
          if (asLine && hallLine(ed.o, cl, a5, b5)) { nHallLine++; continue; }
          var np = { o: ed.o, c: cl, lo: a5 - 0.15, hi: b5 + 0.15 };
          if (asLine) np.sep = true;
          np.lo = _impSnap(snapTo(np, np.lo, 0.35, true)); np.hi = _impSnap(snapTo(np, np.hi, 0.35, true));
          if (CONN.length) {
            /* v172: the stepped ends meet their short walls exactly */
            var edL = ed.line, kq9;
            for (kq9 = 0; kq9 < MP.length; kq9++) if (MP[kq9].o === ed.o && Math.abs(MP[kq9].c - ed.line) <= 0.2) { edL = MP[kq9].c; break; }
            for (kq9 = 0; kq9 < CONN.length; kq9++) {
              /* the wall across that ends at the box edge already (the closet's
                 own, 0.30 m off the hall's box) is carried on to the new line;
                 none there, a short one is put */
              var zc9 = CONN[kq9], hit9 = null, km9;
              for (km9 = 0; km9 < MP.length; km9++) {
                var M9 = MP[km9];
                if (M9.o === ed.o || M9.sep || Math.abs(M9.c - zc9) > 0.35) continue;
                if (Math.abs(M9.lo - edL) <= 0.1 || Math.abs(M9.hi - edL) <= 0.1) { hit9 = M9; break; }
              }
              var zz9 = hit9 ? hit9.c : _impSnap(zc9);
              if (hit9) { if (Math.abs(hit9.lo - edL) <= 0.1 && cl < hit9.lo) hit9.lo = _impSnap(cl); else if (cl > hit9.hi) hit9.hi = _impSnap(cl); }
              else MP.push({ o: (ed.o === 'h') ? 'v' : 'h', c: zz9, lo: _impSnap(Math.min(cl, edL)), hi: _impSnap(Math.max(cl, edL)) });
              if (zc9 === a5) np.lo = zz9;
              if (zc9 === b5) np.hi = zz9;
            }
          }
          if (FORCE != null) np.dw = true;
          MP.push(np);
          CLOSE.push({ o: ed.o, c: cl, lo: a5, hi: b5, name: BX.name + ' / ' + acrossR.name, line: asLine, rA: BX, rB: acrossR, plo: np.lo, phi: np.hi });
        }
      }
    }
    if (CLOSE.length) {
      mergeMP();
      out.notes.push(CLOSE.length + ' room edge(s) where two named rooms meet with no wall drawn were closed: ' +
                     CLOSE.map(function (cc) { return '"' + cc.name + '" ' + _impN(cc.hi - cc.lo) + ' m ' + (cc.line ? 'ROOM LINE' : 'wall'); }).join(', '));
    }
    if (CLIPC.length) out.notes.push('CLOSE (v165): ' + CLIPC.length + ' edge(s) closed only along the room across, the box running on past it: ' + CLIPC.join(', '));
    if (DOORW.length) out.notes.push('DOOR WALL (v172): ' + DOORW.length + ' edge(s) running into the next room\'s box closed through the door the reader put between the two, which stood on no wall: ' + DOORW.join(', '));
    if (OVL.length) out.notes.push(OVL.length + ' box edge(s) running inside the next room\'s box were not closed -- a box drawn too big, the walls drawn there part the rooms: ' + OVL.join(', '));
    if (nDiagLeg) out.notes.push('DIAG: ' + nDiagLeg + ' side(s) of a slanted wall\'s corner not closed -- the room line of the corner parts the rooms there, no wall and no door across it (v130)');
    /* AND A LINE ADDED HERE MEETS THE ONES ALREADY THERE (v108). The open
       side between the kitchen and the living room stopped 0.15 m short of
       the room line the closing put under the hall, and the gap left a
       sliver of 0.02 m2 as a room of its own. Every end still short of a
       line within 0.35 m is taken to it, room lines too. */
    var nShort2 = 0;
    for (i = 0; i < MP.length; i++) {
      var mp2 = MP[i], v2;
      if (!attached(mp2, mp2.lo)) { v2 = _impSnap(snapTo(mp2, mp2.lo, 0.35, false)); if (v2 !== mp2.lo) { mp2.lo = v2; nShort2++; } }
      if (!attached(mp2, mp2.hi)) { v2 = _impSnap(snapTo(mp2, mp2.hi, 0.35, false)); if (v2 !== mp2.hi) { mp2.hi = v2; nShort2++; } }
    }
    if (nShort2) { mergeMP(); out.notes.push(nShort2 + ' line end(s) short of a line the closing added were taken to it'); }
    /* THE CORNER OF A WALL PUT THROUGH A DOOR (v172): its free end and the
       end of a wall across, each within 0.35 m of the other's line (the
       bedroom's wall along the hall, the closet's wall beside it), meet --
       the wall across is carried on to it, the wall to the wall across */
    var CORN = [];
    for (i = 0; i < MP.length; i++) {
      var dw = MP[i];
      if (!dw.dw || dw.sep) continue;
      ['lo', 'hi'].forEach(function (endK) {
        var v9 = dw[endK];
        if (attached(dw, v9)) return;
        var best9 = null, bd9 = 1e9, k9;
        for (k9 = 0; k9 < MP.length; k9++) {
          var P9 = MP[k9];
          if (P9 === dw || P9.o === dw.o || P9.sep || Math.abs(P9.c - v9) > 0.35) continue;
          if (dw.c >= P9.lo - 0.02 && dw.c <= P9.hi + 0.02) continue;
          var e9 = (Math.abs(P9.lo - dw.c) <= Math.abs(P9.hi - dw.c)) ? 'lo' : 'hi', gap9 = Math.abs(P9[e9] - dw.c);
          if (gap9 > 0.35) continue;
          var d9 = gap9 + Math.abs(P9.c - v9);
          if (d9 < bd9) { bd9 = d9; best9 = { P: P9, e: e9 }; }
        }
        if (!best9) return;
        best9.P[best9.e] = dw.c;
        dw[endK] = best9.P.c;
        CORN.push((dw.o === 'v' ? 'x' : 'z') + _impN(dw.c) + ' to ' + (best9.P.o === 'v' ? 'x' : 'z') + _impN(best9.P.c));
      });
    }
    if (CORN.length) { mergeMP(); out.notes.push('CORNER (v172): ' + CORN.length + ' end(s) of a wall put through a door met the wall across beside it: ' + CORN.join(', ')); }
    /* THE MOUTH OF A ROOM (v110, Marek: Fable nejlepsi vysledky, krome
       rozdeleni mistnosti na living a kitchen -- dokonce se spojila se
       spojovaci mistnosti). The hall ran out between the laundry wall and
       the bathroom wall into the open kitchen and living room; the reader
       gave no open side there and the hall's box stopped short of the line,
       so nothing closed it, and the hall, the kitchen and the living room
       became one room under one name. Two walls in one line with a gap of
       0.50 to 2.40 m between their ends, no door read in it and two
       different rooms -- neither a closet -- on its two sides, is the mouth
       of a room: a ROOM LINE closes it. Nothing is built there. */
    var MOUTH = [], MOUTHD = [];
    function boxProbe(x, z, o, sgn) {
      var dd, k;
      for (dd = 0.3; dd <= 0.95; dd += 0.3) {
        var px = (o === 'h') ? x : x + sgn * dd, pz = (o === 'h') ? z + sgn * dd : z, best = null, ba = 1e9;
        for (k = 0; k < ALLB.length; k++) {
          var B = ALLB[k];
          if (px < B.x0 - 0.05 || px > B.x1 + 0.05 || pz < B.z0 - 0.05 || pz > B.z1 + 0.05) continue;
          var ar = (B.x1 - B.x0) * (B.z1 - B.z0);
          if (ar < ba) { ba = ar; best = B; }
        }
        if (best) return best;
      }
      return null;
    }
    for (i = 0; i < MP.length; i++) {
      for (q = 0; q < MP.length; q++) {
        var mA = MP[i], mB = MP[q];
        if (i === q || mA.sep || mB.sep || mA.o !== mB.o || Math.abs(mA.c - mB.c) > 0.06) continue;
        var g0 = mA.hi, g1 = mB.lo;
        if (!(g1 - g0 >= 0.5 && g1 - g0 <= 2.4)) continue;
        var covM = false, k7;
        for (k7 = 0; k7 < MP.length; k7++) {
          var mC = MP[k7];
          if (mC !== mA && mC !== mB && mC.o === mA.o && Math.abs(mC.c - mA.c) <= 0.06 && mC.hi > g0 + 0.05 && mC.lo < g1 - 0.05) covM = true;
        }
        for (k7 = 0; !covM && k7 < DPT.length; k7++) {
          var dal = (mA.o === 'h') ? DPT[k7][0] : DPT[k7][1], dpe = Math.abs(((mA.o === 'h') ? DPT[k7][1] : DPT[k7][0]) - mA.c);
          if (dpe < 0.35 && dal > g0 - 0.1 && dal < g1 + 0.1) covM = true;
        }
        if (covM) continue;
        var cM = (mA.c + mB.c) / 2, midM = (g0 + g1) / 2;
        var mx3 = (mA.o === 'h') ? midM : cM, mz3 = (mA.o === 'h') ? cM : midM;
        if (!inside(mx3, mz3)) continue;
        var bM1 = boxProbe(mx3, mz3, mA.o, -1), bM2 = boxProbe(mx3, mz3, mA.o, 1);
        if (!bM1 || !bM2 || bM1 === bM2 || CLOSETS[bM1.type] || CLOSETS[bM2.type]) continue;
        /* v130: the gap between the feet of a slanted wall is its corner's open side */
        if (onDiagLeg(mA.o, cM, g0, g1).length) { nDiagLeg = Object.keys(DLEGS).length; continue; }
        if (!(OPEN_Z[bM1.type] && OPEN_Z[bM2.type]) && DOORS_DRAWN) {
          /* v130: the plan draws its doors and none here -- the mouth stays open */
          MP.push({ o: mA.o, c: cM, lo: g0, hi: g1, sep: true });
          NODOOR.push('mouth "' + bM1.name + ' / ' + bM2.name + '" ' + _impN(g1 - g0) + ' m');
          continue;
        }
        if (!(OPEN_Z[bM1.type] && OPEN_Z[bM2.type])) {
          /* v116: not two open zones -- the wall goes on, and a door stands in the gap */
          MP.push({ o: mA.o, c: cM, lo: g0, hi: g1 });
          MOUTHD.push({ o: mA.o, c: cM, lo: g0, hi: g1, b1: bM1, b2: bM2 });
          continue;
        }
        if (hallLine(mA.o, cM, g0, g1)) { nHallLine++; continue; }
        MP.push({ o: mA.o, c: cM, lo: g0, hi: g1, sep: true });
        MOUTH.push('"' + bM1.name + ' / ' + bM2.name + '" ' + mA.o + ' ' + _impN(g1 - g0) + ' m');
      }
    }
    if (MOUTH.length) out.notes.push(MOUTH.length + ' room mouth(s) between two walls in one line, with no door and no line, became room lines: ' + MOUTH.join(', '));
    if (OPENE.length) out.notes.push(OPENE.length + ' box edge(s) with no wall and no named room across them were left open -- the box was bigger than the room: ' + OPENE.join(', '));
    /* the tidy again, now with the room lines: a line a hand's breadth off
       the wall line it closes goes onto it, then off any wall it lies along */
    var nAl1 = alignLines(true);
    if (nAl1) mergeMP();
    var nCl1 = clipLines();
    var nGp1 = closeGaps();
    if (nAl1 || nCl1 || nGp1) {
      mergeMP();
      var nSn1 = 0;
      for (i = 0; i < MP.length; i++) {
        var mp3 = MP[i], v3;
        if (!attached(mp3, mp3.lo)) { v3 = _impSnap(snapTo(mp3, mp3.lo, 0.35, false)); if (v3 !== mp3.lo) { mp3.lo = v3; nSn1++; } }
        if (!attached(mp3, mp3.hi)) { v3 = _impSnap(snapTo(mp3, mp3.hi, 0.35, false)); if (v3 !== mp3.hi) { mp3.hi = v3; nSn1++; } }
      }
      if (nSn1) mergeMP();
      out.notes.push('TIDY: ' + [nAl1 ? (nAl1 + ' line(s) put on the line of another') : '', nCl1 ? (nCl1 + ' room line(s) cut off a wall they lay along') : '',
                                 nGp1 ? (nGp1 + ' gap(s) under 0.50 m with no door closed') : '', nSn1 ? (nSn1 + ' end(s) taken again to the line they nearly meet') : '']
                                 .filter(function (t) { return t; }).join(', '));
    }
    /* AND A SQUARE WALL REACHES THE SLANTED ONE (v128): the end of a square
       wall a hand short of where a slanted wall starts, on its line, goes to
       that point -- the two are one corner on the drawing */
    var nDgE = 0;
    for (i = 0; out.diag && i < out.diag.length; i++) {
      var EE5 = [out.diag[i].a, out.diag[i].b], e5, k5;
      for (e5 = 0; e5 < 2; e5++) {
        var E5 = EE5[e5];
        for (k5 = 0; k5 < MP.length; k5++) {
          var m5 = MP[k5];
          if (m5.sep) continue;
          var al5 = m5.o === 'h' ? E5[0] : E5[1], pe5 = Math.abs((m5.o === 'h' ? E5[1] : E5[0]) - m5.c);
          if (pe5 > 0.08) continue;
          if (al5 < m5.lo && m5.lo - al5 <= 0.2) { m5.lo = _impR(al5); nDgE++; }
          else if (al5 > m5.hi && al5 - m5.hi <= 0.2) { m5.hi = _impR(al5); nDgE++; }
        }
      }
    }
    if (nDgE) out.notes.push('DIAG: ' + nDgE + ' square wall end(s) taken on to where a slanted wall starts');
    /* AND THE ROOMS STILL PART AT THE CORNER IT CUTS (v128). The rooms are
       cut out of the plan by its square lines, and a 45 degree wall across a
       corner leaves those lines open: the bathroom, the hall and the bedroom
       of a corner with two doors at 45 degrees ran into one room. The corner
       the slanted wall replaces -- where the two square walls it joins would
       have met -- gets its two legs back as room lines: nothing is built on
       them, the rooms part there as they would at the square corner. */
    var nLeg = 0, nLegSnap = 0;
    var lineEnds = function (o6, c6, al6) {
      for (var k6 = 0; k6 < MP.length; k6++) {
        var m6 = MP[k6];
        if (m6.sep || m6.o !== o6 || Math.abs(m6.c - c6) > 0.08) continue;
        if (al6 >= m6.lo - 0.15 && al6 <= m6.hi + 0.15) return true;
      }
      return false;
    };
    var covered = function (o6, c6, lo6, hi6) {
      for (var k6 = 0; k6 < MP.length; k6++) {
        var m6 = MP[k6];
        if (m6.o === o6 && Math.abs(m6.c - c6) <= 0.06 && m6.lo <= lo6 + 0.05 && m6.hi >= hi6 - 0.05) return true;
      }
      return false;
    };
    for (i = 0; out.diag && i < out.diag.length; i++) {
      var GA = out.diag[i].a, GB = out.diag[i].b;
      /* the two corners a slanted wall can stand for, each with its legs */
      var CN = [
        [{ o: 'h', c: GA[1], lo: Math.min(GA[0], GB[0]), hi: Math.max(GA[0], GB[0]), ok: lineEnds('h', GA[1], GA[0]) },
         { o: 'v', c: GB[0], lo: Math.min(GA[1], GB[1]), hi: Math.max(GA[1], GB[1]), ok: lineEnds('v', GB[0], GB[1]) }],
        [{ o: 'v', c: GA[0], lo: Math.min(GA[1], GB[1]), hi: Math.max(GA[1], GB[1]), ok: lineEnds('v', GA[0], GA[1]) },
         { o: 'h', c: GB[1], lo: Math.min(GA[0], GB[0]), hi: Math.max(GA[0], GB[0]), ok: lineEnds('h', GB[1], GB[0]) }]
      ];
      var c0ok = CN[0][0].ok && CN[0][1].ok, c1ok = CN[1][0].ok && CN[1][1].ok;
      if (c0ok === c1ok) continue;
      var LEGS = c0ok ? CN[0] : CN[1], l6;
      for (l6 = 0; l6 < 2; l6++) {
        var LG = LEGS[l6];
        if (LG.hi - LG.lo < 0.1 || covered(LG.o, LG.c, LG.lo, LG.hi)) continue;
        /* v148 (plan 1000073345): the corner's line 5 cm off the line of the
           closets' wall beside it left slivers of 0.04 m2 as rooms once the
           hall in front was the living room's -- a line within 0.10 m of a
           wall it ends at, or runs on from, is put on that wall's line */
        for (var w6 = 0; w6 < MP.length; w6++) {
          var M6 = MP[w6];
          if (M6.sep || M6.o !== LG.o || Math.abs(M6.c - LG.c) > 0.10 || Math.abs(M6.c - LG.c) < 0.005) continue;
          if (M6.hi < LG.lo - 0.30 || M6.lo > LG.hi + 0.30) continue;
          LG.c = M6.c; nLegSnap++;
          break;
        }
        MP.push({ o: LG.o, c: _impR(LG.c), lo: _impR(LG.lo), hi: _impR(LG.hi), sep: true });
        nLeg++;
      }
    }
    if (nLeg) { mergeMP(); out.notes.push('DIAG: ' + nLeg + ' room line(s) where a slanted wall cuts a corner -- nothing built there, the rooms part as at the square corner' + (nLegSnap ? ('; ' + nLegSnap + ' put on the line of the wall beside it (v148)') : '')); }
    /* NO WALL ENDS IN THE MIDDLE OF A ROOM (v133, Marek: pricka nemuze koncit
       volne v prostoru, minimalne by mela pokracovat delenim mistnosti
       prerusovanou carou -- stalo se, pricka byla kousek od hlavni zdi a
       koncila v mistnosti). Every end of a wall that still meets nothing --
       the ends within 0.70 m of a wall were taken to it above -- goes on as a
       ROOM LINE to the first line across its way: a wall, a room line or the
       inner face of an outer wall. A stub under 0.50 m with nothing on it is
       not carried on: it goes. A wall that holds a door, or stands at one,
       stays as it is. */
    /* A CORNER, NOT A CROSS (v133, Marek: neni tam spojity roh -- agent by
       mel vzdy v takovych pripadech vyhodnotit rohovy spoj pricek). A wall
       the picture ends at a passage was read to its outer edge, a hand past
       the wall it turns into: the hall's bottom wall stood 0.15 m out into
       the hall's mouth. An end less than 0.20 m past a wall across it is
       taken back onto that wall's line, and a room line that started there
       goes with it. */
    var OVSH = 0;
    for (i = 0; i < MP.length; i++) {
      var mo = MP[i];
      if (mo.sep) continue;
      for (var eo = 0; eo < 2; eo++) {
        var vO = eo ? mo.hi : mo.lo, dO = eo ? 1 : -1, bestO = null;
        for (q = 0; q < MP.length; q++) {
          var po = MP[q];
          if (po === mo || po.sep || po.o === mo.o || mo.c < po.lo - 0.05 || mo.c > po.hi + 0.05) continue;
          var past = (vO - po.c) * dO;
          if (past > 0.01 && past <= 0.20 && (!bestO || past > bestO.past)) bestO = { c: po.c, past: past };
        }
        if (!bestO) continue;
        /* the stretch past it must hold nothing -- no door, no wall meeting it there */
        var lo8 = Math.min(vO, bestO.c), hi8 = Math.max(vO, bestO.c), held = false;
        for (q = 0; q < MP.length && !held; q++) {
          var hq = MP[q];
          if (hq === mo || hq.sep || hq.o === mo.o) continue;
          if (hq.c > lo8 + 0.01 && hq.c < hi8 - 0.01 && mo.c >= hq.lo - 0.05 && mo.c <= hq.hi + 0.05) held = true;
        }
        if (held || doorBy(mo, lo8, hi8, 0.3)) continue;
        for (q = 0; q < MP.length; q++) {
          var sq = MP[q];
          if (!sq.sep || sq.o !== mo.o || Math.abs(sq.c - mo.c) > 0.03) continue;
          if (Math.abs(sq.lo - vO) < 0.04) sq.lo = bestO.c; else if (Math.abs(sq.hi - vO) < 0.04) sq.hi = bestO.c;
        }
        if (eo) mo.hi = bestO.c; else mo.lo = bestO.c;
        OVSH++;
      }
    }
    if (OVSH) { mergeMP(); out.notes.push('CORNER: ' + OVSH + ' wall end(s) a hand past the wall they turn into were taken back onto it -- a corner, not a cross (v133)'); }
    var FREE = [], STUBS = [];
    function crossAt(m, v, dir) {
      var best = null, bd = 1e9, k;
      for (k = 0; k < SD.length; k++) {
        var sd = SD[k];
        if (sd.o === m.o || m.c < sd.lo - 0.01 || m.c > sd.hi + 0.01) continue;
        var inPlus = (sd.o === 'h') ? inside(m.c, sd.c + 0.05) : inside(sd.c + 0.05, m.c);
        var ff = inPlus ? sd.c + WALL : sd.c - WALL, dd = (ff - v) * dir;
        if (dd > 0.001 && dd < bd) { bd = dd; best = ff; }
      }
      for (k = 0; k < MP.length; k++) {
        var q9 = MP[k];
        if (q9 === m || q9.o === m.o || m.c < q9.lo - 0.05 || m.c > q9.hi + 0.05) continue;
        var dd2 = (q9.c - v) * dir;
        if (dd2 > 0.001 && dd2 < bd) { bd = dd2; best = q9.c; }
      }
      return (best == null) ? null : { at: best, d: bd };
    }
    function doorBy(m, lo, hi, reach) {
      for (var k = 0; k < DPT.length; k++) {
        var al = (m.o === 'h') ? DPT[k][0] : DPT[k][1], pe = Math.abs(((m.o === 'h') ? DPT[k][1] : DPT[k][0]) - m.c);
        if (pe < reach && al >= lo - 0.1 && al <= hi + 0.1) return true;
      }
      return false;
    }
    function wName(m) { return m.o + ' ' + (m.o === 'v' ? 'x' : 'z') + _impN(m.c); }
    var ADDL = [], SPURS = [];
    MP = MP.filter(function (m) {
      if (m.sep) return true;
      var fLo = !attached(m, m.lo), fHi = !attached(m, m.hi), len = m.hi - m.lo;
      if (!fLo && !fHi) return true;
      /* a wall at a door is its jamb, and the door is its end */
      if (doorBy(m, m.lo - 1.0, m.hi + 1.0, 0.35)) {
        /* A SPUR PAST THE LAST WALL ACROSS (v139, Marek: kus pricky, ktera
           konci v obyvaci casti). The water heater closet's wall held its
           door low down, and the wall the reader's open side added above it
           ran on in its line 0.75 m into the living room: the door kept the
           whole wall from the free end test. Each free end is looked at on
           its own now -- a stretch from the free end back to the first wall
           across, no longer than 1.20 m, with no door on it and none within
           1 m past the end, is taken back to that wall. */
        for (var eS = 0; eS < 2; eS++) {
          if (!(eS ? fHi : fLo)) continue;
          var vS = eS ? m.hi : m.lo, inS = eS ? -1 : 1, atS = null, bdS = 1e9, kS;
          for (kS = 0; kS < MP.length; kS++) {
            var qS = MP[kS];
            if (qS === m || qS.sep || qS.o === m.o || m.c < qS.lo - 0.05 || m.c > qS.hi + 0.05) continue;
            var dS = (qS.c - vS) * inS;
            if (dS > 0.03 && dS < bdS) { bdS = dS; atS = qS.c; }
          }
          if (atS == null || bdS <= 0.20 || bdS > 1.20) continue;
          if (doorBy(m, eS ? atS : vS - 1.0, eS ? vS + 1.0 : atS, 0.35)) continue;
          if (eS) m.hi = atS; else m.lo = atS;
          SPURS.push(wName(m) + ' ' + _impN(bdS) + ' m');
        }
        return true;
      }
      if (len < 0.5 || (fLo && fHi && len < 1.0)) { STUBS.push(wName(m) + ' ' + _impN(len) + ' m'); return false; }
      var ends = [];
      if (fLo) ends.push([m.lo, -1]);
      if (fHi) ends.push([m.hi, 1]);
      for (var e = 0; e < ends.length; e++) {
        /* AT A CLOSET'S CORNER the closet is closed across, not the room cut
           in two: the end stands within 0.35 m of a corner of a box the
           reader called a closet, so a wall goes along that box's side to
           the line it reaches -- the washer's niche of the render was open
           at the top and a room line ran on through the living room */
        var PE = (m.o === 'h') ? [ends[e][0], m.c] : [m.c, ends[e][0]], cc = null, kb, ix, iz;
        for (kb = 0; kb < ALLB.length && !cc; kb++) {
          var Bc = ALLB[kb];
          if (!CLOSETS[Bc.type]) continue;
          var CXS = [Bc.x0, Bc.x1], CZS = [Bc.z0, Bc.z1];
          for (ix = 0; ix < 2 && !cc; ix++) for (iz = 0; iz < 2 && !cc; iz++) {
            if (Math.hypot(CXS[ix] - PE[0], CZS[iz] - PE[1]) < 0.35) cc = { B: Bc, ox: CXS[1 - ix], oz: CZS[1 - iz] };
          }
        }
        if (cc) {
          var w2 = (m.o === 'v') ? { o: 'h', c: _impR(PE[1]), lo: Math.min(PE[0], cc.ox), hi: Math.max(PE[0], cc.ox) }
                                 : { o: 'v', c: _impR(PE[0]), lo: Math.min(PE[1], cc.oz), hi: Math.max(PE[1], cc.oz) };
          w2.lo = _impSnap(snapTo(w2, w2.lo, 0.35, true)); w2.hi = _impSnap(snapTo(w2, w2.hi, 0.35, true));
          if (m.o === 'v') m[ends[e][1] > 0 ? 'hi' : 'lo'] = w2.c; else m[ends[e][1] > 0 ? 'hi' : 'lo'] = w2.c;
          /* v135: a wall where the reader put a door in that side, else a
             room line -- an open niche, the washer's or a drop zone's, is
             still a way in */
          if (!doorBy(w2, w2.lo, w2.hi, 0.3)) w2.sep = true;
          if (w2.hi - w2.lo >= 0.3) { ADDL.push(w2); FREE.push(wName(m) + ' closed across "' + cc.B.name + '" ' + _impN(w2.hi - w2.lo) + ' m' + (w2.sep ? ' with a room line (open, no door read there)' : '')); continue; }
        }
        /* A CORNER LEFT OPEN (v195, ADU 1000075604: the closet's top wall
           stopped 0.45 m short of the closet's side, and a room line ran on
           from it through the whole living room). A wall across its way whose
           own end comes within 0.15 m of this line, no further than 0.70 m
           ahead and with no door between, is the other side of a corner: both
           are drawn on to meet. */
        var vE = ends[e][0], dE = ends[e][1], cq = null, cqd = 1e9, kq;
        for (kq = 0; kq < MP.length; kq++) {
          var Q9 = MP[kq];
          if (Q9 === m || Q9.sep || Q9.o === m.o) continue;
          var dq = (Q9.c - vE) * dE;
          if (!(dq > 0.03 && dq <= 0.70) || dq >= cqd) continue;
          var nearLo = Math.abs(Q9.lo - m.c) <= 0.15, nearHi = Math.abs(Q9.hi - m.c) <= 0.15;
          if (!nearLo && !nearHi) continue;
          cq = { Q: Q9, lo: nearLo && !(nearHi && Math.abs(Q9.hi - m.c) < Math.abs(Q9.lo - m.c)) }; cqd = dq;
        }
        if (cq && !doorBy(m, Math.min(vE, cq.Q.c), Math.max(vE, cq.Q.c), 0.3)) {
          if (dE > 0) m.hi = cq.Q.c; else m.lo = cq.Q.c;
          if (cq.lo) cq.Q.lo = Math.min(cq.Q.lo, m.c); else cq.Q.hi = Math.max(cq.Q.hi, m.c);
          FREE.push(wName(m) + ' ' + _impN(cqd) + ' m on to the corner with ' + wName(cq.Q));
          continue;
        }
        var cr = crossAt(m, ends[e][0], ends[e][1]);
        if (!cr || cr.d < 0.05) continue;
        var a9 = Math.min(ends[e][0], cr.at), b9 = Math.max(ends[e][0], cr.at);
        if (hallLine(m.o, m.c, a9, b9)) { nHallLine++; continue; }
        /* A ROOM LINE NEVER CUTS ONE NAMED ROOM IN TWO (v195, Marek on ADU
           1000075604: nedava smysl to rozdeleni na hall / living -- klidne tam
           mohl byt jen living room). A line that would run through the box
           of one room the plan names, that room on both sides of it, would
           make a piece with no name of its own (HALL): the wall ends free. */
        /* well inside it: a quarter of the box (0.60 m at least) on both
           sides of the line -- a box drawn a little past a wall does not
           count */
        var inOne = null, hits = 0, tq;
        for (tq = 0.25; tq <= 0.76; tq += 0.25) {
          var al9 = a9 + (b9 - a9) * tq, hitB = null, kn;
          for (kn = 0; kn < ALLB.length && !hitB; kn++) {
            var BN = ALLB[kn];
            if (!BN || !BN.name || CLOSETS[BN.type] || /porch|patio|deck|terrace|balcony|garden|yard/i.test(String(BN.type || ''))) continue;
            var pa0 = (m.o === 'h') ? BN.z0 : BN.x0, pa1 = (m.o === 'h') ? BN.z1 : BN.x1, aa0 = (m.o === 'h') ? BN.x0 : BN.z0, aa1 = (m.o === 'h') ? BN.x1 : BN.z1;
            var marg = Math.max(0.60, 0.25 * (pa1 - pa0));
            if (m.c - pa0 >= marg && pa1 - m.c >= marg && al9 >= aa0 - 0.05 && al9 <= aa1 + 0.05) hitB = BN;
          }
          if (hitB) { hits++; inOne = hitB.name; }
        }
        if (hits >= 2) { FREE.push(wName(m) + ' left free -- a room line would cut "' + inOne + '" in two'); continue; }
        ADDL.push({ o: m.o, c: m.c, lo: _impR(a9), hi: _impR(b9), sep: true });
        FREE.push(wName(m) + ' ' + _impN(cr.d) + ' m on');
      }
      return true;
    });
    for (i = 0; i < ADDL.length; i++) MP.push(ADDL[i]);
    if (ADDL.length) mergeMP();
    if (FREE.length) out.notes.push('FREE END: ' + FREE.length + ' wall end(s) meeting nothing were carried on as room lines to the next line across, or closed across the closet whose corner they stand at (v133), or drawn on to the corner they leave open, or left free where the line would cut one named room in two (v195): ' + FREE.join(', '));
    if (SPURS.length) out.notes.push('FREE END: ' + SPURS.length + ' wall end(s) running on past the last wall across them, with no door on that stretch, were taken back to that wall (v139): ' + SPURS.join(', '));
    if (STUBS.length) out.notes.push('FREE END: ' + STUBS.length + ' stub(s) meeting nothing at one end and under 0.50 m (or a loose piece under 1 m) were left out (v133): ' + STUBS.join(', '));
    /* AT LEAST 0.60 M BETWEEN TWO WALLS (v133, Marek: minimalni vzdalenost
       pricky od druhe pricky 0,6 m -- jinak by to byl nejaky nesmysl; i
       pokud by neco takoveho v pudorysu bylo, muzeme to nahradit logictejsi
       stavbou / danou pricku vymazat). Two walls side by side, facing each
       other over 0.30 m or more, closer than 0.60 m centre to centre, make a
       strip nobody uses. Under 0.40 m it is one wall read twice, or a strip:
       the shorter goes. From 0.40 to 0.60 m -- a linen closet drawn 0.55 m
       wide -- the shorter is moved out to 0.60 m when the room behind it
       keeps 0.90 m, the walls that end on it going with it, and is taken out
       when it does not. One that holds a door, where the other does not, is
       the one that stays. An outer wall counts the same, 0.55 m from its
       inner face to the wall's middle. */
    var SPACE = [], MOVED = [];
    function beyond(m, cNew, dir) {
      var bd = 1e9, k;
      for (k = 0; k < MP.length; k++) {
        var q8 = MP[k];
        if (q8 === m || q8.o !== m.o || Math.min(q8.hi, m.hi) - Math.max(q8.lo, m.lo) < 0.1) continue;
        var d8 = (q8.c - cNew) * dir;
        if (d8 > -0.001 && d8 < bd) bd = d8;
      }
      for (k = 0; k < SD.length; k++) {
        var sd8 = SD[k];
        if (sd8.o !== m.o || Math.min(sd8.hi, m.hi) - Math.max(sd8.lo, m.lo) < 0.1) continue;
        var in8 = (sd8.o === 'h') ? inside((m.lo + m.hi) / 2, sd8.c + 0.05) : inside(sd8.c + 0.05, (m.lo + m.hi) / 2);
        var f8 = in8 ? sd8.c + WALL : sd8.c - WALL, d9 = (f8 - cNew) * dir;
        if (d9 > -0.001 && d9 < bd) bd = d9;
      }
      return bd;
    }
    function moveWall(m, cNew) {
      var k;
      for (k = 0; k < MP.length; k++) {
        var p8 = MP[k];
        if (p8 === m || p8.o === m.o || p8.c < m.lo - 0.05 || p8.c > m.hi + 0.05) continue;
        if (Math.abs(p8.lo - m.c) < 0.04) p8.lo = cNew;
        else if (Math.abs(p8.hi - m.c) < 0.04) p8.hi = cNew;
        if (p8.lo > p8.hi) { var sw8 = p8.lo; p8.lo = p8.hi; p8.hi = sw8; }
      }
      MOVED.push({ o: m.o, from: m.c, to: cNew, lo: m.lo, hi: m.hi });
      m.c = cNew;
    }
    var again9 = true, guard9 = 0;
    while (again9 && guard9++ < 40) {
      again9 = false;
      for (var a9i = 0; a9i < MP.length && !again9; a9i++) {
        var A9 = MP[a9i];
        if (A9.sep) continue;
        /* against the other walls */
        for (var b9i = 0; b9i < MP.length && !again9; b9i++) {
          var B9 = MP[b9i];
          if (b9i === a9i || B9.sep || B9.o !== A9.o) continue;
          var dc9 = Math.abs(A9.c - B9.c), ov9 = Math.min(A9.hi, B9.hi) - Math.max(A9.lo, B9.lo);
          if (dc9 < 0.001 || dc9 >= 0.6 - 0.005 || ov9 < 0.3) continue;
          var dA = doorBy(A9, A9.lo, A9.hi, 0.3), dB = doorBy(B9, B9.lo, B9.hi, 0.3);
          var M9 = ((A9.hi - A9.lo) <= (B9.hi - B9.lo)) ? A9 : B9;
          if (dA && !dB) M9 = B9; else if (dB && !dA) M9 = A9;
          var F9 = (M9 === A9) ? B9 : A9, dir9 = (M9.c > F9.c) ? 1 : -1, cN9 = _impSnap(F9.c + dir9 * 0.6);
          if (Math.abs(cN9 - F9.c) < 0.6 - 0.005) cN9 = _impR(cN9 + dir9 * IMP_SNAP);
          var room9 = beyond(M9, cN9, dir9);
          var midA = (M9.o === 'h') ? (M9.lo + M9.hi) / 2 : cN9, midB = (M9.o === 'h') ? cN9 : (M9.lo + M9.hi) / 2;
          if (dc9 < 0.4) {
            /* under 0.40 m it is one wall read twice, or a strip -- one goes */
            if (doorBy(M9, M9.lo, M9.hi, 0.3)) continue;
            SPACE.push(wName(M9) + ' ' + _impN(dc9) + ' m from ' + wName(F9) + ' -- taken out (one wall read twice, or a strip)');
            MP.splice(MP.indexOf(M9), 1);
          } else if (room9 >= 0.9 && inside(midA, midB)) {
            SPACE.push(wName(M9) + ' ' + _impN(dc9) + ' m from ' + wName(F9) + ' -- moved to ' + _impN(Math.abs(cN9 - F9.c)));
            moveWall(M9, cN9);
          } else if (!doorBy(M9, M9.lo, M9.hi, 0.3)) {
            SPACE.push(wName(M9) + ' ' + _impN(dc9) + ' m from ' + wName(F9) + ' -- taken out (no room to move it)');
            MP.splice(MP.indexOf(M9), 1);
          } else continue;
          again9 = true;
        }
        if (again9) break;
        /* against the outer walls */
        for (var s9 = 0; s9 < SD.length && !again9; s9++) {
          var S9 = SD[s9];
          if (S9.o !== A9.o || Math.min(S9.hi, A9.hi) - Math.max(S9.lo, A9.lo) < 0.3) continue;
          var in9 = (S9.o === 'h') ? inside((A9.lo + A9.hi) / 2, S9.c + 0.05) : inside(S9.c + 0.05, (A9.lo + A9.hi) / 2);
          var f9 = in9 ? S9.c + WALL : S9.c - WALL, dd9 = Math.abs(A9.c - f9);
          if (dd9 < 0.02 || dd9 >= 0.55 - 0.005) continue;
          var dirS = (A9.c > f9) ? 1 : -1, cS = _impSnap(f9 + dirS * 0.55);
          if (Math.abs(cS - f9) < 0.55 - 0.005) cS = _impR(cS + dirS * IMP_SNAP);
          if (dd9 < 0.35) {
            if (doorBy(A9, A9.lo, A9.hi, 0.3)) continue;
            SPACE.push(wName(A9) + ' ' + _impN(dd9) + ' m from the outer wall -- taken out (a strip)');
            MP.splice(a9i, 1);
          } else if (beyond(A9, cS, dirS) >= 0.9) {
            SPACE.push(wName(A9) + ' ' + _impN(dd9) + ' m from the outer wall -- moved to ' + _impN(Math.abs(cS - f9)));
            moveWall(A9, cS);
          } else if (!doorBy(A9, A9.lo, A9.hi, 0.3)) {
            SPACE.push(wName(A9) + ' ' + _impN(dd9) + ' m from the outer wall -- taken out (no room to move it)');
            MP.splice(a9i, 1);
          } else continue;
          again9 = true;
        }
      }
    }
    if (SPACE.length) { mergeMP(); out.notes.push('SPACE: ' + SPACE.length + ' wall(s) closer than 0.60 m to another were moved or taken out (v133): ' + SPACE.join(', ')); }
    out.moved = MOVED;
    /* v148 (plan 1000073345): a room line added after the tidy -- a closet's
       side closed with a line, a slanted wall's corner -- 5 cm off the line
       of the wall it runs on from left slivers of 0.04 m2 as rooms. Every
       room line within 0.10 m of a wall's line, overlapping it or ending
       within 0.30 m of it, is put on that line; the walls stay. */
    var nSepSnap = 0;
    for (i = 0; i < MP.length; i++) {
      var Ls = MP[i];
      if (!Ls.sep) continue;
      for (q = 0; q < MP.length; q++) {
        var Ws = MP[q];
        if (Ws.sep || Ws.o !== Ls.o) continue;
        var dcs = Math.abs(Ws.c - Ls.c);
        if (dcs < 0.001 || dcs > 0.10) continue;
        if (Math.max(Ws.lo, Ls.lo) - Math.min(Ws.hi, Ls.hi) > 0.30) continue;
        Ls.c = Ws.c; nSepSnap++;
        break;
      }
    }
    if (nSepSnap) { mergeMP(); out.notes.push('TIDY: ' + nSepSnap + ' room line(s) a hand off a wall\'s line put on it (v148)'); }
    for (i = 0; i < MP.length; i++) {
      var opR = { o: MP[i].o, c: _impR(MP[i].c), s: _impR(MP[i].lo), e: _impR(MP[i].hi) };
      if (MP[i].sep) opR.sep = true;
      out.partitions.push(opR);
    }

    /* THE DOORS AND WINDOWS: each on the wall it is near -- a partition or a
       side of the outline -- and a door the width of one we sell */
    /* the units the plan is drawn in, when the reader could tell (v100),
       otherwise the project's */
    var IMPL = (opts.units === 'imperial') || (opts.units !== 'metric' && H && H.isMetric && !H.isMetric());
    var SET = IMPL ? [28 * DRAW.IN, 30 * DRAW.IN, 32 * DRAW.IN] : DOOR_SET_M.slice();
    /* sidesOnly (v110): a window, and a door the reader says is to the
       outside, go on the outline even where a partition or a room line ends
       right at their middle -- the 6040 window between the kitchen and the
       living room sat where the room line met the outer wall, was taken for
       a window in that line and left out (Marek: chybi okno mezi kitchen a
       living room) */
    function wallNear(x, z, sidesOnly) {
      var best = null, bd = IMP_REACH, k;
      for (k = 0; !sidesOnly && k < out.partitions.length; k++) {
        var pp = out.partitions[k], al = pp.o === 'h' ? x : z, pe = Math.abs((pp.o === 'h' ? z : x) - pp.c);
        if (al < pp.s - 0.1 || al > pp.e + 0.1) continue;
        if (pe < bd) { bd = pe; best = { t: 'part', i: k, u: al, o: pp.o, d: pe }; }
      }
      for (k = 0; k < SD.length; k++) {
        var sd = SD[k], al2 = sd.o === 'h' ? x : z, pe2 = Math.abs((sd.o === 'h' ? z : x) - sd.c);
        if (al2 < sd.lo - 0.1 || al2 > sd.hi + 0.1) continue;
        if (pe2 < bd) {
          bd = pe2;
          var dir = (sd.o === 'h') ? (sd.b[0] >= sd.a[0] ? 1 : -1) : (sd.b[1] >= sd.a[1] ? 1 : -1);
          best = { t: 'side', i: k, off: (al2 - (sd.o === 'h' ? sd.a[0] : sd.a[1])) * dir, len: sd.hi - sd.lo, d: pe2 };
        }
      }
      return best;
    }
    function units(w) { return (raw.units === 'm') ? Number(w) : Number(w) * s; }
    var CLOSETDOORS = [], nSepDoor = 0, LBL = [], OUTD = [], PAIRN = [], DBL = [], nRMV = 0, OUTSW = [];
    for (i = 0; raw.doors && i < raw.doors.length; i++) {
      var toOut = (raw.doors[i].rooms || []).some(function (t) { return /^(outside|outdoors?|exterior|garden|yard|porch|deck|patio)$/i.test(String(t).trim()); });
      var dp = T(raw.doors[i].at), dw = units(raw.doors[i].w);
      /* v133: a door in a wall the SPACE rule moved goes with it */
      for (q = 0; q < MOVED.length; q++) {
        var mv = MOVED[q], alM = mv.o === 'h' ? dp[0] : dp[1], acM = mv.o === 'h' ? dp[1] : dp[0];
        if (Math.abs(acM - mv.from) < 0.35 && alM >= mv.lo - 0.1 && alM <= mv.hi + 0.1) { if (mv.o === 'h') dp[1] = mv.to; else dp[0] = mv.to; }
      }
      if (toOut && _impRMove(RMOVED, dp)) nRMV++;
      var wn = (toOut ? wallNear(dp[0], dp[1], true) : null) || wallNear(dp[0], dp[1]);
      var lw = _impDoorLabelW(raw.doors[i].label);
      if (lw) { LBL.push('"' + raw.doors[i].label + '" ' + _impN(lw) + (Math.abs(lw - dw) > 0.05 ? (' (drawn ' + _impN(dw) + ')') : '')); dw = lw; }
      /* A DOOR IN A SLANTED WALL STAYS IN IT (v125): nearer to a slanted
         wall than to any square one, it is that wall's door */
      var dgH = toOut ? null : _impDiagDoorAt(out.diag, dp, wn ? wn.d : null);
      if (dgH) {
        _impDiagDoorAdd(out.diag[dgH.k], dgH.u, dw, String(raw.doors[i].kind || 'hinged'), raw.doors[i].hinge ? T(raw.doors[i].hinge) : null,
                        raw.doors[i].opensTo ? T(raw.doors[i].opensTo) : null, raw.doors[i].label || '', SET, !IMPL);
        continue;
      }
      if (!wn) { out.notes.push('a door at ' + _impN(dp[0]) + ',' + _impN(dp[1]) + ' is on no wall -- left out'); continue; }
      /* a room line has nothing to hang a door in (v105) */
      if (wn.t === 'part' && out.partitions[wn.i].sep) { nSepDoor++; continue; }
      if (wn.t === 'part') {
        var bw = SET[0], bdd = 1e9, k3;
        for (k3 = 0; k3 < SET.length; k3++) if (Math.abs(SET[k3] - dw) < bdd) { bdd = Math.abs(SET[k3] - dw); bw = SET[k3]; }
        var pt = out.partitions[wn.i], kd = String(raw.doors[i].kind || 'hinged'), dq = { part: wn.i, u: 0, w: 0, sw: 0 }, hpOv = null, opOv = null;
        /* A PAIR IS 1.20 M AND MORE (v134, Marek: dvoukridle by mely byt
           minimalne 1,2 metru, spise 1,4 metru). The linen closet's door,
           0.45 m, was seen as two leaves in the picture and became a pair
           1.22 m wide across two walls. A pair read narrower than 1.00 m is
           one leaf; one the PICTURE made a pair of has to be 1.35 m to stay
           one -- under that it goes back to what the reader said. */
        if (kd === 'double') {
          var rdP = raw.doors[i], picP = !!(rdP.pairPx && rdP.kindRead && rdP.kindRead !== 'double');
          if ((picP && dw < 1.35) || dw < 1.0) {
            kd = picP ? String(rdP.kindRead) : 'hinged';
            if (picP) { hpOv = rdP.hingeRead || null; opOv = rdP.opensRead || null; }
            PAIRN.push(_impN(dw) + ' m ' + (picP ? 'seen as a pair in the picture -- back to the reader\'s ' + kd : 'read as a pair -- one leaf'));
          }
        }
        /* WHAT KIND OF DOOR (v101): a doorway keeps its own width, as the
           plan's doorway can be any size; the rest are doors we sell */
        /* THE CLOSET DOORS (v106): a bypass, a bifold and a hinged pair are
           doors of their own now, in the widths the catalogue sells -- the
           nearest to the plan's, and no wider than the wall that holds it */
        var ck = (kd === 'bypass') ? 'bypass' : ((kd === 'bifold') ? 'bifold' : ((kd === 'double') ? 'pair' : ''));
        if (ck) {
          var KW = _closetKindWidths(ck, !IMPL), cap = pt.e - pt.s - 0.1, kb = -1, kbd = 1e9, k4;
          for (k4 = 0; k4 < KW.length; k4++) if (KW[k4] <= cap + 0.005 && Math.abs(KW[k4] - dw) < kbd) { kbd = Math.abs(KW[k4] - dw); kb = k4; }
          if (kb < 0) { out.notes.push('a ' + ck + ' ' + _impN(dw) + ' m wide does not fit its wall -- made an ordinary door'); ck = ''; }
          else { bw = KW[kb]; dq.kind = ck; CLOSETDOORS.push(ck); if (kbd >= 0.025) out.notes.push('a ' + ck + ' ' + _impN(dw) + ' m wide became ' + _impN(bw)); }
        }
        if (ck) { /* the width is settled */ }
        else if (kd === 'opening') { bw = Math.max(0.60, _impSnap(dw)); dq.pass = true; }
        else if ((kd === 'sliding' || kd === 'pocket') && dw > SET[SET.length - 1] + 0.10) bw = _impSnap(dw);
        else if (bdd >= 0.025) out.notes.push('a door ' + _impN(dw) + ' m wide became ' + _impN(bw));
        if (!ck && (kd === 'sliding' || kd === 'pocket')) dq.slide = 1;
        if (!ck) bw = Math.min(bw, Math.max(0.6, pt.e - pt.s - 0.1));
        var u = _impSnap(Math.max(pt.s + bw / 2, Math.min(pt.e - bw / 2, wn.u)));
        dq.u = _impR(u); dq.w = _impR(bw);
        dq.p0 = dp; dq.nm = raw.doors[i].rooms || []; dq.op0 = raw.doors[i].opensTo ? T(raw.doors[i].opensTo) : null;
        /* THE SWING, as the reader drew it: bit 0 the leaf hangs on the far
           end of the opening, bit 1 it opens to +z (a wall along x) or to -x
           (a wall along z) -- the asset's own convention */
        if (!dq.pass && !dq.slide && dq.kind !== 'bypass') {
          var hp = (hpOv || raw.doors[i].hinge) ? T(hpOv || raw.doors[i].hinge) : null, op = (opOv || raw.doors[i].opensTo) ? T(opOv || raw.doors[i].opensTo) : null;
          if (hp && (((pt.o === 'h') ? hp[0] : hp[1]) > u + 0.02)) dq.sw |= 1;
          if (op) {
            var acr = (pt.o === 'h') ? op[1] : op[0];
            if ((pt.o === 'h') ? (acr > pt.c + 0.02) : (acr < pt.c - 0.02)) dq.sw |= 2;
          }
        }
        out.doors.push(dq);
      } else {
        var ew = Math.max(0.7, _impSnap(dw));
        var eo = { side: wn.i, off: _impR(_impSnap(Math.max(ew / 2, Math.min(wn.len - ew / 2, wn.off)))), w: _impR(ew), kind: 'door', sill: 0 };
        /* A GLAZED DOOR AS THE PLAN DRAWS IT (v136): a slider or an HS portal
           read as sliding takes a slider of the catalogue, French doors read
           as a pair take a hinged glazed door -- not merely the nearest width */
        var kOut = String(raw.doors[i].kind || '');
        if (kOut === 'sliding' || kOut === 'pocket') eo.pref = 'slide';
        else if (kOut === 'double') {
          /* A PAIR TO THE OUTSIDE (v142, Marek: v puvodnim jsou dvojite
             kridla, agent tam dal jednokridle -- skoro by se vice hodily HS
             portaly; u STORAGE dole zase dvojkridle dvere). The glazed group
             has no pair, so French doors took the 1.00 m single leaf. A pair
             from a store, a garage or a utility room takes the catalogue's
             double exterior door; a pair from a room people live in takes
             the HS slider nearest its width -- two leaves, glazed. */
          var rIn = (raw.doors[i].rooms || []).filter(function (t9) { return !/^(outside|outdoors?|exterior|garden|yard|porch|deck|patio|balcony|terrace)$/i.test(String(t9).trim()); })[0] || '';
          var tIn = typeOfName(rIn);
          eo.pref = /^(storage|utility|garage|closet|laundry|pantry|shed|workshop|mechanical)$/.test(tIn) ? 'pair' : 'slide';
          eo.dbl = true;
          DBL.push('"' + (rIn || '?') + '" ' + _impN(dw) + ' m -> ' + (eo.pref === 'pair' ? 'a double exterior door' : 'an HS slider'));
        }
        /* WHICH WAY IT OPENS (v149, Marek: vstup do storage -- dvoukridle
           dvere ven). Where the reader says which side the leaf opens to,
           that side: out when the point lies outside the outline. With
           nothing said, a pair to a store, a garage or a utility room opens
           out -- inside, its leaves would take the room they close -- and
           every other outer door opens in, as before. */
        var oTo9 = raw.doors[i].opensTo ? T(raw.doors[i].opensTo) : null;
        var dpR9 = T(raw.doors[i].at);
        /* a point on the door itself says nothing (a pair is read with its
           middle there) */
        if (oTo9 && !(isFinite(oTo9[0]) && isFinite(oTo9[1]) && Math.hypot(oTo9[0] - dpR9[0], oTo9[1] - dpR9[1]) >= 0.15)) oTo9 = null;
        if (oTo9) { if (!inside(oTo9[0], oTo9[1])) { eo.out = true; OUTSW.push(_impN(dw) + ' m "' + (raw.doors[i].rooms || []).join(' / ') + '" -- the plan draws it opening out'); } }
        else if (eo.pref === 'pair') { eo.out = true; if (DBL.length) DBL[DBL.length - 1] += ', opening out (v149)'; }
        out.openings.push(eo);
        OUTD.push({ op: eo, side: wn.i, x: dp[0], z: dp[1], w: dw, nm: raw.doors[i].rooms || [], op0: raw.doors[i].opensTo ? T(raw.doors[i].opensTo) : null });
      }
    }
    if (DBL.length) out.notes.push('DOUBLE: ' + DBL.length + ' pair(s) of doors to the outside (v142): ' + DBL.join(', '));
    if (OUTSW.length) out.notes.push('OUTSWING: ' + OUTSW.length + ' outer door(s) open out of the building as the plan draws them (v149): ' + OUTSW.join(', '));
    /* THE NICHE BECOMES AN ENTRY (v114). A niche the box took in -- the
       covered entry between the water heater closet and the store -- has
       the front door in its back wall, and that door is now a door inside
       the house. The niche gets the outer door in its open side, where one
       walks in from outside, and the name ENTRY once the rooms are read;
       a niche with no door to the outside is named in the log only. */
    var NICHE = [];
    for (i = 0; i < RECESS.length; i++) {
      var RC = RECESS[i], rcA = Math.min(RC.c, RC.c + RC.inw * RC.depth), rcB = Math.max(RC.c, RC.c + RC.inw * RC.depth), rcBack = RC.c + RC.inw * RC.depth;
      var inRc = function (x, z) {
        var al = (RC.o === 'h') ? x : z, ac = (RC.o === 'h') ? z : x;
        return al >= RC.lo - 0.3 && al <= RC.hi + 0.3 && ac >= rcA - 0.3 && ac <= rcB + 0.3;
      };
      var bestN = null, kN;
      for (kN = 0; raw.doors && kN < raw.doors.length; kN++) {
        var toON = (raw.doors[kN].rooms || []).some(function (t) { return /^(outside|outdoors?|exterior|garden|yard|porch|deck|patio)$/i.test(String(t).trim()); });
        if (!toON) continue;
        var dpN = T(raw.doors[kN].at);
        if (!inRc(dpN[0], dpN[1])) continue;
        var onBk = Math.abs(((RC.o === 'h') ? dpN[1] : dpN[0]) - rcBack) < 0.45;
        if (!bestN || (onBk && !bestN.onBk)) bestN = { p: dpN, w: units(raw.doors[kN].w), onBk: onBk };
      }
      var nmN = '';
      for (kN = 0; raw.rooms && kN < raw.rooms.length; kN++) {
        var rpN = T(raw.rooms[kN].at);
        if (inRc(rpN[0], rpN[1]) && Math.abs(((RC.o === 'h') ? rpN[1] : rpN[0]) - RC.c) < RC.depth) { nmN = String(raw.rooms[kN].label || raw.rooms[kN].type || 'room'); break; }
      }
      var NI = { o: RC.o, lo: RC.lo, hi: RC.hi, c: RC.c, depth: RC.depth, inw: RC.inw, name: nmN, door: false, outd: false, al0: RC.lo, al1: RC.hi, ac0: rcA, ac1: rcB,
                 x: (RC.o === 'h') ? (RC.lo + RC.hi) / 2 : RC.c + RC.inw * RC.depth / 2, z: (RC.o === 'h') ? RC.c + RC.inw * RC.depth / 2 : (RC.lo + RC.hi) / 2 };
      /* v139: a porch the box took in that holds the plan's front door in its
         back wall is the way into the house -- it gets the outer door too;
         plan 1000073345 came out with no front door at all, its porch walled
         in and the living room reached only through the porch */
      var outdN = !!(nmN && IMP_OUTDOOR.test(nmN));
      /* v148 (Marek, plan 1000073345: vstup pred vchod je reseny jako
         otevreny pruchod, agent ho nahradil dvermi -- pokud nemame pruchod,
         dat aspon posuvne proskleni, obvodovou stenu uskocenou neumime): an
         open side 1.4 m wide or more gets the glazed slider nearest its
         width, in its middle; a narrower one the outer door as before */
      var mouthN = RC.hi - RC.lo, glzN = (mouthN >= 1.4);
      if (bestN && (!outdN || bestN.onBk)) {
        var alN = glzN ? (RC.lo + RC.hi) / 2 : Math.max(RC.lo + 0.5, Math.min(RC.hi - 0.5, (RC.o === 'h') ? bestN.p[0] : bestN.p[1]));
        var wnN = (RC.o === 'h') ? wallNear(alN, RC.c, true) : wallNear(RC.c, alN, true);
        if (wnN && wnN.t === 'side') {
          var ewN = glzN ? _impSnap(mouthN - 0.2) : Math.max(0.8, Math.min(1.0, _impSnap(bestN.w)));
          var opN = { side: wnN.i, off: _impR(_impSnap(Math.max(ewN / 2, Math.min(wnN.len - ewN / 2, wnN.off)))), w: _impR(ewN), kind: 'door', sill: 0 };
          if (glzN) opN.pref = 'slide';
          out.openings.push(opN);
          NI.door = true;
          NI.outd = outdN;
          NI.glz = glzN;
        }
      }
      var hbN = out.outline.reduce(function (m9, p9) { return { x0: Math.min(m9.x0, p9[0]), x1: Math.max(m9.x1, p9[0]), z0: Math.min(m9.z0, p9[1]), z1: Math.max(m9.z1, p9[1]) }; }, { x0: 1e9, x1: -1e9, z0: 1e9, z1: -1e9 });
      var pctN = Math.round(mouthN * RC.depth / Math.max(0.01, (hbN.x1 - hbN.x0) * (hbN.z1 - hbN.z0)) * 1000) / 10;
      NICHE.push(NI);
      out.notes.push('NICHE: ' + _impN(RC.hi - RC.lo) + ' x ' + _impN(RC.depth) + ' m open to the outside is inside the box now (' + pctN + ' % of the house' + (pctN < 5 ? ', under 5 %' : '') + ')' +
                     (nmN ? (' -- "' + nmN + '"') : '') + (NI.door ? (' -- it is the entry: ' + (NI.glz ? 'sliding glazing in its open side (v148)' : 'an outer door in its open side') + ', the plan\'s front door inside it' + (NI.outd ? ' (an outdoor space with the front door in it, v139)' : '')) :
                     (nmN && IMP_OUTDOOR.test(nmN) ? ' -- an outdoor space, built in for now' : ' -- no door to the outside in it, walled off')));
    }
    /* THE DOORS WHERE THE PLAN DRAWS THEM (v108, Marek: pruchody bez dveri
       mas na nekolika mistech, i kdyz tam maji byt dvere -- dvere musi byt
       presne na mistech, kde jsou zaznaceny; nemusi to byt 1:1, ale musi to
       sedet). A hinged door is drawn OPEN: the leaf stands square to its
       wall and its arc sweeps the room, and the reader took the line of the
       open leaf for the wall more than once -- the bedroom doors off the hall
       landed on the wall between each bedroom and the closet beside the hall,
       and the bathroom door on the outer wall the open leaf lies against.
       The hall then had no door to either bedroom, so the closing below put
       a doorway in each, and the house came out with a door into a laundry
       closet, a door into a linen closet and a front door into a bathroom. */
    var MOVED = [], NEWD = [], WALLONLY = [], WALLC = [], FRONTS = [], PSQ = [], V2 = (opts.variant === 2);
    var NOEXT = { bathroom: 1, wc: 1, closet: 1, wardrobe: 1, walk_in_closet: 1, pantry: 1 };
    function dXZ(d) { var pp = out.partitions[d.part]; return (pp.o === 'h') ? [d.u, pp.c] : [pp.c, d.u]; }
    /* the smallest box the reader gave that holds a point */
    function boxAt(x, z) {
      var best = null, ba = 1e9, k;
      for (k = 0; k < ALLB.length; k++) {
        var B = ALLB[k];
        if (x < B.x0 - 0.05 || x > B.x1 + 0.05 || z < B.z0 - 0.05 || z > B.z1 + 0.05) continue;
        var ar = (B.x1 - B.x0) * (B.z1 - B.z0);
        if (ar < ba) { ba = ar; best = B; }
      }
      return best;
    }
    /* how many doors stand on the edges of a box, one door left out */
    function doorsOnBox(B, skip) {
      var n = 0, k;
      for (k = 0; k < out.doors.length; k++) {
        if (out.doors[k] === skip) continue;
        var dp2 = dXZ(out.doors[k]), pp = out.partitions[out.doors[k].part];
        var onV = pp.o === 'v' && (Math.abs(dp2[0] - B.x0) < 0.35 || Math.abs(dp2[0] - B.x1) < 0.35) && dp2[1] > B.z0 - 0.1 && dp2[1] < B.z1 + 0.1;
        var onH = pp.o === 'h' && (Math.abs(dp2[1] - B.z0) < 0.35 || Math.abs(dp2[1] - B.z1) < 0.35) && dp2[0] > B.x0 - 0.1 && dp2[0] < B.x1 + 0.1;
        if (onV || onH) n++;
      }
      return n;
    }
    /* a door hung on partition pi beside the corner whose face is at cv: the
       leaf on the corner's jamb, so it opens back against that wall, the way
       the plans draw it, and into box B */
    function hang(d, pi, w, cv, high, B) {
      var pp = out.partitions[pi];
      w = Math.min(w, Math.max(0.60, pp.e - pp.s - 0.10));
      var u = _impSnap(high ? cv - 0.10 - w / 2 : cv + 0.10 + w / 2);
      u = Math.max(pp.s + w / 2 + 0.02, Math.min(pp.e - w / 2 - 0.02, u));
      d.part = pi; d.u = _impR(u); d.w = _impR(w); d.sw = high ? 1 : 0;
      delete d.pass;
      if (B) {
        var bc = (pp.o === 'h') ? (B.z0 + B.z1) / 2 : (B.x0 + B.x1) / 2;
        if ((pp.o === 'h') ? (bc > pp.c) : (bc < pp.c)) d.sw |= 2;
      }
      return d;
    }
    function nearSet(w) { var b = SET[0], k; for (k = 0; k < SET.length; k++) if (Math.abs(SET[k] - w) < Math.abs(b - w)) b = SET[k]; return b; }
    /* the front of closet box cl on partition pi, toward room box rm, along
       the closed edge cq (v162): a bypass, else a bifold, the widest sold
       that fits the edge -- or null when none does */
    function closetFront(cl, rm, pi, cq) {
      var pt = out.partitions[pi], lo = Math.max(cq.lo, pt.s), hi = Math.min(cq.hi, pt.e), cap = hi - lo - 0.10, KD = ['bypass', 'bifold'], kd = '', w = 0, k, j;
      for (k = 0; k < KD.length && !kd; k++) {
        var KW = _closetKindWidths(KD[k], !IMPL);
        for (j = KW.length - 1; j >= 0; j--) if (KW[j] <= cap + 0.005) { kd = KD[k]; w = KW[j]; break; }
      }
      if (!kd) return null;
      var u = _impSnap((lo + hi) / 2);
      u = Math.max(pt.s + w / 2 + 0.02, Math.min(pt.e - w / 2 - 0.02, u));
      var d = { part: pi, u: _impR(u), w: _impR(w), sw: 0, kind: kd, nm: [rm.name, cl.name], front: true };
      if (kd !== 'bypass') {
        var bc = (pt.o === 'h') ? (rm.z0 + rm.z1) / 2 : (rm.x0 + rm.x1) / 2;
        if ((pt.o === 'h') ? (bc > pt.c) : (bc < pt.c)) d.sw |= 2;
      }
      return d;
    }
    /* EACH DOOR IN THE WALL BETWEEN ITS OWN TWO ROOMS (v109, Marek: kde je
       symbol oteviranych dveri, musi byt vlozene interierove dvere -- to by
       mely byt zakladni pravidla). The reader names the two rooms each door
       joins (w1.84). A door standing in a wall with other rooms on its sides
       goes to the wall between the two it names, at the point of that wall
       nearest to where it was read: hung on the end of that wall nearer to
       it, opening into the room the reader saw it open into. A door read on
       the outer wall that joins two rooms comes inside the same way. A name
       is matched to the room boxes by label, then by type; of two rooms of
       one name the one nearer the door. */
    var PRI = { bathroom: 3, wc: 3, bedroom: 2, kids: 2, office: 2, closet: 1, wardrobe: 1, walk_in_closet: 1, pantry: 1, storage: 1, laundry: 1, utility: 1 };
    function normN(t) { return String(t || '').toLowerCase().replace(/\s+/g, ' ').trim(); }
    function boxNamed(n, x, z) {
      var t = normN(n), best = null, bd = 1e9, k;
      if (!t) return null;
      if (/^(outside|outdoors?|exterior|garden|yard|porch|deck|patio)$/.test(t)) return 'OUT';
      for (k = 0; k < ALLB.length; k++) {
        var B = ALLB[k];
        if (normN(B.label) !== t && normN(B.type) !== t) continue;
        var ddx = Math.max(B.x0 - x, 0, x - B.x1), ddz = Math.max(B.z0 - z, 0, z - B.z1), dd = Math.hypot(ddx, ddz);
        if (dd < bd) { bd = dd; best = B; }
      }
      return best;
    }
    function inBox(B, p) { return !!(B && p && p[0] > B.x0 - 0.05 && p[0] < B.x1 + 0.05 && p[1] > B.z0 - 0.05 && p[1] < B.z1 + 0.05); }
    /* the stretch of a partition with one of two boxes on each side of it */
    function wallBetween(bA, bB, p0, needW) {
      var best = null, bd = 1e9, k;
      for (k = 0; k < out.partitions.length; k++) {
        var q = out.partitions[k], vv = (q.o === 'v');
        if (q.sep) continue;
        var aLo = vv ? bA.x0 : bA.z0, aHi = vv ? bA.x1 : bA.z1, bLo = vv ? bB.x0 : bB.z0, bHi = vv ? bB.x1 : bB.z1;
        var face = (Math.abs(aHi - q.c) <= 0.35 && Math.abs(bLo - q.c) <= 0.35) || (Math.abs(bHi - q.c) <= 0.35 && Math.abs(aLo - q.c) <= 0.35);
        if (!face) continue;
        var lo = Math.max(q.s, vv ? Math.max(bA.z0, bB.z0) : Math.max(bA.x0, bB.x0));
        var hi = Math.min(q.e, vv ? Math.min(bA.z1, bB.z1) : Math.min(bA.x1, bB.x1));
        if (hi - lo < Math.min(needW, 0.6) + 0.04) continue;
        var al = vv ? p0[1] : p0[0], pe = Math.abs((vv ? p0[0] : p0[1]) - q.c);
        var dd = pe + ((al < lo) ? lo - al : ((al > hi) ? al - hi : 0));
        if (dd < bd) { bd = dd; best = { i: k, lo: lo, hi: hi, al: al }; }
      }
      return best;
    }
    /* put a door on that stretch: as near the reader's point as it fits, the
       leaf on the nearer end, opening into the room it opened into */
    function setOn(d, wb, w, intoP, bA, bB) {
      var pp = out.partitions[wb.i];
      w = Math.min(w, Math.max(0.60, wb.hi - wb.lo - 0.04));
      var u = Math.max(wb.lo + w / 2 + 0.05, Math.min(wb.hi - w / 2 - 0.05, wb.al));
      if (wb.hi - wb.lo < w + 0.10) u = (wb.lo + wb.hi) / 2;
      d.part = wb.i; d.u = _impR(_impSnap(u)); d.w = _impR(w); d.sw = 0;
      if (d.kind || d.slide || d.pass) return d;
      if ((wb.hi - wb.al) < (wb.al - wb.lo)) d.sw |= 1;
      var into = inBox(bA, intoP) ? bA : (inBox(bB, intoP) ? bB : (((PRI[bA.type] || 0) >= (PRI[bB.type] || 0)) ? bA : bB));
      var bc = (pp.o === 'h') ? (into.z0 + into.z1) / 2 : (into.x0 + into.x1) / 2;
      if ((pp.o === 'h') ? (bc > pp.c) : (bc < pp.c)) d.sw |= 2;
      return d;
    }
    var NMOK = 0, NMSAID = [], DROPN = [], NOWALL = [];
    for (i = 0; i < out.doors.length; i++) {
      var dn = out.doors[i];
      if (!dn.nm || dn.nm.length !== 2 || dn.pass) continue;
      var pn = dXZ(dn), bnA = boxNamed(dn.nm[0], pn[0], pn[1]), bnB = boxNamed(dn.nm[1], pn[0], pn[1]);
      if (!bnA || !bnB || bnA === 'OUT' || bnB === 'OUT' || bnA === bnB) continue;
      var ppn = out.partitions[dn.part];
      var sn1 = (ppn.o === 'h') ? boxAt(dn.u, ppn.c - 0.3) : boxAt(ppn.c - 0.3, dn.u);
      var sn2 = (ppn.o === 'h') ? boxAt(dn.u, ppn.c + 0.3) : boxAt(ppn.c + 0.3, dn.u);
      var hit = ((sn1 === bnA || sn1 === bnB) ? 1 : 0) + ((sn2 === bnA || sn2 === bnB) ? 1 : 0);
      if (hit === 2 || (hit === 1 && (!sn1 || !sn2))) { NMOK++; continue; }
      var wbn = wallBetween(bnA, bnB, dn.p0 || pn, dn.w);
      /* A DOOR WITH NO WALL BETWEEN ITS OWN ROOMS (v161, plan 1000073631,
         Marek: dvere do koupelny, kde je kuchynska linka -- pri tom tam
         kuchynska linka je, tak by tam nemely byt dvere). The shower's glass
         door, "BATH 1 / closet", was hung in the wall between the kitchen and
         the bathroom, through the kitchen run. A door standing between one of
         its rooms and a room it does not name, where no wall parts the two it
         names, is left out -- when the room it names has another door. */
      if (!wbn && hit === 1 && sn1 && sn2) {
        var mineN = (sn1 === bnA || sn1 === bnB) ? sn1 : sn2, otherN = (mineN === sn1) ? sn2 : sn1, moreN = false, qN;
        for (qN = 0; qN < out.doors.length && !moreN; qN++) {
          var dO = out.doors[qN];
          if (dO === dn || DROPN.indexOf(dO) >= 0) continue;
          var pO = out.partitions[dO.part];
          if (!pO || pO.sep) continue;
          var oA = (pO.o === 'h') ? boxAt(dO.u, pO.c - 0.3) : boxAt(pO.c - 0.3, dO.u), oB = (pO.o === 'h') ? boxAt(dO.u, pO.c + 0.3) : boxAt(pO.c + 0.3, dO.u);
          if (oA === mineN || oB === mineN) moreN = true;
        }
        if (moreN) { DROPN.push(dn); NOWALL.push('"' + dn.nm.join(' / ') + '" stood between "' + mineN.name + '" and "' + otherN.name + '"'); }
      }
      if (!wbn || wbn.i === dn.part) continue;
      setOn(dn, wbn, dn.w, dn.op0, bnA, bnB);
      dn.moved = true;
      MOVED.push('"' + dn.nm.join(' / ') + '" stood between "' + (sn1 ? sn1.name : '?') + '" and "' + (sn2 ? sn2.name : '?') + '" -- now on partition ' + wbn.i);
    }
    for (i = 0; i < OUTD.length; i++) {
      var on = OUTD[i];
      if (!on.nm || on.nm.length !== 2) continue;
      var obA = boxNamed(on.nm[0], on.x, on.z), obB = boxNamed(on.nm[1], on.x, on.z);
      if (!obA || !obB || obA === 'OUT' || obB === 'OUT' || obA === obB) continue;
      var wbo = wallBetween(obA, obB, [on.x, on.z], on.w);
      if (!wbo) continue;
      out.openings.splice(out.openings.indexOf(on.op), 1);
      var ndn = setOn({ part: wbo.i, u: 0, w: 0, sw: 0 }, wbo, nearSet(on.w), on.op0, obA, obB);
      ndn.moved = true; on.done = true;
      out.doors.push(ndn);
      MOVED.push('the outer door at ' + _impN(on.x) + ',' + _impN(on.z) + ' joins "' + on.nm.join(' / ') + '" -- an inside door, now on partition ' + wbo.i);
    }
    if (NMOK) NMSAID.push(NMOK + ' door(s) stand between the two rooms the reader named for them');
    if (DROPN.length) {
      out.doors = out.doors.filter(function (dX) { return DROPN.indexOf(dX) < 0; });
      NMSAID.push('NO WALL BETWEEN ITS ROOMS (v161): ' + DROPN.length + ' door(s) left out -- ' + NOWALL.join(', ') + ', with no wall between the rooms it names, and the room has another door');
    }
    /* AN OUTER DOOR INTO A BATHROOM IS ITS INSIDE DOOR. A bathroom, a WC or
       a closet has no door to the garden: a door the reader put on the outer
       wall of one goes to the inside wall of that room meeting the outer
       wall beside it, hung on that corner and opening into the room */
    for (i = 0; i < OUTD.length; i++) {
      var od = OUTD[i], sdo = SD[od.side];
      if (od.done) continue;
      var inPl = (sdo.o === 'h') ? inside(od.x, sdo.c + 0.3) : inside(sdo.c + 0.3, od.z);
      var ffc = inPl ? sdo.c + WALL : sdo.c - WALL, inw = inPl ? 0.3 : -0.3;
      var Aex = (sdo.o === 'h') ? boxAt(od.x, sdo.c + inw) : boxAt(sdo.c + inw, od.z);
      if (!Aex || !NOEXT[Aex.type]) continue;
      var alx = (sdo.o === 'h') ? od.x : od.z, bq = -1, bdq = 1e9;
      for (q = 0; q < out.partitions.length; q++) {
        var pq = out.partitions[q];
        if (pq.sep || pq.o === sdo.o) continue;
        if (Math.abs(pq.s - ffc) > 0.12 && Math.abs(pq.e - ffc) > 0.12) continue;
        var ddq = Math.abs(pq.c - alx);
        if (ddq > od.w / 2 + 0.6) continue;
        var eLo = (pq.o === 'h') ? Aex.z0 : Aex.x0, eHi = (pq.o === 'h') ? Aex.z1 : Aex.x1;
        if (Math.abs(pq.c - eLo) > 0.35 && Math.abs(pq.c - eHi) > 0.35) continue;
        if (ddq < bdq) { bdq = ddq; bq = q; }
      }
      if (bq < 0) continue;
      var pbq = out.partitions[bq], hiE = Math.abs(pbq.e - ffc) <= 0.12, busy = false;
      for (q = 0; q < out.doors.length; q++) {
        var dbq = out.doors[q];
        if (dbq.part === bq && (hiE ? (pbq.e - (dbq.u + dbq.w / 2)) : ((dbq.u - dbq.w / 2) - pbq.s)) < 0.5) busy = true;
      }
      if (busy) continue;
      out.openings.splice(out.openings.indexOf(od.op), 1);
      var ndx = hang({ part: bq, u: 0, w: 0, sw: 0 }, bq, nearSet(od.w), ffc, hiE, Aex);
      ndx.moved = true;
      out.doors.push(ndx);
      MOVED.push('the outer door at ' + _impN(od.x) + ',' + _impN(od.z) + ' is the door into "' + Aex.name + '" -- now on partition ' + bq);
    }
    for (i = 0; i < CLOSE.length; i++) {
      var cq = CLOSE[i], pi2 = -1, mid2 = (cq.lo + cq.hi) / 2, has = false;
      for (q = 0; q < out.partitions.length; q++) {
        var op2 = out.partitions[q];
        if (!op2.sep && op2.o === cq.o && Math.abs(op2.c - cq.c) <= 0.06 && op2.s <= mid2 && op2.e >= mid2) { pi2 = q; break; }
      }
      if (pi2 < 0) continue;
      for (q = 0; q < out.doors.length; q++) {
        var od2 = out.doors[q];
        if (od2.part === pi2 && od2.u + od2.w / 2 > cq.lo && od2.u - od2.w / 2 < cq.hi) { has = true; break; }
      }
      if (has) continue;
      /* A CLOSET HAS ITS OWN DOOR (Marek: krome dveri do skrine): beside a
         closet whose door is already there the closing is the wall only */
      var clB = CLOSETS[cq.rA.type] ? cq.rA : (CLOSETS[cq.rB.type] ? cq.rB : null);
      if (clB && doorsOnBox(clB, null) > 0) { WALLONLY.push('"' + cq.name + '"'); continue; }
      /* THE DOOR ROUND THE CORNER. A hinged door within reach of the corner
         this closing runs into, on the wall square to it, between one of the
         two rooms and a closet that has a door of its own besides, is this
         room's door read onto the wrong wall: it comes here, to that corner */
      var cand = null, cdd = 1e9, ends = [cq.lo, cq.hi], ee;
      for (q = 0; q < out.doors.length; q++) {
        var dm = out.doors[q], pk = out.partitions[dm.part];
        if (dm.pass || dm.kind || dm.moved || dm.part === pi2 || pk.sep || pk.o === cq.o) continue;
        if (cq.c < pk.s - 0.1 || cq.c > pk.e + 0.1) continue;
        if (Math.abs(dm.u - cq.c) > dm.w / 2 + 0.6) continue;
        var e2 = -1;
        for (ee = 0; ee < 2; ee++) if (Math.abs(pk.c - ends[ee]) <= 0.45) e2 = ee;
        if (e2 < 0) continue;
        var s1 = (pk.o === 'h') ? boxAt(dm.u, pk.c - 0.3) : boxAt(pk.c - 0.3, dm.u);
        var s2 = (pk.o === 'h') ? boxAt(dm.u, pk.c + 0.3) : boxAt(pk.c + 0.3, dm.u);
        var mine = (s1 === cq.rA || s1 === cq.rB) ? s1 : ((s2 === cq.rA || s2 === cq.rB) ? s2 : null);
        var other = (mine === s1) ? s2 : s1;
        if (!mine || !other || !CLOSETS[other.type] || doorsOnBox(other, dm) < 1) continue;
        var dist = Math.abs(dm.u - cq.c) + Math.abs(pk.c - ends[e2]);
        if (dist < cdd) { cdd = dist; cand = { d: dm, high: e2 === 1, cv: pk.c + (e2 === 1 ? -0.06 : 0.06), into: mine, from: other }; }
      }
      if (cand) {
        var from0 = dXZ(cand.d);
        hang(cand.d, pi2, cand.d.w, cand.cv, cand.high, cand.into);
        cand.d.moved = true;
        MOVED.push('the door into "' + cand.into.name + '" at ' + _impN(from0[0]) + ',' + _impN(from0[1]) + ' opened into the closet "' + cand.from.name +
                   '" -- now in the wall to "' + ((cand.into === cq.rA) ? cq.rB : cq.rA).name + '"');
        continue;
      }
      /* VARIANT 1 (v109, Marek: kde je pruchod, tam bych dal spise
         prerusovanou caru, a ve variante 2 by tam byly dvere): two rooms
         that meet with no wall and no door drawn are open to each other --
         a room line in the wall's place. Variant 2 puts the door below. */
      /* v116: two open zones get the room line (variant 1); any other pair
         keeps the wall, and gets a door here only when one of the two rooms
         has no way in of its own */
      if (OPEN_Z[cq.rA.type] && OPEN_Z[cq.rB.type]) {
        if (!V2) { PSQ.push({ o: cq.o, c: out.partitions[pi2].c, a: cq.plo, b: cq.phi, name: cq.name, cl: true }); continue; }
      } else {
        /* A CLOSET WITH NO DOOR OF ITS OWN OPENS HERE (v162, plan
           1000073631, Marek: stale vidim stary pudorys v knihovne). A closet
           counted as having its own way in for being a closet, so the edge to
           the bedroom it opens into got the wall only and the wardrobe was
           shut in -- the two wardrobes of the bedrooms, whose sliding fronts
           the plan draws in thin lines and one reading did not name. The audit
           then found no way into either and the plan stayed out of the
           library. A closet with no door on any of its edges, beside a room
           that is not a closet, has its front on this edge: the widest closet
           door the catalogue sells that fits it -- a bypass, else a bifold
           opening into the room. */
        var clF = CLOSETS[cq.rA.type] ? cq.rA : (CLOSETS[cq.rB.type] ? cq.rB : null), rmF = (clF === cq.rA) ? cq.rB : cq.rA;
        if (clF && !CLOSETS[rmF.type] && doorsOnBox(clF, null) === 0) {
          var dF = closetFront(clF, rmF, pi2, cq);
          if (dF) {
            out.doors.push(dF);
            FRONTS.push('"' + cq.name + '" ' + dF.kind + ' ' + _impN(dF.w) + ' m');
            continue;
          }
        }
        var inA = !!CLOSETS[cq.rA.type] || doorsOnBox(cq.rA, null) > 0, inB = !!CLOSETS[cq.rB.type] || doorsOnBox(cq.rB, null) > 0;
        if (inA && inB) { WALLC.push('"' + cq.name + '"'); continue; }
      }
      /* v130: on a plan that draws its doors, nothing drawn is no door -- a room line */
      if (DOORS_DRAWN) { PSQ.push({ o: cq.o, c: out.partitions[pi2].c, a: cq.plo, b: cq.phi, name: cq.name, cl: true }); NODOOR.push('closed edge "' + cq.name + '"'); continue; }
      /* NOTHING DRAWN THERE: A DOOR ALL THE SAME, not a doorway (Marek: dal
         bych vsude klasicke dvere, nebo posuvne, pokud je to nutne kvuli
         mistu). It opens into the bathroom, then the bedroom, rather than
         into a hall; where neither room is deep enough for the leaf to
         swing it slides instead. Two rooms of the open part of the house
         got a room line above and never come here. */
      var wN = nearSet(IMPL ? 30 * DRAW.IN : 0.80);
      var ord = ((PRI[cq.rA.type] || 0) >= (PRI[cq.rB.type] || 0)) ? [cq.rA, cq.rB] : [cq.rB, cq.rA], into = null;
      for (q = 0; q < 2; q++) {
        var BB = ord[q], dep = (cq.o === 'h') ? (BB.z1 - BB.z0) : (BB.x1 - BB.x0);
        if (dep >= wN + 0.10) { into = BB; break; }
      }
      var BI = into || ord[0], bLo = (cq.o === 'h') ? BI.x0 : BI.z0, bHi = (cq.o === 'h') ? BI.x1 : BI.z1;
      var dLo = Math.abs(cq.lo - bLo), dHi = Math.abs(bHi - cq.hi), cvN, hiN;
      if (Math.min(dLo, dHi) <= 0.5) { hiN = dHi < dLo; cvN = hiN ? bHi : bLo; }
      else { hiN = false; cvN = mid2 - wN / 2 - 0.10; }
      var nd2 = hang({ part: pi2, u: 0, w: 0, sw: 0 }, pi2, wN, cvN, hiN, BI);
      if (!into) { nd2.slide = 1; nd2.sw = 0; }
      out.doors.push(nd2);
      NEWD.push('"' + cq.name + '" ' + (into ? ('hinged ' + _impN(nd2.w) + ' into "' + into.name + '"') : ('sliding ' + _impN(nd2.w) + ' -- no room for a leaf to swing')));
    }
    if (NMSAID.length) out.notes.push(NMSAID.join('; '));
    if (MOVED.length) out.notes.push(MOVED.length + ' door(s) the reader set on the wrong wall went where the plan draws them: ' + MOVED.join('; '));
    if (NEWD.length) out.notes.push(NEWD.length + ' closed room edge(s) with nothing drawn got a door: ' + NEWD.join(', '));
    if (WALLONLY.length) out.notes.push(WALLONLY.length + ' closed edge(s) beside a closet that has its own door got the wall only: ' + WALLONLY.join(', '));
    if (WALLC.length) out.notes.push(WALLC.length + ' closed edge(s) between rooms that each have their own way in got the wall only -- a room line is for the open zones: ' + WALLC.join(', '));
    if (FRONTS.length) out.notes.push('CLOSET FRONT (v162): ' + FRONTS.length + ' closet(s) with no door of their own got their front on the edge to the room beside them -- the plan draws it in thin lines: ' + FRONTS.join(', '));
    /* the doors in the mouths that are not open zones (v116) */
    var MDS = [];
    for (i = 0; i < MOUTHD.length; i++) {
      var MD = MOUTHD[i], midD = (MD.lo + MD.hi) / 2, piD = -1;
      for (q = 0; q < out.partitions.length; q++) {
        var oqD = out.partitions[q];
        if (!oqD.sep && oqD.o === MD.o && Math.abs(oqD.c - MD.c) <= 0.06 && oqD.s <= midD && oqD.e >= midD) { piD = q; break; }
      }
      if (piD < 0) continue;
      var busy = false;
      for (q = 0; q < out.doors.length; q++) {
        var dqD = out.doors[q];
        if (dqD.part === piD && Math.abs(dqD.u - midD) < (dqD.w / 2 + (MD.hi - MD.lo) / 2)) busy = true;
      }
      if (busy) continue;
      var ordD = ((PRI[MD.b1.type] || 0) >= (PRI[MD.b2.type] || 0)) ? MD.b1 : MD.b2;
      var wD = nearSet(Math.min(MD.hi - MD.lo - 0.1, IMPL ? 30 * DRAW.IN : 0.80));
      var ndD = hang({ part: piD, u: 0, w: 0, sw: 0 }, piD, wD, midD - wD / 2 - 0.10, false, ordD);
      out.doors.push(ndD);
      MDS.push('"' + MD.b1.name + ' / ' + MD.b2.name + '" ' + _impN(wD));
    }
    if (MDS.length) out.notes.push(MDS.length + ' room mouth(s) between rooms that are not open zones got a wall and a door: ' + MDS.join(', '));
    if (nSepDoor) out.notes.push(nSepDoor + ' door(s) or doorway(s) on a room line were left out -- two rooms open to each other have no door between them');
    if (LBL.length) out.notes.push(LBL.length + ' door width(s) read from the door\'s own label: ' + LBL.join(', '));
    if (PAIRN.length) out.notes.push('PAIRS: ' + PAIRN.length + ' door(s) too narrow for two leaves -- a pair is 1.20 m and more (v134): ' + PAIRN.join(', '));
    if (CLOSETDOORS.length) {
      var cdN = {};
      for (q = 0; q < CLOSETDOORS.length; q++) cdN[CLOSETDOORS[q]] = (cdN[CLOSETDOORS[q]] || 0) + 1;
      out.notes.push('closet doors: ' + Object.keys(cdN).map(function (kk) { return cdN[kk] + ' ' + kk; }).join(', ') +
                     ' -- each opens out of the closet once the plan is written');
    }
    /* DOORS ON TOP OF EACH OTHER (v104). The reader can put two or three
       things on one stretch of a partition -- the open side between the
       kitchen and the living room came as a slider 1.80 and two doorways,
       1.75 and 3.20, all over each other. Overlapping doors on one partition
       become one: a doorway wider than any leaf among them takes the whole
       stretch they cover; otherwise the widest leaf stays and the rest go. */
    (function () {
      var drop = {}, add = [], said = [], pI, k;
      for (pI = 0; pI < out.partitions.length; pI++) {
        var DD = [];
        for (k = 0; k < out.doors.length; k++) if (out.doors[k].part === pI) DD.push(k);
        if (DD.length < 2) continue;
        DD.sort(function (a, b) { return (out.doors[a].u - out.doors[a].w / 2) - (out.doors[b].u - out.doors[b].w / 2); });
        var groups = [], g = [DD[0]], gHi = out.doors[DD[0]].u + out.doors[DD[0]].w / 2;
        for (k = 1; k < DD.length; k++) {
          var dk = out.doors[DD[k]];
          if (dk.u - dk.w / 2 < gHi - 0.02) { g.push(DD[k]); gHi = Math.max(gHi, dk.u + dk.w / 2); }
          else { groups.push(g); g = [DD[k]]; gHi = dk.u + dk.w / 2; }
        }
        groups.push(g);
        for (k = 0; k < groups.length; k++) {
          var G = groups[k];
          if (G.length < 2) continue;
          var lo = 1e9, hi = -1e9, leafW = 0, leafI = -1, passW = 0, j;
          for (j = 0; j < G.length; j++) {
            var dj = out.doors[G[j]];
            lo = Math.min(lo, dj.u - dj.w / 2); hi = Math.max(hi, dj.u + dj.w / 2);
            if (dj.pass) passW = Math.max(passW, dj.w);
            else if (dj.w > leafW) { leafW = dj.w; leafI = G[j]; }
          }
          for (j = 0; j < G.length; j++) drop[G[j]] = true;
          if (passW > leafW + 0.30 || leafI < 0) {
            var pp3 = out.partitions[pI], w3 = Math.max(0.60, Math.min(_impSnap(hi - lo), pp3.e - pp3.s - 0.10));
            var u3 = _impSnap(Math.max(pp3.s + w3 / 2, Math.min(pp3.e - w3 / 2, (lo + hi) / 2)));
            add.push({ part: pI, u: _impR(u3), w: _impR(w3), sw: 0, pass: true });
            said.push(G.length + ' overlapping on partition ' + pI + ' became one doorway ' + _impN(w3) + ' m');
          } else {
            drop[leafI] = false;
            said.push(G.length + ' overlapping on partition ' + pI + ': the ' + _impN(leafW) + ' m door stays, ' + (G.length - 1) + ' left out');
          }
        }
      }
      if (!said.length) return;
      var keep = [];
      for (k = 0; k < out.doors.length; k++) if (!drop[k]) keep.push(out.doors[k]);
      out.doors = keep.concat(add);
      out.notes.push('doors on top of each other: ' + said.join('; '));
    })();
    /* THE PASSAGES (v109). A gap in a wall with no door in it -- a cased
       opening, an arch, a doorway -- is in variant 1 a ROOM LINE the width of
       the gap, with the wall standing on both sides of it: the two rooms
       stay two rooms, nothing is built or billed in the gap. In variant 2 it
       is a door: an interior door up to the widest we sell, a pair of doors
       up to the widest pair; a gap wider still stays open. Between two rooms
       of the open part of the house -- living, dining, kitchen, hall, entry
       -- a gap stays open in both. */
    var OPENT2 = { living: 1, dining: 1, kitchen: 1, hallway: 1, entry: 1, mudroom: 1 }, PDOOR = [], BDOOR = [];
    /* A BATHROOM IS SHUT BY A DOOR (v177, plan 1000076010, Marek: publikovany
       projekt pustil koupelnu bez interierovych dveri). The plan drew its
       doors, the bathroom's entry from the dining area was an opening with
       no leaf, and it stayed a passage: a bathroom open to the dining room,
       published so. A gap into a bathroom or a WC always gets a door -- the
       nearest we sell, one leaf however wide the gap, the wall standing on
       either side of it -- and that is tidying, not a change to the plan. */
    function wet9(b) { return !!(b && String(b.type || '') !== 'shower' && (/^(bathroom|wc|toilet|powder|half_?bath)$/.test(String(b.type || '')) || /bath|\bwc\b|toilet|powder/i.test(String(b.label || b.name || '')))); }
    for (i = 0; i < out.doors.length; i++) {
      var dp9 = out.doors[i], pp9 = out.partitions[dp9.part];
      if (!dp9.pass || pp9.sep) continue;
      var t1 = (pp9.o === 'h') ? boxAt(dp9.u, pp9.c - 0.3) : boxAt(pp9.c - 0.3, dp9.u);
      var t2 = (pp9.o === 'h') ? boxAt(dp9.u, pp9.c + 0.3) : boxAt(pp9.c + 0.3, dp9.u);
      var nm9 = (t1 || t2) ? ((t1 ? t1.name : 'a space') + ' / ' + (t2 ? t2.name : 'a space')) : ('a gap on partition ' + dp9.part);
      var bothOpen = !!(t1 && t2 && OPENT2[t1.type] && OPENT2[t2.type]);
      /* one side a bathroom, the other not -- a shower box in its bathroom,
         a bathroom into a bathroom or into its own closet, stays as drawn */
      var oth9 = wet9(t1) ? t2 : t1;
      /* v197 (Marek on plan 1000075646: vygenerovaly se dvere v koupelne do
         W/D, a to je jen vyklenek, dvere ani v puvodnim obrazku nejsou): a
         laundry, pantry or utility niche under 3 m2 opening off the bathroom
         is the bathroom's own niche, as its closet is -- no door into it */
      var niche9 = !!(oth9 && (/^(closet|wardrobe|walk_in_closet|linen|storage)$/.test(String(oth9.type || '')) ||
                               (/^(laundry|pantry|utility)$/.test(String(oth9.type || '')) && (oth9.x1 - oth9.x0) * (oth9.z1 - oth9.z0) < CLOSET_MAX_M2)));
      var wetG = !bothOpen && (wet9(t1) !== wet9(t2)) && !(t1 && t1.type === 'shower') && !(t2 && t2.type === 'shower') && !niche9;
      if (wetG) {
        var wMaxB = SET[SET.length - 1], intoB = wet9(t1) ? t1 : t2, wB = nearSet(Math.min(dp9.w, wMaxB)), gB = dp9.w;
        delete dp9.pass;
        hang(dp9, dp9.part, wB, dp9.u - wB / 2 - 0.10, false, intoB);
        BDOOR.push('"' + nm9 + '" gap ' + _impN(gB) + ' m -> a door ' + _impN(dp9.w));
        continue;
      }
      /* v130: the plan draws its doors, and this gap has none -- it stays a passage */
      if (!bothOpen && DOORS_DRAWN) NODOOR.push('passage "' + nm9 + '" ' + _impN(dp9.w) + ' m');
      /* v197: and a bathroom's niche stays open in a plan that draws no
         doors as well */
      if (!bothOpen && !DOORS_DRAWN && !(niche9 && (wet9(t1) || wet9(t2)))) {
        var wMax = SET[SET.length - 1];
        if (dp9.w <= wMax + 0.15) {
          var into9 = (t1 && t2) ? (((PRI[t1.type] || 0) >= (PRI[t2.type] || 0)) ? t1 : t2) : (t1 || t2);
          var w9 = nearSet(Math.min(dp9.w, wMax));
          delete dp9.pass;
          hang(dp9, dp9.part, w9, dp9.u - w9 / 2 - 0.10, false, into9);
          PDOOR.push('"' + nm9 + '" door ' + _impN(dp9.w));
          continue;
        }
        var KP = _closetKindWidths('pair', !IMPL), kp9 = -1, k9;
        for (k9 = 0; k9 < KP.length; k9++) if (KP[k9] <= dp9.w + 0.15 && (kp9 < 0 || KP[k9] > KP[kp9])) kp9 = k9;
        if (kp9 >= 0 && dp9.w <= KP[KP.length - 1] + 0.15) {
          delete dp9.pass; dp9.kind = 'pair'; dp9.w = _impR(KP[kp9]); dp9.sw = 0;
          PDOOR.push('"' + nm9 + '" pair ' + _impN(dp9.w));
          continue;
        }
      }
      PSQ.push({ o: pp9.o, c: pp9.c, a: dp9.u - dp9.w / 2, b: dp9.u + dp9.w / 2, name: nm9, d: dp9 });
    }
    /* a stretch of a wall made a room line: the partition is cut there, the
       pieces either side stay walls with their doors */
    var PSAID = [], PSKIP = [];
    for (i = 0; i < PSQ.length; i++) {
      var ps9 = PSQ[i], pi9 = -1, mid9 = (ps9.a + ps9.b) / 2;
      for (q = 0; q < out.partitions.length; q++) {
        var oq9 = out.partitions[q];
        if (!oq9.sep && oq9.o === ps9.o && Math.abs(oq9.c - ps9.c) <= 0.06 && oq9.s <= mid9 && oq9.e >= mid9) { pi9 = q; break; }
      }
      if (pi9 < 0) continue;
      var P9 = out.partitions[pi9], a9 = Math.max(P9.s, ps9.a), b9 = Math.min(P9.e, ps9.b), blocked = false;
      if (a9 - P9.s < 0.25) a9 = P9.s;
      if (P9.e - b9 < 0.25) b9 = P9.e;
      /* A CORNER, NOT A STUB (v133, Marek: neni tam spojity roh). The
         picture's break starts at the outer edge of the wall across it, so
         the wall either side ran on a hand past that wall into the passage.
         A passage ending within 0.20 m of a wall across its line is taken
         on to that wall's middle: the wall turns the corner there. */
      var corn9 = 0;
      for (q = 0; q < out.partitions.length; q++) {
        var cq9 = out.partitions[q];
        if (cq9.sep || cq9.o === P9.o || P9.c < cq9.s - 0.05 || P9.c > cq9.e + 0.05) continue;
        if (cq9.c < a9 - 0.01 && cq9.c >= a9 - 0.20 && cq9.c > P9.s + 0.01) { a9 = cq9.c; corn9++; }
        else if (cq9.c > b9 + 0.01 && cq9.c <= b9 + 0.20 && cq9.c < P9.e - 0.01) { b9 = cq9.c; corn9++; }
      }
      for (q = 0; q < out.doors.length; q++) {
        var dq9 = out.doors[q];
        if (dq9.part !== pi9 || dq9 === ps9.d) continue;
        if (dq9.u + dq9.w / 2 > a9 + 0.01 && dq9.u - dq9.w / 2 < b9 - 0.01) blocked = true;
      }
      if (blocked || b9 - a9 < 0.3) { PSKIP.push('"' + ps9.name + '"'); continue; }
      var keepD = [], pieces = [];
      if (a9 > P9.s) pieces.push([P9.s, a9]);
      if (b9 < P9.e) pieces.push([b9, P9.e]);
      var pidx = [];
      for (q = 0; q < pieces.length; q++) { out.partitions.push({ o: P9.o, c: P9.c, s: _impR(pieces[q][0]), e: _impR(pieces[q][1]) }); pidx.push(out.partitions.length - 1); }
      P9.s = _impR(a9); P9.e = _impR(b9); P9.sep = true;
      for (q = 0; q < out.doors.length; q++) {
        var dr9 = out.doors[q];
        if (dr9 === ps9.d) continue;
        if (dr9.part === pi9) {
          for (var z9 = 0; z9 < pieces.length; z9++) if (dr9.u >= pieces[z9][0] - 0.01 && dr9.u <= pieces[z9][1] + 0.01) { dr9.part = pidx[z9]; break; }
        }
        keepD.push(dr9);
      }
      out.doors = keepD;
      PSAID.push('"' + ps9.name + '" ' + _impN(b9 - a9) + ' m' + (ps9.cl ? ' (no wall, no door drawn)' : '') + (corn9 ? (', taken on to ' + corn9 + ' corner(s) (v133)') : ''));
    }
    if (PSAID.length) out.notes.push('VARIANT ' + (V2 ? 2 : 1) + ': ' + PSAID.length + ' passage(s) became a room line in the wall' + (V2 ? '' : ' -- V2 puts doors there') + ': ' + PSAID.join(', '));
    if (PDOOR.length) out.notes.push(PDOOR.length + ' passage(s) between rooms that are not open zones got a door: ' + PDOOR.join(', '));
    if (BDOOR.length) out.notes.push('BATH DOOR (v177): ' + BDOOR.length + ' gap(s) into a bathroom got a door -- a bathroom is shut off: ' + BDOOR.join(', '));
    if (NODOOR.length) out.notes.push('DOORS: ' + NODOOR.length + ' place(s) with no door drawn left open, a room line and nothing built (v130): ' + NODOOR.join(', '));
    if (PSKIP.length) out.notes.push(PSKIP.length + ' passage(s) left as they were, a door stands in the way: ' + PSKIP.join(', '));
    if (!PSAID.length && !PDOOR.length && !BDOOR.length) out.notes.push('VARIANT ' + (V2 ? 2 : 1) + ': no passage to change');
    for (q = 0; q < out.doors.length; q++) { out.doors[q].nmT = out.doors[q].nm; delete out.doors[q].moved; delete out.doors[q].p0; delete out.doors[q].nm; delete out.doors[q].op0; }
    out.variant = V2 ? 2 : 1;
    /* A WINDOW'S OWN LABEL GIVES ITS SIZE (v109, Marek: okna ve vnejsich
       stenach musi sedet): 3050 SH is 3'-0" wide by 5'-0" high, as a door's
       2668 is. The plan's number wins over the width measured off the
       picture; the height picks between the units of that width. */
    var WLBL = [], PORCH = [], PODB = [], PINB = [], PODR = [];
    /* the rooms with a door out already, by the reader's names */
    for (i = 0; raw.doors && i < raw.doors.length; i++) {
      var rmO = raw.doors[i].rooms || [];
      if (!rmO.some(function (t) { return IMP_OUTDOOR.test(String(t)) || /^(outside|outdoors?|exterior)$/i.test(String(t).trim()); })) continue;
      rmO.forEach(function (t) { if (!(IMP_OUTDOOR.test(String(t)) || /^(outside|outdoors?|exterior)$/i.test(String(t).trim()))) PODR.push(String(t)); });
    }
    function porchNm(t) { return String(t || '').toUpperCase().replace(/[^A-Z0-9]/g, ''); }
    function porchOut(ib) {
      var oz = /^(living|dining|kitchen)$/.test(ib.t);
      for (var q9 = 0; q9 < PODR.length; q9++) {
        if (porchNm(PODR[q9]) === porchNm(ib.nm)) return true;
        if (oz && /^(living|dining|kitchen)$/.test(typeOfName(PODR[q9]))) return true;
      }
      return false;
    }
    /* v144: the porches, patios and decks the plan draws, and the rooms people live in */
    for (i = 0; raw.rooms && i < raw.rooms.length; i++) {
      var rP = raw.rooms[i], bP = rP && rP.box;
      if (!bP || !bP[0] || !bP[1]) continue;
      var pA9 = T(bP[0]), pB9 = T(bP[1]), tP = String(rP.type || ''), nP = String(rP.label || rP.type || 'room');
      var bx9 = { x0: Math.min(pA9[0], pB9[0]), x1: Math.max(pA9[0], pB9[0]), z0: Math.min(pA9[1], pB9[1]), z1: Math.max(pA9[1], pB9[1]), nm: nP };
      bx9.t = tP;
      if (IMP_OUTDOOR.test(tP) || IMP_OUTDOOR.test(nP)) PODB.push(bx9);
      else if (/^(bedroom|kids|living|dining|kitchen|office)$/.test(tP)) PINB.push(bx9);
    }
    for (i = 0; raw.windows && i < raw.windows.length; i++) {
      var wp = T(raw.windows[i].at), wMv = _impRMove(RMOVED, wp), ww = _impSnap(units(raw.windows[i].w)), wn2 = wallNear(wp[0], wp[1], true);
      if (wMv) nRMV++;
      var wl = _impWinLabel(raw.windows[i].label);
      if (wl) { WLBL.push('"' + raw.windows[i].label + '" ' + _impN(wl.w) + ' x ' + _impN(wl.h) + (Math.abs(wl.w - ww) > 0.05 ? (' (drawn ' + _impN(ww) + ')') : '')); ww = _impSnap(wl.w); }
      /* v203: a window in a wall of the L's corner keeps its place -- the
         product's wall it will be in comes with the L */
      var lnW = out.lnotch ? _impOnLNotch(out.lnotch, wp, 0.30) : null;
      if (lnW && (!wn2 || wn2.t === 'part' || wn2.d > 0.30)) {
        out.openings.push({ side: -1, npt: [_impR(lnW[0]), _impR(lnW[1])], off: 0, w: _impR(ww), kind: 'window',
                            sill: (raw.windows[i].sill != null) ? Number(raw.windows[i].sill) : 0.9, lh: wl ? _impR(wl.h) : 0,
                            sx: (raw.windows[i].sill != null) ? 1 : 0 });
        continue;
      }
      if (!wn2) { out.notes.push('a window at ' + _impN(wp[0]) + ',' + _impN(wp[1]) + ' is on no wall -- left out'); continue; }
      if (wn2.t === 'part') { out.notes.push('a window on an inside wall was left out'); continue; }
      /* A WIDE OPENING ONTO A PORCH IS A DOOR (v144, plan 1000073628: Marek --
         v puvodnim planku jsou posuvne dvere na covered porch). The reader
         gave the bedroom's 5 ft slider onto the covered porch as a window. An
         opening 1.40 m wide or more from a bedroom, a living room, a dining
         room, a kitchen or an office, standing on the house's side where a
         porch, a patio or a deck the plan draws lies against it, and with no
         label of a window's height, is taken for a sliding door: the HS
         nearest its width -- unless the room has a door out already (the
         living room, the kitchen and the dining room as one): plan C's
         kitchen and living room windows onto the covered balcony stay
         windows, the slider between them is the way out. */
      if (ww >= 1.4 && !(wl && wl.h > 0 && wl.h < 1.9)) {
        var odH = null, inH = null, qp;
        for (qp = 0; qp < PODB.length && !odH; qp++) { var ob = PODB[qp]; if (wp[0] >= ob.x0 - 0.6 && wp[0] <= ob.x1 + 0.6 && wp[1] >= ob.z0 - 0.6 && wp[1] <= ob.z1 + 0.6) odH = ob; }
        for (qp = 0; odH && qp < PINB.length && !inH; qp++) { var ib = PINB[qp]; if (wp[0] >= ib.x0 - 0.6 && wp[0] <= ib.x1 + 0.6 && wp[1] >= ib.z0 - 0.6 && wp[1] <= ib.z1 + 0.6) inH = ib; }
        if (odH && inH && !porchOut(inH)) {
          out.openings.push({ side: wn2.i, off: _impR(_impSnap(Math.max(ww / 2, Math.min(wn2.len - ww / 2, wn2.off)))), w: _impR(ww), kind: 'door', sill: 0, pref: 'slide' });
          PORCH.push('"' + inH.nm + '" ' + _impN(ww) + ' m onto "' + odH.nm + '"');
          continue;
        }
      }
      out.openings.push({ side: wn2.i, off: _impR(_impSnap(Math.max(ww / 2, Math.min(wn2.len - ww / 2, wn2.off)))), w: _impR(ww), kind: 'window',
                          sill: (raw.windows[i].sill != null) ? Number(raw.windows[i].sill) : 0.9, lh: wl ? _impR(wl.h) : 0,
                          /* v158: the sill is the drawing's own */
                          sx: (raw.windows[i].sill != null) ? 1 : 0 });
    }
    if (WLBL.length) out.notes.push(WLBL.length + ' window size(s) read from the window\'s own label: ' + WLBL.join(', '));
    if (PORCH.length) out.notes.push('PORCH: ' + PORCH.length + ' opening(s) 1.40 m or wider onto a porch, a patio or a deck read as a window became sliding doors (v144): ' + PORCH.join(', '));
    if (nRMV) out.notes.push('STEP: ' + nRMV + ' window(s) and outer door(s) on a side the rectangle took out went out with it (v144)');

    /* THE OUTDOOR SPACES (v115): a patio, a porch, a deck -- kept with their
       box, for the terrace and the roof pulled out over it */
    out.outdoor = [];
    for (i = 0; raw.rooms && i < raw.rooms.length; i++) {
      var ro = raw.rooms[i];
      if (!ro || !ro.box || !ro.box[0] || !ro.box[1] || !/^(patio|porch|deck|balcony|terrace)$/.test(String(ro.type || ''))) continue;
      var ob0 = T(ro.box[0]), ob1 = T(ro.box[1]);
      if (inside((ob0[0] + ob1[0]) / 2, (ob0[1] + ob1[1]) / 2)) continue;
      out.outdoor.push({ type: String(ro.type), label: String(ro.label || ro.type), x0: _impR(Math.min(ob0[0], ob1[0])), x1: _impR(Math.max(ob0[0], ob1[0])),
                         z0: _impR(Math.min(ob0[1], ob1[1])), z1: _impR(Math.max(ob0[1], ob1[1])) });
    }
    /* THE ROOMS: the label becomes our key, and it has to be inside; a
       closet takes no name (v102) -- it is in out.closets instead */
    if (HALLSAID.length) out.notes.push('HALL: ' + HALLSAID.length + ' hall(s) the plan does not name, a strip along the living room and open to it, are the living room\'s -- no room line' +
                                        (nHallLine ? (' (' + nHallLine + ' left out)') : '') + ', no name of their own (v148): ' + HALLSAID.join(', '));
    for (i = 0; raw.rooms && i < raw.rooms.length; i++) {
      if (isCloset[i] || HALLIN[i]) continue;
      var rp = T(raw.rooms[i].at), rk = _impKey(raw.rooms[i].label, raw.rooms[i].key);
      if (!inside(rp[0], rp[1])) { out.notes.push('the room label "' + raw.rooms[i].label + '" is outside the house -- left out'); continue; }
      if (IMP_OUTDOOR.test(String(raw.rooms[i].type || '') + ' ' + String(raw.rooms[i].label || '')) &&
          NICHE.some(function (n9) {
            var al9 = (n9.o === 'h') ? rp[0] : rp[1], ac9 = (n9.o === 'h') ? rp[1] : rp[0];
            return n9.door && n9.outd && al9 >= n9.al0 - 0.1 && al9 <= n9.al1 + 0.1 && ac9 >= n9.ac0 - 0.1 && ac9 <= n9.ac1 + 0.1;
          })) continue;
      if (rk.note) out.notes.push(rk.note);
      out.rooms.push({ x: _impR(rp[0]), z: _impR(rp[1]), key: rk.key, label: String(raw.rooms[i].label || '') });
    }
    /* the niche that is the entry, named so (v114) */
    for (i = 0; i < NICHE.length; i++) {
      if (!NICHE[i].door || (NICHE[i].name && !NICHE[i].outd)) continue;
      var nkE = _impKey('ENTRY', ['rooms.hallway', 'rooms.hall']);
      if (nkE.key) out.rooms.push({ x: _impR(NICHE[i].x), z: _impR(NICHE[i].z), key: nkE.key, label: 'ENTRY' });
    }
    /* THE FITTINGS (v111): each box into the plan's metres; one whose middle
       is outside the house is left out */
    out.fixtures = [];
    var FXOUT = 0;
    for (i = 0; raw.fixtures && i < raw.fixtures.length; i++) {
      var fq = raw.fixtures[i], fp0 = T(fq.a), fp1 = T(fq.b);
      var fr = { type: fq.type, x0: Math.min(fp0[0], fp1[0]), x1: Math.max(fp0[0], fp1[0]), z0: Math.min(fp0[1], fp1[1]), z1: Math.max(fp0[1], fp1[1]) };
      if (fq.label) fr.label = String(fq.label);
      if (fq.sug) fr.sug = true;
      if (fq.seats) fr.seats = fq.seats;
      if (fq.stools) fr.stools = fq.stools;
      if (!inside((fr.x0 + fr.x1) / 2, (fr.z0 + fr.z1) / 2) || fr.x1 - fr.x0 < 0.15 || fr.z1 - fr.z0 < 0.15) { FXOUT++; continue; }
      out.fixtures.push(fr);
    }
    if (FXOUT) out.notes.push(FXOUT + ' fixture(s) outside the house or too small to be one were left out');
    /* THE SLIDER HAS ROOM TO PARK (v116, Marek: na fasade zvenku jakysi sedy
       objekt -- rekl bych, ze je to ta skrin mezi loznicemi). A sliding leaf
       runs along the face of its wall and parks beside the opening, seven
       tenths of its width past the jamb; the walk-in closet's 1.55 m slider
       sat against the outer wall and parked through it, out on the facade.
       Each slider now parks on the side with the wall to take it -- up to the
       next wall across its line or the end of its own -- or parts in two when
       each side takes half; with room on neither side a closet's becomes a
       bypass (both leaves inside the opening) and any other door a hinged one. */
    /* NO DOOR ACROSS A T (v119, Marek: dvere ve skrini jsou pres T spoj
       pricky -- coz by mel byt vzdy bug). A door is a hole in ONE wall
       between two rooms; where another wall meets its wall inside the
       opening, the door stands across the junction and opens onto the end
       of that wall -- the walk-in closet's slider, read 0.32 m wide and made
       the smallest door we sell, 0.71, ran past the closet's own wall. Every
       door is checked against the walls that meet or cross its wall: one
       that spans a junction is slid along its wall into the stretch between
       junctions that holds its middle, a jamb's width clear of each; if the
       stretch is too short it becomes the next narrower door of its kind
       that fits; failing both, the nearest stretch that takes it. */
    var TJX = [];
    for (i = 0; i < out.doors.length; i++) {
      var dJ = out.doors[i], pJ = out.partitions[dJ.part];
      if (!pJ || pJ.sep) continue;
      var JN = [], kJ;
      for (q = 0; q < out.partitions.length; q++) {
        var cJ = out.partitions[q];
        if (q === dJ.part || cJ.o === pJ.o) continue;
        if (pJ.c < cJ.s - 0.06 || pJ.c > cJ.e + 0.06) continue;
        if (cJ.c <= pJ.s + 0.05 || cJ.c >= pJ.e - 0.05) continue;
        JN.push(cJ.c);
      }
      if (!JN.length) continue;
      /* the meeting wall's body (half a partition, 5 cm) inside the opening
         by more than a centimetre is across; a door right beside a T, clear of
         its face, is a door by a corner and stays */
      var CLJ = 0.06, loJ = dJ.u - dJ.w / 2, hiJ = dJ.u + dJ.w / 2, acrossJ = null;
      for (kJ = 0; kJ < JN.length; kJ++) if (JN[kJ] > loJ - 0.04 && JN[kJ] < hiJ + 0.04) { acrossJ = JN[kJ]; break; }
      if (acrossJ === null) continue;
      JN.sort(function (a, b) { return a - b; });
      var cutJ = [pJ.s].concat(JN, [pJ.e]), STJ = [];
      for (kJ = 1; kJ < cutJ.length; kJ++) {
        var sa = cutJ[kJ - 1] + ((kJ === 1) ? 0.03 : CLJ), sb = cutJ[kJ] - ((kJ === cutJ.length - 1) ? 0.03 : CLJ);
        if (sb - sa > 0.3) STJ.push([sa, sb]);
      }
      var uJ = dJ.u;
      function ddJ(S0) { return (uJ < S0[0]) ? S0[0] - uJ : ((uJ > S0[1]) ? uJ - S0[1] : 0); }
      /* BETWEEN THE TWO ROOMS THE READER NAMED (v146, plan 1000073628: the
         bathroom's pocket door, "bathroom / hallway", was moved off the T
         into the mechanical closet's stretch, and the laundry's door,
         "laundry / hallway", past the junction into the bathroom's -- each
         now opened into a room the reader did not name). Where the reader
         named both rooms of a door, only a stretch with those two rooms on
         its two sides takes it -- up to 1.0 m along its wall, as it is still
         the same door; when no such stretch takes any door, the door stays as
         read. A door the reader gave no rooms is moved as before. */
      var pnJ = (pJ.o === 'h') ? [uJ, pJ.c] : [pJ.c, uJ], nmA = null, nmB = null;
      if (dJ.nmT && dJ.nmT.length === 2) { nmA = boxNamed(dJ.nmT[0], pnJ[0], pnJ[1]); nmB = boxNamed(dJ.nmT[1], pnJ[0], pnJ[1]); }
      var NMJ = !!(nmA && nmB && nmA !== 'OUT' && nmB !== 'OUT' && nmA !== nmB);
      function namedSt(S0) {
        var hw = Math.min(dJ.w, S0[1] - S0[0]) / 2, u9 = Math.max(S0[0] + hw, Math.min(S0[1] - hw, uJ));
        var s1 = (pJ.o === 'h') ? boxAt(u9, pJ.c - 0.3) : boxAt(pJ.c - 0.3, u9), s2 = (pJ.o === 'h') ? boxAt(u9, pJ.c + 0.3) : boxAt(pJ.c + 0.3, u9);
        return s1 !== s2 && (s1 === nmA || s1 === nmB) && (s2 === nmA || s2 === nmB);
      }
      /* only the stretch the door's middle is in, or one a step from it: a
         door carried down its wall into other rooms is a different door */
      if (NMJ) STJ = STJ.filter(function (S0) { return ddJ(S0) <= 1.0 && namedSt(S0); });
      else STJ = STJ.filter(function (S0) { return ddJ(S0) <= 0.30; });
      STJ.sort(function (A, B) { return ddJ(A) - ddJ(B); });
      var doneJ = '', w0J = dJ.w;
      for (kJ = 0; kJ < STJ.length && !doneJ; kJ++) {
        var SJ = STJ[kJ], roomJ = SJ[1] - SJ[0], w1J = w0J;
        if (roomJ < w0J - 1e-6) {
          var WLJ = dJ.kind ? _closetKindWidths(dJ.kind, !IMPL) : ((dJ.pass || dJ.slide) ? null : SET), bwJ = -1;
          if (WLJ) { for (q = 0; q < WLJ.length; q++) if (WLJ[q] <= roomJ + 1e-6 && WLJ[q] > bwJ) bwJ = WLJ[q]; }
          else if (roomJ >= 0.6) bwJ = Math.floor(roomJ / IMP_SNAP) * IMP_SNAP;
          if (bwJ <= 0) continue;
          w1J = bwJ;
        }
        var u1J = _impSnap(Math.max(SJ[0] + w1J / 2, Math.min(SJ[1] - w1J / 2, uJ)));
        if (u1J - w1J / 2 < SJ[0] - 1e-6 || u1J + w1J / 2 > SJ[1] + 1e-6) u1J = Math.max(SJ[0] + w1J / 2, Math.min(SJ[1] - w1J / 2, uJ));
        /* never onto another door of the same wall */
        var clashJ = false;
        for (q = 0; q < out.doors.length; q++) {
          var oJ = out.doors[q];
          if (oJ === dJ || oJ.part !== dJ.part) continue;
          if (Math.abs(oJ.u - u1J) < (oJ.w + w1J) / 2 + 0.05) { clashJ = true; break; }
        }
        if (clashJ) continue;
        doneJ = _impN(w0J) + ' m on partition ' + dJ.part + ' stood across the wall meeting it at ' + _impN(acrossJ) + ' -- ' +
                ((Math.abs(w1J - w0J) > 0.005) ? ('made ' + _impN(w1J) + ' and ') : '') + 'moved ' + _impN(Math.abs(u1J - uJ)) + ' m along its wall' +
                ((uJ < SJ[0] || uJ > SJ[1]) ? (NMJ ? (' past the junction, between "' + dJ.nmT.join('" and "') + '" (v146)') : ' past the junction (its own stretch was too short)') : '');
        dJ.u = _impR(u1J); dJ.w = _impR(w1J);
      }
      /* NEVER LEFT ACROSS IT (v134, Marek: i kdyz je pudorys ze zdroje
         spatne, dvere by nemely presahovat T spoje pricek). With no stretch
         taking a door of its kind, the stretch its middle is in takes the
         next kind that fits -- a pair a bifold, then one leaf, down to the
         0.61 m we sell -- and failing every door, an opening as wide as the
         stretch; under 0.40 m the door goes. */
      if (!doneJ && STJ.length) {
        var SJ0 = STJ[0], room0 = SJ0[1] - SJ0[0], CH = [], alt = null, kc, wc;
        var W61 = IMPL ? 24 * DRAW.IN : 0.61;
        if (dJ.kind === 'pair') CH.push(['bifold', _closetKindWidths('bifold', !IMPL)]);
        if (dJ.kind || (!dJ.pass && !dJ.slide)) CH.push(['', SET.concat([W61])]);
        for (kc = 0; kc < CH.length && !alt; kc++) {
          var bwc = -1;
          for (wc = 0; wc < CH[kc][1].length; wc++) if (CH[kc][1][wc] <= room0 + 1e-6 && CH[kc][1][wc] > bwc) bwc = CH[kc][1][wc];
          if (bwc > 0) alt = { kind: CH[kc][0], w: bwc };
        }
        var kWas = dJ.kind || (dJ.pass ? 'opening' : (dJ.slide ? 'slider' : 'door'));
        if (alt) {
          if (alt.kind) dJ.kind = alt.kind; else delete dJ.kind;
          dJ.w = _impR(alt.w); dJ.u = _impR(_impSnap(Math.max(SJ0[0] + alt.w / 2, Math.min(SJ0[1] - alt.w / 2, uJ))));
          if (dJ.u - alt.w / 2 < SJ0[0] - 1e-6 || dJ.u + alt.w / 2 > SJ0[1] + 1e-6) dJ.u = _impR((SJ0[0] + SJ0[1]) / 2);
          doneJ = _impN(w0J) + ' m ' + kWas + ' on partition ' + dJ.part + ' stood across the wall meeting it at ' + _impN(acrossJ) + ' -- no ' + kWas +
                  ' fits ' + _impN(room0) + ' m between the walls, made ' + (alt.kind ? ('a ' + alt.kind) : 'one leaf') + ' ' + _impN(alt.w) + ' (v134)';
        } else if (room0 >= (NMJ ? 0.60 : 0.40)) {
          delete dJ.kind; delete dJ.slide; dJ.pass = true; dJ.sw = 0;
          dJ.w = _impR(Math.floor(room0 / IMP_SNAP) * IMP_SNAP); dJ.u = _impR((SJ0[0] + SJ0[1]) / 2);
          doneJ = _impN(w0J) + ' m ' + kWas + ' on partition ' + dJ.part + ' stood across the wall meeting it at ' + _impN(acrossJ) + ' -- no door fits ' +
                  _impN(room0) + ' m between the walls, made an opening (v134)';
        } else if (!NMJ) {
          dJ.gone = true;
          doneJ = _impN(w0J) + ' m ' + kWas + ' on partition ' + dJ.part + ' stood across the wall meeting it at ' + _impN(acrossJ) + ' -- ' + _impN(room0) +
                  ' m between the walls takes nothing, taken out (v134)';
        }
      }
      if (!doneJ && NMJ) doneJ = _impN(w0J) + ' m "' + dJ.nmT.join(' / ') + '" on partition ' + dJ.part + ' stands across the wall meeting it at ' + _impN(acrossJ) +
                                 ' -- ' + (STJ.length ? ('the ' + _impN(STJ[0][1] - STJ[0][0]) + ' m between those two rooms takes no door') : 'no stretch of its wall has those two rooms either side') + ', left as read (v146)';
      TJX.push(doneJ || (_impN(w0J) + ' m on partition ' + dJ.part + ' stands across the wall meeting it at ' + _impN(acrossJ) + ' and no stretch of its wall takes it -- left as read'));
    }
    out.doors = out.doors.filter(function (dG) { return !dG.gone; });
    for (i = 0; i < out.doors.length; i++) delete out.doors[i].nmT;
    if (TJX.length) out.notes.push('OFF THE T: ' + TJX.join('; '));
    var SLX = [];
    for (i = 0; i < out.doors.length; i++) {
      var dS = out.doors[i], pS = out.partitions[dS.part];
      if (!dS.slide || dS.kind || !pS || pS.sep) continue;
      var loS = dS.u - dS.w / 2, hiS = dS.u + dS.w / 2, stLo = pS.s, stHi = pS.e;
      for (q = 0; q < out.partitions.length; q++) {
        var cS = out.partitions[q];
        if (q === dS.part || cS.sep || cS.o === pS.o || pS.c < cS.s - 0.05 || pS.c > cS.e + 0.05) continue;
        if (cS.c <= loS + 0.02 && cS.c > stLo) stLo = cS.c;
        if (cS.c >= hiS - 0.02 && cS.c < stHi) stHi = cS.c;
      }
      var needS = 0.7 * dS.w + 0.05, frLo = loS - stLo, frHi = stHi - hiS, was = dS.slide;
      if ((dS.slide === 1 && frLo >= needS) || (dS.slide === 2 && frHi >= needS) || (dS.slide === 3 && frLo >= needS / 2 && frHi >= needS / 2)) continue;
      if (frLo >= needS) dS.slide = 1;
      else if (frHi >= needS) dS.slide = 2;
      else if (frLo >= needS / 2 && frHi >= needS / 2) dS.slide = 3;
      else {
        var KWb = _closetKindWidths('bypass', !IMPL), kbS = -1, kq;
        for (kq = 0; kq < KWb.length; kq++) if (KWb[kq] <= pS.e - pS.s - 0.1 && (kbS < 0 || Math.abs(KWb[kq] - dS.w) < Math.abs(KWb[kbS] - dS.w))) kbS = kq;
        var clS = false;
        for (q = 0; q < ALLB.length; q++) {
          var BS = ALLB[q];
          if (!CLOSETS[BS.type]) continue;
          var px1 = (pS.o === 'h') ? dS.u : pS.c - 0.3, pz1 = (pS.o === 'h') ? pS.c - 0.3 : dS.u, px2 = (pS.o === 'h') ? dS.u : pS.c + 0.3, pz2 = (pS.o === 'h') ? pS.c + 0.3 : dS.u;
          if ((px1 >= BS.x0 && px1 <= BS.x1 && pz1 >= BS.z0 && pz1 <= BS.z1) || (px2 >= BS.x0 && px2 <= BS.x1 && pz2 >= BS.z0 && pz2 <= BS.z1)) { clS = true; break; }
        }
        /* v145 (plan 1000073628): the laundry's 0.83 m slider became the
           smallest bypass, 1.22 m, ran past the hall and put the bathroom's
           basin in its way. A bypass only when one sold is no more than an
           eighth wider than the door; a hinged door otherwise */
        if (clS && kbS >= 0 && KWb[kbS] > dS.w * 1.12 + 0.01) clS = false;
        if (clS && kbS >= 0) { delete dS.slide; dS.kind = 'bypass'; dS.w = _impR(KWb[kbS]); dS.sw = 0; SLX.push(_impN(dS.w) + ' m on partition ' + dS.part + ' made a bypass -- no wall either side to park a leaf'); continue; }
        delete dS.slide; dS.sw = 0; dS.w = _impR(Math.min(dS.w, SET[SET.length - 1]));
        SLX.push(_impN(dS.w) + ' m on partition ' + dS.part + ' made a hinged door -- no wall either side to park a leaf');
        continue;
      }
      SLX.push(_impN(dS.w) + ' m on partition ' + dS.part + ' parks ' + (dS.slide === 1 ? 'to the low end' : (dS.slide === 2 ? 'to the high end' : 'in two leaves, one each way')) + ' (was ' + (was === 1 ? 'low' : (was === 2 ? 'high' : 'both')) + ')');
    }
    if (SLX.length) out.notes.push('SLIDERS: ' + SLX.join('; '));
    /* DOORS THAT STRIKE EACH OTHER (v134, Marek: dvere tam brutalne
       koliduji). Two hinged doors whose leaves sweep the same floor are a
       mistake of the reading, whatever the drawing says. The later one turns
       first -- the other jamb, the other room, both -- then the earlier one;
       the first turn that clears every door is kept, and of two that do, the
       one that does not open into a hall (a door opens into the room it
       serves). Closet doors are left to the closet rule. */
    function swingOf(d, sw) {
      var p = out.partitions[d.part];
      if (!p || p.sep || d.pass || d.slide || d.kind) return null;
      var nA = (p.o === 'h') ? ((sw & 2) ? 1 : -1) : ((sw & 2) ? -1 : 1), hiE = sw & 1;
      return { o: p.o, c: p.c, h: hiE ? d.u + d.w / 2 : d.u - d.w / 2, tow: hiE ? -1 : 1, R: d.w, nA: nA };
    }
    function swPts(S) {
      var P = [], r, th;
      for (r = 0.3; r < 1; r += 0.3) for (th = 10; th <= 85; th += 15) {
        var al = S.h + S.tow * S.R * r * Math.cos(th * Math.PI / 180), ac = S.c + S.nA * S.R * r * Math.sin(th * Math.PI / 180);
        P.push(S.o === 'h' ? [al, ac] : [ac, al]);
      }
      return P;
    }
    function inSw(S, x, z) {
      var al = S.o === 'h' ? x : z, ac = S.o === 'h' ? z : x, da = (al - S.h) * S.tow, dc = (ac - S.c) * S.nA;
      return da >= -0.02 && dc >= 0.02 && Math.hypot(da, dc) <= S.R + 0.02;
    }
    function strikes(A, B) {
      if (!A || !B) return false;
      var PA = swPts(A), PB = swPts(B), k;
      for (k = 0; k < PA.length; k++) if (inSw(B, PA[k][0], PA[k][1])) return true;
      for (k = 0; k < PB.length; k++) if (inSw(A, PB[k][0], PB[k][1])) return true;
      return false;
    }
    function intoHall(d, sw) {
      var S = swingOf(d, sw);
      if (!S) return false;
      var mid = S.o === 'h' ? [d.u, S.c + S.nA * 0.4] : [S.c + S.nA * 0.4, d.u], B = boxAt(mid[0], mid[1]);
      return !!(B && /^(hall|hallway|entry|mudroom|corridor)$/.test(String(B.type || '')));
    }
    var SWX = [];
    for (i = 0; i < out.doors.length; i++) {
      for (q = i + 1; q < out.doors.length; q++) {
        var dA = out.doors[i], dB = out.doors[q];
        if (!strikes(swingOf(dA, dA.sw | 0), swingOf(dB, dB.sw | 0))) continue;
        var best = null, tries = [[dB, 2], [dB, 1], [dB, 3], [dA, 2], [dA, 1], [dA, 3]], t9;
        for (t9 = 0; t9 < tries.length; t9++) {
          var dT = tries[t9][0], swT = (dT.sw | 0) ^ tries[t9][1], ST = swingOf(dT, swT), ok = true, k9;
          for (k9 = 0; k9 < out.doors.length && ok; k9++) {
            var dO2 = out.doors[k9];
            if (dO2 === dT) continue;
            if (strikes(ST, swingOf(dO2, dO2.sw | 0))) ok = false;
          }
          if (!ok) continue;
          var hallT = intoHall(dT, swT);
          if (!best || (best.hall && !hallT)) best = { d: dT, sw: swT, hall: hallT, how: tries[t9][1] };
          if (!hallT) break;
        }
        if (best) {
          var nmT = (best.d.nm && best.d.nm.length) ? best.d.nm.join(' / ') : ('partition ' + best.d.part);
          best.d.sw = best.sw;
          SWX.push('"' + nmT + '" ' + (best.how === 2 ? 'opens into the other room' : (best.how === 1 ? 'hangs on the other jamb' : 'other jamb, other room')) + (best.hall ? '' : ''));
        } else SWX.push('two doors on partitions ' + dA.part + ' and ' + dB.part + ' strike each other and no turn clears them -- left as read');
      }
    }
    if (SWX.length) out.notes.push('SWING: ' + SWX.length + ' door(s) whose leaves struck another were turned (v134): ' + SWX.join(', '));
    /* NO WALL OF NO LENGTH (v161, plan 1000073631, Marek: vygenerovala se
       navic vodorovna pricka pres cely objekt). Tidying put the end of a
       closet's side wall onto the line of another and left it 0.00 m long;
       the configurator, finding no length to build, built it from outer wall
       to outer wall. A partition under 0.30 m once the plan is tidied is left
       out, and the doors in it with it. */
    (function () {
      var KP = [], MAPP = {}, nG = 0, dG = 0, kq;
      for (kq = 0; kq < out.partitions.length; kq++) {
        var Pq = out.partitions[kq];
        if (!(Pq.e - Pq.s >= 0.30)) { nG++; continue; }
        MAPP[kq] = KP.length; KP.push(Pq);
      }
      if (!nG) return;
      out.doors = out.doors.filter(function (dq) { if (MAPP[dq.part] == null) { dG++; return false; } dq.part = MAPP[dq.part]; return true; });
      out.partitions = KP;
      out.notes.push('NO LENGTH (v161): ' + nG + ' partition(s) under 0.30 m once tidied left out -- a wall of no length is built from outer wall to outer wall' + (dG ? (', ' + dG + ' door(s) in them with them') : ''));
    })();
    /* A SHUT BOX IS A CABINET, NOT A ROOM (v162, plan 1000073631). Under the
       tub of BATH 2 the plan draws a box 0.90 x 0.60 m in thick lines, with
       no door and no name -- a cabinet or a chase. Its walls closed a room of
       0.53 m2 nobody could get into, and the audit kept the plan out of the
       library. Four partitions closing a box no more than 1.00 m either way,
       with no door in any of them and nothing inside, that holds no room the
       reader named and lies inside the box of one he did, are a cabinet
       standing in that room -- as CABINET (v139) has it for a store the
       reader named: the walls that are the box's alone are left out, the
       walls it shares with the rooms round it stay. */
    (function () {
      var P = out.partitions, MAXS = 1.00, TOL = 0.06, cut = {}, said = [], a, b, k;
      function doorIn(pi, lo, hi) {
        for (var k2 = 0; k2 < out.doors.length; k2++) { var d2 = out.doors[k2]; if (d2.part === pi && d2.u + d2.w / 2 > lo + 0.02 && d2.u - d2.w / 2 < hi - 0.02) return true; }
        return false;
      }
      function own(q, o, lo, hi) { return q.o === o && q.s >= lo - TOL && q.e <= hi + TOL; }
      for (a = 0; a < P.length; a++) {
        var A = P[a];
        if (A.sep || cut[a]) continue;
        for (b = 0; b < P.length; b++) {
          var Bq = P[b];
          if (b === a || Bq.sep || Bq.o !== A.o || cut[b]) continue;
          var c0 = A.c, c1 = Bq.c;
          if (!(c1 - c0 > 0.30 && c1 - c0 <= MAXS)) continue;
          /* the ends: partitions across, standing between the two lines */
          var E = [];
          for (k = 0; k < P.length; k++) {
            var q = P[k];
            if (q.sep || q.o === A.o || cut[k]) continue;
            if (q.c < Math.max(A.s, Bq.s) - TOL || q.c > Math.min(A.e, Bq.e) + TOL) continue;
            if (q.s <= c0 + TOL && q.e >= c1 - TOL) E.push(k);
          }
          E.sort(function (x, y) { return P[x].c - P[y].c; });
          for (k = 0; k + 1 < E.length; k++) {
            var e0 = P[E[k]].c, e1 = P[E[k + 1]].c;
            if (!(e1 - e0 > 0.30 && e1 - e0 <= MAXS)) continue;
            if (doorIn(a, e0, e1) || doorIn(b, e0, e1) || doorIn(E[k], c0, c1) || doorIn(E[k + 1], c0, c1)) continue;
            var X0 = (A.o === 'h') ? e0 : c0, X1 = (A.o === 'h') ? e1 : c1, Z0 = (A.o === 'h') ? c0 : e0, Z1 = (A.o === 'h') ? c1 : e1, j, inside = false, host = null;
            /* nothing inside it: no wall, no room line */
            for (j = 0; j < P.length && !inside; j++) {
              var r = P[j];
              if (j === a || j === b || j === E[k] || j === E[k + 1]) continue;
              var rlo = (r.o === A.o) ? Math.max(r.s, e0) : Math.max(r.s, c0), rhi = (r.o === A.o) ? Math.min(r.e, e1) : Math.min(r.e, c1);
              if (rhi - rlo <= 0.05) continue;
              if ((r.o === A.o) ? (r.c > c0 + TOL && r.c < c1 - TOL) : (r.c > e0 + TOL && r.c < e1 - TOL)) inside = true;
            }
            if (inside) continue;
            /* no room the reader named in it, and one he named round it */
            var ar = (X1 - X0) * (Z1 - Z0), named = false;
            for (j = 0; j < ALLB.length && !named; j++) {
              var R = ALLB[j], ov = Math.max(0, Math.min(R.x1, X1) - Math.max(R.x0, X0)) * Math.max(0, Math.min(R.z1, Z1) - Math.max(R.z0, Z0));
              var rA = (R.x1 - R.x0) * (R.z1 - R.z0);
              if (rA > 0 && ov > 0.5 * Math.min(ar, rA) && rA < 2 * ar) named = true;
            }
            if (named) continue;
            for (j = 0; j < ALLB.length && !host; j++) {
              var H = ALLB[j];
              if (CLOSETS[H.type] || H.type === 'shower' || (H.x1 - H.x0) * (H.z1 - H.z0) < 2 * ar) continue;
              if (X0 >= H.x0 - 0.10 && X1 <= H.x1 + 0.10 && Z0 >= H.z0 - 0.10 && Z1 <= H.z1 + 0.10) host = H;
            }
            if (!host) continue;
            /* the walls that are the box's alone */
            var mine = [], ids = [a, b, E[k], E[k + 1]];
            for (j = 0; j < ids.length; j++) {
              var w = P[ids[j]];
              if ((w.o === A.o) ? own(w, A.o, e0, e1) : own(w, w.o, c0, c1)) mine.push(ids[j]);
            }
            if (!mine.length) continue;
            for (j = 0; j < mine.length; j++) cut[mine[j]] = 1;
            said.push(_impN(X1 - X0) + ' x ' + _impN(Z1 - Z0) + ' m at ' + _impN(X0) + ',' + _impN(Z0) + ' in "' + (host.name || host.type || 'room') + '", ' + mine.length + ' wall(s) its own left out');
            break;
          }
          if (cut[a]) break;
        }
      }
      if (!said.length) return;
      var KP = [], MAPP = {};
      for (k = 0; k < P.length; k++) { if (cut[k]) continue; MAPP[k] = KP.length; KP.push(P[k]); }
      out.doors = out.doors.filter(function (dq) { if (MAPP[dq.part] == null) return false; dq.part = MAPP[dq.part]; return true; });
      out.partitions = KP;
      out.notes.push('BOX (v162): ' + said.length + ' shut box(es) with no door and no name, inside a room, are a cabinet standing in it -- no room, no walls of their own: ' + said.join(', '));
    })();
    /* WINDOWS IN A ROW (v138, Marek: pokud jsou na stene 3 okna, mely by byt
       vsechny stejne a se stejnou rozteci; na puvodnim pudorysu evidentne
       jsou se stejnou rozteci, agent je tak neudelal). The windows of one
       wall that look into the same room -- no wall meets the outer wall
       between them; a room line does not part them -- are one row: two or
       more of nearly the same width (within a third), or three and more of
       any, are made the width most of them have, so the catalogue gives them
       one unit; three and more set out nearly evenly (gaps within a third of
       each other) are set out evenly between the first and the last. A high
       window (sill over 1.2 m) keeps its own size. Nothing is moved onto an
       outer door: where the even set-out would touch one, the windows stay
       where they were read. */
    (function () {
      var NO = out.outline.length, s9, said = [];
      for (s9 = 0; s9 < NO; s9++) {
        var A9 = out.outline[(s9 + NO - 1) % NO], B9 = out.outline[s9], hz9 = Math.abs(B9[1] - A9[1]) < 0.01, dir9 = hz9 ? ((B9[0] >= A9[0]) ? 1 : -1) : ((B9[1] >= A9[1]) ? 1 : -1);
        var line9 = hz9 ? A9[1] : A9[0], base9 = hz9 ? A9[0] : A9[1], WN = [], DR = [], q9;
        for (q9 = 0; q9 < out.openings.length; q9++) {
          var o9 = out.openings[q9];
          if (o9.side !== s9) continue;
          /* v142: a window whose size its own label gives (3050 SH, 6040 SW)
             keeps it -- the plan of 1000073355 has a 6040 between two 3050s,
             and the row made it 0.90; it stands in the row's way instead */
          if (o9.kind === 'window' && !(o9.sill > 1.2) && !(o9.lh > 0)) WN.push(o9); else DR.push(o9);
        }
        if (WN.length < 2) continue;
        WN.sort(function (p, q) { return p.off - q.off; });
        /* a wall meeting this side between two windows parts the row */
        function parted(o1, o2) {
          var lo = Math.min(o1.off, o2.off), hi = Math.max(o1.off, o2.off), j9;
          for (j9 = 0; j9 < out.partitions.length; j9++) {
            var P9 = out.partitions[j9];
            if (P9.sep || P9.o === (hz9 ? 'h' : 'v')) continue;
            var offP = (P9.c - base9) * dir9;
            if (offP <= lo + 0.05 || offP >= hi - 0.05) continue;
            if (Math.min(Math.abs(P9.s - line9), Math.abs(P9.e - line9)) <= 0.6) return true;
          }
          return false;
        }
        var GR = [[WN[0]]], g9;
        for (q9 = 1; q9 < WN.length; q9++) { if (parted(WN[q9 - 1], WN[q9])) GR.push([WN[q9]]); else GR[GR.length - 1].push(WN[q9]); }
        for (g9 = 0; g9 < GR.length; g9++) {
          var R9 = GR[g9];
          if (R9.length < 2) continue;
          var ws = R9.map(function (o) { return o.w; }), wmin = Math.min.apply(null, ws), wmax = Math.max.apply(null, ws), w0 = null;
          if (R9.length >= 3 || wmax / wmin <= 1.34) {
            /* the width most of them have; with no two alike, the middle one */
            var cnt = {}, kk, bestN = 0;
            for (kk = 0; kk < ws.length; kk++) { var key9 = _impR(_impSnap(ws[kk])); cnt[key9] = (cnt[key9] || 0) + 1; }
            for (kk in cnt) if (cnt.hasOwnProperty(kk) && cnt[kk] > bestN) { bestN = cnt[kk]; w0 = Number(kk); }
            if (bestN < 2) { var srt = ws.slice().sort(function (p, q) { return p - q; }); w0 = _impR(_impSnap(srt[srt.length >> 1])); }
          }
          var was = R9.map(function (o) { return { off: o.off, w: o.w }; }), chW = 0, chO = 0;
          if (w0 != null) R9.forEach(function (o) { if (Math.abs(o.w - w0) > 0.01) { o.w = w0; chW++; } });
          if (R9.length >= 3) {
            var gaps = [], f0 = R9[0].off, fN = R9[R9.length - 1].off;
            for (kk = 1; kk < R9.length; kk++) gaps.push(R9[kk].off - R9[kk - 1].off);
            if (Math.max.apply(null, gaps) / Math.max(0.01, Math.min.apply(null, gaps)) <= 1.34) {
              var step9 = (fN - f0) / (R9.length - 1), hit = false;
              var newOff = R9.map(function (o, i9) { return _impR(_impSnap(f0 + step9 * i9)); });
              for (kk = 0; kk < R9.length && !hit; kk++) {
                for (var dq = 0; dq < DR.length; dq++) if (Math.abs(newOff[kk] - DR[dq].off) < (R9[kk].w + DR[dq].w) / 2 + 0.1) { hit = true; break; }
              }
              if (!hit) R9.forEach(function (o, i9) { if (Math.abs(o.off - newOff[i9]) > 0.01) { o.off = newOff[i9]; chO++; } });
            }
          }
          /* and never onto an outer door at its new width either */
          var clash9 = false;
          R9.forEach(function (o) { DR.forEach(function (d9) { if (Math.abs(o.off - d9.off) < (o.w + d9.w) / 2 + 0.05) clash9 = true; }); });
          if (clash9) { R9.forEach(function (o, i9) { o.off = was[i9].off; o.w = was[i9].w; }); continue; }
          if (chW || chO) said.push('side ' + s9 + ': ' + R9.length + ' window(s) ' + was.map(function (v) { return _impN(v.w) + '@' + _impN(v.off); }).join(' ') +
                                   ' -> ' + R9.map(function (o) { return _impN(o.w) + '@' + _impN(o.off); }).join(' ') + (chO ? ' (set out evenly)' : ''));
        }
      }
      if (said.length) out.notes.push('WINDOWS: ' + said.length + ' row(s) of windows into one room made alike (v138): ' + said.join('; '));
    })();
    out.ok = true;
    return out;
  }

  /* ---------- THE SAMPLE FOR ?planTest=1 ---------------------------------
     An L-shaped house 9.0 x 7.0 m, the way a scan would hand it over: in
     pixels at 50 px per metre, lying 1.2 deg crooked, with a few centimetres
     of wobble on the walls (a dimension line is drawn exactly, so it has
     none), one door a little too wide, one more a little too narrow, a door
     drawn where there is no wall and three dimension lines of which one is
     drawn 12 cm too long. The lines say what came back. */
  function _impSampleRaw() {
    var ang = 1.2 * Math.PI / 180, cA = Math.cos(ang), sA = Math.sin(ang), PX = 50, ox = 40, oy = 30, k = 0;
    var jit = [0.02, -0.015, 0.01, -0.02, 0.015, 0, -0.01, 0.02, -0.005, 0.01];
    function p(x, z, exact) {
      var j = exact ? 0 : jit[(k++) % jit.length];
      x += j; z -= j * 0.5;
      return [Math.round((x * cA - z * sA) * PX + ox), Math.round((x * sA + z * cA) * PX + oy)];
    }
    return {
      units: 'px',
      outline: [p(0, 0), p(9, 0), p(9, 4.5), p(6, 4.5), p(6, 7), p(0, 7)],
      dims: [{ a: p(0, -0.6, 1), b: p(9, -0.6, 1), m: 9.0 }, { a: p(-0.6, 0, 1), b: p(-0.6, 7, 1), m: 7.0 }, { a: p(6, 7.6, 1), b: p(9.12, 7.6, 1), m: 3.0 }],
      partitions: [{ a: p(4.0, 0), b: p(4.03, 4.5) }, { a: p(0.1, 4.5), b: p(5.9, 4.52) }, { a: p(3.38, 4.5), b: p(3.43, 6.8) }],
      doors: [{ at: p(4.0, 3.6), w: 0.83 * PX }, { at: p(1.6, 4.5), w: 0.9 * PX }, { at: p(4.8, 4.5), w: 0.72 * PX },
              { at: p(2.0, 0), w: 0.9 * PX }, { at: p(7.8, 6.2), w: 0.8 * PX }],
      windows: [{ at: p(6.5, 0), w: 1.2 * PX }, { at: p(0, 2.2), w: 1.0 * PX }, { at: p(9, 2.2), w: 1.0 * PX },
                { at: p(1.7, 7), w: 1.2 * PX }, { at: p(7.5, 4.5), w: 0.9 * PX }],
      rooms: [{ at: p(2, 2), label: 'Living room' }, { at: p(6.5, 2.2), label: 'Kitchen' },
              { at: p(1.7, 5.8), label: 'Bedroom' }, { at: p(4.7, 5.8), label: 'Bath' }]
    };
  }
  /* a door's kind in a line: doorway, sliding, a pair, or which way it swings */
  function _impDoorKind(d) {
    if (d.pass) return ' doorway';
    if (d.kind === 'pair') return ' HINGED PAIR sw' + (d.sw | 0);
    if (d.kind === 'bifold') return ' BIFOLD sw' + (d.sw | 0);
    if (d.kind === 'bypass') return ' BYPASS';
    if (d.slide === 3) return ' sliding pair';
    if (d.slide) return ' sliding';
    return ' sw' + (d.sw | 0);
  }
  /* A DOOR'S OWN LABEL GIVES ITS SIZE (v106). US plans print it as four
     digits, feet and inches of the width then of the height -- 2668 is 2'-6"
     by 6'-8", 5068 BP is a 5'-0" bypass, 2/2068 is two leaves of 2'-0". The
     reader copies the text and the code reads it; it is the plan's own
     number, so it wins over a width measured off the picture. */
  function _impWinLabel(t) {
    var m = /(?:^|[^0-9])([1-9])([0-9])([1-8])([0-9])(?![0-9])/.exec(String(t || ''));
    if (!m) return null;
    var w = (Number(m[1]) * 12 + Number(m[2])) * 0.0254, h = (Number(m[3]) * 12 + Number(m[4])) * 0.0254;
    return (w >= 0.3 && w <= 3.7 && h >= 0.3 && h <= 2.6) ? { w: w, h: h } : null;
  }
  function _impDoorLabelW(t) {
    var m = /(?:^|[^0-9])(?:([1-4])\s*\/\s*)?([1-9])([0-9])([6-8])([0-9])(?![0-9])/.exec(String(t || ''));
    if (!m) return 0;
    var n = m[1] ? Number(m[1]) : 1, w = n * (Number(m[2]) * 12 + Number(m[3])) * 0.0254;
    return (w >= 0.4 && w <= 2.6) ? w : 0;
  }
  function _impLines(n, title) {
    var L = [], i;
    if (n.px) L.push('PLAN walls from the picture: ' + (n.px.used ? ((n.px.thr ? ('read at grey under ' + n.px.thr + ', ') : '') + n.px.walls + ' inside the outline' + (n.px.box ? ', the outline from the picture' : '') +
                                                       (n.px.islands ? (', ' + n.px.islands + ' stroke(s) hanging on no wall left out -- lettering or a symbol (v128)') : '') +
                                                       (n.px.combs ? (', ' + n.px.combSegs + ' short stroke(s) in ' + n.px.combs + ' row(s) left out -- a wardrobe\'s hangers or a hatch, not walls (v133)') : '') +
                                                       (n.px.leaves ? (', ' + n.px.leaves + ' slanted line(s) as thin as a door leaf left out -- leaves drawn ajar, not walls (v142)') : '') +
                                                       (n.px.diagDoors ? (', ' + n.px.diagDoors + ' door(s) left in the slanted wall the reader put them in (v128)') : '') +
                                                       (n.px.turned ? (', ' + n.px.turned + ' door(s) the reader laid along their open leaf turned into the wall at their hinge (v142)') : '') +
                                                       ', ' + n.px.sides + ' outline side(s) put on the picture\'s wall, ' +
                     (n.px.showerSide ? (n.px.showerSide.length + ' closet(s) against a bathroom with no wall drawn between taken into it as its shower (v163): ' + n.px.showerSide.join(', ') + ', ') : '') +
                     n.px.model + ' of the reader\'s walls kept where the picture has none' + (n.px.jambs ? (' (' + n.px.jambs + ' of them at a door\'s jamb or turning the corner from one, v140)') : '') + ', ' + n.px.dropped + ' left out (in line with a picture wall, or with a free end' +
                     (n.px.thin ? (', ' + n.px.thin + ' of them because the picture draws no wall there (' + (n.px.thinSaid || []).join('; ') + '), v161') : '') + '), ' +
                     n.px.doors + ' door(s) and ' + n.px.wins + ' window(s) put into the break in the wall' + (n.px.bridged ? (' (the wall run on through ' + n.px.bridged + ' of the doors)') : '') + (n.px.capped ? (' (' + n.px.capped + ' door(s) kept within a fifth of the width drawn, the rest of the break walled, v145)') : '') +
                     (n.px.byName ? (', ' + n.px.byName + ' more door(s) into the break between the two rooms they join') : '') +
                     ', ' + (n.px.boxes || 0) + ' room box(es) put on the walls') : ('none -- ' + n.px.why)));
    /* the doors read off the picture (v117) */
    if (n.px && n.px.used && n.px.leaf) {
      var LF = n.px.leaf;
      L.push('PLAN doors from the picture: ' + (LF.why ? ('none -- ' + LF.why) :
             (LF.looked + ' inside door(s) looked at, a leaf found at ' + LF.found + ' -- ' + LF.moved + ' hinge(s) moved to the other jamb, ' +
              LF.turned + ' turned to open into the other room, ' + LF.kinds + ' of another kind; ' + LF.kept + ' kept as read' +
              (LF.swept ? (', ' + LF.swept + ' leaf/leaves that would sweep another door\'s opening set aside (v134)') : '') + ' (' + (LF.ms || 0) + ' ms)' +
              (LF.said && LF.said.length ? (': ' + LF.said.join(' | ')) : ''))));
    }
    /* v130: the doors in the slanted walls */
    if (n.px && n.px.used && n.px.dleaf && (n.px.dleaf.looked || n.px.dleaf.why !== 'no slanted wall')) {
      var DL = n.px.dleaf;
      L.push('PLAN slanted doors from the picture: ' + (DL.why ? ('none -- ' + DL.why) :
             (DL.looked + ' looked at, a leaf found at ' + DL.found + ' -- ' + DL.moved + ' hinge(s) moved to the other jamb, ' + DL.turned + ' turned to open the other way; ' +
              DL.kept + ' kept as read (' + (DL.ms || 0) + ' ms)' + (DL.said && DL.said.length ? (': ' + DL.said.join(' | ')) : '') + ' (v130)')));
    }
    L.push('PLAN ' + title + ' | ' + (n.ok ? 'read' : ('NOT READ: ' + n.why)) + ' | scale ' + (n.scale ? (n.scale.toFixed(4) + ' m/px from ' + n.scaleFrom) : '-') +
           (n.spread ? (' (spread ' + (n.spread * 100).toFixed(1) + '%)') : '') + (n.turn ? (' | turned ' + n.turn.toFixed(1) + ' deg') : ''));
    if (n.ok) {
      L.push('PLAN outline ' + n.outline.length + ' corners, ' + _impN(n.w) + ' x ' + _impN(n.d) + ' m: ' +
             n.outline.map(function (v) { return _impN(v[0]) + ',' + _impN(v[1]); }).join(' '));
      L.push('PLAN partitions ' + n.partitions.length + ': ' + n.partitions.map(function (p) {
        return p.o + ' ' + (p.o === 'v' ? 'x' : 'z') + _impN(p.c) + ' ' + (p.o === 'v' ? 'z' : 'x') + _impN(p.s) + '..' + _impN(p.e) + (p.sep ? ' ROOM LINE' : '');
      }).join(' | '));
      L.push('PLAN doors inside ' + n.doors.length + ': ' + n.doors.map(function (d) {
        return 'on partition ' + d.part + ' at ' + _impN(d.u) + ' w ' + _impN(d.w) + _impDoorKind(d);
      }).join(' | '));
      if (n.closets && n.closets.length) {
        L.push('PLAN closets ' + n.closets.length + ' (under 3 m2 and named as a closet by the plan -- walls only, no room): ' +
               n.closets.map(function (c) { return '"' + c.label + '" ' + c.type + ' ' + c.area.toFixed(1) + ' m2'; }).join(' | '));
      }
      var od = [], ow = [];
      for (i = 0; i < n.openings.length; i++) {
        var o = n.openings[i], t = 'side ' + o.side + ' at ' + _impN(o.off) + ' w ' + _impN(o.w);
        if (o.kind === 'door') od.push(t); else ow.push(t);
      }
      L.push('PLAN outer doors ' + od.length + ': ' + od.join(' | ') + ' || windows ' + ow.length + ': ' + ow.join(' | '));
      L.push('PLAN rooms ' + n.rooms.length + ': ' + n.rooms.map(function (r) {
        return '"' + r.label + '" ' + (r.key || 'NO KEY') + ' at ' + _impN(r.x) + ',' + _impN(r.z);
      }).join(' | '));
      if (n.fixtures) L.push('PLAN fixtures ' + n.fixtures.length + ': ' + n.fixtures.map(function (q) {
        return q.type + (q.label ? (' "' + q.label + '"') : '') + (q.sug ? ' SUGGESTED' : '') + (q.seats ? (' seats ' + q.seats) : '') + (q.stools ? (' stools ' + q.stools) : '') +
               ' ' + _impN(q.x1 - q.x0) + 'x' + _impN(q.z1 - q.z0) +
               ' at ' + _impN((q.x0 + q.x1) / 2) + ',' + _impN((q.z0 + q.z1) / 2);
      }).join(' | '));
      if (n.diag && n.diag.length) L.push('PLAN slanted walls ' + n.diag.length + ': ' + n.diag.map(function (g) {
        return _impN(g.a[0]) + ',' + _impN(g.a[1]) + ' -> ' + _impN(g.b[0]) + ',' + _impN(g.b[1]) + ' (' + _impN(g.len) + ' m, ' + g.ang + ' deg' +
               ((g.doors && g.doors.length) ? (', door(s) ' + g.doors.map(function (d) {
                 /* v130: where it is hung and which way it opens, as the plan will build it */
                 var gl = Math.hypot(g.b[0] - g.a[0], g.b[1] - g.a[1]) || 1, ux = (g.b[0] - g.a[0]) / gl, uz = (g.b[1] - g.a[1]) / gl;
                 var cx = g.a[0] + ux * d.u, cz = g.a[1] + uz * d.u, hg = '', op = '';
                 if (d.hp) hg = (((d.hp[0] - cx) * ux + (d.hp[1] - cz) * uz) > 0.02) ? ', hinge at the b end' : ', hinge at the a end';
                 if (d.op) op = (((d.op[0] - cx) * (-uz) + (d.op[1] - cz) * ux) > 0.02) ? ', opens to +' : ', opens to -';
                 return d.kind + ' ' + _impN(d.w) + ' at ' + _impN(d.u) + hg + op;
               }).join(', ')) : ', no door') + ')';
      }).join(' | '));
    }
    L.push('PLAN changed: ' + (n.notes.length ? n.notes.join('; ') : 'nothing'));
    return L;
  }

  /* =========================================================================
     THE PLAN IMPORT, PART 2 (v95): the straightened plan WRITTEN INTO THE
     PROJECT, as one step that Undo takes back.

     Two moves, so the person can see what will happen before it does:

       fit(n)     where the plan will land in THIS product, worked out and
                  nothing written: turned a quarter if the product builds it
                  only that way round, the size brought onto the product's
                  own module (the ADU builds on half a sheet), the rooms
                  stretched with it so every wall keeps its place in the
                  plan, every window and door matched to a unit the
                  catalogue sells. Each change is said.
       apply(f)   the outline, the partitions, the doors in them, the
                  windows and outer doors, the room names -- written in one
                  go, saved, built once, and the state before it kept as one
                  Undo step.

     It talks to the house core every house product shares (HouseLib), so
     the ADU, the shed, the sauna, the T house and the A-frame take it with
     no code of their own. A plan the product cannot build -- an L where it
     builds a rectangle, a house past its limits -- is refused, with why.
     ========================================================================= */
  function _impCore() {
    var C = global.HouseLib;
    if (!C || !C.STATE || !C.STATE.footprint || typeof C.rebuild !== 'function') return null;
    return C;
  }
  /* the unit the catalogue sells nearest to what the plan shows: a window
     among the windows, a door under 1.2 m among the entry doors, a wider one
     among the glazed sliding doors. A left hand where there is a choice. */
  function _impUnit(kind, w, sill, lh, pref) {
    /* v142: a pair of doors to a store -- the double exterior door; with none
       in the catalogue, the glazed sliders */
    if (kind === 'door' && pref === 'pair') {
      var UP = global.ConfiguratorUI, GD = null, bP = null, dP = 1e9, iP;
      try { GD = (UP && typeof UP.openingGroup === 'function') ? UP.openingGroup('door') : null; } catch (eP) { GD = null; }
      for (iP = 0; GD && GD.items && iP < GD.items.length; iP++) {
        var itP = GD.items[iP];
        if (!itP || !(itP.w > 0) || !((itP.leaves || 1) >= 2)) continue;
        if (Math.abs(itP.w - w) < dP) { dP = Math.abs(itP.w - w); bP = itP; }
      }
      if (bP) return { kind: 'door', code: bP.code, w: bP.w, h: bP.h, hand: null, leaves: bP.leaves || 2, name: bP.name || 'door', sill: 0, off: dP };
      pref = 'slide';
    }
    var U = global.ConfiguratorUI, grp = (kind === 'door') ? ((w >= 1.2 || pref === 'slide') ? 'french' : 'door') : 'window', G = null, i, it, best = null, bd = 1e9, d;
    try { G = (U && typeof U.openingGroup === 'function') ? U.openingGroup(grp) : null; } catch (e) { G = null; }
    if (!G || !G.items) return null;
    /* v136: the sliders only, or the hinged ones only, where the glazed
       group has both */
    var nSl = 0, nUse = 0;
    for (i = 0; i < G.items.length; i++) { it = G.items[i]; if (!it || !(it.w > 0) || it.full || it.round || it.hand === 'R') continue; nUse++; if (_impIsSlider(it)) nSl++; }
    var only = (grp === 'french' && pref === 'slide' && nSl) ? 1 : ((grp === 'french' && pref === 'swing' && nUse > nSl) ? -1 : 0);
    /* the width decides; between units of the same width, the one whose
       head lands nearest 2.30 over the floor -- a 0.90 x 1.35, not the
       0.90 x 0.50 strip that happens to come first in the list */
    var HEAD = 2.30, bh = 1e9, hd;
    for (i = 0; i < G.items.length; i++) {
      it = G.items[i];
      if (!it || !(it.w > 0) || it.full || it.round || it.hand === 'R') continue;
      if (only && (only > 0) !== _impIsSlider(it)) continue;
      /* a glass wall down to the floor only where the plan's window is one */
      if (grp === 'window' && it.sill === 0 && sill > 0.05) continue;
      d = Math.abs(it.w - w);
      hd = (lh > 0 && grp === 'window') ? Math.abs((it.h || 0) - lh) : Math.abs(((grp === 'window') ? sill : 0) + (it.h || 0) - HEAD);
      if (d < bd - 1e-6 || (Math.abs(d - bd) <= 1e-6 && hd < bh)) { bd = d; bh = hd; best = it; }
    }
    if (!best) return null;
    return { kind: grp, code: best.code, w: best.w, h: best.h, hand: best.hand || null, leaves: best.leaves || null,
             name: best.name || grp, sill: (grp === 'window') ? sill : 0, off: bd };
  }
  /* where a partition end meets something, read in the plan's own frame */
  function _impEndOf(NP, k, v, low, len, WT) {
    var p = NP[k], j, q, E = 0.03;
    if (low ? (v <= WT + E) : (v >= len - WT - E)) return { t: 'shell' };
    for (j = 0; j < NP.length; j++) {
      q = NP[j];
      if (j === k || q.o === p.o) continue;
      if (Math.abs(q.c - v) <= E && p.c >= q.s - 0.05 && p.c <= q.e + 0.05) return { t: 'part', j: j };
    }
    return { t: 'free' };
  }

  /* THE DIFFERENCE TAKEN FROM THE ROOMS THAT CAN GIVE IT (v145, Marek: ubrat
     z obyvaku, kuchyne a loznic -- koupelny, satny, pradelna a chodba
     zustanou podle vykresu). The plan was stretched evenly onto the size the
     product builds, so 7.55 m drawn and 7.32 m built took 3 % off every room
     -- and a 5 ft bathroom no longer held its 60 in tub. Now, along each
     axis, the plan is cut into bands at its walls; a band is given or
     taken only where every space it crosses is a living room, a kitchen, a
     dining room, a bedroom, a kids room or an office (a reach-in closet or
     a store goes along with the room it stands in); the bathrooms, the WC,
     the walk-in closets, the laundry, the pantry, the halls and the entry
     -- and any space with no name -- keep their size as drawn. A band gives
     at most 35 % of its width, and a bedroom stays 2.44 m (8 ft) across at
     least. When that is not enough, the rest is taken evenly, as before,
     and the line says so. Returns { map, said } or null when nothing is to
     be given or taken. The picture under the plan stays as it was laid --
     it is one image and is scaled evenly. */
  var IMP_BAND_FLEX = /rooms\.(livingroom|kitchen|diningroom|bedroom|office)|container_room_7/;
  var IMP_BAND_RIGIDCL = /^(laundry|utility|wardrobe|pantry)$/;
  function _impBands(n, turned, ax, from, to, WT) {
    var D = from - to;
    if (!n || !(from > 0) || !(to > 0) || Math.abs(D) < 0.01) return null;
    var Wr = turned ? n.d : n.w, Dr = turned ? n.w : n.d, CS = 0.1, NX = Math.max(1, Math.ceil(Wr / CS)), NZ = Math.max(1, Math.ceil(Dr / CS)), i, k, q;
    function rot(p) { return turned ? [n.d - p[1], p[0]] : [p[0], p[1]]; }
    /* the walls in the turned frame */
    var PS = [];
    for (k = 0; k < (n.partitions || []).length; k++) {
      var p0 = n.partitions[k], o = p0.o, c = p0.c, s0 = p0.s, e0 = p0.e;
      if (turned) { if (o === 'v') { o = 'h'; var t0 = s0; s0 = n.d - e0; e0 = n.d - t0; } else { o = 'v'; c = n.d - c; } }
      PS.push({ o: o, c: c, s: Math.min(s0, e0), e: Math.max(s0, e0) });
    }
    var G = new Int32Array(NX * NZ), WALL = -2;
    for (i = 0; i < G.length; i++) G[i] = -1;
    for (k = 0; k < PS.length; k++) {
      var P = PS[k];
      for (i = 0; i < NX; i++) for (q = 0; q < NZ; q++) {
        var cx = (i + 0.5) * CS, cz = (q + 0.5) * CS, al = P.o === 'h' ? cx : cz, ac = P.o === 'h' ? cz : cx;
        if (Math.abs(ac - P.c) <= CS / 2 + 1e-6 && al >= P.s - 0.15 && al <= P.e + 0.15) G[q * NX + i] = WALL;
      }
    }
    /* and the outer walls round it all, so no space runs round a wall's end */
    var nb9 = Math.max(1, Math.round(WT / CS));
    for (i = 0; i < NX; i++) for (q = 0; q < NZ; q++) if (i < nb9 || q < nb9 || i >= NX - nb9 || q >= NZ - nb9) G[q * NX + i] = WALL;
    /* the spaces, each grown from the name written in it */
    var RG = [];
    for (k = 0; k < (n.rooms || []).length; k++) {
      var rp = rot([n.rooms[k].x, n.rooms[k].z]), gi = Math.max(0, Math.min(NX - 1, Math.floor(rp[0] / CS))), gq = Math.max(0, Math.min(NZ - 1, Math.floor(rp[1] / CS)));
      var at = gq * NX + gi;
      if (G[at] === WALL) { var nb = [at + 1, at - 1, at + NX, at - NX]; for (q = 0; q < 4; q++) if (nb[q] >= 0 && nb[q] < G.length && G[nb[q]] !== WALL) { at = nb[q]; break; } }
      if (G[at] === WALL) continue;
      if (G[at] >= 0) { RG[G[at]].keys.push(String(n.rooms[k].key || '')); RG[G[at]].names.push(String(n.rooms[k].label || n.rooms[k].key || '')); continue; }
      var id = RG.length, Q = [at], cells = 0, mn = 1e9, mx = -1e9;
      RG.push({ keys: [String(n.rooms[k].key || '')], names: [String(n.rooms[k].label || n.rooms[k].key || '')], n: 0, lo: 0, hi: 0 });
      G[at] = id;
      while (Q.length) {
        var cI = Q.pop(), ci = cI % NX, cq = (cI - ci) / NX;
        cells++;
        var ca = ax === 'x' ? (ci + 0.5) * CS : (cq + 0.5) * CS;
        if (ca < mn) mn = ca; if (ca > mx) mx = ca;
        var N4 = [ci > 0 ? cI - 1 : -1, ci < NX - 1 ? cI + 1 : -1, cq > 0 ? cI - NX : -1, cq < NZ - 1 ? cI + NX : -1];
        for (q = 0; q < 4; q++) if (N4[q] >= 0 && G[N4[q]] === -1) { G[N4[q]] = id; Q.push(N4[q]); }
      }
      RG[id].n = cells; RG[id].lo = mn - CS / 2; RG[id].hi = mx + CS / 2;
    }
    for (k = 0; k < RG.length; k++) {
      RG[k].flex = RG[k].keys.every(function (kk) { return IMP_BAND_FLEX.test(kk); });
      RG[k].bed = RG[k].keys.some(function (kk) { return /rooms\.bedroom|container_room_7/.test(kk); });
    }
    /* the closets that keep their size; a reach-in closet or a store goes
       along with its room */
    var CLR = [], CLF = [];
    for (k = 0; k < (n.closets || []).length; k++) {
      var cl = n.closets[k];
      if (!cl || !cl.box) continue;
      var cA = rot([cl.box[0], cl.box[1]]), cB = rot([cl.box[2], cl.box[3]]);
      if (!IMP_BAND_RIGIDCL.test(String(cl.type || ''))) { CLF.push([Math.min(cA[0], cB[0]) - 0.1, Math.max(cA[0], cB[0]) + 0.1, Math.min(cA[1], cB[1]) - 0.1, Math.max(cA[1], cB[1]) + 0.1]); continue; }
      CLR.push(ax === 'x' ? [Math.min(cA[0], cB[0]), Math.max(cA[0], cB[0])] : [Math.min(cA[1], cB[1]), Math.max(cA[1], cB[1])]);
    }
    function inCLF(x, z) { for (var q8 = 0; q8 < CLF.length; q8++) if (x >= CLF[q8][0] && x <= CLF[q8][1] && z >= CLF[q8][2] && z <= CLF[q8][3]) return true; return false; }
    /* the bands: the inner faces and every wall across this axis */
    var BK = [WT, from - WT];
    for (k = 0; k < PS.length; k++) if ((ax === 'x' && PS[k].o === 'v') || (ax === 'z' && PS[k].o === 'h')) if (PS[k].c > WT + 0.05 && PS[k].c < from - WT - 0.05) BK.push(PS[k].c);
    BK.sort(function (a, b) { return a - b; });
    var B = [BK[0]];
    for (k = 1; k < BK.length; k++) if (BK[k] - B[B.length - 1] > 0.05) B.push(BK[k]); else B[B.length - 1] = (B[B.length - 1] + BK[k]) / 2;
    if (B.length < 2) return null;
    var BD = [];
    for (k = 0; k + 1 < B.length; k++) {
      var b0 = B[k], b1 = B[k + 1], seen = {}, flex = true, unnamed = 0, rs = [];
      for (i = 0; i < NX; i++) for (q = 0; q < NZ; q++) {
        var cc = ax === 'x' ? (i + 0.5) * CS : (q + 0.5) * CS;
        if (cc < b0 || cc >= b1) continue;
        var g9 = G[q * NX + i];
        if (g9 === WALL) continue;
        if (g9 < 0) { if (!inCLF((i + 0.5) * CS, (q + 0.5) * CS)) unnamed++; continue; }
        if (!seen[g9]) { seen[g9] = 1; rs.push(g9); if (!RG[g9].flex) flex = false; }
      }
      var why9 = flex ? '' : 'a bathroom, a hall or such';
      if (unnamed * CS * CS > 0.3) { flex = false; why9 = _impN(unnamed * CS * CS) + ' m2 with no name'; }
      for (q = 0; q < CLR.length && flex; q++) if (Math.min(b1, CLR[q][1]) - Math.max(b0, CLR[q][0]) > 0.05) { flex = false; why9 = 'a laundry, pantry or walk-in closet'; }
      if (!rs.length) { flex = false; why9 = 'no named space'; }
      BD.push({ a: b0, b: b1, w: b1 - b0, flex: flex, rs: rs, cap: 0, give: 0, why: why9 });
    }
    var said = [], tot = 0;
    if (D > 0) {
      /* taken: at most 35 % of a band, a bedroom 2.44 m across at least */
      for (k = 0; k < BD.length; k++) if (BD[k].flex) BD[k].cap = 0.35 * BD[k].w;
      for (q = 0; q < RG.length; q++) {
        if (!RG[q].bed) continue;
        var span = RG[q].hi - RG[q].lo, room9 = Math.max(0, span - 2.44);
        for (k = 0; k < BD.length; k++) if (BD[k].flex && BD[k].rs.indexOf(q) >= 0) BD[k].cap = Math.min(BD[k].cap, room9 * BD[k].w / Math.max(span, 0.01));
      }
      var C = 0;
      for (k = 0; k < BD.length; k++) C += BD[k].cap;
      if (C >= D) { for (k = 0; k < BD.length; k++) BD[k].give = D * BD[k].cap / C; }
      else {
        var rest = D - C, all = 0;
        for (k = 0; k < BD.length; k++) all += BD[k].w;
        for (k = 0; k < BD.length; k++) BD[k].give = BD[k].cap + rest * BD[k].w / all;
        said.push('the rooms that can give it had only ' + _impN(C) + ' m -- the other ' + _impN(rest) + ' m taken evenly, the bathrooms and halls too');
      }
    } else {
      /* given: to the same rooms, by their width */
      var FW = 0;
      for (k = 0; k < BD.length; k++) if (BD[k].flex) FW += BD[k].w;
      for (k = 0; k < BD.length; k++) BD[k].give = FW > 0 ? (BD[k].flex ? D * BD[k].w / FW : 0) : D * BD[k].w / (B[B.length - 1] - B[0]);
      if (!(FW > 0)) said.push('no living room, kitchen or bedroom runs across this side alone -- taken evenly');
    }
    /* the map: band by band */
    var T = [B[0]];
    for (k = 0; k < BD.length; k++) T.push(T[k] + BD[k].w - BD[k].give);
    function map(v) {
      if (v <= B[0]) return v;
      if (v >= B[B.length - 1]) return v + (to - from);
      for (var j = 0; j < BD.length; j++) if (v <= B[j + 1]) return T[j] + (v - B[j]) * (T[j + 1] - T[j]) / Math.max(B[j + 1] - B[j], 1e-6);
      return v + (to - from);
    }
    /* what each named space gave or took */
    var per = {};
    for (k = 0; k < BD.length; k++) {
      if (Math.abs(BD[k].give) < 0.005) continue;
      tot += BD[k].give;
      var nmK = BD[k].rs.map(function (r9) { return RG[r9].names.join('/'); }).join(' + ') || 'no named space';
      per[nmK] = (per[nmK] || 0) + BD[k].give;
    }
    var parts9 = Object.keys(per).map(function (kq) { return _impN(Math.abs(per[kq])) + ' m ' + (D > 0 ? 'from ' : 'to ') + kq; });
    said.unshift(_impN(from) + ' -> ' + _impN(to) + ' m along ' + (ax === 'x' ? 'the width' : 'the length') + ': ' + (parts9.join(', ') || 'nothing'));
    /* and the bands themselves, for the log */
    said.push('bands ' + BD.map(function (b9) {
      var nm9 = b9.rs.map(function (r9) { return RG[r9].names.join('/'); }).join('+') || '-';
      return _impN(b9.w) + ' ' + nm9 + (b9.flex ? (' gives up to ' + _impN(b9.cap)) : (' kept' + (b9.why ? (' (' + b9.why + ')') : '')));
    }).join(' | '));
    return { map: map, said: said.join('; '), bands: BD.length };
  }
  var IMP_SCALE_MAX = 0.10;
  /* BUILT SMALLER ON THE BUYER'S WORD (v171, Marek: aby uzivatel dostal
     odpoved na svuj upload -- oboji: open it in a product that builds it, or
     build it here smaller). Only when the buyer taps it in the bubble
     (_imp.squeeze), and up to 35 % over the largest size; the difference is
     taken as for the 10 % (SIZE, v145) and the plan never goes to the
     library -- it is not the drawing any more. */
  var IMP_SQUEEZE_MAX = 0.35;
  function _impFit(n, opts) {
    opts = opts || {};
    var C = _impCore(), H = _PH, i, k;
    var f = { ok: false, core: true, why: '', notes: [], turned: false, from: { w: n ? n.w : 0, d: n ? n.d : 0 },
              w: 0, l: 0, ox: 0, oz: 0, x0: 0, x1: 0, z0: 0, z1: 0, wt: 0.16, storey: 0,
              parts: [], doors: [], openings: [], pins: [] };
    if (!n || !n.ok) { f.why = 'plan'; f.notes.push('the plan was not read'); return f; }
    if (!C || !H) { f.why = 'core'; f.notes.push('this configurator has no house core yet -- a plan cannot be written into it'); return f; }
    if (n.outline.length !== 4) {
      f.why = 'shape';
      f.notes.push('this product builds a rectangle and the plan has ' + n.outline.length + ' corners -- nothing written');
      return f;
    }
    var S = C.STATE, LIM = C.LIMITS || { minW: 4, maxW: 8, minL: 4, maxL: 12 };
    var maxW = LIM.maxW, maxL = LIM.maxL;
    /* the roof's span limit, the same one the core holds a drag to */
    if (S.maxSpan > 0) {
      if (S.roof && S.roof.axis === 'l') maxW = Math.min(maxW, S.maxSpan); else maxL = Math.min(maxL, S.maxSpan);
    }
    /* v170: the sizes of the OUTER faces where the wall reaches past the outline */
    var OUTF = _impOut();
    if (OUTF) {
      LIM = { minW: LIM.minW + 2 * OUTF, maxW: LIM.maxW + 2 * OUTF, minL: LIM.minL + 2 * OUTF, maxL: LIM.maxL + 2 * OUTF };
      maxW += 2 * OUTF; maxL += 2 * OUTF;
      f.notes.push('OUTER (v170): the plan is its outer faces and this house is built on its wall axis, ' + _impN(OUTF) + ' inside them -- fitted over the outer faces, written ' + _impN(2 * OUTF) + ' smaller on the axis');
    }
    f.out = OUTF;
    /* THE PLAN'S OWN UNITS (v100): a plan in feet is built in feet -- APPLY
       switches the project, so the size is brought onto the module of the
       units it will have, which the core gives with the flag set for the
       moment of the question and put straight back */
    f.units = (opts.units === 'imperial' || opts.units === 'metric') ? opts.units : '';
    var flip = f.units && ((f.units === 'imperial') === !!S.isMetric);
    if (flip) f.notes.push('the plan is drawn in ' + (f.units === 'imperial' ? 'feet and inches' : 'metres') + ' -- APPLY switches this project to ' + f.units + ' units');
    function snap(v, key, lo, hi) {
      var r = v, was = S.isMetric;
      try {
        if (flip) S.isMetric = (f.units === 'metric');
        if (typeof C.snapDim === 'function') r = OUTF ? (C.snapDim(v - 2 * OUTF, key, { min: lo - 2 * OUTF, max: hi - 2 * OUTF }) + 2 * OUTF) : C.snapDim(v, key, { min: lo, max: hi });
      } catch (e) { r = v; }
      finally { S.isMetric = was; }
      if (!(r > 0)) r = v;
      return Math.max(lo, Math.min(hi, r));
    }
    function size(w, l) {
      var a = snap(w, 'w', LIM.minW, maxW), b = snap(l, 'l', LIM.minL, maxL);
      return { w: a, l: b, d: Math.max(Math.abs(a - w), Math.abs(b - l)) };
    }
    /* AS DRAWN WHEREVER IT FITS; turned a quarter only when it does not */
    /* THE LONG SIDE ALONG THE RIDGE (v132, Marek: pudorysy otacet tak, aby
       delsi stena byla vzdy na eave strane sedlove strechy -- rozpon mensi =
       levnejsi strecha; a pritom se da vymyslet zakryta terasa vysunutym
       stitem). The plan is turned so its longer side runs with the ridge and
       the roof spans the shorter one -- a 6.15 x 4.65 m render was built 6.10
       across. Where the two sides are within 6 % of each other the span hardly
       changes, so a porch or a balcony decides: it goes to a gable end, where
       the gable pulled out over it roofs it. A turn the product cannot build
       falls back to the other way round, and the line says why. */
    var TOL = 0.5, A = size(n.w, n.d), B = size(n.d, n.w), pick = null;
    var ridgeL = !(S.roof && S.roof.axis === 'w'), wantTurn = ridgeL ? (n.w > n.d + 0.005) : (n.w + 0.005 < n.d), turnWhy = 'ridge';
    if (Math.abs(n.w - n.d) / Math.max(n.w, n.d, 0.01) <= 0.06 && n.outdoor && n.outdoor.length) {
      var onX = 0, onZ = 0, oq9;
      for (i = 0; i < n.outdoor.length; i++) {
        oq9 = n.outdoor[i];
        var ocx = (oq9.x0 + oq9.x1) / 2, ocz = (oq9.z0 + oq9.z1) / 2;
        if (ocz < 0.3 || ocz > n.d - 0.3) onZ++; else if (ocx < 0.3 || ocx > n.w - 0.3) onX++;
      }
      if (onX > onZ) { wantTurn = ridgeL; turnWhy = 'porch'; } else if (onZ > onX) { wantTurn = !ridgeL; turnWhy = 'porch'; }
    }
    var PW1 = wantTurn ? B : A, PW2 = wantTurn ? A : B, turnFell = false;
    if (PW1.d <= TOL) { pick = PW1; f.turned = wantTurn; }
    else if (PW2.d <= TOL) { pick = PW2; f.turned = !wantTurn; turnFell = true; }
    /* UP TO 10 % TOO BIG (v145, Marek: pokud je pudorys do 10 % rozdil,
       mohli bychom vygenerovat o neco zmenseny (max ADU), s upozornenim, ze
       tento konfigurator ma omezeni). Plan 1000073678 is 13.11 m long and
       the ADU goes to 12.00 m. A plan no more than 10 % over the largest
       size, either way round, is built at the largest size; the difference
       is taken from the living rooms, the kitchen and the bedrooms like any
       other, and the buyer is told. */
    /* AND UP TO 10 % TOO SMALL (v160, Marek: tech 10 % pravidlo budeme
       pouzivat -- after a sauna plan 5.36 x 2.54 m was refused in the ADU,
       which builds from 4.00 x 4.00 m, under a bubble that spoke of the
       largest size only). A side no more than 10 % under the smallest this
       product builds is built at the smallest, the rooms stretched with it as
       with any other size; more than that, and the plan is refused with the
       smallest size in the bubble. */
    if (!pick) {
      var SCC = [[PW1, wantTurn], [PW2, !wantTurn]], sc9;
      for (sc9 = 0; sc9 < 2 && !pick; sc9++) {
        var cand = SCC[sc9][0], tn = SCC[sc9][1], w9 = tn ? n.d : n.w, l9 = tn ? n.w : n.d;
        var ow = w9 / maxW, ol = l9 / maxL, uw = (w9 > 0) ? LIM.minW / w9 : 99, ul = (l9 > 0) ? LIM.minL / l9 : 99, off9 = Math.max(ow, ol, uw, ul);
        var SMX = (_imp.squeeze > 0 && (ow > 1 || ol > 1) && !(uw > 1 || ul > 1)) ? Math.max(IMP_SCALE_MAX, _imp.squeeze) : IMP_SCALE_MAX;
        if (off9 > 1 + SMX + 1e-6 || off9 <= 1) continue;
        var dW9 = (ow > 1 || uw > 1) ? 0 : Math.abs(cand.w - w9), dL9 = (ol > 1 || ul > 1) ? 0 : Math.abs(cand.l - l9);
        if (Math.max(dW9, dL9) > TOL) continue;
        pick = cand; f.turned = tn;
        var up9 = (uw > 1 || ul > 1), dn9 = (ow > 1 || ol > 1);
        f.scaled = { w0: _impR(w9), l0: _impR(l9), w: _impR(cand.w), l: _impR(cand.l), maxW: _impR(maxW), maxL: _impR(maxL), minW: _impR(LIM.minW), minL: _impR(LIM.minL),
                     up: up9, down: dn9, pct: Math.round((off9 - 1) * 1000) / 10 };
        var sq9 = dn9 && (Math.max(ow, ol) > 1 + IMP_SCALE_MAX + 1e-6);
        if (sq9) f.scaled.squeezed = true;
        if (dn9) f.notes.push((sq9 ? 'SQUEEZED (v171): the buyer asked for it -- ' : 'SCALED: ') + 'the plan is ' + _impN(w9) + ' x ' + _impN(l9) + ' m, ' + Math.round((Math.max(ow, ol) - 1) * 1000) / 10 + ' % over the ' + _impN(maxW) + ' x ' + _impN(maxL) +
                              ' m this product builds -- built at ' + _impN(cand.w) + ' x ' + _impN(cand.l) + ' m (v145)');
        if (up9) f.notes.push('GROWN: the plan is ' + _impN(w9) + ' x ' + _impN(l9) + ' m, ' + Math.round((Math.max(uw, ul) - 1) * 1000) / 10 + ' % under the ' + _impN(LIM.minW) + ' x ' + _impN(LIM.minL) +
                              ' m this product builds at the least -- built at ' + _impN(cand.w) + ' x ' + _impN(cand.l) + ' m (v160)');
      }
    }
    /* a plan a little under the smallest size was grown to it within the
       half metre every plan is brought onto the module by -- quietly until
       now; the bubble says it as for any other plan built at another size */
    if (pick && !f.scaled) {
      var w8 = f.turned ? n.d : n.w, l8 = f.turned ? n.w : n.d;
      if ((w8 < LIM.minW - 0.01 || l8 < LIM.minL - 0.01) && w8 > 0 && l8 > 0) {
        f.scaled = { w0: _impR(w8), l0: _impR(l8), w: _impR(pick.w), l: _impR(pick.l), maxW: _impR(maxW), maxL: _impR(maxL), minW: _impR(LIM.minW), minL: _impR(LIM.minL),
                     up: true, down: false, pct: Math.round((Math.max(LIM.minW / w8, LIM.minL / l8) - 1) * 1000) / 10 };
        f.notes.push('GROWN: the plan is ' + _impN(w8) + ' x ' + _impN(l8) + ' m, ' + f.scaled.pct + ' % under the ' + _impN(LIM.minW) + ' x ' + _impN(LIM.minL) +
                     ' m this product builds at the least -- built at ' + _impN(pick.w) + ' x ' + _impN(pick.l) + ' m (v160)');
      }
    }
    if (!pick) {
      f.why = 'size';
      f.notes.push('the plan is ' + _impN(n.w) + ' x ' + _impN(n.d) + ' m and this product builds ' + _impN(LIM.minW) + '..' + _impN(maxW) +
                   ' x ' + _impN(LIM.minL) + '..' + _impN(maxL) + ' m -- nothing written');
      return f;
    }
    /* v170: the wall from the outer face in, the whole of it */
    var WT = (H.wallThk ? H.wallThk() : 0.16) + OUTF;
    var W1 = f.turned ? n.d : n.w, D1 = f.turned ? n.w : n.d, Wt = pick.w, Lt = pick.l;
    if (turnFell) f.notes.push('the plan is ' + _impN(n.w) + ' x ' + _impN(n.d) + ' m and this product cannot build it ' + (f.turned ? 'as drawn' : 'turned a quarter') +
                               ', so it was ' + (f.turned ? 'turned a quarter' : 'left as drawn') + ': ' + _impN(W1) + ' across, ' + _impN(D1) + ' long' +
                               ((ridgeL ? (W1 > D1 + 0.005) : (W1 + 0.005 < D1)) ? ' -- the roof spans the longer side' : ''));
    else if (f.turned) f.notes.push('turned a quarter (v132): ' + (turnWhy === 'porch' ? ('the two sides are within 6 %, and the ' + (n.outdoor[0].label || 'porch') +
                                    ' goes to a gable end, where the gable pulled out roofs it') : 'the longer side runs along the ridge, the roof spans the shorter one') +
                                    ' -- ' + _impN(W1) + ' across, ' + _impN(D1) + ' long');
    else if (turnWhy === 'porch') f.notes.push('as drawn (v132): the two sides are within 6 %, and the ' + (n.outdoor[0].label || 'porch') + ' is already on a gable end');
    if (Math.abs(Wt - W1) > 0.004 || Math.abs(Lt - D1) > 0.004) {
      f.notes.push(_impN(W1) + ' x ' + _impN(D1) + ' became ' + _impN(Wt) + ' x ' + _impN(Lt) +
                   ' -- the size this product builds nearest to it; the rooms were stretched with it');
    }
    f.w = Wt; f.l = Lt; f.wt = WT;
    f.tf = { W1: W1, D1: D1 };
    f.ox = (S.origin && typeof S.origin.x === 'number') ? S.origin.x : 0;
    f.oz = (S.origin && typeof S.origin.z === 'number') ? S.origin.z : 0;
    f.x0 = f.ox - Wt / 2; f.x1 = f.ox + Wt / 2; f.z0 = f.oz - Lt / 2; f.z1 = f.oz + Lt / 2;
    f.storey = S.planStorey | 0;
    /* turned: x' = d - z, z' = x, the winding kept */
    function rot(p) { return f.turned ? [n.d - p[1], p[0]] : [p[0], p[1]]; }
    /* STRETCHED BETWEEN THE INNER FACES, so a wall that met the shell still
       meets it and nothing crosses into the wall */
    function stretch(v, from, to) {
      if (Math.abs(from - to) < 1e-6) return v;
      if (v <= WT) return v;
      if (v >= from - WT) return v + (to - from);
      return WT + (v - WT) * (to - 2 * WT) / (from - 2 * WT);
    }
    var BMX = null, BMZ = null;
    try { BMX = _impBands(n, f.turned, 'x', W1, Wt, WT); } catch (eBX) { BMX = null; f.notes.push('SIZE: the bands across THREW ' + ((eBX && eBX.message) || eBX)); }
    try { BMZ = _impBands(n, f.turned, 'z', D1, Lt, WT); } catch (eBZ) { BMZ = null; f.notes.push('SIZE: the bands along THREW ' + ((eBZ && eBZ.message) || eBZ)); }
    if (BMX || BMZ) f.notes.push('SIZE (v145): the difference taken from the living rooms, the kitchen and the bedrooms, the bathrooms, the closets, the laundry and the halls kept as drawn -- ' +
                                 [BMX ? BMX.said : '', BMZ ? BMZ.said : ''].filter(function (t9) { return t9; }).join(' | '));
    function MX(v) { return _impR(f.x0 + (BMX ? BMX.map(v) : stretch(v, W1, Wt))); }
    function MZ(v) { return _impR(f.z0 + (BMZ ? BMZ.map(v) : stretch(v, D1, Lt))); }
    function MP(p) { var r = rot(p); return [MX(r[0]), MZ(r[1])]; }

    /* THE PARTITIONS, each end tied to what it meets: the shell, another
       partition (by its id, so the corner follows that wall), or nothing */
    var NP = n.partitions, base = 0, ps = S.parts || [];
    for (i = 0; i < ps.length; i++) if (ps[i] && (ps[i].id | 0) > base) base = ps[i].id | 0;
    var ends = [];
    for (k = 0; k < NP.length; k++) {
      var lenK = (NP[k].o === 'h') ? n.w : n.d;
      ends.push({ s: _impEndOf(NP, k, NP[k].s, true, lenK, WT), e: _impEndOf(NP, k, NP[k].e, false, lenK, WT) });
    }
    for (k = 0; k < NP.length; k++) {
      var p0 = NP[k], o = p0.o, c = p0.c, s = p0.s, e = p0.e, es = ends[k].s, ee = ends[k].e, tt;
      if (f.turned) {
        if (o === 'v') { o = 'h'; tt = s; s = n.d - e; e = n.d - tt; tt = es; es = ee; ee = tt; }
        else { o = 'v'; c = n.d - c; }
      }
      var AL = (o === 'v') ? MZ : MX;
      f.parts.push({ id: base + 1 + k, o: o, c: (o === 'v') ? MX(c) : MZ(c), s: AL(s), e: AL(e), es: es, ee: ee, sep: !!p0.sep });
    }
    for (k = 0; k < f.parts.length; k++) {
      var P = f.parts[k];
      if (P.es.t === 'shell') P.s = null; else if (P.es.t === 'part') { P.sTo = f.parts[P.es.j].id; P.s = f.parts[P.es.j].c; }
      if (P.ee.t === 'shell') P.e = null; else if (P.ee.t === 'part') { P.eTo = f.parts[P.ee.j].id; P.e = f.parts[P.ee.j].c; }
    }
    /* THE DOORS IN THEM, kept inside their wall */
    for (i = 0; i < n.doors.length; i++) {
      var d = n.doors[i], P2 = f.parts[d.part], orig = n.partitions[d.part];
      if (!P2 || !orig) continue;
      var u = d.u;
      if (f.turned && orig.o === 'v') u = n.d - u;
      u = (P2.o === 'v') ? MZ(u) : MX(u);
      var lo = (P2.s != null) ? P2.s : ((P2.o === 'v') ? f.z0 + WT : f.x0 + WT);
      var hi = (P2.e != null) ? P2.e : ((P2.o === 'v') ? f.z1 - WT : f.x1 - WT);
      u = Math.max(lo + d.w / 2 + 0.05, Math.min(hi - d.w / 2 - 0.05, u));
      /* turned a quarter, a wall along z becomes one along x running the
         other way: the leaf hangs on the other end and opens to the other
         side by the asset's convention; a wall along x keeps both */
      var swF = d.sw | 0;
      if (f.turned && orig.o === 'v' && !d.pass && !d.slide && d.kind !== 'bypass') swF ^= 3;
      var fd = { pid: P2.id, u: _impR(u), w: d.w, sw: swF };
      if (d.pass) fd.pass = true;
      if (d.slide) fd.slide = d.slide;
      if (d.kind) fd.kind = d.kind;
      f.doors.push(fd);
    }
    /* v161: and none reaches the house as a wall from one wall to the same
       wall again, or shorter than 0.30 m -- the configurator would build it
       across the whole house */
    (function () {
      var gone = [], kf;
      for (kf = 0; kf < f.parts.length; kf++) {
        var PF = f.parts[kf];
        if ((PF.sTo != null && PF.sTo === PF.eTo) || (PF.s != null && PF.e != null && Math.abs(PF.e - PF.s) < 0.30)) gone.push(PF.id);
      }
      if (!gone.length) return;
      f.parts = f.parts.filter(function (q) { return gone.indexOf(q.id) < 0; });
      var dn0 = f.doors.length;
      f.doors = f.doors.filter(function (q) { return gone.indexOf(q.pid) < 0; });
      f.notes.push('NO LENGTH (v161): ' + gone.length + ' partition(s) of no length left out (#' + gone.join(', #') + ')' + (dn0 > f.doors.length ? (', ' + (dn0 - f.doors.length) + ' door(s) in them with them') : ''));
    })();
    /* THE WINDOWS AND OUTER DOORS: on whichever of the four walls they now
       stand, measured along it from where the core's wall starts, as a unit
       the catalogue sells */
    var NO = n.outline.length, UNK = 0;
    /* v203: the L's corner where the house will stand, and the openings in
       its walls by their point -- the product's wall comes with the L */
    if (n.lnotch) {
      var lnA = MP([n.lnotch.x0, n.lnotch.z0]), lnB = MP([n.lnotch.x1, n.lnotch.z1]);
      f.lnotch = { x0: Math.min(lnA[0], lnB[0]), x1: Math.max(lnA[0], lnB[0]), z0: Math.min(lnA[1], lnB[1]), z1: Math.max(lnA[1], lnB[1]) };
    }
    for (i = 0; i < n.openings.length; i++) {
      if (n.openings[i].side < 0) {
        var opL = n.openings[i], QL = MP(opL.npt), unL = _impUnit(opL.kind, opL.w, (opL.kind === 'door') ? 0 : opL.sill, opL.lh || 0, opL.pref || '');
        if (!unL || !f.lnotch) { UNK += unL ? 0 : 1; continue; }
        f.openings.push({ seg: null, wall: -1, pt: [_impR(QL[0]), _impR(QL[1])], at: 0, w: unL.w, h: unL.h, sill: unL.sill, kind: unL.kind, code: unL.code,
                          hand: unL.hand, leaves: unL.leaves, name: unL.name, out: null, lwall: true });
        continue;
      }
      var op = n.openings[i], a = n.outline[(op.side + NO - 1) % NO], b = n.outline[op.side];
      var L = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
      var Q = MP([a[0] + (b[0] - a[0]) / L * op.off, a[1] + (b[1] - a[1]) / L * op.off]);
      var dd = [Math.abs(Q[1] - f.z0), Math.abs(Q[0] - f.x1), Math.abs(Q[1] - f.z1), Math.abs(Q[0] - f.x0)], wi = 0, q;
      for (q = 1; q < 4; q++) if (dd[q] < dd[wi]) wi = q;
      var at = [Q[0] - f.x0, Q[1] - f.z0, f.x1 - Q[0], f.z1 - Q[1]][wi];
      var un = _impUnit(op.kind, op.w, (op.kind === 'door') ? 0 : op.sill, op.lh || 0, op.pref || '');
      var rec;
      if (un) {
        if (un.off >= 0.025 || op.pref) f.notes.push('a' + (op.kind === 'door' ? 'n outer door ' : ' window ') + _impN(op.w) + ' m wide' +
                                                     (op.pref ? (op.dbl ? ' read as a pair' : (op.pref === 'slide' ? ' read as sliding' : ' read as French doors')) : '') + ' became ' + un.code + ' ' + _impN(un.w) + ' x ' + _impN(un.h) + ((un.name && op.kind === 'door') ? (' (' + un.name + ')') : ''));
        rec = { seg: 'S' + f.storey + '-W' + wi, wall: wi, at: _impR(at), w: un.w, h: un.h, sill: un.sill, kind: un.kind, code: un.code,
                hand: un.hand, leaves: un.leaves, name: un.name, out: (op.out && un.kind === 'door') ? true : null };
        /* v158: a size from the window's label, a sill from the drawing */
        if (op.kind === 'window' && op.lh > 0) rec.lab = 1;
        if (op.kind === 'window' && op.sx) rec.sx = 1;
      } else {
        UNK++;
        rec = { seg: 'S' + f.storey + '-W' + wi, wall: wi, at: _impR(at), w: op.w, h: (op.kind === 'door') ? 2.0 : 1.2,
                sill: (op.kind === 'door') ? 0 : op.sill, kind: (op.kind === 'door') ? 'door' : 'window', code: null, hand: null, leaves: null, name: op.kind,
                out: (op.out && op.kind === 'door') ? true : null };
      }
      f.openings.push(rec);
    }
    if (UNK) f.notes.push(UNK + ' opening(s) have no catalogue unit -- the shared catalogue is not loaded; placed at the plan\'s width');
    /* THE CLOSETS (v102): a mark in the middle of each, which is what makes
       the asset treat the space as a closet and not as a room */
    f.outdoor = [];
    for (i = 0; n.outdoor && i < n.outdoor.length; i++) {
      var OQ = n.outdoor[i], oA = MP([OQ.x0, OQ.z0]), oB = MP([OQ.x1, OQ.z1]);
      f.outdoor.push({ type: OQ.type, label: OQ.label, x0: Math.min(oA[0], oB[0]), x1: Math.max(oA[0], oB[0]), z0: Math.min(oA[1], oB[1]), z1: Math.max(oA[1], oB[1]) });
    }
    f.closets = [];
    for (i = 0; n.closets && i < n.closets.length; i++) {
      var CQ = MP([n.closets[i].x, n.closets[i].z]), CB = n.closets[i].box, CBo = null;
      if (CB) { var cA = MP([CB[0], CB[1]]), cB = MP([CB[2], CB[3]]); CBo = [Math.min(cA[0], cB[0]), Math.min(cA[1], cB[1]), Math.max(cA[0], cB[0]), Math.max(cA[1], cB[1])]; }
      f.closets.push({ x: CQ[0], z: CQ[1], label: n.closets[i].label, type: n.closets[i].type, box: CBo });
    }
    /* THE FITTINGS (v111), each box corner by the same mapping as the walls */
    f.fixtures = [];
    for (i = 0; n.fixtures && i < n.fixtures.length; i++) {
      var FQ = n.fixtures[i], fA = MP([FQ.x0, FQ.z0]), fB = MP([FQ.x1, FQ.z1]);
      var fQ = { type: FQ.type, x0: Math.min(fA[0], fB[0]), x1: Math.max(fA[0], fB[0]), z0: Math.min(fA[1], fB[1]), z1: Math.max(fA[1], fB[1]) };
      if (FQ.label) fQ.label = FQ.label;
      if (FQ.sug) fQ.sug = true;
      if (FQ.seats) fQ.seats = FQ.seats;
      if (FQ.stools) fQ.stools = FQ.stools;
      f.fixtures.push(fQ);
    }
    /* THE SLANTED WALLS (v114), both ends by the same mapping -- drawn in
       the preview only, as the configurator has no slanted wall yet */
    f.diag = [];
    for (i = 0; n.diag && i < n.diag.length; i++) {
      var gA = MP(n.diag[i].a), gB = MP(n.diag[i].b);
      /* v125: its doors, the hinge and the swing by the same mapping */
      var gD = (n.diag[i].doors || []).map(function (d) { return { u: d.u, w: d.w, kind: d.kind, label: d.label, hp: d.hp ? MP(d.hp) : null, op: d.op ? MP(d.op) : null }; });
      f.diag.push({ a: gA, b: gB, len: n.diag[i].len, ang: n.diag[i].ang, doors: gD });
    }
    /* THE ROOM NAMES, pinned where the plan wrote them */
    var TB = DRAW.roomTable();
    for (i = 0; i < n.rooms.length; i++) {
      var r = n.rooms[i];
      if (!r.key) continue;
      var ix = -1, R = MP([r.x, r.z]);
      for (q = 0; q < TB.length; q++) if (TB[q] && TB[q].i18n === r.key) { ix = q; break; }
      if (ix < 0) continue;
      f.pins.push({ x: R[0], z: R[1], n: ix, k: r.key });
    }
    try { var bw9 = _impBathWin(f, WT); if (bw9) f.notes.push(bw9); } catch (eBw) { f.notes.push('BATHWIN: THREW ' + ((eBw && eBw.message) || eBw)); }
    try { var hs9 = _impTerraceHs(f, WT); if (hs9) f.notes.push(hs9); } catch (eHs) { f.notes.push('TERRACE DOORS: THREW ' + ((eHs && eHs.message) || eHs)); }
    try { var hw9 = _impHouseWin(f, WT); if (hw9) f.notes.push(hw9); } catch (eHw) { f.notes.push('HOUSE WINDOWS: THREW ' + ((eHw && eHw.message) || eHw)); }
    try { var gd9 = _impGarageDoor(f, WT); if (gd9) f.notes.push(gd9); } catch (eGd) { f.notes.push('GARAGE DOOR: THREW ' + ((eGd && eGd.message) || eGd)); }
    try { var sy9 = _impOpenSym(f, WT); if (sy9) f.notes.push(sy9); } catch (eSy) { f.notes.push('SYMMETRY: THREW ' + ((eSy && eSy.message) || eSy)); }
    f.ok = true;
    return f;
  }
  /* ---------- THE OPENINGS ON THE BUILT WALLS (v148) ---------------------
     A position along one of the four walls of the fitted house: wall 0 the
     z0 side from x0, 1 the x1 side from z0, 2 the z1 side from x1 back, 3
     the x0 side from z1 back -- as the openings carry it. */
  function _impWallLen(f, wi) { return (wi % 2) ? (f.z1 - f.z0) : (f.x1 - f.x0); }
  /* the partitions meeting a wall, where along it: { at, line } */
  function _impWallMeets(f, wi) {
    var out = [], k;
    for (k = 0; k < f.parts.length; k++) {
      var P = f.parts[k];
      var hit = (wi === 0) ? (P.o === 'v' && P.s == null) : (wi === 2) ? (P.o === 'v' && P.e == null) : (wi === 3) ? (P.o === 'h' && P.s == null) : (P.o === 'h' && P.e == null);
      if (!hit) continue;
      var at = (wi === 0) ? P.c - f.x0 : (wi === 1) ? P.c - f.z0 : (wi === 2) ? f.x1 - P.c : f.z1 - P.c;
      out.push({ at: at, line: !!P.sep });
    }
    out.sort(function (a, b) { return a.at - b.at; });
    return out;
  }
  /* the walls round a point, looked for along the axes: a room's box */
  function _impFitBox(f, x, z) {
    var b = { x0: f.x0, x1: f.x1, z0: f.z0, z1: f.z1 }, k;
    for (k = 0; k < f.parts.length; k++) {
      var P = f.parts[k];
      if (P.sep) continue;
      var lo = (P.s != null) ? P.s : ((P.o === 'v') ? f.z0 : f.x0), hi = (P.e != null) ? P.e : ((P.o === 'v') ? f.z1 : f.x1);
      var a = Math.min(lo, hi) - 0.01, c = Math.max(lo, hi) + 0.01;
      if (P.o === 'v') { if (z < a || z > c) continue; if (P.c < x && P.c > b.x0) b.x0 = P.c; if (P.c > x && P.c < b.x1) b.x1 = P.c; }
      else { if (x < a || x > c) continue; if (P.c < z && P.c > b.z0) b.z0 = P.c; if (P.c > z && P.c < b.z1) b.z1 = P.c; }
    }
    return b;
  }
  /* A WINDOW IN A BATHROOM ON AN OUTER WALL (v148, Marek: pokud mame
     koupelnu, ktera ma jednu stenu na obvodove stene, a v puvodnim planu
     nebylo okno, meli bychom tam pridat vhodne okno sami -- treba umistit
     vys, mozna udelat mensi, ale i nad vanou a sprchovym koutem by byt
     melo). A bathroom or a WC with an outer wall and no window or door in
     any of its outer walls gets one: the 0.90 x 0.50 strip where its wall
     takes it, a 0.50 x 0.70 or a 0.40 x 0.60 where it is shorter, high --
     its head at 2.10 m, over the tub, the shower or the WC -- in the middle
     of its longest outer wall, clear of the walls meeting it and of the
     other openings. */
  var IMP_BATHWIN = [[0.9, 0.5, 1.6], [0.5, 0.7, 1.4], [0.4, 0.6, 1.5]];
  function _impBathWin(f, WT) {
    var PTb = (_PH && _PH.partThk) ? _PH.partThk() : 0.1, said = [], i, k, q;
    for (i = 0; i < f.pins.length; i++) {
      var pn = f.pins[i];
      if (!/rooms\.(bathroom|wc)/.test(String(pn.k || ''))) continue;
      var b = _impFitBox(f, pn.x, pn.z), WL = [];
      if (Math.abs(b.z0 - f.z0) < 0.01) WL.push({ wi: 0, lo: b.x0 - f.x0, hi: b.x1 - f.x0 });
      if (Math.abs(b.x1 - f.x1) < 0.01) WL.push({ wi: 1, lo: b.z0 - f.z0, hi: b.z1 - f.z0 });
      if (Math.abs(b.z1 - f.z1) < 0.01) WL.push({ wi: 2, lo: f.x1 - b.x1, hi: f.x1 - b.x0 });
      if (Math.abs(b.x0 - f.x0) < 0.01) WL.push({ wi: 3, lo: f.z1 - b.z1, hi: f.z1 - b.z0 });
      if (!WL.length) continue;
      var has = false;
      for (k = 0; k < WL.length && !has; k++) for (q = 0; q < f.openings.length; q++) {
        var o = f.openings[q];
        if (o.wall === WL[k].wi && o.at > WL[k].lo - 0.05 && o.at < WL[k].hi + 0.05) { has = true; break; }
      }
      if (has) continue;
      WL.sort(function (p1, p2) { return (p2.hi - p2.lo) - (p1.hi - p1.lo); });
      var put = null;
      for (k = 0; k < WL.length && !put; k++) {
        var L = WL[k], Lw = _impWallLen(f, L.wi);
        var lo = L.lo + ((L.lo < 0.01) ? WT : PTb / 2) + 0.1, hi = L.hi - ((L.hi > Lw - 0.01) ? WT : PTb / 2) - 0.1;
        for (q = 0; q < IMP_BATHWIN.length && !put; q++) {
          var BW = IMP_BATHWIN[q];
          if (hi - lo < BW[0]) continue;
          var un = _impUnit('window', BW[0], BW[2], BW[1], '');
          if (!un) continue;
          var at = _impR((lo + hi) / 2), clash = false;
          for (var j = 0; j < f.openings.length; j++) { var o2 = f.openings[j]; if (o2.wall === L.wi && Math.abs(o2.at - at) < (o2.w + un.w) / 2 + 0.1) { clash = true; break; } }
          if (clash) continue;
          put = { seg: 'S' + f.storey + '-W' + L.wi, wall: L.wi, at: at, w: un.w, h: un.h, sill: un.sill, kind: un.kind, code: un.code, hand: un.hand, leaves: un.leaves, name: un.name, added: 'bath' };
        }
      }
      if (put) { f.openings.push(put); said.push(String(pn.k).replace(/^rooms\./, '') + ' on wall ' + put.wall + ' at ' + _impN(put.at) + ': ' + put.code + ' ' + _impN(put.w) + ' x ' + _impN(put.h) + ', sill ' + _impN(put.sill)); }
      else said.push(String(pn.k).replace(/^rooms\./, '') + ' -- its outer wall takes no window clear of the walls and openings');
    }
    return said.length ? ('BATHWIN: ' + said.length + ' bathroom(s) on an outer wall with no window in the plan (v148): ' + said.join(' | ')) : '';
  }
  /* ---------- THE HOUSE'S WINDOWS AND THE TERRACE'S DOORS (v158) ----------
     Marek: agent dava do objektu ruzna okna, s ruznou vyskou parapetu. Byl
     bych radeji, aby se drzelo pravidlo, ze okna, pokud to cca vychazi, by
     mela byt na dome (nebo alespon na jedne stene) stejna -- pokud tedy neni
     nejake male okno do koupelny, zachodu, nebo to nevychazi dispozicne do
     mistnosti. Take pokud je mistnost prilehla k terase -- vzdy HS
     oteviratelny portal.
     A window is looked at from inside: the room behind it is the box its
     walls close round a point 0.3 m in from the wall, named by the names
     pinned in it. A bathroom, a WC, a laundry, a closet and the like are the
     small rooms; every other room is a room. */
  var IMP_HW_SVC = /^(rooms\.(bathroom|wc|storage|laundry|wardrobe|pantry|mudroom|garage|sauna)|leva\.closet|container_room_6)$/;
  var IMP_HS_ROOM = /^(rooms\.(livingroom|kitchen|diningroom|bedroom|office)|container_room_7)$/;
  /* a point inn metres in from wall wi, at along it */
  function _impWallPt(f, wi, at, inn) {
    if (wi === 0) return [f.x0 + at, f.z0 + inn];
    if (wi === 1) return [f.x1 - inn, f.z0 + at];
    if (wi === 2) return [f.x1 - at, f.z1 - inn];
    return [f.x0 + inn, f.z1 - at];
  }
  /* a room's stretch along wall wi: a0..a1 between its walls, lo..hi where
     an opening may stand, 0.1 m off the faces of the walls meeting it */
  function _impWallSpan(f, wi, b, WT, PT) {
    var L = _impWallLen(f, wi), a0, a1;
    if (wi === 0) { a0 = b.x0 - f.x0; a1 = b.x1 - f.x0; }
    else if (wi === 1) { a0 = b.z0 - f.z0; a1 = b.z1 - f.z0; }
    else if (wi === 2) { a0 = f.x1 - b.x1; a1 = f.x1 - b.x0; }
    else { a0 = f.z1 - b.z1; a1 = f.z1 - b.z0; }
    return { a0: a0, a1: a1, lo: a0 + ((a0 < 0.01) ? WT : PT / 2) + 0.1, hi: a1 - ((a1 > L - 0.01) ? WT : PT / 2) - 0.1 };
  }
  /* the names pinned inside a room's box, and its closet marks */
  function _impRoomKeys(f, b) {
    var K = [], k;
    for (k = 0; k < (f.pins || []).length; k++) {
      var p = f.pins[k];
      if (p.x >= b.x0 - 0.01 && p.x <= b.x1 + 0.01 && p.z >= b.z0 - 0.01 && p.z <= b.z1 + 0.01) K.push(String(p.k || ''));
    }
    for (k = 0; f.closets && k < f.closets.length; k++) {
      var c = f.closets[k];
      if (c.x >= b.x0 && c.x <= b.x1 && c.z >= b.z0 && c.z <= b.z1) K.push(CLOSET_KEY);
    }
    return K;
  }
  function _impRoomTxt(K) {
    return K.length ? K.map(function (k) { return k.replace(/^rooms\./, '').replace(/^leva\./, ''); }).join('/') : 'a room with no name';
  }
  /* the catalogue's sliding doors (the HS portals), narrowest first -- none
     taller than a storey */
  function _impSliders() {
    var U = global.ConfiguratorUI, G = null, L = [], i;
    try { G = (U && typeof U.openingGroup === 'function') ? U.openingGroup('french') : null; } catch (e) { G = null; }
    for (i = 0; G && G.items && i < G.items.length; i++) {
      var it = G.items[i];
      if (!it || !(it.w > 0) || it.full || it.round || it.hand === 'R' || (it.h || 0) > 2.8) continue;
      if (_impIsSlider(it)) L.push(it);
    }
    L.sort(function (a, b) { return a.w - b.w; });
    return L;
  }
  /* A ROOM THE TERRACE LIES AGAINST OPENS ONTO IT WITH AN HS PORTAL. Every
     living room, kitchen, dining room and bedroom along the terrace gets a
     sliding door onto it, all of it over the terrace: in place of its window
     or its French door there, as wide as the room takes -- 2.0 m or the
     window it replaces in a living room, a kitchen or a dining room, 1.5 m
     in a bedroom, the catalogue's nearest -- in the middle of the stretch the
     room has along the terrace, clear of the kitchen run, the fridge and the
     other fittings standing at that wall. A room with a sliding door onto it
     already keeps it; a room the entrance door opens from onto the terrace
     keeps the entrance door. */
  function _impTerraceHs(f, WT) {
    if (!f.outdoor || !f.outdoor.length || (f.storey | 0) !== 0) return '';
    var PK = _impOutdoorPick(f), B = PK.best;
    if (!B) return '';
    var SL = _impSliders();
    if (!SL.length) return 'TERRACE DOORS (v158): the catalogue has no sliding door -- none put';
    var PT = (_PH && _PH.partThk) ? _PH.partThk() : 0.1, wi = { z0: 0, x1: 1, z1: 2, x0: 3 }[B.side], Q = B.Q, L = _impWallLen(f, wi), t0, t1, k;
    if (wi === 0) { t0 = Q.x0 - f.x0; t1 = Q.x1 - f.x0; }
    else if (wi === 1) { t0 = Q.z0 - f.z0; t1 = Q.z1 - f.z0; }
    else if (wi === 2) { t0 = f.x1 - Q.x1; t1 = f.x1 - Q.x0; }
    else { t0 = f.z1 - Q.z1; t1 = f.z1 - Q.z0; }
    t0 = Math.max(0, t0); t1 = Math.min(L, t1);
    /* the rooms along the wall, over the terrace's length */
    var seen = {}, rooms = [], a;
    for (a = t0 + 0.15; a < t1; a += 0.25) {
      var pt = _impWallPt(f, wi, a, WT + 0.3), bx = _impFitBox(f, pt[0], pt[1]), sp = _impWallSpan(f, wi, bx, WT, PT);
      /* one room once: by its stretch along the wall */
      var key = _impR(sp.a0) + ',' + _impR(sp.a1);
      if (seen[key]) continue;
      seen[key] = 1;
      rooms.push({ b: bx, K: _impRoomKeys(f, bx), sp: sp });
    }
    var said = [], put = 0;
    rooms.forEach(function (R) {
      var nm = _impRoomTxt(R.K) + ' (' + _impN(R.sp.a0) + '..' + _impN(R.sp.a1) + ')';
      if (!R.K.some(function (q) { return IMP_HS_ROOM.test(q); })) return;
      var lo = Math.max(R.sp.lo, t0 + 0.1), hi = Math.min(R.sp.hi, t1 - 0.1);
      var mine = f.openings.filter(function (o) { return o.wall === wi && o.at > R.sp.a0 && o.at < R.sp.a1; });
      if (mine.some(function (o) { return _impOpSlides(o) && o.at + o.w / 2 > t0 && o.at - o.w / 2 < t1; })) { said.push(nm + ': a sliding door onto it already'); return; }
      if (mine.some(function (o) { return o.kind === 'door' && o.at > t0 && o.at < t1; })) { said.push(nm + ': the entrance door opens onto it -- kept, no second door'); return; }
      if (hi - lo < SL[0].w) { said.push(nm + ': ' + _impN(Math.max(0, hi - lo)) + ' m of wall over the terrace, the narrowest sliding door is ' + _impN(SL[0].w) + ' -- left as it is'); return; }
      /* the fittings standing at that wall keep their stretch of it */
      var free = [[lo, hi]];
      (f.fixtures || []).forEach(function (fx) {
        if (fx.sug || !/kitchen|island|fridge|toilet|basin|shower|bathtub|washer|dryer|water_heater/.test(String(fx.type || ''))) return;
        var near, s0, s1;
        if (wi === 0) { near = fx.z0 - f.z0; s0 = fx.x0 - f.x0; s1 = fx.x1 - f.x0; }
        else if (wi === 1) { near = f.x1 - fx.x1; s0 = fx.z0 - f.z0; s1 = fx.z1 - f.z0; }
        else if (wi === 2) { near = f.z1 - fx.z1; s0 = f.x1 - fx.x1; s1 = f.x1 - fx.x0; }
        else { near = fx.x0 - f.x0; s0 = f.z1 - fx.z1; s1 = f.z1 - fx.z0; }
        if (near > WT + 0.9) return;
        var c0 = s0 - 0.05, c1 = s1 + 0.05, nf = [];
        free.forEach(function (fr) {
          if (c1 <= fr[0] || c0 >= fr[1]) { nf.push(fr); return; }
          if (c0 > fr[0]) nf.push([fr[0], c0]);
          if (c1 < fr[1]) nf.push([c1, fr[1]]);
        });
        free = nf;
      });
      var big = mine.filter(function (o) { return o.kind !== 'door'; }).sort(function (p, q) { return q.w - p.w; })[0] || null;
      var want = Math.max(R.K.some(function (q) { return /livingroom|kitchen|diningroom/.test(q); }) ? 2.0 : 1.5, big ? big.w : 0), bestP = null;
      free.forEach(function (fr) {
        var cap = fr[1] - fr[0], u = null;
        SL.forEach(function (it) {
          if (it.w > cap + 0.001) return;
          if (!u || Math.abs(it.w - want) < Math.abs(u.w - want) - 1e-6 || (Math.abs(Math.abs(it.w - want) - Math.abs(u.w - want)) <= 1e-6 && it.w > u.w)) u = it;
        });
        if (!u) return;
        if (!bestP || Math.abs(u.w - want) < Math.abs(bestP.u.w - want) - 1e-6) bestP = { u: u, at: (fr[0] + fr[1]) / 2 };
      });
      if (!bestP) { said.push(nm + ': the fittings at that wall leave no stretch for a sliding door -- left as it is'); return; }
      var u = bestP.u, gone = [];
      mine.forEach(function (o) { if (o.kind !== 'door' && Math.abs(o.at - bestP.at) < (o.w + u.w) / 2 + 0.1) gone.push(o); });
      var clash = f.openings.some(function (o) { return o.wall === wi && gone.indexOf(o) < 0 && Math.abs(o.at - bestP.at) < (o.w + u.w) / 2 + 0.1; });
      if (clash) { said.push(nm + ': a sliding door of ' + _impN(u.w) + ' m would stand on another opening -- left as it is'); return; }
      for (k = 0; k < gone.length; k++) f.openings.splice(f.openings.indexOf(gone[k]), 1);
      f.openings.push({ seg: 'S' + f.storey + '-W' + wi, wall: wi, at: _impR(bestP.at), w: u.w, h: u.h, sill: 0, kind: 'french', code: u.code,
                        hand: u.hand || null, leaves: u.leaves || null, name: u.name || 'french', added: 'terrace' });
      put++;
      said.push(nm + ': ' + u.code + ' ' + _impN(u.w) + ' m at ' + _impN(bestP.at) + (gone.length ? (' in place of ' + gone.map(function (o) { return (o.code || o.kind) + ' ' + _impN(o.w); }).join(', ')) : ', a new opening'));
    });
    return said.length ? ('TERRACE DOORS (v158): the terrace against wall ' + wi + ' (' + B.side + ', ' + _impN(t0) + '..' + _impN(t1) + ' along it), ' + put + ' sliding door(s) put -- ' + said.join(' | ')) : '';
  }
  /* ONE WINDOW FOR THE HOUSE. The house window is the unit most of the
     rooms' windows are (between two as many, the one nearest 1.20 m). A
     room's window that is within a third of its width becomes it, where the
     room takes it clear of its walls and of the other openings -- the plan's
     own size from a label stays. Then the heads in one line: a window that
     keeps its own size -- a picture window, a strip, the small window of a
     bathroom or a WC -- has its sill set so its head is where the house
     window's is; a room's window no lower than 0.5 m, a small room's no lower
     than 0.9 m, and a sill the drawing gives stays. The product's own rules
     (the window under the top plate, the head on a bed joint) then move them
     all alike. */
  function _impHouseWin(f, WT) {
    var PT = (_PH && _PH.partThk) ? _PH.partThk() : 0.1, Wn = [], i, k;
    for (i = 0; i < f.openings.length; i++) {
      var o = f.openings[i];
      if (o.kind !== 'window' || !o.code || !((o.sill || 0) > 0.05) || o.wall < 0) continue;
      var pt = _impWallPt(f, o.wall, o.at, WT + 0.3), bx = _impFitBox(f, pt[0], pt[1]), K = _impRoomKeys(f, bx);
      Wn.push({ o: o, K: K, svc: K.length > 0 && K.every(function (q) { return IMP_HW_SVC.test(q); }), sp: _impWallSpan(f, o.wall, bx, WT, PT) });
    }
    var MW = Wn.filter(function (q) { return !q.svc; });
    if (!MW.length) return '';
    var cnt = {}, best = null, c;
    MW.forEach(function (q) { var e = cnt[q.o.code] = cnt[q.o.code] || { n: 0, o: q.o }; e.n++; });
    for (c in cnt) if (cnt.hasOwnProperty(c)) {
      var e2 = cnt[c];
      if (!best || e2.n > best.n || (e2.n === best.n && Math.abs(e2.o.w - 1.2) < Math.abs(best.o.w - 1.2))) best = e2;
    }
    var HU = { code: best.o.code, w: best.o.w, h: best.o.h, sill: best.o.sill, hand: best.o.hand, leaves: best.o.leaves, name: best.o.name };
    var head = _impR((HU.sill || 0) + (HU.h || 0)), made = [], heads = [], kept = [];
    MW.forEach(function (q) {
      var o = q.o, nm = _impRoomTxt(q.K);
      if (o.code === HU.code) return;
      if (o.lab) { kept.push(nm + ' ' + o.code + ' (its label)'); return; }
      if (Math.max(o.w, HU.w) / Math.max(0.01, Math.min(o.w, HU.w)) > 1.34) { kept.push(nm + ' ' + o.code + ' ' + _impN(o.w) + ' (not near the house window)'); return; }
      if (q.sp.hi - q.sp.lo < HU.w) { kept.push(nm + ' ' + o.code + ' (the room takes ' + _impN(Math.max(0, q.sp.hi - q.sp.lo)) + ' m)'); return; }
      var at = Math.max(q.sp.lo + HU.w / 2, Math.min(q.sp.hi - HU.w / 2, o.at));
      for (k = 0; k < f.openings.length; k++) {
        var o2 = f.openings[k];
        if (o2 === o || o2.wall !== o.wall) continue;
        if (Math.abs(o2.at - at) < (o2.w + HU.w) / 2 + 0.1) { kept.push(nm + ' ' + o.code + ' (another opening in the way)'); return; }
      }
      made.push(nm + ' ' + o.code + ' ' + _impN(o.w) + ' x ' + _impN(o.h) + ' on wall ' + o.wall);
      o.at = _impR(at); o.w = HU.w; o.h = HU.h; o.code = HU.code; o.hand = HU.hand; o.leaves = HU.leaves; o.name = HU.name;
      if (!o.sx) o.sill = HU.sill;
    });
    Wn.forEach(function (q) {
      var o = q.o;
      if (o.sx) return;
      var s2 = (o.code === HU.code) ? HU.sill : _impR(head - (o.h || 0));
      if (Math.abs(s2 - (o.sill || 0)) < 0.005) return;
      if (s2 < (q.svc ? 0.9 : 0.5) - 0.005) { kept.push(_impRoomTxt(q.K) + ' ' + o.code + ' sill ' + _impN(o.sill) + ' (its head would need ' + _impN(s2) + ')'); return; }
      heads.push((q.svc ? 'small window ' : '') + _impRoomTxt(q.K) + ' ' + o.code + ' sill ' + _impN(o.sill) + ' -> ' + _impN(s2));
      o.sill = s2;
    });
    return 'HOUSE WINDOWS (v158): the house window ' + HU.code + ' ' + _impN(HU.w) + ' x ' + _impN(HU.h) + ', sill ' + _impN(HU.sill) + ', head ' + _impN(head) + ' m (' + best.n + ' of ' + MW.length + ' room window(s) were it)' +
           (made.length ? (' -- made it: ' + made.join(', ')) : ' -- nothing to make it') +
           (heads.length ? (' | heads in one line: ' + heads.join(', ')) : '') + (kept.length ? (' | kept: ' + kept.join(', ')) : '');
  }
  /* SYMMETRY (v148, Marek: idealne vyplne rozmistovat symetricky, pokud to
     dava smysl a nerozchazi se to moc s pudorysem -- okno na stred
     mistnosti, posuvne dvere primo mezi okna na stred mistnosti; bude to
     vypadat daleko cisteji). Along each outer wall, the middles it offers
     are the middle of the wall and the middle of each room on it -- between
     the walls meeting it, and between the walls and the room lines. Two
     openings of one width that stand nearly mirrored about one of them are
     set exactly mirrored, then a single one nearly in a middle is set in
     it: nearly means within a tenth of the room's length, 0.12 m at least
     and 0.40 m at most. Nothing is moved onto another opening or a wall
     meeting this one. */
  /* A GARAGE GETS ITS VEHICLE DOOR (v202, RANCH thread -- the plan library
     thread's request on plan 1000076006, Marek: "Do garaze pak chybi
     garazovy vjezd / dvere"). The reader has no word for an overhead door:
     it comes as a wide door, a pair, a slider -- or not at all, and the
     garage of that plan got only a 0.85 m door on its back wall. Only in a
     product that sells garage doors (HouseLib.openingKinds holds 'garage',
     RANCH), for every room named GARAGE on the ground storey with an outer
     wall and no garage door on it yet:
       - an outer door into it 2.0 m wide or more is the vehicle door the
         plan draws -- it becomes the catalogue's garage door nearest its
         width that the wall takes, where it stands;
       - with none, the door goes on the garage's outer wall that takes the
         widest unit -- the wall the house's entrance is on first (the
         street side), then the longest -- in the middle of the garage's
         stretch of it;
     what stands in its way on that wall goes (a window, the wide door it
     replaces); an ordinary door elsewhere stays -- the man door. A garage
     whose outer walls take no unit keeps what it has, and the note says how
     much wall the narrowest unit needs. */
  function _impGarageDoor(f, WT) {
    var C = _impCore(), KD = C && C.openingKinds;
    if (!KD || KD.indexOf('garage') < 0 || (f.storey | 0) !== 0) return '';
    var U = global.ConfiguratorUI, G = null, i, k;
    try { G = (U && typeof U.openingGroup === 'function') ? U.openingGroup('garage') : null; } catch (e) { G = null; }
    var GU = ((G && G.items) || []).filter(function (it) { return it && it.w > 0; }).sort(function (a, b) { return a.w - b.w; });
    var hasG = f.pins.some(function (q) { return q.k === 'rooms.garage'; });
    if (!hasG) return '';
    if (!GU.length) return 'GARAGE DOOR (v202): the catalogue has no garage door -- none put';
    var PT = (_PH && _PH.partThk) ? _PH.partThk() : 0.1, said = [], seen = {}, put = 0;
    function roomKeysAt(o) { var pt = _impWallPt(f, o.wall, o.at, WT + 0.3); return _impRoomKeys(f, _impFitBox(f, pt[0], pt[1])); }
    /* the house's entrance: an outer hinged door that is not the garage's */
    var entW = -1;
    for (i = 0; i < f.openings.length && entW < 0; i++) {
      var oe = f.openings[i];
      if (oe.wall >= 0 && oe.kind === 'door' && oe.w < 2.0 && roomKeysAt(oe).indexOf('rooms.garage') < 0) entW = oe.wall;
    }
    /* v203: the L's corner gives the garage walls of its own -- the two inner
       walls of the corner, each facing the way one of the four does */
    var LNf = f.lnotch || null, LI = null;
    if (LNf) LI = { z: (LNf.z0 > f.z0 + 0.01) ? LNf.z0 : LNf.z1, zEq: (LNf.z0 > f.z0 + 0.01) ? 2 : 0,
                    x: (LNf.x0 > f.x0 + 0.01) ? LNf.x0 : LNf.x1, xEq: (LNf.x0 > f.x0 + 0.01) ? 1 : 3 };
    for (i = 0; i < f.pins.length; i++) {
      var pn = f.pins[i];
      if (pn.k !== 'rooms.garage') continue;
      var b = _impFitBox(f, pn.x, pn.z), key = [b.x0, b.x1, b.z0, b.z1].map(_impR).join(',');
      if (seen[key]) continue;
      seen[key] = 1;
      var nm = 'garage ' + _impN(b.x1 - b.x0) + ' x ' + _impN(b.z1 - b.z0), WL = [];
      /* each candidate wall: { key, wi (or -1), eq (the side it faces), lo..hi where a door may stand, line } */
      function addRect(wi) { var sp = _impWallSpan(f, wi, b, WT, PT); WL.push({ key: 'W' + wi, wi: wi, eq: wi, lo: sp.lo, hi: sp.hi, a0: sp.a0, a1: sp.a1 }); }
      if (Math.abs(b.z0 - f.z0) < 0.01) addRect(0);
      if (Math.abs(b.x1 - f.x1) < 0.01) addRect(1);
      if (Math.abs(b.z1 - f.z1) < 0.01) addRect(2);
      if (Math.abs(b.x0 - f.x0) < 0.01) addRect(3);
      if (LI) {
        if (Math.abs(b.z1 - LI.z) < 0.05 || Math.abs(b.z0 - LI.z) < 0.05) {
          var hx0 = Math.max(b.x0, LNf.x0), hx1 = Math.min(b.x1, LNf.x1);
          if (hx1 - hx0 > 0.5) WL.push({ key: 'LH', wi: -1, eq: LI.zEq, o: 'h', c: LI.z, lo: hx0 + WT + 0.1, hi: hx1 - WT - 0.1 });
        }
        if (Math.abs(b.x1 - LI.x) < 0.05 || Math.abs(b.x0 - LI.x) < 0.05) {
          var vz0 = Math.max(b.z0, LNf.z0), vz1 = Math.min(b.z1, LNf.z1);
          if (vz1 - vz0 > 0.5) WL.push({ key: 'LV', wi: -1, eq: LI.xEq, o: 'v', c: LI.x, lo: vz0 + WT + 0.1, hi: vz1 - WT - 0.1 });
        }
      }
      if (!WL.length) { said.push(nm + ': no outer wall -- no garage door'); continue; }
      /* the openings on a candidate wall, along it */
      function onW(o, W) {
        if (W.wi >= 0) return o.wall === W.wi && o.at > W.a0 && o.at < W.a1;
        if (!o.lwall || !o.pt) return false;
        return (W.o === 'h') ? (Math.abs(o.pt[1] - W.c) < 0.1 && o.pt[0] > W.lo - 0.5 && o.pt[0] < W.hi + 0.5)
                             : (Math.abs(o.pt[0] - W.c) < 0.1 && o.pt[1] > W.lo - 0.5 && o.pt[1] < W.hi + 0.5);
      }
      function along(o, W) { return (W.wi >= 0) ? o.at : ((W.o === 'h') ? o.pt[0] : o.pt[1]); }
      var mine = f.openings.filter(function (o) { return WL.some(function (W) { return onW(o, W); }); });
      if (mine.some(function (o) { return o.kind === 'garage'; })) { said.push(nm + ': a garage door already'); continue; }
      /* the unit nearest the width wanted that a stretch takes */
      function unitFor(cap, want) {
        var u = null;
        GU.forEach(function (it) {
          if (it.w > cap + 0.001) return;
          if (!u || Math.abs(it.w - want) < Math.abs(u.w - want) - 1e-6 || (Math.abs(Math.abs(it.w - want) - Math.abs(u.w - want)) <= 1e-6 && it.w > u.w)) u = it;
        });
        return u;
      }
      var wide = mine.filter(function (o) { return (o.kind === 'door' || o.kind === 'french') && o.w >= 2.0; }).sort(function (p, q) { return q.w - p.w; })[0] || null;
      var pick = null, why = '';
      if (wide) {
        var WW = WL.filter(function (W) { return onW(wide, W); })[0], uW = WW ? unitFor(WW.hi - WW.lo, wide.w) : null;
        if (uW) { pick = { W: WW, u: uW, at: along(wide, WW) }; why = 'in place of the ' + _impN(wide.w) + ' m ' + (wide.code || wide.kind) + ' the plan draws there'; }
      }
      if (!pick) {
        /* the street side first (the entrance's side -- an L's inner wall facing
           it counts), the L's own wall before an end wall of the same side,
           then the longest */
        var order = WL.slice().sort(function (p, q) {
          var ep = (p.eq === entW) ? 1 : 0, eq = (q.eq === entW) ? 1 : 0;
          if (ep !== eq) return eq - ep;
          return (q.hi - q.lo) - (p.hi - p.lo);
        });
        for (k = 0; k < order.length && !pick; k++) {
          var WO = order[k], uO = unitFor(WO.hi - WO.lo, GU[GU.length - 1].w);
          if (uO) { pick = { W: WO, u: uO, at: (WO.lo + WO.hi) / 2 }; why = (WO.eq === entW) ? ('on the entrance side (the street)' + (WO.wi < 0 ? ', in the wall of the L\'s corner' : '')) : 'on its longest outer wall'; }
        }
      }
      if (!pick) {
        var capMax = Math.max.apply(null, WL.map(function (W) { return W.hi - W.lo; }));
        said.push(nm + ': its outer walls take ' + _impN(Math.max(0, capMax)) + ' m, the narrowest garage door is ' + _impN(GU[0].w) + ' -- none put');
        continue;
      }
      var u = pick.u, W0 = pick.W, at = Math.max(W0.lo + u.w / 2, Math.min(W0.hi - u.w / 2, pick.at));
      /* what stands in its way on that wall: a window or the wide door goes;
         an ordinary door is moved along to clear it where its room has room,
         else it goes too */
      var gone = [], moved = [];
      for (k = 0; k < f.openings.length; k++) {
        var o2 = f.openings[k];
        if (!onW(o2, W0) || Math.abs(along(o2, W0) - at) >= (o2.w + u.w) / 2 + 0.1) continue;
        if (o2.kind === 'door' && o2 !== wide && o2.w < 2.0) {
          var a2 = along(o2, W0), lft = at - u.w / 2 - 0.1 - o2.w / 2, rgt = at + u.w / 2 + 0.1 + o2.w / 2, nA = null;
          if (lft - o2.w / 2 >= W0.lo) nA = lft; else if (rgt + o2.w / 2 <= W0.hi) nA = rgt;
          if (nA != null) {
            moved.push((o2.code || o2.kind) + ' ' + _impN(a2) + ' -> ' + _impN(nA));
            if (W0.wi >= 0) o2.at = _impR(nA); else if (W0.o === 'h') o2.pt[0] = _impR(nA); else o2.pt[1] = _impR(nA);
            continue;
          }
        }
        gone.push(o2);
      }
      for (k = 0; k < gone.length; k++) f.openings.splice(f.openings.indexOf(gone[k]), 1);
      var recG = { seg: 'S' + f.storey + '-W' + W0.wi, wall: W0.wi, at: _impR(at), w: u.w, h: u.h, sill: 0, kind: 'garage', code: u.code,
                   hand: null, leaves: Math.max(1, Math.round(u.w / 1.0)), name: u.name || 'garage', added: 'garage' };
      if (W0.wi < 0) { recG.seg = null; recG.at = 0; recG.lwall = true; recG.pt = (W0.o === 'h') ? [_impR(at), _impR(W0.c)] : [_impR(W0.c), _impR(at)]; }
      f.openings.push(recG);
      put++;
      said.push(nm + ': ' + u.code + ' ' + _impN(u.w) + ' x ' + _impN(u.h) + ' on ' + (W0.wi >= 0 ? ('wall ' + W0.wi) : ('the L corner\'s ' + (W0.o === 'h' ? 'cross' : 'side') + ' wall')) + ' at ' + _impN(at) + ' ' + why +
                (gone.length ? (', in place of ' + gone.map(function (o) { return (o.code || o.kind) + ' ' + _impN(o.w); }).join(', ')) : '') +
                (moved.length ? ('; the door moved clear of it: ' + moved.join(', ')) : ''));
    }
    return said.length ? ('GARAGE DOOR (v202): ' + put + ' garage door(s) put -- ' + said.join(' | ')) : '';
  }
  function _impOpenSym(f, WT) {
    var PTs = (_PH && _PH.partThk) ? _PH.partThk() : 0.1, said = [], wi, i, j, k;
    for (wi = 0; wi < 4; wi++) {
      var Lw = _impWallLen(f, wi), O = [];
      for (i = 0; i < f.openings.length; i++) if (f.openings[i].wall === wi && f.openings[i].w > 0) O.push(f.openings[i]);
      if (!O.length) continue;
      var M = _impWallMeets(f, wi), walls = M.filter(function (m) { return !m.line; }).map(function (m) { return m.at; });
      var SP = [{ lo: 0, hi: Lw, c: Lw / 2, what: 'the middle of the wall' }];
      function spans(cuts, what) {
        var cs = [0].concat(cuts, [Lw]), q;
        for (q = 1; q < cs.length; q++) {
          var a = cs[q - 1], b = cs[q];
          if (b - a < 0.6) continue;
          var ia = a + ((q === 1) ? WT : PTs / 2), ib = b - ((q === cs.length - 1) ? WT : PTs / 2);
          SP.push({ lo: a, hi: b, c: (ia + ib) / 2, what: what });
        }
      }
      spans(walls, 'the middle of the room');
      if (M.some(function (m) { return m.line; })) spans(M.map(function (m) { return m.at; }), 'the middle of the room at its room line');
      function inSp(o, at, sp) { return at - o.w / 2 >= sp.lo + 0.05 && at + o.w / 2 <= sp.hi - 0.05; }
      function tol(sp) { return Math.max(0.12, Math.min(0.40, 0.10 * (sp.hi - sp.lo))); }
      function ok(o, at, moving) {
        if (at - o.w / 2 < WT + 0.1 || at + o.w / 2 > Lw - WT - 0.1) return false;
        for (var q = 0; q < walls.length; q++) if (Math.abs(walls[q] - at) < o.w / 2 + PTs / 2 + 0.08) return false;
        for (q = 0; q < O.length; q++) { var p = O[q]; if (p === o || moving.indexOf(p) >= 0) continue; if (Math.abs(p.at - at) < (p.w + o.w) / 2 + 0.1) return false; }
        return true;
      }
      var done = [];
      for (i = 0; i < O.length; i++) for (j = i + 1; j < O.length; j++) {
        var A = O[i], B = O[j];
        if (done.indexOf(A) >= 0 || done.indexOf(B) >= 0 || Math.abs(A.w - B.w) > 0.02 || A.kind !== B.kind) continue;
        /* v158: the terrace's sliding door stays over the terrace */
        if (A.added === 'terrace' || B.added === 'terrace') continue;
        /* v202: a garage door stays where it was put */
        if (A.added === 'garage' || B.added === 'garage') continue;
        var mid = (A.at + B.at) / 2, best = null;
        for (k = 0; k < SP.length; k++) {
          var sp = SP[k], d = sp.c - mid;
          if (!inSp(A, A.at, sp) || !inSp(B, B.at, sp) || Math.abs(d) > tol(sp)) continue;
          if (!best || Math.abs(d) < Math.abs(best.d)) best = { sp: sp, d: d };
        }
        if (!best || Math.abs(best.d) < 0.005) { if (best) { done.push(A); done.push(B); } continue; }
        var nA = _impR(A.at + best.d), nB = _impR(B.at + best.d);
        if (!ok(A, nA, [B]) || !ok(B, nB, [A])) continue;
        said.push('wall ' + wi + ': ' + (A.code || A.kind) + ' ' + _impN(A.at) + ' and ' + _impN(B.at) + ' -> ' + _impN(nA) + ' and ' + _impN(nB) + ' (mirrored about ' + best.sp.what + ')');
        A.at = nA; B.at = nB; done.push(A); done.push(B);
      }
      for (i = 0; i < O.length; i++) {
        var C = O[i], bst = null;
        if (done.indexOf(C) >= 0 || C.added === 'terrace') continue;
        for (k = 0; k < SP.length; k++) {
          var sp2 = SP[k], d2 = sp2.c - C.at;
          if (!inSp(C, C.at, sp2) || Math.abs(d2) > tol(sp2)) continue;
          if (!bst || Math.abs(d2) < Math.abs(bst.d)) bst = { sp: sp2, d: d2 };
        }
        if (!bst || Math.abs(bst.d) < 0.005) continue;
        var nC = _impR(C.at + bst.d);
        if (!ok(C, nC, [])) continue;
        said.push('wall ' + wi + ': ' + (C.code || C.kind) + ' ' + _impN(C.at) + ' -> ' + _impN(nC) + ' (' + bst.sp.what + ')');
        C.at = nC;
      }
    }
    return said.length ? ('SYMMETRY (v148): ' + said.length + ' opening(s) set symmetric -- ' + said.join(' | ')) : '';
  }

  /* what the project holds, counted, for the lines and for telling whether
     an Undo brought it back */
  function _impSum(S) {
    var PR = S.product || {};
    return { w: S.footprint ? S.footprint.w : 0, l: S.footprint ? S.footprint.l : 0, parts: (S.parts || []).length, doors: (S.doors || []).length,
             openings: (S.openings || []).length, pins: (S.roomNames && S.roomNames.length) || 0, pieces: (PR.fixtures || []).length,
             wing: !!(S.wing && S.wing.on) };
  }
  function _impSumTxt(m) {
    return _impN(m.w) + ' x ' + _impN(m.l) + (m.wing ? ' + wing' : '') + ', partitions ' + m.parts + ', doors ' + m.doors + ', openings ' + m.openings +
           ', room names ' + m.pins + ', pieces ' + m.pieces;
  }
  /* WHAT IS COMPARED to tell whether a project is the one before or after an
     import. The room pins are not: the core pins any room that has none a
     moment after the plan settles, which read as the project changing under
     the import when nothing had (v100). The units are. */
  function _impDigest() {
    var C = _impCore(); if (!C) return '';
    var S = C.STATE, PR = S.product || {};
    function r3(v) { return (typeof v === 'number') ? Math.round(v * 1000) : v; }
    try {
      return JSON.stringify({
        f: [r3(S.footprint.w), r3(S.footprint.l)], o: S.origin ? [r3(S.origin.x), r3(S.origin.z)] : null, wg: !!(S.wing && S.wing.on), u: !!S.isMetric,
        pg: S.polygon ? S.polygon.length : 0,
        p: (S.parts || []).map(function (p) { return [p.id, p.o || p.dir, r3(p.c), r3(p.s), r3(p.e), p.st | 0].concat(p.sep ? ['line'] : []); }),
        d: (S.doors || []).map(function (d) { return [d.pid, r3(d.u), r3(d.w)].concat(d.kind ? [d.kind] : []); }),
        op: (S.openings || []).map(function (o) { return [o.seg, r3(o.at), r3(o.w), o.code || '']; }),
        fx: (PR.fixtures || []).map(function (x) { return [x.kind, r3(x.x), r3(x.z), x.rot | 0]; })
      });
    } catch (e) { return ''; }
  }

  /* THE COPY THAT OUTLIVES THE PAGE (v96). Undo lives in the open page
     only, so a reload after an import used to leave no way back. The project
     as it was is kept on this device as well -- one copy, the last import,
     for a week -- with the short link it was last saved under (every save
     gets a link of its own, so that link still opens it). RESTORE reads it
     after a reload; an Undo or a RESTORE that brings the project back lets
     it go. A copy from another product is never offered. */
  var IMP_KEEP = 'leva-plan-import-kept', IMP_KEEP_MS = 7 * 864e5;
  function _impShareId() {
    try { return new URL(global.location.href).searchParams.get('p') || ''; } catch (e) { return ''; }
  }
  function _impProdName() {
    var C = _impCore();
    try { return (C && C.payload) ? String(C.payload().productName || '') : ''; } catch (e) { return ''; }
  }
  function _impKeepWrite(o) {
    _imp.keptRead = true; _imp.kept = null;
    try { global.localStorage.setItem(IMP_KEEP, JSON.stringify(o)); _imp.kept = o; return true; } catch (e) { return false; }
  }
  function _impKeepClear() {
    _imp.keptRead = true; _imp.kept = null;
    try { global.localStorage.removeItem(IMP_KEEP); } catch (e) {}
  }
  function _impKept() {
    if (_imp.keptRead) return _imp.kept;
    var o = null, prod;
    try { o = JSON.parse(global.localStorage.getItem(IMP_KEEP) || 'null'); } catch (e) { o = null; }
    if (!_impCore()) return null;
    _imp.keptRead = true; _imp.kept = null;
    if (!o || !o.before) return null;
    if (!(Date.now() - (o.at || 0) < IMP_KEEP_MS)) { _impKeepClear(); return null; }
    prod = _impProdName();
    if (o.product && prod && o.product !== prod) return null;
    _imp.kept = o;
    return o;
  }
  function _impWhen(t) {
    var d = new Date(t || 0);
    function two(v) { return (v < 10 ? '0' : '') + v; }
    return two(d.getHours()) + ':' + two(d.getMinutes()) + ' ' + d.getDate() + '.' + (d.getMonth() + 1) + '.';
  }

  /* v203: the partitions against the product's L -- a stretch lying on one
     of the L's walls (within 0.10) or inside its corner goes; what is left
     of a partition stays in pieces 0.30 m or longer, a cut end tied to
     nothing; a door in a stretch that went comes back as { pt, w } for an
     outer door there. Coordinates are the house's (the axis outline, a
     partition's c / s / e as written); in a block house the walls are found
     within half the wall more. */
  function _impLClip(S, P, f, fOut) {
    var out = { doors: [], said: '' }, n = P.length, i, k, gone = 0, cut = 0, nid = 0;
    /* a block house's outline is the wall's axis and the fit stood on its outer faces */
    var tolW = 0.10 + (fOut > 0 ? fOut : 0);
    var bx0 = 1e9, bx1 = -1e9, bz0 = 1e9, bz1 = -1e9;
    for (i = 0; i < n; i++) { bx0 = Math.min(bx0, P[i][0]); bx1 = Math.max(bx1, P[i][0]); bz0 = Math.min(bz0, P[i][1]); bz1 = Math.max(bz1, P[i][1]); }
    function inPoly(x, z) { return _impInPoly(x, z, P); }
    var parts = S.parts || [], keep = [];
    for (i = 0; i < parts.length; i++) nid = Math.max(nid, parts[i].id | 0);
    for (i = 0; i < parts.length; i++) {
      var p = parts[i];
      if (!p || (p.st | 0) !== (f.storey | 0)) { keep.push(p); continue; }
      var h = (p.o === 'h'), lo = (p.s != null) ? p.s : (h ? bx0 : bz0), hi = (p.e != null) ? p.e : (h ? bx1 : bz1), c = p.c;
      /* the stretches of [lo, hi] that stay: inside the L and not on one of its walls */
      var step = 0.02, pcs = [], a = null, t;
      for (t = lo; t <= hi + 1e-6; t += step) {
        var x = h ? t : c, z = h ? c : t, okP = inPoly(x, z);
        if (okP) for (k = 0; k < n && okP; k++) {
          var A = P[k], B = P[(k + 1) % n], ho = Math.abs(B[1] - A[1]) < 1e-6;
          if (ho !== h) continue;
          var on = h ? Math.abs(z - A[1]) < tolW : Math.abs(x - A[0]) < tolW;
          var lo2 = h ? Math.min(A[0], B[0]) : Math.min(A[1], B[1]), hi2 = h ? Math.max(A[0], B[0]) : Math.max(A[1], B[1]);
          if (on && t >= lo2 - 0.05 && t <= hi2 + 0.05) okP = false;
        }
        if (okP && a == null) a = t;
        if (!okP && a != null) { pcs.push([a, t - step]); a = null; }
      }
      if (a != null) pcs.push([a, hi]);
      var whole = pcs.length === 1 && Math.abs(pcs[0][0] - lo) < 0.03 && Math.abs(pcs[0][1] - hi) < 0.03;
      if (whole) { keep.push(p); continue; }
      /* the doors in the stretches that went */
      var DS = (S.doors || []).filter(function (d) { return d.pid === p.id; });
      DS.forEach(function (d) {
        var inK = pcs.some(function (q) { return d.u - d.w / 2 >= q[0] - 0.02 && d.u + d.w / 2 <= q[1] + 0.02; });
        if (!inK) { out.doors.push({ pt: h ? [_impR(d.u), _impR(c)] : [_impR(c), _impR(d.u)], w: d.w }); S.doors.splice(S.doors.indexOf(d), 1); }
      });
      pcs = pcs.filter(function (q) { return q[1] - q[0] >= 0.30; });
      if (!pcs.length) { gone++; S.doors = (S.doors || []).filter(function (d) { return d.pid !== p.id; }); continue; }
      cut++;
      pcs.forEach(function (q, j) {
        var np = {}, key;
        for (key in p) if (p.hasOwnProperty(key)) np[key] = p[key];
        if (j > 0) np.id = ++nid;
        np.s = _impR(q[0]); np.e = _impR(q[1]);
        if (Math.abs(q[0] - lo) > 0.03) delete np.sTo;
        if (Math.abs(q[1] - hi) > 0.03) delete np.eTo;
        /* its doors go with the piece they stand in */
        if (j > 0) (S.doors || []).forEach(function (d) { if (d.pid === p.id && d.u >= q[0] && d.u <= q[1]) d.pid = np.id; });
        keep.push(np);
      });
    }
    S.parts = keep;
    out.said = gone + ' partition(s) on the walls of the L or in its corner taken out, ' + cut + ' cut to the L' + (out.doors.length ? (', ' + out.doors.length + ' door(s) in them made outer doors') : '');
    return out;
  }
  /* v203: every opening onto the wall of the L it stands on -- its point (a
     wall of the rectangle and its place along it, or the point an L wall's
     opening carries), the nearest wall of the L running the same way, held
     inside it; seg and at are then the axis outline's own */
  function _impLMap(OP, P, f, fOut) {
    var n = P.length, i, k, moved = 0, lost = 0, onL = 0;
    for (i = 0; i < OP.length; i++) {
      var o = OP[i], pt;
      if (o.lwall && o.pt) pt = o.pt;
      else if (o.wall >= 0) pt = _impWallPt(f, o.wall, o.at, 0);
      else { lost++; continue; }
      var ux0 = 0, uz0 = 0;
      if (!o.lwall) { if (o.wall === 0 || o.wall === 2) ux0 = 1; else uz0 = 1; }
      var best = -1, bs = 1e9, bAt = 0, bL = 0;
      for (k = 0; k < n; k++) {
        var A = P[k], B = P[(k + 1) % n], L = Math.hypot(B[0] - A[0], B[1] - A[1]);
        if (!(L > 0.01)) continue;
        var vx = (B[0] - A[0]) / L, vz = (B[1] - A[1]) / L;
        if (!o.lwall && Math.abs(ux0 * vx + uz0 * vz) < 0.99) continue;
        var al = (pt[0] - A[0]) * vx + (pt[1] - A[1]) * vz, off = Math.abs((pt[0] - A[0]) * (-vz) + (pt[1] - A[1]) * vx);
        var outside = Math.max(0, -al, al - L), sc = off + 2 * outside;
        if (sc < bs) { bs = sc; best = k; bAt = al; bL = L; }
      }
      if (best < 0) { lost++; continue; }
      var hw = (+o.w || 0.9) / 2 + 0.05, at2 = (bL > 2 * hw) ? Math.max(hw, Math.min(bL - hw, bAt)) : bL / 2;
      var sg2 = 'S' + (f.storey | 0) + '-W' + best;
      if (o.lwall) onL++; else if (sg2 !== o.seg) moved++;
      o.seg = sg2; o.wall = best; o.at = _impR(at2); o.atAxis = true;
    }
    return { said: onL + ' opening(s) in the walls of the L\'s corner, ' + moved + ' on the outer walls renumbered for the L' + (lost ? (', ' + lost + ' with no wall') : '') };
  }
  function _impApply(f) {
    var C = _impCore(), res = { ok: false, why: '', notes: [], was: null, now: null, steps: '', kept: false, keptP: '' };
    if (!f || !f.ok) { res.why = 'fit'; res.notes.push('nothing to write: ' + ((f && f.why) || 'no plan')); return res; }
    if (!C) { res.why = 'core'; res.notes.push('no house core'); return res; }
    var S = C.STATE, i, k, before = '', st0 = -1;
    try { before = JSON.stringify(C.payload()); } catch (eP) { before = ''; }
    if (!before) { res.why = 'payload'; res.notes.push('the project could not be copied before writing, so nothing was written'); return res; }
    try { st0 = C.undoState ? C.undoState().steps : -1; } catch (eU0) {}
    var pBefore = _impShareId(), prodBefore = '';
    try { prodBefore = String(JSON.parse(before).productName || ''); } catch (eN) {}
    /* THE STATE BEFORE IS THE UNDO STEP: pushed now (the core skips it when
       it is the last one already), and the new one pushed after */
    try { if (C.undoPush) C.undoPush(); } catch (eU1) {}
    res.was = _impSum(S);
    var digBefore = _impDigest();
    if (f.units && ((f.units === 'imperial') === !!S.isMetric) && typeof C.toggleUnits === 'function') {
      try { C.toggleUnits(); res.notes.push('the project was switched to ' + f.units + ' units, as the plan is drawn'); }
      catch (eT) { res.notes.push('switching the project to ' + f.units + ' units failed: ' + ((eT && eT.message) || eT)); }
    }
    try {
      var otherP = 0, otherO = 0, pre = 'S' + f.storey + '-';
      for (i = 0; i < (S.parts || []).length; i++) if (S.parts[i] && (S.parts[i].st | 0) !== f.storey) otherP++;
      for (i = 0; i < (S.openings || []).length; i++) if (S.openings[i] && String(S.openings[i].seg || '').indexOf(pre) !== 0) otherO++;
      if (otherP || otherO) res.notes.push('the other storey lost ' + otherP + ' partition(s) and ' + otherO + ' opening(s) -- the outline under them changed');
      S.polygon = null;
      if (S.wing && S.wing.on) { S.wing.on = false; res.notes.push('the side wing was taken off -- the plan is one rectangle'); }
      /* THE PRODUCT'S OWN OUTLINE (v166, RANCH's L corner). A product that
         shapes the outline itself sets it again before every build, so the
         polygon cleared above came back on the build and the plan's openings
         (S0-W0..3 of the rectangle) stood on other walls. The product is told
         the plan is one rectangle now; it answers with a line for the notes. */
      try {
        if (typeof C.importOutline === 'function') { var ioN = C.importOutline(f); if (ioN) res.notes.push(String(ioN)); }
      } catch (eIO) { res.notes.push('the product could not put its outline back to a rectangle: ' + ((eIO && eIO.message) || eIO)); }
      /* v170: a fit over the outer faces is written on the wall axis */
      var fOut = (f.out > 0) ? f.out : 0;
      S.footprint.w = fOut ? _impR(f.w - 2 * fOut) : f.w; S.footprint.l = fOut ? _impR(f.l - 2 * fOut) : f.l;
      if (fOut) res.notes.push('the plan\'s outer faces ' + _impN(f.w) + ' x ' + _impN(f.l) + ' -> wall axis ' + _impN(S.footprint.w) + ' x ' + _impN(S.footprint.l) + ' (v170)');
      S.parts = f.parts.map(function (p) {
        var q = { id: p.id, o: p.o, dir: p.o, c: p.c, at: p.c, st: f.storey };
        if (p.s != null) q.s = p.s;
        if (p.e != null) q.e = p.e;
        if (p.sTo != null) q.sTo = p.sTo;
        if (p.eTo != null) q.eTo = p.eTo;
        if (p.sep) q.sep = true;
        return q;
      });
      S.doors = f.doors.map(function (d) {
        var q = { pid: d.pid, u: d.u, w: d.w, sw: d.sw | 0 };
        if (d.pass) q.pass = true;
        if (d.slide) q.slide = d.slide;
        if (d.kind) q.kind = d.kind;
        return q;
      });
      /* THE L (v203): the product takes the plan's corner as its L corner and
         answers with its outline; the partitions on the corner's two walls
         and inside it go (those walls are the house's now), a door in them
         becomes an outer door there, and every window and outer door is set
         on the wall of the L it stands on, by its point */
      var LnR = null, lnSaid = [];
      if (f.lnotch && typeof C.importNotch === 'function') {
        try { LnR = C.importNotch({ x0: f.lnotch.x0, x1: f.lnotch.x1, z0: f.lnotch.z0, z1: f.lnotch.z1 }); } catch (eLN) { LnR = null; res.notes.push('the product could not take the L: ' + ((eLN && eLN.message) || eLN)); }
        if (LnR && LnR.poly && LnR.poly.length >= 6) {
          if (LnR.note) lnSaid.push(String(LnR.note));
          var lnc = _impLClip(S, LnR.poly, f, fOut);
          if (lnc.said) lnSaid.push(lnc.said);
          for (i = 0; i < lnc.doors.length; i++) {
            var dO = lnc.doors[i], unD = _impUnit('door', dO.w, 0, 0, dO.w >= 1.2 ? 'slide' : '');
            if (unD) f.openings.push({ seg: null, wall: -1, pt: dO.pt, at: 0, w: unD.w, h: unD.h, sill: 0, kind: unD.kind, code: unD.code, hand: unD.hand, leaves: unD.leaves, name: unD.name, out: null, lwall: true });
          }
          var lnm = _impLMap(f.openings, LnR.poly, f, fOut);
          lnSaid.push(lnm.said);
        } else if (f.lnotch) { LnR = null; lnSaid.push('the product kept its rectangle -- the corner\'s walls stay partitions'); }
        if (lnSaid.length) res.notes.push('L PLAN (v203): ' + lnSaid.join('; '));
      }
      /* the openings by the core's own rules: on a stud face, under the head
         of the wall, and not over each other */
      var put = [];
      S.openings = put;
      for (i = 0; i < f.openings.length; i++) {
        var o = f.openings[i], sg = (C.segment && o.seg) ? C.segment(o.seg) : null;
        if (!sg) { res.notes.push('wall ' + (o.seg || 'of the L') + ' is not there -- a ' + o.kind + ' left out'); continue; }
        var rec = { seg: o.seg, at: o.atAxis ? o.at : (o.at - fOut), w: o.w, h: o.h, sill: o.sill, kind: o.kind, code: o.code, hand: o.hand,
                    fullW: null, halfW: null, rake: null, out: o.out ? true : null,
                    leaves: o.leaves || Math.max(1, Math.round(o.w / ((o.kind === 'french') ? 1.0 : 1.4))) };
        try { if (C.snapOpeningAt) rec.at = C.snapOpeningAt(rec, rec.at); } catch (eS) {}
        if (sg.h && rec.sill + rec.h > sg.h) {
          var h0 = rec.h;
          rec.h = Math.max(0.3, sg.h - rec.sill - 0.05);
          res.notes.push(rec.code + ' on ' + rec.seg + ' is ' + _impN(h0) + ' high and the wall leaves ' + _impN(sg.h - rec.sill) + ' -- made ' + _impN(rec.h));
        }
        var clash = null;
        for (k = 0; k < put.length; k++) {
          if (put[k].seg === rec.seg && Math.abs(put[k].at - rec.at) < (put[k].w + rec.w) / 2 + 0.12) { clash = put[k]; break; }
        }
        if (clash) { res.notes.push('a ' + rec.kind + ' on ' + rec.seg + ' at ' + _impN(rec.at) + ' would overlap another -- left out'); continue; }
        put.push(rec);
      }
      S.roomNames = f.pins.filter(function (q) { return !(LnR && f.lnotch && q.x > f.lnotch.x0 + 0.05 && q.x < f.lnotch.x1 - 0.05 && q.z > f.lnotch.z0 + 0.05 && q.z < f.lnotch.z1 - 0.05); })
        .map(function (q) { return { x: q.x, z: q.z, n: q.n, k: q.k }; })
        .concat((f.closets || []).map(function (c) { return { x: c.x, z: c.z, n: 0, k: CLOSET_KEY }; }));
      /* the pieces stood in rooms that are no longer there */
      var PR = S.product = S.product || {}, nFx = (PR.fixtures || []).length;
      if (PR.fixtures) PR.fixtures.length = 0;
      if (nFx) res.notes.push(nFx + ' fitout piece(s) were taken out -- the rooms they stood in are gone');
      var odN = _impOutdoor(S, f);
      if (odN) res.notes.push(odN);
      C.savePref();
      C.rebuild();
      /* THE SPACES THE PLAN DID NOT NAME (v102): closing the rooms leaves the
         bits between them -- a hall, an entry -- as rooms of their own, and
         with no name they took whichever name came next in the list (a hall
         called kitchen). They are halls, so they are called that. */
      var cbc = _impClosetsByCell(S, f);
      if (cbc) { res.notes.push(cbc); C.savePref(); C.rebuild(); }
      /* THE CLOSET DOORS OPEN OUT OF THE CLOSET (v106): the reader gives no
         swing for a pair or a bifold, and which side is the closet is only
         known once the rooms are there */
      var nOut = 0;
      for (i = 0; i < (S.doors || []).length; i++) {
        var dK = S.doors[i];
        if (!dK || (dK.kind !== 'pair' && dK.kind !== 'bifold')) continue;
        var swK = _closetSwing(dK);
        if (swK !== (dK.sw | 0)) { dK.sw = swK; nOut++; }
      }
      if (nOut) { res.notes.push(nOut + ' closet door(s) turned to open out of the closet'); C.savePref(); C.rebuild(); }
      var nLeft = _impNameLeftovers(S, f);
      if (nLeft) { res.notes.push(nLeft + ' space(s) the plan did not name were named as a hall'); C.savePref(); C.rebuild(); }
      /* v169: one open room, one name -- before the pieces and the living
         set, which go by the room's name */
      var oneN = _impOneName(S);
      if (oneN) { res.notes.push(oneN); C.savePref(); C.rebuild(); }
      /* v127: the slanted walls before the pieces, so the placer keeps the
         pieces off them, and inside the Undo step the plan makes */
      try { _impDiagWrite(f); } catch (eDg) { res.notes.push('slanted walls: writing them THREW ' + ((eDg && eDg.message) || eDg)); }
      try { var wSay = _impWedgeSay(); if (wSay) res.notes.push(wSay); } catch (eWs) { res.notes.push('slanted walls: their corners THREW ' + ((eWs && eWs.message) || eWs)); }
      var fxs = _impPlaceFixtures(f);
      if (fxs) { res.notes.push(fxs); C.savePref(); C.rebuild(); }
      /* v143: the living room's coffee table, armchair and bigger sofa */
      var lvs = '';
      try { lvs = _impLivingSet(); } catch (eLv) { lvs = 'LIVING (v146): THREW ' + ((eLv && eLv.message) || eLv); }
      if (lvs) { res.notes.push(lvs); C.savePref(); C.rebuild(); }
      /* v173: a bedroom's walk-in closet is its wardrobe */
      var wrd = '';
      try { wrd = _impWardrobeRooms(S); } catch (eWr) { wrd = 'WARDROBE (v173): THREW ' + ((eWr && eWr.message) || eWr); }
      if (wrd) { res.notes.push(wrd); C.savePref(); C.rebuild(); }
      /* v173: what still stands on another piece, over a wall or in a door */
      var lst = '';
      try { lst = _impLastClear(); } catch (eLc) { lst = 'LAST (v173): THREW ' + ((eLc && eLc.message) || eLc); }
      if (lst) { res.notes.push(lst); C.savePref(); C.rebuild(); }
      /* v135: the plan checked as built */
      try { res.audit = _imp.audit = _impAudit(S); } catch (eAu) { res.audit = _imp.audit = { ok: false, problems: ['the check THREW ' + ((eAu && eAu.message) || eAu)] }; }
    } catch (e) {
      /* NOTHING HALF-WRITTEN: whatever went wrong, the project goes back */
      _impPutBack(before);
      res.why = 'threw: ' + ((e && e.message) || e);
      res.notes.push('writing failed and the project was put back as it was');
      return res;
    }
    try { if (C.undoPush) C.undoPush(); } catch (eU2) {}
    res.now = _impSum(S);
    try { res.steps = st0 + ' -> ' + (C.undoState ? C.undoState().steps : '?'); } catch (eU3) {}
    _imp.before = before; _imp.digBefore = digBefore; _imp.digAfter = _impDigest();
    _imp.prev = null;
    res.keptP = pBefore;
    res.kept = _impKeepWrite({ at: Date.now(), product: prodBefore, p: pBefore, was: res.was, dig: digBefore, digAfter: _imp.digAfter, before: before });
    res.ok = true;
    try { if (_PH && _PH.log) _PH.log('plan import: ' + _impSumTxt(res.was) + ' -> ' + _impSumTxt(res.now)); } catch (eL) {}
    return res;
  }
  /* THE FITTINGS, PLACED (v111, Marek: ty by se mely take vygenerovat -- vana,
     kuchynska linka, postel). Each box the reader drew becomes the piece the
     + offers that fits it: a bath of 1520 or 1800 by its length, a shower of
     900 or 1200, a basin of 500 or 800, a counter of 2.4, 3.0 or 3.6 m, an L
     or a U, a double or a single bed by its width, a sofa by its length. A
     piece that stands at a wall goes back to the wall of its room nearest to
     the box -- of the sides near a wall, the one whose length is the piece's
     own width, so a counter backs onto its long side and a bed onto its
     head -- and stays where the box is along it; a free-standing piece
     stands in the middle of its box, turned with it. What the catalogue has
     no piece for (a dryer, a water heater) is named in the log. */
  /* THE TABLE FOR ITS CHAIRS, THE COUNTER FOR ITS STOOLS (v150, Marek, plan
     1000073678: doplnit fitouts i o vybaveni, ktere v tomto floorplanu je --
     barovy pult se zidlickami a jidelni stul pro 6+2 osoby, a umistit je na
     jejich misto). The reader counts the chairs drawn round a table and the
     stools drawn at a counter (leva-ai w2.05). A table for one or two is the
     small one, for three or four the table of four, for five or six the
     table of six (three a side), for seven and more the table of eight
     (three a side and one at each end). A counter with one or two stools is
     the bar table, with three the 1.8 m island, with four or more the 2.4 m
     one. With no count, the drawn size decides as before; a table drawn 2.4
     m and over with its chairs is the eight, 2.0 m and over the six. A dryer
     drawn apart from the washer is a dryer. */
  function _impFixKind(type, a, b, label, n) {
    var nSe = (n && n.seats) | 0, nSt = (n && n.stools) | 0;
    if (type === 'table' && nSe > 0) return (nSe <= 2) ? 'table2' : ((nSe <= 4) ? 'table' : ((nSe <= 6) ? 'table6' : 'table8'));
    if (type === 'island' && nSt > 0) return (nSt <= 2) ? 'barTable' : ((nSt === 3) ? 'island18' : 'island24');
    if (type === 'table' && a >= 2.4) return 'table8';
    if (type === 'table' && a >= 2.0) return 'table6';
    if (type === 'dryer') return 'dryer';
    /* v114: the word written in the piece decides before its drawn size --
       a QUEEN drawn a little narrow is still a double bed, a 5 FT tub the
       short one */
    var LB = String(label || '').toUpperCase();
    if (type === 'bed') {
      if (/KING|QUEEN|DOUBLE|FULL|MASTER/.test(LB)) return 'bedDouble';
      if (/TWIN|SINGLE|BUNK/.test(LB)) return 'bedSingle';
    }
    if (type === 'bathtub') {
      if (/(^|[^0-9])5 ?('|FT|FOOT)|60/.test(LB)) return 'bath15';
      if (/(^|[^0-9])(5.5|6) ?('|FT|FOOT)|66|72/.test(LB)) return 'bath18';
    }
    switch (type) {
      case 'bathtub': return (a >= 1.65) ? 'bath18' : 'bath15';
      case 'shower': return (a >= 1.05) ? 'shower120' : 'shower90';
      case 'toilet': return 'wc';
      case 'basin': return (a >= 0.65) ? 'basin80' : 'basin50';
      case 'kitchen': return (a >= 3.3) ? 'kitchen36' : ((a >= 2.7) ? 'kitchen30' : 'kitchen24');
      case 'kitchen_l': return 'kitchenL';
      case 'kitchen_u': return 'kitchenU';
      case 'island': return 'island';
      case 'fridge': return 'fridge';
      case 'washer': return 'washer';
      case 'bed': return (b >= 1.05) ? 'bedDouble' : 'bedSingle';
      case 'sofa': return (b >= 1.4) ? 'sofaL' : ((a >= 2.35) ? 'sofa4' : ((a >= 1.85) ? 'sofa3' : 'sofa2'));
      /* a small low table in front of a sofa is a coffee table (v118) */
      /* v169: the table of four is 1.40 x 0.80 -- a table drawn 1.37 x 0.75
         (plan 1000075648) is that one, not the table of two */
      case 'table': return (a >= 1.5 || (a >= 1.3 && b >= 0.7)) ? 'table' : ((a <= 1.25 && b <= 0.7) ? 'coffee' : 'table2');
      case 'desk': return 'desk';
      /* v174: the easy chairs and the TV unit the reader draws now (w2.21) */
      case 'armchair': return 'armchair';
      case 'tv_stand': case 'tv': return 'tvStand';
      case 'wardrobe': return (a >= 1.1) ? 'wardrobe' : ((a >= 0.9) ? 'wardrobe100' : 'wardrobe80');
    }
    return '';
  }
  /* the faces of the walls round a point, found by looking each way from it
     to the nearest wall that crosses its line -- a real wall, not a room
     line, as nothing backs onto a line. The room's own box (_roomFaces)
     grows cell by cell and stopped at the line of the closets' wall in the
     next room, so a bed was stood a metre short of where it was drawn. */
  function _impFaces(x, z) {
    var H = _PH; if (!H) return null;
    var P = H.poly(), WALL = H.wallThk ? H.wallThk() : 0.16, PT = H.partThk ? H.partThk() : 0.1, k;
    var bx0 = 1e9, bx1 = -1e9, bz0 = 1e9, bz1 = -1e9;
    for (k = 0; k < P.length; k++) { bx0 = Math.min(bx0, P[k][0]); bx1 = Math.max(bx1, P[k][0]); bz0 = Math.min(bz0, P[k][1]); bz1 = Math.max(bz1, P[k][1]); }
    var F = { x0: bx0 + WALL, x1: bx1 - WALL, z0: bz0 + WALL, z1: bz1 - WALL };
    var ps = H.partitions ? (H.partitions() || []) : [];
    for (k = 0; k < ps.length; k++) {
      var p = ps[k]; if (!p || p.sep) continue;
      var sp = _partSpan(p); if (!sp) continue;
      var lo = Math.min(sp[0], sp[1]), hi = Math.max(sp[0], sp[1]);
      if (p.o === 'h') {
        if (x < lo || x > hi) continue;
        if (p.c <= z && p.c + PT / 2 > F.z0) F.z0 = p.c + PT / 2;
        if (p.c > z && p.c - PT / 2 < F.z1) F.z1 = p.c - PT / 2;
      } else {
        if (z < lo || z > hi) continue;
        if (p.c <= x && p.c + PT / 2 > F.x0) F.x0 = p.c + PT / 2;
        if (p.c > x && p.c - PT / 2 < F.x1) F.x1 = p.c - PT / 2;
      }
    }
    return F;
  }
  /* THE L AND THE U (v114). An L was turned like a straight counter -- to
     the wall nearest its box -- and stood in the middle of it, so its short
     arm came out on the wrong side, in the room. Now the L takes the corner
     of its box that is nearest two walls: its long arm (the piece's back,
     drawn along its own top) goes on the box side at the wall that fits its
     length best, and the short arm turns to the side of that corner -- the
     mirror is what turns it. The table, from the drawing (back at -z, the
     short arm at the piece's own left):
       back top    : arm left  rot 0       arm right rot 0 mirror
       back right  : arm top   rot 1       arm bottom rot 1 mirror
       back bottom : arm right rot 2       arm left  rot 2 mirror
       back left   : arm bottom rot 3      arm top   rot 3 mirror */
  function _impLCorner(q, F, D) {
    var bw = q.x1 - q.x0, bd = q.z1 - q.z0;
    var dT = q.z0 - F.z0, dB = F.z1 - q.z1, dL = q.x0 - F.x0, dR = F.x1 - q.x1;
    /* both sides of the corner come to a wall, so a few centimetres more
       on one says nothing; the length of the run decides */
    /* v165 (plan 1000075648): and an arm that would run past its box --
       the L's box cut back off the fridge at the end of its short arm --
       goes the other way where it fits */
    function sc(dist, along, across) { return ((dist < 0.4) ? 0 : (5 + dist * 2)) + Math.abs(along - D.w) * 0.5 + ((along < D.w - 0.1) ? 3 : 0) + ((across < D.d - 0.1) ? 3 : 0); }
    var top = dT <= dB, left = dL <= dR;
    var sH = sc(top ? dT : dB, bw, bd), sV = sc(left ? dL : dR, bd, bw);
    var backTB = (sH < sV) || (sH === sV && bw >= bd);
    var rot, mir;
    if (backTB) {
      if (top) { rot = 0; mir = !left; } else { rot = 2; mir = left; }
    } else {
      if (left) { rot = 3; mir = top; } else { rot = 1; mir = !top; }
    }
    return { rot: rot, mirror: mir, top: top, left: left, dSide: backTB ? (left ? dL : dR) : (top ? dT : dB) };
  }
  /* where a piece the reader drew goes: against the wall of its room nearest
     its box, or in the middle of the box, turned with it (v111, v114) */
  var _impKitAt = [];
  /* A COUNTER WITH STOOLS FACES THE ROOM (v150). Its top stands at the back
     of the piece, the stools in front: the back goes to the kitchen counter
     drawn in the plan, or with none to the nearer wall of the room, and the
     stools face the other way. The reader's box is the counter alone where
     it is shallower than the piece with its stools, and the piece then moves
     forward by the stools' depth so the counter stays where it was drawn. */
  var IMP_STOOLED = { barTable: 1, island18: 1, island24: 1 };
  /* v197: the inside doors' zones, once per placing of a plan's pieces */
  var _impPoseZ = null;
  function _impPose(q, kind, D, i, compact) {
    var bw = q.x1 - q.x0, bd = q.z1 - q.z0;
    var cx = (q.x0 + q.x1) / 2, cz = (q.z0 + q.z1) / 2;
    var F = _impFaces(cx, cz) || { x0: q.x0, x1: q.x1, z0: q.z0, z1: q.z1 }, rot = 0, LC = null, why = '';
    if (!D.wall) {
      rot = ((bw >= bd) === (D.w >= D.d)) ? 0 : 1;
      if (IMP_STOOLED[kind]) {
        var kit = null, kd = 1e9, k9;
        for (k9 = 0; k9 < _impKitAt.length; k9++) { var dk = Math.hypot(_impKitAt[k9][0] - cx, _impKitAt[k9][1] - cz); if (dk < kd) { kd = dk; kit = _impKitAt[k9]; } }
        /* the back: rot 0 north, 2 south, 1 east, 3 west */
        if (rot === 0) rot = kit ? ((kit[1] <= cz) ? 0 : 2) : (((cz - F.z0) <= (F.z1 - cz)) ? 0 : 2);
        else rot = kit ? ((kit[0] >= cx) ? 1 : 3) : (((F.x1 - cx) <= (cx - F.x0)) ? 1 : 3);
        var tdS = D.td || D.d, shS = (Math.min(bw, bd) < (tdS + D.d) / 2) ? (D.d - tdS) / 2 : 0;
        if (shS > 0) {
          if (rot === 0) cz += shS; else if (rot === 2) cz -= shS; else if (rot === 1) cx -= shS; else cx += shS;
        }
        why = 'the back to the ' + (kit ? 'kitchen' : 'nearer wall') + ', the stools ' + ['south', 'west', 'north', 'east'][rot] + (shS > 0 ? (', moved ' + _impN(shS) + ' m for them') : '');
      }
    } else if (D.shape === 'L') {
      LC = _impLCorner(q, F, D);
      rot = LC.rot;
    } else {
      var C4 = [{ r: 0, dist: q.z0 - F.z0, along: bw }, { r: 2, dist: F.z1 - q.z1, along: bw },
                { r: 3, dist: q.x0 - F.x0, along: bd }, { r: 1, dist: F.x1 - q.x1, along: bd }], best = null, bs = 1e9, k;
      /* v141: and, for a box clearly longer one way, the depth across that
         wall -- the bath's vanity of plan 1000073500, a long box in the
         corner by the door, was turned to the short wall and stood in the
         door's swing */
      /* v197 (Marek on plan 1000075646: lednice se vygenerovala nesmyslne
         na opacnem konci). The fridge drawn square in the corner by the
         bathroom door was turned with its back to the partition -- the box
         a little narrower once the plan was fitted -- so its width ran along
         that wall into the door's swing, and it was sent to the far end of
         the kitchen. A wall that would put the piece in an inside door's
         swing counts against it; the other wall of the corner wins. */
      if (_impPoseZ == null) { try { _impPoseZ = (_fitZones() || []).filter(function (z9) { return z9 && z9.t === 'door'; }); } catch (eZP) { _impPoseZ = []; } }
      for (k = 0; k < 4; k++) {
        var acr = (C4[k].r === 0 || C4[k].r === 2) ? bd : bw, elong = Math.max(bw, bd) >= 1.4 * Math.min(bw, bd);
        var sc = Math.max(0, C4[k].dist) * 2 + ((C4[k].dist < 0.4) ? 0 : 5) + Math.abs(C4[k].along - D.w) * 0.5 + (elong ? Math.abs(acr - D.d) * 0.5 : 0);
        if (C4[k].dist < 0.4 && _impPoseZ.length) {
          try {
            var gP = { kind: kind, x: cx, z: cz, rot: C4[k].r }, mP = _fixDims(gP);
            if (C4[k].r === 0) gP.z = F.z0 + mP.d / 2; else if (C4[k].r === 2) gP.z = F.z1 - mP.d / 2; else if (C4[k].r === 3) gP.x = F.x0 + mP.w / 2; else gP.x = F.x1 - mP.w / 2;
            if (mP.w < F.x1 - F.x0) gP.x = Math.max(F.x0 + mP.w / 2, Math.min(F.x1 - mP.w / 2, gP.x));
            if (mP.d < F.z1 - F.z0) gP.z = Math.max(F.z0 + mP.d / 2, Math.min(F.z1 - mP.d / 2, gP.z));
            var rP = _fixRect(gP), zq;
            for (zq = 0; zq < _impPoseZ.length; zq++) if (_fitHit(rP, _impPoseZ[zq], 0.005)) { sc += 8; break; }
          } catch (eSP) {}
        }
        if (sc < bs) { bs = sc; best = C4[k]; }
      }
      rot = best.r;
    }
    var fx = { id: 'f' + Date.now().toString(36) + i + Math.floor(Math.random() * 1000).toString(36), kind: kind, x: cx, z: cz, rot: rot };
    if (compact) fx.compact = true;
    if (LC && LC.mirror) fx.mirror = true;
    /* v167: the L or the U at the arms the plan draws */
    var KSz = IMP_KIT_SIZE[kind];
    if (KSz && (D.shape === 'L' || D.shape === 'U')) {
      var evK = (rot % 2 === 0), awK = evK ? bw : bd, adK = evK ? bd : bw;
      var spWK = evK ? (F.x1 - F.x0) : (F.z1 - F.z0), spDK = evK ? (F.z1 - F.z0) : (F.x1 - F.x0);
      var swK = _impKitStep(Math.min(KSz.w1, spWK, Math.max(KSz.w0, awK))), sdK = _impKitStep(Math.min(KSz.d1, spDK, Math.max(KSz.d0, adK)));
      if (swK >= KSz.w0 - 1e-6 && sdK >= KSz.d0 - 1e-6) {
        fx.sizeW = swK; fx.sizeD = sdK;
        why = (why ? (why + ', ') : '') + 'arms ' + _impN(swK) + ' x ' + _impN(sdK) + ' m as drawn (' + _impN(awK) + ' x ' + _impN(adK) + ', v167)';
      }
    }
    var stQ = _fixStorey(); if (stQ != null) fx.st = stQ;
    var m = _fixDims(fx);
    if (LC) {
      /* into the corner: the back to its wall, the short arm's end to the
         side wall when the box comes near it, else to the box's own edge */
      var nearS = LC.dSide < 0.6;
      if (rot === 0 || rot === 2) {
        fx.z = (rot === 0) ? F.z0 + m.d / 2 : F.z1 - m.d / 2;
        fx.x = LC.left ? ((nearS ? F.x0 : q.x0) + m.w / 2) : ((nearS ? F.x1 : q.x1) - m.w / 2);
      } else {
        fx.x = (rot === 3) ? F.x0 + m.w / 2 : F.x1 - m.w / 2;
        fx.z = LC.top ? ((nearS ? F.z0 : q.z0) + m.d / 2) : ((nearS ? F.z1 : q.z1) - m.d / 2);
      }
    } else if (D.wall) {
      if (rot === 0) fx.z = F.z0 + m.d / 2; else if (rot === 2) fx.z = F.z1 - m.d / 2;
      else if (rot === 3) fx.x = F.x0 + m.w / 2; else fx.x = F.x1 - m.w / 2;
    }
    if (m.w < F.x1 - F.x0) fx.x = Math.max(F.x0 + m.w / 2, Math.min(F.x1 - m.w / 2, fx.x));
    if (m.d < F.z1 - F.z0) fx.z = Math.max(F.z0 + m.d / 2, Math.min(F.z1 - m.d / 2, fx.z));
    fx.x = _impR(fx.x); fx.z = _impR(fx.z);
    return { fx: fx, F: F, why: why };
  }
  /* A DOOR'S ZONE IS ITS OWN ROOMS' (v141, plan 1000073500). The placer's
     zone round an inside door is a box: the leaf's swing on one side, room
     to pass on the other -- and a box knows no walls. The storage closet's
     door, its leaf longer than the closet is deep, reached through the
     closet's back wall onto the master bath's shower; the laundry closet's
     door, opening into the hall, kept the passing room inside the closet,
     where the washer is all the closet is for. Here a door's zone is let be
     for a piece in neither of the door's two rooms, and for a piece in a
     closet whose own door opens away from it. _fitZones lists the inside
     doors first, one zone each, in the order _doorsLive gives them. */
  function _impZonesFor(fx, Z) {
    if (!Z || !Z.length) return Z || [];
    var DL = [], RT = [];
    try { DL = _doorsLive() || []; RT = _roomsTracked() || []; } catch (eD) { return Z; }
    if (!DL.length || !RT.length || Z.length < DL.length) return Z;
    function roomAt(x, z) { for (var r = 0; r < RT.length; r++) if (_roomHolds(RT[r], x, z)) return r; return -1; }
    var rP = roomAt(fx.x, fx.z), out = [], i;
    if (rP < 0) return Z;
    for (i = 0; i < Z.length; i++) {
      if (i < DL.length && Z[i] && Z[i].t === 'door' && Z[i].why === 'interior') {
        var d = DL[i].d, p = DL[i].p, cx = p.o === 'h' ? d.u : p.c, cz = p.o === 'h' ? p.c : d.u;
        var sg = (p.o === 'v') ? (((d.sw | 0) & 2) ? -1 : 1) : (((d.sw | 0) & 2) ? 1 : -1);
        var rS = roomAt(p.o === 'h' ? cx : cx + sg * 0.2, p.o === 'h' ? cz + sg * 0.2 : cz), rO = roomAt(p.o === 'h' ? cx : cx - sg * 0.2, p.o === 'h' ? cz - sg * 0.2 : cz);
        var leafy = !(d.slide || d.pass || d.kind === 'bypass');
        if (rP !== rS && rP !== rO) continue;
        if (RT[rP] && RT[rP].closet && (rP === rO || !leafy)) continue;
      }
      out.push(Z[i]);
    }
    return out;
  }
  /* ---------- THE LIVING ROOM'S SET (v143) --------------------------------
     Marek: pokud je misto, chtelo by to umistovat do living space i
     konferencni stolek a kreslo, pripadne vetsi sedaci soupravu (L nebo U).
     After the pieces the plan drew and the ones the reader suggested, each
     living room is looked at once more:
       no sofa at all -- a three-seater on the wall the placer picks, a
         two-seater when that does not go;
       a sofa the reader only suggested, in a room of 13 m2 or more, becomes
         an L (a U from 20 m2) on the same wall, its back where the sofa's
         was, slid along the wall up to 0.75 m and turned either way, when it
         stands clear and leaves room for the coffee table in front of it;
       no coffee table -- one in front of the sofa, 0.45 m off the seat
         (between the arms of a U, in front of the long seat of an L);
       no armchair, in a room of 12 m2 or more with a coffee table -- one
         facing the table: beside it, on the side away from an L's chaise, or
         across it from the sofa (none with a U, which seats all round).
     Every piece added is clear of the doors, of the windows it is taller
     than and of the other pieces, stands inside the room and is marked
     suggested (SUG), so it comes and goes with the reader's suggestions. A
     sofa drawn in the plan is kept as drawn; a suggested coffee table or
     armchair in the way of the L is taken up and put down again in front of
     it, or back where it was. */
  var IMP_LIV = { coffee: 8, chair: 12, L: 13, U: 20, gap: 0.45, BK: 1.0, ARM: 0.85, CH: 1.05 };
  /* ---------- THE LIVING ROOM'S SEATS (v146) -----------------------------
     Marek (plan 1000073629 -- two sofas, an armchair and the dining table
     by the window): kreslo v tesne blizkosti u sedaci soupravy; max sezeni
     pro 5 lidi v obyvaci casti u velkeho ADU -- L sedacka a kreslo, nebo 2
     rovne naproti sobe; nakreslene nechat, jen doplnit. The seats are
     counted -- a two-, three- or four-seater by its name, an L four, a U
     six, an armchair one -- and a living room is set for five in a house of
     60 m2 or more whose living room has 14 m2 or more, for three anywhere
     else. What the plan draws stays, even past that; what is added keeps
     within it. The pieces make one of three sets:
       one sofa, an L or straight -- the coffee table in front of it and an
         armchair facing it across the table (beside the table, when the
         sofa is straight and across does not go); no armchair with a U;
       two straight sofas facing each other -- the coffee table between
         them, no armchair;
       two straight sofas meeting at a corner -- the coffee table in that
         corner, in front of both, no armchair.
     An armchair keeps 0.60 m from every sofa -- none tucked against a
     sofa's end -- and a piece added keeps a 0.90 m way to the pieces in the
     room that are not of the set (the dining table, the island, the
     kitchen), besides the doors, the windows and the walls as before; the
     coffee table, low, slides up to 0.30 m along the sofa to keep it, and
     failing that keeps 0.76 m (30 in), the least way between furniture. A
     suggested armchair closer to a sofa is taken up and set again by these
     rules. A suggested sofa becomes an L only where five may sit; a U is no
     longer made. */
  var IMP_SEAT = { sofa2: 2, sofa3: 3, sofa4: 4, sofaL: 4, sofaU: 6, armchair: 1 };
  /* v173: the dining tables by their seats, smallest first */
  var IMP_DSEAT = { table2: 2, table2r: 2, table: 4, table6: 6, table8: 8 }, IMP_DORDER = ['table2', 'table', 'table6', 'table8'];
  var IMP_LIV6 = { house: 60, room: 14, big: 5, small: 3, chair: 0.6, walk: 0.9, walkMin: 0.76 };
  /* the front of a piece and its own +x, for each quarter turn */
  var IMP_NRM = [[0, 1], [-1, 0], [0, -1], [1, 0]], IMP_TAN = [[1, 0], [0, 1], [-1, 0], [0, -1]];
  /* v167 (Marek: linka by se mohla uzpusobit pudorysu nebo mistnosti, aby to
     nekoncilo na malych zbytecnych chybach; stale realne parametry -- sirka
     pracovni plochy). An L or a U kitchen takes the arms the plan draws, in
     5 cm steps, between these limits and never longer than its room; the
     counter stays 0.6 m deep. One catalogue row whatever the size. */
  var IMP_KIT_LEG = 0.6, IMP_KIT_STEP = 0.05;
  var IMP_KIT_SIZE = { kitchenL: { w0: 1.5, w1: 4.2, d0: 1.2, d1: 3.6 }, kitchenU: { w0: 1.8, w1: 4.2, d0: 1.2, d1: 3.6 } };
  function _impKitStep(v) { return Math.round(Math.floor(v / IMP_KIT_STEP + 1e-6) * IMP_KIT_STEP * 1000) / 1000; }
  /* THE L AND THE U BY THEIR SHAPE (v143): an L sofa is its back along the
     piece's top and its chaise at its own left (the mirror puts it right), a
     U its back and an arm each side. Two pieces meet only where these parts
     meet -- a coffee table in front of the L's long seat stands inside its
     box and touches nothing. Every other piece is its box. */
  function _impShapeRects(f) {
    var k = String((f && f.kind) || '');
    var kitS = (k === 'kitchenL' || k === 'kitchenU');
    if (k !== 'sofaL' && k !== 'sofaU' && !kitS) return [_fixRect(f)];
    var m = _fixWD(f), w = m.w, d = m.d, r = (((f.rot | 0) % 4) + 4) % 4, mi = f.mirror ? -1 : 1, P = [], out = [], i, q;
    if (kitS) {
      /* v167: an L or a U kitchen by its counter, 0.6 m deep -- the box
         round its arms is mostly floor (the 3D builds the same arms) */
      var lg = Math.min(IMP_KIT_LEG, d / 2, w / 2);
      P.push([-w / 2, w / 2, -d / 2, -d / 2 + lg]);
      P.push([-w / 2, -w / 2 + lg, -d / 2 + lg, d / 2]);
      if (k === 'kitchenU') P.push([w / 2 - lg, w / 2, -d / 2 + lg, d / 2]);
    } else {
    var bk = Math.min(IMP_LIV.BK, d * 0.6);
    P.push([-w / 2, w / 2, -d / 2, -d / 2 + bk]);
    if (k === 'sofaL') P.push([-w / 2, -w / 2 + Math.min(IMP_LIV.CH, w * 0.45), -d / 2 + bk, d / 2]);
    else {
      var am = Math.min(IMP_LIV.ARM, w * 0.3);
      P.push([-w / 2, -w / 2 + am, -d / 2 + bk, d / 2]);
      P.push([w / 2 - am, w / 2, -d / 2 + bk, d / 2]);
    }
    }
    for (i = 0; i < P.length; i++) {
      var x0 = 1e9, x1 = -1e9, z0 = 1e9, z1 = -1e9;
      for (q = 0; q < 4; q++) {
        var u = ((q & 1) ? P[i][1] : P[i][0]) * mi, v = (q & 2) ? P[i][3] : P[i][2];
        var x = f.x + u * IMP_TAN[r][0] + v * IMP_NRM[r][0], z = f.z + u * IMP_TAN[r][1] + v * IMP_NRM[r][1];
        x0 = Math.min(x0, x); x1 = Math.max(x1, x); z0 = Math.min(z0, z); z1 = Math.max(z1, z);
      }
      out.push({ x0: x0, x1: x1, z0: z0, z1: z1 });
    }
    return out;
  }
  /* v167: an L or a U kitchen standing on another piece gives up the end of
     the arm that runs into it -- its corner stays where it is, the arm ends
     before the piece, in 5 cm steps, no shorter than IMP_KIT_SIZE allows.
     A piece at the corner itself cannot be cleared this way (null). */
  function _impKitShrink(fk) {
    var KS = IMP_KIT_SIZE[fk.kind];
    if (!KS) return null;
    var m = _fixWD(fk), w = m.w, d = m.d, r = (((fk.rot | 0) % 4) + 4) % 4, mi = fk.mirror ? -1 : 1, lg = Math.min(IMP_KIT_LEG, d / 2, w / 2);
    var A = _impShapeRects(fk), L = _fixturesRef(), cutW = 0, cutD = 0, who = [], i, a, q;
    function loc(x, z) {
      var dx = x - fk.x, dz = z - fk.z;
      return [(dx * IMP_TAN[r][0] + dz * IMP_TAN[r][1]) * mi, dx * IMP_NRM[r][0] + dz * IMP_NRM[r][1]];
    }
    for (i = 0; i < L.length; i++) {
      var o = L[i];
      if (o === fk || o.id === fk.id || _fitStOf(o) !== _fitStOf(fk)) continue;
      var B = _impShapeRects(o), hit = [false, false, false];
      for (a = 0; a < A.length; a++) for (q = 0; q < B.length; q++) if (_fitHit(A[a], B[q], 0.01)) hit[a] = true;
      if (!hit[0] && !hit[1] && !hit[2]) continue;
      var ro = _fixRect(o), u0 = 1e9, v0 = 1e9, c;
      [[ro.x0, ro.z0], [ro.x1, ro.z0], [ro.x0, ro.z1], [ro.x1, ro.z1]].forEach(function (pt) { c = loc(pt[0], pt[1]); u0 = Math.min(u0, c[0]); v0 = Math.min(v0, c[1]); });
      /* the back arm: its free end is at +w/2 on an L; a U has none */
      if (hit[0]) {
        if (fk.kind !== 'kitchenL' || u0 <= -w / 2 + lg + 0.05) return null;
        cutW = Math.max(cutW, w / 2 - u0);
      }
      /* the side arm(s): their free ends at +d/2 */
      if (hit[1] || hit[2]) {
        if (v0 <= -d / 2 + lg + 0.05) return null;
        cutD = Math.max(cutD, d / 2 - v0);
      }
      who.push(o.kind);
    }
    if (!who.length) return null;
    var nw = cutW > 0 ? _impKitStep(w - cutW - 0.005) : w, nd = cutD > 0 ? _impKitStep(d - cutD - 0.005) : d;
    if (nw < KS.w0 - 1e-6 || nd < KS.d0 - 1e-6) return null;
    var du = -(w - nw) / 2, dv = -(d - nd) / 2;
    fk.x = _impR(fk.x + du * mi * IMP_TAN[r][0] + dv * IMP_NRM[r][0]);
    fk.z = _impR(fk.z + du * mi * IMP_TAN[r][1] + dv * IMP_NRM[r][1]);
    fk.sizeW = nw; fk.sizeD = nd;
    return fk.kind + ' ' + _impN(w) + ' x ' + _impN(d) + ' -> ' + _impN(nw) + ' x ' + _impN(nd) + ' m (the arm ends before the ' + who.join(', ') + ')';
  }
  /* the boxes met already: do the parts? */
  function _impShapeHit(a, b) {
    var A = _impShapeRects(a), B = _impShapeRects(b), i, j;
    if (A.length === 1 && B.length === 1) return true;
    for (i = 0; i < A.length; i++) for (j = 0; j < B.length; j++) if (_fitHit(A[i], B[j], 0.01)) return true;
    return false;
  }
  function _impLivingSet() {
    var H = _PH;
    if (!H || !H.fixturesRef) return '';
    var L = H.fixturesRef(), RM = [], Z = [], NA = [], said = [], seq = 0, st0 = _fixStorey(), i, k;
    if (!L) return '';
    try { RM = _fitRooms() || []; } catch (eR) { RM = []; }
    try { Z = _fitZones() || []; } catch (eZ) { Z = []; }
    try { var RT = _roomsTracked(); NA = _impNetAreas(RT, _impWedges(RT) || []); } catch (eN) { NA = []; }
    var stW = (st0 == null) ? null : (st0 | 0);
    function nid() { return 'f' + Date.now().toString(36) + 'lv' + (seq++) + Math.floor(Math.random() * 1000).toString(36); }
    /* a point in the sofa's own frame (u along its seat, v out of its front), in the plan */
    function at(S, u, v) {
      var r = (((S.rot | 0) % 4) + 4) % 4, mi = S.mirror ? -1 : 1;
      return [S.x + u * mi * IMP_TAN[r][0] + v * IMP_NRM[r][0], S.z + u * mi * IMP_TAN[r][1] + v * IMP_NRM[r][1]];
    }
    /* and back */
    function local(S, x, z) {
      var r = (((S.rot | 0) % 4) + 4) % 4, mi = S.mirror ? -1 : 1, dx = x - S.x, dz = z - S.z;
      return [(dx * IMP_TAN[r][0] + dz * IMP_TAN[r][1]) * mi, dx * IMP_NRM[r][0] + dz * IMP_NRM[r][1]];
    }
    /* the quarter turn whose front looks from p towards t */
    function facing(px, pz, tx, tz) {
      var best = 0, bd = -1e9, q;
      for (q = 0; q < 4; q++) { var dd = IMP_NRM[q][0] * (tx - px) + IMP_NRM[q][1] * (tz - pz); if (dd > bd) { bd = dd; best = q; } }
      return best;
    }
    function inRoom(f, rm) {
      var rs = _impShapeRects(f), q, c;
      for (q = 0; q < rs.length; q++) {
        var b = rs[q], pt = [[b.x0 + 0.02, b.z0 + 0.02], [b.x1 - 0.02, b.z0 + 0.02], [b.x0 + 0.02, b.z1 - 0.02], [b.x1 - 0.02, b.z1 - 0.02]];
        for (c = 0; c < 4; c++) if (!_roomHolds(rm.r, pt[c][0], pt[c][1])) return false;
      }
      return true;
    }
    /* '' when the piece stands clear, else what is in its way */
    function clear(f, rm, pend, skip) {
      if (!inRoom(f, rm)) return 'outside the room';
      var c = null, F = null;
      try { F = _impFaces(f.x, f.z); } catch (eF) { F = null; }
      try { c = _fitClash(f, F, Z, pend || null, skip || f.id); } catch (eC) { c = { reason: 'error', detail: String((eC && eC.message) || eC) }; }
      return c ? (c.reason + (c.detail ? (' ' + c.detail) : '')) : '';
    }
    function piece(kind, x, z, rot, S) {
      var g = { id: nid(), kind: kind, x: _impR(x), z: _impR(z), rot: rot, sug: true };
      if (S && S.st != null) g.st = S.st; else if (st0 != null) g.st = st0;
      return g;
    }
    /* the coffee table in front of a sofa, and where it is in the sofa's frame */
    function coffeeFor(S) {
      var m = _fixWD(S), k9 = String(S.kind), cd = _fixDef('coffee'), cdd = cd ? cd.d : 0.6, u = 0, v;
      var bk = Math.min(IMP_LIV.BK, m.d * 0.6);
      if (k9 === 'sofaL') { u = Math.min(IMP_LIV.CH, m.w * 0.45) / 2; v = -m.d / 2 + bk + IMP_LIV.gap + cdd / 2; }
      else if (k9 === 'sofaU') v = -m.d / 2 + bk + IMP_LIV.gap + cdd / 2;
      else v = m.d / 2 + IMP_LIV.gap + cdd / 2;
      var p = at(S, u, v);
      return { f: piece('coffee', p[0], p[1], (S.rot | 0) % 2, S), u: u, v: v };
    }
    /* the armchair's places round a table at u, v of the sofa's frame (v146:
       across the table first; beside it only for a straight sofa, and
       stepped out from the sofa's front till it keeps 0.60 m from it) */
    function chairsFor(S, u, v) {
      var cd = _fixDef('coffee'), ad = _fixDef('armchair'), out = [], C = [], q, j;
      if (!cd || !ad || String(S.kind) === 'sofaU') return out;
      var side = IMP_LIV.gap + cd.w / 2 + ad.d / 2, across = IMP_LIV.gap + cd.d / 2 + ad.d / 2;
      C.push([u, v + across, 'across the coffee table from the ' + S.kind]);
      if (String(S.kind) !== 'sofaL') {
        for (j = 0; j <= 3; j++) { C.push([u + side, v + j * 0.15, 'beside the coffee table']); C.push([u - side, v + j * 0.15, 'beside the coffee table']); }
      }
      var tc = at(S, u, v);
      for (q = 0; q < C.length; q++) {
        var p = at(S, C[q][0], C[q][1]);
        out.push({ f: piece('armchair', p[0], p[1], facing(p[0], p[1], tc[0], tc[1]), S), how: C[q][2] });
      }
      return out;
    }
    /* the suggested sofa as an L or a U on its own wall */
    function bigger(S, rm, area) {
      var kinds = [], r = (((S.rot | 0) % 4) + 4) % 4, m0 = _fixWD(S), sh = [0, 0.25, -0.25, 0.5, -0.5, 0.75, -0.75], a, b, c;
      if (area >= IMP_LIV.L) kinds.push('sofaL');
      for (a = 0; a < kinds.length; a++) {
        if (!_fixDef(kinds[a])) continue;
        var mirs = (kinds[a] === 'sofaL') ? [false, true] : [false];
        for (b = 0; b < sh.length; b++) for (c = 0; c < mirs.length; c++) {
          var g = piece(kinds[a], 0, 0, r, S);
          if (mirs[c]) g.mirror = true;
          var m1 = _fixWD(g), back = (m1.d - m0.d) / 2;
          g.x = _impR(S.x + IMP_NRM[r][0] * back + IMP_TAN[r][0] * sh[b]);
          g.z = _impR(S.z + IMP_NRM[r][1] * back + IMP_TAN[r][1] * sh[b]);
          if (clear(g, rm, null, S.id)) continue;
          if (clear(coffeeFor(g).f, rm, [g], S.id)) continue;
          return { g: g, sh: sh[b] };
        }
      }
      return null;
    }
    /* v146: the house's floor, for how many may sit */
    var HA = 0;
    try { var PP = H.poly() || []; for (k = 0; k < PP.length; k++) { var p1 = PP[k], p2 = PP[(k + 1) % PP.length]; HA += p1[0] * p2[1] - p2[0] * p1[1]; } HA = Math.abs(HA) / 2; } catch (eHA) { HA = 0; }
    function onSt(o) { var so = _fitStOf(o); return so == null || stW == null || so === stW; }
    function seatsOf(arr) { var n9 = 0; for (var q = 0; q < arr.length; q++) n9 += IMP_SEAT[String(arr[q].kind)] || 0; return n9; }
    /* the least distance between two pieces, by their shapes */
    function gapOf(a, b) {
      var A = _impShapeRects(a), B = _impShapeRects(b), g = 1e9, q, j;
      for (q = 0; q < A.length; q++) for (j = 0; j < B.length; j++) {
        var dx = Math.max(0, B[j].x0 - A[q].x1, A[q].x0 - B[j].x1), dz = Math.max(0, B[j].z0 - A[q].z1, A[q].z0 - B[j].z1);
        g = Math.min(g, Math.hypot(dx, dz));
      }
      return g;
    }
    var LIVK = /^(sofa|armchair|coffee|tvStand)/;
    /* '' when a piece added keeps 0.90 m to the room's other pieces -- those
       behind a wall from it do not count -- else which is too near */
    function walk(f, min) {
      min = min || IMP_LIV6.walk;
      var F = null, q;
      try { F = _impFaces(f.x, f.z); } catch (eF) { F = null; }
      for (q = 0; q < L.length; q++) {
        var o = L[q];
        if (!o || o === f || o.id === f.id || LIVK.test(String(o.kind)) || !onSt(o)) continue;
        var ro = _fixRect(o);
        if (F && (ro.x0 >= F.x1 - 0.01 || ro.x1 <= F.x0 + 0.01 || ro.z0 >= F.z1 - 0.01 || ro.z1 <= F.z0 + 0.01)) continue;
        var g = gapOf(f, o);
        if (g < min - 0.005) return 'a way of ' + _impN(g) + ' m to the ' + o.kind + ', ' + _impN(min) + ' kept';
      }
      return '';
    }
    /* '' when an armchair keeps 0.60 m from every sofa, else which */
    function offSofas(f) {
      for (var q = 0; q < L.length; q++) {
        var o = L[q];
        if (!o || o === f || !/^sofa/.test(String(o.kind)) || !onSt(o)) continue;
        var g = gapOf(f, o);
        if (g < IMP_LIV6.chair - 0.005) return _impN(g) + ' m from the ' + o.kind + ', 0.60 kept';
      }
      return '';
    }
    /* two straight sofas: facing each other, meeting at a corner, or neither */
    function rel(A, B) {
      if (!/^sofa[234]$/.test(String(A.kind)) || !/^sofa[234]$/.test(String(B.kind))) return null;
      var rA = (((A.rot | 0) % 4) + 4) % 4, rB = (((B.rot | 0) % 4) + 4) % 4, mA = _fixWD(A), mB = _fixWD(B);
      var lb = local(A, B.x, B.z), la = local(B, A.x, A.z);
      if ((rA + 2) % 4 === rB) {
        var D = lb[1] - mA.d / 2 - mB.d / 2, hi = Math.min(mA.w / 2, lb[0] + mB.w / 2), lo = Math.max(-mA.w / 2, lb[0] - mB.w / 2);
        if (D >= 1.2 && D <= 3.6 && hi - lo >= 0.5 * Math.min(mA.w, mB.w)) return { t: 'facing', D: D, u: (hi + lo) / 2 };
        return null;
      }
      if (rA % 2 !== rB % 2 && lb[1] > mA.d / 2 && la[1] > mB.d / 2 && gapOf(A, B) <= 1.8) return { t: 'corner' };
      return null;
    }
    /* the coffee table between two sofas facing each other */
    function coffeeBetween(A, R) {
      var m = _fixWD(A), v = m.d / 2 + R.D / 2, p = at(A, R.u, v);
      return { f: piece('coffee', p[0], p[1], (A.rot | 0) % 2, A), u: R.u, v: v };
    }
    /* the coffee table in the corner of two sofas, in front of both: its
       end 0.45 m off the second sofa's seat, still 0.30 m in front of the
       first's, and reaching 0.30 m along the second's when that is no more
       than 0.30 m further from the first */
    function coffeeCorner(A, B) {
      var mA = _fixWD(A), mB = _fixWD(B), cd = _fixDef('coffee'), cw = cd ? cd.w : 0.9, cdd = cd ? cd.d : 0.6, rB = (((B.rot | 0) % 4) + 4) % 4;
      var lb = local(A, B.x, B.z), sg = (lb[0] >= 0) ? 1 : -1;
      var fr = local(A, B.x + IMP_NRM[rB][0] * mB.d / 2, B.z + IMP_NRM[rB][1] * mB.d / 2);
      var uM = mA.w / 2 + cw / 2 - 0.3, u = Math.max(-uM, Math.min(uM, fr[0] - sg * (IMP_LIV.gap + cw / 2)));
      var v0 = mA.d / 2 + IMP_LIV.gap + cdd / 2, v = Math.max(v0, Math.min(v0 + 0.3, lb[1] - mB.w / 2 + 0.3 - cdd / 2));
      var p = at(A, u, v);
      return { f: piece('coffee', p[0], p[1], (A.rot | 0) % 2, A), u: u, v: v };
    }
    /* the coffee table where it stands clear and keeps the way: where it was
       put, then slid along the sofa, then with the least way; '' or why not,
       and how it went in cf.how */
    function placeCoffee(cf, S, rm) {
      var w0 = clear(cf.f, rm);
      if (w0) return w0;
      var w1 = walk(cf.f);
      if (!w1) return '';
      var SH = [0.15, -0.15, 0.3, -0.3], q;
      for (q = 0; q < SH.length; q++) {
        var p = at(S, cf.u + SH[q], cf.v), g = piece('coffee', p[0], p[1], cf.f.rot, S);
        if (!clear(g, rm) && !walk(g)) { cf.f = g; cf.u += SH[q]; cf.how = 'slid ' + _impN(Math.abs(SH[q])) + ' m along the ' + S.kind + ' (' + w1 + ')'; return ''; }
      }
      var w2 = walk(cf.f, IMP_LIV6.walkMin);
      if (!w2) { cf.how = w1.replace(/, [0-9.]+ kept$/, '') + ', ' + _impN(IMP_LIV6.walkMin) + ' at least'; return ''; }
      return w2;
    }
    /* v171: a dining table keeps 0.76 m of way to the other pieces and to
       the sofa set too */
    function dineWay(g) {
      var w = walk(g, IMP_LIV6.walkMin), q;
      if (w) return w;
      for (q = 0; q < L.length; q++) {
        var o = L[q];
        if (!o || o.id === g.id || !LIVK.test(String(o.kind)) || !onSt(o)) continue;
        var gg = gapOf(g, o);
        if (gg < IMP_LIV6.walkMin - 0.005) return 'a way of ' + _impN(gg) + ' m to the ' + o.kind;
      }
      return '';
    }
    function placeDining(t, rm, bigR) {
      var kinds = (bigR && DMAX >= 4 && _fixDef('table') && /^table2r?$/.test(String(t.kind))) ? ['table', String(t.kind)] : [String(t.kind)];
      var RTd = [t.rot | 0, ((t.rot | 0) + 1) % 4], SHd = [0, 0.15, -0.15, 0.3, -0.3], n, a, b, c, g, why = '';
      placeDining.last = '';
      for (n = 0; n < kinds.length; n++) {
        if (!_fixDef(kinds[n])) continue;
        for (a = 0; a < RTd.length; a++) for (b = 0; b < SHd.length; b++) for (c = 0; c < SHd.length; c++) {
          g = piece(kinds[n], t.x + SHd[b], t.z + SHd[c], RTd[a], t); g.id = t.id; g.sug = !!t.sug;
          why = clear(g, rm, null, t.id) || dineWay(g);
          if (!why) return { g: g, how: (b || c) ? 'slid by its place' + (a ? ', turned a quarter' : '') : (a ? 'turned a quarter' : 'where suggested') };
          if (!placeDining.last) placeDining.last = why;
        }
        var BXd = (rm.F && rm.F.boxes && rm.F.boxes.length) ? rm.F.boxes : [rm.F], bq, pq;
        for (bq = 0; bq < BXd.length; bq++) {
          var PSd = [];
          try { PSd = _fitFreePoses(kinds[n], BXd[bq], (t.st != null) ? t.st : st0) || []; } catch (eFp) { PSd = []; }
          for (pq = 0; pq < PSd.length && pq < 400; pq++) {
            g = piece(kinds[n], PSd[pq].x, PSd[pq].z, PSd[pq].rot, t); g.id = t.id; g.sug = !!t.sug;
            if (!clear(g, rm, null, t.id) && !dineWay(g)) return { g: g, how: 'moved to the free floor of the room' };
          }
        }
      }
      return null;
    }
    /* THE DINING TABLE FOR THE HOUSE, THE SOFA FOR THE TABLE (v173, Marek:
       mame tam dinning pro 8, ale v obyvaci casti jen dvojsedacku a kreslo,
       melo by to korespondovat -- dining pro 6 a sedaci souprava L a kreslo).
       A dining table seats two a bedroom and two more, eight at most (a
       studio two, one bedroom four, two bedrooms six): a bigger one drawn or
       counted is made that size where it stands. The living room then aims
       at one seat fewer than the table -- the sofa a drawn straight one too,
       made an L where the room is big and it fits. */
    var nBedH = 0, DSEAT = 0, DSAID = [];
    for (k = 0; k < RM.length; k++) if (/bedroom|container_room_7|guestroom/.test(String(RM[k].key || ''))) nBedH++;
    var DMAX = Math.min(8, 2 * nBedH + 2);
    for (k = 0; k < L.length; k++) {
      var dT = L[k];
      if (!dT || !onSt(dT) || IMP_DSEAT[String(dT.kind)] == null) continue;
      if (IMP_DSEAT[dT.kind] > DMAX) {
        var dk = '', qd;
        for (qd = IMP_DORDER.length - 1; qd >= 0; qd--) if (IMP_DSEAT[IMP_DORDER[qd]] <= DMAX && _fixDef(IMP_DORDER[qd])) { dk = IMP_DORDER[qd]; break; }
        if (dk) { DSAID.push('the dining ' + dT.kind + ' -> ' + dk + ' (' + nBedH + ' bedroom(s): ' + DMAX + ' seats at most)'); dT.kind = dk; delete dT.sizeW; delete dT.sizeD; }
      }
      DSEAT = Math.max(DSEAT, IMP_DSEAT[String(dT.kind)] || 0);
    }
    if (DSAID.length) said.push(DSAID.join(', '));
    var LTGT = DSEAT > 0 ? DSEAT - 1 : 0;
    for (i = 0; i < RM.length; i++) {
      var rm = RM[i];
      if (!/livingroom/.test(String(rm.key || ''))) continue;
      var area = (NA[rm.i] != null) ? NA[rm.i] : rm.area, did = [], big = (HA >= IMP_LIV6.house && area >= IMP_LIV6.room), cap = big ? IMP_LIV6.big : IMP_LIV6.small;
      var mine = function (re) {
        return L.filter(function (o) { return o && re.test(String(o.kind)) && onSt(o) && _roomHolds(rm.r, o.x, o.z); });
      };
      var sofas = mine(/^sofa/);
      if (!sofas.length) {
        var got = null, KS = ['sofa3', 'sofa2'], q;
        for (q = 0; q < KS.length && !got; q++) {
          if (IMP_SEAT[KS[q]] + seatsOf(mine(/^armchair$/)) > cap && q < KS.length - 1) continue;
          var res = null;
          try { res = _fitPlace({ kind: KS[q], room: { x: rm.r.cx, z: rm.r.cz }, wall: 'auto', align: 'auto' }); } catch (eP) { res = null; }
          if (res && res.ok && res.f && res.room && res.room.i === rm.i) got = piece(KS[q], res.f.x, res.f.z, res.f.rot, res.f);
        }
        if (!got) { said.push('living room ' + _impN(area) + ' m2: no sofa, and none fits'); continue; }
        L.push(got);
        sofas = [got];
        did.push('a ' + got.kind + ' (the room had no sofa)');
      }
      sofas.sort(function (p1, p2) { var A1 = _fixWD(p1), A2 = _fixWD(p2); return A2.w * A2.d - A1.w * A1.d; });
      /* a suggested armchair tucked against a sofa is set again */
      var reset = 0;
      for (k = L.length - 1; k >= 0; k--) {
        var oA = L[k];
        if (oA && oA.sug && oA.kind === 'armchair' && onSt(oA) && _roomHolds(rm.r, oA.x, oA.z) && offSofas(oA)) { L.splice(k, 1); reset++; }
      }
      if (reset) did.push(reset + ' suggested armchair(s) closer than 0.60 m to a sofa taken up');
      var S0 = sofas[0], took = [];
      /* THE SOFA SET BEFORE THE DINING TABLE (v171, plan 1000075648 read
         again: the suggested table of two was put on the free wall in front
         of the sofa, so neither the corner sofa nor the coffee table nor the
         armchair could stand). A dining table the reader only suggested is
         taken up here and put down last, where it stands clear of the set. */
      var DIN = [];
      for (k = L.length - 1; k >= 0; k--) {
        var oD = L[k];
        if (oD && oD.sug && /^table(2r?|6|8)?$/.test(String(oD.kind)) && onSt(oD) && _roomHolds(rm.r, oD.x, oD.z)) DIN.push(L.splice(k, 1)[0]);
      }
      var seat0 = seatsOf(mine(/^(sofa|armchair)/));
      /* v173: a drawn straight sofa too, where the table wants more seats */
      /* v174 (Marek: v puvodnim planu je dobre udelane umisteni sedaci
         soupravy s kresly a televiznim stolkem -- to se mohlo prenest, sofa
         pro 2/3 podle velikosti a dve kresla): a DRAWN sofa stays as drawn;
         the seats the table wants come from armchairs beside it (below) */
      var shortS = false;
      if ((S0.sug || shortS) && /^sofa[234]$/.test(String(S0.kind)) && area >= IMP_LIV.L && seat0 - (IMP_SEAT[S0.kind] || 0) + IMP_SEAT.sofaL <= cap) {
        for (k = L.length - 1; k >= 0; k--) {
          var o9 = L[k];
          if (o9 && (o9.sug || shortS) && (o9.kind === 'coffee' || o9.kind === 'armchair') && _roomHolds(rm.r, o9.x, o9.z)) took.push(L.splice(k, 1)[0]);
        }
        var BG = bigger(S0, rm, area);
        if (BG) {
          L.splice(L.indexOf(S0), 1, BG.g);
          did.push(S0.kind + ' -> ' + BG.g.kind + (BG.g.mirror ? ' (chaise on its right)' : '') + (BG.sh ? (' slid ' + _impN(Math.abs(BG.sh)) + ' m along its wall') : ''));
          S0 = BG.g;
        }
      }
      /* which set the sofas make */
      var REL = null, S1 = null;
      for (k = 1; k < sofas.length && !REL; k++) { REL = rel(S0, sofas[k]); if (REL) S1 = sofas[k]; }
      var setN = REL ? ((REL.t === 'facing') ? 'two sofas facing each other' : 'two sofas meeting at a corner') : ('one ' + S0.kind);
      var CF = mine(/^coffee$/), uv = null;
      if (!CF.length && area >= IMP_LIV.coffee) {
        var cf = REL ? ((REL.t === 'facing') ? coffeeBetween(S0, REL) : coffeeCorner(S0, S1)) : coffeeFor(S0), why = placeCoffee(cf, S0, rm);
        var whereC = REL ? ((REL.t === 'facing') ? 'between them' : 'in their corner, in front of both') : ('in front of the ' + S0.kind);
        if (why && REL) {
          var cf2 = coffeeFor(S0), why2 = placeCoffee(cf2, S0, rm);
          if (!why2) { whereC = 'in front of the ' + S0.kind + ' (' + whereC + ': ' + why + ')'; cf = cf2; why = ''; }
        }
        if (!why) { L.push(cf.f); CF = [cf.f]; uv = [cf.u, cf.v]; did.push('a coffee table ' + whereC + (cf.how ? (', ' + cf.how) : '')); }
        else said.push('living room: no coffee table ' + whereC + ' -- ' + why);
      }
      /* what was taken up and not put down again goes back, if it still can */
      for (k = 0; k < took.length; k++) {
        var tk = took[k];
        if (tk.kind === 'coffee' && CF.length) continue;
        if (!clear(tk, rm) && (tk.kind !== 'armchair' || !offSofas(tk))) { L.push(tk); if (tk.kind === 'coffee') CF = [tk]; }
        else said.push('living room: the suggested ' + tk.kind + ' left out -- in the way of the ' + S0.kind);
      }
      /* the armchair: one sofa only, with a coffee table, while the seats allow */
      var seats = seatsOf(mine(/^(sofa|armchair)/)), nArm0 = mine(/^armchair$/).length;
      /* v174: armchairs the plan draws face the coffee table (the sofa when
         there is none) */
      var tgtA = CF.length ? CF[0] : S0, turned = 0;
      mine(/^armchair$/).forEach(function (a9) { if (a9.sug) return; var r9 = facing(a9.x, a9.z, tgtA.x, tgtA.z); if (r9 !== (a9.rot | 0)) { a9.rot = r9; turned++; } });
      if (turned) did.push(turned + ' drawn armchair(s) turned to the ' + (CF.length ? 'coffee table' : S0.kind));
      /* v174: with a straight sofa two armchairs beside the coffee table, one
         each side, as plans draw it, while the dining table wants the seats;
         with an L one across */
      var wantA = /^sofa[234]$/.test(String(S0.kind)) ? Math.max(1, Math.min(2, LTGT - seats)) : 1;
      wantA = Math.max(0, wantA - nArm0);
      if (!REL && String(S0.kind) !== 'sofaU' && CF.length && wantA > 0 && area >= IMP_LIV.chair) {
        if (seats + IMP_SEAT.armchair > cap) did.push('no armchair (' + seats + ' seats already)');
        else {
          if (!uv) uv = local(S0, CF[0].x, CF[0].z);
          var CH = chairsFor(S0, uv[0], uv[1]), putA = [], last = '', pp;
          if (wantA >= 2 && seats + 2 * IMP_SEAT.armchair <= cap) {
            for (pp = 1; pp + 1 < CH.length && !putA.length; pp += 2) {
              var A9 = CH[pp], B9 = CH[pp + 1], wa = clear(A9.f, rm) || offSofas(A9.f) || walk(A9.f);
              if (wa) { last = wa; continue; }
              L.push(A9.f);
              var wb = clear(B9.f, rm) || offSofas(B9.f) || walk(B9.f);
              if (wb) { L.splice(L.indexOf(A9.f), 1); last = wb; continue; }
              L.push(B9.f); putA.push(A9, B9);
            }
          }
          if (!putA.length) {
            for (k = 0; k < CH.length && !putA.length; k++) { var w9 = clear(CH[k].f, rm) || offSofas(CH[k].f) || walk(CH[k].f); if (!w9) { L.push(CH[k].f); putA.push(CH[k]); } else last = w9; }
          }
          if (putA.length) { seats += putA.length * IMP_SEAT.armchair; did.push(putA.length === 2 ? 'two armchairs beside the coffee table, one each side' : ('an armchair ' + putA[0].how)); }
          else if (CH.length) said.push('living room: no armchair -- ' + (last || 'no place'));
        }
      } else if (REL && !mine(/^armchair$/).length) did.push('no armchair with ' + setN);
      /* v174: the TV unit on the wall the sofa looks at, across from the
         coffee table, 2 to 6 m off -- where the plan draws none */
      if (_fixDef('tvStand') && !mine(/^tvStand$/).length && !REL && String(S0.kind) !== 'sofaU' && area >= IMP_LIV.coffee) {
        var r0 = (((S0.rot | 0) % 4) + 4) % 4, nr = IMP_NRM[r0], Ftv = null, tvd = _fixDef('tvStand').d;
        try { Ftv = _impFaces(S0.x, S0.z); } catch (eFt) { Ftv = null; }
        if (Ftv) {
          var onZ = Math.abs(nr[1]) > 0.5, face = onZ ? (nr[1] > 0 ? Ftv.z1 : Ftv.z0) : (nr[0] > 0 ? Ftv.x1 : Ftv.x0);
          var aim = CF.length ? CF[0] : S0, al0 = onZ ? aim.x : aim.z, dst = Math.abs(face - (onZ ? S0.z : S0.x));
          if (isFinite(face) && dst >= 2.0 && dst <= 6.0) {
            var SHt = [0, 0.15, -0.15, 0.3, -0.3, 0.45, -0.45, 0.6, -0.6], tq, tvP = null, lastT = '';
            for (tq = 0; tq < SHt.length && !tvP; tq++) {
              var fxT = onZ ? face - (nr[1] > 0 ? 1 : -1) * tvd / 2 : face - (nr[0] > 0 ? 1 : -1) * tvd / 2, alT = al0 + SHt[tq];
              var gT = piece('tvStand', onZ ? alT : fxT, onZ ? fxT : alT, (r0 + 2) % 4, S0);
              var wT = clear(gT, rm) || walk(gT, IMP_LIV6.walkMin);
              if (!wT) tvP = gT; else lastT = wT;
            }
            if (tvP) { L.push(tvP); did.push('a TV unit on the wall across from the ' + S0.kind + ', ' + _impN(dst) + ' m off'); }
            else said.push('living room: no TV unit across from the ' + S0.kind + ' -- ' + (lastT || 'no place'));
          }
        }
      }
      /* DINING FOR FOUR IN A BIG ROOM (v169, Marek: vzhledem k velikosti
         living space by se vesel dining table pro 4). In a living room of a
         big house (IMP_LIV6.house, .room) the table of two becomes the table
         of four where it stands clear, its chairs in the room and 0.76 m of
         way kept -- turned with it or a quarter, slid up to 0.30 m. */
      /* v171: the suggested dining table(s) back, last -- where they were
         (slid up to 0.30 m, or turned a quarter), else on the free floor of
         the room nearest its middle; in a big room the table of two comes
         back as the table of four where that stands clear */
      for (k = 0; k < DIN.length; k++) {
        var pdn = placeDining(DIN[k], rm, big);
        if (pdn) {
          L.push(pdn.g);
          did.push('the suggested ' + (pdn.g.kind !== DIN[k].kind ? (DIN[k].kind + ' as the ' + pdn.g.kind + ' (of four)') : pdn.g.kind) + ' after the sofa set, ' + pdn.how);
        } else said.push('living room: the suggested ' + DIN[k].kind + ' left out -- no place clear of the sofa set (' + (placeDining.last || 'no place') + ')');
      }
      if (big && DMAX >= 4 && _fixDef('table') && !mine(/^table(6|8)?$/).length) {
        var T2s = mine(/^table2r?$/).filter(function (o) { return !DIN.some(function (d9) { return d9.id === o.id; }); });
        if (T2s.length) {
          var t2 = T2s[0], up4 = null, RT4 = [t2.rot | 0, ((t2.rot | 0) + 1) % 4], SH4 = [0, 0.15, -0.15, 0.3, -0.3], a4, b4, c4;
          for (a4 = 0; a4 < RT4.length && !up4; a4++) for (b4 = 0; b4 < SH4.length && !up4; b4++) for (c4 = 0; c4 < SH4.length && !up4; c4++) {
            var g4 = piece('table', t2.x + SH4[b4], t2.z + SH4[c4], RT4[a4], t2);
            g4.id = t2.id; g4.sug = !!t2.sug;
            if (!clear(g4, rm, null, t2.id) && !walk(g4, IMP_LIV6.walkMin)) up4 = g4;
          }
          if (up4) {
            L.splice(L.indexOf(t2), 1, up4);
            did.push('the dining table of two -> of four' + ((up4.x !== _impR(t2.x) || up4.z !== _impR(t2.z)) ? (' slid ' + _impN(Math.hypot(up4.x - t2.x, up4.z - t2.z)) + ' m') : '') + ((up4.rot | 0) !== (t2.rot | 0) ? ', turned a quarter' : ''));
          } else said.push('living room: the dining table of two stays -- the one of four does not stand clear by it');
        }
      }
      said.unshift('living room ' + _impN(area) + ' m2, ' + setN + ', ' + seatsOf(mine(/^(sofa|armchair)/)) + ' seat(s) -- ' + cap + ' at most (house ' + _impN(HA) + ' m2)' + (did.length ? (': ' + did.join(', ')) : ''));
    }
    return said.length ? ('LIVING (v146): ' + said.join(' | ')) : '';
  }
  function _impPlaceFixtures(f) {
    var H = _PH;
    if (!f || !f.fixtures || !f.fixtures.length || !H || !H.fixturesRef) return '';
    var L = H.fixturesRef();
    if (!L) return '';
    var put = [], none = {}, over = 0, SUGQ = [], DRAWNP = [], i, COMPACT = [];
    _impPoseZ = null;
    /* v150: where the kitchen stands, for a counter with stools to turn its
       back to it */
    _impKitAt = [];
    for (i = 0; i < f.fixtures.length; i++) {
      var qk = f.fixtures[i];
      if (qk && !qk.sug && /^kitchen/.test(String(qk.type || ''))) _impKitAt.push([(qk.x0 + qk.x1) / 2, (qk.z0 + qk.z1) / 2]);
    }
    /* THE FRIDGE AT THE END OF THE RUN (v162, plan 1000073631, Marek:
       stale vidim stary pudorys v knihovne). The reader drew the kitchen run
       3.35 m long with the fridge standing in it at one end; the run became
       the 3.0 m counter and the fridge stood in the counter, and the library
       takes no plan with a piece on another. A straight run holding most of
       a fridge the reader drew is the counter beside it: the run is cut back
       to the fridge's face, and the counter is the longest that fits what is
       left. */
    var FXQ = f.fixtures.slice(), FRIDGEN = [];
    for (i = 0; i < FXQ.length; i++) {
      var qr = FXQ[i];
      if (!qr || qr.sug || qr.type !== 'kitchen') continue;
      var alongX = (qr.x1 - qr.x0) >= (qr.z1 - qr.z0), j9;
      for (j9 = 0; j9 < FXQ.length; j9++) {
        var fr = FXQ[j9];
        if (!fr || fr.sug || fr.type !== 'fridge') continue;
        var ovx = Math.min(fr.x1, qr.x1) - Math.max(fr.x0, qr.x0), ovz = Math.min(fr.z1, qr.z1) - Math.max(fr.z0, qr.z0), fa = (fr.x1 - fr.x0) * (fr.z1 - fr.z0);
        if (!(ovx > 0 && ovz > 0 && ovx * ovz >= 0.5 * fa)) continue;
        var q0 = alongX ? qr.x0 : qr.z0, q1 = alongX ? qr.x1 : qr.z1, f0 = alongX ? fr.x0 : fr.z0, f1 = alongX ? fr.x1 : fr.z1, len0 = q1 - q0;
        var cut = Object.assign({}, qr);
        if ((f0 + f1) / 2 < (q0 + q1) / 2) { if (alongX) cut.x0 = Math.max(q0, f1); else cut.z0 = Math.max(q0, f1); }
        else { if (alongX) cut.x1 = Math.min(q1, f0); else cut.z1 = Math.min(q1, f0); }
        var len1 = alongX ? (cut.x1 - cut.x0) : (cut.z1 - cut.z0);
        if (len1 < 0.6 || len1 >= len0 - 0.05) continue;
        cut.runCut = len1;
        FXQ[i] = qr = cut;
        FRIDGEN.push('the run ' + _impN(len0) + ' m holds the fridge -- the counter ' + _impN(len1) + ' m beside it');
      }
    }
    /* AND AT THE END OF AN L'S ARM (v165, plan 1000075648). The reader's
       box of an L kitchen is the box round both arms, and the fridge drawn
       at the end of the short arm stood in it: the catalogue L put its long
       arm where the fridge was. A fridge in a corner of the L's box, its
       back on one side of it, is cut off the arm it ends -- the arm its
       width runs along -- and the L takes what is left. */
    for (i = 0; i < FXQ.length; i++) {
      var qL = FXQ[i];
      if (!qL || qL.sug || qL.type !== 'kitchen_l') continue;
      for (j9 = 0; j9 < FXQ.length; j9++) {
        var frL = FXQ[j9];
        if (!frL || frL.sug || frL.type !== 'fridge') continue;
        var oxL = Math.min(frL.x1, qL.x1) - Math.max(frL.x0, qL.x0), ozL = Math.min(frL.z1, qL.z1) - Math.max(frL.z0, qL.z0), faL = (frL.x1 - frL.x0) * (frL.z1 - frL.z0);
        if (!(oxL > 0 && ozL > 0 && oxL * ozL >= 0.5 * faL)) continue;
        var aX0 = frL.x0 - qL.x0 < 0.15, aX1 = qL.x1 - frL.x1 < 0.15, aZ0 = frL.z0 - qL.z0 < 0.15, aZ1 = qL.z1 - frL.z1 < 0.15;
        if (!((aX0 || aX1) && (aZ0 || aZ1))) continue;
        var byX = (frL.x1 - frL.x0) >= (frL.z1 - frL.z0), cutL = Object.assign({}, qL), l0L, l1L;
        if (byX) { l0L = qL.x1 - qL.x0; if (aX1) cutL.x1 = frL.x0; else cutL.x0 = frL.x1; l1L = cutL.x1 - cutL.x0; }
        else { l0L = qL.z1 - qL.z0; if (aZ1) cutL.z1 = frL.z0; else cutL.z0 = frL.z1; l1L = cutL.z1 - cutL.z0; }
        if (l1L < 1.2 || l1L >= l0L - 0.05) continue;
        cutL.runCut = l1L;
        FXQ[i] = qL = cutL;
        FRIDGEN.push('the L ' + _impN(l0L) + ' m along its ' + (byX ? 'x' : 'z') + ' arm holds the fridge at its end -- the L ' + _impN(l1L) + ' m beside it');
      }
    }
    for (i = 0; i < FXQ.length; i++) {
      var q = FXQ[i], bw = q.x1 - q.x0, bd = q.z1 - q.z0;
      /* a piece the reader only suggested goes in after the drawn ones */
      if (q.sug) { SUGQ.push(q); continue; }
      var kind = _impFixKind(q.type, Math.max(bw, bd), Math.min(bw, bd), q.label, q), D = kind ? _fixDef(kind) : null;
      if (!D) { none[q.type] = (none[q.type] || 0) + 1; continue; }
      if (q.runCut) {
        var KCUT = ['kitchen36', 'kitchen30', 'kitchen24'], kc = KCUT.indexOf(kind);
        while (kc >= 0 && kc < KCUT.length - 1 && _fixDef(KCUT[kc]).w > q.runCut + 0.005) { kind = KCUT[++kc]; D = _fixDef(kind); }
      }
      var PO = _impPose(q, kind, D, i), fx = PO.fx;
      /* THE COMPACT ONE WHERE THE FULL SIZE DOES NOT FIT (v151). On an
         imperial page a washer, a dryer and a fridge stand at the US stock
         size (27 in, 36 in); a laundry closet drawn 0.7 m deep cannot take a
         washer 30 in deep, and the piece stood over the wall. Where the full
         size runs past its room, the compact one goes in its place -- 600 mm,
         a 24 in machine, on the same row of the catalogue (one row a type). */
      if (D.appliance && D.wi > 0 && _fixImperial()) {
        var rA = _fixRect(fx), FA = PO.F;
        if (rA.x0 < FA.x0 - 0.005 || rA.x1 > FA.x1 + 0.005 || rA.z0 < FA.z0 - 0.005 || rA.z1 > FA.z1 + 0.005) {
          PO = _impPose(q, kind, D, i, true); fx = PO.fx;
          COMPACT.push(kind + ' (the ' + _impN(D.wi) + ' m one runs ' + _impN(Math.max(FA.x0 - rA.x0, rA.x1 - FA.x1, FA.z0 - rA.z0, rA.z1 - FA.z1)) + ' m past its room)');
        }
      }
      /* v142: a counter run no longer than the wall it stands on -- a 3.31 m
         run of plan 1000073631 took the 3.60 m kitchen, over the wall */
      var KRUN = ['kitchen36', 'kitchen30', 'kitchen24'], kr = KRUN.indexOf(kind);
      while (kr >= 0 && kr < KRUN.length - 1) {
        var mK = _fixDims(fx), spK = ((fx.rot | 0) % 2 === 0) ? (PO.F.x1 - PO.F.x0) : (PO.F.z1 - PO.F.z0), lenK = ((fx.rot | 0) % 2 === 0) ? mK.w : mK.d;
        if (lenK <= spK + 0.005) break;
        kind = KRUN[++kr]; D = _fixDef(kind);
        PO = _impPose(q, kind, D, i); fx = PO.fx;
      }
      if (_fixOverlaps(fx, fx.id)) over++;
      L.push(fx);
      DRAWNP.push({ fx: fx, F: PO.F, D: D, kind: kind, q: q });
      put.push(q.type + (q.label ? (' "' + q.label + '"') : '') + (q.seats ? (' seats ' + q.seats) : '') + (q.stools ? (' stools ' + q.stools) : '') +
               ' -> ' + kind + ' at ' + _impN(fx.x) + ',' + _impN(fx.z) + ' turn ' + fx.rot + (fx.mirror ? ' mirrored' : '') + (PO.why ? (' (' + PO.why + ')') : ''));
    }
    /* OUT OF A DOOR'S SWING (v141, Marek: nabytek mimo dvere zustava
       podminkou -- and the plan library takes no plan with a piece in a
       door's way). A piece is put where the reader drew it, and a box drawn
       a hand off or a door hung the other way left the master bath's basin
       and shower, the washer and the dining table of plan 1000073500 in a
       doorway or a door's swing, and the L of the kitchen over the table. A
       drawn piece standing in a door's swing, a doorway or on another piece
       is slid along its own wall -- 5 cm at a time, up to 1.20 m either way,
       inside its room -- to the nearest place where nothing is in its way
       (a window it stands under is let be); a free-standing piece, a table,
       may go either way. A kitchen in an L or a U keeps its corner. A piece
       with no such place stays where it was drawn and is named. */
    /* A TUB OR A SHOWER A HAIR TOO LONG FOR ITS ROOM (v145, plan 1000073628:
       the 5 ft bathroom read 1.49 m clear and its 60 in tub, 1.50 m, stood a
       centimetre into the wall -- and the plan library took no plan with a
       piece over a wall). A drawn tub or shower that stands over its room's
       face is first slid back inside where the room is long enough; where it
       is up to 12 cm too long, the partition on that side moves out by what
       is missing, and the room beyond gives it. An outer wall never moves. */
    var WETN = [], Hw = _PH, PTw = (Hw && Hw.partThk) ? Hw.partThk() : 0.1, Cw = _impCore(), SPw = (Cw && Cw.STATE && Cw.STATE.parts) || [];
    function wetPart(o, at, lo, hi) {
      for (var q7 = 0; q7 < SPw.length; q7++) {
        var p7 = SPw[q7];
        if (!p7 || p7.sep || (p7.o || p7.dir) !== o || Math.abs(p7.c - at) > 0.03) continue;
        var sp7 = null;
        try { sp7 = _partSpan(p7); } catch (eS7) { sp7 = null; }
        if (sp7 && Math.min(sp7[0], sp7[1]) <= lo + 0.02 && Math.max(sp7[0], sp7[1]) >= hi - 0.02) return p7;
      }
      return null;
    }
    /* AND ANY DRAWN PIECE A HAIR OVER ITS ROOM'S FACE (v165, plan 1000075648:
       the washer and the dryer of the laundry, drawn against its wall, are
       deeper in the catalogue than in the drawing and stood 2 and 4 cm in
       the wall -- the bathroom beyond had taken 4 cm of the laundry for its
       tub). It is slid back inside where the room is long enough; only a
       tub or a shower moves a partition, and the tubs go first so what
       they take is known. */
    var WORD = DRAWNP.filter(function (dw) { return /^(bath|shower)/.test(String(dw.kind)); })
                     .concat(DRAWNP.filter(function (dw) { return !/^(bath|shower)/.test(String(dw.kind)); }));
    for (i = 0; i < WORD.length; i++) {
      var DPw = WORD[i], wetW = /^(bath|shower)/.test(String(DPw.kind));
      if (!wetW && /^(sofa|table|chair|armchair|island|barTable)/.test(String(DPw.kind))) continue;
      var Fw = null;
      try { Fw = _impFaces(DPw.fx.x, DPw.fx.z); } catch (eFw) { Fw = null; }
      if (!Fw) continue;
      var rw = _fixRect(DPw.fx), wX = rw.x1 - rw.x0, wZ = rw.z1 - rw.z0, spX = Fw.x1 - Fw.x0, spZ = Fw.z1 - Fw.z0, said7 = [];
      [['x', wX, spX, rw.x0, rw.x1, Fw.x0, Fw.x1, rw.z0, rw.z1, 'v'], ['z', wZ, spZ, rw.z0, rw.z1, Fw.z0, Fw.z1, rw.x0, rw.x1, 'h']].forEach(function (A) {
        var axw = A[0], len = A[1], sp = A[2], r0 = A[3], r1 = A[4], f0 = A[5], f1 = A[6], o0 = A[7], o1 = A[8], ow = A[9];
        if (r0 >= f0 - 0.005 && r1 <= f1 + 0.005) return;
        if (len <= sp + 0.005) {
          var sh7 = (r0 < f0) ? (f0 - r0) : -(r1 - f1);
          if (axw === 'x') DPw.fx.x = _impR(DPw.fx.x + sh7); else DPw.fx.z = _impR(DPw.fx.z + sh7);
          said7.push(DPw.kind + ' slid ' + _impN(Math.abs(sh7)) + ' m back inside its room');
          return;
        }
        if (!wetW) return;
        var need = len - sp + 0.005;
        /* v177 (Marek: pokud je koupelna mala a nevychazi prvky, muzes plan
           roztahnout): up to 30 cm, where the room beyond keeps 1.80 m */
        if (need > 0.30) { said7.push(DPw.kind + ' ' + _impN(need) + ' m longer than its room -- left'); return; }
        /* the side it stands over first, then the other */
        var sides = (r0 < f0 - 0.001) ? [[f0 - PTw / 2, -1], [f1 + PTw / 2, 1]] : [[f1 + PTw / 2, 1], [f0 - PTw / 2, -1]], k7, pm = null, sg7 = 0;
        for (k7 = 0; k7 < 2 && !pm; k7++) {
          var pc7 = wetPart(ow, sides[k7][0], o0, o1);
          if (!pc7) continue;
          /* the room beyond: what is left of it once the wall moves */
          var bq7 = sides[k7][0] + sides[k7][1] * (PTw / 2 + 0.15), mo7 = (o0 + o1) / 2, FB7 = null;
          try { FB7 = (axw === 'x') ? _impFaces(bq7, mo7) : _impFaces(mo7, bq7); } catch (eB7) { FB7 = null; }
          var left7 = FB7 ? (((axw === 'x') ? (FB7.x1 - FB7.x0) : (FB7.z1 - FB7.z0)) - need) : 9;
          if (need > 0.12 && left7 < 1.80) { said7.push(DPw.kind + ' ' + _impN(need) + ' m too long -- the room beyond would keep ' + _impN(left7) + ' m'); continue; }
          pm = pc7; sg7 = sides[k7][1];
        }
        if (!pm) { said7.push(DPw.kind + ' ' + _impN(need) + ' m too long and no partition beside it to move -- left'); return; }
        pm.c = _impR(pm.c + sg7 * need);
        var n0 = (sg7 < 0) ? f0 - need : f0, n1 = (sg7 > 0) ? f1 + need : f1, mid = Math.max(n0 + len / 2, Math.min(n1 - len / 2, (r0 + r1) / 2));
        if (axw === 'x') DPw.fx.x = _impR(mid); else DPw.fx.z = _impR(mid);
        said7.push(DPw.kind + ': the partition beside it moved ' + _impN(need) + ' m out, the room beyond giving it');
      });
      if (said7.length) WETN.push(said7.join(', '));
    }
    var DSW = [], DSN = [], ISLN = [], Zd = null;
    if (DRAWNP.length) { try { Zd = _fitZones(); } catch (eZd) { Zd = null; } }
    /* v164: the islands and bar tables first -- one that has to give way
       gives way before the counter it stands on is judged stuck */
    /* v167: then the L and U kitchens -- one that shortens an arm frees the
       fridge it ran into before the fridge is judged stuck */
    var ORDP = DRAWNP.filter(function (dq) { return /^(island24|island18|island|barTable)$/.test(String(dq.kind)); })
                     .concat(DRAWNP.filter(function (dq) { return /^kitchen[LU]$/.test(String(dq.kind)); }))
                     .concat(DRAWNP.filter(function (dq) { return !/^(island24|island18|island|barTable|kitchenL|kitchenU)$/.test(String(dq.kind)); }));
    var KITN = [];
    for (i = 0; Zd && i < ORDP.length; i++) {
      var DP = ORDP[i], fd = DP.fx, c0 = null;
      try { c0 = _fitClash(fd, DP.F, _impZonesFor(fd, Zd), null, fd.id); } catch (eC0) { c0 = null; }
      if (!c0 || (c0.reason !== 'door' && c0.reason !== 'overlap' && !(c0.reason === 'window' && _impWinBlocks(fd)))) continue;
      /* THE L SOFA IN A DOORWAY IS A STRAIGHT ONE (v146, plan 1000073678:
         the reader drew a 3.2 x 1.4 m sofa by the front door, it became an
         L, and the L stood in the door's swing). A drawn L or U in a door's
         way becomes the straight sofa its drawn length takes -- a four-, a
         three- or a two-seater -- on the same wall, its back where the L's
         was, slid along the wall up to 1.20 m to where it stands clear. */
      if (/^sofa[LU]$/.test(String(DP.kind))) {
        var qS6 = DP.q, mL6 = _fixWD(fd), aS6 = qS6 ? Math.max(qS6.x1 - qS6.x0, qS6.z1 - qS6.z0) : mL6.w, rS6 = (((fd.rot | 0) % 4) + 4) % 4;
        var KS6 = ['sofa4', 'sofa3', 'sofa2'], k6, gS6 = null, stS6 = 0;
        for (k6 = (aS6 >= 2.35) ? 0 : ((aS6 >= 1.85) ? 1 : 2); k6 < KS6.length && !gS6; k6++) {
          var dS6 = _fixDef(KS6[k6]);
          if (!dS6) continue;
          var off6 = (dS6.d - mL6.d) / 2, bx6 = fd.x + IMP_NRM[rS6][0] * off6, bz6 = fd.z + IMP_NRM[rS6][1] * off6, st6, sg6;
          for (st6 = 0; st6 <= 1.2001 && !gS6; st6 += 0.05) for (sg6 = -1; sg6 <= 1 && !gS6; sg6 += 2) {
            if (st6 === 0 && sg6 > 0) continue;
            var g6 = { id: fd.id, kind: KS6[k6], x: _impR(bx6 + IMP_TAN[rS6][0] * sg6 * st6), z: _impR(bz6 + IMP_TAN[rS6][1] * sg6 * st6), rot: rS6 };
            if (fd.st != null) g6.st = fd.st;
            var c6 = null;
            try { c6 = _fitClash(g6, DP.F, _impZonesFor(g6, Zd), null, g6.id); } catch (eC6) { c6 = { reason: 'error' }; }
            if (_impClear(c6, g6)) { gS6 = g6; stS6 = st6; }
          }
        }
        if (gS6) {
          L[L.indexOf(fd)] = gS6;
          DSW.push(DP.kind + ' -> ' + gS6.kind + ' (was ' + c0.reason + ' -- a straight sofa on the same wall' + (stS6 > 0.001 ? (', slid ' + _impN(stS6) + ' m') : '') + ', v146)');
          DP.fx = gS6; DP.kind = gS6.kind; DP.D = _fixDef(gS6.kind);
          continue;
        }
      }
      if (DP.D && (DP.D.shape === 'L' || DP.D.shape === 'U')) {
        /* v167: an L or a U on another piece shortens the arm that runs into
           it -- the corner stays, the arm ends before the piece */
        if (c0.reason === 'overlap') {
          var kS = null, cK = null;
          try { kS = _impKitShrink(fd); } catch (eKS) { kS = null; }
          if (kS) {
            try { cK = _fitClash(fd, DP.F, _impZonesFor(fd, Zd), null, fd.id); } catch (eCK) { cK = null; }
            if (_impClear(cK, fd)) { KITN.push(kS); continue; }
            KITN.push(kS + ' -- still ' + cK.reason + (cK.detail ? (' ' + cK.detail) : ''));
            c0 = cK;
          }
        }
        DP.stuck = c0;
        DSN.push(DP.kind + ' (' + c0.reason + (c0.detail ? (' ' + c0.detail) : '') + ', keeps its corner)');
        continue;
      }
      var axes = (DP.D && DP.D.wall) ? [((fd.rot | 0) % 2 === 0) ? 'x' : 'z'] : ['x', 'z'], x9 = fd.x, z9 = fd.z, got = null, st9, sg9, ax9;
      for (st9 = 0.05; st9 <= 1.2001 && !got; st9 += 0.05) {
        for (ax9 = 0; ax9 < axes.length && !got; ax9++) for (sg9 = -1; sg9 <= 1 && !got; sg9 += 2) {
          fd.x = x9; fd.z = z9;
          if (axes[ax9] === 'x') fd.x = _impR(x9 + sg9 * st9); else fd.z = _impR(z9 + sg9 * st9);
          var c1 = null;
          try { c1 = _fitClash(fd, DP.F, _impZonesFor(fd, Zd), null, fd.id); } catch (eC1) { c1 = { reason: 'error' }; }
          if (_impClear(c1, fd)) got = { ax: axes[ax9], d: sg9 * st9 };
        }
      }
      /* THE BAR COUNTER THAT HAS NO PLACE (v164, plan of 346 sqft, Marek:
         barovy pult s tremi zidlickami pres kuchynskou linku -- kdyz neni
         mozne nabytek rozmistit bez kolizi, tak tam barovy pult nema co delat,
         nebo ma byt mensi ostruvek). An island or a bar table that stays on
         another piece or in a door's way wherever it slides is tried smaller --
         the island of 2.4 m, of 1.8 m, the plain one of 1.2 m with no stools --
         where it stood and slid up to 1.20 m either way; with none of them
         clear it is left out. It is the one piece a plan can do without. */
      if (!got && /^(island24|island18|island|barTable)$/.test(String(DP.kind))) {
        fd.x = x9; fd.z = z9;
        var KI = ['island24', 'island18', 'island'], ki = KI.indexOf(String(DP.kind)), gI = null, stI, axI, sgI;
        for (ki = (ki < 0) ? KI.length - 1 : ki + 1; ki < KI.length && !gI; ki++) {
          for (stI = 0; stI <= 1.2001 && !gI; stI += 0.05) {
            for (axI = 0; axI < 2 && !gI; axI++) for (sgI = -1; sgI <= 1 && !gI; sgI += 2) {
              if (stI === 0 && (axI > 0 || sgI > 0)) continue;
              var gq = { id: fd.id, kind: KI[ki], x: _impR(x9 + (axI === 0 ? sgI * stI : 0)), z: _impR(z9 + (axI === 1 ? sgI * stI : 0)), rot: fd.rot | 0 };
              if (fd.st != null) gq.st = fd.st;
              if (fd.mirror) gq.mirror = fd.mirror;
              var cI = null;
              try { cI = _fitClash(gq, DP.F, _impZonesFor(gq, Zd), null, gq.id); } catch (eCI) { cI = { reason: 'error' }; }
              if (_impClear(cI, gq)) gI = gq;
            }
          }
        }
        if (gI) {
          L[L.indexOf(fd)] = gI;
          ISLN.push(DP.kind + ' -> ' + gI.kind + ' (it was ' + c0.reason + ' wherever it slid)');
          DP.fx = gI; DP.kind = gI.kind; DP.D = _fixDef(gI.kind);
          continue;
        }
        var kI = L.indexOf(fd);
        if (kI >= 0) L.splice(kI, 1);
        DP.gone = true;
        ISLN.push(DP.kind + ' left out (' + c0.reason + (c0.detail ? (' ' + c0.detail) : '') + ' wherever it or a smaller island stood)');
        continue;
      }
      if (!got) { fd.x = x9; fd.z = z9; DP.stuck = c0; DSN.push(DP.kind + ' (' + c0.reason + (c0.detail ? (' ' + c0.detail) : '') + ')'); continue; }
      DSW.push(DP.kind + ' ' + _impN(Math.abs(got.d)) + ' m along ' + got.ax + ' (was ' + c0.reason + ')');
    }
    if (ISLN.length) DRAWNP = DRAWNP.filter(function (dq) { return !dq.gone; });
    /* A WASHER AND A DRYER WITH NO ROOM FOR BOTH ARE STACKED (v150, plan
       1000073678 as read the first time: a laundry closet of 16 sqft, its
       door swinging in -- the washer was slid out of the swing, to where the
       dryer, a piece of its own now, stands). A drawn dryer that is itself in
       a door's way, or that holds the washer beside it in one, goes on top
       of that washer: one footprint and both machines, as a stacked pair is
       built. The washer says so (stack: 'dryer'), and the house bills both. */
    var STK = [];
    for (i = 0; Zd && i < DRAWNP.length; i++) {
      var DPd = DRAWNP[i];
      if (DPd.kind !== 'dryer' || L.indexOf(DPd.fx) < 0) continue;
      var wsh = null, dW = 1e9, j8;
      for (j8 = 0; j8 < DRAWNP.length; j8++) {
        var w8 = DRAWNP[j8];
        if (w8.kind !== 'washer' || L.indexOf(w8.fx) < 0) continue;
        var d8 = Math.hypot(w8.fx.x - DPd.fx.x, w8.fx.z - DPd.fx.z);
        if (d8 < dW) { dW = d8; wsh = w8; }
      }
      if (!wsh || dW > 1.6 || (!DPd.stuck && !wsh.stuck)) continue;
      L.splice(L.indexOf(DPd.fx), 1);
      var gotW = 0;
      if (wsh.stuck) {
        var fw = wsh.fx, xw = fw.x, zw = fw.z, stW, sgW, axW = ((fw.rot | 0) % 2 === 0) ? 'x' : 'z';
        for (stW = 0.05; stW <= 1.2001 && !gotW; stW += 0.05) for (sgW = -1; sgW <= 1 && !gotW; sgW += 2) {
          fw.x = xw; fw.z = zw;
          if (axW === 'x') fw.x = _impR(xw + sgW * stW); else fw.z = _impR(zw + sgW * stW);
          var cW = null;
          try { cW = _fitClash(fw, wsh.F, _impZonesFor(fw, Zd), null, fw.id); } catch (eCW) { cW = { reason: 'error' }; }
          if (_impClear(cW, fw)) gotW = stW;
        }
        /* the dryer was not what held the washer: it stays where it was */
        if (!gotW) { fw.x = xw; fw.z = zw; L.push(DPd.fx); continue; }
        wsh.stuck = null;
      }
      wsh.fx.stack = 'dryer';
      STK.push('the dryer on the washer' + (DPd.stuck ? (' (the dryer was in a ' + DPd.stuck.reason + ')') : '') + (gotW ? (', the washer then ' + _impN(gotW) + ' m along its wall out of the door\'s way') : ''));
    }
    /* TWO PIECES ON EACH OTHER: ONE OF THEM GOES (v167, Marek: obecne
       pravidlo -- pokud by se mely fitouts prekryvat, tak jeden z nich
       nestavet). A drawn piece still standing on another after the slides
       and the shortened arms is weighed against it: the bathroom pieces stay
       before the kitchen counters, those before the appliances, those before
       beds, sofas and wardrobes, those before the rest; of two alike the
       smaller goes. The plan is built and saved without it. */
    var DROPN = [];
    function keepRank(k) {
      k = String(k || '');
      /* v180: the WC before the tub -- a bathroom with no WC is no bathroom */
      if (/^wc/.test(k)) return 6;
      if (/^(bath|shower|basin)/.test(k)) return 5;
      if (/^kitchen/.test(k)) return 4;
      if (/^(fridge|washer|dryer)/.test(k)) return 3;
      if (/^(bed|bunk|sofa|wardrobe|desk)/.test(k)) return 2;
      return 1;
    }
    function areaOf(o) { var mo = _fixWD(o); return mo.w * mo.d; }
    for (i = 0; Zd && i < DRAWNP.length; i++) {
      var DPo = DRAWNP[i], fo = DPo.fx;
      if (!DPo.stuck || DPo.stuck.reason !== 'overlap' || L.indexOf(fo) < 0) continue;
      var co = null;
      try { co = _fitClash(fo, DPo.F, _impZonesFor(fo, Zd), null, fo.id); } catch (eCo) { co = null; }
      if (!co || co.reason !== 'overlap') { DPo.stuck = co; continue; }
      var ro = _fixRect(fo), mate = null, jo;
      for (jo = 0; jo < L.length && !mate; jo++) {
        var oo = L[jo];
        if (oo === fo || _fitStOf(oo) !== _fitStOf(fo)) continue;
        if (_fitHit(ro, _fixRect(oo), 0.01) && _impShapeHit(fo, oo)) mate = oo;
      }
      if (!mate) continue;
      /* THE FRIDGE IN THE CORNER, THE COUNTER SHORTER (v178, plan 1000076010,
         Marek: lednice by se klidne vlezla vedle kuchynske linky L, staci ji
         prizpusobit tak, at se tam lednice do rohu vleze). A fridge, a washer
         or a dryer drawn at the end of an L or U counter stood on its arm and
         was left out. The arm that runs into it now ends before it -- the
         corner kept, 5 cm steps, not under the catalogue's least -- and both
         stay; only where no arm can give way does one of them go. */
      var kq9 = /^kitchen[LU]$/.test(String(fo.kind)) ? fo : (/^kitchen[LU]$/.test(String(mate.kind)) ? mate : null), oq9 = (kq9 === fo) ? mate : fo;
      if (kq9 && /^(fridge|washer|dryer)/.test(String(oq9.kind))) {
        var ksF = null;
        try { ksF = _impKitShrink(kq9); } catch (eKF) { ksF = null; }
        if (ksF && !(_fitHit(_fixRect(kq9), _fixRect(oq9), 0.01) && _impShapeHit(kq9, oq9))) {
          KITN.push(ksF + ' -- the ' + oq9.kind + ' kept in its place (v178)');
          DRAWNP.forEach(function (dq) { if ((dq.fx === fo || dq.fx === mate) && dq.stuck && dq.stuck.reason === 'overlap') dq.stuck = null; });
          continue;
        }
      }
      var rA = keepRank(fo.kind), rB = keepRank(mate.kind);
      var drop = (rA < rB || (rA === rB && areaOf(fo) <= areaOf(mate))) ? fo : mate, keep = (drop === fo) ? mate : fo;
      /* A TUB ON THE WC IS A SHOWER IN ITS PLACE (v180, plan 1000075604: a
         4 ft tub drawn beside the WC became the 1.52 m one and ran onto it --
         the WC was left out, and the library refused "a bathroom with no
         WC"). The tub that gives way becomes a 1.20 or a 0.90 m shower on
         the same wall, its back where the tub's was, slid up to 0.60 m along
         the wall to where it stands clear. */
      if (/^bath/.test(String(drop.kind))) {
        var DQ = null, qd;
        for (qd = 0; qd < DRAWNP.length && !DQ; qd++) if (DRAWNP[qd].fx === drop) DQ = DRAWNP[qd];
        var rT = (((drop.rot | 0) % 4) + 4) % 4, mT = _fixWD(drop), SHK = ['shower120', 'shower90'], gT = null, kT, stT, sgT;
        for (kT = 0; kT < SHK.length && !gT && DQ; kT++) {
          var DT = _fixDef(SHK[kT]);
          if (!DT) continue;
          var offT = (DT.d - mT.d) / 2, bxT = drop.x + IMP_NRM[rT][0] * offT, bzT = drop.z + IMP_NRM[rT][1] * offT;
          for (stT = 0; stT <= 0.6001 && !gT; stT += 0.05) for (sgT = -1; sgT <= 1 && !gT; sgT += 2) {
            if (stT === 0 && sgT > 0) continue;
            var gq = { id: drop.id, kind: SHK[kT], x: _impR(bxT + IMP_TAN[rT][0] * sgT * stT), z: _impR(bzT + IMP_TAN[rT][1] * sgT * stT), rot: rT };
            if (drop.st != null) gq.st = drop.st;
            var cT = null;
            try { cT = _fitClash(gq, DQ.F, _impZonesFor(gq, Zd), null, gq.id); } catch (eT) { cT = { reason: 'error' }; }
            /* v191: and clear of the piece it gave way to and of every other one
               (plan 1000075604: the shower120 was put on the WC again, and the
               last check took it out -- no tub, no shower) */
            var hitT = false, qh;
            for (qh = 0; qh < L.length && !hitT; qh++) {
              var oh = L[qh];
              if (!oh || oh === drop || oh.stack) continue;
              if (_fitHit(_fixRect(gq), _fixRect(oh), 0.01) && _impShapeHit(gq, oh)) hitT = true;
            }
            if (!hitT && _impClear(cT, gq)) gT = gq;
          }
        }
        if (gT) {
          L[L.indexOf(drop)] = gT;
          DQ.fx = gT; DQ.kind = gT.kind; DQ.D = _fixDef(gT.kind); DQ.stuck = null;
          DRAWNP.forEach(function (dq) { if (dq.fx === keep && dq.stuck && dq.stuck.reason === 'overlap') dq.stuck = null; });
          DROPN.push(drop.kind + ' stood on the ' + keep.kind + ' -- a ' + gT.kind + ' in its place (v180)');
          continue;
        }
      }
      L.splice(L.indexOf(drop), 1);
      DRAWNP.forEach(function (dq) { if (dq.fx === drop) { dq.gone = true; dq.stuck = null; } if (dq.fx === keep && dq.stuck && dq.stuck.reason === 'overlap') dq.stuck = null; });
      DROPN.push(drop.kind + ' left out -- it stood on the ' + keep.kind);
    }
    if (DROPN.length) DRAWNP = DRAWNP.filter(function (dq) { return !dq.gone; });
    /* THE SUGGESTED FURNITURE (v118, Marek: chybi mi tam ten nabytek v living
       room). A main room the plan draws empty -- the living room of the sales
       plan -- got pieces from the reader marked SUGGESTED, and they waited for
       a switch that was not there yet. They are placed now, after everything
       drawn, and marked sug: first where the reader suggested them; where that
       stands in a doorway, in a door's swing, in front of a window it is taller
       than or on another piece, the placer finds the room a free place, the
       one the + uses. A piece with no free place anywhere in its room is left
       out and named. The SUG switch in the drawer takes them out and puts them
       back (the UI's part, next). */
    var sugPut = [], sugNo = [], Z = null;
    if (SUGQ.length) { try { Z = _fitZones(); } catch (eZ) { Z = []; } }
    /* the coffee table after the sofa it belongs to */
    var SQ = SUGQ.map(function (q3) {
      var a3 = Math.max(q3.x1 - q3.x0, q3.z1 - q3.z0), b3 = Math.min(q3.x1 - q3.x0, q3.z1 - q3.z0);
      return { q: q3, kind: _impFixKind(q3.type, a3, b3, q3.label) };
    });
    SQ.sort(function (p1, p2) { return (p1.kind === 'coffee' ? 1 : 0) - (p2.kind === 'coffee' ? 1 : 0); });
    /* in front of the sofa nearest it in the same room, at a knee's distance */
    function frontOfSofa(fx3, F3) {
      var best = null, bd3 = 1e9, k3;
      for (k3 = 0; k3 < L.length; k3++) {
        var o3 = L[k3];
        if (!/^sofa/.test(String(o3.kind)) || o3.x < F3.x0 || o3.x > F3.x1 || o3.z < F3.z0 || o3.z > F3.z1) continue;
        var dd3 = Math.hypot(o3.x - fx3.x, o3.z - fx3.z);
        if (dd3 < bd3) { bd3 = dd3; best = o3; }
      }
      if (!best) return null;
      var ms = _fixDims(best), r3 = (best.rot | 0) % 4, nx = [0, -1, 0, 1][r3], nz = [1, 0, -1, 0][r3];
      var g = { id: fx3.id, kind: fx3.kind, x: 0, z: 0, rot: r3 % 2 };
      if (fx3.st != null) g.st = fx3.st;
      var mc = _fixDims(g), reach = ((r3 % 2) ? ms.w : ms.d) / 2 + 0.45 + ((r3 % 2) ? mc.w : mc.d) / 2;
      g.x = _impR(best.x + nx * reach); g.z = _impR(best.z + nz * reach);
      return g;
    }
    for (i = 0; i < SQ.length; i++) {
      var q2 = SQ[i].q, kind2 = SQ[i].kind, D2 = kind2 ? _fixDef(kind2) : null;
      if (!D2) { sugNo.push(q2.type + ' -- no piece in this product'); continue; }
      var PO2 = _impPose(q2, kind2, D2, 100 + i), fx2 = PO2.fx, how = 'as suggested';
      var c2 = null;
      if (kind2 === 'coffee') {
        var fs = frontOfSofa(fx2, PO2.F), cs = null;
        if (fs) { try { cs = _fitClash(fs, PO2.F, Z || [], null, fs.id); } catch (eCs) { cs = { reason: 'error' }; } }
        if (fs && !cs) { fx2.x = fs.x; fx2.z = fs.z; fx2.rot = fs.rot; how = 'in front of the sofa'; }
      }
      try { c2 = _fitClash(fx2, PO2.F, Z || [], null, fx2.id); } catch (eC) { c2 = null; }
      if (c2) {
        var res2 = null;
        try { res2 = _fitPlace({ kind: kind2, room: { x: (q2.x0 + q2.x1) / 2, z: (q2.z0 + q2.z1) / 2 }, wall: 'auto', align: 'auto' }); } catch (eP) { res2 = null; }
        if (!res2 || !res2.ok || !res2.f) {
          sugNo.push(q2.type + ' -> ' + kind2 + ' -- ' + c2.reason + (c2.detail ? (' ' + c2.detail) : '') + ' where suggested, no free place in its room' + (res2 ? (' (' + res2.reason + ')') : ''));
          continue;
        }
        fx2.x = res2.f.x; fx2.z = res2.f.z; fx2.rot = res2.f.rot; delete fx2.mirror;
        how = c2.reason + (c2.detail ? (' ' + c2.detail) : '') + ' where suggested -- moved to the ' + res2.wall + ' wall';
      }
      fx2.sug = true;
      L.push(fx2);
      sugPut.push(q2.type + ' -> ' + kind2 + ' at ' + _impN(fx2.x) + ',' + _impN(fx2.z) + ' turn ' + fx2.rot + ' (' + how + ')');
    }
    var nn = Object.keys(none);
    return put.length + ' fitout piece(s) placed from the plan: ' + put.join(' | ') +
           (over ? ('; ' + over + ' of them touch another piece -- the boxes were drawn close') : '') +
           (DSW.length ? ('; DOOR SWING (v141): ' + DSW.length + ' drawn piece(s) slid along their wall out of a door\'s way or off another piece: ' + DSW.join(' | ')) : '') +
           (DSN.length ? ('; DOOR SWING (v141): ' + DSN.length + ' drawn piece(s) with no free place near, left where drawn: ' + DSN.join(' | ')) : '') +
           (STK.length ? ('; STACKED (v150): ' + STK.join(' | ') + ' -- no room for the two side by side') : '') +
           (COMPACT.length ? ('; COMPACT (v151): ' + COMPACT.join(' | ') + ' -- the 24 in / 600 mm appliance instead') : '') +
           (FRIDGEN.length ? ('; FRIDGE (v162): ' + FRIDGEN.join(' | ')) : '') +
           (ISLN.length ? ('; ISLAND (v164): ' + ISLN.join(' | ')) : '') +
           (KITN.length ? ('; KITCHEN ARMS (v167): ' + KITN.join(' | ')) : '') +
           (DROPN.length ? ('; ON EACH OTHER (v167): ' + DROPN.join(' | ')) : '') +
           (WETN.length ? ('; WET (v145): ' + WETN.join(' | ')) : '') +
           (sugPut.length ? ('; ' + sugPut.length + ' suggested piece(s) placed, marked SUG: ' + sugPut.join(' | ')) : '') +
           (sugNo.length ? ('; ' + sugNo.length + ' suggested piece(s) left out: ' + sugNo.join(' | ')) : '') +
           (nn.length ? ('; no piece in this product for: ' + nn.map(function (t) { return t + (none[t] > 1 ? (' x' + none[t]) : ''); }).join(', ')) : '');
  }
  /* THE CLOSETS BY THEIR REAL CELLS (v105). Whether a space is a closet was
     decided from the box the reader gave it, and a box can be wrong both ways:
     too narrow, and a linen closet was named STORAGE like a room; too small,
     and a walk-in was marked a closet. Once the plan is written the cells are
     real, so the question is asked again of them: a STORAGE, LAUNDRY, PANTRY
     or WARDROBE under 3 m2 becomes a closet, and a closet mark in a cell of
     3 m2 or more becomes a room of its own type. */
  function _impClosetsByCell(S, f) {
    var R = _roomsTracked(), pins = S.roomNames, TB = DRAW.roomTable(), toMark = [], toRoom = [], i, k;
    if (!Array.isArray(pins)) return '';
    var SMALL = { 'rooms.storage': 1, 'rooms.laundry': 1, 'rooms.pantry': 1, 'rooms.wardrobe': 1 };
    function ixOf(key) { for (var q = 0; q < TB.length; q++) if (TB[q] && TB[q].i18n === key) return q; return -1; }
    function typeAt(x, z) {
      var best = null, bd = 1e9, q;
      for (q = 0; f && f.closets && q < f.closets.length; q++) {
        var dd = Math.hypot(f.closets[q].x - x, f.closets[q].z - z);
        if (dd < bd) { bd = dd; best = f.closets[q]; }
      }
      return (best && bd < 0.05) ? best.type : '';
    }
    for (i = 0; i < R.length; i++) {
      var net = _closetNet(R[i]), mark = -1, named = -1;
      for (k = 0; k < pins.length; k++) {
        if (!pins[k] || typeof pins[k].x !== 'number' || !_roomHolds(R[i], pins[k].x, pins[k].z)) continue;
        if (_closetPin(pins[k])) { if (mark < 0) mark = k; } else if (named < 0) named = k;
      }
      if (net < CLOSET_MAX_M2 && mark < 0 && named >= 0 && SMALL[pins[named].k]) {
        toMark.push(pins[named].k.replace('rooms.', '') + ' ' + net.toFixed(1) + ' m2');
        pins[named] = { x: pins[named].x, z: pins[named].z, n: 0, k: CLOSET_KEY };
      } else if (net >= CLOSET_MAX_M2 && mark >= 0 && named < 0) {
        var ty = typeAt(pins[mark].x, pins[mark].z) || 'storage';
        var kk = _impKey('', (ty === 'closet' || ty === 'storage') ? ['rooms.wardrobe', 'rooms.storage'] : (IMP_TYPE_KEY[ty] || ['rooms.storage'])).key, ix = kk ? ixOf(kk) : -1;
        if (ix < 0) continue;
        toRoom.push(ty + ' ' + net.toFixed(1) + ' m2 -> ' + kk);
        pins[mark] = { x: pins[mark].x, z: pins[mark].z, n: ix, k: kk };
      }
    }
    var out = [];
    if (toMark.length) out.push(toMark.length + ' small space(s) named as storage became closets by their real size: ' + toMark.join(', '));
    if (toRoom.length) out.push(toRoom.length + ' closet(s) turned out to be 3 m2 or more and became rooms: ' + toRoom.join(', '));
    return out.join('; ');
  }
  /* THE PATIO UNDER THE ROOF (v115, Marek: u ADU by bylo idealni, aby tam
     byla vytazena strecha, kterou ADU umi; strecha se nepretahla pres patio).
     A patio, a porch or a deck the plan draws against a wall of the house
     becomes the product's terrace on that side -- its width along the wall,
     its depth out from it, pavers for a patio and boards for a deck or a
     porch -- and the roof is pulled out over it by its depth (a pitched roof
     only; a flat roof has its parapet). The core holds the reach to its own
     limits and stands posts under it past 1.5 m. The biggest one wins; a
     space that touches no wall is named in the log only. */
  /* the patio, porch or deck that becomes the terrace: against a wall of
     the house, the biggest one (v115) -- picked here for the terrace and for
     its doors (v158) alike */
  function _impOutdoorPick(f) {
    var best = null, i, NEAR = 0.8, INN = 1.0, txt = [];
    for (i = 0; f.outdoor && i < f.outdoor.length; i++) {
      var Q = f.outdoor[i], sd = '', dep = 0, ovA = 0;
      var ovX = Math.min(Q.x1, f.x1) - Math.max(Q.x0, f.x0), ovZ = Math.min(Q.z1, f.z1) - Math.max(Q.z0, f.z0);
      if (ovX > 0.5 && Q.z0 >= f.z1 - INN && Q.z0 - f.z1 <= NEAR && Q.z1 > f.z1) { sd = 'z1'; dep = Q.z1 - f.z1; ovA = ovX; }
      else if (ovX > 0.5 && Q.z1 <= f.z0 + INN && f.z0 - Q.z1 <= NEAR && Q.z0 < f.z0) { sd = 'z0'; dep = f.z0 - Q.z0; ovA = ovX; }
      else if (ovZ > 0.5 && Q.x0 >= f.x1 - INN && Q.x0 - f.x1 <= NEAR && Q.x1 > f.x1) { sd = 'x1'; dep = Q.x1 - f.x1; ovA = ovZ; }
      else if (ovZ > 0.5 && Q.x1 <= f.x0 + INN && f.x0 - Q.x1 <= NEAR && Q.x0 < f.x0) { sd = 'x0'; dep = f.x0 - Q.x0; ovA = ovZ; }
      if (!sd || dep < 0.8) { txt.push('"' + Q.label + '" touches no wall of the house -- left out'); continue; }
      if (!best || ovA * dep > best.a) best = { Q: Q, side: sd, dep: dep, a: ovA * dep };
    }
    return { best: best, txt: txt };
  }
  function _impOutdoor(S, f) {
    /* NO TERRACE ON THE PLAN, NO TERRACE (v137, Marek: pri nahrani tohoto
       pudorysu je terasa pod protazenou strechou a pritom tam neni pruchod
       na terasu; zadna terasa ani na pudorysu neni). A plan with no patio,
       porch or deck left the terrace of the house before where it was --
       under a roof pulled out for it, at the old house's place, often no
       longer against the new walls. It is taken off now, and the roof on
       that side goes back to its eave. */
    if (!f.outdoor || !f.outdoor.length) {
      var P0 = S.product = S.product || {}, T0 = P0.terrace;
      if (!T0 || !T0.on) return '';
      T0.on = false;
      var R0 = S.roof || {}, sd0 = T0.side, ev = (R0.eave > 0) ? R0.eave : 0.45, back = '';
      if (R0.over && sd0 && Number(R0.over[sd0]) > ev + 0.01) { back = '; the roof on that side back from ' + _impN(R0.over[sd0]) + ' to its eave, ' + _impN(ev) + ' m'; R0.over[sd0] = _impR(ev); }
      return 'the plan has no terrace, porch or deck -- the terrace of the house before was taken off' + back + ' (v137)';
    }
    /* v135: a box drawn into the house -- the porch under a corner the plan
       squares off -- is cut at the wall: up to 1.0 m in is still the porch
       outside it (it was 0.3 m, and the covered porch of the 1BR plan, 0.37
       m in, was left out) */
    var PK = _impOutdoorPick(f), best = PK.best, txt = PK.txt;
    if (!best) return txt.join('; ');
    var Qb = best.Q, sdb = best.side, d = Math.min(best.dep, 6.0), P = S.product = S.product || {};
    var T = P.terrace = P.terrace || { on: false, side: 'z1', w: 4.0, d: 3.0, off: 0, surface: 'deck', pergola: 'none' };
    var lo = (sdb === 'z0' || sdb === 'z1') ? Qb.x0 : Qb.z0, hi = (sdb === 'z0' || sdb === 'z1') ? Qb.x1 : Qb.z1;
    var paver = /patio/.test(Qb.type), tl = S.isMetric === false ? 0.508 : 0.5;
    var along = hi - lo;
    if (paver) { along = Math.max(2 * tl, Math.round(along / tl) * tl); d = Math.max(2 * tl, Math.round(d / tl) * tl); }
    T.on = true; T.side = sdb; T.surface = paver ? 'pavers' : 'deck'; T.pergola = 'none';
    if (sdb === 'z1') { T.x0 = lo; T.z0 = f.z1; T.sx = along; T.sz = d; }
    else if (sdb === 'z0') { T.x0 = lo; T.z0 = f.z0 - d; T.sx = along; T.sz = d; }
    else if (sdb === 'x1') { T.x0 = f.x1; T.z0 = lo; T.sx = d; T.sz = along; }
    else { T.x0 = f.x0 - d; T.z0 = lo; T.sx = d; T.sz = along; }
    T.x0 = _impR(T.x0); T.z0 = _impR(T.z0); T.sx = _impR(T.sx); T.sz = _impR(T.sz);
    T.w = (sdb === 'z0' || sdb === 'z1') ? T.sx : T.sz; T.d = d;
    var R = S.roof || {}, roofTxt;
    if (R.type === 'flat') roofTxt = 'the roof is flat -- no overhang over it';
    else {
      if (!R.over) R.over = { x0: R.eave || 0.45, x1: R.eave || 0.45, z0: R.eave || 0.45, z1: R.eave || 0.45 };
      R.over[sdb] = _impR(Math.max(Number(R.over[sdb]) || 0, d));
      roofTxt = 'the roof pulled out ' + _impN(d) + ' m over it on that side (posts under it past 1.5 m)';
    }
    txt.unshift('"' + Qb.label + '" became the terrace on side ' + sdb + ', ' + _impN(along) + ' x ' + _impN(d) + ' m of ' + T.surface + ', and ' + roofTxt);
    return txt.join('; ');
  }
  function _impNameLeftovers(S, f) {
    var R = _roomsTracked(), pins = S.roomNames, TB = DRAW.roomTable(), hk = 'rooms.hall', hix = -1, n = 0, i, k;
    if (!Array.isArray(pins)) return 0;
    /* a hall where the product offers one (v108), the hallway where not */
    for (k = 0; k < TB.length; k++) if (TB[k] && TB[k].i18n === hk) { hix = k; break; }
    if (hix < 0) { hk = 'rooms.hallway'; for (k = 0; k < TB.length; k++) if (TB[k] && TB[k].i18n === hk) { hix = k; break; } }
    if (hix < 0) return 0;
    for (i = 0; i < R.length; i++) {
      if (R[i].closet || !(R[i].area >= 0.8)) continue;
      var has = false;
      for (k = 0; k < pins.length; k++) if (pins[k] && typeof pins[k].x === 'number' && _roomHolds(R[i], pins[k].x, pins[k].z)) { has = true; break; }
      if (has) continue;
      /* A SPACE INSIDE A CLOSET'S BOX IS THAT CLOSET (v115). The reader drew
         the bedroom closet's box up into the bedroom, so its mark landed in
         the bedroom and the closet itself, nameless, was called a hall */
      var inCl = false;
      for (k = 0; f && f.closets && k < f.closets.length; k++) {
        var CB2 = f.closets[k].box;
        if (CB2 && R[i].cx >= CB2[0] - 0.15 && R[i].cx <= CB2[2] + 0.15 && R[i].cz >= CB2[1] - 0.15 && R[i].cz <= CB2[3] + 0.15 && R[i].area < CLOSET_MAX_M2) { inCl = true; break; }
      }
      if (inCl) { pins.push({ x: R[i].cx, z: R[i].cz, n: 0, k: CLOSET_KEY }); continue; }
      pins.push({ x: R[i].cx, z: R[i].cz, n: hix, k: hk });
      n++;
    }
    return n;
  }
  /* ONE OPEN ROOM, ONE NAME (v169, see PIN_OPEN): a room holding only names
     of the open kind keeps the main one and loses the others, so the name
     on the plan, the pieces, the living set, the services and the library
     check all read the same room. */
  function _impOneName(S) {
    var R = _roomsTracked(), pins = S.roomNames, said = [], i, q;
    if (!Array.isArray(pins)) return '';
    for (i = 0; i < R.length; i++) {
      if (R[i].closet) continue;
      var idx = _roomPinsIn(R[i], pins);
      if (idx.length < 2) continue;
      var open = true;
      for (q = 0; q < idx.length; q++) if (PIN_OPEN[String(pins[idx[q]].k || '')] == null) { open = false; break; }
      if (!open) continue;
      var keep = _pinBest(pins, idx), gone = [];
      for (q = idx.length - 1; q >= 0; q--) {
        if (idx[q] === keep) continue;
        gone.unshift(String(pins[idx[q]].k || '').replace(/^rooms\./, ''));
        pins.splice(idx[q], 1);
        if (idx[q] < keep) keep--;
      }
      said.push('"' + gone.join('" + "') + '" and "' + String(pins[keep].k || '').replace(/^rooms\./, '') + '" in one room ' + _impN(R[i].area || 0) + ' m2 (no wall between them) -- named ' + String(pins[keep].k || '').replace(/^rooms\./, ''));
    }
    return said.length ? ('ONE NAME (v169): ' + said.join(' | ')) : '';
  }
  /* A BEDROOM'S WALK-IN CLOSET IS ITS WARDROBE (v173, Marek: storage v master
     bedroom -- radeji to melo byt postavene jako skrin). A room named storage
     whose every door leads into a bedroom is called WARDROBE and gets
     wardrobe cabinets along one wall, as many as stand clear of its door. */
  function _impWardrobeRooms(S) {
    var H = _PH, R = _roomsTracked(), pins = S.roomNames, said = [], i, k;
    if (!H || !Array.isArray(pins) || !H.fixturesRef || !_fixDef('wardrobe')) return '';
    var TB = DRAW.roomTable(), wix = -1;
    for (k = 0; k < TB.length; k++) if (TB[k] && TB[k].i18n === 'rooms.wardrobe') { wix = k; break; }
    if (wix < 0) return '';
    var DL = _doorsLive(), PS = H.partitions() || [], nb = R.map(function () { return []; });
    function roomAt(x, z) { for (var r = 0; r < R.length; r++) if (_roomHolds(R[r], x, z)) return r; return -1; }
    function pinOf(r) { return _pinBest(pins, _roomPinsIn(R[r], pins)); }
    function keyOf(r) { var ix = pinOf(r); return (ix >= 0) ? String(pins[ix].k || '') : ''; }
    function link(a, b) { if (a >= 0 && b >= 0 && a !== b) { nb[a].push(b); nb[b].push(a); } }
    for (i = 0; i < DL.length; i++) {
      var d = DL[i].d, p = DL[i].p, cx = (p.o === 'h') ? d.u : p.c, cz = (p.o === 'h') ? p.c : d.u;
      link(roomAt(p.o === 'h' ? cx : cx - 0.15, p.o === 'h' ? cz - 0.15 : cz), roomAt(p.o === 'h' ? cx : cx + 0.15, p.o === 'h' ? cz + 0.15 : cz));
    }
    for (i = 0; i < PS.length; i++) {
      var sp = PS[i], span = sp && sp.sep ? _partSpan(sp) : null;
      if (!span) continue;
      var al = (span[0] + span[1]) / 2;
      link(roomAt(sp.o === 'h' ? al : sp.c - 0.15, sp.o === 'h' ? sp.c - 0.15 : al), roomAt(sp.o === 'h' ? al : sp.c + 0.15, sp.o === 'h' ? sp.c + 0.15 : al));
    }
    var L = H.fixturesRef(), st = _fixStorey();
    for (i = 0; i < R.length; i++) {
      if (R[i].closet) continue;
      var kw = keyOf(i), isW = (kw === 'rooms.wardrobe');
      /* v174: one the reader named a wardrobe gets its cabinets too */
      if (isW) {
        var hasC = false;
        for (k = 0; k < L.length && !hasC; k++) if (L[k] && /^wardrobe/.test(String(L[k].kind)) && _roomHolds(R[i], L[k].x, L[k].z)) hasC = true;
        if (hasC) continue;
      } else {
        if (kw !== 'rooms.storage' || !nb[i].length) continue;
        if (!nb[i].every(function (j) { return /bedroom|container_room_7|guestroom/.test(keyOf(j)); })) continue;
      }
      var ix = pinOf(i);
      pins[ix].k = 'rooms.wardrobe'; pins[ix].n = wix;
      var put = [], side = 'auto', q, kq, KW = ['wardrobe', 'wardrobe100', 'wardrobe80'];
      for (q = 0; q < 6; q++) {
        var got = null;
        for (kq = 0; kq < KW.length && !got; kq++) {
          if (!_fixDef(KW[kq])) continue;
          var r9 = null;
          try { r9 = _fitPlace({ kind: KW[kq], room: { x: pins[ix].x, z: pins[ix].z }, wall: side, align: 'auto' }); } catch (eP) { r9 = null; }
          if (r9 && r9.ok && r9.f && r9.room && r9.room.i === i) { got = r9.f; if (side === 'auto') side = r9.wall || 'auto'; }
        }
        if (!got) break;
        var g = { id: 'f' + Date.now().toString(36) + 'wd' + q + Math.floor(Math.random() * 1000).toString(36), kind: got.kind, x: _impR(got.x), z: _impR(got.z), rot: got.rot | 0 };
        if (st != null) g.st = st;
        L.push(g); put.push(g.kind);
      }
      said.push((isW ? ('a wardrobe ' + _impN(R[i].area || 0) + ' m2') : ('a storage ' + _impN(R[i].area || 0) + ' m2 opening only into a bedroom is its wardrobe')) + (put.length ? (', ' + put.length + ' cabinet(s) along one wall: ' + put.join(', ')) : ', no cabinet stands clear in it'));
    }
    return said.length ? ('WARDROBE (v173): ' + said.join(' | ')) : '';
  }
  /* NOTHING STANDS ON ANOTHER PIECE, OVER A WALL OR IN A DOOR (v173, Marek:
     byla tam zase lednice pres kuchynskou linku -- pokud nejaky objekt
     koliduje, ten mensi a mene dulezity proste nestavet; pak nam to blokuje
     publikovani). The last word once every piece is placed: of two pieces
     on each other the less needed goes (the WC before the basin, the bath
     and the shower; those before the kitchen; then the appliances; beds,
     sofas and wardrobes; the rest), of two alike the smaller; a bath that
     goes, or stands over a wall or in a door, is a shower where one stands
     clear in its room; an appliance or anything less over a wall or in a
     door goes. The kitchen counter and the bathroom pieces otherwise stay. */
  /* NOTHING TALL IN FRONT OF A WINDOW (v182, Marek: zadny nabytek nemuze
     zastinovat okna -- to je zakladni pravidlo; sofa a spodni kuchynska
     dvirka mohou byt pod). Since v141 a drawn piece standing in front of a
     window was let be -- "how the drawing has it". A piece 1.20 m or more
     high in front of the glass -- a wardrobe, a fridge, a bunk bed, a stair
     -- is now in the way like a door, and is slid, moved or left out; the
     low ones (a sofa, a counter, a bed, a desk, a table) stand under the
     sill as before, and the sanitary pieces stay where the drawing has
     them (a shower under a bathroom window). */
  function _impWinBlocks(f) {
    if (!f || /^(bath|shower|wc|basin)/.test(String(f.kind || ''))) return false;
    var h = 0;
    try { h = _fixDef(f.kind) ? _fixHt(f) : 0; } catch (eH) { h = 0; }
    return h >= 1.2;
  }
  function _impClear(c, f) { return !c || (c.reason === 'window' && !_impWinBlocks(f)); }
  function _impKeepRank(k) {
    k = String(k || '');
    if (/^wc/.test(k)) return 6;
    if (/^(bath|shower|basin)/.test(k)) return 5;
    if (/^kitchen/.test(k)) return 4;
    if (/^(fridge|washer|dryer)/.test(k)) return 3;
    if (/^(bed|bunk|sofa|wardrobe|desk)/.test(k)) return 2;
    return 1;
  }
  /* v176: the L or U counter whose arm runs into a doorway (or over a wall)
     is shortened at that arm's free end, the corner kept, until it stands
     clear -- the arm that keeps more counter wins; null when neither does */
  function _impKitOffDoor(fk, Z) {
    var KS = IMP_KIT_SIZE[fk.kind];
    if (!KS) return null;
    var m = _fixWD(fk), w = m.w, d = m.d, r = (((fk.rot | 0) % 4) + 4) % 4, mi = fk.mirror ? -1 : 1, best = null, nw, nd;
    function at(nw2, nd2) {
      var du = -(w - nw2) / 2, dv = -(d - nd2) / 2;
      var g = { id: fk.id, kind: fk.kind, rot: fk.rot | 0, sizeW: nw2, sizeD: nd2,
                x: _impR(fk.x + du * mi * IMP_TAN[r][0] + dv * IMP_NRM[r][0]), z: _impR(fk.z + du * mi * IMP_TAN[r][1] + dv * IMP_NRM[r][1]) };
      if (fk.mirror) g.mirror = true;
      if (fk.st != null) g.st = fk.st;
      var c = null;
      try { c = _fitClash(g, _impFaces(g.x, g.z), _impZonesFor(g, Z), null, fk.id); } catch (eC) { c = { reason: 'error' }; }
      return _impClear(c, g) ? g : null;
    }
    if (fk.kind === 'kitchenL') {
      for (nw = _impKitStep(w - IMP_KIT_STEP); nw >= KS.w0 - 1e-6; nw = _impKitStep(nw - IMP_KIT_STEP)) { var gw = at(nw, d); if (gw) { best = { g: gw, keep: nw + d, what: 'its back arm ' + _impN(w) + ' -> ' + _impN(nw) }; break; } }
    }
    for (nd = _impKitStep(d - IMP_KIT_STEP); nd >= KS.d0 - 1e-6; nd = _impKitStep(nd - IMP_KIT_STEP)) {
      var gd = at(w, nd);
      if (gd) { if (!best || w + nd > best.keep) best = { g: gd, keep: w + nd, what: 'its side arm' + (fk.kind === 'kitchenU' ? 's' : '') + ' ' + _impN(d) + ' -> ' + _impN(nd) }; break; }
    }
    if (!best) return null;
    fk.x = best.g.x; fk.z = best.g.z; fk.sizeW = best.g.sizeW; fk.sizeD = best.g.sizeD;
    return fk.kind + ' in a doorway or over a wall -- ' + best.what + ' m, the corner kept';
  }
  function _impLastClear() {
    var H = _PH;
    if (!H || !H.fixturesRef) return '';
    var L = H.fixturesRef(), st = _fixStorey(), out = [], Z = [], i, j, guard = 0, again = true;
    if (!L || !L.length) return '';
    try { Z = _fitZones() || []; } catch (eZ) { Z = []; }
    function on(o) { var so = _fitStOf(o); return so == null || st == null || so === (st | 0); }
    function area(o) { var m = _fixWD(o); return m.w * m.d; }
    function shower(b, small) {
      var KS = small ? ['shower90'] : ['shower120', 'shower90'], q, r9;
      for (q = 0; q < KS.length; q++) {
        if (!_fixDef(KS[q])) continue;
        r9 = null;
        try { r9 = _fitPlace({ kind: KS[q], room: { x: b.x, z: b.z }, wall: 'auto', align: 'auto' }); } catch (eP) { r9 = null; }
        if (r9 && r9.ok && r9.f) {
          var g = { id: b.id, kind: KS[q], x: _impR(r9.f.x), z: _impR(r9.f.z), rot: r9.f.rot | 0 };
          if (b.st != null) g.st = b.st;
          return g;
        }
      }
      return null;
    }
    function drop(o, why) {
      var at = L.indexOf(o);
      if (at < 0) return;
      L.splice(at, 1);
      if (/^bath/.test(String(o.kind)) || o.kind === 'shower120') {
        var sh = shower(o, o.kind === 'shower120');
        if (sh) { L.push(sh); out.push(o.kind + ' ' + why + ' -- a ' + sh.kind + ' in its room instead'); return; }
      }
      out.push(o.kind + ' left out -- ' + why);
    }
    /* AN ISLAND WITH NO WAY TO THE COUNTER (v174, Marek: ostruvek se 4
       zidlemi v uzke kuchyni nedava smysl, je prakticky na kuchynske lince --
       ten by tam vubec nemel byt): under 0.90 m between an island and the
       kitchen counter, the island goes */
    function gapS(p, q) {
      var A = _impShapeRects(p), B = _impShapeRects(q), g = 1e9, x, y;
      for (x = 0; x < A.length; x++) for (y = 0; y < B.length; y++) {
        var dx = Math.max(0, B[y].x0 - A[x].x1, A[x].x0 - B[y].x1), dz = Math.max(0, B[y].z0 - A[x].z1, A[x].z0 - B[y].z1);
        g = Math.min(g, Math.hypot(dx, dz));
      }
      return g;
    }
    for (i = L.length - 1; i >= 0; i--) {
      var isl = L[i];
      if (!isl || !on(isl) || !/^island/.test(String(isl.kind))) continue;
      var gI = 1e9;
      for (j = 0; j < L.length; j++) { var kc = L[j]; if (kc && kc !== isl && on(kc) && /^kitchen/.test(String(kc.kind))) gI = Math.min(gI, gapS(isl, kc)); }
      if (gI < 0.90) { L.splice(i, 1); out.push(isl.kind + ' left out -- ' + _impN(gI) + ' m of way to the kitchen counter, 0.90 m wanted'); }
    }
    /* A WARDROBE NEEDS ROOM IN FRONT OF ITS DOORS (v191, plan 1000075604,
       Marek: ty skrine v rohu u postele by nemely byt, neni tam prostor):
       a wardrobe with another piece -- the bed, as a rule -- less than
       0.60 m in front of its doors is left out */
    for (i = L.length - 1; i >= 0; i--) {
      var wq = L[i];
      if (!wq || !on(wq) || !/^wardrobe/.test(String(wq.kind))) continue;
      var mW = _fixWD(wq), rW = (((wq.rot | 0) % 4) + 4) % 4, ux = IMP_NRM[rW][0], uz = IMP_NRM[rW][1], tx = IMP_TAN[rW][0];
      var fcx = wq.x + ux * (mW.d / 2 + 0.30), fcz = wq.z + uz * (mW.d / 2 + 0.30);
      var hwF = mW.w / 2 - 0.05, hdF = 0.29;
      var FR = Math.abs(tx) > 0.5 ? { x0: fcx - hwF, x1: fcx + hwF, z0: fcz - hdF, z1: fcz + hdF } : { x0: fcx - hdF, x1: fcx + hdF, z0: fcz - hwF, z1: fcz + hwF };
      var blk = null;
      for (j = 0; j < L.length && !blk; j++) {
        var gq2 = L[j];
        if (!gq2 || gq2 === wq || !on(gq2) || gq2.stack || /^wardrobe/.test(String(gq2.kind))) continue;
        if (_fitHit(FR, _fixRect(gq2), 0.01)) blk = gq2;
      }
      if (blk) { L.splice(i, 1); out.push(wq.kind + ' left out -- the ' + blk.kind + ' stands less than 0.60 m in front of its doors (v191)'); }
    }
    while (again && guard++ < 60) {
      again = false;
      for (i = 0; i < L.length && !again; i++) {
        var a = L[i];
        if (!a || !on(a) || a.stack) continue;
        for (j = i + 1; j < L.length; j++) {
          var b = L[j];
          if (!b || !on(b) || b.stack) continue;
          if (!_fitHit(_fixRect(a), _fixRect(b), 0.01) || !_impShapeHit(a, b)) continue;
          /* v178: an appliance at the end of an L/U arm -- the arm ends before it */
          var kL = /^kitchen[LU]$/.test(String(a.kind)) ? a : (/^kitchen[LU]$/.test(String(b.kind)) ? b : null), oL = (kL === a) ? b : a;
          if (kL && /^(fridge|washer|dryer)/.test(String(oL.kind))) {
            var kSL = null;
            try { kSL = _impKitShrink(kL); } catch (eKL) { kSL = null; }
            if (kSL && !(_fitHit(_fixRect(kL), _fixRect(oL), 0.01) && _impShapeHit(kL, oL))) { out.push(kSL + ' -- the ' + oL.kind + ' kept (v178)'); again = true; break; }
          }
          var ra = _impKeepRank(a.kind), rb = _impKeepRank(b.kind);
          var dq = (ra < rb || (ra === rb && area(a) <= area(b))) ? a : b, kp = (dq === a) ? b : a;
          drop(dq, 'it stood on the ' + kp.kind);
          again = true;
          break;
        }
      }
    }
    for (i = L.length - 1; i >= 0; i--) {
      var f = L[i];
      if (!f || !on(f) || f.stack) continue;
      var c = null;
      try { c = _fitClash(f, _impFaces(f.x, f.z), _impZonesFor(f, Z), null, f.id); } catch (eC) { c = null; }
      if (!c || (c.reason !== 'door' && c.reason !== 'size' && !(c.reason === 'window' && _impWinBlocks(f)))) continue;
      var rk = _impKeepRank(f.kind), wh = (c.reason === 'door') ? 'in a doorway or a door\'s swing' : ((c.reason === 'window') ? 'in front of a window' : 'over a wall');
      /* v176: an L or U counter in a doorway gives up the end of the arm
         that runs into it (its corner stays, 5 cm steps, the catalogue's
         least); a wide basin becomes the narrow one where that clears */
      if (/^kitchen[LU]$/.test(String(f.kind))) { var ks = _impKitOffDoor(f, Z); if (ks) { out.push(ks); continue; } }
      if (f.kind === 'basin80' && _fixDef('basin50')) {
        var b5 = { id: f.id, kind: 'basin50', x: f.x, z: f.z, rot: f.rot | 0 }, c5 = null;
        if (f.st != null) b5.st = f.st;
        try { c5 = _fitClash(b5, _impFaces(b5.x, b5.z), _impZonesFor(b5, Z), null, f.id); } catch (eC5) { c5 = { reason: 'error' }; }
        if (_impClear(c5, b5)) { f.kind = 'basin50'; out.push('basin80 ' + wh + ' -- the basin50 in its place clears it'); continue; }
      }
      /* v178 (plan 1000076010, the library refused it for "a desk in a door's
         swing"): a desk, an appliance or a smaller piece first finds a free
         place in its own room (the placer, any wall); with none it goes */
      if (rk === 3 || rk === 1 || /^desk/.test(String(f.kind)) || (c.reason === 'window' && /^wardrobe/.test(String(f.kind)))) {
        var rp = null, cp = null;
        try { rp = _fitPlace({ kind: f.kind, room: { x: f.x, z: f.z }, wall: 'auto', align: 'auto', id: f.id }); } catch (eRp) { rp = null; }
        if (rp && rp.ok && rp.f) {
          var gp = { id: f.id, kind: f.kind, x: _impR(rp.f.x), z: _impR(rp.f.z), rot: rp.f.rot | 0 };
          if (f.st != null) gp.st = f.st;
          try { cp = _fitClash(gp, _impFaces(gp.x, gp.z), _impZonesFor(gp, Z), null, f.id); } catch (eCp) { cp = { reason: 'error' }; }
          if (_impClear(cp, gp)) {
            var mv = Math.hypot(gp.x - f.x, gp.z - f.z);
            f.x = gp.x; f.z = gp.z; f.rot = gp.rot;
            out.push(f.kind + ' ' + wh + ' -- moved ' + _impN(mv) + ' m to a free place in its room');
            continue;
          }
        }
      }
      /* a bed or a sofa stays (an empty bedroom tells nothing) -- the library
         says it is in the way */
      if (rk === 3 || rk === 1 || /^(bath|wardrobe|desk)/.test(String(f.kind))) drop(f, wh);
    }
    /* A WASHER IN THE LIVING ROOM (v180, plan 1000075604: a closet's folding
       door read as a washer stood in the living room, and the library
       refused "a washer not in a laundry"). A washer or a dryer outside a
       laundry, a bathroom, a hall, a store, a kitchen or a closet goes to a
       room of those kinds within 1.50 m where it stands clear; with none,
       it goes. */
    var WOK = /laundry|bathroom|rooms\.wc|container_room_6|hall|storage|kitchen|mudroom|pantry/, RMW = [];
    try { RMW = _fitRooms() || []; } catch (eRW) { RMW = []; }
    function roomOfW(x, z) { for (var q9 = 0; q9 < RMW.length; q9++) { var F9 = RMW[q9].F; if (x >= F9.x0 - 0.02 && x <= F9.x1 + 0.02 && z >= F9.z0 - 0.02 && z <= F9.z1 + 0.02) return RMW[q9]; } return null; }
    for (i = L.length - 1; i >= 0; i--) {
      var fw = L[i];
      if (!fw || !on(fw) || !/^(washer|dryer)/.test(String(fw.kind))) continue;
      var rmW = roomOfW(fw.x, fw.z);
      if (!rmW || WOK.test(String(rmW.key || ''))) continue;
      var cand = RMW.filter(function (r9) {
        if (r9 === rmW || !WOK.test(String(r9.key || ''))) return false;
        var dx9 = Math.max(0, r9.F.x0 - fw.x, fw.x - r9.F.x1), dz9 = Math.max(0, r9.F.z0 - fw.z, fw.z - r9.F.z1);
        return Math.hypot(dx9, dz9) <= 1.50;
      }), moved = false, cq;
      for (cq = 0; cq < cand.length && !moved; cq++) {
        var rpW = null, cpW = null, Fc = cand[cq].F;
        try { rpW = _fitPlace({ kind: fw.kind, room: { x: (Fc.x0 + Fc.x1) / 2, z: (Fc.z0 + Fc.z1) / 2 }, wall: 'auto', align: 'auto', id: fw.id }); } catch (eWp) { rpW = null; }
        if (!rpW || !rpW.ok || !rpW.f) continue;
        var gW = { id: fw.id, kind: fw.kind, x: _impR(rpW.f.x), z: _impR(rpW.f.z), rot: rpW.f.rot | 0 };
        if (fw.st != null) gW.st = fw.st;
        try { cpW = _fitClash(gW, _impFaces(gW.x, gW.z), _impZonesFor(gW, Z), null, fw.id); } catch (eCw) { cpW = { reason: 'error' }; }
        if (!_impClear(cpW, gW)) continue;
        var nmW = String(cand[cq].key || 'room').replace(/^rooms\./, '');
        fw.x = gW.x; fw.z = gW.z; fw.rot = gW.rot;
        out.push(fw.kind + ' in the ' + String(rmW.key || 'room').replace(/^rooms\./, '') + ' -- moved to the ' + nmW);
        moved = true;
      }
      if (!moved) drop(fw, 'not in a laundry, a bathroom or a hall (' + String(rmW.key || 'room').replace(/^rooms\./, '') + ') and no place for it in one near');
    }
    return out.length ? ('LAST (v173): ' + out.join(' | ')) : '';
  }
  /* the project as it was, by the same rule the core's Undo uses */
  function _impPutBack(json) {
    var C = _impCore(); if (!C || !json) return false;
    var p = JSON.parse(json), S = C.STATE, k;
    for (k in p) { if (k !== 'v' && p.hasOwnProperty(k) && S.hasOwnProperty(k)) S[k] = p[k]; }
    try { C.savePref(); } catch (e1) {}
    try { C.rebuild(); } catch (e2) {}
    return true;
  }
  function _impRestore() {
    var src = _imp.before, k;
    if (!src) {
      k = _impKept();
      if (k) { src = k.before; _imp.digBefore = k.dig; _imp.digAfter = k.digAfter; }
    }
    if (!src) return false;
    var ok = _impPutBack(src);
    try { var C = _impCore(); if (ok && C && C.undoPush) C.undoPush(); } catch (e) {}
    if (ok) _impKeepClear();
    return ok;
  }

  function _impFitLines(f) {
    var L = [];
    if (!f.ok) { L.push('PLAN fit: NOT WRITABLE (' + f.why + ') -- ' + f.notes.join('; ')); return L; }
    L.push('PLAN fit: ' + _impN(f.from.w) + ' x ' + _impN(f.from.d) + (f.turned ? ' turned a quarter' : ' as drawn') + ' -> ' + _impN(f.w) + ' across x ' + _impN(f.l) +
           ' long, centred at ' + _impN(f.ox) + ',' + _impN(f.oz) + ' | storey ' + (f.storey + 1));
    L.push('PLAN fit partitions ' + f.parts.length + ': ' + f.parts.map(function (p) {
      function en(v, t, to) { return (t.t === 'shell') ? 'shell' : (t.t === 'part' ? ('#' + to) : _impN(v)); }
      return '#' + p.id + ' ' + p.o + ' ' + (p.o === 'v' ? 'x' : 'z') + _impN(p.c) + ' ' + en(p.s, p.es, p.sTo) + '..' + en(p.e, p.ee, p.eTo) + (p.sep ? ' ROOM LINE' : '');
    }).join(' | '));
    L.push('PLAN fit doors ' + f.doors.length + ': ' + f.doors.map(function (d) { return 'in #' + d.pid + ' at ' + _impN(d.u) + ' w ' + _impN(d.w) + _impDoorKind(d); }).join(' | '));
    L.push('PLAN fit openings ' + f.openings.length + ': ' + f.openings.map(function (o) {
      return o.seg + ' at ' + _impN(o.at) + ' ' + (o.code || o.kind) + ' ' + _impN(o.w) + 'x' + _impN(o.h) + (o.sill ? (' sill ' + _impN(o.sill)) : '') + (o.out ? ' OPENS OUT' : '');
    }).join(' | '));
    L.push('PLAN fit room names ' + f.pins.length + ': ' + f.pins.map(function (q) { return q.k.replace(/^rooms\./, '') + ' ' + _impN(q.x) + ',' + _impN(q.z); }).join(' | ') +
           ((f.closets && f.closets.length) ? (' || closet marks ' + f.closets.length + ': ' + f.closets.map(function (c) { return '"' + c.label + '" ' + _impN(c.x) + ',' + _impN(c.z); }).join(' | ')) : ''));
    if (f.outdoor && f.outdoor.length) L.push('PLAN fit outdoor ' + f.outdoor.length + ': ' + f.outdoor.map(function (q) {
      return '"' + q.label + '" ' + q.type + ' ' + _impN(q.x1 - q.x0) + ' x ' + _impN(q.z1 - q.z0) + ' at ' + _impN((q.x0 + q.x1) / 2) + ',' + _impN((q.z0 + q.z1) / 2) + ' (house ' + _impN(f.x0) + '..' + _impN(f.x1) + ' x ' + _impN(f.z0) + '..' + _impN(f.z1) + ')';
    }).join(' | '));
    if (f.diag && f.diag.length) L.push('PLAN fit slanted walls ' + f.diag.length + ': ' + f.diag.map(function (g) {
      return _impN(g.a[0]) + ',' + _impN(g.a[1]) + ' -> ' + _impN(g.b[0]) + ',' + _impN(g.b[1]) + ' ' + g.ang + ' deg' + ((g.doors && g.doors.length) ? (' + ' + g.doors.length + ' door(s)') : '');
    }).join(' | '));
    L.push('PLAN fit changed: ' + (f.notes.length ? f.notes.join('; ') : 'nothing'));
    return L;
  }
  /* THE PLAN CHECKED AS BUILT (v135, Marek: v nabidce draweru nechat jen
     plany, ktere budou korektne postavene -- agent by mel mit jistotu, ze tam
     nejsou nesmysly). Once the plan is written, the house itself is asked:
       TINY      a room under 2 m2 that is not a closet, a hall or a WC;
       NO WAY IN a room or a closet no door, passage or room line reaches
                 from a door to the outside;
       NO DOOR OUT nothing opens to the outside at all;
       WINDOW    a bedroom with no window;
       T         a door standing across a wall that meets its wall;
       SWING     two hinged doors whose leaves strike each other.
     No problem: the plan is fit for the plan library. The answer is kept
     with the reading for the library to come, and said in one log line. */
  /* A DOOR IS A DOOR BY ITS KIND (v177, plan 1002 on the RANCH block
     house, Marek: novy import vygeneroval plan, ktery se opet nepublikoval).
     The core stands a door of a masonry house on the finished floor -- its
     sill is the floor build-up, +300 on the RANCH block house -- so a test
     for "a sill on the slab" called both outer doors windows: no door to
     the outside, no door out to the terrace, and the library refused the
     plan. A door, French doors, a garage door, a portal are doors whatever
     their sill; anything else with its sill on the slab is one too. */
  function _impOpDoor(o) {
    if (!o) return false;
    if (/^(door|french|garage|portal|glass_door|sliding_door)$/.test(String(o.kind || ''))) return true;
    return (o.sill || 0) <= 0.05;
  }
  function _impAudit(S) {
    var H = _PH, P = [], i, k, q;
    if (!H) return { ok: false, problems: ['no plan host'] };
    var R = _roomsTracked(), pins = Array.isArray(S.roomNames) ? S.roomNames : [], DL = _doorsLive(), PS = H.partitions() || [];
    var WT = H.wallThk ? H.wallThk() : 0.16;
    function roomAt(x, z) { for (var r = 0; r < R.length; r++) if (_roomHolds(R[r], x, z)) return r; return -1; }
    function keyOf(r) {
      for (var j = 0; j < pins.length; j++) if (pins[j] && typeof pins[j].x === 'number' && _roomHolds(R[r], pins[j].x, pins[j].z)) return String(pins[j].k || '');
      return '';
    }
    function nameOf(r) { var kq = keyOf(r); return R[r].closet ? 'closet' : (kq ? kq.replace(/^rooms\./, '').replace(/^container_room_\d+$/, 'room') : 'room'); }
    var KEY = R.map(function (r9, j9) { return keyOf(j9); });
    /* the ways between the rooms */
    var adj = R.map(function () { return []; });
    function link(a, b) { if (a >= 0 && b >= 0 && a !== b) { adj[a].push(b); adj[b].push(a); } }
    for (i = 0; i < DL.length; i++) {
      var d = DL[i].d, p = DL[i].p, cx = p.o === 'h' ? d.u : p.c, cz = p.o === 'h' ? p.c : d.u;
      link(roomAt(p.o === 'h' ? cx : cx - 0.15, p.o === 'h' ? cz - 0.15 : cz), roomAt(p.o === 'h' ? cx : cx + 0.15, p.o === 'h' ? cz + 0.15 : cz));
    }
    var WETOPEN = {};
    for (i = 0; i < PS.length; i++) {
      var sp = PS[i];
      if (!sp || !sp.sep) continue;
      var span = _partSpan(sp);
      if (!span) continue;
      for (k = 1; k <= 3; k++) {
        var al = span[0] + (span[1] - span[0]) * k / 4;
        var ra = roomAt(sp.o === 'h' ? al : sp.c - 0.15, sp.o === 'h' ? sp.c - 0.15 : al), rb = roomAt(sp.o === 'h' ? al : sp.c + 0.15, sp.o === 'h' ? sp.c + 0.15 : al);
        link(ra, rb);
        /* v177: a bathroom open to another room, no wall and no door (plan
           1000076010: published so) */
        if (ra >= 0 && rb >= 0 && ra !== rb) {
          var wa = /bathroom|rooms\.wc/.test(KEY[ra]), wb = /bathroom|rooms\.wc/.test(KEY[rb]);
          if (wa !== wb && !R[wa ? rb : ra].closet) WETOPEN[(wa ? nameOf(ra) : nameOf(rb)) + ' open to the ' + (wa ? nameOf(rb) : nameOf(ra))] = 1;
        }
      }
    }
    for (var wo9 in WETOPEN) if (WETOPEN.hasOwnProperty(wo9)) P.push('a ' + wo9 + ', no door');
    /* the doors in slanted walls */
    var DG = (S.product && S.product.diagWalls) || [];
    for (i = 0; i < DG.length; i++) {
      var g = DG[i], ga = g.a, gb = g.b;
      if (!ga || !gb || !g.doors) continue;
      var gl = Math.hypot(gb[0] - ga[0], gb[1] - ga[1]);
      if (!(gl > 0)) continue;
      var ux = (gb[0] - ga[0]) / gl, uz = (gb[1] - ga[1]) / gl;
      for (k = 0; k < g.doors.length; k++) {
        var gx = ga[0] + ux * g.doors[k].u, gz = ga[1] + uz * g.doors[k].u;
        link(roomAt(gx - uz * 0.2, gz + ux * 0.2), roomAt(gx + uz * 0.2, gz - ux * 0.2));
      }
    }
    /* the doors and the windows in the outer walls */
    var C = _impCore(), SG = [], OUT = [], WIN = {}, OUTP = [];
    try { SG = (C && C.segments) ? (C.segments() || []) : []; } catch (eS) { SG = []; }
    for (i = 0; i < SG.length; i++) {
      var sg = SG[i];
      if (!sg || !sg.openings || !sg.len) continue;
      var sx = (sg.x1 - sg.x0) / sg.len, sz = (sg.z1 - sg.z0) / sg.len;
      for (k = 0; k < sg.openings.length; k++) {
        var o = sg.openings[k], wx = sg.x0 + sx * o.at, wz = sg.z0 + sz * o.at, rIn = -1, e;
        /* v176: in a block house (planOut) the segment may stand on the outer
           face -- the room starts a whole block further in */
        /* v177: and further -- every 10 cm from the wall's line to 1.5 m in,
           both ways, the first room found (a block house's wall and its
           outer face put the room further in than one timber wall) */
        var PRB = [WT + 0.25, WT + 2 * _impOut() + 0.25], pq9;
        for (pq9 = 0.1; pq9 <= 1.5001; pq9 += 0.1) PRB.push(pq9);
        for (pq9 = 0; pq9 < PRB.length && rIn < 0; pq9++) for (e = -1; e <= 1 && rIn < 0; e += 2) rIn = roomAt(wx - sz * e * PRB[pq9], wz + sx * e * PRB[pq9]);
        if (_impOpDoor(o)) OUTP.push(sg.id + '@' + wx.toFixed(2) + ',' + wz.toFixed(2) + '->' + rIn);
        if (rIn < 0) continue;
        if (_impOpDoor(o)) OUT.push(rIn); else WIN[rIn] = true;
        /* v144: a glazed door -- a slider, an HS, French doors -- is the room's window as well */
        if (_impOpDoor(o) && (o.kind === 'french' || o.kind === 'glass_door' || o.kind === 'sliding_door')) WIN[rIn] = true;
      }
    }
    /* who is reached from outside */
    var seen = R.map(function () { return false; }), Q = OUT.slice();
    for (i = 0; i < Q.length; i++) seen[Q[i]] = true;
    while (Q.length) { var r0 = Q.shift(); for (k = 0; k < adj[r0].length; k++) if (!seen[adj[r0][k]]) { seen[adj[r0][k]] = true; Q.push(adj[r0][k]); } }
    if (!OUT.length) P.push('no door to the outside' + (OUTP.length ? (' (doors in the outer walls, none reaching a room: ' + OUTP.slice(0, 4).join(' ') + ')') : (' (no door in the ' + SG.length + ' outer wall(s); ' + ((S.openings || []).length) + ' opening(s) in the project)')));
    for (i = 0; i < R.length; i++) {
      var nm = nameOf(i), ar = R[i].area || 0, kk = KEY[i];
      if (!R[i].closet && ar < 2.0 && !/hall|entry|wc/.test(kk)) P.push('tiny room "' + nm + '" ' + _impN(ar) + ' m2');
      if (OUT.length && !seen[i]) P.push('no way into "' + nm + '" ' + _impN(ar) + ' m2');
      if (/bedroom|kids|container_room_7/.test(kk) && !WIN[i]) P.push('bedroom "' + nm + '" has no window');
    }
    /* TWO ROOMS IN ONE (v140, Marek: agent by mel vedet, ze tam ta mistnost
       je, a nepustit do produkce pudorys, ktery nema uzavrenou mistnost). The
       master bath of plan 1000073500 had no wall to the bedroom and the audit
       came out clean: one room, two names. A bedroom, a bathroom, a WC, an
       office or a closet whose name stands in a room with another name has
       lost a wall; the living room, the kitchen, the dining room and the
       halls may share one. */
    var SHUTK = /bedroom|kids|guest|bathroom|rooms\.wc|office|storage|laundry|pantry|wardrobe|closet|utility|technical|container_room_7/, CELL = R.map(function () { return []; });
    for (i = 0; i < pins.length; i++) {
      var pn = pins[i];
      if (!pn || typeof pn.x !== 'number' || !pn.k) continue;
      var ri9 = roomAt(pn.x, pn.z);
      if (ri9 >= 0 && !R[ri9].closet) CELL[ri9].push(String(pn.k));
    }
    for (i = 0; i < R.length; i++) {
      if (CELL[i].length < 2) continue;
      var shut = CELL[i].filter(function (k9) { return SHUTK.test(k9); });
      if (shut.length) P.push('two rooms in one: ' + CELL[i].map(function (k9) { return '"' + k9.replace(/^rooms\./, '') + '"'; }).join(' + ') + ' ' + _impN(R[i].area || 0) + ' m2 -- a wall is missing');
    }
    /* a door across a T */
    for (i = 0; i < DL.length; i++) {
      var dT = DL[i].d, pT = DL[i].p, w = dT.w || 0.8;
      for (k = 0; k < PS.length; k++) {
        var cT = PS[k];
        if (!cT || cT.sep || cT.o === pT.o || cT.id === pT.id) continue;
        var spT = _partSpan(cT);
        if (!spT || pT.c < spT[0] - 0.06 || pT.c > spT[1] + 0.06) continue;
        if (cT.c > dT.u - w / 2 + 0.04 && cT.c < dT.u + w / 2 - 0.04) { P.push('a door ' + _impN(w) + ' m across the wall meeting its wall at ' + _impN(cT.c)); break; }
      }
    }
    /* two leaves on the same floor */
    function swingOf(d9, p9) {
      if (d9.pass || d9.slide || d9.kind) return null;
      var sw = d9.sw | 0, nA = (p9.o === 'h') ? ((sw & 2) ? 1 : -1) : ((sw & 2) ? -1 : 1), hiE = sw & 1, wd = d9.w || 0.8;
      return { o: p9.o, c: p9.c, h: hiE ? d9.u + wd / 2 : d9.u - wd / 2, tow: hiE ? -1 : 1, R: wd, nA: nA };
    }
    function inSw(A, x, z) {
      var al2 = A.o === 'h' ? x : z, ac2 = A.o === 'h' ? z : x, da = (al2 - A.h) * A.tow, dc = (ac2 - A.c) * A.nA;
      return da >= -0.02 && dc >= 0.02 && Math.hypot(da, dc) <= A.R + 0.02;
    }
    function pts(A) {
      var out = [], r2, th;
      for (r2 = 0.3; r2 < 1; r2 += 0.3) for (th = 10; th <= 85; th += 15) {
        var a2 = A.h + A.tow * A.R * r2 * Math.cos(th * Math.PI / 180), c2 = A.c + A.nA * A.R * r2 * Math.sin(th * Math.PI / 180);
        out.push(A.o === 'h' ? [a2, c2] : [c2, a2]);
      }
      return out;
    }
    for (i = 0; i < DL.length; i++) for (k = i + 1; k < DL.length; k++) {
      var A1 = swingOf(DL[i].d, DL[i].p), B1 = swingOf(DL[k].d, DL[k].p), hit = false;
      if (!A1 || !B1) continue;
      var PA = pts(A1), PB = pts(B1);
      for (q = 0; q < PA.length && !hit; q++) if (inSw(B1, PA[q][0], PA[q][1])) hit = true;
      for (q = 0; q < PB.length && !hit; q++) if (inSw(A1, PB[q][0], PB[q][1])) hit = true;
      if (hit) P.push('two doors strike each other (' + _impN(DL[i].d.w) + ' and ' + _impN(DL[k].d.w) + ' m)');
    }
    var res9 = { ok: !P.length, problems: P, rooms: R.length, at: Date.now() };
    if (global.__impLeafDebug) res9.dbg = 'PS ' + PS.map(function (p9) { var sp9 = _partSpan(p9); return p9.o + p9.c.toFixed(2) + (sp9 ? ('[' + sp9[0].toFixed(2) + '..' + sp9[1].toFixed(2) + ']') : '[-]') + (p9.sep ? 'S' : ''); }).join(' ') +
                                         ' | OUTPTS ' + OUTP.join(' ') + ' | OUT ' + OUT.map(nameOf).join(',') + ' | links ' + adj.map(function (a9, j9) { return nameOf(j9) + ':' + a9.map(nameOf).join('/'); }).join(' ; ') +
                                         ' | doors ' + DL.length + ' segs ' + SG.length + ' rects ' + R.map(function (r9) { return r9.rects.map(function (c9) { return c9.x0.toFixed(1) + '..' + c9.x1.toFixed(1) + 'x' + c9.z0.toFixed(1) + '..' + c9.z1.toFixed(1); }).join('+'); }).join(' | ');
    return res9;
  }
  function _impApplyLines(r) {
    var L = [];
    if (!r.ok) { L.push('PLAN apply: NOT WRITTEN (' + r.why + ') -- ' + r.notes.join('; ')); return L; }
    L.push('PLAN applied: was ' + _impSumTxt(r.was) + ' -> now ' + _impSumTxt(r.now) + ' | undo steps ' + r.steps);
    L.push('PLAN apply changed: ' + (r.notes.length ? r.notes.join('; ') : 'nothing'));
    if (r.audit) L.push('PLAN audit (v135): ' + (r.audit.ok ? ('clean, ' + r.audit.rooms + ' rooms -- the plan library checks it further')
                                                          : (r.audit.problems.length + ' problem(s) -- not for the plan library: ' + r.audit.problems.join('; '))) + (r.audit.dbg ? (' DBG ' + r.audit.dbg) : ''));
    L.push(r.kept ? ('PLAN kept: a copy of the project before the import is on this device -- RESTORE puts it back even after a reload' +
                     (r.keptP ? ('; it was last saved as ?p=' + r.keptP + ', and that link still opens it') : ''))
                  : ('PLAN kept: NO copy on this device (storage blocked or full) -- Undo works only until the page is reloaded' +
                     (r.keptP ? ('; the link ?p=' + r.keptP + ' still opens the project as it was') : '')));
    return L;
  }
  function _impNowLine() {
    var d = _impDigest();
    if (_imp.restored && d === _imp.digBefore) return 'PLAN now: RESTORE put the project back as it was before the import (same, right)';
    if (d === _imp.digAfter) {
      /* a reload opens whatever link the address holds, and the new one
         comes a moment after the save -- so say whether it has come yet */
      var pn = _impShareId(), pk = (_imp.kept && _imp.kept.p) || '';
      return 'PLAN now: the imported plan -- check it in 2D and 3D, then tap Undo (or RESTORE here)' +
             ((pk && pn === pk) ? (' | the address still holds ?p=' + pk + ', saved BEFORE the import -- a reload now would open the old project; wait for a new link')
                                : (pn ? (' | saved as ?p=' + pn + ' -- a reload opens the imported plan') : ''));
    }
    if (d === _imp.digBefore) return 'PLAN now: back to the project as it was before the import (same, right -- Undo worked)';
    var what = [];
    try {
      var A2 = JSON.parse(d), B2 = JSON.parse(_imp.digAfter || '{}'), k2;
      var NAMES = { f: 'size', o: 'position', wg: 'wing', u: 'units', pg: 'outline', p: 'partitions', d: 'doors', op: 'windows and outer doors', fx: 'fitout pieces' };
      for (k2 in NAMES) {
        if (!NAMES.hasOwnProperty(k2)) continue;
        if (JSON.stringify(A2[k2]) !== JSON.stringify(B2[k2])) {
          var na = Array.isArray(A2[k2]) && k2 !== 'f' && k2 !== 'o' ? (' ' + (B2[k2] ? B2[k2].length : 0) + ' -> ' + A2[k2].length) : '';
          what.push(NAMES[k2] + na);
        }
      }
    } catch (eW) {}
    return 'PLAN now: changed since the import, and not back to before it either' + (what.length ? (' -- changed: ' + what.join(', ')) : '');
  }

  /* ---------- THE PREVIEW -------------------------------------------------
     Drawn over the plan in violet. A plan only read is centred on the house
     that is there now, so the two can be compared; a plan FITTED is drawn
     exactly where it will be written. It changes nothing; preview(null)
     takes it away. */
  function _impDraw(ctx, X, Y, ppm) {
    /* v125: the project's slanted walls, whenever the plan is drawn */
    try { _impDiagDraw(ctx, X, Y, ppm); } catch (eDg) {}
    var n = _imp.prev, H = _PH, i;
    if (!n || !n.ok || !H) return;
    if (!_imp.drawn) { _imp.drawn = true; try { _fitShow(); } catch (eS) {} }
    var VIO = '#7B1FA2';
    ctx.save();
    ctx.lineJoin = 'miter'; ctx.lineCap = 'butt';
    if (ctx.setLineDash) ctx.setLineDash([]);
    if (n.core) { _impDrawFit(ctx, X, Y, n, VIO); ctx.restore(); return; }
    var P = H.poly() || [], bx0 = 1e9, bx1 = -1e9, bz0 = 1e9, bz1 = -1e9;
    for (i = 0; i < P.length; i++) { bx0 = Math.min(bx0, P[i][0]); bx1 = Math.max(bx1, P[i][0]); bz0 = Math.min(bz0, P[i][1]); bz1 = Math.max(bz1, P[i][1]); }
    var ox = P.length ? (bx0 + bx1) / 2 - n.w / 2 : 0, oz = P.length ? (bz0 + bz1) / 2 - n.d / 2 : 0;
    function XX(v) { return X(v + ox); }
    function YY(v) { return Y(v + oz); }
    ctx.strokeStyle = VIO; ctx.lineWidth = 3;
    ctx.beginPath();
    for (i = 0; i < n.outline.length; i++) {
      if (i) ctx.lineTo(XX(n.outline[i][0]), YY(n.outline[i][1])); else ctx.moveTo(XX(n.outline[i][0]), YY(n.outline[i][1]));
    }
    ctx.closePath(); ctx.stroke();
    ctx.lineWidth = 2;
    for (i = 0; i < n.partitions.length; i++) {
      var p = n.partitions[i];
      ctx.beginPath();
      if (p.o === 'v') { ctx.moveTo(XX(p.c), YY(p.s)); ctx.lineTo(XX(p.c), YY(p.e)); }
      else { ctx.moveTo(XX(p.s), YY(p.c)); ctx.lineTo(XX(p.e), YY(p.c)); }
      ctx.stroke();
    }
    if (n.diag && n.diag.length && ctx.setLineDash) {
      ctx.setLineDash([5, 4]);
      for (i = 0; i < n.diag.length; i++) {
        ctx.beginPath(); ctx.moveTo(XX(n.diag[i].a[0]), YY(n.diag[i].a[1])); ctx.lineTo(XX(n.diag[i].b[0]), YY(n.diag[i].b[1])); ctx.stroke();
      }
      ctx.setLineDash([]);
    }
    /* doors orange, windows blue, each as a bar across its opening */
    ctx.lineWidth = 6;
    for (i = 0; i < n.doors.length; i++) {
      var d = n.doors[i], pp = n.partitions[d.part];
      if (!pp) continue;
      ctx.strokeStyle = '#EF6C00';
      ctx.beginPath();
      if (pp.o === 'v') { ctx.moveTo(XX(pp.c), YY(d.u - d.w / 2)); ctx.lineTo(XX(pp.c), YY(d.u + d.w / 2)); }
      else { ctx.moveTo(XX(d.u - d.w / 2), YY(pp.c)); ctx.lineTo(XX(d.u + d.w / 2), YY(pp.c)); }
      ctx.stroke();
    }
    for (i = 0; i < n.openings.length; i++) {
      var o = n.openings[i], a = n.outline[(o.side + n.outline.length - 1) % n.outline.length], b = n.outline[o.side];
      var L = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1, ux = (b[0] - a[0]) / L, uz = (b[1] - a[1]) / L;
      var c0 = o.off - o.w / 2, c1 = o.off + o.w / 2;
      ctx.strokeStyle = (o.kind === 'door') ? '#EF6C00' : '#1E88E5';
      ctx.beginPath();
      ctx.moveTo(XX(a[0] + ux * c0), YY(a[1] + uz * c0)); ctx.lineTo(XX(a[0] + ux * c1), YY(a[1] + uz * c1));
      ctx.stroke();
    }
    ctx.fillStyle = VIO;
    ctx.font = '700 11px Montserrat, Arial';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    for (i = 0; i < n.rooms.length; i++) {
      var r = n.rooms[i];
      ctx.fillText(r.key ? r.key.replace(/^rooms\./, '').replace(/^container_room_7$/, 'kids') : ('? ' + r.label), XX(r.x), YY(r.z));
    }
    ctx.restore();
  }
  /* the fitted plan, in the plan's own coordinates: where it will be */
  function _impDrawFit(ctx, X, Y, f, VIO) {
    var i, WT = f.wt;
    ctx.strokeStyle = VIO; ctx.lineWidth = 3;
    ctx.strokeRect(X(f.x0), Y(f.z0), X(f.x1) - X(f.x0), Y(f.z1) - Y(f.z0));
    ctx.lineWidth = 2;
    for (i = 0; i < f.parts.length; i++) {
      var p = f.parts[i], s = (p.s != null) ? p.s : ((p.o === 'v') ? f.z0 + WT : f.x0 + WT), e = (p.e != null) ? p.e : ((p.o === 'v') ? f.z1 - WT : f.x1 - WT);
      if (p.sep && ctx.setLineDash) ctx.setLineDash([8, 6]);
      ctx.beginPath();
      if (p.o === 'v') { ctx.moveTo(X(p.c), Y(s)); ctx.lineTo(X(p.c), Y(e)); }
      else { ctx.moveTo(X(s), Y(p.c)); ctx.lineTo(X(e), Y(p.c)); }
      ctx.stroke();
      if (p.sep && ctx.setLineDash) ctx.setLineDash([]);
    }
    ctx.lineWidth = 6;
    var byId = {};
    for (i = 0; i < f.parts.length; i++) byId[f.parts[i].id] = f.parts[i];
    for (i = 0; i < f.doors.length; i++) {
      var d = f.doors[i], pp = byId[d.pid];
      if (!pp) continue;
      ctx.strokeStyle = '#EF6C00';
      ctx.beginPath();
      if (pp.o === 'v') { ctx.moveTo(X(pp.c), Y(d.u - d.w / 2)); ctx.lineTo(X(pp.c), Y(d.u + d.w / 2)); }
      else { ctx.moveTo(X(d.u - d.w / 2), Y(pp.c)); ctx.lineTo(X(d.u + d.w / 2), Y(pp.c)); }
      ctx.stroke();
    }
    /* the walls as the core numbers them: W0 along z0 from x0, W1 along x1
       from z0, W2 along z1 from x1, W3 along x0 from z1 */
    var W = [[f.x0, f.z0, 1, 0], [f.x1, f.z0, 0, 1], [f.x1, f.z1, -1, 0], [f.x0, f.z1, 0, -1]];
    for (i = 0; i < f.openings.length; i++) {
      var o = f.openings[i], w = W[o.wall], c0 = o.at - o.w / 2, c1 = o.at + o.w / 2;
      ctx.strokeStyle = (o.kind === 'window') ? '#1E88E5' : '#EF6C00';
      ctx.beginPath();
      ctx.moveTo(X(w[0] + w[2] * c0), Y(w[1] + w[3] * c0)); ctx.lineTo(X(w[0] + w[2] * c1), Y(w[1] + w[3] * c1));
      ctx.stroke();
    }
    /* the slanted walls (v114): dashed, as they are shown and not built */
    if (f.diag && f.diag.length) {
      ctx.strokeStyle = VIO; ctx.lineWidth = 3;
      if (ctx.setLineDash) ctx.setLineDash([5, 4]);
      for (i = 0; i < f.diag.length; i++) {
        ctx.beginPath(); ctx.moveTo(X(f.diag[i].a[0]), Y(f.diag[i].a[1])); ctx.lineTo(X(f.diag[i].b[0]), Y(f.diag[i].b[1])); ctx.stroke();
      }
      if (ctx.setLineDash) ctx.setLineDash([]);
    }
    /* the patio, the porch, the deck (v115): a dashed box with its name */
    if (f.outdoor && f.outdoor.length) {
      ctx.strokeStyle = '#2E7D32'; ctx.lineWidth = 2;
      if (ctx.setLineDash) ctx.setLineDash([6, 4]);
      ctx.font = '700 10px Montserrat, Arial'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillStyle = '#2E7D32';
      for (i = 0; i < f.outdoor.length; i++) {
        var oq = f.outdoor[i];
        ctx.strokeRect(X(oq.x0), Y(oq.z0), X(oq.x1) - X(oq.x0), Y(oq.z1) - Y(oq.z0));
        ctx.fillText(oq.label, X((oq.x0 + oq.x1) / 2), Y((oq.z0 + oq.z1) / 2));
      }
      if (ctx.setLineDash) ctx.setLineDash([]);
    }
    /* the fittings (v111): a thin box each, the type written small in it */
    ctx.lineWidth = 1.5; ctx.strokeStyle = VIO;
    ctx.font = '600 9px Montserrat, Arial';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    for (i = 0; f.fixtures && i < f.fixtures.length; i++) {
      var fz = f.fixtures[i];
      ctx.strokeRect(X(fz.x0), Y(fz.z0), X(fz.x1) - X(fz.x0), Y(fz.z1) - Y(fz.z0));
      ctx.fillStyle = VIO;
      ctx.fillText(fz.type.replace('_', ' '), X((fz.x0 + fz.x1) / 2), Y((fz.z0 + fz.z1) / 2));
    }
    ctx.fillStyle = VIO;
    ctx.font = '700 11px Montserrat, Arial';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    for (i = 0; i < f.pins.length; i++) {
      var q = f.pins[i];
      ctx.fillText(q.k.replace(/^rooms\./, '').replace(/^container_room_7$/, 'kids'), X(q.x), Y(q.z));
    }
  }

  /* ---------- THE TEST SWITCH, ADMIN ONLY ---------------------------------
     ?planTest=1  the L sample read and straightened, shown in violet
     ?planTest=2  a rectangular sample read, FITTED to this product and shown
                  where it will land; APPLY in the box writes it into the
                  project shown, and Undo -- or RESTORE in the box -- takes it
                  back. The box says whether the project came back the same. */
  function _impMode() {
    var now = _fitUrlNow();
    if (/[?&#]planTest=0/.test(now)) return 0;
    var m = /[?&#]planTest=([123])/.exec(_FIT_AT_LOAD) || /[?&#]planTest=([123])/.exec(now);
    return m ? (m[1] | 0) : 0;
  }
  function _impParam() { return _impMode() > 0; }
  function _impStatus() {
    var md = _impMode(), C = _impCore(), st = '';
    try { st = (C && C.undoState) ? (' | undo steps ' + C.undoState().steps) : ''; } catch (e) { st = ''; }
    /* the seconds a reading has run, here in the box's first line only: the
       lines below go to the log on every change, and a counter there would
       write one line a second (v104) */
    var R = _imp.rd, run = ((md === 3 || _imp.user) && R && R.st === 'reading' && R.t1) ? (' | reading ' + Math.round((Date.now() - R.t1) / 1000) + ' s of ' + Math.round(IMP_WAIT_MS / 1000)) : '';
    return 'PLAN ' + VERSION + (_imp.user ? (' | the bar: ' + _impBarWhat(R)) : '') + ' | address at load ' + (/[?&#]planTest=[123]/.test(_FIT_AT_LOAD) ? ('planTest=' + md) : 'no switch') +
           (md === 3 ? (' | model ' + _impModel()) : '') + run +
           ' | plan host ' + (_PH ? 'yes' : 'NOT YET') + ' | house core ' + (C ? 'yes' : 'NOT YET') + st +
           ' | kept copy ' + (C ? (_impKept() ? 'yes' : 'none') : '-') +
           ' | address ?p=' + (_impShareId() || 'none') + ' | buttons ' + (_imp.btnFor || 'none') +
           ' | preview ' + (_imp.prev ? (_imp.drawn ? 'drawn in violet' : 'not drawn yet -- open Floorplan and switch to 2D') : 'none') +
           /* v158 (core-v574): the last time the page was left and came back */
           (_imp.tab ? (' | ' + _imp.tab) : '');
  }
  function _impLog(lines) {
    var txt = lines.join('\n');
    if (txt === _imp.logged) return;
    _imp.logged = txt;
    try {
      if (global.Configurator && global.Configurator.log && global.Configurator.log.log) {
        for (var i = 0; i < lines.length; i++) global.Configurator.log.log('[floorplan-lib] ' + lines[i]);
      }
    } catch (e2) {}
  }
  function _impTick() {
    if (_impMode() >= 2) { _impTick2(); return; }
    if (_imp.out) return;
    var n = null;
    try { n = _impNormalize(_impSampleRaw(), {}); } catch (e) { n = { ok: false, why: 'threw: ' + ((e && e.message) || e), notes: [] }; }
    _imp.out = n;
    _imp.lines = _impLines(n, 'test, sample L house 9.00 x 7.00 drawn 1.2 deg crooked at 50 px/m');
    _imp.prev = n.ok ? n : null;
    _impLog(_imp.lines);
  }
  function _impTick2() {
    var md = _impMode();
    if (!_PH || !_impCore()) { _imp.lines = ['PLAN test ' + md + ': waiting for the plan host and the house core']; _impButtons(); return; }
    var L;
    if (md === 3) {
      /* TEST 3 (v98): the plan is whatever the model read off the picture */
      L = _impReadLines();
      _imp.n2 = (_imp.rd && _imp.rd.n) ? _imp.rd.n : null;
      if (_imp.n2) L = L.concat(_impLines(_imp.n2, 'test 3, "' + _imp.rd.name + '" | VARIANT ' + (_imp.v2 ? '2 (passages are doors)' : '1 (passages are room lines)')));
    } else {
      if (!_imp.n2) {
        try { _imp.n2 = _impNormalize(_impSampleRect(), {}); } catch (e) { _imp.n2 = { ok: false, why: 'threw: ' + ((e && e.message) || e), notes: [] }; }
      }
      L = _impLines(_imp.n2, 'test 2, sample house 10.00 x 7.00 drawn 0.8 deg crooked at 40 px/m');
    }
    /* an Undo that brought the project back ends the import: APPLY again,
       and the kept copy is no longer needed */
    var dNow = _impDigest(), kp = _impKept();
    if (_imp.applied && _imp.digBefore && dNow === _imp.digBefore) { _imp.applied = false; _imp.undone = true; _impKeepClear(); kp = null; }
    /* A COPY KEPT BY AN EARLIER PAGE, AND WHAT THIS PAGE OPENED (v97). It
       used to be dropped without a word when the page came up on the
       project as it was before the import -- which is what a reload does
       when the address still holds the link saved before it -- so RESTORE
       simply was not there (Marek: nevidim restore). It is never dropped on
       a page's own say now; the line says which project the page opened. */
    if (!_imp.applied && !_imp.before && kp) {
      L.push('PLAN kept from an earlier page: the project before an import at ' + _impWhen(kp.at) + ' (' + (kp.was ? _impSumTxt(kp.was) : '?') +
             ')' + (kp.p ? ('; it was last saved as ?p=' + kp.p) : '') + ' | this page opened ' +
             ((dNow === kp.digAfter) ? 'the imported plan -- RESTORE puts the old project back'
              : (dNow === kp.dig) ? ('the project as it was BEFORE the import' + ((kp.p && _impShareId() === kp.p) ? (' (the address holds ?p=' + kp.p + ', the link saved before it)') : '') + ' -- nothing to restore, so no RESTORE')
              : 'a project changed since that import -- RESTORE would replace it with the one before the import'));
    }
    if (!_imp.n2) {
      _imp.fit = null; _imp.prev = null;
      if (_imp.applyLines) L = L.concat(_imp.applyLines);
      if (_imp.applied || _imp.restored || _imp.undone) L.push(_impNowLine());
    } else if (!_imp.applied) {
      var f;
      try { f = _impFit(_imp.n2, { units: _impUnits() }); } catch (e2) { f = { ok: false, why: 'threw: ' + ((e2 && e2.message) || e2), notes: [] }; }
      _imp.fit = f;
      _imp.prev = f.ok ? f : null;
      if (md === 3 && f.ok) _impTrace(_imp.rd, f);
      L = L.concat(_impFitLines(f));
      if (_imp.applyLines) L = L.concat(_imp.applyLines);
      if (_imp.restored || _imp.undone) L.push(_impNowLine());
      else if (f.ok) L.push('PLAN test ' + md + ': the violet plan is where it will land -- tap APPLY to write it into THIS project (one step; Undo or RESTORE takes it back)');
    } else {
      L = L.concat(_impFitLines(_imp.fit)).concat(_imp.applyLines || []);
      L.push(_impNowLine());
    }
    _imp.lines = L;
    _impLog(L);
    _impButtons();
  }
  /* the units the plan read in test 3 is drawn in; none for the sample */
  function _impUnits() {
    var R = _imp.rd;
    return ((_impMode() === 3 || _imp.user) && R && R.conv && R.st === 'done') ? (R.conv.units || '') : '';
  }
  function _impDoApply() {
    var f;
    try { f = _impFit(_imp.n2, { units: _impUnits() }); } catch (e) { f = { ok: false, why: 'threw: ' + ((e && e.message) || e), notes: [] }; }
    _imp.fit = f;
    var r;
    try { r = _impApply(f); } catch (e2) { r = { ok: false, why: 'threw: ' + ((e2 && e2.message) || e2), notes: [] }; }
    _imp.applyLines = _impApplyLines(r);
    _imp.lastApply = r;
    _imp.applied = !!r.ok;
    _imp.restored = false; _imp.undone = false;
    if (r.ok) { try { _impPicOnApply(_imp.rd); } catch (eP) { _impSay('PLAN picture: keeping it THREW ' + ((eP && eP.message) || eP)); } }
    /* v125: the slanted walls go into the project with the plan -- since
       v127 inside _impApply, before the pieces are placed */
    _impTickAny();
    try { _fitShow(); } catch (e3) {}
  }
  function _impDoRestore() {
    var ok = false;
    try { ok = _impRestore(); } catch (e) { ok = false; }
    _imp.applied = false;
    _imp.restored = ok; _imp.undone = false;
    if (!ok) _imp.applyLines = (_imp.applyLines || []).concat(['PLAN restore: nothing kept to restore']);
    _impTickAny();
    try { _fitShow(); } catch (e3) {}
  }
  /* THE BUTTONS IN THE BOX'S OWN BAR, BEFORE COPY. Test 3 has the model
     (tap to switch) and PICK; both tests have APPLY while nothing is
     applied, and RESTORE while there is something to put back -- an import
     in this page the project has not come back from, or a copy an earlier
     page kept. */
  /* THE DEBUG BOX IS A ROW OF BUTTONS (v121, Marek: nas debug banner nahore
     udelej uz jen jako tlacitka -- model / pick / restore / apply / copy, bez
     hide -- ne cele okno). The lines are still made, still go to the log, and
     COPY still copies them; the window round them, its title, HIDE and the
     picture are gone. What is left is the buttons in the corner, on nothing,
     a little larger for a finger. The box itself lets taps through. */
  function _dbgStrip() {
    if (_fit.strip || !_fit.box || !_fit.bar) return;
    var b = _fit.box, bar = _fit.bar, i, c;
    b.style.background = 'transparent'; b.style.border = '0'; b.style.borderRadius = '0'; b.style.boxShadow = 'none';
    b.style.font = '700 12px/1 ui-monospace,Menlo,monospace';
    b.style.pointerEvents = 'none';
    bar.style.borderBottom = '0'; bar.style.padding = '0'; bar.style.flexWrap = 'wrap'; bar.style.pointerEvents = 'auto';
    if (_fit.pre) _fit.pre.style.display = 'none';
    for (i = 0; i < bar.children.length; i++) {
      c = bar.children[i];
      if (c === _fit.cp) continue;
      if (c.tagName === 'SPAN' || /^(HIDE|SHOW)$/.test(c.textContent || '')) c.style.display = 'none';
    }
    /* AND COPY GOES TOO (v163, Marek: porad nam nahore na strance visi
       tlacitko copy, ktere jsme drive pouzivali). The lines it copied go into
       the log, and the SEND RAW of the debug row carries every line of it to
       the LEVA log since house-liquid-v69 -- it only took room over the
       canvas. It stays in the bar, hidden, as the place the other buttons
       are put before. */
    if (_fit.cp) _fit.cp.style.display = 'none';
    _fit.strip = true;
  }
  function _impButtons() {
    try { _dbgStrip(); } catch (eS) {}
    if (!_fit.bar || typeof document === 'undefined' || !document.createElement) return;
    var md = _impMode(), m23 = (md === 2 || md === 3), want = [];
    if (md === 3) {
      want.push(_impModel().toUpperCase());
      if (!_impReading()) want.push('PICK');
      if (_imp.rd && _imp.rd.st === 'done') want.push('SIZE');
      /* V1 / V2 (v109): passages as room lines, or as doors */
      if (_imp.rd && _imp.rd.st === 'done' && !_imp.applied) want.push(_imp.v2 ? 'V2' : 'V1');
    }
    if (m23 && _imp.n2 && !_imp.applied && _imp.fit && _imp.fit.ok) want.push('APPLY');
    var kq = (m23 && !_imp.applied && !_imp.before) ? _impKept() : null;
    if (m23 && (_imp.applied || (_imp.before && _impDigest() !== _imp.digBefore) || (kq && _impDigest() !== kq.dig))) want.push('RESTORE');
    /* RAW (v132): the reader's answer as it came, for the test bench */
    /* v153: and for the admin's reading from the bar -- the plan, or a change's operations */
    /* v181 (Marek: nahore se objevuje po dojeti agenta FP tlacitko RAW --
       pryc): gone from the bar; SEND RAW of the debug row carries the same
       answer, and since v181 also after a reload (_impLastKept) */
    var key = want.join(',');
    if (_imp.btnFor === key) return;
    _imp.btnFor = key;
    var B = _imp.btns = _imp.btns || {}, lab, i;
    for (lab in B) { if (B.hasOwnProperty(lab) && B[lab]) { try { _fit.bar.removeChild(B[lab]); } catch (e) {} B[lab] = null; } }
    for (i = 0; i < want.length; i++) {
      (function (w) {
        var b = document.createElement('button');
        b.type = 'button'; b.textContent = w;
        b.style.cssText = 'font:inherit;font-weight:700;padding:7px 11px;border:1px solid #7B1FA2;border-radius:4px;background:#F3E5F5;color:#4A148C;box-shadow:0 1px 4px rgba(0,0,0,0.22)';
        b.addEventListener('click', function () {
          if (w === 'APPLY') _impDoApply();
          else if (w === 'RESTORE') _impDoRestore();
          else if (w === 'PICK') _impPick();
          else if (w === 'SIZE') _impAskSize();
          else if (w === 'RAW') _impCopyRaw(b);
          else if (w === 'V1' || w === 'V2') { _imp.v2 = !_imp.v2; _impRenorm(_imp.rd); _imp.applyLines = null; _impRefresh(); }
          else { _imp.model = IMP_MODELS[(IMP_MODELS.indexOf(_impModel()) + 1) % IMP_MODELS.length]; _impTick2(); try { _fitShow(); } catch (eM) {} }
        });
        try { _fit.bar.insertBefore(b, _fit.cp || null); } catch (e2) { _fit.bar.appendChild(b); }
        B[w] = b;
      })(want[i]);
    }
  }

  /* THE READER'S ANSWER, WORD FOR WORD (v132, Marek: pozdeji poslu dalsi
     pudorysy a budeme trenovat). COPY copies the lines this box writes; RAW
     copies what the reader itself answered -- the plan in the picture's
     pixels, before any of our rules touched it -- with the file's name and
     the picture's size. Pasted into the chat with the picture and the log, it
     lets every rule be replayed on exactly that plan, again after each
     change. */
  /* the reader's answer as text (v163: the page's SEND RAW carries it too,
     so a reading can be replayed without copying anything) */
  function _impRawJson() {
    var R = _imp.rd;
    if (!R || !R.res) return '';
    try {
      return JSON.stringify({ raw: 'leva-plan-reader', lib: VERSION, file: R.name || '', model: R.model || '',
                              /* v153: the road, the words and what went with them */
                              road: _impBarWhat(R), text: R.text || '', asked: R.sent || null, after: (R.after && R.after.length) ? R.after : null,
                              picture: R.img ? { w: R.img.w, h: R.img.h, w0: R.img.w0 || 0, h0: R.img.h0 || 0 } : null, answer: R.res });
    } catch (eJ) { return ''; }
  }
  function _impCopyRaw(btn) {
    var R = _imp.rd;
    if (!R || !R.res) return;
    var t = _impRawJson();
    if (!t) return;
    var kb = Math.round(t.length / 102.4) / 10;
    function done() {
      if (btn) { btn.textContent = 'COPIED'; global.setTimeout(function () { btn.textContent = 'RAW'; }, 1400); }
      _impSay('PLAN raw: the reader\'s answer for "' + (R.name || 'the picture') + '" copied, ' + kb + ' kB -- paste it into the chat with the picture and the log');
    }
    function fallback() {
      try {
        var ta = document.createElement('textarea');
        ta.value = t; ta.style.cssText = 'position:fixed;left:-9999px;top:0';
        document.body.appendChild(ta); ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        done();
      } catch (eF) { _impSay('PLAN raw: the copy failed -- ' + ((eF && eF.message) || eF)); }
    }
    try {
      if (global.navigator && global.navigator.clipboard && global.navigator.clipboard.writeText) global.navigator.clipboard.writeText(t).then(done, fallback);
      else fallback();
    } catch (eC) { fallback(); }
  }

  /* ===== ZONE C -- replace from here to the END of the file ============ */

  /* =========================================================================
     THE PLAN IMPORT, PART 3 (v98): a picture read by the model.

     ?planTest=3, admin only. PICK takes a photo or a screenshot of a plan;
     the browser brings it down to 2576 px on the long side (as much as the
     model looks at -- more is only more to send), asks the worker for a
     ticket through the shop's own address, and sends the picture straight
     to the worker with it. What comes back is what the model SAW, in the
     picture's pixels; from there on it is the same road as test 2: read,
     straightened, fitted, shown in violet, APPLY. The box says which model,
     how long, how many tokens and what it cost, and every dimension text as
     the model read it next to the metres made of it.
     ========================================================================= */
  /* FIVE MINUTES, up from two and a half (v104): Sonnet and Opus answer in
     well under a minute, but Fable thinks for as long as it likes and the
     first try was given up on before it could answer (Marek: Fable se vubec
     nespustil) */
  /* EIGHT MINUTES (v110): Fable answered a hard plan in 230 s with the
     upload, too near the five */
  var IMP_SIDE = 2576, IMP_TICKET = '/apps/leva-ai/plan-ticket', IMP_WAIT_MS = 480000;
  /* the readers to try, in the order the button walks them (v103: Fable
     added, Marek: nezkusime jak si s tim poradi nejaky vyssi model) -- a
     worker that does not know one answers with Sonnet, and the line says so */
  var IMP_MODELS = ['sonnet', 'opus', 'fable'];
  function _impModel() { return (IMP_MODELS.indexOf(_imp.model) >= 0) ? _imp.model : 'sonnet'; }
  /* the model's room types, onto our keys; a garage, a terrace or anything
     else is left to the name, which then says it matched nothing */
  /* v105: each type is a list -- the room itself first, then what stands in
     for it in a product that does not offer it */
  var IMP_TYPE_KEY = { living: ['rooms.livingroom'], dining: ['rooms.diningroom', 'rooms.livingroom'], kitchen: ['rooms.kitchen'],
                       bedroom: ['rooms.bedroom'], kids: ['container_room_7', 'rooms.bedroom'], bathroom: ['rooms.bathroom'],
                       wc: ['rooms.wc', 'rooms.bathroom'], laundry: ['rooms.laundry'], utility: ['container_room_6', 'rooms.laundry'],
                       pantry: ['rooms.pantry', 'rooms.storage'], hallway: ['rooms.hall', 'rooms.hallway'], entry: ['rooms.hallway'],
                       mudroom: ['rooms.mudroom', 'rooms.hallway'], office: ['rooms.office'], storage: ['rooms.storage'],
                       closet: ['rooms.storage'], wardrobe: ['rooms.wardrobe', 'rooms.storage'], walk_in_closet: ['rooms.wardrobe', 'rooms.storage'],
                       garage: ['rooms.garage'], loft: ['rooms.loft'], sauna: ['rooms.sauna'], workshop: ['rooms.workshop'] };
  function _impReading() {
    var R = _imp.rd;
    return !!(R && (R.st === 'shrinking' || R.st === 'ticket' || R.st === 'reading'));
  }
  function _impClock(t) {
    var d = new Date(t || 0);
    function two(v) { return (v < 10 ? '0' : '') + v; }
    return two(d.getHours()) + ':' + two(d.getMinutes()) + ':' + two(d.getSeconds());
  }
  /* A DIMENSION'S TEXT, READ BY CODE: feet and inches, or a metric number
     whose unit is written or, when it is not, the one of mm / cm / m that
     lands nearest the model's own reading. The model's metres are the
     fallback, and a disagreement over 3% is said. */
  function _impDimText(text, vm) {
    var t = String(text == null ? '' : text).trim(), m = 0, how = '';
    vm = Number(vm) || 0;
    if (/['\u2019"\u201d]|\bft\b|\bin\b/i.test(t)) {
      var fi = /(\d+(?:\.\d+)?)\s*(?:'|\u2019|ft)\s*-?\s*(\d+(?:\.\d+)?)?\s*(?:(\d+)\s*\/\s*(\d+))?\s*(?:"|\u201d|in)?/i.exec(t);
      var io = /^(\d+(?:\.\d+)?)\s*(?:(\d+)\s*\/\s*(\d+))?\s*(?:"|\u201d|in)/i.exec(t);
      if (fi) { m = Number(fi[1]) * 0.3048 + ((fi[2] ? Number(fi[2]) : 0) + (fi[3] ? Number(fi[3]) / Number(fi[4]) : 0)) * 0.0254; how = 'ft-in'; }
      else if (io) { m = (Number(io[1]) + (io[2] ? Number(io[2]) / Number(io[3]) : 0)) * 0.0254; how = 'in'; }
    } else {
      var c = t.replace(/\s+/g, '').replace(',', '.'), mu = /^(\d+(?:\.\d+)?)(mm|cm|m)?$/i.exec(c);
      if (mu) {
        var v = Number(mu[1]), u = (mu[2] || '').toLowerCase();
        if (u === 'mm') { m = v / 1000; how = 'mm'; }
        else if (u === 'cm') { m = v / 100; how = 'cm'; }
        else if (u === 'm') { m = v; how = 'm'; }
        else {
          var cand = [[v / 1000, 'mm'], [v / 100, 'cm'], [v, 'm']], best = null, k;
          if (vm > 0) { for (k = 0; k < cand.length; k++) if (!best || Math.abs(cand[k][0] - vm) < Math.abs(best[0] - vm)) best = cand[k]; }
          else best = (mu[1].indexOf('.') >= 0 || v < 30) ? cand[2] : cand[0];
          m = best[0]; how = best[1] + '?';
        }
      }
    }
    var note = '';
    if (!(m > 0) && vm > 0) { m = vm; how = 'model'; note = 'the dimension "' + t + '" could not be read by code -- the model\'s ' + _impN(vm) + ' m was used'; }
    else if (m > 0 && vm > 0 && Math.abs(m - vm) / vm > 0.03) note = 'the dimension "' + t + '" is ' + _impN(m) + ' m read by code and ' + _impN(vm) + ' m by the model -- the code\'s reading was used';
    return { m: m, how: how, note: note };
  }
  /* A ROOM AREA AS WRITTEN IN THE ROOM (v171, plan 1000075647: KITCHEN
     AREA 165 SQ. FT and seven more, no dimension line anywhere -- none of
     the areas could be read, and the scale came from the furniture). 165
     SQ. FT, 165 sq ft, 165 SF, 165 sqft, 165 ft2, 15.3 m2, 15,3 m2: the area
     in m2 and its unit, or null. */
  function _impRoomArea(t) {
    var s0 = String(t == null ? '' : t).replace(/\u00b2/g, '2').replace(/\+\/-|\u00b1|approx\.?|ca\.?/gi, '').trim(), m;
    s0 = s0.replace(/^(\d{1,3}),(\d{3})(?!\d)/, '$1$2');
    m = /^(\d{1,5}(?:[.,]\d+)?)\s*(?:sq\.?\s*f(?:ee|oo)?t\.?|s\.?\s*f\.?|sqft\.?|ft\.?\s*\^?2)$/i.exec(s0);
    if (m) { var vf = Number(m[1].replace(',', '.')); return (vf > 0) ? { m2: vf * 0.09290304, how: 'ft-in' } : null; }
    m = /^(\d{1,4}(?:[.,]\d+)?)\s*(?:m\.?\s*\^?2|sq\.?\s*m\.?|qm)$/i.exec(s0);
    if (m) { var vm = Number(m[1].replace(',', '.')); return (vm > 0) ? { m2: vm, how: 'm' } : null; }
    return null;
  }
  /* A ROOM SIZE AS WRITTEN IN THE ROOM: two lengths with an x between
     them. 16/0 is the sales-plan way of writing 16 feet 0 inches; anything
     else goes through the dimension reader; +/- and the like are dropped. */
  function _impRoomSize(t, hint) {
    var s0 = String(t == null ? '' : t).replace(/\+\/-|\u00b1|approx\.?|ca\.?/gi, '').trim();
    var parts = s0.split(/\s*[xX\u00d7]\s*/);
    /* v142, plan 1000073678: 11' 0"-12' 0" -- two sizes in feet and inches
       with a dash between them */
    if (parts.length !== 2) {
      var mD = /^(\d+\s*'[^']*?)\s*[-\u2013]\s*(\d+\s*'.*)$/.exec(s0);
      if (mD) parts = [mD[1], mD[2]];
    }
    if (parts.length !== 2) return null;
    var how = '';
    function one(q) {
      q = String(q).trim();
      var fs = /^(\d+)\s*\/\s*(\d+(?:\.\d+)?)$/.exec(q);
      if (fs && Number(fs[2]) < 12) { how = 'ft/in'; return Number(fs[1]) * 0.3048 + Number(fs[2]) * 0.0254; }
      /* FEET AND THE INCHES OVER THEM (v132, plan 1BR Grt. Rm.: 15 5 x 12 6 is
         15 feet 5 inches -- the inches are printed small and high, and they
         come as a second number after a space or a dash) */
      var fsp = /^(\d+)\s*[-\s]\s*(\d+(?:\.\d+)?)$/.exec(q);
      if (fsp && Number(fsp[2]) < 12) { how = 'ft/in'; return Number(fsp[1]) * 0.3048 + Number(fsp[2]) * 0.0254; }
      /* A BARE NUMBER IS FEET (v132, plan covered balcony: BEDROOM 14 x 12
         was read as 14 by 12 metres and the project went metric). A room
         size with no unit is a US sales plan's feet up to 60; a decimal is
         metres; past 60 it is centimetres, past 1000 millimetres. Each is a
         guess and says so -- it counts only where no text says its unit. */
      var bare = /^(\d+(?:[.,]\d+)?)$/.exec(q);
      if (bare) {
        var bv = Number(bare[1].replace(',', '.'));
        if (/[.,]/.test(bare[1])) { if (bv < 30) { if (!how) how = 'm?'; return bv; } }
        else if (bv <= 60 && hint === 'metric') { if (!how) how = 'm?'; return bv; }
        else if (bv <= 60) { if (!how) how = 'ft?'; return bv * 0.3048; }
        else if (bv >= 1000) { if (!how) how = 'mm?'; return bv / 1000; }
        else { if (!how) how = 'cm?'; return bv / 100; }
      }
      var r = _impDimText(q, 0);
      if (r.m > 0 && !how) how = r.how;
      return (r.m > 0) ? r.m : 0;
    }
    var a = one(parts[0]), b = one(parts[1]);
    if (!(a > 0.3 && b > 0.3)) return null;
    var out2 = [a, b];
    out2.how = how;
    return out2;
  }
  /* THE WORDS OF A CLOSET (v135, Marek: male prostory kolem vstupnich dveri
     by mely vypadat spise jako closet -- Drop, Lin.). A space the plan
     labels with a closet's word is a closet whatever type the reader gave
     it: a coat, linen or broom closet, a walk-in, a drop zone with its
     lockers or bench, a pantry, a mechanical or water heater closet. A
     mudroom stays a room. Returns the closet type, or '' for a room. */
  var IMP_CLOSET_WORDS = { CL: 'closet', CLO: 'closet', CLOS: 'closet', CLOSET: 'closet', CLST: 'closet', LIN: 'closet', LINEN: 'closet', COAT: 'closet', COATS: 'closet',
                           BROOM: 'closet', WIC: 'walk_in_closet', DROP: 'closet', LOCKER: 'closet', LOCKERS: 'closet', CUBBY: 'closet', CUBBIES: 'closet', BENCH: 'closet',
                           STOR: 'storage', STORAGE: 'storage', PAN: 'pantry', PANT: 'pantry', PANTRY: 'pantry',
                           MECH: 'utility', UTIL: 'utility', UTILITY: 'utility', WH: 'utility', TWH: 'utility', ELEC: 'utility', FURN: 'utility', HVAC: 'utility' };
  function _impClosetWord(label) {
    var t = String(label || '').toUpperCase(), W = t.replace(/[^A-Z0-9]+/g, ' ').trim().split(' '), k, c = t.replace(/[^A-Z]/g, '');
    if (!t.trim()) return '';
    for (k = 0; k < W.length; k++) if (/^MUD/.test(W[k])) return '';
    if (/^(WIC|TWH|WH)$/.test(c)) return IMP_CLOSET_WORDS[c];
    for (k = 0; k < W.length; k++) if (IMP_CLOSET_WORDS[W[k]]) return IMP_CLOSET_WORDS[W[k]];
    if (/CLOSET|LINEN|PANTRY|DROPZONE/.test(c)) return /PANTRY/.test(c) ? 'pantry' : 'closet';
    return '';
  }
  /* what the model saw, as the plan the importer reads */
  function _impFromModel(p) {
    p = p || {};
    var raw = { units: 'px', outline: [], dims: [], partitions: [], doors: [], windows: [], rooms: [] }, notes = [], txt = [], sizes = [], i;
    /* which units the plan is drawn in, counted off the texts (v100) */
    /* v132: a text that writes its unit counts first; a bare number's
       guess (mm?, ft? ...) only where no text says its unit */
    var nImp = 0, nMet = 0, gImp = 0, gMet = 0;
    function unitOf(how) {
      if (/^(ft-in|in|ft\/in)$/.test(how)) nImp++;
      else if (/^(mm|cm|m)$/.test(how)) nMet++;
      else if (how === 'ft?') gImp++;
      else if (/^(mm|cm|m)\?$/.test(how)) gMet++;
    }
    function pt(q) { return (q && q.length >= 2 && isFinite(q[0]) && isFinite(q[1])) ? [Number(q[0]), Number(q[1])] : null; }
    for (i = 0; p.outline && i < p.outline.length; i++) { var o = pt(p.outline[i]); if (o) raw.outline.push(o); }
    for (i = 0; p.dims && i < p.dims.length; i++) {
      var d = p.dims[i], a = pt(d && d.a), b = pt(d && d.b);
      if (!a || !b) continue;
      var r = _impDimText(d.text, d.value_m);
      txt.push('"' + String(d.text) + '" ' + (r.m > 0 ? (_impN(r.m) + ' (' + r.how + ')') : '?'));
      if (r.note) notes.push(r.note);
      unitOf(r.how);
      if (r.m > 0) raw.dims.push({ a: a, b: b, m: r.m });
    }
    for (i = 0; p.partitions && i < p.partitions.length; i++) {
      var pa = pt(p.partitions[i] && p.partitions[i].a), pb = pt(p.partitions[i] && p.partitions[i].b);
      if (pa && pb) raw.partitions.push({ a: pa, b: pb });
    }
    for (i = 0; p.doors && i < p.doors.length; i++) {
      var dd0 = p.doors[i], da = pt(dd0 && dd0.at);
      if (!da || !(dd0.w > 0)) continue;
      /* v101: the kind, the jamb it hangs on and the side it opens to */
      raw.doors.push({ at: da, w: Number(dd0.w), kind: String(dd0.kind || 'hinged'), label: String(dd0.label || ''), hinge: pt(dd0.hinge), opensTo: pt(dd0.opens_to),
                       rooms: Array.isArray(dd0.rooms) ? dd0.rooms.map(String) : [] });
    }
    for (i = 0; p.windows && i < p.windows.length; i++) {
      var wa = pt(p.windows[i] && p.windows[i].at), wd = p.windows[i];
      if (wa && wd.w > 0) raw.windows.push({ at: wa, w: Number(wd.w), sill: (wd.sill_m > 0) ? Number(wd.sill_m) : null, label: String(wd.label || '') });
    }
    /* v111: the fittings and the furniture the reader saw, each a type and a box */
    raw.fixtures = [];
    for (i = 0; p.fixtures && i < p.fixtures.length; i++) {
      var fx0 = p.fixtures[i], fb = (fx0 && fx0.box) || [], fa0 = pt(fb[0]), fa1 = pt(fb[1]);
      /* v114: the word written in it (QUEEN, 5 FT, W/D) and whether it was
         drawn on the plan or only suggested by the reader */
      if (fx0 && fx0.type && fa0 && fa1) {
        var rfx = { type: String(fx0.type), a: fa0, b: fa1, label: String(fx0.label || ''), sug: (fx0.suggested === true || fx0.drawn === false) };
        /* v150: the chairs drawn round a table, the stools at a counter (leva-ai w2.05) */
        var se0 = parseInt(fx0.seats, 10) || 0, so0 = parseInt(fx0.stools, 10) || 0;
        if (se0 > 0) rfx.seats = Math.min(24, se0);
        if (so0 > 0) rfx.stools = Math.min(12, so0);
        raw.fixtures.push(rfx);
      }
    }
    /* v105: where two rooms meet with no wall -- a room line, not a partition */
    raw.open_sides = [];
    for (i = 0; p.open_sides && i < p.open_sides.length; i++) {
      var os0 = p.open_sides[i], osa = pt(os0 && os0.a), osb = pt(os0 && os0.b);
      if (osa && osb) raw.open_sides.push({ a: osa, b: osb, rooms: (os0.rooms || []).map(String) });
    }
    for (i = 0; p.rooms && i < p.rooms.length; i++) {
      var ra = pt(p.rooms[i] && p.rooms[i].at), rr = p.rooms[i];
      if (!ra) continue;
      var rq = { at: ra, label: String(rr.label || ''), key: IMP_TYPE_KEY[rr.type] || [], type: String(rr.type || '') };
      var cw = _impClosetWord(rq.label);
      if (cw && !/^(closet|storage|pantry|laundry|utility|wardrobe|walk_in_closet)$/.test(rq.type)) {
        notes.push('"' + rq.label + '" is a closet\'s word -- a ' + cw.replace(/_/g, ' ') + ', not a ' + (rq.type || 'room') + ' (v135)');
        rq.typeRead = rq.type; rq.type = cw; rq.key = IMP_TYPE_KEY[cw] || [];
      }
      /* v99: the size written in the room and the box of its walls, when
         the reader gives them; a box comes as { a, b } or as two points */
      var bx = rr.box, b0 = null, b1 = null;
      if (bx && bx.a && bx.b) { b0 = pt(bx.a); b1 = pt(bx.b); } else if (bx && bx.length === 2) { b0 = pt(bx[0]); b1 = pt(bx[1]); }
      if (rr.size_text) {
        /* the dimension lines, read first, say how a bare number is meant */
        var sz = _impRoomSize(rr.size_text, (nMet > nImp) ? 'metric' : '');
        /* v171: or an area, 165 SQ. FT */
        var arW = sz ? null : _impRoomArea(rr.size_text);
        if (sz) unitOf(sz.how); else if (arW) unitOf(arW.how);
        sizes.push('"' + rq.label + '" ' + String(rr.size_text) + (sz ? (' = ' + _impN(sz[0]) + ' x ' + _impN(sz[1])) : (arW ? (' = ' + _impN(arW.m2) + ' m2') : ' = ?')) + ((b0 && b1) ? '' : ' (no box)'));
        if (sz && b0 && b1) rq.size = sz;
        else if (arW && b0 && b1) rq.areaW = arW.m2;
        else if (!sz && !arW) notes.push('the room size "' + rr.size_text + '" could not be read');
      }
      /* the box is kept for every room now (v101): it is what closes the room */
      if (b0 && b1) rq.box = [b0, b1];
      raw.rooms.push(rq);
    }
    var un = (nImp > nMet) ? 'imperial' : ((nMet > nImp) ? 'metric' : '');
    if (!un && !nImp && !nMet) un = (gImp > gMet) ? 'imperial' : ((gMet > gImp) ? 'metric' : '');
    var unTxt = un ? (un + ' (' + nImp + ' text(s) in feet and inches, ' + nMet + ' in metric' +
                      ((gImp || gMet) ? (', ' + (gImp ? (gImp + ' bare number(s) taken as feet') : '') + ((gImp && gMet) ? ', ' : '') + (gMet ? (gMet + ' taken as metric') : '') +
                                         ((nImp || nMet) ? ' -- the texts with a unit decide' : ' -- no text writes its unit')) : '') + ')') : 'not told by any text';
    return { raw: raw, notes: notes, units: un, unitsTxt: unTxt,
             dimsTxt: txt.length ? txt.join(' | ') : 'none', sizesTxt: sizes.length ? sizes.join(' | ') : '' };
  }
  /* ---------- THE WHOLE ROAD IN THE LOG (v153) ---------------------------
     Marek: chtel jsem ti zaslat log z textoveho zadani ale skoro nic tam
     neni -- muzes upravit sledovani tak, abys dostal kompletni info co model
     dela a co vygeneroval? COPY held the FIT line and nothing else: the PLAN
     lines went to the console. They go to COPY now, for the admin, and they
     say all of it -- what went to the worker with the words, what it cost
     and what record it made, and what the model drew, room by room, door by
     door, window by window and piece by piece, in metres, before the page
     straightened any of it. RAW copies the answer itself. */
  function _impBarWhat(R) {
    if (!R) return 'nothing yet';
    return (R.change ? 'a change in words' : (R.lib ? 'a library plan' : (R.hand ? 'the plan read on the homepage' : (R.text ? (R.redesign ? 'a redesign in words' : 'a design from words') : 'a picture')))) + ', ' + R.st;
  }
  function _impSentCopy(b) {
    var o = {}, k;
    for (k in b) {
      if (!b.hasOwnProperty(k) || k === 'ticket') continue;
      if ((k === 'image' || k === 'clean') && typeof b[k] === 'string') { o[k] = Math.round(b[k].length * 0.75 / 1024) + ' kB'; continue; }
      if (k === 'marks' && b[k] && b[k].s) { o[k] = b[k].s.length + ' line(s), ' + (b[k].g || []).length + ' break(s)'; continue; }
      o[k] = b[k];
    }
    return o;
  }
  function _impOfferTxt(f) {
    var o = [], k;
    for (k in f) if (f.hasOwnProperty(k)) o.push(k + ' ' + (Array.isArray(f[k]) ? f[k].join('/') : String(f[k])));
    return o.join(', ');
  }
  function _impSentTxt(R) {
    var sd = (R && R.sent) || null, o = [];
    if (!sd) return '';
    if (sd.model) o.push('model asked ' + sd.model);
    if (sd.units) o.push('units ' + sd.units);
    if (sd.product) o.push('product ' + sd.product);
    if (sd.limits && sd.limits.w) o.push('largest outline ' + _impN(sd.limits.w) + ' x ' + _impN(sd.limits.d) + ' m');
    if (sd.lang) o.push('page language ' + sd.lang);
    if (sd.offer) o.push('the configurator offers ' + _impOfferTxt(sd.offer));
    if (sd.base) o.push('the plan as it stands, ' + JSON.stringify(sd.base).length + ' characters');
    if (sd.project) o.push('the project, ' + JSON.stringify(sd.project).length + ' characters' + (sd.project.roof ? (' (roof ' + sd.project.roof.type + ' ' + sd.project.roof.pitch + ' deg, offers ' + (sd.project.roof.offers || []).join('/') + ')') : ''));
    if (sd.history && sd.history.length) o.push(sd.history.length + ' earlier instruction(s): ' + sd.history.map(function (h) { return '"' + String(h).slice(0, 80) + '"'; }).join(', '));
    if (sd.image) o.push('the picture ' + (sd.w || '?') + 'x' + (sd.h || '?') + ' px, ' + sd.image + (sd.clean ? (', the clean one ' + sd.clean) : ''));
    return 'PLAN sent with it: ' + (o.length ? o.join(' | ') : 'nothing more');
  }
  function _impPaidTxt(R) {
    var j = (R && R.res) || {}, t = j.tokens || null, o = [];
    if (t) o.push((R.hand ? 'charged when it was read on the homepage: ' : 'charged: ') + (t.how || '?') + (t.spent ? (', ' + t.spent + ' token(s)') : ', no token') +
                  (t.balance != null ? (', balance ' + t.balance) : '') + (t.cost != null ? (' (it costs ' + t.cost + ')') : ''));
    else o.push('charged: the worker says nothing');
    if (j.rec || R.rec) o.push('record ' + (j.rec || R.rec));
    else if (!R.change) o.push('no record (a worker before w2.06)');
    if (j.design && j.design.id) o.push('the design kept for the library as ' + j.design.id + ' for ' + Math.round((j.design.keepS || 0) / 86400) + ' day(s)');
    else if (R.text && !R.change) o.push('the design NOT kept for the library');
    if (j.pic && j.pic.id) o.push('the picture kept as ' + j.pic.id + (j.pic.clean ? ' (the clean one)' : ''));
    return 'PLAN ' + o.join(' | ');
  }
  function _impNeedTxt(R) {
    if (!R) return '';
    return (R.need != null ? (' | needs ' + R.need + ' token(s), has ' + R.balance) : '') + (R.loginUrl ? ' | not signed in' : '');
  }
  function _impModelLines(R, what) {
    var j = (R && R.res) || null, p = j && j.plan, L = [];
    if (!p) return L;
    var k = 0, how = '', i;
    if (R.sheet && R.sheet.ppm > 0) { k = 1 / R.sheet.ppm; how = 'on its sheet at ' + R.sheet.ppm + ' px/m'; }
    else if (R.n && R.n.ok && R.n.scale > 0) { k = R.n.scale; how = 'at the scale read, ' + R.n.scale.toFixed(4) + ' m/px'; }
    if (!(k > 0)) { L.push('PLAN ' + what + ': no scale, so nothing measured'); return L; }
    var O = p.outline || [], x0 = 1e9, y0 = 1e9, x1 = -1e9, y1 = -1e9, a2 = 0;
    for (i = 0; i < O.length; i++) { x0 = Math.min(x0, O[i][0]); y0 = Math.min(y0, O[i][1]); x1 = Math.max(x1, O[i][0]); y1 = Math.max(y1, O[i][1]); }
    if (!O.length) { x0 = 0; y0 = 0; x1 = 0; y1 = 0; }
    for (i = 0; i < O.length; i++) { var Pa = O[i], Pb = O[(i + 1) % O.length]; a2 += Pa[0] * Pb[1] - Pb[0] * Pa[1]; }
    function M(v) { return _impN(v * k); }
    function XY(pt) { return (pt && pt.length >= 2) ? (_impN((pt[0] - x0) * k) + ',' + _impN((pt[1] - y0) * k)) : '?'; }
    function box(b) {
      if (!b || b.length !== 2 || !b[0] || !b[1]) return null;
      return { lx: Math.min(b[0][0], b[1][0]), hx: Math.max(b[0][0], b[1][0]), ly: Math.min(b[0][1], b[1][1]), hy: Math.max(b[0][1], b[1][1]),
               w: Math.abs(b[1][0] - b[0][0]) * k, d: Math.abs(b[1][1] - b[0][1]) * k, cx: (b[0][0] + b[1][0]) / 2, cy: (b[0][1] + b[1][1]) / 2 };
    }
    var RM = (p.rooms || []).map(function (r) { return { r: r, bx: box(r.box) }; });
    function roomOf(pt, grow) {
      var best = null, ba = 1e18, g = (grow || 0) / k, q;
      if (!pt) return null;
      for (q = 0; q < RM.length; q++) {
        var B = RM[q].bx;
        if (!B) continue;
        if (pt[0] >= B.lx - g && pt[0] <= B.hx + g && pt[1] >= B.ly - g && pt[1] <= B.hy + g && B.w * B.d < ba) { ba = B.w * B.d; best = RM[q].r; }
      }
      return best;
    }
    function nm(r) { return r ? ('"' + (r.label || r.type || '?') + '"') : 'no room'; }
    function side(pt) {
      if (!pt) return '?';
      var dd = [[Math.abs(pt[1] - y0), 'top'], [Math.abs(pt[1] - y1), 'bottom'], [Math.abs(pt[0] - x0), 'left'], [Math.abs(pt[0] - x1), 'right']];
      dd.sort(function (a, b) { return a[0] - b[0]; });
      return (dd[0][0] * k < 0.6) ? (dd[0][1] + ' wall') : 'inside';
    }
    L.push('PLAN ' + what + ' outline: ' + O.length + ' corner(s), ' + M(x1 - x0) + ' x ' + M(y1 - y0) + ' m, ' + (Math.abs(a2) / 2 * k * k).toFixed(1) + ' m2 -- metres from its top left corner, ' + how +
           ((O.length && O.length <= 16) ? (': ' + O.map(XY).join(' ')) : ''));
    var tot = 0, rmTxt = RM.map(function (q) {
      var B = q.bx, r = q.r;
      if (B) tot += B.w * B.d;
      return nm(r) + ' ' + (r.type || '-') + (B ? (' ' + _impN(B.w) + 'x' + _impN(B.d) + ' = ' + (B.w * B.d).toFixed(1) + ' m2 at ' + XY([B.lx, B.ly])) : ' NO BOX') + (r.size_text ? (' (written ' + r.size_text + ')') : '');
    });
    L.push('PLAN ' + what + ' rooms ' + RM.length + (RM.length ? (', ' + tot.toFixed(1) + ' m2 in their boxes: ' + rmTxt.join(' | ')) : ': none'));
    var DR = p.doors || [];
    L.push('PLAN ' + what + ' doors ' + DR.length + ': ' + (DR.length ? DR.map(function (d) {
      var rs = Array.isArray(d.rooms) ? d.rooms.join(' / ') : String(d.rooms || '');
      return (d.kind || 'door') + ' ' + M(d.w || 0) + (rs ? (' ' + rs) : '') + (d.label ? (' "' + d.label + '"') : '') + ' at ' + XY(d.at) + ' (' + side(d.at) + ')';
    }).join(' | ') : 'none'));
    var WN = p.windows || [];
    L.push('PLAN ' + what + ' windows ' + WN.length + ': ' + (WN.length ? WN.map(function (w) {
      return M(w.w || 0) + (w.sill_m > 0 ? (' sill ' + _impN(w.sill_m)) : '') + ' ' + side(w.at) + ', ' + nm(roomOf(w.at, 0.5)) + (w.label ? (' "' + w.label + '"') : '') + ' at ' + XY(w.at);
    }).join(' | ') : 'none'));
    var FX = p.fixtures || [], G = {}, order = [];
    for (i = 0; i < FX.length; i++) {
      var f = FX[i], fb = box(f.box), rr = fb ? roomOf([fb.cx, fb.cy], 0.1) : null, kq = nm(rr);
      if (!G[kq]) { G[kq] = []; order.push(kq); }
      G[kq].push((f.type || '?') + (f.label ? (' "' + f.label + '"') : '') + (fb ? (' ' + _impN(fb.w) + 'x' + _impN(fb.d)) : '') + (f.suggested ? ' SUGGESTED' : ''));
    }
    L.push('PLAN ' + what + ' fittings ' + FX.length + ': ' + (FX.length ? order.map(function (q) { return q + ' ' + G[q].join(', '); }).join(' | ') : 'none'));
    var PT = p.partitions || [], len = 0, OS = p.open_sides || [];
    for (i = 0; i < PT.length; i++) if (PT[i] && PT[i].a && PT[i].b) len += Math.hypot(PT[i].b[0] - PT[i].a[0], PT[i].b[1] - PT[i].a[1]);
    L.push('PLAN ' + what + ' partitions ' + PT.length + ', ' + M(len) + ' m of inner wall | ' + (OS.length ? ('open sides ' + OS.length + ': ' + OS.map(function (o9) { return Array.isArray(o9.rooms) ? o9.rooms.join(' / ') : String(o9.rooms || '?'); }).join(' | ')) : 'no open side') +
           ((p.building || p.floors) ? (' | the building ' + (p.building || '?') + ', ' + (p.floors || '?') + ' floor(s)') : ''));
    return L;
  }
  function _impReadLines() {
    var R = _imp.rd, L = [], img = R && R.img, q0;
    if (!R) {
      L.push('PLAN test 3: tap PICK and choose a photo or a screenshot of a floor plan; the model is ' + _impModel().toUpperCase() +
             ' -- tap that button to switch');
      return L;
    }
    /* v153: the change this reading came from, first */
    if (R.pre && R.pre.length) for (q0 = 0; q0 < R.pre.length; q0++) L.push(R.pre[q0]);
    /* v154: what the library said for the words */
    if (R.matched) L.push(_impMatchTxt(R.matched, true));
    if (R.matchNote) L.push(_impMatchTxt(R.matchNote, false));
    var pic = img ? (' | picture ' + img.w0 + 'x' + img.h0 + ' -> ' + img.w + 'x' + img.h + (img.up ? ' (enlarged)' : '') + ', ' + img.kb + ' kB' +
                     (img.walls ? (' | walls traced ' + (img.walls.segs ? img.walls.segs.length : 0) + ' at ' + img.walls.t + ' px' + (img.walls.thr ? (', grey under ' + img.walls.thr) : '') +
                                    (img.walls.variants ? (' (' + img.walls.variants.map(function (v) { return v.thr + (v.thin ? ' thin' : '') + ': ' + v.segs.length + ' line(s)' + (v.thin ? '' : (', ' + v.score + ' px long')); }).join(' | ') + ')') : '') +
                                    (img.walls.err ? (' THREW ' + img.walls.err) : '')) : '') +
                     (img.ink ? (' | ' + (img.ink.used ? ('the reader got the black ink only (grey under ' + img.ink.thr + ', ' + img.ink.pct + ' % of the sheet kept' + (img.ink.hair != null ? (', ' + img.ink.hair + ' % of it hair lines (v196)') : '') + '), the picture kept as drawn (v189)')
                                                       : ('sent in colour: ' + img.ink.why + ' (v189)'))) : '')) : '';
    /* a change (v123): the instruction, and what came back */
    if (R.change) {
      var askC = 'the change "' + R.text + '"' + (R.hist && R.hist.length ? (' after ' + R.hist.length + ' earlier') : '');
      if (R.st === 'ticket') L.push('PLAN change: asking the worker for a ticket -- ' + askC);
      else if (R.st === 'reading') L.push('PLAN change: thinking with ' + R.model + ' since ' + _impClock(R.t1) + ' -- ' + askC);
      else if (R.st === 'failed') L.push('PLAN change: FAILED with ' + R.model + ' -- ' + R.err + _impNeedTxt(R) + ' -- ' + askC);
      else if (R.st === 'done') {
        var jc = R.res || {}, C2 = R.chg || { ops: [] };
        L.push('PLAN change: answered by ' + jc.model + ' in ' + ((jc.ms || 0) / 1000).toFixed(1) + ' s | tokens in ' + ((jc.usage && jc.usage.input) || 0) + ', out ' +
               ((jc.usage && jc.usage.output) || 0) + ' | cost ' + Number(jc.cost || 0).toFixed(3) + ' USD | worker ' + jc.version + ' -- ' + askC);
        var sxC = _impSentTxt(R);
        if (sxC) L.push(sxC);
        L.push(_impPaidTxt(R) + (C2.dropped ? (' | ' + C2.dropped + ' operation(s) the worker did not know, left out') : ''));
        if (C2.question) L.push('PLAN change: the designer asks "' + C2.question + '" -- nothing changed');
        else L.push('PLAN change: ' + C2.ops.length + ' operation(s): ' + C2.ops.map(_impChgOpTxt).join(' | ') + (C2.summary ? (' -- "' + C2.summary + '"') : ''));
      }
      return L;
    }
    /* a description (v120): what was asked for, and for what */
    if (R.text) {
      var ask = 'the description "' + R.text + '" | units ' + (R.units || '?') + (R.prod ? (' | product ' + R.prod) : '') +
                (R.lim ? (' | largest outline ' + _impN(R.lim.w) + ' x ' + _impN(R.lim.d) + ' m') : '');
      /* v124: a redesign of the plan there is says so, and from what */
      var hd = R.redesign ? ('PLAN redesign' + (R.redesignOf ? (' (the change "' + R.redesignOf + '")') : '') + ': ') : 'PLAN from words: ';
      if (R.matching) { L.push((R.chgOf ? 'PLAN change' : 'PLAN from words') + ': looking in the plan library first, since ' + _impClock(R.t0) + ' -- ' + ask + ' (v154)'); return L; }
      if (R.st === 'ticket') L.push(hd + 'asking the worker for a ticket -- ' + ask);
      else if (R.st === 'reading') L.push(hd + 'designing with ' + R.model + ' since ' + _impClock(R.t1) + ' -- ' + ask + (R.redesign ? ' | from the plan as it stands' : ''));
      else if (R.st === 'failed') L.push(hd + 'FAILED with ' + R.model + ' -- ' + R.err + _impNeedTxt(R) + ' -- ' + ask);
      else if (R.st === 'done') {
        var jd = R.res, pd = jd.plan || {};
        var nd = function (a) { return (a && a.length) || 0; };
        L.push(hd + 'designed by ' + jd.model + ' in ' + (jd.ms / 1000).toFixed(1) + ' s' + (R.t2 ? (' (' + ((R.t2 - R.t0) / 1000).toFixed(1) + ' s from the ask)') : '') +
               ' | tokens in ' + jd.usage.input + ', out ' + jd.usage.output +
               ' | cost ' + Number(jd.cost).toFixed(3) + ' USD | stop ' + jd.stop + ' | worker ' + jd.version + ' -- ' + ask);
        var sxD = _impSentTxt(R);
        if (sxD) L.push(sxD);
        L.push(_impPaidTxt(R));
        L.push('PLAN the design has: outline ' + nd(pd.outline) + ' corners, partitions ' + nd(pd.partitions) + ', doors ' + nd(pd.doors) + ', windows ' + nd(pd.windows) +
               ', rooms ' + nd(pd.rooms) + ', open sides ' + nd(pd.open_sides) + ', fixtures ' + nd(pd.fixtures) + ' | the designer notes: ' + (nd(pd.unreadable) ? pd.unreadable.join('; ') : 'nothing'));
        if (R.sheet) L.push('PLAN drawn on a sheet of ' + R.sheet.w + ' x ' + R.sheet.h + ' px at ' + R.sheet.ppm + ' px/m, designed in ' + (R.sheet.unit === 'ft' ? 'feet' : 'metres') +
                            (R.sheet.size ? (', outline ' + R.sheet.size[0] + ' x ' + R.sheet.size[1] + ' m') : '') + ' -- no picture under it');
        var MLd = _impModelLines(R, 'designed');
        for (q0 = 0; q0 < MLd.length; q0++) L.push(MLd[q0]);
        L.push('PLAN dimensions as drawn: ' + R.conv.dimsTxt);
        L.push('PLAN units of the plan: ' + R.conv.unitsTxt);
        if (R.conv.notes.length) L.push('PLAN reading notes: ' + R.conv.notes.join('; '));
        L.push(_impIdeasLine(R));
        if (R.after && R.after.length) L.push('PLAN with it, written once the plan is: ' + R.after.map(_impChgOpTxt).join(' | ') + ' -- the roof and the terrace the words asked for; the designer draws the rooms only (v153)');
      }
      return L;
    }
    if (R.st === 'shrinking') L.push('PLAN test 3: opening "' + R.name + '"');
    else if (R.st === 'ticket') L.push('PLAN test 3: asking the worker for a ticket' + pic);
    /* v144: a plan out of the library is not read -- it is built again from
       what was saved, in a second or two, with no tokens */
    else if (R.st === 'reading' && R.lib) L.push('PLAN library: taking plan ' + R.lib + ' out of the library since ' + _impClock(R.t1) + ' (' + Math.round((Date.now() - R.t1) / 1000) +
                                                 ' s) -- a plan read before, built here again from its walls, doors and rooms: nothing is read and no tokens are spent; given up after ' +
                                                 Math.round(IMP_LIB_WAIT_MS / 1000) + ' s (v144)');
    else if (R.st === 'failed' && R.lib) L.push('PLAN library: plan ' + R.lib + ' NOT taken out -- ' + R.err);
    else if (R.st === 'reading') L.push('PLAN test 3: reading "' + R.name + '" with ' + R.model + ' since ' + _impClock(R.t1) +
                                        ' -- Sonnet and Opus take under a minute, Fable can take several; the page waits ' +
                                        Math.round(IMP_WAIT_MS / 60000) + ' min, the first line counts the seconds' + pic + _impLostTxt(R));
    else if (R.st === 'failed') L.push('PLAN test 3: FAILED on "' + R.name + '" with ' + R.model + ' -- ' + R.err + _impNeedTxt(R) + pic + _impLostTxt(R));
    else if (R.st === 'done' && R.lib && R.proj) {
      L.push('PLAN library: plan ' + R.lib + ' opened as its project ?p=' + R.proj + (R.projProd ? (' (' + R.projProd + ')') : '') + ' in ' + (((R.t2 || Date.now()) - R.t0) / 1000).toFixed(1) + ' s' +
             (R.libHttp ? (' (the plan HTTP ' + R.libHttp + ' after ' + ((R.libAns - R.t1) / 1000).toFixed(1) + ' s, the project ' + (R.projMs / 1000).toFixed(1) + ' s)') : '') +
             ' -- nothing read, nothing built again, no tokens | worker ' + ((R.res && R.res.version) || '?') + ' (v147)');
    }
    else if (R.st === 'done') {
      var j = R.res, p = j.plan || {};
      function n(a) { return (a && a.length) || 0; }
      if (R.lib) L.push('PLAN library: plan ' + R.lib + ' taken out of the library in ' + (((R.t2 || Date.now()) - R.t0) / 1000).toFixed(1) + ' s' +
                        (R.libHttp ? (' (the answer HTTP ' + R.libHttp + ', ' + R.libChars + ' characters, after ' + ((R.libAns - R.t1) / 1000).toFixed(1) + ' s' +
                                      (R.libBuilt != null ? ('; read into a plan in ' + R.libBuilt + ' ms') : '') + ')') : '') +
                        ' -- a plan read before, built here again: nothing read, no tokens | worker ' + j.version + ' (v144)');
      else L.push('PLAN test 3: "' + R.name + '" read by ' + j.model + ' in ' + (j.ms / 1000).toFixed(1) + ' s (' + ((R.t2 - R.t0) / 1000).toFixed(1) +
             ' s with the upload) | tokens in ' + j.usage.input + ', out ' + j.usage.output + ' | cost ' + Number(j.cost).toFixed(3) + ' USD | stop ' + j.stop + pic +
             ' | worker ' + j.version + _impLostTxt(R));
      if (R.img && R.img.marks) {
        var MU = R.marksUse;
        L.push('PLAN MARKS (v194): ' + R.img.marks.segs.length + ' numbered line(s) and ' + R.img.marks.gaps.length + ' break(s) on the picture; the reader named ' +
               n(p.wall_marks) + ' wall(s) and ' + n(p.gap_marks) + ' door(s) or passage(s) -- ' +
               (MU && MU.used ? ('the walls built from the marks: ' + MU.inner + ' inside, ' + MU.doors + ' door(s), ' + MU.openings + ' opening(s), ' + MU.fills + ' stretch(es) of the reader\'s own wall where no mark lies, ' + MU.keptDoors + ' of its doors kept, ' + (MU.shut || 0) + ' narrow unnamed break(s) closed')
                              : ('not used: ' + ((MU && MU.why) || 'no answer for them') + ' -- the reader\'s own walls and doors, as before')));
      }
      L.push('PLAN the model saw: outline ' + n(p.outline) + ' corners, dimensions ' + n(p.dims) + ', partitions ' + n(p.partitions) + ', doors ' + n(p.doors) +
             ', windows ' + n(p.windows) + ', rooms ' + n(p.rooms) + ', open sides ' + n(p.open_sides) + ', fixtures ' + n(p.fixtures) + ' | could not read: ' + (n(p.unreadable) ? p.unreadable.join('; ') : 'nothing'));
      /* v153: what was sent, what it cost, and what the reader drew */
      if (!R.lib) {
        var sxP = _impSentTxt(R);
        if (sxP) L.push(sxP);
        L.push(_impPaidTxt(R));
      }
      var MLp = _impModelLines(R, R.lib ? 'as saved' : 'as read');
      for (q0 = 0; q0 < MLp.length; q0++) L.push(MLp[q0]);
      L.push('PLAN dimensions as read: ' + R.conv.dimsTxt);
      if (R.conv.sizesTxt) L.push('PLAN room sizes as read: ' + R.conv.sizesTxt);
      if (R.traceTxt) L.push('PLAN underlay: ' + R.traceTxt + ' -- the Trace button unlocks it, the slider sets how much shows; on this screen only, never on the sheet, in the PDF or in a share (v132)');
      L.push('PLAN units of the plan: ' + R.conv.unitsTxt);
      if (R.longSide) L.push('PLAN long side given by hand: ' + _impN(R.longSide) + ' m ("' + R.longTxt + '")');
      if (R.sizeNote) L.push('PLAN ' + R.sizeNote);
      if (R.conv.notes.length) L.push('PLAN reading notes: ' + R.conv.notes.join('; '));
      L.push(_impIdeasLine(R));
    }
    return L;
  }
  function _impPick() {
    if (typeof document === 'undefined' || !document.body) return;
    var inp = _imp.input;
    if (!inp) {
      inp = document.createElement('input');
      inp.type = 'file'; inp.accept = 'image/*';
      inp.style.cssText = 'position:fixed;left:-9999px;top:0;width:1px;height:1px;opacity:0';
      inp.addEventListener('change', function () {
        var fl = inp.files && inp.files[0];
        if (fl) _impReadFile(fl);
        try { inp.value = ''; } catch (e) {}
      });
      document.body.appendChild(inp);
      _imp.input = inp;
    }
    inp.click();
  }
  /* ---------- THE WALLS FROM THE PICTURE (v115) ---------------------------
     Marek: pricky divoke -- musi se postavit rozumny pudorys, pokud to model
     nedokaze idealne analyzovat. The reader is good at what a room is called
     and where a door is, and bad at where a wall is to the centimetre: Opus
     put the bathroom's wall 30 cm off the hall's line, took the open leaf of
     a door for a wall and missed the closet's. The picture itself knows: a
     wall is drawn as a THICK dark band (8 to 13 px on a 519 px sales plan),
     everything else -- a door leaf, a fixture, a dimension, the letters -- as
     a thin line. So the walls are read off the pixels, here in the browser,
     for nothing: every column is walked for dark runs as thick as a wall
     (a wall along x crossed), every row the same way (a wall along z), the
     runs that line up become one wall, a break no wider than the wall is a
     junction and is closed, a break wider than that is a door, a window or
     a passage and stays. What is short and touches nothing -- a heavy
     letter -- goes. The reader's answer still names everything; only the
     lines come from here, and the reader's own walls are kept where the
     picture has none (a thin wall, a hand sketch).
     Out: { t, thr, segs: [{ o, c, a0, a1, t }] } in the picture's pixels,
     o 'h' a wall along x at y = c from x = a0 to a1, 'v' along y at x = c. */
  /* THREE WAYS A WALL IS DRAWN (v116, Marek: u tohoto pudorysu se to zase
     rozbilo -- chybi pricka mezi kitchen a bathroom). The sales plan had
     black walls; the SNAPADU plan draws its outer walls as two black lines
     with a mid-grey fill and its inner walls as two black lines with a pale
     grey fill, so at the one darkness used the picture showed no wall at all
     -- only the big black title letters, outside the house -- and the import
     then threw every wall the reader gave away. The picture is now read at
     three darknesses (black; mid grey; pale grey), and only grey counts:
     coloured ink -- blue room names, a red fireplace, a green patio -- is
     never a wall. Which of the three is used is decided once the reader's
     outline is known (_impPixMerge); before that, the first whose long
     walls run at least six tenths of the picture's width and height is the
     one traced for the reader. */
  var IMP_WALL_THR = [110, 165, 235];
  function _impWalls(px, w, h) {
    var n = w * h, i, gray = new Uint8Array(n), sat = new Uint8Array(n);
    for (i = 0; i < n; i++) {
      var r0 = px[i * 4], g0 = px[i * 4 + 1], b0 = px[i * 4 + 2];
      gray[i] = (r0 * 299 + g0 * 587 + b0 * 114) / 1000;
      sat[i] = Math.max(r0, g0, b0) - Math.min(r0, g0, b0);
    }
    /* HOW MUCH OF IT IS IN COLOUR (v133): a render of the plan in 3D -- wood
       floors, tiles, a white wall between -- is a third colour; a drawing,
       even a cream sales sheet, has almost none (a pixel counts when its
       channels differ by more than 50) */
    var nCol = 0;
    for (i = 0; i < n; i++) if (sat[i] > 50) nCol++;
    var colorFrac = n ? nCol / n : 0;
    var V = [], k, pick = -1, best = -1, bestS = -1;
    for (k = 0; k < IMP_WALL_THR.length; k++) {
      var r = _impWallsAt(gray, sat, w, h, IMP_WALL_THR[k]);
      r.score = 0;
      for (i = 0; i < r.segs.length; i++) if (r.segs[i].a1 - r.segs[i].a0 >= 10 * r.segs[i].t) r.score += r.segs[i].a1 - r.segs[i].a0;
      r.score = Math.round(r.score);
      V.push(r);
      if (pick < 0 && r.score >= 0.6 * (w + h)) pick = k;
      if (r.score > bestS) { bestS = r.score; best = k; }
    }
    if (pick < 0) pick = best;
    /* THIN WALLS (v188, plan 1000076007, Marek: model dobre neprecte dvere,
       jejich umisteni -- tam kde maji byt je neda, kde nejsou, tam da). The
       sales plan draws its partitions about 4 px thick on a 1280 px sheet;
       the trace took nothing under 6 px as a wall, found the outer walls
       only, and the reader's own walls and doors were used -- doors where
       the drawing has none, none where it has them, a different plan at
       every reading. Read again with walls from 3 px (the dark greys only),
       the partitions come out with a break at every door, as drawn. Kept
       beside the usual readings for the merge, never sent to the reader. */
    var TT = Math.max(3, Math.round(Math.min(w, h) * 0.0035)), TN = Math.max(5, Math.round(Math.min(w, h) * 0.0075));
    if (TT < TN) {
      for (k = 0; k < IMP_WALL_THIN_THR.length; k++) {
        var rT = _impWallsAt(gray, sat, w, h, IMP_WALL_THIN_THR[k], TT);
        rT.thin = true; rT.score = 0;
        V.push(rT);
      }
    }
    var P = V[pick];
    /* the grey picture is kept for the doors (v117): their leaves are read
       off it once the walls are settled -- and the paper's own grey, the
       middle of all its pixels, so a cream sales plan and a white CAD sheet
       are told dark against their own background */
    var HG = new Uint32Array(256), acc = 0, bg = 255;
    for (i = 0; i < n; i++) HG[gray[i]]++;
    for (k = 0; k < 256; k++) { acc += HG[k]; if (acc >= n / 2) { bg = k; break; } }
    return { t: P.t, thr: P.thr, tmin: P.tmin, segs: P.segs, pick: pick, variants: V, gray: gray, w: w, h: h, bg: bg, color: colorFrac };
  }
  var IMP_WALL_THIN_THR = [110, 165], IMP_THIN_MERGE = false, IMP_DARK_FIRST = false;
  /* ---------- THE NUMBERED READING (v194) ---------------------------------
     Marek: nedari se nam dobre detekovat pricky a dvere, coz je
     nejdulezitejsi; zaklad je vlozit to co je skutecne na vlozenem fp. The
     reader gave walls and doors as coordinates by eye, and every reading of
     the same plan was a different plan. Now the page finds every line the
     drawing may draw as a wall (the usual reading and the thin one, the
     black ink only), and every break between two of them on one line --
     numbers them on the picture the reader gets (S1.. along the lines,
     G1.. in the breaks) and sends the list beside it. The reader answers
     which S are walls and which G are doors, of what kind, between which
     rooms; the walls are then built exactly where the drawing has them,
     the doors exactly in their breaks. The reader's own walls and doors
     stay the fallback. */
  var IMP_MARKS_ON = true, IMP_MARKS_MAX = 220;
  /* LETTERS ARE SMALL ISLANDS OF INK (v194): every dark blot of the drawing
     that stands alone and fits in a small box -- a letter, a digit, a dot
     -- is lettering; the walls, joined to each other, are one big blot. A
     stroke lying mostly on lettering is not numbered. */
  function _impSmallInk(PW, thr) {
    var W = PW.w, Hh = PW.h, G = PW.gray, n = W * Hh, lab = new Int32Array(n), small = new Uint8Array(n), i, cur = 0;
    if (!G || !W || !Hh) return null;
    var S = Math.max(12, Math.round(Math.min(W, Hh) * 0.035)), stack = new Int32Array(n), pix = [];
    for (i = 0; i < n; i++) {
      if (lab[i] || G[i] >= thr) continue;
      cur++;
      var sp = 0, x0 = 1e9, x1 = -1, y0 = 1e9, y1 = -1, cnt = 0;
      stack[sp++] = i; lab[i] = cur; pix.length = 0;
      while (sp) {
        var p = stack[--sp], px = p % W, py = (p - px) / W;
        pix.push(p); cnt++;
        if (px < x0) x0 = px; if (px > x1) x1 = px; if (py < y0) y0 = py; if (py > y1) y1 = py;
        if (px > 0 && !lab[p - 1] && G[p - 1] < thr) { lab[p - 1] = cur; stack[sp++] = p - 1; }
        if (px < W - 1 && !lab[p + 1] && G[p + 1] < thr) { lab[p + 1] = cur; stack[sp++] = p + 1; }
        if (py > 0 && !lab[p - W] && G[p - W] < thr) { lab[p - W] = cur; stack[sp++] = p - W; }
        if (py < Hh - 1 && !lab[p + W] && G[p + W] < thr) { lab[p + W] = cur; stack[sp++] = p + W; }
      }
      if (x1 - x0 + 1 <= S && y1 - y0 + 1 <= S) for (var k = 0; k < pix.length; k++) small[pix[k]] = 1;
    }
    return { small: small, S: S };
  }
  function _impMarks(PW) {
    if (!PW || !PW.variants || !PW.variants.length || (PW.color >= IMP_RENDER_COLOR)) return null;
    var base = PW.variants[PW.pick] || PW.variants[0], thin = null, k, q;
    for (k = 0; k < PW.variants.length; k++) if (PW.variants[k].thin && PW.variants[k].thr === base.thr) thin = PW.variants[k];
    if (!thin) for (k = 0; k < PW.variants.length; k++) if (PW.variants[k].thin) { thin = PW.variants[k]; break; }
    var S = [];
    function add(L) { for (var i = 0; L && i < L.length; i++) S.push({ o: L[i].o, c: L[i].c, a0: L[i].a0, a1: L[i].a1, t: L[i].t || 4 }); }
    add(base.segs); add(thin && thin.segs);
    if (S.length < 4) return null;
    var TS = S.map(function (x) { return x.t; }).sort(function (a, b) { return a - b; }), tm = Math.max(3, TS[TS.length >> 1]);
    /* one line drawn once: strokes on the same line that overlap or nearly
       touch are one; the thicker reading's centre wins */
    S.sort(function (a, b) { return (a.o < b.o ? -1 : a.o > b.o ? 1 : 0) || (a.c - b.c) || (a.a0 - b.a0); });
    var M = [], again = true, guard = 0;
    for (k = 0; k < S.length; k++) M.push(S[k]);
    while (again && guard++ < 50) {
      again = false;
      for (k = 0; k < M.length && !again; k++) for (q = k + 1; q < M.length; q++) {
        var A = M[k], B = M[q];
        if (A.o !== B.o || Math.abs(A.c - B.c) > Math.max(2, 0.6 * Math.max(A.t, B.t))) continue;
        var gp = Math.max(A.a0, B.a0) - Math.min(A.a1, B.a1);
        if (gp > Math.max(3, 0.8 * Math.min(A.t, B.t))) continue;
        var thick = A.t >= B.t ? A : B;
        A.c = thick.c; A.t = Math.max(A.t, B.t); A.a0 = Math.min(A.a0, B.a0); A.a1 = Math.max(A.a1, B.a1);
        M.splice(q, 1); again = true; break;
      }
    }
    M = M.filter(function (x) { return x.a1 - x.a0 >= Math.max(2.5 * x.t, 8); });
    var SI = null;
    try { SI = _impSmallInk(PW, Math.max(base.thr, thin ? thin.thr : 0)); } catch (eSI) { SI = null; }
    if (SI) {
      M = M.filter(function (x) {
        var nS = 0, nA = 0, a;
        for (a = x.a0; a <= x.a1; a += 2) {
          var px = Math.round(x.o === 'h' ? a : x.c), py = Math.round(x.o === 'h' ? x.c : a), hit = false, d;
          for (d = -Math.ceil(x.t / 2); d <= Math.ceil(x.t / 2) && !hit; d++) {
            var qx = x.o === 'h' ? px : px + d, qy = x.o === 'h' ? py + d : py;
            if (qx >= 0 && qy >= 0 && qx < PW.w && qy < PW.h && SI.small[qy * PW.w + qx]) hit = true;
          }
          nA++; if (hit) nS++;
        }
        return !nA || nS / nA < 0.5;
      });
    }
    /* a stroke touching no line across it at either end, and short, is the
       bar of a letter, a counter's edge, a tub's rim -- not numbered */
    function touchesAcross(E) {
      var e9, j9;
      for (e9 = 0; e9 < 2; e9++) {
        var end = e9 ? E.a1 : E.a0;
        for (j9 = 0; j9 < M.length; j9++) {
          var P9 = M[j9];
          if (P9 === E || P9.o === E.o) continue;
          if (Math.abs(P9.c - end) <= P9.t / 2 + E.t + 2 && E.c >= P9.a0 - E.t - 2 && E.c <= P9.a1 + E.t + 2) return true;
          /* or crosses it */
          if (P9.c > E.a0 && P9.c < E.a1 && E.c >= P9.a0 - 2 && E.c <= P9.a1 + 2) return true;
        }
        /* or a wall that steps: a line beside it, a wall width or two off,
           going on where this one ends */
        for (j9 = 0; j9 < M.length; j9++) {
          var Q9 = M[j9], tq = Math.max(Q9.t, E.t);
          if (Q9 === E || Q9.o !== E.o || Math.abs(Q9.c - E.c) > 2.5 * tq || Math.abs(Q9.c - E.c) < 0.5 * tq) continue;
          if (e9 ? (Q9.a0 >= E.a1 - 1.5 * tq && Q9.a0 <= E.a1 + 1.5 * tq) : (Q9.a1 >= E.a0 - 1.5 * tq && Q9.a1 <= E.a0 + 1.5 * tq)) return true;
        }
      }
      return false;
    }
    M = M.filter(function (x) { return touchesAcross(x) || (x.a1 - x.a0) >= 10 * tm; });
    /* and lettering: its bars and stems touch each other, never a wall --
       a short stroke stays only when it touches a long line */
    function touchesLong(E) {
      for (var j9 = 0; j9 < M.length; j9++) {
        var P9 = M[j9];
        if (P9 === E || P9.o === E.o || (P9.a1 - P9.a0) < 12 * tm) continue;
        var nearEnd = Math.min(Math.abs(P9.c - E.a0), Math.abs(P9.c - E.a1)) <= P9.t / 2 + E.t + 2;
        var crosses = P9.c > E.a0 && P9.c < E.a1;
        if ((nearEnd || crosses) && E.c >= P9.a0 - E.t - 2 && E.c <= P9.a1 + E.t + 2) return true;
      }
      return false;
    }
    /* a line with a row of short stems on it is a word's baseline: the
       letters merge into one stroke along their feet -- it goes, and the
       stems with it */
    var DROP = [];
    for (k = 0; k < M.length; k++) {
      var Lw = M[k], stems = [];
      if ((Lw.a1 - Lw.a0) > 60 * tm) continue;
      for (q = 0; q < M.length; q++) {
        var St = M[q];
        if (St === Lw || St.o === Lw.o || (St.a1 - St.a0) > 5 * tm) continue;
        if (St.c >= Lw.a0 - 2 && St.c <= Lw.a1 + 2 && Lw.c >= St.a0 - Lw.t - 2 && Lw.c <= St.a1 + Lw.t + 2) stems.push(St);
      }
      /* letters stand close: four stems at least, one in every 3.5 wall
         widths or closer (a door's leaves and jambs on a wall stand further
         apart) */
      if (stems.length >= 4) {
        var sx = stems.map(function (x9) { return x9.c; }).sort(function (a, b) { return a - b; });
        if ((sx[sx.length - 1] - sx[0]) / (stems.length - 1) <= 3.5 * tm) { DROP.push(Lw); stems.forEach(function (x9) { DROP.push(x9); }); }
      }
    }
    if (DROP.length) M = M.filter(function (x) { return DROP.indexOf(x) < 0; });
    M = M.filter(function (x) { return (x.a1 - x.a0) >= 6 * tm || touchesLong(x); });
    /* the house's box: breaks on its sides are windows and outside doors,
       which the reader reads as before */
    var BX0 = 1e9, BX1 = -1e9, BY0 = 1e9, BY1 = -1e9;
    for (k = 0; k < M.length; k++) {
      if (M[k].o === 'h') { BY0 = Math.min(BY0, M[k].c); BY1 = Math.max(BY1, M[k].c); BX0 = Math.min(BX0, M[k].a0); BX1 = Math.max(BX1, M[k].a1); }
      else { BX0 = Math.min(BX0, M[k].c); BX1 = Math.max(BX1, M[k].c); BY0 = Math.min(BY0, M[k].a0); BY1 = Math.max(BY1, M[k].a1); }
    }
    function onBox(o, c) { return o === 'h' ? (Math.abs(c - BY0) <= 3 * tm || Math.abs(c - BY1) <= 3 * tm) : (Math.abs(c - BX0) <= 3 * tm || Math.abs(c - BX1) <= 3 * tm); }
    /* the breaks: between two strokes on one line, and from a stroke's free
       end to the first stroke across its line -- a door's width, 3 to 25
       wall widths (a thin partition is about 4 to 6 in) */
    /* a break as narrow as a door can be, as wide as a passage: from 1.2
       wall widths (10 px at least) to 15 % of the house's long side */
    var G = [], lo9 = Math.max(10, 1.2 * tm), hi9 = Math.max(25 * tm, 0.15 * Math.max(BX1 - BX0, BY1 - BY0));
    function across(x, c0, from, dir, o) {
      var best = null;
      for (var j = 0; j < M.length; j++) {
        var P = M[j];
        if (P.o === o) continue;
        if (c0 < P.a0 - P.t || c0 > P.a1 + P.t) continue;
        var d = (P.c - from) * dir;
        if (d > P.t * 0.5 && d <= hi9 && (best == null || d < best.d)) best = { d: d, at: P.c - dir * P.t / 2 };
      }
      return best;
    }
    /* the picture itself says where a break ends: walking along the line
       from a stroke's end, past its own ink, the first ink as wide as a wall
       across the line and at least 0.6 wall widths deep along it (a door's
       leaf or arc is a hair line and does not stop the walk; lettering is
       skipped) -- also where that wall was too short to be traced */
    var GY = PW.gray, thrS = base.thr, runNeed = Math.max(3, Math.round(0.6 * tm));
    function darkAt(o, c, a, half) {
      var dk = 0, tot = 0;
      for (var d = -half; d <= half; d++) {
        var px = o === 'h' ? a : Math.round(c) + d, py = o === 'h' ? Math.round(c) + d : a;
        if (px < 0 || py < 0 || px >= PW.w || py >= PW.h) return -1;
        var ii = py * PW.w + px; tot++;
        if (GY[ii] < thrS && !(SI && SI.small[ii])) dk++;
      }
      return dk >= 0.6 * tot ? 1 : 0;
    }
    /* distance from the end to the next wall's ink; -1 = the stroke goes on
       (its own ink runs further than 2 wall widths) or the picture ends */
    function inkAhead(E, end, dir) {
      if (!GY) return null;
      var half = Math.max(1, Math.floor(Math.min(E.t, tm) / 2)), s = 0, v;
      while (s <= 2 * E.t + 2) { v = darkAt(E.o, E.c, Math.round(end + dir * (s + 1)), half); if (v < 0) return -1; if (!v) break; s++; }
      if (s > 2 * E.t + 2) return -1;
      var run = 0, st = null;
      for (; s <= hi9 + runNeed + 2; s++) {
        v = darkAt(E.o, E.c, Math.round(end + dir * (s + 1)), half);
        if (v < 0) return -1;
        if (v) { if (st == null) st = s + 1; run++; if (run >= runNeed) return st; } else { run = 0; st = null; }
      }
      return null;
    }
    /* a break is open: no wall's ink inside it (beyond a wall width from
       either end), and no wall running on beside it -- a wall that steps
       aside by its own width is no break */
    function openBetween(o, c, a0, a1, t9) {
      if (!GY) return 0;
      var half = Math.max(1, Math.round(t9 / 2)), run = 0, a, nPos = 0, nSide = 0, need = Math.max(3, Math.round(0.8 * t9)), B = Math.round(2.5 * t9);
      for (a = Math.round(a0 + 1.5 * t9); a <= a1 - 1.5 * t9; a++) {
        var v = darkAt(o, c, a, half);
        if (v > 0) { run++; if (run >= runNeed) return 1; } else run = 0;
        var r2 = 0, side = false;
        for (var d = -B; d <= B && !side; d++) {
          var px = o === 'h' ? a : Math.round(c) + d, py = o === 'h' ? Math.round(c) + d : a;
          if (px < 0 || py < 0 || px >= PW.w || py >= PW.h) { r2 = 0; continue; }
          var ii = py * PW.w + px;
          if (GY[ii] < thrS && !(SI && SI.small[ii])) { r2++; if (r2 >= need) side = true; } else r2 = 0;
        }
        nPos++; if (side) nSide++;
      }
      return (!nPos || nSide < 0.5 * nPos) ? 0 : 2;
    }
    /* a break with no wall beyond it on one side lies in an outer wall (a
       window, an outside door): the reader reads those as before */
    function enclosed(o, c, a0, a1) {
      var L = Math.max(a1 - a0, 10 * tm), lo = false, hi = false;
      for (var j = 0; j < M.length && !(lo && hi); j++) {
        var P = M[j];
        if (P.o !== o || P.a1 < a0 - L || P.a0 > a1 + L) continue;
        if (P.c < c - 3 * tm) lo = true; else if (P.c > c + 3 * tm) hi = true;
      }
      return lo && hi;
    }
    var byLine = {};
    for (k = 0; k < M.length; k++) {
      var key = M[k].o + Math.round(M[k].c / Math.max(2, tm));
      (byLine[key] = byLine[key] || []).push(M[k]);
    }
    for (var key2 in byLine) {
      if (!byLine.hasOwnProperty(key2)) continue;
      var LN = byLine[key2].sort(function (a, b) { return a.a0 - b.a0; });
      for (k = 0; k + 1 < LN.length; k++) {
        var g = LN[k + 1].a0 - LN[k].a1;
        if (g >= lo9 && g <= hi9 && !onBox(LN[k].o, LN[k].c) && enclosed(LN[k].o, LN[k].c, LN[k].a1, LN[k + 1].a0) && !openBetween(LN[k].o, (LN[k].c + LN[k + 1].c) / 2, LN[k].a1, LN[k + 1].a0, Math.min(LN[k].t, LN[k + 1].t)))
          G.push({ o: LN[k].o, c: (LN[k].c + LN[k + 1].c) / 2, a0: LN[k].a1, a1: LN[k + 1].a0 });
      }
    }
    for (k = 0; k < M.length; k++) {
      var E = M[k], e2;
      for (e2 = 0; e2 < 2; e2++) {
        var end = e2 ? E.a1 : E.a0, dir = e2 ? 1 : -1, held = false, j2;
        for (j2 = 0; j2 < M.length && !held; j2++) {
          var P2 = M[j2];
          if (P2.o === E.o) continue;
          if (Math.abs(P2.c - end) <= P2.t / 2 + E.t && E.c >= P2.a0 - E.t && E.c <= P2.a1 + E.t) held = true;
        }
        if (held || onBox(E.o, E.c)) continue;
        var ac = across(E, E.c, end, dir, E.o), ink = inkAhead(E, end, dir), stop = null;
        if (ink === -1) continue;
        /* the nearer of the traced line across and the ink seen ahead */
        if (ink != null && ink < lo9) continue;
        if (ink != null && (!ac || ink < ac.d - 2 * tm)) stop = end + dir * ink;
        else if (ac) stop = ac.at;
        if (stop == null) continue;
        var ga = dir > 0 ? end : stop, gb = dir > 0 ? stop : end;
        if (!enclosed(E.o, E.c, ga, gb)) continue;
        var ob = openBetween(E.o, E.c, ga, gb, E.t);
        /* the wall steps aside and goes on: one wall, drawn on to there */
        if (ob === 2) { if (dir > 0) E.a1 = Math.round(stop); else E.a0 = Math.round(stop); continue; }
        if (ob) continue;
        if (gb - ga < lo9) continue;
        var dup = false;
        for (j2 = 0; j2 < G.length && !dup; j2++) if (G[j2].o === E.o && Math.abs(G[j2].c - E.c) <= 2 * tm && Math.abs(G[j2].a0 - ga) <= 2 * tm && Math.abs(G[j2].a1 - gb) <= 2 * tm) dup = true;
        if (!dup) G.push({ o: E.o, c: E.c, a0: ga, a1: gb });
      }
    }
    function mid(x) { return x.o === 'h' ? [(x.a0 + x.a1) / 2, x.c] : [x.c, (x.a0 + x.a1) / 2]; }
    function ord(a, b) { var A2 = mid(a), B2 = mid(b); return (Math.round(A2[1] / 20) - Math.round(B2[1] / 20)) || (A2[0] - B2[0]); }
    M.sort(ord); G.sort(ord);
    if (M.length + G.length > IMP_MARKS_MAX) {
      M = M.slice().sort(function (a, b) { return (b.a1 - b.a0) - (a.a1 - a.a0); }).slice(0, Math.max(10, IMP_MARKS_MAX - G.length)).sort(ord);
      if (M.length + G.length > IMP_MARKS_MAX) G = G.slice(0, IMP_MARKS_MAX - M.length);
    }
    var out = { t: tm, thr: base.thr, segs: [], gaps: [] };
    for (k = 0; k < M.length; k++) out.segs.push({ id: 'S' + (k + 1), o: M[k].o, c: Math.round(M[k].c * 10) / 10, a0: Math.round(M[k].a0), a1: Math.round(M[k].a1), t: M[k].t });
    for (k = 0; k < G.length; k++) out.gaps.push({ id: 'G' + (k + 1), o: G[k].o, c: Math.round(G[k].c * 10) / 10, a0: Math.round(G[k].a0), a1: Math.round(G[k].a1) });
    return out;
  }
  /* THE PLAN FROM THE MARKS (v194): the S the reader named walls are the
     walls, the G it named doors are the doors, in their breaks; its own
     outer doors, windows, rooms, fittings, dimensions and outline as
     before. Where it drew a wall along which no named S lies (a stroke too
     faint or too broken to trace), its own wall is kept for that stretch.
     Fewer than three named walls: nothing changes (the fallback). */
  function _impFromMarks(raw, plan, MK, PW) {
    if (!MK || !plan) return null;
    var wm = Array.isArray(plan.wall_marks) ? plan.wall_marks : [], gm = Array.isArray(plan.gap_marks) ? plan.gap_marks : [];
    if (!wm.length) return { used: false, why: 'the reader named no walls among the ' + MK.segs.length + ' marks' };
    var SET = {}, k, q;
    wm.forEach(function (id) { SET[String(id).toUpperCase()] = 1; });
    var W = MK.segs.filter(function (x) { return SET[x.id]; });
    if (W.length < 3) return { used: false, why: 'the reader named ' + W.length + ' wall(s) only' };
    var R2 = JSON.parse(JSON.stringify(raw)), O = R2.outline || [], t = MK.t || 4;
    function onOutline(S) {
      for (var i = 0; i < O.length; i++) {
        var A = O[i], B = O[(i + 1) % O.length], ho = Math.abs(B[0] - A[0]) >= Math.abs(B[1] - A[1]);
        if ((ho ? 'h' : 'v') !== S.o) continue;
        var c = ho ? (A[1] + B[1]) / 2 : (A[0] + B[0]) / 2, lo = ho ? Math.min(A[0], B[0]) : Math.min(A[1], B[1]), hi = ho ? Math.max(A[0], B[0]) : Math.max(A[1], B[1]);
        if (Math.abs(c - S.c) <= Math.max(3 * S.t, 12) && Math.min(hi, S.a1) - Math.max(lo, S.a0) > 0.5 * (S.a1 - S.a0)) return true;
      }
      return false;
    }
    var IN = W.filter(function (x) { return !onOutline(x); });
    var parts = IN.map(function (x) { return x.o === 'h' ? { a: [x.a0, x.c], b: [x.a1, x.c], px: true, mk: x.id } : { a: [x.c, x.a0], b: [x.c, x.a1], px: true, mk: x.id }; });
    /* the doors: the named breaks */
    var GD = {}, doors = [], nDoor = 0, nOpen = 0;
    MK.gaps.forEach(function (g) { GD[g.id] = g; });
    /* the reader's usual inside door width, for a break wider than a door */
    var DWs = (raw.doors || []).filter(function (d) { return d && d.w > 0 && !(d.rooms || []).some(function (r) { return /^(outside|outdoors?|exterior)$/i.test(String(r)); }); })
                               .map(function (d) { return Number(d.w); }).sort(function (a, b) { return a - b; });
    var dwUsual = DWs.length ? DWs[DWs.length >> 1] : 0;
    gm.forEach(function (g) {
      var G = GD[String(g.id || '').toUpperCase()];
      if (!G) return;
      var kind = String(g.kind || 'hinged'), len = G.a1 - G.a0, ctr = (G.a0 + G.a1) / 2, w = len;
      /* a door narrower than its break: the reader's own door in the break
         gives its width and place; none, its usual width in the middle */
      if (kind !== 'opening') {
        var own = null, k3;
        for (k3 = 0; k3 < (raw.doors || []).length && !own; k3++) {
          var d3 = raw.doors[k3];
          if (!d3 || !d3.at || !(d3.w > 0)) continue;
          var al3 = G.o === 'h' ? d3.at[0] : d3.at[1], ac3 = G.o === 'h' ? d3.at[1] : d3.at[0];
          if (Math.abs(ac3 - G.c) <= Math.max(3 * t, 14) && al3 >= G.a0 - 4 && al3 <= G.a1 + 4) own = d3;
        }
        var want = own ? Number(own.w) : (dwUsual > 0 ? dwUsual * (/double|bypass/.test(kind) ? 1.6 : 1) : len);
        if (want > 0 && want < len * 0.85) {
          w = want;
          var c3 = own ? (G.o === 'h' ? own.at[0] : own.at[1]) : ctr;
          ctr = Math.max(G.a0 + w / 2, Math.min(G.a1 - w / 2, c3));
        }
      }
      var at = G.o === 'h' ? [ctr, G.c] : [G.c, ctr];
      doors.push({ at: at, w: w, kind: kind, label: '', rooms: Array.isArray(g.rooms) ? g.rooms : [], hinge: null, opens_to: null, mk: G.id });
      if (kind === 'opening') nOpen++; else nDoor++;
    });
    /* a break left unnamed, narrower than any door and held by named walls
       at both ends, is the wall itself, broken in the tracing */
    var NAMED = {}, shut = 0;
    gm.forEach(function (x) { NAMED[String(x.id || '').toUpperCase()] = 1; });
    function heldAt(P) {
      for (var i = 0; i < W.length; i++) {
        var S = W[i], ax = S.o === 'h' ? [S.a0, S.c] : [S.c, S.a0], bx = S.o === 'h' ? [S.a1, S.c] : [S.c, S.a1];
        var tx = Math.max(ax[0], Math.min(bx[0], P[0])), ty = Math.max(ax[1], Math.min(bx[1], P[1]));
        if (Math.hypot(tx - P[0], ty - P[1]) <= 2 * Math.max(t, S.t)) return true;
      }
      return false;
    }
    if (dwUsual > 0) MK.gaps.forEach(function (g) {
      if (NAMED[g.id] || (g.a1 - g.a0) >= 0.7 * dwUsual) return;
      var A = g.o === 'h' ? [g.a0, g.c] : [g.c, g.a0], B = g.o === 'h' ? [g.a1, g.c] : [g.c, g.a1];
      if (!heldAt(A) || !heldAt(B)) return;
      parts.push({ a: A, b: B, px: true, mk: g.id }); shut++;
    });
    /* the reader's own walls where no named stroke lies along them */
    var fills = 0, keptD = 0;
    (raw.partitions || []).forEach(function (p) {
      var a = p.a, b = p.b;
      if (!a || !b) return;
      var dx = b[0] - a[0], dy = b[1] - a[1], ho = Math.abs(dx) >= Math.abs(dy);
      if (Math.min(Math.abs(dx), Math.abs(dy)) > 0.35 * Math.max(Math.abs(dx), Math.abs(dy))) { parts.push({ a: a, b: b }); return; }
      var c = ho ? (a[1] + b[1]) / 2 : (a[0] + b[0]) / 2, lo = ho ? Math.min(a[0], b[0]) : Math.min(a[1], b[1]), hi = ho ? Math.max(a[0], b[0]) : Math.max(a[1], b[1]);
      var IV = W.concat(MK.gaps.filter(function (g) { return GD[g.id] && gm.some(function (x) { return String(x.id || '').toUpperCase() === g.id; }); }))
                .filter(function (x) { return x.o === (ho ? 'h' : 'v') && Math.abs(x.c - c) <= Math.max(3 * t, 14); })
                .map(function (x) { return [x.a0, x.a1]; }).sort(function (u, v) { return u[0] - v[0]; });
      var at0 = lo, gaps = [];
      for (k = 0; k < IV.length; k++) { if (IV[k][0] - at0 > 4 * t) gaps.push([at0, Math.min(IV[k][0], hi)]); at0 = Math.max(at0, IV[k][1]); }
      if (hi - at0 > 4 * t) gaps.push([at0, hi]);
      gaps.forEach(function (gp) {
        if (gp[1] - gp[0] <= 4 * t) return;
        parts.push(ho ? { a: [gp[0], c], b: [gp[1], c] } : { a: [c, gp[0]], b: [c, gp[1]] });
        fills++;
        /* and the reader's door in that stretch */
        (raw.doors || []).forEach(function (d) {
          if (!d.at || (d.rooms || []).some(function (r) { return /^(outside|outdoors?|exterior)$/i.test(String(r)); })) return;
          var al = ho ? d.at[0] : d.at[1], ac = ho ? d.at[1] : d.at[0];
          if (Math.abs(ac - c) <= Math.max(3 * t, 14) && al >= gp[0] - 2 && al <= gp[1] + 2 && doors.indexOf(d) < 0) { doors.push(d); keptD++; }
        });
      });
    });
    /* the outer doors as the reader gave them; and an inside door of its own
       that no named break is near -- one in a wall the marks could not
       trace (a slanted corner, a wall under a bench) -- goes on to the
       usual road, which puts it on the wall nearest */
    var USED = doors.slice(), nOwn = 0;
    (raw.doors || []).forEach(function (d) {
      if (doors.indexOf(d) >= 0 || !d.at) return;
      if ((d.rooms || []).some(function (r) { return /^(outside|outdoors?|exterior|porch|patio|deck)$/i.test(String(r)); })) { doors.push(d); return; }
      var near = false, wd = Math.max(Number(d.w) || 0, 6 * t);
      function pair(x) { return (x.rooms || []).map(function (r) { return String(r).toLowerCase().trim(); }).sort().join('|'); }
      for (var u = 0; u < USED.length && !near; u++) {
        if (!USED[u].at) continue;
        var dd = Math.hypot(USED[u].at[0] - d.at[0], USED[u].at[1] - d.at[1]);
        if (dd <= 0.5 * wd || (dd <= 2 * wd && pair(USED[u]) === pair(d))) near = true;
      }
      if (!near) { doors.push(d); nOwn++; }
    });
    keptD += nOwn;
    R2.partitions = parts;
    R2.doors = doors;
    var V = { thr: MK.thr, t: t, segs: W.map(function (x) { return { o: x.o, c: x.c, a0: x.a0, a1: x.a1, t: x.t }; }), score: 0 };
    var PWm = { t: t, thr: MK.thr, segs: V.segs, pick: 0, variants: [V], gray: PW && PW.gray, w: PW && PW.w, h: PW && PW.h, bg: PW && PW.bg, color: PW ? PW.color : 0, tmin: PW && PW.tmin };
    return { used: true, raw: R2, PW: PWm, sent: MK.segs.length + ' line(s), ' + MK.gaps.length + ' break(s)', walls: W.length, inner: IN.length, doors: nDoor, openings: nOpen, fills: fills, keptDoors: keptD, shut: shut };
  }
  function _impMarksOut(MK) {
    if (!MK || !MK.segs || MK.segs.length < 6) return undefined;
    function row(x) { return [x.id, x.o, Math.round(x.c), x.a0, x.a1]; }
    return { s: MK.segs.map(row), g: MK.gaps.map(row) };
  }
  /* the marks on the picture the reader gets: each line in colour with its
     number beside it, each break in green with its number in it */
  function _impMarksDraw(cx, MK) {
    var k;
    cx.save();
    cx.lineCap = 'butt';
    for (k = 0; k < MK.segs.length; k++) {
      var S = MK.segs[k];
      cx.strokeStyle = (S.o === 'h') ? '#FF00FF' : '#00A0FF';
      cx.lineWidth = Math.max(2, Math.round(S.t * 0.35));
      cx.beginPath();
      if (S.o === 'h') { cx.moveTo(S.a0, S.c); cx.lineTo(S.a1, S.c); } else { cx.moveTo(S.c, S.a0); cx.lineTo(S.c, S.a1); }
      cx.stroke();
    }
    for (k = 0; k < MK.gaps.length; k++) {
      var Gq = MK.gaps[k];
      cx.strokeStyle = '#00B050'; cx.lineWidth = 2;
      cx.setLineDash ? cx.setLineDash([3, 2]) : 0;
      cx.beginPath();
      if (Gq.o === 'h') { cx.moveTo(Gq.a0, Gq.c); cx.lineTo(Gq.a1, Gq.c); } else { cx.moveTo(Gq.c, Gq.a0); cx.lineTo(Gq.c, Gq.a1); }
      cx.stroke();
    }
    if (cx.setLineDash) cx.setLineDash([]);
    cx.font = 'bold 10px Arial'; cx.textAlign = 'center'; cx.textBaseline = 'middle'; cx.lineJoin = 'round';
    function tag(txt, x, y, col) { cx.lineWidth = 3; cx.strokeStyle = '#FFFFFF'; cx.strokeText(txt, x, y); cx.fillStyle = col; cx.fillText(txt, x, y); }
    for (k = 0; k < MK.segs.length; k++) {
      var S2 = MK.segs[k], off = S2.t / 2 + 7;
      if (S2.o === 'h') tag(S2.id, (S2.a0 + S2.a1) / 2, S2.c - off, '#B00000'); else tag(S2.id, S2.c + off + 4, (S2.a0 + S2.a1) / 2, '#B00000');
    }
    for (k = 0; k < MK.gaps.length; k++) {
      var G2 = MK.gaps[k];
      if (G2.o === 'h') tag(G2.id, (G2.a0 + G2.a1) / 2, G2.c + 9, '#007A30'); else tag(G2.id, G2.c - 12, (G2.a0 + G2.a1) / 2, '#007A30');
    }
    cx.restore();
  }
  function _impWallsAt(gray, sat, w, h, thr, tmin) {
    var n = w * h, i, minS = Math.min(w, h);
    var D = new Uint8Array(n);
    for (i = 0; i < n; i++) D[i] = (gray[i] < thr && sat[i] < 70) ? 1 : 0;
    var TMIN = tmin || Math.max(5, Math.round(minS * 0.0075)), TMAX = tmin ? Math.max(15, tmin * 5) : TMIN * 5;
    function scan(horiz) {
      /* horiz: walls along x, crossed by walking each column down */
      var S = {}, lines = horiz ? w : h, len = horiz ? h : w, a, j;
      for (a = 0; a < lines; a++) {
        j = 0;
        while (j < len) {
          if (!D[horiz ? (j * w + a) : (a * w + j)]) { j++; continue; }
          var k = j;
          while (k < len && D[horiz ? (k * w + a) : (a * w + k)]) k++;
          var t = k - j;
          if (t >= TMIN && t <= TMAX) {
            var c = (j + k - 1) / 2, key = Math.round(c);
            (S[key] = S[key] || []).push([a, c, t]);
          }
          j = k;
        }
      }
      /* centres a pixel apart are one line; along it, a gap of 3 px or less
         is one run */
      var keys = Object.keys(S).map(Number).sort(function (p, q) { return p - q; }), groups = [], g, out = [];
      for (g = 0; g < keys.length; g++) {
        if (groups.length && keys[g] - groups[groups.length - 1][groups[groups.length - 1].length - 1] <= 1) groups[groups.length - 1].push(keys[g]);
        else groups.push([keys[g]]);
      }
      for (g = 0; g < groups.length; g++) {
        var pts = [], q;
        for (q = 0; q < groups[g].length; q++) pts = pts.concat(S[groups[g][q]]);
        pts.sort(function (p, r) { return p[0] - r[0]; });
        var run = [pts[0]];
        for (q = 1; q <= pts.length; q++) {
          if (q < pts.length && pts[q][0] - run[run.length - 1][0] <= 3) { run.push(pts[q]); continue; }
          if (run[run.length - 1][0] - run[0][0] >= 6) {
            var sc = 0, ts = [];
            for (var r2 = 0; r2 < run.length; r2++) { sc += run[r2][1]; ts.push(run[r2][2]); }
            ts.sort(function (p, r) { return p - r; });
            out.push({ o: horiz ? 'h' : 'v', c: sc / run.length, a0: run[0][0], a1: run[run.length - 1][0], t: ts[ts.length >> 1], n: run.length });
          }
          if (q < pts.length) run = [pts[q]];
        }
      }
      return out;
    }
    var segs = scan(true).concat(scan(false)), again = true, a2, b2;
    /* a break no wider than the wall is where another wall crosses it */
    while (again) {
      again = false;
      for (a2 = 0; a2 < segs.length && !again; a2++) {
        for (b2 = 0; b2 < segs.length; b2++) {
          var A = segs[a2], B = segs[b2];
          if (a2 === b2 || A.o !== B.o || Math.abs(A.c - B.c) > Math.max(1.5, 0.3 * Math.max(A.t, B.t))) continue;
          var gap = B.a0 - A.a1;
          if (gap < -2 || gap > 1.5 * Math.max(A.t, B.t) + 2) continue;
          A.c = (A.c * A.n + B.c * B.n) / (A.n + B.n); A.n += B.n; A.a0 = Math.min(A.a0, B.a0); A.a1 = Math.max(A.a1, B.a1); A.t = Math.max(A.t, B.t);
          segs.splice(b2, 1); again = true; break;
        }
      }
    }
    /* short and touching nothing: a letter, a dot, a symbol -- and touching
       only another short one is touching nothing */
    var MINL = Math.max(3 * TMIN, minS * 0.05);
    function touches(S0) {
      for (var q = 0; q < segs.length; q++) {
        var P = segs[q];
        if (P === S0 || P.o === S0.o || (P.a1 - P.a0) < MINL) continue;
        var reach = P.t / 2 + S0.t + 2;
        var nearEnd = Math.min(Math.abs(P.c - S0.a0), Math.abs(P.c - S0.a1)) <= reach;
        if (nearEnd && S0.c >= P.a0 - reach && S0.c <= P.a1 + reach) return true;
      }
      return false;
    }
    segs = segs.filter(function (S0) { return (S0.a1 - S0.a0) >= Math.max(3 * S0.t, MINL) || touches(S0); });
    var tl = segs.map(function (S0) { return S0.t; }).sort(function (p, q) { return p - q; });
    return { t: tl.length ? tl[tl.length >> 1] : 0, thr: thr, tmin: TMIN, segs: segs.map(function (S0) {
      return { o: S0.o, c: Math.round(S0.c * 10) / 10, a0: S0.a0, a1: S0.a1, t: S0.t, f: Math.round(Math.min(1, S0.n / Math.max(1, S0.a1 - S0.a0 + 1)) * 100) / 100 };
    }) };
  }
  /* THE READER'S ANSWER ON THE PICTURE'S WALLS (v115), all of it still in
     the picture's pixels, before anything is straightened:
       OUTLINE    each side of the reader's outline goes onto the outer face
                  of the picture's wall beside it (within three walls' width);
       WALLS      the picture's walls inside the outline are the partitions.
                  The reader's own is kept only where the picture has no wall
                  along it and both its ends meet a wall -- a thin wall the
                  picture could not see; one in line with a picture wall is
                  that wall read a little off, and one with a free end is the
                  open leaf of a door;
       OPENINGS   a door or a window the reader put at a break in a wall is
                  moved into the middle of that break and given its width.
     With fewer than six walls in the picture (a sketch in pencil, a photo,
     walls drawn as two thin lines) nothing changes and the log says so. */
  /* THE DARKNESS THAT FITS (v116): each reading of the picture is tried on
     the reader's answer, the one traced for the reader first, and the first
     that puts at least two of the outline's sides on its walls and has at
     least a third as many walls inside as the reader drew is used. None
     fits: the reader's walls are used whole, nothing of them thrown away. */
  /* A RENDER IS NOT TRACED (v133, plan 3D render: bile steny -- jedna pricka
     uplne vypadla). Its walls are white, lighter than the wood floor beside
     them, and the darkness that finds a drawn wall finds only the edges of
     the furniture there: the trace came back in pieces under 0.40 m, the
     reader's walls were thrown out as read off-line, and the bathroom wall
     and the walk-in closet were gone. A picture a fifth or more in colour is
     a render: the reader's walls are used whole and the picture is not asked
     about walls, doors or leaves. */
  /* ---------- A WINDOW THAT IS A SLIDING DOOR (v138) -----------------------
     Marek: na horni stene 3 okna, ale to prostredni je podle puvodniho
     obrazku HS portal. The reader took the patio door of plan A for a window
     -- in the drawing it is two panels side by side in the wall, one nearer
     the outside, one nearer the inside, each over half the opening, where a
     window draws its lines the whole length. That is asked of the picture
     here, for every window the reader put in an outer wall: the opening is
     found between the two jambs of solid wall, and the wall's band is read
     row by row; a row drawn over one half only, and another row drawn over
     the other half, is the two panels of a slider. Such a window becomes an
     outer door of kind sliding -- the catalogue's HS sliders take it (v136).
     A render is not read (its walls are white). */
  function _impPicSliders(raw, PW, style) {
    if (!raw || !raw.windows || !raw.windows.length || !PW || !PW.gray || !PW.w || style === 'render' || (PW.color >= IMP_RENDER_COLOR)) return null;
    var G = PW.gray, W = PW.w, H = PW.h, bg = PW.bg || 255, t = Math.max(3, PW.t || 6), OL = raw.outline || [], i, k;
    if (OL.length < 3) return null;
    function gray(x, y) { x = Math.round(x); y = Math.round(y); return (x < 0 || y < 0 || x >= W || y >= H) ? 255 : G[y * W + x]; }
    var out = JSON.parse(JSON.stringify(raw)), keep = [], said = [], kept = [], n = 0;
    for (i = 0; i < raw.windows.length; i++) {
      var wd = raw.windows[i], P = wd.at, wpx = Number(wd.w) || 0, best = null, bd = 1e9;
      if (!P || !(wpx > 4 * t * 0.5)) { keep.push(wd); continue; }
      for (k = 0; k < OL.length; k++) {
        var A = OL[k], B = OL[(k + 1) % OL.length], ex = B[0] - A[0], ey = B[1] - A[1], L2 = ex * ex + ey * ey;
        if (!(L2 > 0)) continue;
        var tt = Math.max(0, Math.min(1, ((P[0] - A[0]) * ex + (P[1] - A[1]) * ey) / L2)), dd = Math.hypot(P[0] - A[0] - ex * tt, P[1] - A[1] - ey * tt);
        if (dd < bd) { bd = dd; best = { hz: Math.abs(ey) < Math.abs(ex) }; }
      }
      if (!best) { keep.push(wd); continue; }
      /* along / across the wall */
      var hz = best.hz;
      function gA(a, c) { return hz ? gray(a, c) : gray(c, a); }
      var a0 = hz ? P[0] : P[1], c0 = hz ? P[1] : P[0], reach = wpx * 0.75 + 6, rows = [], jl = [], jr = [], c;
      for (c = Math.round(c0 - 2 * t); c <= Math.round(c0 + 2 * t); c++) {
        var l = null, r = null, a;
        for (a = Math.round(a0); a >= a0 - reach; a--) if (gA(a, c) < 100 && gA(a - 1, c) < 100) { l = a; break; }
        for (a = Math.round(a0); a <= a0 + reach; a++) if (gA(a, c) < 100 && gA(a + 1, c) < 100) { r = a; break; }
        if (l != null && r != null && (r - l) > 0.6 * wpx && (r - l) < 1.4 * wpx) { rows.push(c); jl.push(l); jr.push(r); }
      }
      if (rows.length < 2) { keep.push(wd); continue; }
      jl.sort(function (p, q) { return p - q; }); jr.sort(function (p, q) { return p - q; });
      var JL = jl[jl.length >> 1] + 2, JR = jr[jr.length >> 1] - 2, mid = (JL + JR) / 2, lineThr = Math.min(200, bg - 40);
      var left = 0, right = 0, full = 0;
      for (k = 0; k < rows.length; k++) {
        var cL = 0, nL = 0, cR = 0, nR = 0, a2;
        for (a2 = JL; a2 <= JR; a2++) {
          var dk = gA(a2, rows[k]) < lineThr;
          if (a2 < mid) { nL++; if (dk) cL++; } else { nR++; if (dk) cR++; }
        }
        var fL = nL ? cL / nL : 0, fR = nR ? cR / nR : 0;
        if (fL >= 0.75 && fR >= 0.75) full++;
        else if (fL >= 0.75 && fR <= 0.2) left++;
        else if (fR >= 0.75 && fL <= 0.2) right++;
      }
      /* A PIECE DRAWN IN FRONT OF IT KEEPS IT A WINDOW (v165, plan 1000075648,
         Marek: vygeneroval se jakysi objekt pres dvere). A sliding window is
         drawn the same two panels as a patio slider; the bedroom window over
         the head of a drawn bed became a sliding door and the bed stood in
         its doorway. Where the reader drew a piece against the wall right
         in front of the opening, it is a window. */
      var inFront = '';
      for (k = 0; left && right && raw.fixtures && k < raw.fixtures.length && !inFront; k++) {
        var fq = raw.fixtures[k], fA = fq && (fq.a || (fq.box && fq.box[0])), fB = fq && (fq.b || (fq.box && fq.box[1]));
        if (!fq || fq.sug || fq.suggested || !fA || !fB) continue;
        var fa0 = hz ? Math.min(fA[0], fB[0]) : Math.min(fA[1], fB[1]), fa1 = hz ? Math.max(fA[0], fB[0]) : Math.max(fA[1], fB[1]);
        var fc0 = hz ? Math.min(fA[1], fB[1]) : Math.min(fA[0], fB[0]), fc1 = hz ? Math.max(fA[1], fB[1]) : Math.max(fA[0], fB[0]);
        var ovA = Math.min(fa1, JR) - Math.max(fa0, JL), gapC = Math.min(Math.abs(fc0 - c0), Math.abs(fc1 - c0));
        if (ovA >= 0.3 * (JR - JL) && gapC <= Math.max(4 * t, 0.25 * wpx)) inFront = String(fq.type || 'piece');
      }
      if (left && right && inFront) { kept.push(Math.round(wpx) + ' px wide at ' + Math.round(P[0]) + ',' + Math.round(P[1]) + ' kept a window -- a ' + inFront + ' drawn in front of it'); keep.push(wd); continue; }
      if (left && right) {
        n++;
        out.doors.push({ at: [P[0], P[1]], w: wpx, kind: 'sliding', label: 'SLIDER', hinge: null, opensTo: null, rooms: ['', 'outside'], fromWindow: true });
        said.push(Math.round(wpx) + ' px wide at ' + Math.round(P[0]) + ',' + Math.round(P[1]) + ' (' + left + ' row(s) over one half, ' + right + ' over the other, ' + full + ' whole)' + (wd.label ? (' "' + wd.label + '"') : ''));
      } else keep.push(wd);
    }
    out.windows = keep;
    return { raw: out, n: n, said: said, kept: kept };
  }
  var IMP_RENDER_COLOR = 0.2;
  /* A COLOURED DRAWING IS STILL A DRAWING (v140, plan 1000073500: Marek --
     pricky kolem koupelny v pravo se plne nevygenerovaly, mistnost bathroom
     neni uzavrena). The reader called a sales plan a render -- cream paper,
     a green patio, a red fireplace, the walls drawn solid black -- and the
     picture was not asked about walls; the reader's wall beside the master
     bath ran on through the shower, was taken for the shower's edge and
     left out, and the bath was open to the bedroom. A render's walls are
     white or shaded; a drawing's are dark bands. When the reader says
     render and the picture is under a fifth in colour, its darkest reading
     is laid on the reader's outline: every side of the outline on a dark
     wall, and at least half as many dark walls inside as the reader drew,
     is a drawing. Returns what was found, or '' for a render. */
  function _impStyleProbe(raw, PW) {
    if (!raw || !PW || !PW.variants || !PW.variants.length || (PW.color >= IMP_RENDER_COLOR)) return '';
    var nO = (raw.outline || []).length, V0 = PW.variants[0], need = Math.max(3, Math.round(0.5 * (raw.partitions || []).length));
    if (nO < 4 || !V0) return '';
    var r0 = _impPixMerge1(raw, V0, PW);
    if (!r0 || !r0.info || !r0.info.used || !(r0.info.sides >= nO) || !(r0.info.walls >= need)) return '';
    return 'its walls are drawn dark (grey under ' + V0.thr + '): all ' + nO + ' sides of the outline lie on them and ' + r0.info.walls + ' walls inside (' + need + ' wanted), ' +
           Math.round((PW.color || 0) * 100) + ' % of it in colour';
  }
  function _impPixMerge(raw, PW, style) {
    /* THE READER SAYS WHAT KIND OF PICTURE IT IS (v136, worker w1.98): a
       render in grey -- white walls, shaded floors -- has too little colour
       for the test below, and its dark lines are furniture and floors, not
       walls. A photo and a sketch are traced as a drawing is. */
    if (PW && PW.segs && style === 'render' && !(PW.color >= IMP_RENDER_COLOR)) {
      return { raw: raw, info: { used: false, render: true, color: PW.color, segs: (PW.segs || []).length, t: PW.t,
                                 why: 'the reader calls the picture a render (' + Math.round((PW.color || 0) * 100) + ' % of it in colour) -- its walls are not traced: the reader\'s walls are used, all of them (v136)' } };
    }
    if (PW && PW.color >= IMP_RENDER_COLOR) {
      return { raw: raw, info: { used: false, render: true, color: PW.color, segs: (PW.segs || []).length, t: PW.t,
                                 why: 'the picture is a render, ' + Math.round(PW.color * 100) + ' % of it in colour -- its walls are lighter than its floors, so they are not traced: the reader\'s walls are used, all of them (v133)' } };
    }
    if (!PW || !PW.variants) return _impPixMerge1(raw, PW, PW);
    var order = [PW.pick], k, tried = [], nModel = (raw.partitions || []).length, need = Math.max(3, Math.round(0.35 * nModel)), DONE = {};
    for (k = 0; k < PW.variants.length; k++) if (k !== PW.pick && (IMP_THIN_MERGE || !PW.variants[k].thin)) order.push(k);
    /* THE DARKEST THAT HOLDS THE WHOLE HOUSE (v185, plan 1000076007, the
       RANCH thread: on a coloured sales plan the palest reading, grey under
       235, was taken -- 157 lines with the edges of the coloured fills and
       the dashed 9'/10' ceiling lines among them; 104 "walls", 113
       partitions, 46 spaces, 54 tiny rooms -- where grey under 110 gives
       the 24 real ones). The readings are tried darkest first: the first
       that finds every side of the outline long enough to be drawn as a
       wall, and enough walls inside, is used. Pale and dashed lines are
       ink, not walls. None does: the order as before. */
    /* v190 (Marek: rekl bych ze ted je to horsi, D1 #85): the THIN readings
       (v188) put every thin stroke of the drawing in as a wall -- shelves,
       a tub's rim, the double lines of a closet -- and the plan was written
       with 12 slivers of 0.02 to 0.7 m2 and a hall, a bedroom and the
       office as one room (22 problems, 7 before). They are still read and
       logged, but not used for the walls until the rooms they make are
       checked. */
    var DK = PW.variants.map(function (v9, i9) { return i9; }).filter(function (i9) { return IMP_THIN_MERGE || !PW.variants[i9].thin; })
                        .sort(function (a9, b9) { return (PW.variants[a9].thr || 0) - (PW.variants[b9].thr || 0); });
    /* v191 (Marek: neni moc povedeny import, uz jsme ho udelali lepe -- ADU
       plan 1000075604, D1 #87): darkest first took grey under 110 for that
       plan, where 165 had been read before (#72, #76: audit clean); the
       darker reading lost a piece of the closet's wall and the closet ran
       into the living room. On the RANCH plan it never applied (no usual
       reading held every side). Off: the order as before v185. */
    for (k = 0; IMP_DARK_FIRST && k < DK.length; k++) {
      var VD = PW.variants[DK[k]], rD = _impPixMerge1(raw, VD, PW);
      DONE[DK[k]] = rD;
      if (rD.info.used && rD.info.longN >= 2 && rD.info.longHit === rD.info.longN && rD.info.walls >= need) {
        rD.info.thr = VD.thr;
        rD.info.tried = 'the darkest reading that finds all ' + rD.info.longN + ' long side(s) of the outline: grey under ' + VD.thr + (VD.thin ? (', THIN walls from ' + VD.tmin + ' px (v188)') : '') + ', ' + rD.info.walls + ' wall(s) (' + need + ' wanted, v185)';
        return rD;
      }
    }
    /* INK IS NOT WALLS (v186, plan 1000076007 again, the picture itself):
       its partitions are drawn thinner than the trace takes a wall, so the
       dark readings find the outer walls only (5 and 7 walls inside), and
       the pale one -- grey under 235 -- found 101 "walls" where the reader
       drew 29: the edges of the coloured fills, the dashed ceiling lines,
       the letters. Built, that was 106 partitions and the tiny rooms. A
       reading with more than two and a half times the reader's walls is
       ink: it is passed over, and with none left the reader's walls are
       used whole -- 36 partitions, the rooms as drawn. */
    var ink = Math.max(12, Math.round(2.5 * nModel));
    for (k = 0; k < order.length; k++) {
      var VV = PW.variants[order[k]], res = DONE[order[k]] || _impPixMerge1(raw, VV, PW);
      var over = res.info.used && res.info.walls > ink;
      tried.push('grey under ' + VV.thr + ': ' + (res.info.sides || 0) + ' side(s), ' + (res.info.walls || 0) + ' wall(s)' + (over ? (' -- over ' + ink + ', ink not walls (v186)') : ''));
      if (over) continue;
      if (res.info.used && res.info.sides >= 2 && res.info.walls >= need) { res.info.thr = VV.thr; res.info.tried = tried.join('; '); return res; }
    }
    return { raw: raw, info: { used: false, segs: (PW.segs || []).length, t: PW.t, why: 'the picture\'s walls do not fit the reader\'s outline (' + tried.join('; ') + ', ' + need + ' wanted, ' + ink + ' at most) -- the reader\'s walls are used, all of them' } };
  }
  function _impPixMerge1(raw, PW, GP) {
    var info = { used: false, segs: PW && PW.segs ? PW.segs.length : 0, t: PW ? PW.t : 0, walls: 0, model: 0, dropped: 0, sides: 0, doors: 0, wins: 0, why: '' };
    if (!PW || !PW.segs || PW.segs.length < 6 || !(PW.t > 0)) {
      info.why = 'the picture gave ' + info.segs + ' wall line(s) -- the reader\'s walls are used';
      return { raw: raw, info: info };
    }
    var r = JSON.parse(JSON.stringify(raw)), segs = PW.segs, t = PW.t, i, k;
    /* no outline from the reader: the box round the long walls */
    if (!r.outline || r.outline.length < 3) {
      var bx0 = 1e9, bx1 = -1e9, bz0 = 1e9, bz1 = -1e9, nb = 0;
      for (k = 0; k < segs.length; k++) {
        var sb = segs[k];
        if (sb.a1 - sb.a0 < 8 * t) continue;
        if (sb.o === 'h') { bz0 = Math.min(bz0, sb.c - sb.t / 2); bz1 = Math.max(bz1, sb.c + sb.t / 2); }
        else { bx0 = Math.min(bx0, sb.c - sb.t / 2); bx1 = Math.max(bx1, sb.c + sb.t / 2); }
        nb++;
      }
      if (nb >= 4 && bx1 > bx0 && bz1 > bz0) { r.outline = [[bx0, bz0], [bx1, bz0], [bx1, bz1], [bx0, bz1]]; info.box = true; }
    }
    var O = r.outline || [], nO = O.length, EO = [], alt = nO >= 4 && nO % 2 === 0;
    for (i = 0; i < nO; i++) {
      var A = O[i], B = O[(i + 1) % nO];
      EO.push(Math.abs(B[0] - A[0]) >= Math.abs(B[1] - A[1]) ? 'h' : 'v');
    }
    for (i = 0; alt && i < nO; i++) if (EO[i] === EO[(i + 1) % nO]) alt = false;
    var EDGE = [];
    if (alt) {
      var NC = [];
      for (i = 0; i < nO; i++) {
        var A1 = O[i], B1 = O[(i + 1) % nO], ho = EO[i] === 'h';
        var c0 = ho ? (A1[1] + B1[1]) / 2 : (A1[0] + B1[0]) / 2, lo = ho ? Math.min(A1[0], B1[0]) : Math.min(A1[1], B1[1]), hi = ho ? Math.max(A1[0], B1[0]) : Math.max(A1[1], B1[1]);
        var best = null, bd = 3 * t;
        for (k = 0; k < segs.length; k++) {
          var s0 = segs[k];
          if (s0.o !== EO[i]) continue;
          var ov = Math.min(hi, s0.a1) - Math.max(lo, s0.a0);
          if (ov < 0.3 * (hi - lo) && ov < 0.6 * (s0.a1 - s0.a0)) continue;
          var dd = Math.abs(s0.c - c0);
          if (dd < bd) { bd = dd; best = s0; }
        }
        var mx = (lo + hi) / 2, inPlus = ho ? _impInPoly(mx, c0 + 2 * t, O) : _impInPoly(c0 + 2 * t, mx, O);
        if (best) { NC.push(best.c + (inPlus ? -1 : 1) * best.t / 2); info.sides++; } else NC.push(c0);
        /* v185: the sides long enough to be drawn as a wall, and of them the ones found */
        if (hi - lo >= 6 * t) { info.longN = (info.longN || 0) + 1; if (best) info.longHit = (info.longHit || 0) + 1; }
        EDGE.push({ o: EO[i], lo: lo, hi: hi, out: inPlus ? -1 : 1 });
      }
      var NV = [];
      for (i = 0; i < nO; i++) {
        var e1 = i, e2 = (i + 1) % nO;
        NV[e2] = (EO[e1] === 'h') ? [NC[e2], NC[e1]] : [NC[e1], NC[e2]];
      }
      r.outline = NV;
      for (i = 0; i < nO; i++) EDGE[i].c = NC[i];
    }
    /* which picture walls are the outline itself */
    function onShell(s1) {
      for (var q = 0; q < EDGE.length; q++) {
        var E = EDGE[q];
        if (E.o !== s1.o) continue;
        if (Math.abs(s1.c - (E.c - E.out * s1.t / 2)) > Math.max(1.5 * t, s1.t)) continue;
        if (Math.min(E.hi, s1.a1) - Math.max(E.lo, s1.a0) > 0.5 * (s1.a1 - s1.a0)) return true;
      }
      return false;
    }
    var IN = [], GONE = [];
    for (k = 0; k < segs.length; k++) {
      var s2 = segs[k], mid = s2.o === 'h' ? [(s2.a0 + s2.a1) / 2, s2.c] : [s2.c, (s2.a0 + s2.a1) / 2];
      if (EDGE.length && onShell(s2)) continue;
      if (O.length >= 3 && !_impInPoly(mid[0], mid[1], r.outline)) continue;
      IN.push(s2);
    }
    /* NOT A WALL UNLESS IT HOLDS ON TO ONE (v128, Marek: fragmenty pricek
       uprostred living room -- z principu blbost, asi napis v bold fontu).
       Lettering drawn as thick as a wall -- LIV/DIN/KIT in the middle of the
       living room -- was traced as walls and stood as partitions. The walls
       of a house hang together: every partition reaches the outline through
       the ones it meets. A picture wall is kept when it does, or when the
       reader drew a wall along it; strokes that touch only each other are
       lettering or a symbol and are left out. */
    (function () {
      var DRAWN = [];
      for (var q0 = 0; raw.partitions && q0 < raw.partitions.length; q0++) {
        var ra = raw.partitions[q0].a, rb = raw.partitions[q0].b;
        var an0 = Math.atan2(Math.abs(rb[1] - ra[1]), Math.abs(rb[0] - ra[0])) * 180 / Math.PI;
        if (an0 > 20 && an0 < 70) continue;
        var ro = an0 <= 20 ? 'h' : 'v';
        DRAWN.push({ o: ro, c: ro === 'h' ? (ra[1] + rb[1]) / 2 : (ra[0] + rb[0]) / 2,
                     lo: ro === 'h' ? Math.min(ra[0], rb[0]) : Math.min(ra[1], rb[1]), hi: ro === 'h' ? Math.max(ra[0], rb[0]) : Math.max(ra[1], rb[1]) });
      }
      function ends(S0) { return S0.o === 'h' ? [[S0.a0, S0.c], [S0.a1, S0.c]] : [[S0.c, S0.a0], [S0.c, S0.a1]]; }
      function endIn(P0, Q0, reach) {
        var E0 = ends(P0);
        for (var e0 = 0; e0 < 2; e0++) {
          var al0 = Q0.o === 'h' ? E0[e0][0] : E0[e0][1], pe0 = Math.abs((Q0.o === 'h' ? E0[e0][1] : E0[e0][0]) - Q0.c);
          if (pe0 <= Q0.t / 2 + reach && al0 >= Q0.a0 - reach && al0 <= Q0.a1 + reach) return true;
        }
        return false;
      }
      function touch(A0, B0) {
        var rch = 0.75 * Math.max(A0.t, B0.t) + 3;
        if (endIn(A0, B0, rch) || endIn(B0, A0, rch)) return true;
        if (A0.o !== B0.o) {
          var HH = A0.o === 'h' ? A0 : B0, VV = A0.o === 'h' ? B0 : A0;
          if (VV.c >= HH.a0 - rch && VV.c <= HH.a1 + rch && HH.c >= VV.a0 - rch && HH.c <= VV.a1 + rch) return true;
        }
        return false;
      }
      /* on the outline: an end within a wall and a half of its line */
      function onOutline(S0) {
        var OL = r.outline || [], E0 = ends(S0), q1, e1;
        for (q1 = 0; q1 < OL.length; q1++) {
          var A2 = OL[q1], B2 = OL[(q1 + 1) % OL.length], dx = B2[0] - A2[0], dz = B2[1] - A2[1], L2 = Math.hypot(dx, dz);
          if (!(L2 > 0)) continue;
          for (e1 = 0; e1 < 2; e1++) {
            var px = E0[e1][0] - A2[0], pz = E0[e1][1] - A2[1], al2 = (px * dx + pz * dz) / L2, pe2 = Math.abs(px * dz - pz * dx) / L2;
            if (al2 >= -2 * t && al2 <= L2 + 2 * t && pe2 <= 2.5 * t + 2) return true;
          }
        }
        return false;
      }
      var SHL = segs.filter(function (S1) { return IN.indexOf(S1) < 0; }), ok = IN.map(function () { return false; }), q2, q3, grew = true;
      for (q2 = 0; q2 < IN.length; q2++) {
        var S2 = IN[q2];
        if (onOutline(S2)) { ok[q2] = true; continue; }
        for (q3 = 0; q3 < SHL.length && !ok[q2]; q3++) if (touch(S2, SHL[q3])) ok[q2] = true;
        for (q3 = 0; q3 < DRAWN.length && !ok[q2]; q3++) {
          var D2 = DRAWN[q3];
          if (D2.o === S2.o && Math.abs(D2.c - S2.c) <= 3 * t && Math.min(D2.hi, S2.a1) - Math.max(D2.lo, S2.a0) > 0) ok[q2] = true;
        }
      }
      while (grew) {
        grew = false;
        for (q2 = 0; q2 < IN.length; q2++) {
          if (ok[q2]) continue;
          for (q3 = 0; q3 < IN.length; q3++) if (ok[q3] && touch(IN[q2], IN[q3])) { ok[q2] = true; grew = true; break; }
        }
      }
      var kept0 = [];
      for (q2 = 0; q2 < IN.length; q2++) { if (ok[q2]) kept0.push(IN[q2]); else { info.islands = (info.islands || 0) + 1; GONE.push(IN[q2]); } }
      IN = kept0;
    })();
    /* THE WARDROBE'S HANGERS ARE NOT WALLS (v133, plan 1BR Grt. Rm.: skrin,
       kterou agent prevedl na nekolik mistnosti; Marek: skrin se takto znaci
       velice casto). A reach-in closet is drawn as a band along a wall with a
       rod down its middle and a row of short strokes across it -- the
       hangers. Each stroke hangs on the back wall, so each held on to a wall
       and stood as a partition, and the bedroom's closet became five rooms.
       A ROW of short strokes, all spanning the same band (their ends within a
       wall and a half of each other) and standing closer together than they
       are long, is a symbol: three or more thinner than the walls, or four or
       more of any weight. All of them go; the closet's own end wall, thick
       and running on to its front, is not one of them. */
    (function () {
      var C0 = [], q, q2;
      for (q = 0; q < IN.length; q++) { var S0 = IN[q]; if (S0.a1 - S0.a0 <= 12 * t) C0.push(S0); }
      var done = [], DROPC = [], rows = 0;
      for (q = 0; q < C0.length; q++) {
        if (done.indexOf(C0[q]) >= 0) continue;
        var A0 = C0[q], G0 = [];
        for (q2 = 0; q2 < C0.length; q2++) {
          var B0 = C0[q2];
          if (B0.o !== A0.o || done.indexOf(B0) >= 0) continue;
          if (Math.abs(B0.a0 - A0.a0) <= 1.5 * t && Math.abs(B0.a1 - A0.a1) <= 1.5 * t) G0.push(B0);
        }
        G0.forEach(function (x0) { done.push(x0); });
        G0.sort(function (p1, p2) { return p1.c - p2.c; });
        var RUN = [G0[0]];
        for (q2 = 1; q2 <= G0.length; q2++) {
          var L0 = (RUN[0].a1 - RUN[0].a0);
          if (q2 < G0.length && G0[q2].c - RUN[RUN.length - 1].c <= Math.max(1.2 * L0, 6 * t)) { RUN.push(G0[q2]); continue; }
          var thin = RUN.filter(function (x1) { return x1.t <= 0.85 * t; }).length;
          if ((RUN.length >= 3 && thin >= RUN.length - 1) || RUN.length >= 4) {
            rows++;
            RUN.forEach(function (x2) { DROPC.push(x2); });
          }
          if (q2 < G0.length) RUN = [G0[q2]];
        }
      }
      if (DROPC.length) {
        IN = IN.filter(function (x3) { return DROPC.indexOf(x3) < 0; });
        DROPC.forEach(function (x4) { GONE.push(x4); });
        info.combs = rows; info.combSegs = DROPC.length;
      }
    })();
    /* the reader's walls: kept only where the picture is silent */
    /* v133: the strokes left out above -- lettering, a symbol, a wardrobe's
       hangers -- are not the picture's walls: a reader's wall in their line
       is not taken for one of them, and its ends do not meet them */
    var SURV = segs.filter(function (x5) { return GONE.indexOf(x5) < 0; });
    function endMeets(x, z) {
      var q, P;
      for (q = 0; q < SURV.length; q++) {
        P = SURV[q];
        var al = P.o === 'h' ? x : z, pe = Math.abs((P.o === 'h' ? z : x) - P.c);
        if (pe <= 2.5 * t && al >= P.a0 - 1.5 * t && al <= P.a1 + 1.5 * t) return true;
      }
      var OL5 = r.outline || [];
      for (q = 0; q < OL5.length; q++) {
        var A5 = OL5[q], B5 = OL5[(q + 1) % OL5.length], dx5 = B5[0] - A5[0], dz5 = B5[1] - A5[1], L5 = Math.hypot(dx5, dz5);
        if (!(L5 > 0)) continue;
        var px5 = x - A5[0], pz5 = z - A5[1], al6 = (px5 * dx5 + pz5 * dz5) / L5, pe6 = Math.abs(px5 * dz5 - pz5 * dx5) / L5;
        if (al6 >= -2 * t && al6 <= L5 + 2 * t && pe6 <= 2.5 * t) return true;
      }
      return false;
    }
    /* AT THE JAMB OF A DOOR (v140, plan 1000073500: the walk-in closet's
       wall stops at its sliding door, drawn thinner than the walls and not
       traced). A wall end standing at the jamb of a door the reader drew in
       the same line holds on to that door, as a wall end holds on to a wall */
    function atJamb(P, o, c) {
      for (var q = 0; raw.doors && q < raw.doors.length; q++) {
        var D = raw.doors[q], A = D && D.at, w = Number(D && D.w) || 0;
        if (!A || !(w > 0)) continue;
        var pe = Math.abs((o === 'h' ? A[1] : A[0]) - c), al = o === 'h' ? A[0] : A[1], ep = o === 'h' ? P[0] : P[1];
        if (pe > 2.5 * t || Math.abs((o === 'h' ? P[1] : P[0]) - c) > 2.5 * t) continue;
        if (Math.abs(ep - (al - w / 2)) <= 2 * t || Math.abs(ep - (al + w / 2)) <= 2 * t) return true;
      }
      return false;
    }
    /* A LEAF DRAWN AJAR IS NOT A SLANTED WALL (v142, plan 1000073355,
       Marek: sikme steny do nej vnesly zmatek -- kvuli znackam dveri, kterych
       je tam dost, v pruchozi hall i v koupelne, kde se prochazi z obou
       stran; pricky jsou jasne viditelne). The reader took the half-open
       leaves of the hall's two bifolds for four slanted walls, a diamond in
       the hall, and the two leaves of the bathroom's doors for one slanted
       wall across the bathroom. A slanted wall is drawn as the walls are, a
       band as thick as they are; a leaf is a line. Each slanted partition is
       measured across in the picture at ten points along its middle: a line
       there, under half a wall's thickness, is a leaf and no wall -- it is
       left out, and the doors the reader hung in it go to the breaks of the
       square walls with the others. */
    var THIN = {};
    (function () {
      var G9 = (GP && GP.gray && GP.w) ? GP : null;
      if (!G9 || !raw.partitions) return;
      var thrD = PW.thr || 165, W9 = G9.w, H9 = G9.h, q9, k9;
      function dk(x, y) { x = Math.round(x); y = Math.round(y); return x >= 0 && y >= 0 && x < W9 && y < H9 && G9.gray[y * W9 + x] < thrD; }
      for (q9 = 0; q9 < raw.partitions.length; q9++) {
        var A9 = raw.partitions[q9].a, B9 = raw.partitions[q9].b;
        var an9 = Math.atan2(Math.abs(B9[1] - A9[1]), Math.abs(B9[0] - A9[0])) * 180 / Math.PI, L9 = Math.hypot(B9[0] - A9[0], B9[1] - A9[1]);
        if (!(an9 > 20 && an9 < 70) || !(L9 > 2 * t)) continue;
        var ux = (B9[0] - A9[0]) / L9, uy = (B9[1] - A9[1]) / L9, nx = -uy, ny = ux, R9 = Math.round(2.5 * t), ws = [];
        for (k9 = 0; k9 < 10; k9++) {
          var s9 = 0.2 + 0.6 * (k9 + 0.5) / 10, px9 = A9[0] + ux * s9 * L9, py9 = A9[1] + uy * s9 * L9, runs = [], cur = null, e9;
          for (e9 = -R9; e9 <= R9; e9++) {
            if (dk(px9 + nx * e9, py9 + ny * e9)) { if (!cur) cur = { a: e9, b: e9 }; else cur.b = e9; }
            else if (cur) { runs.push(cur); cur = null; }
          }
          if (cur) runs.push(cur);
          var wb = 0, db = 1e9;
          runs.forEach(function (rn) { var cc = (rn.a + rn.b) / 2; if (Math.abs(cc) <= 1.5 * t && Math.abs(cc) < db) { db = Math.abs(cc); wb = rn.b - rn.a + 1; } });
          ws.push(wb);
        }
        ws.sort(function (p1, p2) { return p1 - p2; });
        /* a line runs along it (seven points of ten), and thin: a leaf. Nothing
           along it is a door's opening in a slanted wall -- the V of plan
           1000073345, whose two doors take nearly the whole of each wall */
        var nOn = ws.filter(function (v9) { return v9 > 0; }).length;
        if (ws[5] < 0.5 * t && nOn >= 7) { THIN[q9] = true; info.leaves = (info.leaves || 0) + 1; }
      }
    })();
    /* A WALL ONLY WHERE THE PICTURE DRAWS ONE (v161, plan 1000073631,
       Marek: pricky maji na floorplanech stejnou tloustku, nebo vetsi -- ale
       negenerovat, kde nic neni, ani kde je zastena sprchoveho koutu). A
       reader's wall the tracing did not find is measured across in the
       picture at ten points along its middle: a band at least six tenths as
       thick as its partitions at six points of the ten is a wall; nothing
       there is no wall; a thin line is no wall inside a bathroom (a shower's
       glass) and a wall elsewhere (a closet drawn thin, v140). */
    var REFT = (function () {
      var L = IN.map(function (x) { return Number(x.t) || 0; }).filter(function (v) { return v > 0; }).sort(function (p1, p2) { return p1 - p2; });
      return L.length >= 2 ? L[L.length >> 1] : t;
    })();
    function band(A9, B9) {
      var G9 = (GP && GP.gray && GP.w) ? GP : null;
      if (!G9) return 'thick';
      var thrD = PW.thr || 165, W9 = G9.w, H9 = G9.h, k9;
      /* the wall's own grey for its thickness; any visible line, a lighter
         grey, for a line at all -- a thin line is drawn lighter than a wall */
      function dk(x, y, th) { x = Math.round(x); y = Math.round(y); return x >= 0 && y >= 0 && x < W9 && y < H9 && G9.gray[y * W9 + x] < th; }
      function across(px9, py9, nx, ny, R9, th) {
        var runs = [], cur = null, e9, wb = 0, db = 1e9;
        for (e9 = -R9; e9 <= R9; e9++) {
          if (dk(px9 + nx * e9, py9 + ny * e9, th)) { if (!cur) cur = { a: e9, b: e9 }; else cur.b = e9; }
          else if (cur) { runs.push(cur); cur = null; }
        }
        if (cur) runs.push(cur);
        runs.forEach(function (rn) { var cc = (rn.a + rn.b) / 2; if (Math.abs(cc) <= 1.5 * t && Math.abs(cc) < db) { db = Math.abs(cc); wb = rn.b - rn.a + 1; } });
        return wb;
      }
      var L9 = Math.hypot(B9[0] - A9[0], B9[1] - A9[1]);
      if (!(L9 > 0)) return 'none';
      var ux = (B9[0] - A9[0]) / L9, uy = (B9[1] - A9[1]) / L9, nx = -uy, ny = ux, R9 = Math.round(2.5 * t), thick = 0, any = 0;
      for (k9 = 0; k9 < 10; k9++) {
        var s9 = 0.2 + 0.6 * (k9 + 0.5) / 10, px9 = A9[0] + ux * s9 * L9, py9 = A9[1] + uy * s9 * L9;
        if (across(px9, py9, nx, ny, R9, thrD) >= 0.6 * REFT) thick++;
        if (across(px9, py9, nx, ny, R9, Math.max(thrD, 215)) > 0) any++;
      }
      return thick >= 6 ? 'thick' : (any >= 6 ? 'thin' : 'none');
    }
    /* a thin line is a wall only outside a bathroom: in one it is a shower's
       glass (plan 1000073631); elsewhere a closet drawn thin is still a
       closet (v140, plan A's pantry) */
    var BATHPX = (r.rooms || []).filter(function (rm) { return rm && rm.box && rm.box[0] && rm.box[1] && /^(bathroom|wc)$/.test(String(rm.type || '')); }).map(function (rm) {
      return { x0: Math.min(rm.box[0][0], rm.box[1][0]), x1: Math.max(rm.box[0][0], rm.box[1][0]), z0: Math.min(rm.box[0][1], rm.box[1][1]), z1: Math.max(rm.box[0][1], rm.box[1][1]) };
    });
    function inBath(A9, B9) {
      var mx = (A9[0] + B9[0]) / 2, mz = (A9[1] + B9[1]) / 2;
      return BATHPX.some(function (bx) { return mx > bx.x0 + t && mx < bx.x1 - t && mz > bx.z0 + t && mz < bx.z1 - t; });
    }
    /* THE SHOWER BESIDE THE BATHROOM (v163, plan 1000073631, Marek:
       sprchovy kout udelal zase jako samostatnou mistnost, pritom je tam
       zastena tenci nez jsou pricky). One reading boxed BATH 1 down to the
       shower's glass and called the shower under it a closet with a door into
       the bathroom: the glass lay on the bathroom's edge, not inside it, and
       was kept as a closet drawn thin (v140) -- a room of its own with a door.
       A closet or store with no name of its own, of a shower's size, against
       a side of a bathroom it runs the whole of, whose every door the reader
       drew stands on that side, with no wall drawn as thick as the others
       between them, is the bathroom's shower: the bathroom's box takes it in,
       so the line between is the shower's glass, and the closet inside the
       bathroom is its shower (v161). */
    (function () {
      var RMS = r.rooms || [], a, c, q;
      function bxOf(rm) { return { x0: Math.min(rm.box[0][0], rm.box[1][0]), x1: Math.max(rm.box[0][0], rm.box[1][0]), z0: Math.min(rm.box[0][1], rm.box[1][1]), z1: Math.max(rm.box[0][1], rm.box[1][1]) }; }
      function ok(rm) { return rm && rm.box && rm.box[0] && rm.box[1]; }
      for (a = 0; a < RMS.length; a++) {
        if (!ok(RMS[a]) || !/^(bathroom|wc)$/.test(String(RMS[a].type || ''))) continue;
        for (c = 0; c < RMS.length; c++) {
          var Cr = RMS[c];
          if (c === a || !ok(Cr) || !/^(closet|storage)$/.test(String(Cr.type || ''))) continue;
          if (Cr.label && !/^\s*(closet|cl\.?|storage)?\s*$/i.test(String(Cr.label))) continue;
          var B = bxOf(RMS[a]), C = bxOf(Cr), cw = C.x1 - C.x0, cd = C.z1 - C.z0, tol = 3 * t, side = null;
          if (Math.min(cw, cd) < 4 * t || Math.max(cw, cd) > 14 * t) continue;
          var spanX = Math.min(B.x1, C.x1) - Math.max(B.x0, C.x0), spanZ = Math.min(B.z1, C.z1) - Math.max(B.z0, C.z0);
          var fullX = spanX >= 0.8 * (B.x1 - B.x0) && spanX >= 0.8 * cw, fullZ = spanZ >= 0.8 * (B.z1 - B.z0) && spanZ >= 0.8 * cd;
          if (fullX && Math.abs(C.z0 - B.z1) <= tol) side = ['h', (C.z0 + B.z1) / 2, Math.max(B.x0, C.x0), Math.min(B.x1, C.x1)];
          else if (fullX && Math.abs(C.z1 - B.z0) <= tol) side = ['h', (C.z1 + B.z0) / 2, Math.max(B.x0, C.x0), Math.min(B.x1, C.x1)];
          else if (fullZ && Math.abs(C.x0 - B.x1) <= tol) side = ['v', (C.x0 + B.x1) / 2, Math.max(B.z0, C.z0), Math.min(B.z1, C.z1)];
          else if (fullZ && Math.abs(C.x1 - B.x0) <= tol) side = ['v', (C.x1 + B.x0) / 2, Math.max(B.z0, C.z0), Math.min(B.z1, C.z1)];
          if (!side) continue;
          var off = 0;
          for (q = 0; r.doors && q < r.doors.length; q++) {
            var Ad = r.doors[q] && r.doors[q].at;
            if (!Ad) continue;
            if (!(Ad[0] >= C.x0 - 2 * t && Ad[0] <= C.x1 + 2 * t && Ad[1] >= C.z0 - 2 * t && Ad[1] <= C.z1 + 2 * t)) continue;
            if (Math.min(Math.abs(Ad[0] - C.x0), Math.abs(Ad[0] - C.x1), Math.abs(Ad[1] - C.z0), Math.abs(Ad[1] - C.z1)) > 2 * t) continue;
            if (Math.abs((side[0] === 'h' ? Ad[1] : Ad[0]) - side[1]) > 2 * t) off++;
          }
          if (off) continue;
          var P0 = side[0] === 'h' ? [side[2], side[1]] : [side[1], side[2]], P1 = side[0] === 'h' ? [side[3], side[1]] : [side[1], side[3]];
          if (band(P0, P1) === 'thick') continue;
          var nb = { x0: Math.min(B.x0, C.x0), x1: Math.max(B.x1, C.x1), z0: Math.min(B.z0, C.z0), z1: Math.max(B.z1, C.z1) };
          RMS[a].box = [[nb.x0, nb.z0], [nb.x1, nb.z1]];
          BATHPX.push(nb);
          info.showerSide = info.showerSide || [];
          info.showerSide.push('"' + (Cr.label || Cr.type) + '" ' + Math.round(cw) + ' x ' + Math.round(cd) + ' px against "' + (RMS[a].label || RMS[a].type) + '"');
        }
      }
    })();
    function bandOK(A9, B9) {
      var bnd = band(A9, B9), ib = (bnd === 'thin') && inBath(A9, B9), ok = bnd === 'thick' || (bnd === 'thin' && !ib);
      if (!ok) { info.thinSaid = info.thinSaid || []; info.thinSaid.push(Math.round(A9[0]) + ',' + Math.round(A9[1]) + '-' + Math.round(B9[0]) + ',' + Math.round(B9[1]) + ' px ' + (bnd === 'none' ? 'nothing drawn' : 'a thin line in a bathroom')); }
      return ok;
    }
    var keep = [], LATE = [], JK = [];
    for (i = 0; raw.partitions && i < raw.partitions.length; i++) {
      var pa = raw.partitions[i].a, pb = raw.partitions[i].b;
      var ang = Math.atan2(Math.abs(pb[1] - pa[1]), Math.abs(pb[0] - pa[0])) * 180 / Math.PI;
      if (ang > 20 && ang < 70) { if (!THIN[i]) keep.push(raw.partitions[i]); continue; }
      var po = ang <= 20 ? 'h' : 'v', pc = po === 'h' ? (pa[1] + pb[1]) / 2 : (pa[0] + pb[0]) / 2;
      var plo = po === 'h' ? Math.min(pa[0], pb[0]) : Math.min(pa[1], pb[1]), phi = po === 'h' ? Math.max(pa[0], pb[0]) : Math.max(pa[1], pb[1]);
      var inLine = false;
      for (k = 0; k < SURV.length && !inLine; k++) {
        var s3 = SURV[k];
        if (s3.o === po && Math.abs(s3.c - pc) <= 3 * t && Math.min(phi, s3.a1) - Math.max(plo, s3.a0) > -2 * t) inLine = true;
      }
      if (inLine || phi - plo < 4 * t) { info.dropped++; continue; }
      var okA = endMeets(pa[0], pa[1]), okB = endMeets(pb[0], pb[1]);
      if (!(okA || atJamb(pa, po, pc)) || !(okB || atJamb(pb, po, pc))) { info.dropped++; LATE.push({ a: pa, b: pb, okA: okA, okB: okB }); continue; }
      if (!bandOK(pa, pb)) { info.dropped++; info.thin = (info.thin || 0) + 1; continue; }
      if (!okA || !okB) { JK.push({ o: po, c: pc, lo: plo, hi: phi }); info.jambs = (info.jambs || 0) + 1; }
      keep.push({ a: pa, b: pb });
      info.model++;
    }
    /* and the wall that turns the corner from such a wall (v140, plan A: the
       pantry's front holds its pair of doors, its side runs from the front's
       end to the outer wall -- both drawn thin, neither traced). One step
       only: a reader's wall left out for an end meeting nothing is kept when
       that end stands on a wall kept at a door's jamb. */
    for (k = 0; k < LATE.length; k++) {
      var LT = LATE[k], nearJ = function (P) {
        for (var q = 0; q < JK.length; q++) {
          var J = JK[q], al = J.o === 'h' ? P[0] : P[1], pe = Math.abs((J.o === 'h' ? P[1] : P[0]) - J.c);
          if (pe <= 2.5 * t && al >= J.lo - 1.5 * t && al <= J.hi + 1.5 * t) return true;
        }
        return false;
      };
      if ((LT.okA || nearJ(LT.a)) && (LT.okB || nearJ(LT.b)) && !(LT.okA && LT.okB)) {
        if (!bandOK(LT.a, LT.b)) { info.thin = (info.thin || 0) + 1; continue; }
        keep.push({ a: LT.a, b: LT.b }); info.model++; info.dropped--; info.jambs = (info.jambs || 0) + 1;
      }
    }
    r.partitions = keep;
    for (k = 0; k < IN.length; k++) {
      var s4 = IN[k];
      r.partitions.push(s4.o === 'h' ? { a: [s4.a0, s4.c], b: [s4.a1, s4.c], px: true } : { a: [s4.c, s4.a0], b: [s4.c, s4.a1], px: true });
      info.walls++;
    }
    /* THE ROOM BOXES ON THE WALLS: each side of a room's box onto the inner
       face of the picture's wall beside it, so the sizes written in the
       rooms are measured against the real rooms (the scale comes from them
       when the plan prints no dimension line). An outdoor space is left. */
    for (i = 0; r.rooms && i < r.rooms.length; i++) {
      var RM = r.rooms[i];
      if (!RM.box || !RM.box[0] || !RM.box[1] || /^(patio|porch|deck|balcony|terrace)$/.test(String(RM.type || ''))) continue;
      var x0 = Math.min(RM.box[0][0], RM.box[1][0]), x1 = Math.max(RM.box[0][0], RM.box[1][0]);
      var z0 = Math.min(RM.box[0][1], RM.box[1][1]), z1 = Math.max(RM.box[0][1], RM.box[1][1]);
      var face = function (o, v, sgn, lo2, hi2) {
        var bb = null, bdd = 2.5 * t;
        for (var q4 = 0; q4 < segs.length; q4++) {
          var S6 = segs[q4];
          if (S6.o !== o) continue;
          if (Math.min(hi2, S6.a1) - Math.max(lo2, S6.a0) < 0.3 * (hi2 - lo2)) continue;
          var f6 = S6.c + sgn * S6.t / 2, d6 = Math.abs(f6 - v);
          if (d6 < bdd) { bdd = d6; bb = f6; }
        }
        return bb;
      };
      var nx0 = face('v', x0, 1, z0, z1), nx1 = face('v', x1, -1, z0, z1), nz0 = face('h', z0, 1, x0, x1), nz1 = face('h', z1, -1, x0, x1), moved = 0;
      if (nx0 != null) { x0 = nx0; moved++; } if (nx1 != null) { x1 = nx1; moved++; }
      if (nz0 != null) { z0 = nz0; moved++; } if (nz1 != null) { z1 = nz1; moved++; }
      if (moved && x1 - x0 > 2 * t && z1 - z0 > 2 * t) { RM.box = [[x0, z0], [x1, z1]]; info.boxes = (info.boxes || 0) + 1; }
    }
    /* the breaks in the walls, line by line: between two pieces of one wall,
       or between the end of a wall and the wall across its line -- a closet's
       front is often one short stub and then its doors up to the side wall */
    var LN = [];
    for (k = 0; k < segs.length; k++) {
      var s5 = segs[k], L0 = null, q2;
      for (q2 = 0; q2 < LN.length; q2++) if (LN[q2].o === s5.o && Math.abs(LN[q2].c - s5.c) <= Math.max(2, 0.4 * t)) { L0 = LN[q2]; break; }
      if (!L0) { L0 = { o: s5.o, c: s5.c, s: [] }; LN.push(L0); }
      L0.s.push(s5);
    }
    var GAPS = [];
    /* v140: the walk-in closet's door went into the whole break between the
       outer wall and the laundry -- the wall the reader drew down to the
       door's jamb, kept above, was run over by it. A reader's wall kept in
       the line of a break is a piece of that line. */
    var KEPTL = [];
    for (k = 0; k < keep.length; k++) {
      var kA = keep[k].a, kB = keep[k].b, kAn = Math.atan2(Math.abs(kB[1] - kA[1]), Math.abs(kB[0] - kA[0])) * 180 / Math.PI;
      if (kAn > 20 && kAn < 70) continue;
      var kO = kAn <= 20 ? 'h' : 'v';
      KEPTL.push({ o: kO, c: kO === 'h' ? (kA[1] + kB[1]) / 2 : (kA[0] + kB[0]) / 2, a0: kO === 'h' ? Math.min(kA[0], kB[0]) : Math.min(kA[1], kB[1]), a1: kO === 'h' ? Math.max(kA[0], kB[0]) : Math.max(kA[1], kB[1]) });
    }
    for (k = 0; k < LN.length; k++) {
      var IT = [];
      LN[k].s.forEach(function (S7) { IT.push({ a: S7.a0, b: S7.a1, seg: true }); });
      KEPTL.forEach(function (K7) { if (K7.o === LN[k].o && Math.abs(K7.c - LN[k].c) <= 1.5 * t) IT.push({ a: K7.a0, b: K7.a1, seg: true }); });
      for (i = 0; i < segs.length; i++) {
        var P7 = segs[i];
        if (P7.o === LN[k].o || LN[k].c < P7.a0 - t || LN[k].c > P7.a1 + t) continue;
        IT.push({ a: P7.c - P7.t / 2, b: P7.c + P7.t / 2, seg: false });
      }
      IT.sort(function (p1, p2) { return p1.a - p2.a; });
      var MG = [];
      for (i = 0; i < IT.length; i++) {
        var last = MG.length ? MG[MG.length - 1] : null;
        if (last && IT[i].a <= last.b + 1) { last.b = Math.max(last.b, IT[i].b); last.seg = last.seg || IT[i].seg; continue; }
        MG.push({ a: IT[i].a, b: IT[i].b, seg: IT[i].seg });
      }
      var TWk = LN[k].s.map(function (S9) { return S9.t; }).sort(function (p1, p2) { return p1 - p2; }), twk = TWk.length ? TWk[TWk.length >> 1] : t;
      for (i = 1; i < MG.length; i++) {
        var g0 = MG[i - 1].b, g1 = MG[i].a;
        if (g1 - g0 < 2 * t || !(MG[i - 1].seg || MG[i].seg)) continue;
        GAPS.push({ o: LN[k].o, c: LN[k].c, g0: g0, g1: g1, tw: twk, shell: EDGE.length ? onShell({ o: LN[k].o, c: LN[k].c, a0: g0, a1: g1, t: t }) : false });
      }
    }
    /* each door to the break nearest it -- one door to a break -- the break
       holding the door first, then one just beside it */
    function snap(list, isWin) {
      var C = [], j, q3, HAS = {};
      for (j = 0; list && j < list.length; j++) {
        var d = list[j], w = Number(d.w) || 0;
        if (d.onDiag) continue;
        HAS[j] = 0;
        /* v138: a door between two rooms never goes into a break of the outer
           wall -- the pantry's pair of plan A took the window's break beside
           it and became a door to the outside */
        var outD = !isWin && (d.rooms || []).some(function (t9) { return /^(outside|outdoors?|exterior|garden|yard|porch|deck|patio|balcony|terrace)$/i.test(String(t9).trim()); });
        for (q3 = 0; q3 < GAPS.length; q3++) {
          var G = GAPS[q3];
          if (isWin && !G.shell) continue;
          if (!isWin && G.shell && !outD) continue;
          var al = G.o === 'h' ? d.at[0] : d.at[1], pe = Math.abs((G.o === 'h' ? d.at[1] : d.at[0]) - G.c);
          var outA = (al < G.g0) ? (G.g0 - al) : ((al > G.g1) ? (al - G.g1) : 0);
          /* A SLIDING FRONT STANDS IN ITS OWN LINE (v165, plan 1000075648,
             Marek: nechytila se pricka delici vestavene skrine). A door with
             no leaf to swing -- a bypass, a bifold, a slider, an opening --
             is drawn in the line of its wall, so it is put into a break of a
             line no more than two walls' thickness from where the reader
             drew it. Half its width was allowed, as for a swinging door: the
             right closet's bypass, 2.0 m wide, was taken into the line of
             the hall's wall 0.40 m away, that wall ran up through the closet,
             and the wall between the two closets, too close to it, was taken
             out. */
          var noLeaf = !isWin && /^(bypass|bifold|sliding|pocket|opening|pass|passage)$/.test(String(d.kind || ''));
          if (pe > (noLeaf ? 2 * t : Math.max(2.5 * t, 0.5 * w)) || outA > Math.max(t, 0.6 * w)) continue;
          var gl = G.g1 - G.g0;
          if (w > 0 && (gl < 0.5 * w || gl > 2.2 * w)) continue;
          C.push({ j: j, g: q3, sc: pe + 1.5 * outA });
          HAS[j]++;
        }
      }
      /* A DOOR LAID ALONG ITS OPEN LEAF (v142, plan 1000073631: both bedroom
         doors came in on the line of the closets' fronts -- where the plan
         draws their leaves, open against the closets -- and found no break
         there: one was set over the closet's sliders and dropped, the
         bedroom ran into the hall). A hinged door the reader drew that finds
         no break of its own is tried turned a quarter about its hinge: into
         the wall square to its line, from the hinge toward the side it opens
         to -- the leaf then lies where the reader drew the door. */
      for (j = 0; !isWin && list && j < list.length; j++) {
        var dT = list[j], wT = Number(dT.w) || 0;
        if (dT.onDiag || HAS[j] || !(wT > 0) || !dT.hinge || !dT.opensTo || String(dT.kind || 'hinged') !== 'hinged') continue;
        var Hh = dT.hinge, horiz = Math.abs(dT.at[0] - Hh[0]) >= Math.abs(dT.at[1] - Hh[1]);
        var sgO = horiz ? Math.sign(dT.opensTo[1] - Hh[1]) : Math.sign(dT.opensTo[0] - Hh[0]), sgL = horiz ? Math.sign(dT.at[0] - Hh[0]) : Math.sign(dT.at[1] - Hh[1]);
        if (!sgO || !sgL) continue;
        var atT = horiz ? [Hh[0], Hh[1] + sgO * wT / 2] : [Hh[0] + sgO * wT / 2, Hh[1]], oT = horiz ? 'v' : 'h';
        for (q3 = 0; q3 < GAPS.length; q3++) {
          var GT = GAPS[q3];
          if (GT.o !== oT || GT.shell) continue;
          var alT = GT.o === 'h' ? atT[0] : atT[1], peT = Math.abs((GT.o === 'h' ? atT[1] : atT[0]) - GT.c), hAl = GT.o === 'h' ? Hh[0] : Hh[1];
          var outT = (alT < GT.g0) ? (GT.g0 - alT) : ((alT > GT.g1) ? (alT - GT.g1) : 0), glT = GT.g1 - GT.g0;
          if (peT > 2.5 * t || outT > Math.max(t, 0.4 * wT) || glT < 0.6 * wT || glT > 1.6 * wT) continue;
          if (Math.min(Math.abs(hAl - GT.g0), Math.abs(hAl - GT.g1)) > 2 * t) continue;
          C.push({ j: j, g: q3, sc: peT + 1.5 * outT + t, turn: { sgL: sgL, hAl: hAl } });
        }
      }
      C.sort(function (p1, p2) { return p1.sc - p2.sc; });
      var usedD = {}, n = 0;
      for (q3 = 0; q3 < C.length; q3++) {
        var c3 = C[q3];
        if (usedD[c3.j] || GAPS[c3.g].taken) continue;
        usedD[c3.j] = true; GAPS[c3.g].taken = true;
        var d3 = list[c3.j], G3 = GAPS[c3.g];
        d3.snapped = true;
        if (!isWin) d3.gp = { o: G3.o, c: G3.c, g0: G3.g0, g1: G3.g1, tw: G3.tw, shell: !!G3.shell };
        var nat = G3.o === 'h' ? [(G3.g0 + G3.g1) / 2, G3.c] : [G3.c, (G3.g0 + G3.g1) / 2];
        var dx = nat[0] - d3.at[0], dz = nat[1] - d3.at[1];
        var w0d = Number(d3.w) || 0, kd9 = String(d3.kind || 'hinged'), oAt = [d3.at[0], d3.at[1]];
        d3.at = nat; d3.w = G3.g1 - G3.g0;
        /* NOT WIDER THAN THE DOOR DRAWN (v145, plan 1000073628: the laundry's
           0.83 m door took the whole 1.35 m break beside the hall, ran past
           the hall and put the bathroom's basin in its way). A hinged, sliding
           or pocket door is widened to its break by a fifth at most; the rest
           of the break is wall. A pair, a bifold or a bypass takes the break. */
        if (!isWin && !c3.turn && w0d > 0 && (G3.g1 - G3.g0) > 1.2 * w0d && !/^(double|pair|bifold|bypass|opening)$/.test(kd9)) {
          var wC = 1.2 * w0d, alR = G3.o === 'h' ? oAt[0] : oAt[1], ctr = Math.max(G3.g0 + wC / 2, Math.min(G3.g1 - wC / 2, alR));
          nat = G3.o === 'h' ? [ctr, G3.c] : [G3.c, ctr];
          dx = nat[0] - oAt[0]; dz = nat[1] - oAt[1];
          d3.at = nat; d3.w = wC;
          if (d3.gp) { d3.gp.g0 = ctr - wC / 2; d3.gp.g1 = ctr + wC / 2; }
          info.capped = (info.capped || 0) + 1;
        }
        if (!isWin && c3.turn) {
          /* turned: the hinge on the jamb it stood at, the leaf toward the
             side the reader drew the door */
          var gE = (Math.abs(c3.turn.hAl - G3.g0) <= Math.abs(c3.turn.hAl - G3.g1)) ? G3.g0 : G3.g1, gM = (G3.g0 + G3.g1) / 2;
          d3.hinge = G3.o === 'h' ? [gE, G3.c] : [G3.c, gE];
          d3.opensTo = G3.o === 'h' ? [gM, G3.c + c3.turn.sgL * (G3.g1 - G3.g0) / 2] : [G3.c + c3.turn.sgL * (G3.g1 - G3.g0) / 2, gM];
          d3.turnedLeaf = true;
          info.turned = (info.turned || 0) + 1;
        } else if (!isWin) {
          if (d3.hinge) d3.hinge = [d3.hinge[0] + dx, d3.hinge[1] + dz];
          if (d3.opensTo) d3.opensTo = [d3.opensTo[0] + dx, d3.opensTo[1] + dz];
        }
        if (!isWin) {
          /* the wall goes on through its door: a door is a hole in a wall */
          if (!G3.shell) {
            r.partitions.push(G3.o === 'h' ? { a: [G3.g0 - 1, G3.c], b: [G3.g1 + 1, G3.c], px: true } : { a: [G3.c, G3.g0 - 1], b: [G3.c, G3.g1 + 1], px: true });
            info.bridged = (info.bridged || 0) + 1;
          }
        }
        n++;
      }
      return n;
    }
    /* A DOOR IN A SLANTED WALL STAYS IN IT (v128). The doors of a 45 degree
       corner -- a bathroom and a bedroom off one small hall -- were moved to
       the nearest break in a square wall: the break between the two jambs
       across the hall, or one the jamb made with the outer wall. Each became
       a pair as wide as that break, and the wall was run on through it --
       across the bathroom. A door the reader put on one of its slanted walls
       is left there; the plan stands it in that wall. */
    (function () {
      var DG = [], q4, q5;
      for (q4 = 0; raw.partitions && q4 < raw.partitions.length; q4++) {
        var pa4 = raw.partitions[q4].a, pb4 = raw.partitions[q4].b;
        var an4 = Math.atan2(Math.abs(pb4[1] - pa4[1]), Math.abs(pb4[0] - pa4[0])) * 180 / Math.PI;
        if (an4 > 20 && an4 < 70 && !THIN[q4]) DG.push({ a: pa4, b: pb4 });
      }
      for (q4 = 0; DG.length && r.doors && q4 < r.doors.length; q4++) {
        var d4 = r.doors[q4], w4 = Number(d4.w) || 0;
        if (!d4.at) continue;
        /* nearer the slanted wall than any square wall of the picture, and
           in its run, not at its end -- a door in the square wall a slanted
           one starts from stays that wall's */
        var sq4 = 1e9, q6;
        for (q6 = 0; q6 < segs.length; q6++) {
          var S6 = segs[q6], al6 = S6.o === 'h' ? d4.at[0] : d4.at[1];
          if (al6 < S6.a0 - Math.max(w4, 2 * t) || al6 > S6.a1 + Math.max(w4, 2 * t)) continue;
          sq4 = Math.min(sq4, Math.abs((S6.o === 'h' ? d4.at[1] : d4.at[0]) - S6.c));
        }
        for (q5 = 0; q5 < DG.length; q5++) {
          var G4 = DG[q5], dx4 = G4.b[0] - G4.a[0], dz4 = G4.b[1] - G4.a[1], L4 = Math.hypot(dx4, dz4);
          if (!(L4 > 0)) continue;
          var px4 = d4.at[0] - G4.a[0], pz4 = d4.at[1] - G4.a[1], al4 = (px4 * dx4 + pz4 * dz4) / L4, pe4 = Math.abs(px4 * dz4 - pz4 * dx4) / L4;
          if (al4 > 0.1 * L4 && al4 < 0.9 * L4 && pe4 <= Math.max(2.5 * t, 0.35 * w4) && pe4 < sq4) { d4.onDiag = true; info.diagDoors = (info.diagDoors || 0) + 1; break; }
        }
      }
    })();
    info.doors = snap(r.doors, false);
    info.wins = snap(r.windows, true);
    /* A DOOR STILL OFF ITS BREAK GOES TO THE BREAK BETWEEN THE TWO ROOMS IT
       NAMES: the reader put the second bedroom's door into the laundry
       closet's wall, and the break between the bedroom and the hall stood
       open beside it */
    function nm(v) { return String(v || '').toUpperCase().replace(/[^A-Z0-9]/g, ''); }
    function boxOf(name) {
      for (var q5 = 0; r.rooms && q5 < r.rooms.length; q5++) {
        var R5 = r.rooms[q5];
        if (!R5.box || !R5.box[0] || !R5.box[1]) continue;
        if (nm(R5.label) === nm(name) || (!R5.label && nm(R5.type) === nm(name))) {
          return { x0: Math.min(R5.box[0][0], R5.box[1][0]), x1: Math.max(R5.box[0][0], R5.box[1][0]), z0: Math.min(R5.box[0][1], R5.box[1][1]), z1: Math.max(R5.box[0][1], R5.box[1][1]) };
        }
      }
      return null;
    }
    var nByName = 0;
    for (i = 0; r.doors && i < r.doors.length; i++) {
      var D8 = r.doors[i];
      if (D8.snapped || D8.onDiag || !D8.rooms || D8.rooms.length !== 2) continue;
      var BA = boxOf(D8.rooms[0]), BB = boxOf(D8.rooms[1]);
      if (!BA || !BB) continue;
      for (k = 0; k < GAPS.length; k++) {
        var G8 = GAPS[k];
        if (G8.taken || G8.shell) continue;
        var h8 = G8.o === 'h';
        var sideOf = function (Bx) {
          var lo8 = h8 ? Bx.x0 : Bx.z0, hi8 = h8 ? Bx.x1 : Bx.z1, e0 = h8 ? Bx.z0 : Bx.x0, e1 = h8 ? Bx.z1 : Bx.x1;
          if (Math.min(hi8, G8.g1) - Math.max(lo8, G8.g0) <= 0) return 0;
          if (Math.abs(e1 - G8.c) <= 2 * t) return -1;
          if (Math.abs(e0 - G8.c) <= 2 * t) return 1;
          return 0;
        };
        var sA = sideOf(BA), sB = sideOf(BB);
        if (!sA || !sB || sA === sB) continue;
        var gl8 = G8.g1 - G8.g0, w8 = Number(D8.w) || 0;
        if (w8 > 0 && (gl8 < 0.5 * w8 || gl8 > 2.2 * w8)) continue;
        G8.taken = true; D8.snapped = true;
        D8.gp = { o: G8.o, c: G8.c, g0: G8.g0, g1: G8.g1, tw: G8.tw, shell: false };
        var nat8 = h8 ? [(G8.g0 + G8.g1) / 2, G8.c] : [G8.c, (G8.g0 + G8.g1) / 2], dx8 = nat8[0] - D8.at[0], dz8 = nat8[1] - D8.at[1];
        D8.at = nat8; D8.w = gl8;
        /* v145: not wider than the door drawn, here as well */
        var kd8 = String(D8.kind || 'hinged');
        if (w8 > 0 && gl8 > 1.2 * w8 && !/^(double|pair|bifold|bypass|opening)$/.test(kd8)) {
          var wC8 = 1.2 * w8, al8 = h8 ? (nat8[0] - dx8) : (nat8[1] - dz8), ct8 = Math.max(G8.g0 + wC8 / 2, Math.min(G8.g1 - wC8 / 2, al8));
          var nat9 = h8 ? [ct8, G8.c] : [G8.c, ct8];
          dx8 += nat9[0] - nat8[0]; dz8 += nat9[1] - nat8[1];
          D8.at = nat9; D8.w = wC8; D8.gp.g0 = ct8 - wC8 / 2; D8.gp.g1 = ct8 + wC8 / 2;
          info.capped = (info.capped || 0) + 1;
        }
        if (D8.hinge) D8.hinge = [D8.hinge[0] + dx8, D8.hinge[1] + dz8];
        if (D8.opensTo) D8.opensTo = [D8.opensTo[0] + dx8, D8.opensTo[1] + dz8];
        r.partitions.push(h8 ? { a: [G8.g0 - 1, G8.c], b: [G8.g1 + 1, G8.c], px: true } : { a: [G8.c, G8.g0 - 1], b: [G8.c, G8.g1 + 1], px: true });
        nByName++;
        break;
      }
    }
    info.byName = nByName;
    try { info.leaf = _impLeaves(r, segs, GP, t); } catch (eLf) { info.leaf = { why: 'threw: ' + ((eLf && eLf.message) || eLf) }; }
    /* v130: and the doors in the slanted walls */
    try { info.dleaf = _impDiagLeaves(r, segs, GP, t); } catch (eDl) { info.dleaf = { why: 'threw: ' + ((eDl && eDl.message) || eDl) }; }
    info.used = true;
    r.pxInfo = info;
    return { raw: r, info: info };
  }
  /* THE DOORS AS THE PICTURE DRAWS THEM (v117, Marek: vzdy kdyz jsou videt
     kridla, kde se oteviraji -- i bez oblouku -- dat spravne orientovane a
     umistene dvere). The reader says which jamb a door hangs on and which
     room it opens into, and it gets it wrong now and then: the second
     bedroom's door of the sales plan came with its hinge at the top jamb,
     where the picture has it at the bottom. The picture is asked instead.
     At each inside door, now sitting in its break in the wall, a straight
     leaf is looked for: from either jamb, into either room, at any angle
     from lying nearly along the wall to standing square to it -- drawn
     with its arc or without one -- as long as the break is wide (one leaf)
     or half that from both jambs (a pair). It must end where a leaf ends:
     a line that runs on past its tip is a wall or a counter, not a door.
     Pixels of the walls themselves never count. One clear answer wins over
     the reader; two answers close to each other, or none, and the reader's
     door is kept as read. What changes: the jamb, the room it opens into,
     and the kind when the picture draws plainly another (a pair where one
     door was read, one leaf where a pair or a doorway was read). */
  var IMP_LEAF_MIN = 0.62, IMP_LEAF_MARGIN = 0.15;
  function _impLeaves(r, segs, G, t) {
    var res = { looked: 0, found: 0, moved: 0, turned: 0, kinds: 0, kept: 0, said: [] }, T0 = Date.now();
    if (!G || !G.gray || !G.w || !G.h) { res.why = 'no picture to read them from'; return res; }
    var W = G.w, H = G.h, gray = G.gray, bg = G.bg || 245, i, k;
    var DK = Math.min(bg - 60, 175), AK = bg - 28;
    /* the walls are masked out: a leaf is a line, a wall is not */
    var M = new Uint8Array(W * H);
    for (k = 0; k < segs.length; k++) {
      var S = segs[k], hh = S.o === 'h', lo = Math.max(0, Math.floor(S.a0 - 1)), hi = Math.min((hh ? W : H) - 1, Math.ceil(S.a1 + 1));
      var c0 = Math.max(0, Math.floor(S.c - S.t / 2 - 1)), c1 = Math.min((hh ? H : W) - 1, Math.ceil(S.c + S.t / 2 + 1)), a, b;
      for (a = lo; a <= hi; a++) for (b = c0; b <= c1; b++) M[hh ? (b * W + a) : (a * W + b)] = 1;
    }
    function dark(x, y, thr, rr) {
      var xi = Math.round(x), yi = Math.round(y), dx, dy;
      for (dy = -rr; dy <= rr; dy++) {
        var yy = yi + dy;
        if (yy < 0 || yy >= H) continue;
        for (dx = -rr; dx <= rr; dx++) {
          var xx = xi + dx;
          if (xx < 0 || xx >= W) continue;
          var ix = yy * W + xx;
          if (!M[ix] && gray[ix] < thr) return true;
        }
      }
      return false;
    }
    function nm(d) { return (d.rooms && d.rooms.length) ? d.rooms.join(' / ') : (d.label || 'door'); }
    /* A FIXTURE'S EDGE IS NOT A LEAF (v141, plan 1000073500: the laundry
       closet's door opens into the hall, its leaf drawn ajar; the washer's
       box stands square in the closet behind it, a side at each jamb, and the
       test took the two sides for the leaves of a pair opening into the
       closet -- the door turned over the washer). The sides of every piece
       the reader drew, square to a wall at a jamb, are not leaves. */
    var FXE = [];
    for (k = 0; r.fixtures && k < r.fixtures.length; k++) {
      var FQ = r.fixtures[k];
      if (!FQ || FQ.sug || !FQ.a || !FQ.b) continue;
      var fx0 = Math.min(FQ.a[0], FQ.b[0]), fx1 = Math.max(FQ.a[0], FQ.b[0]), fy0 = Math.min(FQ.a[1], FQ.b[1]), fy1 = Math.max(FQ.a[1], FQ.b[1]);
      FXE.push({ o: 'v', c: fx0, lo: fy0, hi: fy1 }, { o: 'v', c: fx1, lo: fy0, hi: fy1 }, { o: 'h', c: fy0, lo: fx0, hi: fx1 }, { o: 'h', c: fy1, lo: fx0, hi: fx1 });
    }
    res.fxEdge = 0;
    for (i = 0; r.doors && i < r.doors.length; i++) {
      var d = r.doors[i], gp = d.gp;
      if (!gp || gp.shell) continue;
      var g = gp.g1 - gp.g0, tw = gp.tw || t, ho = gp.o === 'h';
      if (!(g > 6)) continue;
      res.looked++;
      var rr = Math.max(2, Math.round(tw * 0.15));
      /* a point and a direction, along the wall and across it */
      var XY = function (al, ac) { return ho ? [al, ac] : [ac, al]; };
      var run = function (al0, ac0, dal, dac, L, f0, f1, thr, r2) {
        var n = Math.max(6, Math.round(L * (f1 - f0) / 2)), hit = 0, q;
        for (q = 0; q < n; q++) {
          var f = f0 + (f1 - f0) * (q + 0.5) / n, P = XY(al0 + dal * L * f, ac0 + dac * L * f);
          if (dark(P[0], P[1], thr, r2)) hit++;
        }
        return hit / n;
      };
      /* the best straight leaf from jamb J (0 the low end, 1 the high end)
         into side s (-1, +1), L long */
      var leaf = function (J, s, L) {
        var best = { sc: -1, cov: 0, th: 90, e: 0, end: 0 }, inw = J ? -1 : 1, e, th;
        for (e = 0; e <= 1; e++) {
          var al0 = (J ? gp.g1 : gp.g0) + inw * e * 0.07 * g, ac0 = gp.c + s * tw / 2;
          for (th = 20; th <= 100; th += 4) {
            var rad = th * Math.PI / 180, dal = inw * Math.cos(rad), dac = s * Math.sin(rad);
            var cv = run(al0, ac0, dal, dac, L, 0.15, 0.85, DK, rr);
            if (cv < 0.4) continue;
            var end = run(al0, ac0, dal, dac, L, 1.12, 1.4, DK, rr), sc = cv - 0.7 * end;
            if (sc > best.sc) best = { sc: sc, cov: cv, th: th, e: e, end: end };
          }
        }
        return best;
      };
      /* the arc from the tip of that leaf back to the other jamb */
      var arc = function (J, s, Lf, R) {
        if (!(Lf.sc > 0)) return 0;
        var inw = J ? -1 : 1, al0 = (J ? gp.g1 : gp.g0) + inw * Lf.e * 0.07 * g, ac0 = gp.c + s * tw / 2, bestA = 0, kR;
        for (kR = 0; kR < 3; kR++) {
          var Rr = R * [0.94, 1.0, 1.06][kR], n = 14, hit = 0, q;
          for (q = 0; q < n; q++) {
            var ph = (0.12 + 0.76 * (q + 0.5) / n) * Lf.th * Math.PI / 180;
            var P = XY(al0 + inw * Rr * Math.cos(ph), ac0 + s * Rr * Math.sin(ph));
            if (dark(P[0], P[1], AK, rr + 1)) hit++;
          }
          bestA = Math.max(bestA, hit / n);
        }
        return bestA;
      };
      /* A LEAF IS ITS OWN DOOR'S (v134, plan covered balcony: zase agent
         netrefil orientaci a otevirani dveri -- znacka je polovicni). The
         hall's two doors are drawn half open with a short dashed arc; the
         test found the pantry door's leaf and arc beside them and turned
         both into the hall, where they struck each other. A leaf whose swing
         would sweep the opening of another door -- any door of the plan,
         its middle inside the quarter circle -- is not this door's leaf. */
      var OTH = [];
      for (k = 0; k < r.doors.length; k++) {
        var dO = r.doors[k];
        if (k === i || !dO.gp) continue;
        OTH.push(dO.gp.o === 'h' ? [(dO.gp.g0 + dO.gp.g1) / 2, dO.gp.c] : [dO.gp.c, (dO.gp.g0 + dO.gp.g1) / 2]);
      }
      var sweeps = function (J, s, L) {
        var inw = J ? -1 : 1, h0 = J ? gp.g1 : gp.g0, k2;
        for (k2 = 0; k2 < OTH.length; k2++) {
          var Pq = OTH[k2], al = (ho ? Pq[0] : Pq[1]) - h0, ac = (ho ? Pq[1] : Pq[0]) - gp.c;
          var da = al * inw, dc = ac * s;
          if (da > -tw && dc > tw / 2 && Math.hypot(da, dc) < L * 1.05) return true;
        }
        return false;
      };
      /* the leaf found from jamb J into side s, L long, along a piece's side? */
      var fxEdge = function (J, s, Lf, L) {
        if (!(Lf && Lf.sc > 0) || Lf.th < 66 || !FXE.length) return false;
        var inw = J ? -1 : 1, al0 = (J ? gp.g1 : gp.g0) + inw * Lf.e * 0.07 * g, ac0 = gp.c + s * tw / 2, rad = Lf.th * Math.PI / 180;
        var alM = al0 + inw * Math.cos(rad) * L / 2, c1 = ac0, c2 = ac0 + s * Math.sin(rad) * L, lo = Math.min(c1, c2), hi = Math.max(c1, c2), q;
        for (q = 0; q < FXE.length; q++) {
          var E = FXE[q];
          if (E.o !== (ho ? 'v' : 'h')) continue;
          if (Math.abs(E.c - alM) > Math.max(4, 0.6 * tw)) continue;
          if (Math.min(hi, E.hi) - Math.max(lo, E.lo) >= 0.35 * (hi - lo)) return true;
        }
        return false;
      };
      var ONE = [], J, s, nSw = 0, nFe = 0;
      for (J = 0; J <= 1; J++) for (s = -1; s <= 1; s += 2) {
        var L1 = leaf(J, s, g), a1 = arc(J, s, L1, g), sw1 = sweeps(J, s, g), fe1 = fxEdge(J, s, L1, g);
        if (sw1) nSw++;
        if (fe1) nFe++;
        ONE.push({ J: J, s: s, cov: (sw1 || fe1) ? 0 : L1.cov, sc: (sw1 || fe1) ? -1 : L1.sc + 0.4 * a1, arc: a1, th: L1.th });
      }
      ONE.sort(function (p1, p2) { return p2.sc - p1.sc; });
      var TWO = [];
      for (s = -1; s <= 1; s += 2) {
        var LA = leaf(0, s, g / 2), LB = leaf(1, s, g / 2), aA = arc(0, s, LA, g / 2), aB = arc(1, s, LB, g / 2), sw2 = sweeps(0, s, g / 2) || sweeps(1, s, g / 2);
        var fe2 = fxEdge(0, s, LA, g / 2) || fxEdge(1, s, LB, g / 2);
        if (fe2) nFe++;
        TWO.push({ s: s, cov: (sw2 || fe2) ? 0 : Math.min(LA.cov, LB.cov), sc: (sw2 || fe2) ? -1 : Math.min(LA.sc, LB.sc) + 0.4 * Math.min(aA, aB), arc: Math.min(aA, aB) });
      }
      if (nFe) res.fxEdge++;
      TWO.sort(function (p1, p2) { return p2.sc - p1.sc; });
      if (global.__impLeafDebug) res.said.push('DBG "' + nm(d) + '" gap ' + gp.o + ' c' + gp.c.toFixed(0) + ' ' + gp.g0.toFixed(0) + '..' + gp.g1.toFixed(0) + ' ONE ' + ONE.map(function (o9) { return 'J' + o9.J + (o9.s > 0 ? '+' : '-') + ' sc' + o9.sc.toFixed(2) + ' cov' + o9.cov.toFixed(2) + ' arc' + o9.arc.toFixed(2) + ' th' + o9.th; }).join(', '));
      var b1 = ONE[0], b2 = TWO[0], pick = null;
      var ok1 = b1.cov >= IMP_LEAF_MIN && b1.sc - ONE[1].sc >= IMP_LEAF_MARGIN;
      var ok2 = b2.cov >= IMP_LEAF_MIN && b2.sc - TWO[1].sc >= IMP_LEAF_MARGIN;
      if (ok2 && (!ok1 || b2.sc >= b1.sc - 0.05)) pick = { two: true, s: b2.s, cov: b2.cov, arc: b2.arc };
      else if (ok1) pick = { two: false, J: b1.J, s: b1.s, cov: b1.cov, arc: b1.arc, th: b1.th };
      var kd0 = String(d.kind || 'hinged'), mid = (gp.g0 + gp.g1) / 2;
      if (nSw) res.swept = (res.swept || 0) + nSw;
      if (!pick) {
        res.kept++;
        res.said.push('"' + nm(d) + '" ' + kd0 + ' -- no clear leaf (one ' + b1.cov.toFixed(2) + ', pair ' + b2.cov.toFixed(2) + '), kept as read');
        continue;
      }
      /* A PASSAGE STAYS A PASSAGE (v133, Marek: nesmyslne dvojite dvere v
         miste, kde je volny pruchod). The hall of the covered-balcony plan
         opens into the kitchen with no door; the bathroom's and the
         bedroom's leaves stand at its two jambs, and the test saw a pair in
         them -- the hall got double doors that struck both. The drop zone by
         an entry, its lettering read as leaves, got a pair too. Where the
         reader read a passage the picture no longer makes a door of it. */
      if (kd0 === 'opening') {
        res.kept++;
        res.said.push('"' + nm(d) + '" opening -- ' + (pick.two ? 'a pair' : 'a leaf') + ' near it in the picture (leaf ' + pick.cov.toFixed(2) + ', arc ' + pick.arc.toFixed(2) +
                      '), but a passage the reader read stays a passage -- the leaves at its jambs are the next doors\' (v133)');
        continue;
      }
      /* what the reader said, in the same terms */
      var hal = d.hinge ? (ho ? d.hinge[0] : d.hinge[1]) : mid, oac = d.opensTo ? (ho ? d.opensTo[1] : d.opensTo[0]) : gp.c;
      var J0 = (Math.abs(hal - mid) > 0.1 * g) ? (hal > mid ? 1 : 0) : -1, s0 = (Math.abs(oac - gp.c) > 0.1 * g) ? (oac > gp.c ? 1 : -1) : 0;
      /* THE READER KEEPS IT UNLESS THE PICTURE DRAWS A WHOLE DOOR (v134,
         plan covered balcony: the hall's doors are drawn half open, a pale
         leaf and a short dashed arc; the test read the word HALL as a leaf
         and turned the bathroom's door into the hall). The picture turns a
         door the reader drew -- the other jamb, the other room -- only when
         it shows a whole symbol: a plain leaf (four fifths of it) and its
         arc (three fifths); anything less and the reader's door stands. */
      if (!pick.two && kd0 === 'hinged' && (J0 >= 0 || s0) && ((J0 >= 0 && pick.J !== J0) || (s0 && pick.s !== s0)) && !(pick.cov >= 0.8 && pick.arc >= 0.6)) {
        res.kept++;
        res.said.push('"' + nm(d) + '" hinged -- the picture\'s leaf on the ' + (pick.J ? 'high' : 'low') + ' jamb into the ' + (pick.s > 0 ? '+' : '-') + (ho ? 'y' : 'x') +
                      ' side is not a whole door symbol (leaf ' + pick.cov.toFixed(2) + ', arc ' + pick.arc.toFixed(2) + ') -- the reader\'s hinge and side are kept (v134)');
        continue;
      }
      var kd1 = kd0, ch = [];
      if (pick.two) {
        if (kd0 === 'hinged' || kd0 === 'opening') kd1 = 'double';
        else if ((kd0 === 'bypass' || kd0 === 'sliding' || kd0 === 'pocket') && pick.cov >= 0.75) kd1 = 'double';
      } else {
        if (kd0 === 'opening' && (pick.arc >= 0.25 || pick.cov >= 0.8)) kd1 = 'hinged';
        /* v142: a bifold is drawn as two short leaves and no arc; one leaf with
           no arc is the bifold's own -- plan 1000073355's laundry door became
           a hinged door over the washer */
        else if ((kd0 === 'double' || kd0 === 'bifold') && pick.cov >= 0.75 && (kd0 === 'double' || pick.arc >= 0.5)) kd1 = 'hinged';
        else if ((kd0 === 'bypass' || kd0 === 'sliding' || kd0 === 'pocket') && pick.cov >= 0.75 && pick.arc >= 0.3) kd1 = 'hinged';
      }
      var swings = (kd1 === 'hinged' || kd1 === 'double' || kd1 === 'bifold');
      if (!swings) {
        res.kept++;
        res.said.push('"' + nm(d) + '" ' + kd0 + ' -- a leaf in the picture, but not plain enough to make it a swinging door, kept as read');
        continue;
      }
      res.found++;
      if (kd1 !== kd0) { ch.push(kd0 + ' -> ' + kd1); res.kinds++; }
      if (!pick.two && kd1 === 'hinged' && J0 >= 0 && J0 !== pick.J) { ch.push('hinge to the other jamb'); res.moved++; }
      if (s0 && s0 !== pick.s) { ch.push('opens into the other room'); res.turned++; }
      /* v134: what the reader said, kept -- a pair seen in the picture may
         still be too narrow to be one */
      if (kd1 !== kd0) { d.kindRead = kd0; d.hingeRead = d.hinge || null; d.opensRead = d.opensTo || null; d.pairPx = !!pick.two; }
      d.kind = kd1;
      d.hinge = pick.two ? XY(mid, gp.c) : XY(pick.J ? gp.g1 : gp.g0, gp.c);
      d.opensTo = XY(mid, gp.c + pick.s * g / 2);
      d.pxLeaf = true;
      res.said.push('"' + nm(d) + '" ' + (pick.two ? 'pair' : ('leaf on the ' + (pick.J ? 'high' : 'low') + ' jamb at ' + pick.th + ' deg')) +
                    ' into the ' + (pick.s > 0 ? '+' : '-') + (ho ? 'y' : 'x') + ' side (leaf ' + pick.cov.toFixed(2) + ', arc ' + pick.arc.toFixed(2) + ')' +
                    (ch.length ? (' -- ' + ch.join(', ')) : ' -- as read') + (nFe ? (' -- ' + nFe + ' line(s) at a jamb along a piece\'s side not taken for a leaf (v141)') : ''));
    }
    res.ms = Date.now() - T0;
    return res;
  }
  /* THE LEAF OF A DOOR IN A SLANTED WALL, FROM THE PICTURE (v130, Marek:
     kridla v sikmych stenach opet nereflektuji smer otevirani). The reader
     gives a hinge and a side for each door, and for the two doors of a V it
     put the hinge on the outer jambs -- the drawing has both leaves hung at
     the point of the V. A door in a slanted wall was left out of the leaf
     test above, which works along x and z only. Here the same test runs in
     the slanted wall's own frame: the straight leaf from either jamb into
     either side, scored with its arc back to the other jamb; a clear winner
     sets the hinge and the side, anything less keeps what was read. */
  function _impDiagLeaves(r, segs, G, t) {
    var res = { looked: 0, found: 0, moved: 0, turned: 0, kept: 0, said: [] }, T0 = Date.now();
    if (!G || !G.gray || !G.w || !G.h) { res.why = 'no picture to read them from'; return res; }
    var W = G.w, H = G.h, gray = G.gray, bg = G.bg || 245, i, k;
    var DK = Math.min(bg - 60, 175), AK = bg - 28;
    var DG = [];
    for (k = 0; r.partitions && k < r.partitions.length; k++) {
      var pa = r.partitions[k].a, pb = r.partitions[k].b;
      if (!pa || !pb) continue;
      var an = Math.atan2(Math.abs(pb[1] - pa[1]), Math.abs(pb[0] - pa[0])) * 180 / Math.PI;
      if (an > 20 && an < 70) DG.push({ a: pa, b: pb });
    }
    if (!DG.length) { res.why = 'no slanted wall'; return res; }
    /* the walls are masked out, the slanted ones as well: a leaf is a line */
    var M = new Uint8Array(W * H);
    for (k = 0; segs && k < segs.length; k++) {
      var S = segs[k], hh = S.o === 'h', lo = Math.max(0, Math.floor(S.a0 - 1)), hi = Math.min((hh ? W : H) - 1, Math.ceil(S.a1 + 1));
      var c0 = Math.max(0, Math.floor(S.c - S.t / 2 - 1)), c1 = Math.min((hh ? H : W) - 1, Math.ceil(S.c + S.t / 2 + 1)), a, b;
      for (a = lo; a <= hi; a++) for (b = c0; b <= c1; b++) M[hh ? (b * W + a) : (a * W + b)] = 1;
    }
    for (k = 0; k < DG.length; k++) {
      var ga = DG[k].a, gb = DG[k].b, gL = Math.hypot(gb[0] - ga[0], gb[1] - ga[1]);
      if (!(gL > 1)) continue;
      var gx0 = Math.max(0, Math.floor(Math.min(ga[0], gb[0]) - t)), gx1 = Math.min(W - 1, Math.ceil(Math.max(ga[0], gb[0]) + t));
      var gy0 = Math.max(0, Math.floor(Math.min(ga[1], gb[1]) - t)), gy1 = Math.min(H - 1, Math.ceil(Math.max(ga[1], gb[1]) + t)), xx, yy;
      for (yy = gy0; yy <= gy1; yy++) for (xx = gx0; xx <= gx1; xx++) {
        var tq = ((xx - ga[0]) * (gb[0] - ga[0]) + (yy - ga[1]) * (gb[1] - ga[1])) / (gL * gL);
        if (tq < -0.02 || tq > 1.02) continue;
        var pq = Math.abs((xx - ga[0]) * (gb[1] - ga[1]) - (yy - ga[1]) * (gb[0] - ga[0])) / gL;
        if (pq <= t / 2 + 1) M[yy * W + xx] = 1;
      }
    }
    function dark(x, y, thr, rr) {
      var xi = Math.round(x), yi = Math.round(y), dx, dy;
      for (dy = -rr; dy <= rr; dy++) {
        var y2 = yi + dy;
        if (y2 < 0 || y2 >= H) continue;
        for (dx = -rr; dx <= rr; dx++) {
          var x2 = xi + dx;
          if (x2 < 0 || x2 >= W) continue;
          var ix = y2 * W + x2;
          if (!M[ix] && gray[ix] < thr) return true;
        }
      }
      return false;
    }
    function nm(d) { return (d.rooms && d.rooms.length) ? d.rooms.join(' / ') : (d.label || 'door'); }
    for (i = 0; r.doors && i < r.doors.length; i++) {
      var d = r.doors[i];
      if (!d || !d.onDiag || !d.at) continue;
      var kd0 = String(d.kind || 'hinged');
      if (kd0 !== 'hinged') continue;
      /* its wall: the slanted one it stands in */
      var gw = null, bpe = 1e9;
      for (k = 0; k < DG.length; k++) {
        var A0 = DG[k].a, B0 = DG[k].b, L0 = Math.hypot(B0[0] - A0[0], B0[1] - A0[1]);
        if (!(L0 > 0)) continue;
        var al0 = ((d.at[0] - A0[0]) * (B0[0] - A0[0]) + (d.at[1] - A0[1]) * (B0[1] - A0[1])) / L0;
        var pe0 = Math.abs((d.at[0] - A0[0]) * (B0[1] - A0[1]) - (d.at[1] - A0[1]) * (B0[0] - A0[0])) / L0;
        if (al0 < -0.1 * L0 || al0 > 1.1 * L0) continue;
        if (pe0 < bpe) { bpe = pe0; gw = { a: A0, L: L0, ux: (B0[0] - A0[0]) / L0, uz: (B0[1] - A0[1]) / L0, al: al0 }; }
      }
      if (!gw) continue;
      var w = Number(d.w) || 0;
      if (!(w > 6)) continue;
      res.looked++;
      var g0 = gw.al - w / 2, g1 = gw.al + w / 2, g = w, tw = t, nx = -gw.uz, nz = gw.ux;
      var rr = Math.max(2, Math.round(tw * 0.15));
      var XY = function (al, ac) { return [gw.a[0] + gw.ux * al + nx * ac, gw.a[1] + gw.uz * al + nz * ac]; };
      var run = function (al0, ac0, dal, dac, L, f0, f1, thr, r2) {
        var n = Math.max(6, Math.round(L * (f1 - f0) / 2)), hit = 0, q;
        for (q = 0; q < n; q++) {
          var f = f0 + (f1 - f0) * (q + 0.5) / n, Pp = XY(al0 + dal * L * f, ac0 + dac * L * f);
          if (dark(Pp[0], Pp[1], thr, r2)) hit++;
        }
        return hit / n;
      };
      /* THE LEAF AND ITS ARC TOGETHER, AT THE DOOR'S OWN SIZE. The reader's
         width runs from wall end to wall end, and the drawn door stands
         between the jamb blocks inside them -- its leaf a fifth shorter, its
         hinge in from the end. So the hinge is tried a little in from the
         jamb and the leaf at several lengths, and each leaf is scored with
         the arc of its own length from its tip back along the wall: a leaf
         without its arc is the arc of the other hand seen edge on. */
      var cand = function (J, sd) {
        var best = { sc: -1, cov: 0, arc: 0, th: 90, e: 0, rf: 1 }, inw = J ? -1 : 1, e, th, ri, RF = [0.72, 0.80, 0.88, 0.96];
        for (e = 0; e <= 2; e++) {
          var al0 = (J ? g1 : g0) + inw * e * 0.07 * g, ac0 = sd * tw / 2;
          for (ri = 0; ri < RF.length; ri++) {
            var Lr = RF[ri] * g;
            if (Lr + e * 0.07 * g > g * 1.04) continue;
            for (th = 20; th <= 100; th += 4) {
              var rad = th * Math.PI / 180, dal = inw * Math.cos(rad), dac = sd * Math.sin(rad);
              var cv = run(al0, ac0, dal, dac, Lr, 0.15, 0.85, DK, rr);
              if (cv < 0.4) continue;
              var end = run(al0, ac0, dal, dac, Lr, 1.12, 1.4, DK, rr);
              /* its arc: from near the tip back to the wall */
              var n = 16, hit = 0, q;
              for (q = 0; q < n; q++) {
                var ph = (0.12 + 0.76 * (q + 0.5) / n) * rad;
                var Pp = XY(al0 + inw * Lr * Math.cos(ph), ac0 + sd * Lr * Math.sin(ph));
                if (dark(Pp[0], Pp[1], AK, rr + 1)) hit++;
              }
              var ar = hit / n, sc = cv - 0.7 * end + 0.8 * ar;
              if (sc > best.sc) best = { sc: sc, cov: cv, arc: ar, th: th, e: e, rf: RF[ri] };
            }
          }
        }
        return best;
      };
      var ONE = [], J, sd;
      for (J = 0; J <= 1; J++) for (sd = -1; sd <= 1; sd += 2) {
        var C1 = cand(J, sd);
        ONE.push({ J: J, s: sd, cov: C1.cov, sc: C1.sc, arc: C1.arc, th: C1.th, rf: C1.rf });
      }
      ONE.sort(function (p1, p2) { return p2.sc - p1.sc; });
      var b1 = ONE[0];
      if (!(b1.cov >= IMP_LEAF_MIN && b1.arc >= 0.4 && b1.sc - ONE[1].sc >= IMP_LEAF_MARGIN)) {
        res.kept++;
        res.said.push('"' + nm(d) + '" no clear leaf (' + ONE.map(function (o1) { return (o1.J ? 'b' : 'a') + (o1.s > 0 ? '+' : '-') + ' leaf ' + o1.cov.toFixed(2) + ' arc ' + o1.arc.toFixed(2) + ' th ' + o1.th; }).join(', ') + '), kept as read');
        continue;
      }
      /* what the reader said, in the wall's own terms */
      var mid = (g0 + g1) / 2, ch = [];
      var hal = d.hinge ? ((d.hinge[0] - gw.a[0]) * gw.ux + (d.hinge[1] - gw.a[1]) * gw.uz) : mid;
      var oac = d.opensTo ? ((d.opensTo[0] - gw.a[0]) * nx + (d.opensTo[1] - gw.a[1]) * nz) : 0;
      var J0 = (Math.abs(hal - mid) > 0.1 * g) ? (hal > mid ? 1 : 0) : -1, s0 = (Math.abs(oac) > 0.1 * g) ? (oac > 0 ? 1 : -1) : 0;
      if (J0 >= 0 && J0 !== b1.J) { ch.push('hinge to the other jamb'); res.moved++; }
      if (s0 && s0 !== b1.s) { ch.push('opens into the other side'); res.turned++; }
      res.found++;
      d.hinge = XY(b1.J ? g1 : g0, 0);
      d.opensTo = XY(mid, b1.s * g / 2);
      d.pxLeaf = true;
      res.said.push('"' + nm(d) + '" leaf on the ' + (b1.J ? 'far' : 'near') + ' jamb (toward ' + (b1.J ? 'b' : 'a') + ') at ' + b1.th + ' deg, into the ' + (b1.s > 0 ? '+' : '-') + ' side (leaf ' + b1.cov.toFixed(2) + ', arc ' + b1.arc.toFixed(2) + ', ' + Math.round(b1.rf * 100) + ' % of the opening; next best ' + ONE[1].sc.toFixed(2) + ' against ' + b1.sc.toFixed(2) + ')' +
                    (ch.length ? (' -- ' + ch.join(', ')) : ' -- as read'));
    }
    res.ms = Date.now() - T0;
    return res;
  }
  /* down to IMP_SIDE on the long side, on white, as JPEG. A small picture
     (v115) goes UP to IMP_SMALL on its long side first, at most twice: a
     519 px sales plan gives the reader 8 px walls to aim at, and at twice
     the size it read the walls markedly better (Marek's test with the
     traced picture). It costs about 1,700 more input tokens -- under a cent.
     The walls are traced on the picture that is sent: the clean one goes
     under the plan, the one with the walls traced goes to the reader --
     magenta along x, blue along z, down the middle of each wall. */
  var IMP_SMALL = 1600, IMP_UPTO = 1200;
  /* THE PICTURE'S FINGERPRINT (v143, Marek: pokud uzivatel nahraje stejny
     floorplan vicekrat, i ruzni uzivatele, mel by se ukladat jen jednou --
     ta posledni verze, vysledky jsou casem lepsi). Taken off the picture as
     it is read, before the walls are traced on it: the margins of one colour
     round it cut off (white paper, a dark frame -- a few stray pixels let
     be), what is left averaged on a 17 x 16 grid and a 16 x 17 one, and
     each cell said lighter or darker than its neighbour to the right and
     below -- 512 bits, 128 hex. The same plan saved again, at another size,
     as a JPEG or with other margins, comes out within some 30 bits; two
     different plans of the tests differ by 170 and more. The library
     (worker w2.03) keeps one plan for one picture: the latest. */
  function _impPicPrint(D, w, h) {
    var G = new Float32Array(w * h), HB = new Int32Array(32), i, p;
    for (i = 0, p = 0; i < w * h; i++, p += 4) G[i] = 0.299 * D[p] + 0.587 * D[p + 1] + 0.114 * D[p + 2];
    function flat(a, b, step, at0) {
      var n = b - a + 1, kq, q, best = 0, off = 0;
      for (kq = 0; kq < 32; kq++) HB[kq] = 0;
      for (q = a; q <= b; q++) HB[Math.min(31, G[at0 + q * step] >> 3)]++;
      for (kq = 1; kq < 32; kq++) if (HB[kq] > HB[best]) best = kq;
      var mid = best * 8 + 4;
      for (q = a; q <= b; q++) if (Math.abs(G[at0 + q * step] - mid) > 24) off++;
      return off <= Math.max(2, 0.03 * n);
    }
    var x0 = 0, x1 = w - 1, y0 = 0, y1 = h - 1, moved = true;
    while (moved) {
      moved = false;
      while (y0 < y1 && flat(x0, x1, 1, y0 * w)) { y0++; moved = true; }
      while (y1 > y0 && flat(x0, x1, 1, y1 * w)) { y1--; moved = true; }
      while (x0 < x1 && flat(y0, y1, w, x0)) { x0++; moved = true; }
      while (x1 > x0 && flat(y0, y1, w, x1)) { x1--; moved = true; }
    }
    var bw = x1 - x0 + 1, bh = y1 - y0 + 1;
    if (bw < 32 || bh < 32) { x0 = 0; y0 = 0; bw = w; bh = h; }
    function mean(xa, xb, ya, yb) {
      var sm = 0, n = 0, xx, yy;
      for (yy = ya; yy < Math.max(yb, ya + 1); yy++) for (xx = xa; xx < Math.max(xb, xa + 1); xx++) { sm += G[yy * w + xx]; n++; }
      return sm / n;
    }
    var MH = new Float32Array(17 * 16), MV = new Float32Array(16 * 17), gx, gy, hex = '', nib = 0, nb = 0;
    for (gy = 0; gy < 16; gy++) for (gx = 0; gx < 17; gx++)
      MH[gy * 17 + gx] = mean(x0 + Math.floor(gx * bw / 17), x0 + Math.floor((gx + 1) * bw / 17), y0 + Math.floor(gy * bh / 16), y0 + Math.floor((gy + 1) * bh / 16));
    for (gy = 0; gy < 17; gy++) for (gx = 0; gx < 16; gx++)
      MV[gy * 16 + gx] = mean(x0 + Math.floor(gx * bw / 16), x0 + Math.floor((gx + 1) * bw / 16), y0 + Math.floor(gy * bh / 17), y0 + Math.floor((gy + 1) * bh / 17));
    function bit(b) { nib = (nib << 1) | b; if (++nb === 4) { hex += nib.toString(16); nib = 0; nb = 0; } }
    for (gy = 0; gy < 16; gy++) for (gx = 0; gx < 16; gx++) bit(MH[gy * 17 + gx] > MH[gy * 17 + gx + 1] ? 1 : 0);
    for (gy = 0; gy < 16; gy++) for (gx = 0; gx < 16; gx++) bit(MV[gy * 16 + gx] > MV[(gy + 1) * 16 + gx] ? 1 : 0);
    return { v: 1, d: hex, ar: Math.round(bw / bh * 1000) / 1000 };
  }
  /* exact (v152): a picture already read -- the one kept with a plan read on
     the homepage -- keeps its own size, the size the reading's points are in */
  /* THE BLACK INK ONLY (v189, Marek: za me by mel byt plan dobre citelny --
     pricky, steny, dvere, fitouts, room names. Stacilo by extrahovat cernou
     barvu). The sales plan went to the reader in colour: a cream sheet, a
     grey garage, blue porches, tinted rooms, grey dashed ceiling lines --
     and the reader put doors where the drawing has none and none where it
     has them. What the drawing says is in its dark ink: walls, partitions,
     doors and their swings, the fittings, the names. The reader now gets
     that alone -- every pixel darker than the sheet by a margin (grey under
     170, or 50 under the paper on a grey scan) and not coloured kept as it
     is, everything else white. Not for a render (its walls are white) and
     not when the ink itself is coloured (more than 15 % of the dark pixels
     in colour -- red or blue CAD lines). The picture kept with the account
     and laid under the plan stays as drawn (sent beside it as the clean
     one). */
  function _impInk(cx, px, w, h, PW) {
    if (PW && PW.color >= IMP_RENDER_COLOR) return { used: false, why: 'a render, ' + Math.round(PW.color * 100) + ' % of it in colour' };
    var n = w * h, i, HG = new Uint32Array(256), G = new Uint8Array(n), S = new Uint8Array(n), acc = 0, bg = 255, k;
    for (i = 0; i < n; i++) {
      var r0 = px[i * 4], g0 = px[i * 4 + 1], b0 = px[i * 4 + 2], gg = (r0 * 299 + g0 * 587 + b0 * 114) / 1000 | 0;
      G[i] = gg; S[i] = Math.max(r0, g0, b0) - Math.min(r0, g0, b0); HG[gg]++;
    }
    for (k = 0; k < 256; k++) { acc += HG[k]; if (acc >= n / 2) { bg = k; break; } }
    var thr = Math.min(170, bg - 50);
    if (thr < 60) return { used: false, why: 'the sheet is too dark (paper grey ' + bg + ')' };
    var dark = 0, darkG = 0;
    for (i = 0; i < n; i++) if (G[i] < thr) { dark++; if (S[i] < 70) darkG++; }
    if (!dark) return { used: false, why: 'no ink darker than grey ' + thr };
    if (darkG / dark < 0.85) return { used: false, why: Math.round((1 - darkG / dark) * 100) + ' % of the ink in colour' };
    /* A HAIR LINE IS INK TOO (v196, Marek on plan 1000075646: je tam fitouts
       velmi tenkymi liniemi). The kitchen's counters, its sink and hob, the
       basin, the bed and both door arcs were drawn in thin grey lines --
       grey 170 to 230 once the picture is scaled -- and the grey cut took
       them all: the reader saw an empty kitchen and doors with no swing. A
       pixel is ink also when it stands 50 darker than the lightest pixel
       within 3 px of it: a line on the paper or on a tinted floor is, a
       tinted floor itself (the same grey all round) is not, so the fills
       still go and the hair lines stay. */
    var R = 3, MX = new Uint8Array(n), MY = new Uint8Array(n), x, y, q, m;
    for (y = 0; y < h; y++) for (x = 0; x < w; x++) {
      m = 0;
      for (q = Math.max(0, x - R); q <= Math.min(w - 1, x + R); q++) if (G[y * w + q] > m) m = G[y * w + q];
      MX[y * w + x] = m;
    }
    for (y = 0; y < h; y++) for (x = 0; x < w; x++) {
      m = 0;
      for (q = Math.max(0, y - R); q <= Math.min(h - 1, y + R); q++) if (MX[q * w + x] > m) m = MX[q * w + x];
      MY[y * w + x] = m;
    }
    var ID = cx.createImageData(w, h), d = ID.data, kept = 0, hair = 0;
    for (i = 0; i < n; i++) {
      var inkP = S[i] < 70 && (G[i] < thr || MY[i] - G[i] >= 50), v = inkP ? G[i] : 255;
      if (inkP) { kept++; if (G[i] >= thr) hair++; }
      d[i * 4] = v; d[i * 4 + 1] = v; d[i * 4 + 2] = v; d[i * 4 + 3] = 255;
    }
    cx.putImageData(ID, 0, 0);
    return { used: true, thr: thr, bg: bg, pct: Math.round(kept / n * 1000) / 10, hair: Math.round(hair / n * 1000) / 10 };
  }
  function _impShrink(file, cb, exact) {
    var U = global.URL || global.webkitURL, url = U.createObjectURL(file), img = new global.Image();
    img.onload = function () {
      try {
        var w0 = img.naturalWidth, h0 = img.naturalHeight, L0 = Math.max(w0, h0);
        var k = exact ? 1 : ((L0 < IMP_UPTO) ? Math.min(2, IMP_SMALL / L0) : Math.min(1, IMP_SIDE / L0));
        var w = Math.max(1, Math.round(w0 * k)), h = Math.max(1, Math.round(h0 * k));
        var cv = document.createElement('canvas');
        cv.width = w; cv.height = h;
        var cx = cv.getContext('2d');
        cx.fillStyle = '#fff'; cx.fillRect(0, 0, w, h);
        try { cx.imageSmoothingEnabled = true; cx.imageSmoothingQuality = 'high'; } catch (eQ) {}
        cx.drawImage(img, 0, 0, w, h);
        var data = cv.toDataURL('image/jpeg', 0.85), PW = null, sent = data, ID0 = null, FPP = null;
        /* v143: the fingerprint, off the picture before the walls are traced on it */
        try { ID0 = cx.getImageData(0, 0, w, h).data; } catch (eI) { ID0 = null; }
        try { FPP = ID0 ? _impPicPrint(ID0, w, h) : null; } catch (eFP) { FPP = null; }
        try { PW = _impWalls(ID0 || cx.getImageData(0, 0, w, h).data, w, h); } catch (eW) { PW = { segs: [], t: 0, err: (eW && eW.message) || String(eW) }; }
        var INK = null;
        try { INK = ID0 ? _impInk(cx, ID0, w, h, PW) : null; } catch (eInk) { INK = { used: false, why: 'threw: ' + ((eInk && eInk.message) || eInk) }; }
        if (INK && INK.used) { try { sent = cv.toDataURL('image/jpeg', 0.85); } catch (eS) { sent = data; } }
        var MKS = null;
        if (IMP_MARKS_ON) { try { MKS = _impMarks(PW); } catch (eMk) { MKS = null; } }
        if (MKS && MKS.segs.length < 6) MKS = null;
        if (MKS) {
          try { _impMarksDraw(cx, MKS); sent = cv.toDataURL('image/jpeg', 0.9); } catch (eMD) { MKS = null; sent = data; }
        }
        else if (PW && PW.segs && PW.segs.length >= 6) {
          try {
            cx.lineCap = 'butt';
            for (var sw = 0; sw < PW.segs.length; sw++) {
              var SG = PW.segs[sw];
              cx.strokeStyle = (SG.o === 'h') ? '#FF00FF' : '#00A0FF';
              cx.lineWidth = Math.max(2, Math.round(SG.t * 0.3));
              cx.beginPath();
              if (SG.o === 'h') { cx.moveTo(SG.a0, SG.c); cx.lineTo(SG.a1, SG.c); } else { cx.moveTo(SG.c, SG.a0); cx.lineTo(SG.c, SG.a1); }
              cx.stroke();
            }
            sent = cv.toDataURL('image/jpeg', 0.85);
          } catch (eO) { sent = data; }
        }
        U.revokeObjectURL(url);
        cb(null, { url: data, b64: sent.slice(sent.indexOf(',') + 1), w: w, h: h, w0: w0, h0: h0, kb: Math.round(sent.length * 0.75 / 1024), walls: PW, up: k > 1, fp: FPP,
                   ink: INK, clean: (INK && INK.used) || MKS ? data.slice(data.indexOf(',') + 1) : '', marks: MKS });
      } catch (e) { cb('the picture could not be drawn: ' + ((e && e.message) || e)); }
    };
    img.onerror = function () { try { U.revokeObjectURL(url); } catch (e) {} cb('the file could not be opened as a picture (' + (file.type || 'unknown type') + ')'); };
    img.src = url;
  }
  /* WHAT THREW IS SAID (v144). Every refresh ran in a try with an empty
     catch, so a line that threw stopped the reading's own tick without a
     word: a plan taken out of the library came back, but the line about it
     asked for the tokens and the time of a reading it never had, threw, and
     the plan was never fitted, written or reported -- the spinner turned
     for ever and COPY showed the line from before (Marek: ani po 3 minutach
     se pudorys z databaze nenacetl). What throws here goes to the log, once
     for each message. */
  var _impRefreshErr = '';
  function _impRefresh() {
    try { _impTickAny(); _fitShow(); }
    catch (e) {
      var m9 = String((e && e.message) || e);
      if (m9 !== _impRefreshErr) { _impRefreshErr = m9; try { _impSay('PLAN the page THREW while it showed the plan: ' + m9 + ' (v144)'); } catch (e2) {} }
    }
  }
  /* v179 (Marek: pri refreshi vidim pudorys spolu s nejakymi obrysy
     floorplanu): a page coming back to the screen tells the importer
     (tabSay), and with no reading of the buyer's on this page that ran the
     admin TEST box -- whose sample house, 10 x 7 m, was then drawn in violet
     over the plan as "where it will land". The test runs only with
     ?planTest=2 or 3 in the address now; otherwise nothing is drawn. */
  function _impTickAny() {
    if (_imp.user) { _impUserTick(); return; }
    if (_impMode() >= 2) { _impTick2(); return; }
    if (_imp.prev && !_impParam()) _imp.prev = null;
    /* what a RESTORE or an Undo did is still said */
    var L0 = (_imp.applyLines || []).slice();
    if (_imp.applied || _imp.restored || _imp.undone) L0.push(_impNowLine());
    if (L0.length) { _imp.lines = L0; _impLog(L0); }
  }
  /* THE PICTURE UNDER THE PLAN (v103, Marek: pudorys podlozeny, opacity 30
     nebo 50 %). The plan's own tracing image is used -- the one the Trace
     button brings, with its slider, its handles and its bin -- laid where the
     read plan lands: the picture's pixels go the same way the plan did
     (scaled from its dimensions, turned straight, turned a quarter if the
     product needed it, stretched onto the module), so a wall in the picture
     sits under the wall it became. It comes at 30 % and locked, scenery and
     not something to move; the Trace button wakes it. Placed once for each
     reading and each fit, so a picture moved or binned by hand stays so.
     It lives in the page, not in the project: a reload has none. */
  /* v131 (Marek: default opacity pak staci 10 %): the picture lies over the plan now */
  var IMP_TRACE_OPACITY = 0.10;
  function _impPicToPlan(n, f, px, pz) {
    var t = n.tf, x = px * t.s - t.cx, z = pz * t.s - t.cz, c = Math.cos(-t.turn), sn = Math.sin(-t.turn);
    var mx = x * c - z * sn + t.cx - t.ox0, mz = x * sn + z * c + t.cz - t.oz0;
    var rx = f.turned ? (n.d - mz) : mx, rz = f.turned ? mx : mz;
    return [f.x0 + rx * f.w / f.tf.W1, f.z0 + rz * f.l / f.tf.D1];
  }
  function _impTrace(R, f) {
    if (!R || !R.img || !R.n || !R.n.ok || !R.n.tf || !f || !f.ok || !f.tf) return;
    var key = R.t0 + '|' + (R.longSide || '') + '|' + f.w + 'x' + f.l + '|' + (f.turned ? 1 : 0) + '|' + f.ox + ',' + f.oz;
    if (R.traceKey === key) return;
    if (!R.imgEl) {
      if (R.imgLoading) return;
      R.imgLoading = true;
      var im = new global.Image();
      /* v122: also when the plan was written before the picture had loaded --
         the buyer's plan is written the moment it is read, and the picture
         came too late and was never laid */
      im.onload = function () { R.imgEl = im; R.imgLoading = false; if (_imp.rd === R && _imp.fit && _imp.fit.ok) _impTrace(R, _imp.fit); };
      im.onerror = function () { R.imgLoading = false; };
      im.src = R.img.url;
      return;
    }
    var W = R.img.w, Hh = R.img.h, tf = R.n.tf;
    var A = _impPicToPlan(R.n, f, 0, 0), B = _impPicToPlan(R.n, f, W, 0), Cc = _impPicToPlan(R.n, f, 0, Hh);
    var kx = Math.hypot(B[0] - A[0], B[1] - A[1]) / W, kz = Math.hypot(Cc[0] - A[0], Cc[1] - A[1]) / Hh, k = (kx + kz) / 2;
    var ang = Math.atan2(B[1] - A[1], B[0] - A[0]), ca = Math.cos(ang), sa = Math.sin(ang);
    /* ANCHORED ON THE HOUSE, not on the middle of the picture: the picture
       can only be scaled the same both ways and the plan may have been
       stretched a little more one way, so the error is kept where it does no
       harm -- the house's own middle is exact and its walls are off by half
       the difference at most */
    var HC = _impPicToPlan(R.n, f, tf.hx, tf.hz), du = (W / 2 - tf.hx) * k, dv = (Hh / 2 - tf.hz) * k;
    TRACE.img = R.imgEl; TRACE.ar = Hh / W;
    TRACE.cx = HC[0] + du * ca - dv * sa; TRACE.cz = HC[1] + du * sa + dv * ca;
    TRACE.w = W * k;
    TRACE.angle = ang;
    TRACE.opacity = IMP_TRACE_OPACITY;
    TRACE.armed = false;
    R.traceKey = key;
    _imp.traceOf = R;
    /* laid after the plan was written: it is the project's picture now (v122) */
    if (_imp.applied) { try { _impPicKeep(R, 'the picture loaded after the plan was written'); } catch (eK) {} }
    R.traceTxt = 'the picture lies under the plan at ' + Math.round(IMP_TRACE_OPACITY * 100) + ' %, ' + _impN(TRACE.w) + ' m wide' +
                 (Math.abs(kx - kz) / Math.max(kx, kz) > 0.01 ? (' (the plan was stretched ' + _impN(kx / kz) + ' : 1 across, so it matches to ' + (Math.abs(kx - kz) / Math.max(kx, kz) * 50).toFixed(1) + ' %)') : '');
    try { OVERLAY.repaint(); } catch (eR) {}
  }
  /* SIZE: the long side of the house by hand, outside to outside, for a
     plan with no dimension the reader could use (or to overrule a scale
     taken from the room sizes). Read by the same reader as a dimension. */
  function _impAskSize() {
    var R = _imp.rd;
    if (!R || R.st !== 'done' || typeof global.prompt !== 'function') return;
    var t = global.prompt('The long side of the house, outside to outside -- 12.5 or 12500 in metres or millimetres, 41\'-0" in feet', R.longTxt || '');
    if (t == null || !String(t).trim()) return;
    var r = _impDimText(t, 0);
    if (!(r.m > 1)) { R.sizeNote = 'the long side "' + t + '" could not be read -- nothing changed'; _impRefresh(); return; }
    R.longSide = r.m; R.longTxt = String(t).slice(0, 30); R.sizeNote = '';
    _impRenorm(R);
    _imp.applied = false; _imp.restored = false; _imp.undone = false; _imp.applyLines = null;
    _impRefresh();
  }
  /* THE PLAN OUT OF THE READING, for the variant the V button shows (v109):
     one reading, two plans -- the long side given by hand goes with it */
  function _impRenorm(R) {
    if (!R || !R.conv) return;
    var raw2 = R.conv.raw, o = { units: R.conv.units, variant: _imp.v2 ? 2 : 1 };
    if (R.longSide) {
      o.longSide = R.longSide;
      /* given by hand, it wins over the room sizes (not over a dimension line) */
      if (!raw2.dims.length) { raw2 = JSON.parse(JSON.stringify(raw2)); raw2.rooms.forEach(function (q) { delete q.size; }); }
    }
    /* v140: a render by the reader's word, a drawing by its walls */
    if (R.style === 'render' && !R.styleRead) {
      var SPR = '';
      try { SPR = _impStyleProbe(raw2, R.img && R.img.walls); } catch (eSP) { SPR = ''; }
      if (SPR) { R.styleRead = 'render'; R.styleWhy = SPR; R.style = 'line'; }
    }
    /* v138: a window drawn as two offset panels is a sliding door */
    var SLD = null;
    try { SLD = _impPicSliders(raw2, R.img && R.img.walls, R.style || ''); if (SLD && SLD.n) raw2 = SLD.raw; } catch (eSL) { SLD = { n: 0, said: [], err: String((eSL && eSL.message) || eSL) }; }
    /* v194: the numbered reading -- the walls and doors the reader chose
       among the marks, exactly where the drawing has them */
    var MKU = null, PWuse = R.img && R.img.walls;
    try { MKU = _impFromMarks(raw2, R.res && R.res.plan, R.img && R.img.marks, PWuse); } catch (eMK) { MKU = { used: false, why: 'threw: ' + ((eMK && eMK.message) || eMK) }; }
    R.marksUse = MKU;
    if (MKU && MKU.used) { raw2 = MKU.raw; PWuse = MKU.PW; }
    /* the walls from the picture (v115) */
    var PM = null;
    try { PM = _impPixMerge(raw2, PWuse, R.style || ''); raw2 = PM.raw; } catch (ePM) { PM = { info: { used: false, why: 'threw: ' + ((ePM && ePM.message) || ePM) } }; }
    try { R.n = _impNormalize(raw2, o); } catch (e) { R.n = { ok: false, why: 'threw: ' + ((e && e.message) || e), notes: [] }; }
    if (R.n) R.n.px = PM ? PM.info : null;
    if (R.n && R.styleRead && R.styleWhy) {
      R.n.notes = R.n.notes || [];
      R.n.notes.push('STYLE: the reader called the picture a ' + R.styleRead + ', but ' + R.styleWhy + ' -- it is read as a drawing (v140)');
    }
    if (R.n && SLD && SLD.kept && SLD.kept.length) {
      R.n.notes = R.n.notes || [];
      R.n.notes.push('SLIDER (v165): ' + SLD.kept.length + ' window(s) drawn as two panels kept as windows: ' + SLD.kept.join(', '));
    }
    if (R.n && SLD && (SLD.n || SLD.err)) {
      R.n.notes = R.n.notes || [];
      R.n.notes.push(SLD.err ? ('SLIDER: the picture could not be read for sliding doors -- ' + SLD.err)
                             : ('SLIDER: ' + SLD.n + ' window(s) drawn as two panels side by side became sliding doors (v138): ' + SLD.said.join(', ')));
    }
  }
  function _impReadFail(R, why) {
    if (_imp.rd !== R) return;
    R.st = 'failed'; R.err = String(why || 'unknown');
    _impRefresh();
  }
  function _impAnswer(r) {
    return r.text().then(function (t) { var j = null; try { j = JSON.parse(t); } catch (e) { j = null; } return { s: r.status, j: j, t: t }; });
  }
  function _impReadFile(file) {
    if (_imp.user) _impUserClock();
    var R = _imp.rd = { st: 'shrinking', name: String(file.name || 'picture').slice(0, 60), model: _impModel(), t0: Date.now() };
    _imp.prev = null;
    _impRefresh();
    _impShrink(file, function (err, img) {
      if (_imp.rd !== R) return;
      if (err) { _impReadFail(R, err); return; }
      R.img = img;
      /* v152: the product goes with the picture -- the worker's record of the
         reading (w2.06) names the page it came from */
      _impAsk(R, function (ticket) { return { ticket: ticket, image: img.b64, clean: img.clean || undefined, marks: _impMarksOut(img.marks), mediaType: 'image/jpeg', w: img.w, h: img.h, model: _impModel(), lang: _impLang(), offer: _impOffer(),
                                              product: String(_impProdName() || '').slice(0, 30) }; });
    });
  }
  /* THE TICKET AND THE WORKER (v120): one road for a picture and for a
     description -- only what is sent differs, bodyOf builds it from the
     ticket */
  function _impAsk(R, bodyOf) {
    R.st = 'ticket';
    _impRefresh();
    /* a change has its own quota (v123, worker w1.92) */
    global.fetch(IMP_TICKET + (R.change ? '?for=change' : (R.redesign ? '?for=redesign' : '')), { credentials: 'same-origin', headers: { accept: 'application/json' } })
      .then(_impAnswer)
      .then(function (x) {
        /* cancelled while the ticket came: nothing is sent, nothing spent */
        if (_imp.rd !== R) throw new Error('cancelled');
        if (!x.j || !x.j.ok || !x.j.ticket || !x.j.url) {
          /* what the page needs to say it in words (v119): sign in, or the
             free reading is used and the pack is short */
          if (x.j && x.j.error) { R.code = String(x.j.error); R.loginUrl = x.j.loginUrl || ''; R.buyUrl = x.j.buyUrl || ''; R.need = x.j.need; R.balance = x.j.balance; }
          throw new Error('no ticket (HTTP ' + x.s + ') ' + (x.j ? ((x.j.error || '') + (x.j.message ? (' -- ' + x.j.message) : '')) : String(x.t || '').slice(0, 120)));
        }
        R.quota = x.j.quota || null;
        if (x.j.models && x.j.models[R.model]) R.model = x.j.models[R.model];
        R.st = 'reading'; R.t1 = Date.now();
        _impRefresh();
        var bd0 = bodyOf(x.j.ticket);
        /* v153: what went to the worker, for the log and RAW -- the ticket
           left out, a picture only by its size */
        R.sent = _impSentCopy(bd0);
        var opt = { method: 'POST', headers: { 'content-type': 'text/plain' }, body: JSON.stringify(bd0) };
        try { if (global.AbortController) { var ac = new global.AbortController(); opt.signal = ac.signal; global.setTimeout(function () { try { ac.abort(); } catch (eA) {} }, IMP_WAIT_MS); } } catch (eC) {}
        var tkt = x.j.ticket, url = x.j.url;
        return global.fetch(url, opt).then(_impAnswer, function (eF) { return _impAgain(R, url, tkt, eF); });
      })
      .then(function (x) {
        if (_imp.rd !== R) return;
        if (!x.j) throw new Error('the worker answered HTTP ' + x.s + ' with no JSON: ' + String(x.t || '').slice(0, 120));
        if (!x.j.ok && x.j.error) { R.code = String(x.j.error); R.loginUrl = x.j.loginUrl || ''; R.buyUrl = x.j.buyUrl || ''; R.need = x.j.need; R.balance = x.j.balance; }
        if (!x.j.ok) throw new Error('HTTP ' + x.s + ' ' + (x.j.error || '') + (x.j.message ? (' -- ' + x.j.message) : '') + (x.j.stop ? (' (stop ' + x.j.stop + ')') : '') + (x.j.model ? (' [' + x.j.model + ']') : ''));
        R.res = x.j; R.t2 = Date.now();
        /* v152: the worker's record of this reading (w2.06) -- the end of the
           story goes to it: fitted, refused, built */
        R.rec = String(x.j.rec || '');
        /* A CHANGE (v123): operations, a question or a new design -- no plan */
        if (R.change) {
          R.chg = x.j.change || { ops: [], question: '', summary: '' };
          R.st = 'done';
          var solo = _impChgSolo(R.chg);
          if (R.chg.question || R.chg.ops.length) _impChgHist(R.text);
          _imp.applied = false; _imp.restored = false; _imp.undone = false; _imp.applyLines = null;
          /* THE WORDS GO STRAIGHT ON TO THE DESIGNER (v124): a new layout or a
             new house is designed, not changed -- and without a 'done' in
             between, so the bar never shows a failure for a moment */
          if (solo) {
            var started = false, preL = [], soloOp = null, carry = [], q9;
            /* v153: the roof and the terrace asked for in the same words go
               beside the redesign (worker w2.08) -- the designer draws the
               rooms only, so they are written once the new plan is */
            for (q9 = 0; q9 < R.chg.ops.length; q9++) {
              if (!soloOp && (R.chg.ops[q9].op === 'redesign' || R.chg.ops[q9].op === 'new_design')) soloOp = R.chg.ops[q9];
              else if (IMP_CHG_OUTSIDE.indexOf(R.chg.ops[q9].op) >= 0) carry.push(R.chg.ops[q9]);
            }
            /* v153: and what the change was goes on at the head of the new
               reading's lines, so COPY tells the whole road */
            try { preL = _impReadLines(); } catch (ePr) { preL = []; }
            if (solo === 'redesign') {
              var brief = String((soloOp && soloOp.name) || '').trim() || R.text;
              var saidR = 'PLAN change: the designer read "' + R.text + '" as a new layout -- redesigning from the plan as it stands: "' + brief + '"' +
                          (carry.length ? (' -- and ' + carry.map(_impChgOpTxt).join(', ') + ' once it is written (v153)') : '');
              _impSay(saidR); preL.push(saidR);
              /* v159 (Marek: chtelo by to i pri upravach hledat pudorysy, ktere
                 by mohly odpovidat pozadavkum na zmenu): the library first, with
                 the plan as it stands -- a plan there that is this plan with the
                 change made is taken as any plan from the library, no token;
                 none, and the designer redraws as before. The same words again
                 redesign. */
              started = _impMatchThenDescribe(brief, { product: R.prod, base: R.proj, redesignOf: R.text, pre: preL, after: carry, afterText: R.text,
                                                       matchKey: 'change: ' + R.text, matchBase: true });
            } else {
              var saidN = 'PLAN change: the designer read "' + R.text + '" as a new house -- designing it from the words' +
                          (carry.length ? (' -- and ' + carry.map(_impChgOpTxt).join(', ') + ' once it is written (v153)') : '');
              _impSay(saidN); preL.push(saidN);
              /* v159: a new house from the words -- the library first, as for words on an empty project */
              started = _impMatchThenDescribe(R.text, { product: R.prod, pre: preL, after: carry, afterText: R.text, matchKey: 'new: ' + R.text });
            }
            if (!started) _impReadFail(R, 'the designer could not be asked again');
            return;
          }
          _impRefresh();
          return;
        }
        _impTake(R, x.j);
      })
      ['catch'](function (e) {
        var msg = (e && e.name === 'AbortError') ? ('no answer in ' + Math.round(IMP_WAIT_MS / 1000) + ' s -- stopped waiting') : ((e && e.message) || String(e));
        _impReadFail(R, msg);
      });
  }
  /* THE LINE DROPPED, THE READING DID NOT (v186, worker w2.26; plan
     1000076007 on RANCH, Marek: neproslo to). The tablet left the page
     while the plan was read -- another app, the screen off -- and the line
     to the worker died: "Failed to fetch" after 94 s, the reading lost. The
     worker now finishes the reading and keeps its answer under the ticket;
     the page asks for it again with the same ticket every few seconds until
     it is there -- the same answer, nothing read or spent twice. A stop by
     the page's own clock (AbortError) is not asked again. */
  var IMP_AGAIN_MS = 4000;
  function _impLostTxt(R) {
    var L0 = R && R.lost;
    if (!L0) return '';
    return ' | the line dropped after ' + L0.at + ' s (' + L0.why + '), the kept reading asked for ' + L0.asked + ' time(s), ' + L0.pending + ' still being read' +
           (L0.back != null ? (', back at ' + L0.back + ' s') : '') + ' (v186)';
  }
  function _impAgain(R, url, ticket, e0) {
    if (!e0 || e0.name === 'AbortError' || _imp.rd !== R) throw e0;
    var msg0 = (e0 && e0.message) || String(e0), lost = Date.now();
    R.lost = { at: Math.round((lost - (R.t1 || R.t0)) / 1000), why: msg0, asked: 0, pending: 0 };
    _impSay('PLAN: the line to the reader dropped after ' + R.lost.at + ' s (' + msg0 + ') -- asking the worker for the reading it keeps (v186)');
    _impRefresh();
    return new Promise(function (ok, no) {
      function tick() {
        if (_imp.rd !== R) { no(new Error('cancelled')); return; }
        if (Date.now() - (R.t1 || R.t0) > IMP_WAIT_MS) { no(new Error(msg0 + ' -- the line dropped after ' + R.lost.at + ' s and the kept reading did not come in ' + Math.round(IMP_WAIT_MS / 1000) + ' s (v186)')); return; }
        R.lost.asked++;
        global.fetch(url, { method: 'POST', headers: { 'content-type': 'text/plain' }, body: JSON.stringify({ ticket: ticket, again: true }) })
          .then(_impAnswer)
          .then(function (x) {
            if (_imp.rd !== R) { no(new Error('cancelled')); return; }
            if (x.j && x.j.pending) { R.lost.pending++; global.setTimeout(tick, IMP_AGAIN_MS); return; }
            if (x.j && x.j.error === 'not_kept') { no(new Error(msg0 + ' -- the line dropped after ' + R.lost.at + ' s and the worker kept no reading for it (v186)')); return; }
            R.lost.back = Math.round((Date.now() - (R.t1 || R.t0)) / 1000);
            _impSay('PLAN: the kept reading came back at ' + R.lost.back + ' s, asked ' + R.lost.asked + ' time(s) (v186)');
            ok(x);
          }, function () { global.setTimeout(tick, IMP_AGAIN_MS); });
      }
      global.setTimeout(tick, 1500);
    });
  }
  /* THE READING TAKEN IN (v152: one road for an answer that came now and
     for one read on the homepage and handed over) */
  function _impTake(R, j) {
    /* a designed plan (v120) says what sheet it was drawn on */
    R.sheet = j.sheet || (j.plan && j.plan.sheet) || null;
    /* v136: what kind of picture the reader saw (worker w1.98) */
    R.style = String(j.style || (j.plan && j.plan.style) || '');
    if (R.style) _impSay('PLAN the reader calls the picture: ' + R.style + (R.style === 'render' || R.style === 'photo' ? ' -- a black and white line drawing reads best' : ''));
    /* v124: the suggestions for the bubble over the plan icon */
    R.ideas = _impIdeasClean(j.ideas || (j.plan && j.plan.ideas));
    R.conv = _impFromModel(j.plan);
    _impRenorm(R);
    R.st = 'done';
    /* a new plan: APPLY again, and what came before stays restorable */
    _imp.applied = false; _imp.restored = false; _imp.undone = false; _imp.applyLines = null;
    /* v181: kept on this device before anything is built from it */
    _impKeepLast();
    _impRefresh();
  }
  /* THE LAST READING SURVIVES A RELOAD (v181, Marek: nahral jsem projekt,
     pak obrazovka zamrzla, nemuzu poslat log ani raw). The reader's answer is
     kept on this device the moment it comes, before anything is built from
     it; when the page hung and was reloaded, SEND RAW carries the kept one,
     marked as such, so the plan can be replayed on the bench. One reading,
     the last; nothing is sent anywhere by this. */
  var IMP_LAST_KEY = 'leva:plan:lastRead';
  function _impKeepLast() {
    try {
      var t = _impRawJson();
      if (!t || t.length > 400000 || !global.localStorage) return;
      global.localStorage.setItem(IMP_LAST_KEY, JSON.stringify({ at: Date.now(), page: String((global.location && global.location.pathname) || ''), raw: t }));
    } catch (eK) {}
  }
  function _impLastKept() {
    try {
      var s0 = global.localStorage ? global.localStorage.getItem(IMP_LAST_KEY) : null;
      if (!s0) return '';
      var o = JSON.parse(s0);
      if (!o || !o.raw) return '';
      return '(the last reading kept on this device ' + new Date(o.at || 0).toISOString().slice(0, 16).replace('T', ' ') + ' UTC on ' + (o.page || '?') + ' -- from before a reload, v181)\n' + o.raw;
    } catch (eL) { return ''; }
  }
  /* ---------- THE PLAN READ ON THE HOMEPAGE (v152) -------------------------
     Marek: po nacteni pudorysu agent vyhodnoti co je to za projekt a rovnou
     otevre relevantni projekt uz s nahranym pudorysem. The homepage banner
     reads the picture with the worker (w2.06), the worker keeps the reading
     for seven days under its own id, and the product opens with ?read=id.
     The page asks for it (the buyer's own only, signed in), fetches the
     picture kept with it, and from there it is the road of a picture read
     here: straightened, fitted, violet, APPLY -- nothing read and nothing
     charged a second time. The walls the homepage traced on the clean
     picture come with the reading when the worker has them (keep); without
     them they are traced again on the picture as kept, at its own size. */
  var IMP_HAND = '/apps/leva-ai/plan-hand';
  function _impHandIdOk(id) { return /^[a-f0-9]{16,40}$/.test(String(id || '')); }
  /* a picture kept with the account, as a blob (null when it does not come) */
  function _impPicBlob(id) {
    return global.fetch(IMP_PIC_TICKET, { credentials: 'same-origin', headers: { accept: 'application/json' } })
      .then(_impAnswer)
      .then(function (x) {
        if (!x.j || !x.j.ok || !x.j.ticket || !x.j.url) throw new Error('no picture ticket (HTTP ' + x.s + ')' + (x.j && x.j.error ? (' ' + x.j.error) : ''));
        return global.fetch(x.j.url, { method: 'POST', headers: { 'content-type': 'text/plain' }, body: JSON.stringify({ ticket: x.j.ticket, id: id }) });
      })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.blob(); })
      ['catch'](function (e) { _impSay('PLAN hand-off: the picture ' + id + ' did not come -- ' + ((e && e.message) || e) + '; the plan goes on without it'); return null; });
  }
  /* A READING HANDED ON FROM A PRODUCT PAGE (v171): a plan too big for the
     ADU opens in RANCH, which builds it -- the answer as it came, with the
     walls traced on its picture, kept on this device for two hours under a
     new id; the other page takes it with ?read=id as one read on the
     homepage, its picture from the account. */
  var IMP_HAND_LOCAL = 'leva_plan_hand_local_v1', IMP_HAND_LOCAL_MS = 2 * 3600 * 1000;
  function _impHandLocalPut(to) {
    var R = _imp.rd, id = '', q;
    if (!R || !R.res || R.st !== 'done' || R.change || R.text || R.lib) return '';
    for (q = 0; q < 24; q++) id += '0123456789abcdef'.charAt(Math.floor(Math.random() * 16));
    var ans = null;
    try { ans = JSON.parse(JSON.stringify(R.res)); } catch (eJ) { return ''; }
    if (R.img) ans.keep = { walls: R.img.walls || null, fp: R.img.fp || null, w0: R.img.w0 || 0, h0: R.img.h0 || 0 };
    ans.handFrom = String(_impProdName() || '');
    try { global.localStorage.setItem(IMP_HAND_LOCAL, JSON.stringify({ id: id, at: Date.now(), to: String(to || ''), ans: ans })); }
    catch (eS) { _impSay('PLAN hand-off (v171): the reading could not be kept on this device (' + ((eS && eS.message) || eS) + ')'); return ''; }
    _impSay('PLAN hand-off (v171): the reading kept on this device as ' + id + ' for ' + (to || 'another product') + ' -- nothing is read or charged again there');
    return id;
  }
  /* v175: the reading into the queue of the product that builds it as drawn */
  function _impQueueFor(to, place) {
    var R = _imp.rd;
    if (!R || !R.res || R.change || R.text || R.lib || typeof global.fetch !== 'function') return global.Promise.resolve({ ok: false, why: 'no reading' });
    var ans = null;
    try { ans = JSON.parse(JSON.stringify(R.res)); } catch (eJ) { return global.Promise.resolve({ ok: false, why: 'copy' }); }
    if (R.img) ans.keep = { walls: R.img.walls || null, fp: R.img.fp || null, w0: R.img.w0 || 0, h0: R.img.h0 || 0 };
    ans.handFrom = String(_impProdName() || '');
    var n = R.n || {}, body = { action: 'queue', to: String(to || ''), ans: ans, place: place || null, from: ans.handFrom, name: String(R.name || ''),
                                size: (n.w > 0 && n.d > 0) ? (_impN(n.w) + ' x ' + _impN(n.d) + ' m') : '' };
    return global.fetch(IMP_HAND, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json', accept: 'application/json' }, body: JSON.stringify(body) })
      .then(_impAnswer)
      .then(function (x) {
        var ok = !!(x.j && x.j.ok);
        _impSay('PLAN publish (v175): ' + (ok ? ('the plan as drawn handed to ' + to + ' -- its REF builds, checks and publishes it (queue ' + x.j.n + ', reading ' + x.j.hand + ')')
                                             : ('handing the plan to ' + to + ' FAILED (HTTP ' + x.s + (x.j && x.j.error ? (' ' + x.j.error) : '') + ')')) +
                (place && place.city ? (' | the pin ' + place.city) : ''));
        return ok ? { ok: true, hand: x.j.hand, n: x.j.n } : { ok: false, why: 'HTTP ' + x.s };
      })['catch'](function (e) { _impSay('PLAN publish (v175): handing the plan to ' + to + ' FAILED -- ' + ((e && e.message) || e)); return { ok: false, why: String((e && e.message) || e) }; });
  }
  function _impHandLocalGet(id) {
    try {
      var o = JSON.parse(global.localStorage.getItem(IMP_HAND_LOCAL) || 'null');
      if (o && o.id === id && o.ans && (Date.now() - (o.at || 0)) < IMP_HAND_LOCAL_MS) return o.ans;
    } catch (e) {}
    return null;
  }
  function _impHandLoad(id) {
    if (typeof global.fetch !== 'function') return false;
    var LH = _impHandLocalGet(id);
    var R = _imp.rd = { st: 'ticket', name: (LH ? ('plan from ' + (LH.handFrom || 'another product') + ' ') : 'homepage plan ') + id.slice(0, 6), hand: id, model: '', t0: Date.now() };
    _imp.prev = null;
    _impRefresh();
    _impSay(LH ? ('PLAN hand-off (v171): the plan read on ' + (LH.handFrom || 'another product') + ', kept on this device (' + id + ')')
               : ('PLAN hand-off (v152): asking for the plan read on the homepage (' + id + ')'));
    var pic = null;
    (LH ? global.Promise.resolve({ s: 200, j: LH, t: '' })
        : global.fetch(IMP_HAND + '?id=' + encodeURIComponent(id), { credentials: 'same-origin', headers: { accept: 'application/json' } }).then(_impAnswer))
      .then(function (x) {
        if (_imp.rd !== R) throw new Error('cancelled');
        if (!x.j || !x.j.ok || !x.j.plan) {
          if (x.j && x.j.error) { R.code = String(x.j.error); R.loginUrl = x.j.loginUrl || ''; }
          throw new Error('the plan read on the homepage did not come (HTTP ' + x.s + (x.j && x.j.error ? (' ' + x.j.error) : '') + ')');
        }
        R.res = x.j; R.rec = String(x.j.rec || ''); R.model = String(x.j.model || ''); R.t1 = Date.now(); R.st = 'reading';
        _impRefresh();
        pic = x.j.pic || null;
        return (pic && pic.id) ? _impPicBlob(pic.id) : null;
      })
      .then(function (blob) {
        if (_imp.rd !== R || !R.res) return;
        R.t2 = Date.now();
        var K = R.res.keep || null;
        if (!blob) { _impSay('PLAN hand-off: plan ' + id + ' taken without its picture -- nothing to lay under it'); _impTake(R, R.res); return; }
        R.st = 'shrinking';
        _impShrink(blob, function (err, img) {
          if (_imp.rd !== R) return;
          if (err) { _impSay('PLAN hand-off: the picture could not be drawn (' + err + ') -- the plan goes on without it'); _impTake(R, R.res); return; }
          var how = 'the walls traced again on the picture as kept';
          if (K && K.walls && K.walls.segs) { img.walls = K.walls; how = 'the walls traced on the homepage'; }
          if (K && K.fp && K.fp.d) img.fp = K.fp;
          if (K && K.w0 > 0) { img.w0 = K.w0; img.h0 = K.h0 || 0; }
          if (pic && pic.w && (pic.w !== img.w || pic.h !== img.h)) _impSay('PLAN hand-off: the picture came at ' + img.w + 'x' + img.h + ' px, the reading was made at ' + pic.w + 'x' + pic.h);
          R.img = img;
          _impSay('PLAN hand-off: plan ' + id + ' from the homepage, read by ' + (R.model || 'the reader') + ', with its picture ' + img.w + 'x' + img.h + ' px (' + Math.round(blob.size / 1024) + ' kB), ' + how +
                  ' -- nothing read or charged again; record ' + (R.rec || 'none'));
          _impTake(R, R.res);
        }, true);
      })
      ['catch'](function (e) { if (_imp.rd === R) _impReadFail(R, (e && e.message) || String(e)); });
    return true;
  }
  /* THE END OF THE STORY, TO THE RECORD (v152): the worker (w2.06) writes
     down every reading; the page adds whether the plan fitted, was refused
     or was built, once each */
  var IMP_NOTE = '/apps/leva-ai/plan-note';
  function _impNote(R, res, why) {
    if (!R || !R.rec || typeof global.fetch !== 'function') return;
    R.noted = R.noted || {};
    if (R.noted[res]) return;
    R.noted[res] = 1;
    var body = { rec: R.rec, res: res, why: String(why || '').slice(0, 400) };
    try {
      global.fetch(IMP_NOTE, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json', accept: 'application/json' }, body: JSON.stringify(body) })
        .then(_impAnswer)
        .then(function (x) { _impSay('PLAN record ' + R.rec + ': ' + res + (why ? (' (' + String(why).slice(0, 80) + ')') : '') + (x.j && x.j.ok ? ' -- noted' : (' -- NOT noted, HTTP ' + x.s))); })
        ['catch'](function () {});
    } catch (e) {}
  }
  /* a plan built smaller than drawn, in words for the record */
  function _impScaledTxt(sc) {
    if (!sc || typeof sc !== 'object') return String(sc || '');
    if (!(sc.w0 > 0 && sc.w > 0)) return JSON.stringify(sc).slice(0, 200);
    return 'drawn ' + _impN(sc.w0) + ' x ' + _impN(sc.l0) + ' m, built ' + _impN(sc.w) + ' x ' + _impN(sc.l) + ' m (the product goes to ' + _impN(sc.maxW) + ' x ' + _impN(sc.maxL) + '), ' + sc.pct + ' % smaller';
  }
  /* ---------- A READY PLAN BEFORE A NEW DESIGN (v154) -------------------
     Marek: pokud uzivatel zada prompt a mame uz ulozeny pudorys, ktery
     zadani odpovida, meli bychom ho hned nabidnout a usetrit tokeny --
     rovnou bych vybral nejvhodnejsi a ten bych zobrazil uz na projektu,
     jakoby uzivatel nahral pudorys; aby to bylo i rychle. The words of a new
     plan go to the library first (worker w2.10): the small model reads them
     against the plans that fit this product and picks the one that has all
     they ask for, the worker checks the pick. A plan that holds is taken out
     of the library and written as any plan picked there -- nothing designed,
     no token. None, no answer in a few seconds, or a worker before w2.10:
     the designer, as before. The same words sent again right after a plan
     from the library are designed: the buyer wants something else. */
  var IMP_MATCH_WAIT_MS = 6000;
  function _impMatchAsk(a) {
    if (!a) return 'not said';
    var o = [];
    if (a.beds) o.push(a.beds + ' bedroom(s)');
    if (a.baths) o.push(a.baths + ' bathroom(s)');
    if (a.area_m2) o.push(_impN(a.area_m2) + ' m2');
    if (a.w_m && a.d_m) o.push(_impN(a.w_m) + ' x ' + _impN(a.d_m) + ' m');
    return o.length ? o.join(', ') : 'nothing to compare';
  }
  function _impMatchTxt(M, found) {
    var p = M.pick || {}, cost = (M.cost != null) ? (', ' + Number(M.cost).toFixed(4) + ' USD') : '';
    /* v155: the roof asked for, set once the plan is written */
    var rf = M.roof ? (' | the roof asked for: ' + M.roof.kind + (M.roof.pitch ? (' ' + M.roof.pitch + ' deg') : '') + ', set once the plan is written (v155)') : '';
    if (M.cover) rf += ' | a roof over the terrace asked for, set once the plan is written (v156)';
    if (M.open) rf += ' | an open terrace asked for -- the roof kept off it once the plan is written (v157)';
    /* v159: a look-up for a change -- the plan now, as the worker read it */
    var nw = M.now ? (' | on the change "' + String(M.chg || '').slice(0, 80) + '" -- the plan now ' + (M.now.beds | 0) + ' bd, ' + Number(M.now.baths || 0) + ' ba, ' + _impN(M.now.floor || 0) + ' m2' +
                      (M.now.out ? (', ' + M.now.out + ' plan(s) of its own layout left out') : '') + ' (v159)') : (M.chg ? (' | on the change "' + String(M.chg).slice(0, 80) + '" (v159)') : '');
    rf = nw + rf;
    if (found) return (M.chg ? 'PLAN change' : 'PLAN from words') + ': a ready plan in the library matches -- ' + p.id + ' (' + (p.beds | 0) + ' bd, ' + Number(p.baths || 0) + ' ba, ' + _impN(p.floor || p.area || 0) + ' m2' +
                      (p.leva ? ', LEVA' : '') + (p.hits ? (', taken ' + p.hits + 'x') : '') + ') picked among ' + (M.n || 0) + ' plan(s) in ' + ((M.ms || 0) / 1000).toFixed(1) + ' s' + cost +
                      ' -- "' + (M.why || '') + '" | asked for: ' + _impMatchAsk(M.ask) + rf + (M.rec ? (' | record ' + M.rec) : '') + ' -- nothing designed, no token; the same words again ' + (M.chg ? 'redesign the plan instead (v159)' : 'design a new plan (v154)');
    return (M.chg ? 'PLAN change' : 'PLAN from words') + ': the plan library first -- nothing that holds' + (M.why ? (' ("' + M.why + '")') : '') + ' among ' + (M.n || 0) + ' plan(s) in ' + ((M.ms || 0) / 1000).toFixed(1) + ' s' + cost +
           ' | asked for: ' + _impMatchAsk(M.ask) + rf + (/HTTP 400/.test(M.why || '') ? ' | the worker does not know the look-up yet (before w2.10)' : '') +
           (M.chg && !M.now && M.base ? ' | the worker did not read the plan now (before w2.16)' : '') + (M.chg ? ' -- redesigned (v159)' : ' -- designed (v154)');
  }
  /* THE PLAN NOW, FOR A LOOK-UP ON A CHANGE (v159, worker w2.16): the
     drawer's picture of the plan as built -- the same the library keeps, so
     the worker reads its bedrooms, bathrooms, floor and rooms the way it
     reads the library's -- and the ids it already has there: the plan it was
     taken out of, the plan it was saved as. */
  function _impMatchBase() {
    var T = null, ids = [];
    try { T = _impLibThumb(); } catch (eT) { T = null; }
    if (!T) return null;
    if (_imp.libNow && _impLibIdOk(_imp.libNow)) ids.push(_imp.libNow);
    if (_imp.libSaved && _imp.libSaved.id && _impLibIdOk(_imp.libSaved.id) && ids.indexOf(_imp.libSaved.id) < 0) ids.push(_imp.libSaved.id);
    return { thumb: T, ids: ids };
  }
  function _impMatchThenDescribe(text, opts) {
    opts = opts || {};
    var t = String(text == null ? '' : text).replace(/\s+/g, ' ').trim();
    if (!t) return false;
    if (t.length > IMP_TEXT_MAX) t = t.slice(0, IMP_TEXT_MAX);
    if (typeof global.fetch !== 'function') return _impDescribe(t, opts);
    /* v159: a change is known by the buyer's own words, not by the brief the
       change agent writes from them -- that one is worded anew every time */
    var key = String(opts.matchKey || t);
    if (_imp.lastMatch && _imp.lastMatch.text === key) {
      _impSay((opts.redesignOf ? 'PLAN change' : 'PLAN from words') + ': the same words again after plan ' + _imp.lastMatch.id + ' came from the library -- ' + (opts.redesignOf ? 'redesigned' : 'designed') + ' this time (v154)');
      var aL = _imp.lastMatch.after || [];
      _imp.lastMatch = null;
      return _impDescribe(t, aL.length ? Object.assign({}, opts, { after: aL, afterText: opts.afterText || t }) : opts);
    }
    if (_imp.user) _impUserClock();
    var C = _impCore(), S = C ? C.STATE : null, lim = _impLimits(), prod = String(opts.product || _impProdName() || '').slice(0, 30);
    var R = _imp.rd = { st: 'ticket', matching: true, name: t.length > 48 ? (t.slice(0, 48) + '...') : t, text: t, model: 'library', t0: Date.now(),
                        units: S ? (S.isMetric === false ? 'imperial' : 'metric') : '', lim: lim, prod: prod,
                        /* v159: the change this look-up is for, on top of its lines */
                        pre: Array.isArray(opts.pre) ? opts.pre : null, chgOf: String(opts.redesignOf || '') };
    /* v159: the plan as it stands, for a change -- its picture, and the
       library ids it already is */
    var base = opts.matchBase ? _impMatchBase() : null;
    _imp.prev = null;
    _impRefresh();
    var done = false, tmo = 0;
    function go(j, err) {
      if (done) return;
      done = true;
      if (tmo) { try { global.clearTimeout(tmo); } catch (eC) {} tmo = 0; }
      if (_imp.rd !== R) return;
      var M = { text: t, ms: Date.now() - R.t0, why: (j && j.why) || err || '', ask: (j && j.ask) || null, n: (j && j.n) || 0, cost: j ? j.cost : null, rec: (j && j.rec) || '',
                worker: (j && j.version) || '', pick: (j && j.pick) || null, roof: (j && j.roof && j.roof.kind) ? j.roof : null,
                /* v159 */ chg: opts.redesignOf || '', base: !!base, now: (j && j.now) || null };
      /* v155 (worker w2.11): the roof the words ask for, set once the plan is
         written -- the designer draws rooms, a library plan has none */
      var roofOps = M.roof ? [{ op: 'roof_type', ref: '', wall: '', at: 0, w: 0, d: 0, pitch: Number(M.roof.pitch) || 0, kind: String(M.roof.kind), ax: 0, ay: 0, bx: 0, by: 0, name: '' }] : [];
      /* v156 (worker w2.12): a roof over the terrace -- its wall and its depth
         are known once the plan is written, the op says only 'the terrace' */
      M.cover = !!(j && j.cover);
      if (M.cover) roofOps.push({ op: 'roof_overhang', ref: 'terrace', wall: '', at: 0, w: 0, d: 0, pitch: 0, kind: '', ax: 0, ay: 0, bx: 0, by: 0, name: '' });
      /* v157 (worker w2.13): a terrace the words want open -- the roof back to its eave over it */
      M.open = !M.cover && !!(j && j.open);
      if (M.open) roofOps.push({ op: 'roof_overhang', ref: 'terrace', wall: '', at: 0, w: 0, d: 0, pitch: 0, kind: 'open', ax: 0, ay: 0, bx: 0, by: 0, name: '' });
      /* v159: what the change asked for beside the new layout (the roof, the
         terrace) goes with whichever plan comes, before what the look-up read */
      var allAfter = (opts.after || []).concat(roofOps);
      if (j && j.ok && j.pick && _impLibIdOk(j.pick.id)) {
        _imp.lastMatch = { text: key, id: j.pick.id, at: Date.now(), after: allAfter };
        _impSay(_impMatchTxt(M, true));
        _impLibLoad(String(j.pick.id), { matched: M, after: allAfter, afterText: opts.afterText || t, pre: opts.pre });
        return;
      }
      _impSay(_impMatchTxt(M, false));
      _impDescribe(t, Object.assign({}, opts, { matchNote: M, after: allAfter, afterText: opts.afterText || t }));
    }
    try { tmo = global.setTimeout(function () { tmo = 0; go(null, 'no answer in ' + Math.round(IMP_MATCH_WAIT_MS / 1000) + ' s'); }, IMP_MATCH_WAIT_MS); } catch (eT) { tmo = 0; }
    var mb = { action: 'match', text: t, limits: lim, rect: _impLibRect(), rules: IMP_LIB_RULES, product: prod, lang: _impLang() };
    if (base) mb.base = base;
    _impLibPost(mb)
      .then(function (x) { go(x.j && x.j.ok ? x.j : null, x.j && x.j.ok ? '' : ('HTTP ' + x.s + (x.j && x.j.error ? (' ' + x.j.error) : ''))); })
      ['catch'](function (e) { go(null, (e && e.message) || String(e)); });
    return true;
  }
  /* ---------- A PLAN FROM WORDS (v120) -----------------------------------
     Marek: stejne jako cteni -- pudorys = cteni obrazku. The canvas bar's
     field and its SEND: the description goes to the worker on the same
     ticket, the same quota and the same charge as a picture, with what the
     worker needs to design for THIS configurator -- the units it works in,
     the product, and the largest outline it builds (the roof's span limit
     counted, as in the fit). What comes back is drawn on a sheet of 50 px to
     the metre with its two overall dimensions, so from there on it is the
     picture's road exactly: read, straightened, fitted, violet, APPLY. There
     is no picture -- nothing to lay under the plan and no wall to trace. */
  var IMP_TEXT_MAX = 1500;
  /* the largest outline this product builds, the roof's span counted (v120;
     v136 shared with the library's list) */
  function _impLimits() {
    var C = _impCore(), S = C ? C.STATE : null, LIM = (C && C.LIMITS) || null;
    if (!LIM || !(LIM.maxW > 0) || !(LIM.maxL > 0)) return null;
    var mw = LIM.maxW, ml = LIM.maxL;
    if (S && S.maxSpan > 0) { if (S.roof && S.roof.axis === 'l') mw = Math.min(mw, S.maxSpan); else ml = Math.min(ml, S.maxSpan); }
    /* v166: and the smallest (mw x md), so the library leaves out the plans
       this product would refuse (RANCH listed ADU-sized ones; worker w2.18) */
    /* v170: a plan is its outer faces -- where the wall reaches past the
       outline, so do the sizes the designer and the library are given */
    var oL = _impOut() * 2;
    var out = { w: Math.round((mw + oL) * 100) / 100, d: Math.round((ml + oL) * 100) / 100 };
    if (LIM.minW > 0 && LIM.minL > 0) { out.mw = Math.round((LIM.minW + oL) * 100) / 100; out.md = Math.round((LIM.minL + oL) * 100) / 100; }
    /* v172 (Marek: v nabidce floorplans RANCH jsou i pudorysy z ADU -- prunik
       vice omezit, jsou tam zbytecne male): the smallest floor a product
       wants offered from the library, its own LIMITS.libMinA in m2 */
    if (LIM.libMinA > 0) out.ma = Math.round(Number(LIM.libMinA) * 10) / 10;
    return out;
  }
  function _impDescribe(text, opts) {
    opts = opts || {};
    var t = String(text == null ? '' : text).replace(/\s+/g, ' ').trim();
    if (!t) return false;
    if (t.length > IMP_TEXT_MAX) t = t.slice(0, IMP_TEXT_MAX);
    if (_imp.user) _impUserClock();
    var C = _impCore(), S = C ? C.STATE : null, lim = _impLimits();
    var units = S ? (S.isMetric === false ? 'imperial' : 'metric') : '';
    var prod = String(opts.product || _impProdName() || '').slice(0, 30);
    /* v124: a redesign carries the plan as it stands (the change's own
       description of it) and the words of the change it came from */
    var base = (opts.base && typeof opts.base === 'object') ? opts.base : null;
    var R = _imp.rd = { st: 'ticket', name: t.length > 48 ? (t.slice(0, 48) + '...') : t, text: t, model: _impModel(), t0: Date.now(),
                        units: units, lim: lim, prod: prod, redesign: !!base, redesignOf: base ? String(opts.redesignOf || '') : '',
                        /* v153: the change it came from, and the roof and
                           terrace to write once the plan is */
                        pre: Array.isArray(opts.pre) ? opts.pre : null, after: Array.isArray(opts.after) ? opts.after : [], afterText: String(opts.afterText || ''),
                        /* v154: what the library said before the design */
                        matchNote: opts.matchNote || null };
    _imp.prev = null;
    _impAsk(R, function (ticket) {
      var b = { ticket: ticket, text: t, units: units, product: prod, limits: lim, model: _impModel(), lang: _impLang(), offer: _impOffer() };
      if (base) b.base = base;
      return b;
    });
    return true;
  }
  /* ---------- THE SAMPLE FOR ?planTest=2 ---------------------------------
     A rectangle 10.0 x 7.0 m at 40 px per metre, lying 0.8 deg crooked:
     two partitions wall to wall and one between them, three doors inside
     (one 0.72 that becomes 0.70), an entrance 1.00 wide and a 2.00 sliding
     door on the garden side, five windows (one 1.00 the catalogue does not
     have, one high in the bathroom). Ten metres is past the ADU's eight
     across, so it has to be turned. */
  function _impSampleRect() {
    var ang = -0.8 * Math.PI / 180, cA = Math.cos(ang), sA = Math.sin(ang), PX = 40, ox = 60, oy = 50, k = 0;
    var jit = [0.015, -0.01, 0.02, -0.015, 0.01, 0, -0.02, 0.01];
    function p(x, z, exact) {
      var j = exact ? 0 : jit[(k++) % jit.length];
      x += j; z -= j * 0.5;
      return [Math.round((x * cA - z * sA) * PX + ox), Math.round((x * sA + z * cA) * PX + oy)];
    }
    return {
      units: 'px',
      outline: [p(0, 0), p(10, 0), p(10, 7), p(0, 7)],
      dims: [{ a: p(0, -0.6, 1), b: p(10, -0.6, 1), m: 10.0 }, { a: p(-0.6, 0, 1), b: p(-0.6, 7, 1), m: 7.0 }],
      partitions: [{ a: p(4.0, 0.1), b: p(4.02, 6.9) }, { a: p(7.0, 0.05), b: p(6.98, 6.95) }, { a: p(4.0, 4.0), b: p(7.0, 4.02) }],
      doors: [{ at: p(4.0, 2.0), w: 0.9 * PX }, { at: p(5.5, 4.0), w: 0.72 * PX }, { at: p(7.0, 2.0), w: 0.8 * PX },
              { at: p(2.0, 7.0), w: 1.0 * PX }, { at: p(8.5, 7.0), w: 2.0 * PX }],
      windows: [{ at: p(2.0, 0), w: 1.2 * PX }, { at: p(8.5, 0), w: 1.5 * PX }, { at: p(0, 3.5), w: 1.0 * PX },
                { at: p(10, 4.5), w: 1.2 * PX }, { at: p(5.5, 7.0), w: 0.6 * PX, sill: 1.5 }],
      rooms: [{ at: p(2, 3.5), label: 'Living' }, { at: p(5.5, 2.0), label: 'Kitchen' },
              { at: p(5.5, 5.5), label: 'Bathroom' }, { at: p(8.5, 3.5), label: 'Bedroom' }]
    };
  }
  /* ---------- THE IMPORT FOR THE BUYER (v119) ----------------------------
     The reading, the fit, the violet preview, the picture under it and APPLY
     ran only behind ?planTest=3, driven by the test box. The canvas bar gets
     the same road without the box: start() opens the file picker (or
     readFile() takes a file the page already has), every change of the
     reading is handed to the one listener the page gives, as a small state
     -- what stage it is at, the seconds, what went wrong in a word the page
     can translate (sign_in, plan_tokens), whether the violet plan is ready
     -- and apply(), cancel() and restore() do what the test box's APPLY,
     nothing and RESTORE do. The log lines are the same PLAN lines. */
  var _impUserCb = null;
  function _impUserState() {
    var R = _imp.rd, f = _imp.fit, n = R && R.n;
    var s = { st: R ? R.st : (_imp.applied ? 'applied' : 'idle'), name: R ? R.name : '', secs: 0, wait: Math.round(IMP_WAIT_MS / 1000),
              err: R && R.err ? String(R.err) : '', code: R && R.code ? R.code : '', loginUrl: (R && R.loginUrl) || '', buyUrl: (R && R.buyUrl) || '',
              need: R ? R.need : null, balance: R ? R.balance : null, quota: (R && R.quota) || null,
              ready: (R && R.change) ? !!(R.st === 'done' && R.chg && R.chg.ops.length && !R.chg.question && !_imp.applied && !_impChgSolo(R.chg))
                                     : !!(f && f.ok && !_imp.applied && R && R.st === 'done'), applied: !!_imp.applied, restored: !!_imp.restored,
              canRestore: !!(_imp.applied || _impKept()), why: '',
              /* v120: a picture read or a description designed */
              from: R ? (R.change ? 'change' : (R.lib ? 'library' : (R.text ? 'text' : 'picture'))) : '', sheet: (R && R.sheet) || null,
              /* v136: the kind of picture the reader saw, and the plan taken
                 out of the library */
              style: (R && R.style) || '', lib: (R && R.lib) || '', libSaved: _imp.libSaved || null,
              /* v123: a change's question and summary, in the buyer's own words */
              question: (R && R.chg && R.chg.question) || '', summary: (R && R.chg && R.chg.summary) || '',
              /* v122: the project's own plan, whatever this page did */
              planned: _impPlanned(),
              /* v124: a redesign of the plan there was, and the suggestions
                 for the bubble over the plan icon */
              redesign: !!(R && R.redesign), ideas: _impIdeasNow() };
    if (R && R.st === 'reading' && R.t1) s.secs = Math.round((Date.now() - R.t1) / 1000);
    if (R && R.change && R.st === 'done' && R.chg && R.chg.question) s.why = 'a question';
    else if (R && R.change && R.st === 'done' && (!R.chg || !R.chg.ops.length)) s.why = 'no operation';
    else if (R && R.st === 'done' && n && !n.ok) s.why = String(n.why || 'not read');
    else if (R && R.st === 'done' && f && !f.ok) s.why = String(f.why || 'does not fit');
    if (n && n.ok) s.size = { w: n.w, d: n.d, rooms: (n.rooms || []).length, doors: (n.doors || []).length, units: (R.conv && R.conv.units) || '' };
    /* v142: a plan too big for this product says how big it may be */
    if (s.why === 'size') {
      try { s.lim = _impLimits(); } catch (eLm) { s.lim = null; }
      /* v160: and how small, for a plan under it */
      try { var LMc = (_impCore() && _impCore().LIMITS) || null, oMn = 2 * _impOut(); if (s.lim && LMc) { s.lim.minW = (Number(LMc.minW) || 0) + oMn; s.lim.minL = (Number(LMc.minL) || 0) + oMn; } } catch (eMn) {}
      /* v171: a plan up to 35 % over the largest size may be built smaller
         on the buyer's word -- the size it would come out at, either way
         round, the one that gives up less */
      try {
        if (s.lim && n && n.ok && !_imp.squeeze && !(R && R.change)) {
          var SQ9 = null, OR9 = [[n.w, n.d], [n.d, n.w]], q9;
          for (q9 = 0; q9 < 2; q9++) {
            var a9 = OR9[q9][0], b9 = OR9[q9][1], o9 = Math.max(a9 / s.lim.w, b9 / s.lim.d);
            if (o9 <= 1 || o9 > 1 + IMP_SQUEEZE_MAX + 1e-6) continue;
            if ((s.lim.minW > 0 && a9 < s.lim.minW) || (s.lim.minL > 0 && b9 < s.lim.minL)) continue;
            if (!SQ9 || o9 < SQ9.o) SQ9 = { o: o9, w: Math.min(a9, s.lim.w), l: Math.min(b9, s.lim.d), pct: Math.round((o9 - 1) * 1000) / 10 };
          }
          if (SQ9) s.squeeze = { w: _impR(SQ9.w), l: _impR(SQ9.l), pct: SQ9.pct };
        }
      } catch (eSq) {}
    }
    /* v145: a plan built smaller than drawn says so */
    if (f && f.ok && f.scaled) s.scaled = f.scaled;
    if (_imp.applied) s.st = 'applied';
    return s;
  }
  function _impUserTick() {
    var R = _imp.rd, L = _impReadLines();
    if (R && R.st === 'done' && R.n) {
      _imp.n2 = R.n;
      L = L.concat(_impLines(R.n, '"' + R.name + '" | VARIANT ' + (_imp.v2 ? '2 (passages are doors)' : '1 (passages are room lines)')));
      if (!_imp.applied) {
        var f;
        try { f = _impFit(_imp.n2, { units: _impUnits() }); } catch (e) { f = { ok: false, why: 'threw: ' + ((e && e.message) || e), notes: [] }; }
        _imp.fit = f;
        _imp.prev = f.ok ? f : null;
        if (f.ok) _impTrace(R, f);
        /* v152: fitted or refused, to the worker's record of the reading */
        if (R.rec && !R.change) {
          if (!f.ok) _impNote(R, (R.n && !R.n.ok) ? 'not-read' : 'refused', (R.n && !R.n.ok) ? R.n.why : f.why);
          else _impNote(R, f.scaled ? 'fits-scaled' : 'fits', f.scaled ? _impScaledTxt(f.scaled) : '');
        }
        L = L.concat(_impFitLines(f));
        if (f.ok) L.push('PLAN the violet plan is where it will land -- APPLY in the bar writes it into this project (one step; Undo takes it back)');
      } else {
        L = L.concat(_impFitLines(_imp.fit));
      }
    }
    if (_imp.applyLines) L = L.concat(_imp.applyLines);
    if (_imp.applied || _imp.restored || _imp.undone) L.push(_impNowLine());
    _imp.lines = L;
    _impLog(L);
    if (_impUserCb) { try { _impUserCb(_impUserState()); } catch (eCb) {} }
  }
  /* the seconds while it reads: the listener hears them once a second */
  var _impUserTimer = 0;
  function _impUserClock() {
    if (_impUserTimer || typeof global.setInterval !== 'function') return;
    _impUserTimer = global.setInterval(function () {
      var R = _imp.rd;
      if (!_imp.user || !R || (R.st !== 'reading' && R.st !== 'ticket' && R.st !== 'shrinking')) { global.clearInterval(_impUserTimer); _impUserTimer = 0; return; }
      if (_impUserCb) { try { _impUserCb(_impUserState()); } catch (eCb) {} }
    }, 1000);
  }
  function _impUserBegin(cb) {
    _imp.user = true;
    if (cb) _impUserCb = cb;
    /* the buyer reads with the public model; the worker has the last word */
    if (!_imp.model || IMP_MODELS.indexOf(_imp.model) < 0) _imp.model = 'opus';
    _imp.applied = false; _imp.restored = false; _imp.undone = false; _imp.applyLines = null;
    _imp.squeeze = 0; _imp.squeezeLib = false;
    _impUserClock();
  }
  /* ---------- THE PICTURE GOES WITH THE PROJECT (v122) -------------------
     Marek: pokud delam refresh page, ma si pamatovat nahrany pudorys, pokud
     je uzivatel prihlaseny (k uctu na serveru). The worker keeps the picture
     with the account (w1.91) and answers its id. When the plan is written,
     the project keeps that id and where the picture lies -- the middle, the
     width, the turn, the opacity -- in product.planPic, and where the plan
     came from in product.planFrom. Every 1.5 s a watcher looks: a project
     with a planPic and nothing under its plan asks the proxy for a picture
     ticket and fetches the picture straight from the worker, once per id,
     and lays it where it was; a picture moved or faded by hand is written
     back into the project (the next save carries it); a picture this watcher
     laid that the project no longer names (Undo, RESTORE, another project)
     is taken away. Signed out, or somebody else's project: the plan comes,
     the picture does not, and the log says why. */
  var IMP_PIC_TICKET = '/apps/leva-ai/pic-ticket';
  function _impSay(line) {
    try { if (global.Configurator && global.Configurator.log && global.Configurator.log.log) global.Configurator.log.log('[floorplan-lib] ' + line); } catch (e) {}
  }
  function _impPicOnApply(R) {
    var C = _impCore();
    if (!C || !C.STATE) return;
    var PR = C.STATE.product = C.STATE.product || {};
    PR.planFrom = (R && R.lib) ? 'library' : ((R && R.text) ? 'text' : 'picture');
    var pic = R && R.res && R.res.pic;
    if (!pic || !pic.id) {
      /* a new plan without a kept picture: the old one is not its picture */
      if (PR.planPic) { delete PR.planPic; _impSay('PLAN picture: the project no longer names one -- this plan ' + (R && R.lib ? 'came from the plan library' : (R && R.text ? 'was designed from words' : 'came without a kept picture'))); }
      _imp.pic = null;
      try { C.savePref(); } catch (eS) {}
      return;
    }
    if (R.imgEl && TRACE.img === R.imgEl) _impPicKeep(R, 'the plan was written');
    else _impSay('PLAN picture ' + pic.id + ' kept on the server -- it goes into the project once it lies under the plan');
  }
  function _impPicKeep(R, why) {
    var C = _impCore(), pic = R && R.res && R.res.pic;
    if (!C || !C.STATE || !pic || !pic.id || !R.imgEl || TRACE.img !== R.imgEl) return;
    var PR = C.STATE.product = C.STATE.product || {};
    PR.planPic = { id: pic.id, cx: _impR(TRACE.cx), cz: _impR(TRACE.cz), w: _impR(TRACE.w), ar: _impR(TRACE.ar || 0.75),
                   angle: _impR(TRACE.angle || 0), op: _impR(TRACE.opacity), at: Date.now() };
    _imp.pic = { id: pic.id, el: R.imgEl, how: 'read here' };
    try { C.savePref(); } catch (eS) {}
    _impSay('PLAN picture ' + pic.id + ' goes with the project (' + why + '): ' + _impN(TRACE.w) + ' m wide at ' + _impN(TRACE.cx) + ',' + _impN(TRACE.cz) +
            ', ' + Math.round((TRACE.opacity || 0) * 100) + ' % -- a reload lays it back for its owner');
  }
  function _impPicWatch() {
    var C = _impCore();
    if (!C || !C.STATE) return;
    var pp = (C.STATE.product || {}).planPic || null, P = _imp.pic;
    /* a picture laid by this, no longer the project's */
    if (P && P.el && TRACE.img === P.el && (!pp || pp.id !== P.id)) {
      TRACE.img = null; TRACE.armed = false;
      try { OVERLAY.repaint(); } catch (eR) {}
      _impSay('PLAN picture ' + P.id + ' taken away -- the project no longer names it (Undo, RESTORE or another project)');
      _imp.pic = null; P = null;
    }
    if (!pp || !pp.id) return;
    if (P && P.id === pp.id) {
      /* the one on screen: what the hand did to it goes into the project */
      if (P.el && TRACE.img === P.el) {
        var nw = { cx: _impR(TRACE.cx), cz: _impR(TRACE.cz), w: _impR(TRACE.w), angle: _impR(TRACE.angle || 0), op: _impR(TRACE.opacity) }, k, ch = false;
        for (k in nw) if (nw.hasOwnProperty(k) && pp[k] !== nw[k]) { pp[k] = nw[k]; ch = true; }
        if (ch) _imp.picMoved = true;
        /* saved once the hand has let go of it (the share write is debounced) */
        if (_imp.picMoved && !TRACE.armed) {
          _imp.picMoved = false;
          try { C.savePref(); } catch (eS) {}
          _impSay('PLAN picture ' + pp.id + ' moved or faded by hand -- saved with the project: ' + _impN(pp.w) + ' m wide, ' + Math.round((pp.op || 0) * 100) + ' %');
        }
      }
      return;
    }
    /* something else lies under the plan (a reading in progress, a picture
       of the buyer's own): it is not replaced */
    if (TRACE.img) return;
    if (_imp.picTry === pp.id) return;
    _imp.picTry = pp.id;
    _impPicFetch(pp);
  }
  function _impPicFetch(pp) {
    if (typeof global.fetch !== 'function') return;
    _impSay('PLAN picture: asking for the picture kept with the account (' + pp.id + ')');
    global.fetch(IMP_PIC_TICKET, { credentials: 'same-origin', headers: { accept: 'application/json' } })
      .then(_impAnswer)
      .then(function (x) {
        if (!x.j || !x.j.ok || !x.j.ticket || !x.j.url) {
          throw new Error(x.s === 401 ? 'not signed in -- the picture is kept for its owner only' : ('no picture ticket (HTTP ' + x.s + ')' + (x.j && x.j.error ? (' ' + x.j.error) : '')));
        }
        return global.fetch(x.j.url, { method: 'POST', headers: { 'content-type': 'text/plain' }, body: JSON.stringify({ ticket: x.j.ticket, id: pp.id }) });
      })
      .then(function (r) {
        if (!r.ok) {
          return r.text().then(function (t) {
            var j = null;
            try { j = JSON.parse(t); } catch (eJ) { j = null; }
            throw new Error((r.status === 404 ? 'not kept for this account (somebody else\'s project, or older than a year)' : ('HTTP ' + r.status)) + (j && j.error ? (' [' + j.error + ']') : ''));
          });
        }
        return r.blob();
      })
      .then(function (b) {
        var url = global.URL.createObjectURL(b), im = new global.Image();
        im.onload = function () {
          var C = _impCore(), cur = C && C.STATE && C.STATE.product ? C.STATE.product.planPic : null;
          /* the project moved on while it came */
          if (!cur || cur.id !== pp.id || TRACE.img) { _impSay('PLAN picture ' + pp.id + ' came, but the project has moved on -- not laid'); return; }
          TRACE.img = im;
          TRACE.ar = (cur.ar > 0) ? cur.ar : ((im.naturalHeight || 1) / (im.naturalWidth || 1));
          TRACE.cx = cur.cx; TRACE.cz = cur.cz; TRACE.w = cur.w; TRACE.angle = cur.angle || 0;
          TRACE.opacity = (cur.op >= 0) ? cur.op : IMP_TRACE_OPACITY;
          TRACE.armed = false;
          _imp.pic = { id: pp.id, el: im, how: 'laid back' };
          try { OVERLAY.repaint(); } catch (eR) {}
          _impSay('PLAN picture ' + pp.id + ' laid back under the plan: ' + _impN(cur.w) + ' m wide, ' + Math.round(TRACE.opacity * 100) + ' %, ' +
                  Math.round(b.size / 1024) + ' kB');
        };
        im.onerror = function () { _impSay('PLAN picture ' + pp.id + ' NOT laid back: the browser could not open it'); };
        im.src = url;
      })
      ['catch'](function (e) { _impSay('PLAN picture ' + pp.id + ' NOT laid back: ' + ((e && e.message) || e)); });
  }
  try { if (typeof global.setInterval === 'function') global.setInterval(function () { try { _impPicWatch(); } catch (eW) {} }, 1500); } catch (eI) {}
  /* what the page needs to know of the project's plan (v122): whether it has
     one that came by the bar, and its picture */
  function _impPlanned() {
    var C = _impCore(), PR = (C && C.STATE && C.STATE.product) || {};
    return { from: PR.planFrom || '', pic: PR.planPic ? PR.planPic.id : '', picLaid: !!(_imp.pic && _imp.pic.el && TRACE.img === _imp.pic.el) };
  }
  /* ---------- THE PLAN LIBRARY (v136) --------------------------------------
     Marek: pudorysy, ktere agent vygeneruje z textoveho promptu by se mohly
     uz ukladat; tlacitko upload by mohlo otevrit nejdriv drawer s nasimi
     pudorysy; v nabidce draweru nechat jen plany, ktere budou korektne
     postavene. The worker (w1.98) keeps one library for every buyer, and a
     plan written by the bar goes into it when its audit (v135) came out
     clean -- a design from words by the id the worker kept it under, a plan
     read from a picture as it was BUILT here: the walls, the doors with
     their swing, the windows, the rooms, the pieces and the terrace, in
     metres, the way the designer writes a plan -- never the picture, which
     stays with its owner. A plan taken out of the library comes back on the
     road of a design: laid on a sheet by the worker, read, fitted, written
     -- free, with no ticket and no quota, and not saved again. A plan from
     a change of words is not saved: it is the buyer's own tweak. */
  var IMP_LIB = '/apps/leva-ai/plan-lib';
  var IMP_LIB_LABEL = { living: 'LIVING ROOM', dining: 'DINING', kitchen: 'KITCHEN', bedroom: 'BEDROOM', kids: 'KIDS ROOM', bathroom: 'BATHROOM', wc: 'WC',
                        laundry: 'LAUNDRY', utility: 'UTILITY', pantry: 'PANTRY', hallway: 'HALL', entry: 'ENTRY', mudroom: 'MUDROOM', office: 'OFFICE',
                        storage: 'STORAGE', closet: 'CLOSET', wardrobe: 'WALK-IN CLOSET', loft: 'LOFT', garage: 'GARAGE', sauna: 'SAUNA', workshop: 'WORKSHOP' };
  /* the pieces back to the reader's words; the label keeps what the size
     alone would not tell (a queen bed, a five foot tub) */
  var IMP_LIB_FIX = [[/^bath/, 'bathtub'], [/^shower/, 'shower'], [/^wc$|toilet/, 'toilet'], [/^basin|vanity/, 'basin'], [/^kitchenL/, 'kitchen_l'],
                     [/^kitchenU/, 'kitchen_u'], [/^kitchen/, 'kitchen'], [/^island|^barTable/, 'island'], [/^fridge/, 'fridge'], [/^washer/, 'washer'],
                     [/^dryer/, 'dryer'], [/heater|boiler/, 'water_heater'], [/^bed/, 'bed'], [/^sofa/, 'sofa'], [/^table|^coffee/, 'table'],
                     [/^desk/, 'desk'], [/^wardrobe/, 'wardrobe']];
  var IMP_LIB_FIXLB = { bedDouble: 'QUEEN', bedSingle: 'TWIN', bath15: '5 FT TUB', bath18: '6 FT TUB' };
  /* v150: the chairs and the stools of each piece, kept with the plan */
  var IMP_LIB_SEATS = { table2: 2, table2r: 2, table: 4, table6: 6, table8: 8 }, IMP_LIB_STOOLS = { barTable: 2, island18: 3, island24: 4 };
  function _impLibIdOk(id) { return /^[a-f0-9]{16,40}$/.test(String(id || '')); }
  function _impLibPost(body) {
    return global.fetch(IMP_LIB, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json', accept: 'application/json' }, body: JSON.stringify(body) })
      .then(_impAnswer);
  }
  /* THE PLAN AS BUILT, in the designer's shape: metres from the north-west
     outer corner, x east, y south. Returns { plan, said } or null. */
  function _impLibExport() {
    var C = _impCore(), H = _PH, PLb = global.FloorplanLib && global.FloorplanLib.plan;
    if (!C || !C.STATE || !H || !H.poly) return null;
    var S = C.STATE, st = S.planStorey | 0, P = H.poly(), i, k, q;
    if (!P || P.length < 3) return null;
    /* v170: the plan over its outer faces where the wall reaches past the
       outline (the windows and doors are put onto that outline below) */
    var oX = _impOut();
    if (oX) P = INSET(P, -oX);
    var x0 = 1e9, z0 = 1e9, WT = (H.wallThk ? H.wallThk() : 0.16) + oX;
    for (i = 0; i < P.length; i++) { x0 = Math.min(x0, P[i][0]); z0 = Math.min(z0, P[i][1]); }
    function X(x) { return _impR(x - x0); }
    function Y(z) { return _impR(z - z0); }
    var out = { outline: [], partitions: [], doors: [], windows: [], rooms: [], open_sides: [], fixtures: [] };
    var said = { lines: 0, diag: 0, outer: 0, fx: 0, fxNone: {} };
    for (i = 0; i < P.length; i++) out.outline.push(X(P[i][0]), Y(P[i][1]));
    /* the rooms first: the doors and the room lines name the two they join */
    var RS = [], RM = [], TB = DRAW.roomTable(), nT = {}, seenT = {};
    try { RS = (PLb && PLb.rooms) ? (PLb.rooms() || []) : []; } catch (eR) { RS = []; }
    for (i = 0; i < RS.length; i++) {
      var R = RS[i], big = null, ba = -1;
      for (q = 0; q < (R.rects || []).length; q++) {
        var rc = R.rects[q], ar = (rc.x1 - rc.x0) * (rc.z1 - rc.z0);
        if (ar > ba) { ba = ar; big = rc; }
      }
      if (!big) continue;
      var ix = null, key = '';
      try { ix = PLb.roomNameIdx ? PLb.roomNameIdx(R, i) : R.nameIdx; } catch (eI) { ix = R.nameIdx; }
      if (ix != null && TB.length) { var rw = TB[(((ix | 0) % TB.length) + TB.length) % TB.length]; key = rw ? String(rw.i18n || '') : ''; }
      var type = R.closet ? 'closet' : (IMP_CHG_REV[key] || 'other');
      nT[type] = (nT[type] || 0) + 1;
      RM.push({ R: R, big: big, type: type });
    }
    /* BEDROOM 1, BEDROOM 2 where there are more of a kind */
    for (i = 0; i < RM.length; i++) {
      var tp = RM[i].type, base = IMP_LIB_LABEL[tp] || 'ROOM';
      seenT[tp] = (seenT[tp] || 0) + 1;
      RM[i].label = (nT[tp] > 1 && !/closet|storage/.test(tp)) ? (base + ' ' + seenT[tp]) : base;
      var bg = RM[i].big;
      out.rooms.push({ x: X((bg.x0 + bg.x1) / 2), y: Y((bg.z0 + bg.z1) / 2), label: RM[i].label, type: tp, size_text: '',
                       x0: X(bg.x0), y0: Y(bg.z0), x1: X(bg.x1), y1: Y(bg.z1) });
    }
    function roomAt(x, z) {
      for (var j = 0; j < RM.length; j++) {
        var rr = RM[j].R.rects || [];
        for (var j2 = 0; j2 < rr.length; j2++) if (x >= rr[j2].x0 - 0.01 && x <= rr[j2].x1 + 0.01 && z >= rr[j2].z0 - 0.01 && z <= rr[j2].z1 + 0.01) return RM[j].label;
      }
      return '';
    }
    function two(xa, za, xb, zb) { return (roomAt(xa, za) || 'room') + ' / ' + (roomAt(xb, zb) || 'room'); }
    /* the partitions, and the room lines as open sides */
    var PS = H.partitions() || [];
    for (i = 0; i < PS.length; i++) {
      var p = PS[i], sp = null;
      if (!p || (p.st != null && (p.st | 0) !== st)) continue;
      try { sp = _partSpan(p); } catch (eS) { sp = null; }
      if (!sp) continue;
      var A = (p.o === 'v') ? [p.c, sp[0]] : [sp[0], p.c], B = (p.o === 'v') ? [p.c, sp[1]] : [sp[1], p.c];
      if (p.sep) {
        var mid = (sp[0] + sp[1]) / 2;
        out.open_sides.push({ ax: X(A[0]), ay: Y(A[1]), bx: X(B[0]), by: Y(B[1]),
                              rooms: (p.o === 'v') ? two(p.c - 0.2, mid, p.c + 0.2, mid) : two(mid, p.c - 0.2, mid, p.c + 0.2) });
        said.lines++;
        continue;
      }
      out.partitions.push({ ax: X(A[0]), ay: Y(A[1]), bx: X(B[0]), by: Y(B[1]) });
    }
    /* the doors in them, each with its hinge and the side it opens to: the
       asset's sw -- bit 0 the hinge at the high end, bit 1 open to +z on an
       'h' wall and to -x on a 'v' one */
    var DL = _doorsLive();
    for (i = 0; i < DL.length; i++) {
      var d = DL[i].d, p2 = DL[i].p, w = d.w || 0.8;
      if (p2.st != null && (p2.st | 0) !== st) continue;
      var cx = (p2.o === 'h') ? d.u : p2.c, cz = (p2.o === 'h') ? p2.c : d.u;
      var kd = d.pass ? 'opening' : (d.kind === 'pair' ? 'double' : (d.kind === 'bifold' ? 'bifold' : (d.kind === 'bypass' ? 'bypass' : (d.slide ? 'sliding' : 'hinged'))));
      var sw = d.sw | 0, hu = d.u + ((sw & 1) ? w / 2 : -w / 2);
      var nx = (p2.o === 'h') ? 0 : ((sw & 2) ? -1 : 1), nz = (p2.o === 'h') ? ((sw & 2) ? 1 : -1) : 0;
      var hx = (p2.o === 'h') ? hu : p2.c, hz = (p2.o === 'h') ? p2.c : hu, ox = cx + nx * w / 2, oz = cz + nz * w / 2;
      if (kd !== 'hinged') { hx = cx; hz = cz; ox = cx; oz = cz; }
      out.doors.push({ x: X(cx), y: Y(cz), w: _impR(w), kind: kd, label: '',
                       rooms: (p2.o === 'h') ? two(cx, cz - 0.25, cx, cz + 0.25) : two(cx - 0.25, cz, cx + 0.25, cz),
                       hx: X(hx), hy: Y(hz), ox: X(ox), oy: Y(oz) });
    }
    /* the slanted walls and their doors (v125): sw bit 0 the hinge toward
       the far end, bit 1 open to the left of the way from a to b */
    var GW = (S.product && S.product.diagWalls) || [];
    for (i = 0; i < GW.length; i++) {
      var g = GW[i];
      if (!g || !g.a || !g.b || (g.st | 0) !== st) continue;
      var gl = Math.hypot(g.b[0] - g.a[0], g.b[1] - g.a[1]);
      if (!(gl > 0.3)) continue;
      var ux = (g.b[0] - g.a[0]) / gl, uz = (g.b[1] - g.a[1]) / gl;
      out.partitions.push({ ax: X(g.a[0]), ay: Y(g.a[1]), bx: X(g.b[0]), by: Y(g.b[1]) });
      said.diag++;
      for (k = 0; g.doors && k < g.doors.length; k++) {
        var gd = g.doors[k], gw = gd.w || 0.8, gx = g.a[0] + ux * gd.u, gz = g.a[1] + uz * gd.u, gs = gd.sw | 0;
        var gk = IMP_DIAG_KIND_OUT[gd.kind] || 'hinged', hs = (gs & 1) ? 1 : -1, os = (gs & 2) ? 1 : -1;
        var ghx = gx + ux * hs * gw / 2, ghz = gz + uz * hs * gw / 2, gox = gx - uz * os * gw / 2, goz = gz + ux * os * gw / 2;
        if (gk !== 'hinged') { ghx = gx; ghz = gz; gox = gx; goz = gz; }
        out.doors.push({ x: X(gx), y: Y(gz), w: _impR(gw), kind: gk, label: '', rooms: two(gx - uz * 0.25, gz + ux * 0.25, gx + uz * 0.25, gz - ux * 0.25),
                         hx: X(ghx), hy: Y(ghz), ox: X(gox), oy: Y(goz) });
      }
    }
    /* the windows and the outer doors, ON the outline: the point along the
       core's wall taken square onto the nearest edge of the outline */
    function onOutline(px, pz) {
      var best = null, bd = 1e9, j;
      for (j = 0; j < P.length; j++) {
        var a = P[j], b = P[(j + 1) % P.length], ex = b[0] - a[0], ez = b[1] - a[1], L2 = ex * ex + ez * ez;
        if (!(L2 > 0)) continue;
        var t = Math.max(0, Math.min(1, ((px - a[0]) * ex + (pz - a[1]) * ez) / L2)), qx = a[0] + ex * t, qz = a[1] + ez * t, dd = Math.hypot(px - qx, pz - qz);
        if (dd < bd) { bd = dd; best = { x: qx, z: qz, ex: ex / Math.sqrt(L2), ez: ez / Math.sqrt(L2) }; }
      }
      return best;
    }
    var OL = S.openings || [];
    for (i = 0; i < OL.length; i++) {
      var o = OL[i], sg = null;
      try { sg = C.segment ? C.segment(o.seg) : null; } catch (eG) { sg = null; }
      if (!sg || !(sg.len > 0)) continue;
      var sx = (sg.x1 - sg.x0) / sg.len, sz = (sg.z1 - sg.z0) / sg.len, Q = onOutline(sg.x0 + sx * o.at, sg.z0 + sz * o.at);
      if (!Q) continue;
      if (o.kind === 'window') {
        out.windows.push({ x: X(Q.x), y: Y(Q.z), w: _impR(o.w), sill_m: _impR(o.sill || 0), label: '' });
        continue;
      }
      /* the room behind the door: a step in from the outline, either way */
      var inn = '', e2;
      for (e2 = -1; e2 <= 1 && !inn; e2 += 2) inn = roomAt(Q.x - Q.ez * e2 * (WT + 0.3), Q.z + Q.ex * e2 * (WT + 0.3));
      var ok2 = (o.kind === 'french') ? (_impOpSlides(o) ? 'sliding' : 'double') : 'hinged';
      out.doors.push({ x: X(Q.x), y: Y(Q.z), w: _impR(o.w), kind: ok2, label: '', rooms: (inn || 'room') + ' / outside',
                       hx: X(Q.x), hy: Y(Q.z), ox: X(Q.x), oy: Y(Q.z) });
      said.outer++;
    }
    /* the pieces, each by its box as it stands */
    var FL = (H.fixturesRef && H.fixturesRef()) || [];
    for (i = 0; i < FL.length; i++) {
      var fx = FL[i];
      if (!fx || (fx.st != null && (fx.st | 0) !== st)) continue;
      var kn = String(fx.kind || ''), ft = '';
      for (k = 0; k < IMP_LIB_FIX.length; k++) if (IMP_LIB_FIX[k][0].test(kn)) { ft = IMP_LIB_FIX[k][1]; break; }
      if (!ft) { said.fxNone[kn] = (said.fxNone[kn] || 0) + 1; continue; }
      var rf = null;
      try { rf = _fixRect(fx); } catch (eF) { rf = null; }
      if (!rf) continue;
      var oFx = { type: ft, label: fx.sug ? 'SUGGESTED' : (IMP_LIB_FIXLB[kn] || ''), x0: X(rf.x0), y0: Y(rf.z0), x1: X(rf.x1), y1: Y(rf.z1) };
      if (!fx.sug && IMP_LIB_SEATS[kn]) oFx.seats = IMP_LIB_SEATS[kn];
      if (!fx.sug && IMP_LIB_STOOLS[kn]) oFx.stools = IMP_LIB_STOOLS[kn];
      out.fixtures.push(oFx);
      /* a stacked pair is two machines on one footprint */
      if (fx.stack === 'dryer') out.fixtures.push({ type: 'dryer', label: '', x0: oFx.x0, y0: oFx.y0, x1: oFx.x1, y1: oFx.y1 });
      said.fx++;
    }
    /* the terrace, as the outdoor room it was read as */
    var T = S.product && S.product.terrace;
    if (T && T.on && T.sx > 0 && T.sz > 0) {
      var pav = (T.surface === 'pavers');
      out.rooms.push({ x: X(T.x0 + T.sx / 2), y: Y(T.z0 + T.sz / 2), label: pav ? 'PATIO' : 'DECK', type: pav ? 'patio' : 'deck', size_text: '',
                       x0: X(T.x0), y0: Y(T.z0), x1: X(T.x0 + T.sx), y1: Y(T.z0 + T.sz) });
    }
    var none = Object.keys(said.fxNone);
    return { plan: out, said: out.partitions.length + ' wall(s)' + (said.diag ? (' (' + said.diag + ' slanted)') : '') + ', ' + said.lines + ' room line(s), ' +
                              out.doors.length + ' door(s) (' + said.outer + ' outer), ' + out.windows.length + ' window(s), ' + RM.length + ' room(s)' +
                              (T && T.on ? ' and the terrace' : '') + ', ' + said.fx + ' piece(s)' + (none.length ? (' -- not kept: ' + none.join(', ')) : '') };
  }
  /* ---------- ONLY A PLAN BEYOND DOUBT GOES INTO THE LIBRARY (v137) --------
     Marek: co kdyz tam budeme mit v nabidce vygenerovany pudorys, ktery
     nebude v poradku? Ladeni mozna bude na mesice. Mely by se ukladat jen
     pudorysy, kde agent bude jasne, ze je pudorys korektni -- dvere do kazde
     mistnosti, spojite pricky, spravne rozdelene mistnosti vcetne fitouts.
     The audit (v135) says whether a plan works as built. The library asks
     more: that nothing had to be guessed or mended on the way. Its rules,
     numbered so that the worker (w1.99) can leave out every plan saved under
     older ones the day the rules get stricter:
       1. the audit is clean;
       2. the reader was sure of everything (a plan read from a picture): it
          names nothing it could not read, the picture is a drawing (not a
          render or a photo), and the scale comes from what is written on it
          -- dimensions, room sizes, door labels -- not from furniture;
       3. nothing structural was mended: no wall ending free, walls too close,
          a door across a T, doors striking, a door or a wall added where the
          reader had none, a door moved to another wall, a room box too big,
          a space with no name, an outline that was not a rectangle;
       4. the pieces are where they belong: the bath, the shower, the WC and
          the basin in a bathroom, the kitchen in the kitchen or the living
          room, a bed in a bedroom (in the living room only in a studio), the
          washer in a laundry, a bathroom, a hall or a closet, nothing else in
          a closet; every bathroom has a WC, every bedroom a bed, every
          kitchen a kitchen; no piece in a doorway, a door's swing, in front
          of a window it is taller than, over a wall or another piece;
       5. a terrace lies against the house and has a door out to it. */
  var IMP_LIB_RULES = 2;
  /* WHAT ONLY TIDIES THE READING DOES NOT KEEP A PLAN OUT (v141, Marek --
     drobnosti neblokuji: kosmeticke opravy projdou; zavrene mistnosti,
     vstupy a nabytek mimo dvere zustavaji podminkou). Each mend is marked:
     true for one that leaves the plan as the drawing has it -- a box drawn
     bigger than its room, a pair too narrow made one leaf, a wall end
     carried on as a room line, the edge of a fixture or a shelf not taken
     for a wall, a door turned or narrowed to clear another or a T, a name
     the plan did not give, a slider the reader took for a window; false for
     one that builds what the drawing does not have -- the outline made a
     rectangle, a niche taken in, a wall or a door added, a door or a window
     dropped or moved to another wall, walls moved apart. The audit and the
     pieces are checked as before. */
  var IMP_LIB_MENDS = [
    [/^FREE END:/, 'a wall ended free', true], [/^SPACE:/, 'walls closer than 0.60 m', true], [/^STRIP:/, 'a strip of wall beside another', true],
    [/^OFF THE T:/, 'a door across a T', true], [/^PAIRS:/, 'a pair too narrow', true], [/^SWING:/, 'doors striking each other', true],
    [/^RECT:(?!.*under 5 %)/, 'the outline is not a rectangle', false], [/^RECT: .*under 5 %/, 'a corner under 5 % of the house squared', true], [/^NICHE:(?!.*under 5 %)/, 'a niche taken into the house', false], [/^NICHE: .*under 5 %/, 'a niche under 5 % of the house taken in, glazed', true],
    [/^HALL:/, 'a hall with no name taken into the living room', true], [/^BATHWIN: \d+ bathroom/, 'a window added in a bathroom with none', true], [/^SYMMETRY \(v148\)/, 'openings set symmetric', true], [/is on no wall -- left out/, 'a door or a window on no wall', false],
    [/not made of square corners/, 'the outline is not square', false], [/^no outline was read/, 'no outline', false], [/^no scale/, 'no scale', false],
    [/got a wall and a door/, 'a wall and a door added', false], [/with nothing drawn got a door/, 'a door added where none was drawn', false],
    [/not open zones got a door/, 'a door added in a passage', false], [/were left open -- the box was bigger/, 'a room box bigger than its room', true],
    [/set on the wrong wall/, 'a door on the wrong wall', true], [/not open zones became walls/, 'a wall added on an open side', false],
    [/no wall drawn were closed/, 'a wall the reader missed', true], [/^doors on top of each other/, 'doors on top of each other', false],
    [/left out as the edge of a fixture/, 'the edge of a fixture read as a wall', true], [/matches none of our room names/, 'a room name not known', true],
    [/the plan did not name were named/, 'a space the plan did not name', true], [/^DIAG: .*not closed/, 'a slanted corner left open', true], [/^SLIDER: \d/, 'a sliding door read as a window', true],
    [/^CABINET:/, 'a cabinet read as a closet', true], [/^NOOK:/, 'a laundry corner read as a closet', true], [/^STYLE:/, 'a drawing called a render', true], [/^DOUBLE:/, 'a pair to the outside', true], [/^OUTSWING:/, 'an outer door opening out as drawn', true],
    [/^PORCH:/, 'a wide window onto a porch read as a sliding door', true],
    [/^SCALED:/, 'built at the largest size, up to 10 % smaller than drawn', true], [/^STEP:/, 'a window on a step taken out with it', true],
    [/^CLOSET FRONT \(v162\)/, 'a closet front the reader did not name', true], [/^BOX \(v162\)/, 'a shut box read as a room', true],
    [/^ONE NAME \(v169\)/, 'two names in one open room, the main one kept', true],
    [/^BATH DOOR \(v177\)/, 'a door into a bathroom', true],
    [/^WARDROBE RUN \(v180\)/, 'a built-in wardrobe read as a closet', true],
    [/^SQUEEZED \(v171\)/, 'built more than 10 % smaller than drawn, as the buyer asked', false]
  ];
  var IMP_LIB_FITROOM = [
    [/^(bath|shower|wc$|basin)/, /bathroom|rooms\.wc/, 'not in a bathroom'],
    [/^(kitchen|island|fridge)/, /kitchen|livingroom|diningroom/, 'not in the kitchen or the living room'],
    [/^(washer|dryer)/, /laundry|bathroom|rooms\.wc|container_room_6|hall|storage|kitchen|mudroom|pantry/, 'not in a laundry, a bathroom or a hall']
  ];
  function _impLibGrade(R) {
    var why = [], tidy = [], C = _impCore(), S = C && C.STATE, H = _PH, A = _imp.audit, i, k, q;
    /* 1 */
    if (!A || !A.ok) why.push('the audit: ' + (A && A.problems ? A.problems.join(', ') : 'no result'));
    if (!S || !H) { why.push('no house'); return { ok: false, why: why }; }
    /* 2 */
    if (R && !R.text && !R.lib) {
      var un = (R.res && R.res.plan && Array.isArray(R.res.plan.unreadable)) ? R.res.plan.unreadable : [];
      /* v141 (worker w2.02 the same): what the drawing does not print -- no
         dimension lines, no scale, no north arrow -- is no doubt */
      var NOTDOUBT = /^\s*(there (are|is) )?(no|without)\s+(printed\s+|written\s+)?(overall\s+)?(dimension|dimensions|dimension lines?|dimension strings?|scale( bar)?|north( arrow)?)\b|\b(dimension lines?|dimension strings?|scale bar|north arrow)\b[^.;]*\b(not (printed|shown|given|drawn)|absent|missing)\b/i;
      var unD = un.filter(function (t9) { return !NOTDOUBT.test(String(t9)); }), unN = un.filter(function (t9) { return NOTDOUBT.test(String(t9)); });
      /* v142 (Marek: ten pudorys se mel objevit v seznamu vzhledem ke
         kvalite, s jakou to Fable zvladl): what the reader writes it was
         unsure of is a note -- the plan as built is checked by the audit and
         the pieces, not by the reader's modesty */
      if (unD.length) tidy.push('the reader noted: ' + unD.join('; '));
      if (unN.length) tidy.push('the reader noted: ' + unN.join('; '));
      if (R.style === 'render' || R.style === 'photo') why.push('the picture is a ' + R.style);
      else if (R.n && R.n.px && R.n.px.render) why.push('the picture is a render');
      var sf = String((R.n && R.n.scaleFrom) || '');
      /* v198 (Marek on 1000075646: pudorys opraven, ale neaktualizoval se v
         nabidce). The same picture read again gave no room areas this time,
         the scale came from five pieces of furniture, the doors 0.85 m at
         it -- and the corrected plan was refused, the old one kept in the
         list. Furniture of three pieces or more with the drawn doors a
         door's width at that scale (0.70 to 0.95 m) is a scale too. */
      var sDoors = R.n && R.n.scaleDoors;
      if (!/dimension|room size|door label|given/.test(sf) && !(/furniture/.test(sf) && sDoors)) why.push('the scale is not written on the plan (' + (sf || 'none') + ')');
      else if (/furniture/.test(sf) && sDoors) tidy.push('the scale from the furniture, the doors ' + _impN(sDoors) + ' m at it (v198)');
    }
    /* 3 */
    /* v153: a plan changed in words is the buyer's -- what the page mended
       in the first reading is not the question, the plan as built is */
    var notes = (R && R.change) ? [] : ((R && R.n && R.n.notes) || []).concat((_imp.fit && _imp.fit.notes) || [], (_imp.lastApply && _imp.lastApply.notes) || []), seenM = {};
    for (i = 0; i < notes.length; i++) {
      for (k = 0; k < IMP_LIB_MENDS.length; k++) {
        if (IMP_LIB_MENDS[k][0].test(String(notes[i])) && !seenM[k]) {
          seenM[k] = 1;
          /* v175: built smaller where no product builds it as drawn -- the
             library keeps it as built (Marek: zmensene projekty publikovat
             cele, podle pravidla) */
          if (IMP_LIB_MENDS[k][2] || (_imp.squeezeLib && /^SQUEEZED/.test(String(notes[i])))) tidy.push(IMP_LIB_MENDS[k][1] + (_imp.squeezeLib && /^SQUEEZED/.test(String(notes[i])) ? ' (no product builds it as drawn)' : ''));
          else why.push('mended: ' + IMP_LIB_MENDS[k][1]);
        }
      }
    }
    /* 4 */
    var RT = _roomsTracked(), pins = Array.isArray(S.roomNames) ? S.roomNames : [], FL = (H.fixturesRef && H.fixturesRef()) || [];
    function roomAt(x, z) { for (var r = 0; r < RT.length; r++) if (_roomHolds(RT[r], x, z)) return r; return -1; }
    function keyOf(r) {
      /* v169: the name the plan shows (PIN_OPEN), not just the first pin */
      var jb = _pinBest(pins, _roomPinsIn(RT[r], pins));
      if (jb >= 0) return String(pins[jb].k || '');
      for (var j = 0; j < pins.length; j++) if (pins[j] && typeof pins[j].x === 'number' && _roomHolds(RT[r], pins[j].x, pins[j].z)) return String(pins[j].k || '');
      return '';
    }
    var KEY = RT.map(function (r9, j9) { return RT[j9].closet ? 'closet' : keyOf(j9); });
    var hasBed = KEY.some(function (kq) { return /bedroom|container_room_7|guestroom/.test(kq); });
    var has = RT.map(function () { return {}; }), Z = [], said = {};
    try { Z = _fitZones(); } catch (eZ) { Z = []; }
    function once(t) { if (!said[t]) { said[t] = 1; why.push(t); } }
    for (i = 0; i < RT.length; i++) if (!KEY[i]) once('a room with no name');
    for (i = 0; i < FL.length; i++) {
      var fx = FL[i];
      if (!fx || (fx.st != null && (fx.st | 0) !== (S.planStorey | 0))) continue;
      var kn = String(fx.kind || ''), ri = roomAt(fx.x, fx.z), kq = ri >= 0 ? KEY[ri] : '';
      if (ri < 0) { once('a ' + kn + ' outside every room'); continue; }
      has[ri][kn.replace(/[0-9LU]+$/, '')] = 1;
      if (/^bed/.test(kn)) has[ri].bed = 1;
      if (/^(kitchen|island)/.test(kn)) has[ri].kitchen = 1;
      if (kq === 'closet' && !/^(washer|dryer)/.test(kn)) { once('a ' + kn + ' in a closet'); continue; }
      for (k = 0; k < IMP_LIB_FITROOM.length; k++) {
        if (IMP_LIB_FITROOM[k][0].test(kn) && kq !== 'closet' && !IMP_LIB_FITROOM[k][1].test(kq)) once('a ' + kn + ' ' + IMP_LIB_FITROOM[k][2] + ' (' + (kq.replace(/^rooms\./, '') || 'room') + ')');
      }
      if (/^bed/.test(kn) && !/bedroom|container_room_7|guestroom/.test(kq) && (hasBed || !/livingroom/.test(kq))) once('a bed not in a bedroom (' + (kq.replace(/^rooms\./, '') || 'room') + ')');
      var cl = null;
      try { cl = _fitClash(fx, _impFaces(fx.x, fx.z), _impZonesFor(fx, Z), null, fx.id); } catch (eC) { cl = null; }
      /* v141: under a window is how the drawing has it (a shower under a
         bathroom window, a fridge in a kitchen run) -- noted, not in the way */
      if (cl && cl.reason === 'window' && _impWinBlocks(fx)) once('a ' + kn + ' in front of a window (v182)');
      else if (cl && cl.reason === 'window') { var tw9 = 'a ' + kn + ' in front of a window'; if (tidy.indexOf(tw9) < 0) tidy.push(tw9); }
      else if (cl) once('a ' + kn + (fx.sug ? ' (suggested)' : '') + ' ' + ({ door: 'in a doorway or a door\'s swing', overlap: 'on another piece', size: 'over a wall' }[cl.reason] || ('in the way: ' + cl.reason)));
    }
    for (i = 0; i < RT.length; i++) {
      if (/bathroom|rooms\.wc/.test(KEY[i]) && !has[i].wc) once('a bathroom with no WC');
      if (/bedroom|container_room_7|guestroom/.test(KEY[i]) && !has[i].bed) once('a bedroom with no bed');
      if (/kitchen/.test(KEY[i]) && !has[i].kitchen) once('a kitchen with no kitchen');
    }
    /* v145: a plan built smaller than drawn, while its bedrooms keep 70 sqft
       and 7 ft across (the bathrooms, closets and halls are as drawn) */
    if (_imp.fit && _imp.fit.scaled) {
      var NA5 = [];
      try { NA5 = _impNetAreas(RT, _impWedges(RT) || []); } catch (eN5) { NA5 = []; }
      for (i = 0; i < RT.length; i++) {
        if (!/bedroom|container_room_7|guestroom/.test(KEY[i])) continue;
        var F5 = null;
        try { F5 = _fitRoomBox(RT[i]); } catch (eF5) { F5 = null; }
        var a5 = (NA5[i] != null) ? NA5[i] : (RT[i].area || 0), m5 = F5 ? Math.min(F5.x1 - F5.x0, F5.z1 - F5.z0) : 0;
        if (a5 < 6.5 || (F5 && m5 < 2.13)) why.push('built smaller than drawn and a bedroom came out under 70 sqft or 7 ft across (' + _impN(a5) + ' m2, ' + _impN(m5) + ' m)');
      }
    }
    /* 5 */
    var T = S.product && S.product.terrace;
    if (T && T.on) {
      var P = H.poly() || [], bx0 = 1e9, bx1 = -1e9, bz0 = 1e9, bz1 = -1e9;
      for (i = 0; i < P.length; i++) { bx0 = Math.min(bx0, P[i][0]); bx1 = Math.max(bx1, P[i][0]); bz0 = Math.min(bz0, P[i][1]); bz1 = Math.max(bz1, P[i][1]); }
      var sd = T.side, touch = (sd === 'z1') ? Math.abs(T.z0 - bz1) : ((sd === 'z0') ? Math.abs(T.z0 + T.sz - bz0) : ((sd === 'x1') ? Math.abs(T.x0 - bx1) : Math.abs(T.x0 + T.sx - bx0)));
      /* v176: a block house's terrace lies against the outer face, planOut
         past the wall axis H.poly() gives */
      var OUT6 = _impOut();
      if (!(touch < 0.06 || (OUT6 > 0 && Math.abs(touch - OUT6) < 0.06) || touch < OUT6 + 0.06)) why.push('the terrace does not lie against the house (' + _impN(touch) + ' m off)');
      var SG = [], door = false;
      try { SG = C.segments ? (C.segments() || []) : []; } catch (eS) { SG = []; }
      for (i = 0; i < SG.length && !door; i++) {
        var sg = SG[i], hor = Math.abs(sg.z1 - sg.z0) < 0.02, ver = Math.abs(sg.x1 - sg.x0) < 0.02, ss = '';
        var TOL6 = 0.2 + OUT6;
        if (hor) ss = (Math.abs(sg.z0 - bz1) < TOL6) ? 'z1' : ((Math.abs(sg.z0 - bz0) < TOL6) ? 'z0' : '');
        else if (ver) ss = (Math.abs(sg.x0 - bx1) < TOL6) ? 'x1' : ((Math.abs(sg.x0 - bx0) < TOL6) ? 'x0' : '');
        if (ss !== sd) continue;
        for (q = 0; sg.openings && q < sg.openings.length; q++) if (_impOpDoor(sg.openings[q])) { door = true; break; }
      }
      if (!door) {
        /* v177: which walls the check saw, for the next look */
        var sidesD = [];
        for (i = 0; i < SG.length; i++) {
          var sgD = SG[i], nD = 0;
          for (q = 0; sgD.openings && q < sgD.openings.length; q++) if (_impOpDoor(sgD.openings[q])) nD++;
          if (nD) sidesD.push(sgD.id + ' ' + nD + ' door(s) at ' + _impN((sgD.x0 + sgD.x1) / 2) + ',' + _impN((sgD.z0 + sgD.z1) / 2));
        }
        why.push('no door out to the terrace (the terrace on ' + sd + ', the house ' + _impN(bx0) + '..' + _impN(bx1) + ' x ' + _impN(bz0) + '..' + _impN(bz1) + '; doors: ' + (sidesD.join(' | ') || 'none') + ')');
      }
    }
    return { ok: !why.length, why: why, tidy: tidy, rules: IMP_LIB_RULES };
  }
  /* THE PLAN AS THE DRAWER DRAWS IT (v137, Marek: pudorysy vizualizovat v
     draweru stejne jako je mame v pudorysu -- barevne rozliseni mistnosti,
     okna a dvere, mohou byt vymery mistnosti, nad obrazkem celkova podlahova
     plocha v jednotkach stranky). Taken from the plan as built, in metres
     from its north-west corner: the outline and the wall thickness, the
     walls and room lines, each room with its colour, its area and its
     cells, the doors with their swing, the windows and the outer doors on
     the outline, the terrace, and the inner floor area worked out as the
     figure over the plan is. Small: a kilobyte or two. */
  function _impLibThumb() {
    var C = _impCore(), H = _PH, PLb = global.FloorplanLib && global.FloorplanLib.plan;
    if (!C || !C.STATE || !H || !H.poly) return null;
    var S = C.STATE, st = S.planStorey | 0, P = H.poly(), i, k;
    if (!P || P.length < 3) return null;
    var x0 = 1e9, z0 = 1e9;
    for (i = 0; i < P.length; i++) { x0 = Math.min(x0, P[i][0]); z0 = Math.min(z0, P[i][1]); }
    function N(v) { return Math.round(v * 100) / 100; }
    function X(x) { return N(x - x0); }
    function Y(z) { return N(z - z0); }
    var WT = H.wallThk ? H.wallThk() : 0.16, PT = H.partThk ? H.partThk() : 0.1;
    /* v: 2 (v143) -- the rooms' areas are their own floors, the walls out, and a is their sum */
    var T = { v: 2, o: [], wt: N(WT), pt: N(PT), p: [], r: [], d: [], g: [], w: [], e: [], t: null, a: 0 };
    for (i = 0; i < P.length; i++) T.o.push(X(P[i][0]), Y(P[i][1]));
    var PS = H.partitions() || [];
    for (i = 0; i < PS.length; i++) {
      var p = PS[i], sp = null;
      if (!p || (p.st != null && (p.st | 0) !== st)) continue;
      try { sp = _partSpan(p); } catch (eS) { sp = null; }
      if (!sp) continue;
      if (p.o === 'v') T.p.push([X(p.c), Y(sp[0]), X(p.c), Y(sp[1]), p.sep ? 1 : 0]);
      else T.p.push([X(sp[0]), Y(p.c), X(sp[1]), Y(p.c), p.sep ? 1 : 0]);
    }
    var GW = (S.product && S.product.diagWalls) || [];
    for (i = 0; i < GW.length; i++) {
      var g = GW[i];
      if (!g || !g.a || !g.b || (g.st | 0) !== st) continue;
      var gl = Math.hypot(g.b[0] - g.a[0], g.b[1] - g.a[1]);
      if (!(gl > 0.3)) continue;
      var ux = (g.b[0] - g.a[0]) / gl, uz = (g.b[1] - g.a[1]) / gl;
      T.p.push([X(g.a[0]), Y(g.a[1]), X(g.b[0]), Y(g.b[1]), 0]);
      for (k = 0; g.doors && k < g.doors.length; k++) {
        var gd = g.doors[k];
        T.g.push([X(g.a[0] + ux * gd.u), Y(g.a[1] + uz * gd.u), N(gd.w || 0.8), N(ux), N(uz), gd.sw | 0, IMP_THUMB_KIND[gd.kind] || 0]);
      }
    }
    /* the rooms: colour, area, cells; a closet in the closet's own grey */
    var RS = [], TB = DRAW.roomTable(), aSum = 0, NET = [], nSum = 0;
    try { RS = (PLb && PLb.rooms) ? (PLb.rooms() || []) : []; } catch (eR) { RS = []; }
    try { NET = _impNetAreas(RS, _impWedges(RS) || []); } catch (eNT) { NET = []; }
    for (i = 0; i < RS.length; i++) {
      var R = RS[i], cells = [], q;
      for (q = 0; q < (R.rects || []).length; q++) {
        var rc = R.rects[q];
        cells.push(X(rc.x0), Y(rc.z0), X(rc.x1), Y(rc.z1));
        aSum += Math.max(0, rc.x1 - rc.x0) * Math.max(0, rc.z1 - rc.z0);
      }
      if (!cells.length) continue;
      var ix = null, row = null;
      try { ix = PLb.roomNameIdx ? PLb.roomNameIdx(R, i) : R.nameIdx; } catch (eI) { ix = R.nameIdx; }
      if (ix != null && TB.length) row = TB[(((ix | 0) % TB.length) + TB.length) % TB.length];
      var aR = (NET[i] != null) ? NET[i] : (R.area || 0);
      nSum += _impAreaShown(aR);
      T.r.push([R.closet ? CLOSET_FILL : ((row && row.col) || '#D2A878'), N(aR), R.closet ? 'leva.closet' : String((row && row.i18n) || ''), cells]);
    }
    var DL = _doorsLive();
    for (i = 0; i < DL.length; i++) {
      var d = DL[i].d, p2 = DL[i].p;
      if (p2.st != null && (p2.st | 0) !== st) continue;
      var kd = d.pass ? 5 : (d.kind === 'pair' ? 1 : (d.kind === 'bifold' ? 2 : (d.kind === 'bypass' ? 3 : (d.slide ? 4 : 0))));
      T.d.push([X(p2.o === 'h' ? d.u : p2.c), Y(p2.o === 'h' ? p2.c : d.u), N(d.w || 0.8), p2.o === 'v' ? 1 : 0, d.sw | 0, kd]);
    }
    var OL = S.openings || [];
    for (i = 0; i < OL.length; i++) {
      var o = OL[i], sg = null;
      try { sg = C.segment ? C.segment(o.seg) : null; } catch (eG) { sg = null; }
      if (!sg || !(sg.len > 0)) continue;
      var sx = (sg.x1 - sg.x0) / sg.len, sz = (sg.z1 - sg.z0) / sg.len, px = sg.x0 + sx * o.at, pz = sg.z0 + sz * o.at;
      /* onto the outline: the nearest edge, square */
      var best = null, bd = 1e9, j;
      for (j = 0; j < P.length; j++) {
        var a = P[j], b = P[(j + 1) % P.length], ex = b[0] - a[0], ez = b[1] - a[1], L2 = ex * ex + ez * ez;
        if (!(L2 > 0)) continue;
        var t = Math.max(0, Math.min(1, ((px - a[0]) * ex + (pz - a[1]) * ez) / L2)), qx = a[0] + ex * t, qz = a[1] + ez * t, dd = Math.hypot(px - qx, pz - qz);
        if (dd < bd) { bd = dd; best = [qx, qz, ex / Math.sqrt(L2), ez / Math.sqrt(L2)]; }
      }
      if (!best) continue;
      if (o.kind === 'window') T.w.push([X(best[0]), Y(best[1]), N(o.w), N(best[2]), N(best[3])]);
      /* v149: an entry door of two leaves is a pair (1), and a seventh number
         1 says it opens out of the building (core-v567 draws both) */
      else {
        var eK = (o.kind === 'french') ? (_impOpSlides(o) ? 4 : 1) : (((o.leaves | 0) >= 2) ? 1 : 0), eR = [X(best[0]), Y(best[1]), N(o.w), N(best[2]), N(best[3]), eK];
        if (o.out && o.kind === 'door') eR.push(1);
        T.e.push(eR);
      }
    }
    var TR = S.product && S.product.terrace;
    if (TR && TR.on && TR.sx > 0 && TR.sz > 0) T.t = [X(TR.x0), Y(TR.z0), X(TR.x0 + TR.sx), Y(TR.z0 + TR.sz), (TR.surface === 'pavers') ? 1 : 0];
    /* the inner floor area, as the figure over the plan works it out */
    var inner = 0, partA = 0, PA = S.parts || [];
    try { inner = Math.max(0, (S.footprint.w - 2 * WT) * (S.footprint.l - 2 * WT)); } catch (eA) { inner = aSum; }
    for (k = 0; k < PA.length; k++) { try { var sp2 = _partSpan(PA[k]); if (sp2) partA += Math.abs(sp2[1] - sp2[0]) * PT; } catch (eP) {} }
    T.a = N(Math.max(0, (aSum > 0 ? Math.min(aSum, inner) : inner) - partA));
    /* v143: the sum of the rooms as they are written */
    if (NET.length && nSum > 0) T.a = N(nSum);
    return T;
  }
  var IMP_THUMB_KIND = { hinged: 0, pair: 1, bifold: 2, bypass: 3, slide: 4, pass: 5 };
  /* A CLEAN PLAN INTO THE LIBRARY, once it is written (v136) */
  /* v140: the library's verdict into the lines COPY copies, not only the
     console (Marek: zadne pudorysy se ted neukladaji -- the reason was in
     the console alone) */
  function _impLibSay(t) {
    _impSay(t);
    if (_imp.applyLines) _imp.applyLines.push(t);
    if (_imp.lines) _imp.lines.push(t);
    try { _fitShow(); } catch (eF) {}
  }
  /* A PLAN CHANGED IN WORDS GOES IN TOO (v153, Marek: pudorys, ktery jsem
     pak iteroval / upravoval textovym promptem se vubec neulozil do
     databaze, i kdyz je povedeny). Only a plan read or designed was saved;
     a change never was. Now the plan as it stands after a change -- as
     built, the way a picture's plan is saved -- goes through the same check
     and in as an edit; the library (w2.05) puts it in the place of the
     version it came from when the layout is the same. A change that left
     the plan as it was saved (the roof, the terrace) saves nothing again:
     the project that goes with the plan follows it. */
  function _impLibSave(R) {
    /* v199: LEVA's plan from the worker's list is built as a library plan
       is, and saved as LEVA's own */
    if (!R || (R.lib && R.seed == null) || typeof global.fetch !== 'function') return;
    var edit = !!R.change;
    var G = null;
    try { G = _impLibGrade(R); } catch (eG) { G = { ok: false, why: ['the check THREW ' + ((eG && eG.message) || eG)] }; }
    _imp.libGrade = G;
    if (!G.ok) {
      _imp.libSaved = { st: 'refused', why: G.why.slice(0, 6) };
      _impLibSay('PLAN library check (rules ' + IMP_LIB_RULES + '): NOT for the library, so not saved -- ' + G.why.join(' | ') +
                 ((G.tidy && G.tidy.length) ? (' || tidied only, not in the way (v141): ' + G.tidy.join(', ')) : ''));
      return;
    }
    _impLibSay('PLAN library check (rules ' + IMP_LIB_RULES + '): fit for the library -- the audit clean, the reading sure, nothing built that the drawing does not have, every piece in its place' +
               ((G.tidy && G.tidy.length) ? (' (tidied only, v141: ' + G.tidy.join(', ') + ')') : ''));
    var TH = null;
    try { TH = _impLibThumb(); } catch (eT) { TH = null; _impLibSay('PLAN library: the drawer\'s picture THREW ' + ((eT && eT.message) || eT) + ' -- saved without it'); }
    var body = { action: 'save', audit: { clean: true, v: VERSION, rules: IMP_LIB_RULES }, product: _impProdName(), thumb: TH, floor: TH ? TH.a : 0 }, des = R.res && R.res.design, what = '', keyNow = '';
    try { var ex0 = _impLibExport(); keyNow = ex0 ? JSON.stringify(ex0.plan) : ''; } catch (eK) { keyNow = ''; }
    if (edit) {
      var LS = _imp.libSaved;
      if (LS && LS.id && LS.key && LS.key === keyNow) {
        _impLibSay('PLAN library: the plan is the one saved as ' + LS.id + ' -- not saved again; the project after this change goes with it (v153)');
        try { _impLibProjLater(LS.id, R, R.pBefore || '', _impDigest()); } catch (ePl0) { _impLibSay('PLAN library: the project link THREW ' + ((ePl0 && ePl0.message) || ePl0)); }
        return;
      }
    }
    if (R.text && !edit) {
      if (!des || !des.id) { _impLibSay('PLAN library: not saved -- the worker kept no copy of this design (a worker before w1.98)'); return; }
      body.design = des.id;
      what = (R.redesign ? 'the redesign' : 'the design from words') + ' ' + des.id;
    } else {
      var ex = null;
      try { ex = _impLibExport(); } catch (eX) { _impLibSay('PLAN library: not saved -- writing the plan out THREW ' + ((eX && eX.message) || eX)); return; }
      if (!ex) { _impLibSay('PLAN library: not saved -- there is no plan to write out'); return; }
      body.plan = ex.plan; body.src = edit ? 'edit' : ((R.seed != null) ? 'seed' : 'picture');
      /* v199: the key of LEVA's plan -- its save takes the place of the one before */
      if (!edit && R.seed != null && R.seedKey) body.seed = R.seedKey;
      what = edit ? ('the plan after the change "' + String(R.text || '').slice(0, 80) + '", as built: ' + ex.said) : ((R.seed != null) ? ('LEVA\'s plan ' + R.name + ', as built: ' + ex.said) : ('the plan read from the picture, as built: ' + ex.said));
      /* v143: the picture's fingerprint -- the library keeps one plan for one picture, the latest */
      if (!edit && R.img && R.img.fp && R.img.fp.d) {
        body.pic = { v: R.img.fp.v || 1, d: R.img.fp.d, ar: R.img.fp.ar, w0: R.img.w0 || 0, h0: R.img.h0 || 0 };
        what += ' -- with the picture\'s fingerprint ' + R.img.fp.d.slice(0, 12) + '..., ' + R.img.fp.ar;
      }
    }
    _imp.libSaved = { st: 'saving', at: Date.now() };
    _impLibSay('PLAN library: saving ' + what + (TH ? (' -- with the drawer\'s picture, ' + TH.r.length + ' room(s), ' + (TH.d.length + TH.g.length) + ' door(s), ' + TH.w.length + ' window(s), ' + _impN(TH.a) + ' m2 inside, ' + JSON.stringify(TH).length + ' characters') : ''));
    _impLibPost(body).then(function (x) {
      if (x.j && x.j.ok) {
        /* v177: in the place of an older reading of the same picture -- the
           map's pin of that plan then opens the new project (Marek: idealne
           by mel nahradit puvodni, kde je koupelna bez pristupovych dveri) */
        _imp.libSaved = { st: x.j.dup ? 'there' : 'saved', id: x.j.id, rooms: x.j.rooms, beds: x.j.beds, key: keyNow, replaced: x.j.replaced || 0, replacedIds: x.j.replacedIds || [] };
        _impLibSay('PLAN library: ' + (x.j.dup ? 'the same plan is there already as ' : 'saved as ') + x.j.id + ' -- ' + x.j.rooms + ' room(s), ' + x.j.beds + ' bedroom(s)' +
                (x.j.n ? (', ' + x.j.n + ' plan(s) in the library') : '') +
                (x.j.replaced ? ('; ' + x.j.replaced + ' older version(s) of the same picture taken out' + (x.j.replacedIds ? (' (' + x.j.replacedIds.join(', ') + ')') : '')) : '') +
                (body.pic && !x.j.picKept ? ' (this worker does not keep the picture\'s fingerprint yet -- w2.03)' : ''));
        if (x.j.id) { try { if (edit) _impLibProjLater(x.j.id, R, R.pBefore || '', _impDigest()); else _impLibProjLater(x.j.id, R); } catch (ePl) { _impLibSay('PLAN library: the project link THREW ' + ((ePl && ePl.message) || ePl)); } }
      } else {
        _imp.libSaved = { st: 'failed', code: (x.j && x.j.error) || ('http ' + x.s) };
        _impLibSay('PLAN library: NOT saved (HTTP ' + x.s + ') ' + (x.j ? ((x.j.error || '') + (x.j.message ? (' -- ' + x.j.message) : '')) : String(x.t || '').slice(0, 120)));
      }
    })['catch'](function (e) { _imp.libSaved = { st: 'failed', code: 'network' }; _impLibSay('PLAN library: NOT saved -- ' + ((e && e.message) || e)); });
  }
  /* THE FINISHED PROJECT WITH ITS PLAN (v147, worker w2.04, Marek: proc se
     ulozeny pudorys znovu nacita? Mel jsem za to, ze se rovnou nahraje,
     prenastavi, ze tento pudorys uz mame, bez nutnosti noveho nacitani). A
     plan taken out of the library was read into a plan and fitted to the
     product again -- the same work as the first time, and the same chances
     to go wrong. Now:
       once a plan is saved into the library, the page waits for the short
       link (?p=) its project gets when it is saved -- the address changes a
       second or two after the plan is written -- and, the project still as
       the plan left it, hands that link to the library with the product's
       name; a project changed before its link came is not handed over;
       a plan taken out of the library in the product that built it opens
       that project as it was: the house, its walls, doors, windows, rooms,
       pieces, terrace and the roof's reach over it, in the units it was
       saved in -- the buyer's own colours, roof, foundation and place stay
       his. One Undo step, RESTORE as after any plan. Where the link is
       missing, the product is another, or the project does not come, the
       plan is built from the library as before. */
  var IMP_PROJ_LINK_MS = 45000, IMP_PROJ_WAIT_MS = 12000;
  var IMP_PROJ_KEYS = ['footprint', 'polygon', 'wing', 'openings', 'segH', 'parts', 'doors', 'roomNames', 'holes'];
  /* v166: and RANCH's L corner -- the polygon alone is set aside by the build */
  var IMP_PROJ_PROD = ['fixtures', 'terrace', 'diagWalls', 'sugPark', 'sugOff', 'notch'];
  function _impShareUrl() {
    var B = global.HOUSE_BOOT || {};
    return String(B.shareWorkerUrl || 'https://leva-share.long-cloud-d006.workers.dev').replace(/\/+$/, '');
  }
  function _impLibProjLater(id, R0, p0x, digx) {
    /* v153: a change names the address before it and the plan after it */
    var t0 = Date.now(), p0 = (p0x != null) ? p0x : ((_imp.lastApply && _imp.lastApply.keptP) || ''), dig = (digx != null) ? digx : _imp.digAfter, prod = _impProdName();
    function tick() {
      if (_imp.rd !== R0 || !_imp.applied) { _impLibSay('PLAN library: plan ' + id + ' -- the plan was taken back or another came before its project was saved, so the library keeps no project for it (v147)'); return; }
      var p = _impShareId();
      if (p && p !== p0) {
        if (_impDigest() !== dig) { _impLibSay('PLAN library: plan ' + id + ' -- the project was changed before its link ?p=' + p + ' came, so the library keeps no project for it (v147)'); return; }
        _impLibPost({ action: 'project', id: id, p: p, product: prod }).then(function (x) {
          if (x.j && x.j.ok) {
            if (_imp.libSaved) _imp.libSaved.p = p;
            _impLibSay('PLAN library: plan ' + id + ' got its project ?p=' + p + (prod ? (' (' + prod + ')') : '') + ' after ' + ((Date.now() - t0) / 1000).toFixed(1) +
                       ' s -- taken out of the library in this product, it opens as it is (v147)');
          } else {
            var old = !!(x.j && x.j.error === 'bad_request' && /action/i.test(String(x.j.message || '')));
            _impLibSay('PLAN library: plan ' + id + ' -- its project ?p=' + p + ' NOT kept (HTTP ' + x.s + (x.j && x.j.error ? (' ' + x.j.error) : '') + ')' + (old ? ' -- the worker is older than w2.04' : '') + ' (v147)');
          }
        })['catch'](function (e) { _impLibSay('PLAN library: plan ' + id + ' -- its project ?p=' + p + ' NOT kept: ' + ((e && e.message) || e) + ' (v147)'); });
        return;
      }
      if (Date.now() - t0 > IMP_PROJ_LINK_MS) { _impLibSay('PLAN library: plan ' + id + ' -- no short link of the project came in ' + Math.round(IMP_PROJ_LINK_MS / 1000) + ' s (the address still ?p=' + (p || 'none') + '), so the library keeps no project for it (v147)'); return; }
      global.setTimeout(tick, 1000);
    }
    global.setTimeout(tick, 1000);
  }
  /* the project from the short link into this house: the plan's part only */
  function _impProjWrite(P) {
    var C = _impCore(), res = { ok: false, why: '', said: '', lines: [] };
    if (!C) { res.why = 'no house core'; return res; }
    var S = C.STATE, before = '', k;
    try { before = JSON.stringify(C.payload()); } catch (eP) { before = ''; }
    if (!before) { res.why = 'the project could not be copied before writing, so nothing was written'; return res; }
    var pBefore = _impShareId(), prodBefore = '';
    try { prodBefore = String(JSON.parse(before).productName || ''); } catch (eN) {}
    try { if (C.undoPush) C.undoPush(); } catch (eU1) {}
    var was = _impSum(S), digBefore = _impDigest(), said = [];
    function cp(v) { return (v == null) ? v : JSON.parse(JSON.stringify(v)); }
    try {
      if (typeof P.isMetric === 'boolean' && P.isMetric !== (S.isMetric !== false) && typeof C.toggleUnits === 'function') {
        C.toggleUnits();
        said.push('switched to ' + (P.isMetric ? 'metric' : 'imperial') + ' units, as the project was saved');
      }
      for (k = 0; k < IMP_PROJ_KEYS.length; k++) { var key = IMP_PROJ_KEYS[k]; if (P.hasOwnProperty(key) && S.hasOwnProperty(key)) S[key] = cp(P[key]); }
      var PR = S.product = S.product || {}, PP = (P.product && typeof P.product === 'object') ? P.product : {};
      for (k = 0; k < IMP_PROJ_PROD.length; k++) {
        var pk = IMP_PROJ_PROD[k];
        if (PP.hasOwnProperty(pk)) PR[pk] = cp(PP[pk]);
        else if (pk === 'fixtures') PR.fixtures = [];
        else if (pk === 'terrace') { if (PR.terrace) PR.terrace.on = false; }
        else delete PR[pk];
      }
      delete PR.planIdeas; delete PR.planPic; PR.planFrom = 'library';
      if (P.roof && P.roof.over && S.roof) S.roof.over = cp(P.roof.over);
      C.savePref();
      C.rebuild();
    } catch (e) {
      _impPutBack(before);
      res.why = 'writing it THREW ' + ((e && e.message) || e) + ' -- the project was put back';
      return res;
    }
    try { if (C.undoPush) C.undoPush(); } catch (eU2) {}
    _imp.before = before; _imp.digBefore = digBefore; _imp.digAfter = _impDigest(); _imp.prev = null; _imp.fit = null;
    _impKeepWrite({ at: Date.now(), product: prodBefore, p: pBefore, was: was, dig: digBefore, digAfter: _imp.digAfter, before: before });
    var now = _impSum(S);
    res.ok = true;
    res.said = (PR.fixtures || []).length + ' piece(s)' + (PR.terrace && PR.terrace.on ? ', the terrace' : '') + (said.length ? ('; ' + said.join('; ')) : '');
    res.lines = ['PLAN applied (v147, the project kept with the plan): was ' + _impSumTxt(was) + ' -> now ' + _impSumTxt(now) + (said.length ? (' | ' + said.join('; ')) : '')];
    return res;
  }
  /* true when the project opened (or the reading was let go meanwhile),
     false when the plan is to be built as before */
  function _impLibProject(R, id, pj) {
    var t0 = Date.now();
    return new Promise(function (resolve) {
      var over = false, tmo = 0;
      function fin(ok, why) {
        if (over) return;
        over = true;
        if (tmo) { try { global.clearTimeout(tmo); } catch (eC) {} tmo = 0; }
        if (!ok) _impSay('PLAN library: the project ?p=' + pj.p + ' did not open -- ' + why + ' -- the plan is built from the library as before (v147)');
        resolve(ok);
      }
      tmo = global.setTimeout(function () { tmo = 0; fin(false, 'no answer in ' + Math.round(IMP_PROJ_WAIT_MS / 1000) + ' s'); }, IMP_PROJ_WAIT_MS);
      global.fetch(_impShareUrl() + '/s/' + encodeURIComponent(pj.p))
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(function (P) {
          if (over) return;
          if (_imp.rd !== R || R.st !== 'reading') { fin(true); return; }
          if (!P || !P.footprint || !Array.isArray(P.parts)) { fin(false, 'the answer holds no project'); return; }
          var w = _impProjWrite(P);
          if (!w.ok) { fin(false, w.why); return; }
          R.t2 = Date.now(); R.proj = pj.p; R.projProd = pj.product || ''; R.projMs = R.t2 - t0; R.st = 'done';
          _imp.applied = true; _imp.restored = false; _imp.undone = false; _imp.applyLines = w.lines;
          /* the picture of an earlier plan no longer lies under anything */
          var T0 = _imp.traceOf;
          if (T0 && T0 !== R) {
            try { if (TRACE.img && TRACE.img === T0.imgEl) { TRACE.img = null; TRACE.armed = false; OVERLAY.repaint(); } } catch (eT) {}
            _imp.traceOf = null;
          }
          _impSay('PLAN library: plan ' + id + ' opened as its project ?p=' + pj.p + ' in ' + (R.projMs / 1000).toFixed(1) + ' s -- ' + w.said + ' (v147)');
          /* v155: the roof the words asked for, on the project as it opened */
          if (R.after && R.after.length) { try { _impAfterOps(R); } catch (eAf) { _impSay('PLAN the roof with the library plan THREW ' + ((eAf && eAf.message) || eAf)); } }
          fin(true);
          _impRefresh();
        })['catch'](function (e) { fin(false, (e && e.message) || String(e)); });
    });
  }
  /* THE SHAPES A PRODUCT TAKES FROM THE LIBRARY (v208, RANCH thread, Marek
     2026-10-12: musi se menit nejen pudorysy, ale i L / I tvary): a
     rectangle, and an L too where the product builds the L's corner as a
     covered porch (HouseLib.importNotch, RANCH) -- leva-ai w2.33 reads 'l' */
  function _impLibRect() {
    var C = _impCore();
    return (C && typeof C.importNotch === 'function') ? 'l' : true;
  }
  /* THE LIST FOR THE DRAWER: the plans this product can build, with how many
     there are for each number of rooms and of bedrooms */
  function _impLibList(cb, opts) {
    opts = opts || {};
    if (typeof global.fetch !== 'function') { if (cb) cb('no fetch', null); return false; }
    var lim = _impLimits();
    _impLibPost({ action: 'list', limits: lim, rect: _impLibRect(), rules: IMP_LIB_RULES }).then(function (x) {
      if (!x.j || !x.j.ok) {
        var why = 'HTTP ' + x.s + (x.j && x.j.error ? (' ' + x.j.error) : '');
        _impSay('PLAN library: the list did not come -- ' + why);
        if (cb) cb(why, null);
        return;
      }
      var cr = x.j.counts && x.j.counts.rooms ? x.j.counts.rooms : {}, cb2 = x.j.counts && x.j.counts.beds ? x.j.counts.beds : {};
      /* v204 (GARAGE APARTMENT, Marek: studio / 1BR, one bathroom at most):
         a product may cap the bedrooms and the whole bathrooms it is offered
         -- its own LIMITS.libMaxBeds / libMaxBaths, as libMinA (v172). The
         list is cut here; the drawer counts its chips from what is left. */
      try {
        var LMb = (_impCore() || {}).LIMITS || {}, mB = LMb.libMaxBeds, mBa = LMb.libMaxBaths;
        if (Array.isArray(x.j.items) && (typeof mB === 'number' || typeof mBa === 'number')) {
          var nAll = x.j.items.length;
          x.j.items = x.j.items.filter(function (it) {
            if (!it) return false;
            if (typeof mB === 'number' && (it.beds | 0) > mB) return false;
            if (typeof mBa === 'number' && Math.floor(Number(it.baths) || 0) > mBa) return false;
            return true;
          });
          x.j.n = x.j.items.length;
          _impSay('PLAN library: the product takes ' + (typeof mB === 'number' ? ('up to ' + mB + ' bedroom(s)') : '') +
                  (typeof mB === 'number' && typeof mBa === 'number' ? ' and ' : '') + (typeof mBa === 'number' ? ('up to ' + mBa + ' bathroom(s)') : '') +
                  ' -- ' + x.j.items.length + ' of ' + nAll + ' left (v204)');
        }
      } catch (eMB) {}
      _impSay('PLAN library: ' + x.j.n + ' of ' + x.j.of + ' plan(s) fit this product' + (lim ? (' (up to ' + _impN(lim.w) + ' x ' + _impN(lim.d) + ' m)') : '') +
              ' -- by rooms ' + Object.keys(cr).map(function (k) { return k + ': ' + cr[k]; }).join(', ') +
              ' | by bedrooms ' + Object.keys(cb2).map(function (k) { return k + ': ' + cb2[k]; }).join(', '));
      if (cb) cb(null, x.j);
    })['catch'](function (e) { var why = (e && e.message) || String(e); _impSay('PLAN library: the list did not come -- ' + why); if (cb) cb(why, null); });
    return true;
  }
  /* ONE PLAN TAKEN OUT: the road of a design, in this page's units */
  /* v144: a plan out of the library comes in a second or two; one that has
     not come in this long is given up and said so */
  var IMP_LIB_WAIT_MS = 30000;
  /* A PLAN IN THE MIRROR (v199, Marek: do edit floorplan udelat tlacitko
     zrcadleni planu; a do nacitani planu dat ikony pro nacteni leve a prave
     verze). The plan the page builds from -- the model's shape, its points
     in the pixels of its sheet -- turned over left to right about the middle
     of the sheet: every x becomes w - x, a box keeps its smaller x first.
     A door keeps its hinge and the side it opens to by its own points; a
     kitchen L, a bed, a tub follow their boxes. */
  function _impModelMirror(p, W) {
    if (!p || !(W > 0)) return p;
    var q = JSON.parse(JSON.stringify(p));
    function f(pt) { if (pt && pt.length >= 2 && isFinite(pt[0])) pt[0] = Math.round((W - Number(pt[0])) * 10) / 10; }
    function box(b) {
      var A = b && (b.a && b.b ? [b.a, b.b] : (b.length === 2 ? b : null));
      if (!A) return;
      f(A[0]); f(A[1]);
      var x0 = Math.min(A[0][0], A[1][0]), x1 = Math.max(A[0][0], A[1][0]);
      A[0][0] = x0; A[1][0] = x1;
    }
    (q.outline || []).forEach(f);
    if (q.outline) q.outline.reverse();
    (q.dims || []).forEach(function (d) { f(d.a); f(d.b); });
    (q.partitions || []).forEach(function (d) { f(d.a); f(d.b); });
    (q.doors || []).forEach(function (d) { f(d.at); f(d.hinge); f(d.opens_to); });
    (q.windows || []).forEach(function (d) { f(d.at); });
    (q.rooms || []).forEach(function (r) { f(r.at); box(r.box); });
    (q.open_sides || []).forEach(function (d) { f(d.a); f(d.b); });
    (q.fixtures || []).forEach(function (x) { box(x.box); });
    return q;
  }
  /* A PLAN IN METRES LAID ON A SHEET HERE (v199): the worker's planSheet and
     planShape in one, for a plan the page has in hand -- the plan as built
     (_impLibExport) for the mirror, a plan written for the library in the
     tests. 50 px a metre, 1.5 m round it, the outline's two sizes as
     dimension lines. Returns { plan, sheet } or null. */
  var IMP_SHEET_PPM = 50, IMP_SHEET_MARGIN = 1.5;
  function _impFlatSheet(j, feet) {
    j = j || {};
    /* on an imperial page the outline's sizes are written in feet and
       inches, so the plan is read as drawn in feet and the page keeps them */
    function txtOf(mv) {
      if (!feet) return mv.toFixed(2) + ' m';
      var inch = Math.round(mv / 0.0254), ft = Math.floor(inch / 12);
      return ft + '\'-' + (inch - ft * 12) + '"';
    }
    var S = IMP_SHEET_PPM, M = IMP_SHEET_MARGIN, i;
    function L(v) { return Array.isArray(v) ? v : []; }
    function N(v) { var n = Number(v); return isFinite(n) ? n : NaN; }
    var ol = L(j.outline).map(N), xs = [], ys = [];
    for (i = 0; i + 1 < ol.length; i += 2) if (isFinite(ol[i]) && isFinite(ol[i + 1])) { xs.push(ol[i]); ys.push(ol[i + 1]); }
    if (xs.length < 3) return null;
    var ox0 = Math.min.apply(null, xs), ox1 = Math.max.apply(null, xs), oy0 = Math.min.apply(null, ys), oy1 = Math.max.apply(null, ys);
    if (!(ox1 - ox0 > 1 && oy1 - oy0 > 1)) return null;
    var AX = xs.slice(), AY = ys.slice();
    L(j.rooms).concat(L(j.fixtures)).forEach(function (r) {
      [N(r.x0), N(r.x1)].forEach(function (v) { if (isFinite(v)) AX.push(v); });
      [N(r.y0), N(r.y1)].forEach(function (v) { if (isFinite(v)) AY.push(v); });
    });
    var mx = Math.min.apply(null, AX) - M, my = Math.min.apply(null, AY) - M;
    var W = Math.ceil((Math.max.apply(null, AX) + M - mx) * S), H = Math.ceil((Math.max.apply(null, AY) + M - my) * S);
    function R1(v) { return Math.round(v * 10) / 10; }
    function P(x, y) { var a = N(x), b = N(y); return [isFinite(a) ? R1((a - mx) * S) : 0, isFinite(b) ? R1((b - my) * S) : 0]; }
    function D(v) { var m = N(v); return isFinite(m) ? R1(m * S) : 0; }
    function S2(v) { if (Array.isArray(v)) return v.map(String); var t = String(v || ''); return t.split(/\s+\/\s+/.test(t) ? /\s+\/\s+/ : '/').map(function (q) { return q.trim(); }).filter(function (q) { return q; }); }
    var o = { outline: [] };
    for (i = 0; i < xs.length; i++) o.outline.push(P(xs[i], ys[i]));
    o.dims = [{ a: P(ox0, oy0 - 0.8), b: P(ox1, oy0 - 0.8), text: txtOf(ox1 - ox0), value_m: _impR(ox1 - ox0) },
              { a: P(ox0 - 0.8, oy0), b: P(ox0 - 0.8, oy1), text: txtOf(oy1 - oy0), value_m: _impR(oy1 - oy0) }];
    o.partitions = L(j.partitions).map(function (q) { return { a: P(q.ax, q.ay), b: P(q.bx, q.by) }; });
    o.doors = L(j.doors).map(function (d) { return { at: P(d.x, d.y), w: D(d.w), kind: String(d.kind || 'hinged'), label: '', rooms: S2(d.rooms), hinge: P(d.hx, d.hy), opens_to: P(d.ox, d.oy) }; });
    o.windows = L(j.windows).map(function (w) { return { at: P(w.x, w.y), w: D(w.w), sill_m: Number(w.sill_m) || 0, label: '' }; });
    o.rooms = L(j.rooms).map(function (r) { return { at: P(r.x, r.y), label: String(r.label || ''), type: String(r.type || 'other'), size_text: '', box: [P(r.x0, r.y0), P(r.x1, r.y1)] }; });
    o.open_sides = L(j.open_sides).map(function (q) { return { a: P(q.ax, q.ay), b: P(q.bx, q.by), rooms: S2(q.rooms) }; });
    o.fixtures = L(j.fixtures).map(function (q) {
      var r = { type: String(q.type || ''), label: String(q.label || ''), suggested: false, box: [P(q.x0, q.y0), P(q.x1, q.y1)] };
      if (Number(q.seats) > 0) r.seats = Math.round(Number(q.seats));
      if (Number(q.stools) > 0) r.stools = Math.round(Number(q.stools));
      return r;
    }).filter(function (q) { return q.type; });
    o.unreadable = []; o.noted = []; o.ideas = [];
    return { plan: o, sheet: { w: W, h: H, ppm: S, unit: feet ? 'ft' : 'm', size: [_impR(ox1 - ox0), _impR(oy1 - oy0)] } };
  }
  /* THE PROJECT ITSELF IN THE MIRROR (v199). Building the plan again from
     what the library keeps loses what only the project knows -- a niche's
     room line, a fitted fridge's turn, the terrace -- so where the house is
     a plain rectangle the project is turned over as it stands: x becomes -x
     in every wall, door, room name, piece and slanted wall; the east and
     west walls change places and the openings along the north and south
     walls run the other way; the terrace and the roof's reach over it go to
     the other side. An L, a wing, a stairwell: { ok: false } and the plan is
     built again instead. One Undo step. */
  function _impStateMirror() {
    var C = _impCore();
    if (!C || !C.STATE) return { ok: false, why: 'no house core' };
    var S = C.STATE, PR = S.product || {}, k, q;
    if (S.polygon && S.polygon.length) return { ok: false, why: 'the outline is not a rectangle' };
    if (S.wing && S.wing.on) return { ok: false, why: 'the house has a wing' };
    if (PR.notch) return { ok: false, why: 'the house has an L corner' };
    if (S.holes && S.holes.length) return { ok: false, why: 'the house has a stairwell' };
    var FP = S.footprint || {}, W = Number(FP.w) || 0, Lg = Number(FP.l) || 0;
    if (!(W > 0 && Lg > 0)) return { ok: false, why: 'no footprint' };
    var before = '';
    try { before = JSON.stringify(C.payload()); } catch (eP) { before = ''; }
    if (!before) return { ok: false, why: 'the project could not be copied first' };
    try { if (C.undoPush) C.undoPush(); } catch (eU1) {}
    var n = { parts: 0, doors: 0, open: 0, fx: 0, names: 0, diag: 0 };
    function N(v) { return Math.round(-Number(v) * 10000) / 10000; }
    try {
      /* the walls: a wall along z keeps its run and moves across; a wall
         along x runs the other way, its two ends changing places */
      var IDH = {};
      (S.parts || []).forEach(function (p) {
        if (!p) return;
        if (p.o === 'v' || p.dir === 'v') { p.c = N(p.c); if (p.at != null) p.at = N(p.at); }
        else {
          IDH[p.id] = 1;
          var s0 = p.s, e0 = p.e, a0 = p.a, b0 = p.b, sT = p.sTo, eT = p.eTo;
          if (e0 != null) p.s = N(e0); else delete p.s;
          if (s0 != null) p.e = N(s0); else delete p.e;
          if (b0 != null) p.a = N(b0);
          if (a0 != null) p.b = N(a0);
          if (eT != null) p.sTo = eT; else delete p.sTo;
          if (sT != null) p.eTo = sT; else delete p.eTo;
        }
        n.parts++;
      });
      /* the doors in them: sw bit 0 the hinge at the high end, bit 1 open to
         +z on a wall along x, to -x on a wall along z */
      (S.doors || []).forEach(function (d) {
        if (!d) return;
        var sw = d.sw | 0;
        if (IDH[d.pid]) { d.u = N(d.u); d.sw = sw ^ 1; } else d.sw = sw ^ 2;
        n.doors++;
      });
      /* the openings: W0 north west to east, W1 east north to south, W2
         south east to west, W3 west south to north -- 'at' the middle */
      (S.openings || []).forEach(function (o) {
        var m = /^(S\d+-W)(\d+)$/.exec(String(o.seg || ''));
        if (!m) return;
        var wI = parseInt(m[2], 10), len = (wI % 2) ? Lg : W;
        if (wI === 1) o.seg = m[1] + '3'; else if (wI === 3) o.seg = m[1] + '1';
        o.at = Math.round((len - Number(o.at)) * 10000) / 10000;
        if (o.hand === 'L') o.hand = 'R'; else if (o.hand === 'R') o.hand = 'L';
        n.open++;
      });
      var SH = S.segH || {}, SH2 = {};
      for (k in SH) if (SH.hasOwnProperty(k)) { var mk = /^(S\d+-W)(\d+)$/.exec(k); SH2[mk ? (mk[1] + ({ 1: 3, 3: 1 }[mk[2]] || mk[2])) : k] = SH[k]; }
      S.segH = SH2;
      (S.roomNames || []).forEach(function (r) { if (r && r.x != null) { r.x = N(r.x); n.names++; } });
      /* the pieces: across, their back east for west, and an L the other hand */
      (PR.fixtures || []).forEach(function (f) {
        if (!f) return;
        f.x = N(f.x);
        var r = (f.rot | 0) % 4;
        if (r === 1) f.rot = 3; else if (r === 3) f.rot = 1;
        if (f.mirror) delete f.mirror; else f.mirror = true;
        n.fx++;
      });
      (PR.sugPark || []).forEach(function (f) { if (f && f.x != null) { f.x = N(f.x); var r2 = (f.rot | 0) % 4; if (r2 === 1) f.rot = 3; else if (r2 === 3) f.rot = 1; if (f.mirror) delete f.mirror; else f.mirror = true; } });
      /* slanted walls: their ends across, their doors open to the other hand */
      (PR.diagWalls || []).forEach(function (g) {
        if (!g) return;
        if (g.a) g.a[0] = N(g.a[0]);
        if (g.b) g.b[0] = N(g.b[0]);
        (g.doors || []).forEach(function (d) { d.sw = (d.sw | 0) ^ 2; });
        n.diag++;
      });
      /* the terrace: its side, and its rectangle where it has one (the
         configurator draws that), across the house */
      var T = PR.terrace;
      if (T) {
        if (T.side === 'x0') T.side = 'x1'; else if (T.side === 'x1') T.side = 'x0';
        else if (T.off != null && Number(T.w) > 0) T.off = Math.round(Math.max(0, W - Number(T.off) - Number(T.w)) * 10000) / 10000;
        if (T.x0 != null && Number(T.sx) > 0) T.x0 = Math.round(-(Number(T.x0) + Number(T.sx)) * 10000) / 10000;
      }
      if (S.roof && S.roof.over) { var ov = S.roof.over, x0 = ov.x0; ov.x0 = ov.x1; ov.x1 = x0; }
      C.savePref();
      C.rebuild();
    } catch (e) {
      _impPutBack(before);
      return { ok: false, why: 'turning it over THREW ' + ((e && e.message) || e) + ' -- the project was put back' };
    }
    try { if (C.undoPush) C.undoPush(); } catch (eU2) {}
    return { ok: true, said: n.parts + ' wall(s), ' + n.doors + ' door(s), ' + n.open + ' window(s) and outer door(s), ' + n.fx + ' piece(s), ' + n.names + ' room name(s)' + (n.diag ? (', ' + n.diag + ' slanted wall(s)') : '') + (PR.terrace && PR.terrace.on ? ', the terrace' : '') };
  }
  /* a plan on its sheet built as a plan from the library is built */
  function _impBuildSheet(R, plan, sheet) {
    R.res = { ok: true, plan: plan, sheet: sheet }; R.t2 = Date.now();
    R.sheet = sheet; R.ideas = [];
    var tB = Date.now();
    R.conv = _impFromModel(plan);
    _impRenorm(R);
    R.libBuilt = Date.now() - tB;
    R.st = 'done';
    _imp.applied = false; _imp.restored = false; _imp.undone = false; _imp.applyLines = null;
  }
  /* the plan in this project turned over, built again and written as a plan
     from the library is -- one Undo step, nothing saved to the library */
  function _impMirrorNow() {
    var ex = null, C = _impCore(), S = C ? C.STATE : null;
    try { ex = _impLibExport(); } catch (eX) { ex = null; _impSay('PLAN mirror (v199): writing the plan out THREW ' + ((eX && eX.message) || eX)); }
    if (!ex || !ex.plan) { _impSay('PLAN mirror (v199): there is no plan to turn over'); return false; }
    var sh = _impFlatSheet(ex.plan, !!(S && S.isMetric === false));
    if (!sh) { _impSay('PLAN mirror (v199): the plan could not be laid on a sheet'); return false; }
    var R = _imp.rd = { st: 'reading', name: 'the plan mirrored', lib: 'mirror', mirror: true, model: 'mirror', t0: Date.now(), t1: Date.now(),
                        units: S ? (S.isMetric === false ? 'imperial' : 'metric') : '', matched: null, after: [], afterText: '', pre: null };
    _imp.prev = null; _imp.libSaved = null;
    _impBuildSheet(R, _impModelMirror(sh.plan, sh.sheet.w), sh.sheet);
    _impSay('PLAN mirror (v199): the plan as built turned over left to right -- ' + (ex.said || '') + ' -- built again, to be written as a plan from the library (Undo takes it back)');
    _impRefresh();
    return true;
  }
  /* LEVA'S OWN PLANS INTO THE LIBRARY (v199, worker w2.29; Marek: mohl bys
     vygenerovat sady pudorysu pro ADU, ktere by pokryvaly ruzne varianty --
     admin odkaz na tabletu). The plans are kept in the worker. ?planseed=1
     in the address of a product page, signed in as an admin: the page asks
     the worker for each in turn, builds it as a plan from the library is
     built, writes it, and the library check and save follow as after any
     plan -- LEVA's own, with the drawer's picture and its project. The next
     one comes once the save has its project, or after 45 s. ?planseed=5
     starts at the fifth. The log says what came of each and sums it up. */
  /* v205 (Marek 2026-10-12: the list holds several products' plans now --
     21-26 the barndo's, 27-36 the RANCH's): ?planseed=21-26 stops after the
     26th, so a page puts in only its own; ?planseed=N alone still runs to the
     end of the list */
  var IMP_SEED_WAIT_MS = 45000, _impSeed = null;
  /* THE REF BUTTON SHUT WHILE THE SEED RUNS (v207, Marek 2026-10-12: zase
     se spustil jen jeden). REF pressed during the seed builds its own plans
     and ends by going back to the project, which ends the seed: twice only
     one or two plans went in. While LEVA's plans go in, the button is shut
     and counts them (SEED 30/36); at the end it opens again as
     REF (SEED 8/8) -- the number saved of the number tried. */
  function _impSeedBtn(end) {
    try {
      var SD = _impSeed, b = global.document ? global.document.getElementById('cf-house-ref') : null;
      if (!SD || !b) return;
      if (!end) {
        b.disabled = true;
        b.setAttribute('data-send', 'busy');
        b.textContent = 'SEED ' + (SD.i + 1) + '/' + (SD.to != null ? (SD.to + 1) : (SD.n || '?'));
        return;
      }
      var ok = SD.res.filter(function (r) { return /^saved|^there/.test(r.st); }).length;
      b.disabled = false;
      b.setAttribute('data-send', ok === SD.res.length && ok ? 'ok' : 'fail');
      b.textContent = 'REF (SEED ' + ok + '/' + SD.res.length + ')';
    } catch (eB) {}
  }
  function _impSeedRun(from, to) {
    if ((_impSeed && _impSeed.on) || typeof global.fetch !== 'function') return false;
    _impSeed = { on: true, i: Math.max(0, from | 0), to: (to != null && to >= 0) ? (to | 0) : null, n: 0, res: [], t0: Date.now() };
    _impSay('PLAN SEED (v199): LEVA\'s plans into the library, from plan ' + (_impSeed.i + 1) + (_impSeed.to != null ? (' to plan ' + (_impSeed.to + 1) + ' (v205)') : ' to the end of the list') + ' -- the REF button shut until it is over (v207)');
    _impSeedNext();
    return true;
  }
  function _impSeedEnd(why) {
    var SD = _impSeed;
    if (!SD) return;
    SD.on = false;
    var ok = SD.res.filter(function (r) { return /^saved|^there/.test(r.st); }).length;
    _impSay('PLAN SEED (v199): ' + why + ' -- ' + ok + ' of ' + SD.res.length + ' saved in ' + Math.round((Date.now() - SD.t0) / 1000) + ' s | ' +
            SD.res.map(function (r) { return (r.i + 1) + ' ' + r.name + ': ' + r.st; }).join(' | '));
    _impSeedBtn(true);
    try { _impRefresh(); } catch (eR) {}
  }
  function _impSeedNext() {
    var SD = _impSeed;
    if (!SD || !SD.on) return;
    _impSeedBtn(false);
    if (SD.to != null && SD.i > SD.to) { _impSeedEnd('plans up to ' + (SD.to + 1) + ' gone through (v205)'); return; }
    var C = _impCore(), S = C ? C.STATE : null, units = S ? (S.isMetric === false ? 'imperial' : 'metric') : '';
    _impLibPost({ action: 'seed', i: SD.i, units: units }).then(function (x) {
      if (!x.j || !x.j.ok) { _impSeedEnd('the worker said HTTP ' + x.s + ' ' + ((x.j && (x.j.error || '') + (x.j.message ? (' -- ' + x.j.message) : '')) || '')); return; }
      SD.n = x.j.n || SD.n;
      _impSeedBtn(false);
      if (x.j.done || !x.j.plan) { _impSeedEnd('all ' + SD.n + ' plan(s) gone through'); return; }
      var nm = String(x.j.name || ('plan ' + (SD.i + 1)));
      var R = _imp.rd = { st: 'reading', name: nm, seed: SD.i, seedKey: String(x.j.key || ''), lib: 'LEVA ' + String(x.j.key || (SD.i + 1)), model: 'seed', t0: Date.now(), t1: Date.now(), units: units, matched: null, after: [], afterText: '', pre: null };
      _imp.prev = null; _imp.libSaved = null; _imp.libNow = null;
      /* v208 (RANCH thread, Marek 2026-10-12: vsechny stavby maji zapnute
         vikyre): the plan's own project is saved with the page as it stands,
         and a visitor who picks the plan gets that project -- the admin's
         cross gables, pool and blinds came with it. A product that has a
         plain look for its library plans (HouseLib.seedLook) sets it first. */
      try { var Csl = _impCore(); if (Csl && typeof Csl.seedLook === 'function') _impSay('PLAN SEED (v208): ' + (Csl.seedLook() || 'the product set its plain look')); } catch (eSL) { _impSay('PLAN SEED (v208): the product\'s plain look THREW ' + ((eSL && eSL.message) || eSL)); }
      _impUserBegin(null);
      _impBuildSheet(R, x.j.plan, x.j.sheet || x.j.plan.sheet || null);
      R.res.version = x.j.version || ''; R.libHttp = x.s; R.libAns = Date.now(); R.libChars = 0;
      _impRefresh();
      var put = false;
      try { put = IMPORTER.user.apply(); } catch (eA) { _impSay('PLAN SEED (v199): ' + nm + ' -- writing it THREW ' + ((eA && eA.message) || eA)); }
      _impSay('PLAN SEED (v199): ' + (SD.i + 1) + '/' + SD.n + ' "' + nm + '" -- ' + (put ? 'written' : ('NOT written: ' + ((_imp.fit && _imp.fit.why) || (R.n && R.n.why) || '?'))));
      var t1 = Date.now();
      (function wait() {
        var LS = _imp.libSaved, st = '';
        if (!put) st = 'not written';
        else if (LS && (LS.st === 'refused' || LS.st === 'failed')) st = LS.st + (LS.why ? (' (' + LS.why.join('; ') + ')') : (LS.code ? (' (' + LS.code + ')') : ''));
        else if (LS && (LS.st === 'saved' || LS.st === 'there') && LS.p) st = LS.st + ' as ' + LS.id + ' with ?p=' + LS.p;
        else if (Date.now() - t1 > IMP_SEED_WAIT_MS) st = LS ? (LS.st + (LS.id ? (' as ' + LS.id) : '') + ', no project in ' + Math.round(IMP_SEED_WAIT_MS / 1000) + ' s') : 'no answer from the library';
        if (!st) { global.setTimeout(wait, 1000); return; }
        SD.res.push({ i: SD.i, name: nm, st: st });
        _impSay('PLAN SEED (v199): ' + (SD.i + 1) + '/' + SD.n + ' "' + nm + '" -- ' + st);
        SD.i++;
        global.setTimeout(_impSeedNext, 800);
      })();
    })['catch'](function (e) { _impSeedEnd('the worker could not be asked: ' + ((e && e.message) || e)); });
  }
  /* the test bench: a plan in metres built, written and checked here --
     what the library check would say, without saving it */
  function _impFlatTest(flat, mirror) {
    var C = _impCore(), S = C ? C.STATE : null;
    var sh = _impFlatSheet(flat, !!(S && S.isMetric === false));
    if (!sh) return { ok: false, why: 'no sheet' };
    var R = _imp.rd = { st: 'reading', name: 'test', lib: 'test', model: 'seed', t0: Date.now(), t1: Date.now(), units: S ? (S.isMetric === false ? 'imperial' : 'metric') : '', matched: null, after: [], afterText: '', pre: null };
    _imp.prev = null; _imp.libSaved = null;
    _impUserBegin(null);
    _impBuildSheet(R, mirror ? _impModelMirror(sh.plan, sh.sheet.w) : sh.plan, sh.sheet);
    _impRefresh();
    var put = false;
    try { put = IMPORTER.user.apply(); } catch (eA) { return { ok: false, why: 'apply threw ' + ((eA && eA.message) || eA) }; }
    var G = null;
    delete R.lib;
    try { G = _impLibGrade(R); } catch (eG) { G = { ok: false, why: ['grade threw ' + ((eG && eG.message) || eG)] }; }
    R.lib = 'test';
    /* each piece and what it stands in, if anything -- the zone's box too */
    var CL = [], ZL = [];
    try {
      var Zt = _fitZones(), Lt = _fixturesRef();
      for (var iz = 0; iz < Zt.length; iz++) { var zz = Zt[iz]; if (zz) ZL.push({ t: zz.t, why: zz.why || '', x0: zz.x0, x1: zz.x1, z0: zz.z0, z1: zz.z1 }); }
      for (var it = 0; it < Lt.length; it++) {
        var ft = Lt[it], Ft = _impFaces(ft.x, ft.z), ct = null;
        try { ct = _fitClash(ft, Ft, _impZonesFor(ft, Zt), null, ft.id); } catch (eCt) { ct = { reason: 'threw' }; }
        var rt = _fixRect(ft), hz = null;
        if (ct && (ct.reason === 'door' || ct.reason === 'window')) for (var zt = 0; zt < Zt.length; zt++) if (Zt[zt].t === ct.reason && _fitHit(rt, Zt[zt], 0.005)) { hz = Zt[zt]; break; }
        CL.push({ kind: ft.kind, x: ft.x, z: ft.z, rot: ft.rot, r: rt, clash: ct, zone: hz });
      }
    } catch (eCL) { CL = [{ err: String((eCL && eCL.message) || eCL) }]; }
    return { ok: !!put, n: R.n, fit: _imp.fit, audit: _imp.audit, grade: G, lines: (_imp.applyLines || []).slice(), pieces: CL, zones: ZL };
  }
  function _impLibLoad(id, opts) {
    if (typeof global.fetch !== 'function') return false;
    var C = _impCore(), S = C ? C.STATE : null, units = S ? (S.isMetric === false ? 'imperial' : 'metric') : '';
    var R = _imp.rd = { st: 'reading', name: 'plan library ' + id.slice(0, 6), lib: id, model: 'library', t0: Date.now(), t1: Date.now(), units: units,
                        /* v154: picked for the buyer's words; v155: the roof they asked for */
                        matched: (opts && opts.matched) || null, after: (opts && Array.isArray(opts.after)) ? opts.after : [], afterText: (opts && opts.afterText) || '',
                        /* v159: the change it came for, on top of its lines */
                        pre: (opts && Array.isArray(opts.pre)) ? opts.pre : null, mirror: !!(opts && opts.mirror) };
    /* v159: the plan taken out of the library is the plan now -- a change to
       it is not answered with it again */
    _imp.libNow = id;
    _imp.prev = null; _imp.libSaved = null;
    _impRefresh();
    var tmo = 0;
    try {
      tmo = global.setTimeout(function () {
        tmo = 0;
        if (_imp.rd !== R || R.st !== 'reading') return;
        _impReadFail(R, 'the plan library did not answer in ' + Math.round(IMP_LIB_WAIT_MS / 1000) + ' s (asked at ' + _impClock(R.t1) + ') -- nothing came back from the worker (v144)');
      }, IMP_LIB_WAIT_MS);
    } catch (eT) { tmo = 0; }
    _impLibPost({ action: 'get', id: id, units: units }).then(function (x) {
      if (tmo) { try { global.clearTimeout(tmo); } catch (eC) {} tmo = 0; }
      R.libAns = Date.now(); R.libHttp = x.s; R.libChars = String(x.t || '').length;
      if (_imp.rd !== R) return;
      if (R.st !== 'reading') { _impSay('PLAN library: plan ' + id + ' answered after ' + ((R.libAns - R.t1) / 1000).toFixed(1) + ' s, when it had been given up -- set aside (v144)'); return; }
      if (!x.j || !x.j.ok || !x.j.plan) throw new Error('HTTP ' + x.s + ' ' + (x.j ? ((x.j.error || '') + (x.j.message ? (' -- ' + x.j.message) : '')) : String(x.t || '').slice(0, 120)));
      /* v199: the right-hand version -- the plan turned over on its sheet */
      var shW = (x.j.sheet && x.j.sheet.w) || (x.j.plan.sheet && x.j.plan.sheet.w) || 0;
      if (R.mirror && shW > 0) x.j.plan = _impModelMirror(x.j.plan, shW);
      else if (R.mirror) R.mirror = false;
      var build = function () {
      R.res = x.j; R.t2 = Date.now();
      R.sheet = x.j.sheet || x.j.plan.sheet || null;
      R.ideas = [];
      var tB = Date.now();
      R.conv = _impFromModel(x.j.plan);
      _impRenorm(R);
      R.libBuilt = Date.now() - tB;
      R.st = 'done';
      _imp.applied = false; _imp.restored = false; _imp.undone = false; _imp.applyLines = null;
      var mt = x.j.meta || {};
      _impSay('PLAN library: plan ' + id + (R.mirror ? ' MIRRORED (v199)' : '') + ' taken out -- ' + (mt.rooms || '?') + ' room(s), ' + (mt.beds != null ? mt.beds : '?') + ' bedroom(s), ' +
              (mt.w ? (_impN(mt.w) + ' x ' + _impN(mt.d) + ' m') : '') + (mt.leva ? ', LEVA' : '') + ' -- laid on a sheet in ' + (units || 'metric') + ' units');
      _impRefresh();
      };
      /* v147: the project built from it, in the product that built it */
      var pj = x.j.proj, here = _impProdName();
      /* v199: wanted mirrored -- a rectangle's project opens and is turned
         over as it stands; any other shape is built from its plan turned over */
      var rectP = !!(x.j.plan && x.j.plan.outline && x.j.plan.outline.length === 4);
      if (pj && pj.p && R.mirror && !rectP) _impSay('PLAN library: plan ' + id + ' comes with its project ?p=' + pj.p + ', but it is wanted mirrored and is no rectangle -- built from the plan turned over (v199)');
      else if (pj && pj.p && here && String(pj.product || '').toLowerCase() === here.toLowerCase()) {
        R.res = x.j;
        _impSay('PLAN library: plan ' + id + ' comes with its project ?p=' + pj.p + ' (' + pj.product + ') -- opening it as it is, nothing built again (v147)' + (R.mirror ? ', then turned over (v199)' : ''));
        return _impLibProject(R, id, pj).then(function (ok) {
          if (!ok && _imp.rd === R && R.st === 'reading') { build(); return; }
          if (ok && R.mirror && _imp.rd === R) {
            var M = _impStateMirror();
            _impSay('PLAN library: plan ' + id + (M.ok ? (' turned over left to right as it opened -- ' + M.said) : (' could not be turned over (' + M.why + ') -- it stays as saved')) + ' (v199)');
            try { _impRefresh(); } catch (eRf) {}
          }
        });
      }
      if (pj && pj.p) _impSay('PLAN library: plan ' + id + ' comes with a project of ' + (pj.product || 'another product') + ', this is ' + (here || 'a product with no name') + ' -- the plan is built here (v147)');
      build();
    })['catch'](function (e) {
      if (tmo) { try { global.clearTimeout(tmo); } catch (eC2) {} tmo = 0; }
      _impReadFail(R, 'the plan library: ' + ((e && e.message) || e) + (R.libHttp ? (' (the answer HTTP ' + R.libHttp + ', ' + R.libChars + ' characters)') : ' (no answer)'));
    });
    return true;
  }
  /* one of one's own out of the library again (an admin: any) */
  function _impLibDrop(id, cb) {
    if (!_impLibIdOk(id) || typeof global.fetch !== 'function') return false;
    _impLibPost({ action: 'drop', id: String(id) }).then(function (x) {
      var ok = !!(x.j && x.j.ok);
      _impSay('PLAN library: plan ' + id + (ok ? (' taken out of the library -- ' + x.j.n + ' left') : (' NOT taken out (HTTP ' + x.s + (x.j && x.j.error ? (' ' + x.j.error) : '') + ')')));
      if (cb) cb(ok ? null : ((x.j && x.j.error) || ('http ' + x.s)), x.j);
    })['catch'](function (e) { if (cb) cb((e && e.message) || String(e), null); });
    return true;
  }
  /* ---------- A CHANGE TO THE PLAN THERE IS (v123) ------------------------
     Marek: kdyz mame uz nacteny floorplan, uzivatel zadava dalsi instrukce a
     agent dale upravuje stavajici floorplan (napr. prosklene HS portaly na
     terasu). The project is described for the designer in metres on the
     plan's own frame -- x east from the outer face of the west wall, y south
     from the outer face of the north wall, north at the top -- every part
     with an id: the walls N E S W, the openings in them, the partitions and
     their doors, the rooms, the pieces, the terrace, and the widths the
     catalogue sells. The worker (w1.92) answers operations; they are written
     here, each by the core's own rules -- the catalogue's nearest unit, the
     stud face, the free stretch of a partition, the placer for a piece --
     all of them as ONE Undo step, and every one is said in the log: done, or
     refused and why. The ids are tied to the parts themselves when the
     project is sent, so a part moved meanwhile is found, and one deleted
     meanwhile is refused. */
  var IMP_CHG_HIST = [];
  function _impChgHist(t) {
    t = String(t || '').trim();
    if (!t) return;
    IMP_CHG_HIST.push(t.slice(0, 300));
    while (IMP_CHG_HIST.length > 3) IMP_CHG_HIST.shift();
  }
  var IMP_CHG_REV = (function () {
    var o = {}, t, k;
    for (t in IMP_TYPE_KEY) if (IMP_TYPE_KEY.hasOwnProperty(t)) { k = IMP_TYPE_KEY[t][0]; if (!o[k]) o[k] = t; }
    return o;
  })();
  var IMP_CHG_SIDE = { N: 'top', E: 'right', S: 'bottom', W: 'left' };
  /* the wall letters onto the core's sides of the outline (v124): the roof's
     reach and the terrace are kept by side */
  var IMP_CHG_OVK = { N: 'z0', S: 'z1', W: 'x0', E: 'x1' };
  var IMP_CHG_OVL = { z0: 'N', z1: 'S', x0: 'W', x1: 'E' };
  /* ---------- THE ROOF, THE TERRACE, THE LANGUAGE (v124) ------------------
     Marek: proc by nesla zmenit strecha na atiku, kdyz ji mame ve variantach
     ADU? The designer is told the roof -- its type, its pitch, how far it
     reaches past each wall and the types this product offers -- and the
     terrace, and may change them. The types come from the core when it says
     (roofOffer, core-v551); before that, the line's four. */
  var IMP_ROOFS = ['gable', 'hip', 'mono', 'flat'];
  function _impRoofOffer() {
    var C = _impCore(), L = null;
    try { L = (C && typeof C.roofOffer === 'function') ? C.roofOffer() : null; } catch (e) { L = null; }
    L = (L || []).filter(function (k) { return IMP_ROOFS.indexOf(k) >= 0; });
    if (!L.length) return { list: IMP_ROOFS.slice(), from: 'the line\'s four -- the core names none' };
    return { list: L, from: 'the product' };
  }
  /* the page's language, for the designer's ideas */
  function _impLang() {
    try { var h = global.document && global.document.documentElement; return String((h && h.getAttribute('lang')) || 'en').slice(0, 12); } catch (e) { return 'en'; }
  }
  /* what the configurator can build, for the designer's ideas */
  function _impOffer() {
    var C = _impCore(), S = C ? C.STATE : null;
    return { roofs: _impRoofOffer().list, roof: (S && S.roof && S.roof.type) || '', terrace: true };
  }
  /* a redesign or a new house: the words go on to the designer (v124) */
  /* v153 (worker w2.08): the roof and the terrace may come beside a
     redesign or a new design -- the rest of the answer may not */
  var IMP_CHG_OUTSIDE = ['roof_type', 'roof_overhang', 'terrace_set', 'terrace_remove'];
  function _impChgSolo(chg) {
    if (!chg || !chg.ops || !chg.ops.length) return '';
    var solo = '', i, op;
    for (i = 0; i < chg.ops.length; i++) {
      op = chg.ops[i].op;
      if (op === 'redesign' || op === 'new_design') { if (!solo) solo = op; }
      else if (IMP_CHG_OUTSIDE.indexOf(op) < 0) return '';
    }
    return solo;
  }
  /* ---------- THE IDEAS (v124) --------------------------------------------
     Marek: v ramci tokenu floorplanu by se mohla vygenerovat i nejaka otazka
     -- "Pridat presah strechy a pod nim terasu?" -- bublina nad ikonou
     floorplanu, user rozklikne a muze editovat. The reading and the design
     each bring one or two (worker w1.93), in the page's language. Once the
     plan is written they go with the project (product.planIdeas), so a
     reload still offers them; the page takes one off when it is used or
     dismissed (ideaDone), and the next one shows. */
  function _impIdeasClean(v) {
    var out = [], i, t;
    for (i = 0; Array.isArray(v) && i < v.length && out.length < 2; i++) {
      t = String(v[i] == null ? '' : v[i]).replace(/\s+/g, ' ').trim();
      if (t && t.length <= 160 && out.indexOf(t) < 0) out.push(t);
    }
    return out;
  }
  function _impIdeasNow() {
    var C = _impCore(), PR = (C && C.STATE && C.STATE.product) || {};
    return _impIdeasClean(PR.planIdeas);
  }
  function _impIdeasSet(list, why) {
    var C = _impCore();
    if (!C || !C.STATE) return;
    var PR = C.STATE.product = C.STATE.product || {}, L = _impIdeasClean(list);
    if (L.length) PR.planIdeas = L; else delete PR.planIdeas;
    try { C.savePref(); } catch (eS) {}
    _impSay('PLAN ideas ' + (L.length ? (L.length + ' for the bubble (' + why + '): ' + L.map(function (q) { return '"' + q + '"'; }).join(' | ')) : ('none left (' + why + ')')));
  }
  function _impIdeasLine(R) {
    var L = (R && R.ideas) || [];
    return 'PLAN ideas from the designer: ' + (L.length ? (L.length + ' in ' + _impLang() + ' -- ' + L.map(function (q) { return '"' + q + '"'; }).join(' | ') + ' -- they go to the bubble once the plan is written')
                                                       : 'none' + ((R && R.res && R.res.version && !(R.res.ideas)) ? (' (worker ' + R.res.version + ' sends none)') : ''));
  }
  /* the pieces' usual sizes, for picking the catalogue piece of a type */
  var IMP_CHG_FIXSZ = { bathtub: [1.7, 0.75], shower: [0.9, 0.9], toilet: [0.7, 0.4], basin: [0.6, 0.5], kitchen: [2.4, 0.6], kitchen_l: [2.4, 1.8],
                        kitchen_u: [2.4, 2.4], island: [1.8, 0.9], fridge: [0.65, 0.6], washer: [0.6, 0.6], dryer: [0.6, 0.6], water_heater: [0.6, 0.6],
                        bed: [2.05, 1.6], sofa: [2.1, 0.9], table: [1.6, 0.9], desk: [1.2, 0.6], wardrobe: [1.5, 0.6] };
  function _impChgOpTxt(o) {
    var t = o.op + (o.ref ? (' ' + o.ref) : '') + (o.wall ? (' ' + o.wall) : '');
    if (o.op === 'opening_add' || o.op === 'opening_change' || o.op === 'door_add' || o.op === 'door_change') t += (o.at ? (' at ' + _impN(o.at)) : '') + (o.w ? (' w ' + _impN(o.w)) : '') + (o.kind ? (' ' + o.kind) : '');
    else if (o.op === 'partition_add') t += ' ' + _impN(o.ax) + ',' + _impN(o.ay) + ' -> ' + _impN(o.bx) + ',' + _impN(o.by);
    else if (o.op === 'partition_move') t += ' to ' + _impN(o.at);
    else if (o.op === 'room_rename') t += ' "' + o.name + '"' + (o.kind ? (' ' + o.kind) : '');
    else if (o.op === 'fixture_add') t += ' ' + o.kind;
    else if (o.op === 'roof_type') t += ' ' + o.kind + (o.pitch ? (' ' + o.pitch + ' deg') : '');
    else if (o.op === 'roof_overhang' && o.ref === 'terrace' && !o.wall) t = (o.kind === 'open') ? 'the terrace left open' : 'roof_overhang over the terrace';
    else if (o.op === 'roof_overhang') t += (o.wall ? '' : ' all four') + ' d ' + _impN(o.d || 0);
    else if (o.op === 'terrace_set') t += (o.at ? (' at ' + _impN(o.at)) : '') + (o.w ? (' w ' + _impN(o.w)) : '') + (o.d ? (' d ' + _impN(o.d)) : '') + (o.kind ? (' ' + o.kind) : '');
    else if (o.op === 'redesign') t += ' "' + String(o.name || '').slice(0, 100) + '"';
    return t;
  }
  /* the catalogue's units of a kind: window, door (entry) or glass_door */
  /* A SLIDING DOOR IS A SLIDING DOOR (v131, Marek: navrh byl nahradit okno
     sliding doors -- objevily se jednokridle 39, a kdyz uz navrhne sliding
     doors, tak by tam mely byt sliding doors). A glazed door asked for came
     from the whole glazed group by its width alone, and at 39 inches the
     nearest was the single French door. sliding_door takes the sliders of
     that group only -- the HS sliding doors -- the narrowest of them where a
     narrower one is asked for; glass_door is still the whole glazed group,
     the nearest by width. */
  function _impIsSlider(it) { return !!(it && /slid/i.test(String(it.name || ''))); }
  function _impChgGroup(kind) {
    var U = global.ConfiguratorUI, grp = (kind === 'window') ? 'window' : ((kind === 'glass_door' || kind === 'sliding_door') ? 'french' : 'door'), G = null;
    try { G = (U && typeof U.openingGroup === 'function') ? U.openingGroup(grp) : null; } catch (e) { G = null; }
    var items = [], i, it, sl = 0;
    for (i = 0; G && G.items && i < G.items.length; i++) if (_impIsSlider(G.items[i])) sl++;
    for (i = 0; G && G.items && i < G.items.length; i++) {
      it = G.items[i];
      if (!it || !(it.w > 0) || it.full || it.round || it.hand === 'R') continue;
      /* the sliders only, for a sliding door -- where the catalogue has them */
      if (sl && kind === 'sliding_door' && !_impIsSlider(it)) continue;
      items.push(it);
    }
    return { grp: grp, items: items };
  }
  function _impChgWidths() {
    var out = {}, ks = ['window', 'door', 'glass_door', 'sliding_door'], i, k;
    for (k = 0; k < ks.length; k++) {
      var g = _impChgGroup(ks[k]), seen = {}, L = [];
      for (i = 0; i < g.items.length; i++) { var w = _impR(g.items[i].w); if (!seen[w]) { seen[w] = 1; L.push(w); } }
      L.sort(function (a, b) { return a - b; });
      out[ks[k]] = L;
    }
    return out;
  }
  function _impChgUnit(kind, w) {
    var g = _impChgGroup(kind), best = null, bd = 1e9, bh = 1e9, i, it, HEAD = 2.30;
    for (i = 0; i < g.items.length; i++) {
      it = g.items[i];
      var d = Math.abs(it.w - w), sill = (g.grp === 'window') ? ((it.sill != null) ? it.sill : 0.9) : 0, hd = Math.abs(sill + (it.h || 0) - HEAD);
      if (d < bd - 1e-6 || (Math.abs(d - bd) <= 1e-6 && hd < bh)) { bd = d; bh = hd; best = it; }
    }
    if (!best) return null;
    var sl = (g.grp === 'window') ? ((best.sill != null) ? best.sill : 0.9) : 0;
    return { kind: g.grp, code: best.code, w: best.w, h: best.h, hand: best.hand || null, leaves: best.leaves || null, sill: sl };
  }
  /* is this glazed opening one of the catalogue's sliders (v131) */
  function _impOpSlides(o) {
    if (!o || o.kind !== 'french') return false;
    var U = global.ConfiguratorUI, G = null, i;
    try { G = (U && typeof U.openingGroup === 'function') ? U.openingGroup('french') : null; } catch (e) { G = null; }
    for (i = 0; G && G.items && i < G.items.length; i++) if (G.items[i].code === o.code) return _impIsSlider(G.items[i]);
    return false;
  }
  /* the project, as the designer is told it, and the parts behind each id */
  function _impChgProject() {
    var C = _impCore(), H = _PH, PLb = global.FloorplanLib && global.FloorplanLib.plan;
    if (!C || !C.STATE || !H || !H.poly) return null;
    var S = C.STATE, st = S.planStorey | 0, P = H.poly(), i, k;
    if (!P || P.length < 3) return null;
    var F = { x0: 1e9, x1: -1e9, z0: 1e9, z1: -1e9 }, oC = _impOut();
    for (i = 0; i < P.length; i++) { F.x0 = Math.min(F.x0, P[i][0]); F.x1 = Math.max(F.x1, P[i][0]); F.z0 = Math.min(F.z0, P[i][1]); F.z1 = Math.max(F.z1, P[i][1]); }
    function X(x) { return _impR(x - F.x0); }
    function Y(z) { return _impR(z - F.z0); }
    var M = { F: F, st: st, seg: {}, side: {}, o: [], p: [], span: [], d: [], r: [], f: [], out: oC };
    var proj = { size: { w: _impR(F.x1 - F.x0), d: _impR(F.z1 - F.z0) }, walls: [], openings: [], partitions: [], doors: [], rooms: [], fixtures: [], outdoor: [],
                 catalog: _impChgWidths() };
    /* the walls: which side each is, and "at" from its west or north end */
    for (i = 0; i < P.length; i++) {
      var sg = C.segment ? C.segment('S' + st + '-W' + i) : null;
      if (!sg) continue;
      var hor = Math.abs(sg.z1 - sg.z0) < 0.02, sd = '';
      if (hor) sd = (Math.abs(sg.z0 - F.z0) < 0.05) ? 'N' : ((Math.abs(sg.z0 - F.z1) < 0.05) ? 'S' : '');
      else if (Math.abs(sg.x1 - sg.x0) < 0.02) sd = (Math.abs(sg.x0 - F.x0) < 0.05) ? 'W' : ((Math.abs(sg.x0 - F.x1) < 0.05) ? 'E' : '');
      if (!sd || M.side[sd]) continue;
      /* a copy, so nothing is written onto the core's own segment */
      var sgc = { id: sg.id, len: sg.len, h: sg.h, __flip: hor ? (sg.x0 > sg.x1) : (sg.z0 > sg.z1), __o: oC };
      M.seg[sg.id] = { sg: sgc, side: sd };
      M.side[sd] = sgc;
      proj.walls.push({ id: sd, len: _impR(sg.len + 2 * oC) });
    }
    /* v170: told over the outer faces where the wall reaches past the
       outline -- the size, the walls and every point; the map keeps the
       frame, so the changes come back onto the axis */
    if (oC) { F.x0 -= oC; F.x1 += oC; F.z0 -= oC; F.z1 += oC; proj.size = { w: _impR(F.x1 - F.x0), d: _impR(F.z1 - F.z0) }; }
    function atOut(sg, at) { return _impR((sg.__flip ? (sg.len - at) : at) + (sg.__o || 0)); }
    var OL = S.openings || [];
    for (i = 0; i < OL.length; i++) {
      var o = OL[i], ms = o && M.seg[o.seg];
      if (!ms) continue;
      M.o.push(o);
      proj.openings.push({ id: 'o' + (M.o.length - 1), wall: ms.side, at: atOut(ms.sg, o.at), w: _impR(o.w), h: _impR(o.h || 0), sill: _impR(o.sill || 0),
                           kind: (o.kind === 'french') ? (_impOpSlides(o) ? 'sliding_door' : 'glass_door') : (o.kind === 'door' ? 'door' : 'window'), code: o.code || '' });
    }
    var PS = S.parts || [];
    for (i = 0; i < PS.length; i++) {
      var p = PS[i];
      if (!p || (p.st | 0) !== st) continue;
      var sp = null;
      try { sp = _partSpan(p); } catch (eS) { sp = null; }
      if (!sp) continue;
      M.p.push(p); M.span.push(sp);
      var pr = { id: 'p' + (M.p.length - 1) };
      if (p.o === 'v') { pr.ax = X(p.c); pr.ay = Y(sp[0]); pr.bx = X(p.c); pr.by = Y(sp[1]); }
      else { pr.ax = X(sp[0]); pr.ay = Y(p.c); pr.bx = X(sp[1]); pr.by = Y(p.c); }
      proj.partitions.push(pr);
    }
    /* THE SLANTED WALLS (v126): told as partitions, flagged slanted, so the
       designer knows the rooms they close; their doors are doors like the
       rest. Each map entry points at the wall in product.diagWalls itself. */
    var GW = (S.product && S.product.diagWalls) || [], nG = 0;
    for (i = 0; i < GW.length; i++) {
      var gw = GW[i];
      if (!gw || !gw.a || !gw.b || (gw.st | 0) !== st) continue;
      var gL = Math.hypot(gw.b[0] - gw.a[0], gw.b[1] - gw.a[1]);
      if (!(gL > 0.3)) continue;
      M.p.push({ __diag: gw }); M.span.push([0, gL]);
      proj.partitions.push({ id: 'p' + (M.p.length - 1), ax: X(gw.a[0]), ay: Y(gw.a[1]), bx: X(gw.b[0]), by: Y(gw.b[1]), slanted: true });
      nG++;
    }
    M.nG = nG;
    var DL = S.doors || [];
    for (i = 0; i < DL.length; i++) {
      var d = DL[i], pi = -1;
      for (k = 0; k < M.p.length; k++) if (M.p[k].id === d.pid) { pi = k; break; }
      if (pi < 0) continue;
      M.d.push(d);
      proj.doors.push({ id: 'd' + (M.d.length - 1), ref: 'p' + pi, at: _impR(d.u - M.span[pi][0]), w: _impR(d.w || 0.8),
                        kind: d.pass ? 'opening' : (d.kind === 'pair' ? 'double' : (d.kind === 'bifold' ? 'bifold' : (d.kind === 'bypass' ? 'bypass' : (d.slide ? 'sliding' : 'hinged')))) });
    }
    /* and the doors of the slanted walls, "at" from the wall's first end */
    for (k = 0; k < M.p.length; k++) {
      var gp = M.p[k].__diag, q;
      if (!gp) continue;
      for (q = 0; q < (gp.doors || []).length; q++) {
        var gd = gp.doors[q];
        M.d.push({ __diagDoor: gd, __diag: gp });
        proj.doors.push({ id: 'd' + (M.d.length - 1), ref: 'p' + k, at: _impR(gd.u), w: _impR(gd.w || 0.8), kind: IMP_DIAG_KIND_OUT[gd.kind] || 'hinged' });
      }
    }
    var RS = [];
    try { RS = (PLb && PLb.rooms) ? (PLb.rooms() || []) : []; } catch (eR) { RS = []; }
    var TB = DRAW.roomTable();
    for (i = 0; i < RS.length; i++) {
      var R = RS[i], bx = { x0: 1e9, x1: -1e9, z0: 1e9, z1: -1e9 }, q;
      for (q = 0; q < (R.rects || []).length; q++) { var rc = R.rects[q]; bx.x0 = Math.min(bx.x0, rc.x0); bx.x1 = Math.max(bx.x1, rc.x1); bx.z0 = Math.min(bx.z0, rc.z0); bx.z1 = Math.max(bx.z1, rc.z1); }
      if (!(bx.x1 > bx.x0)) continue;
      var nm = '', ix = null, key = '';
      try { nm = PLb.roomName ? String(PLb.roomName(R, i) || '') : ''; } catch (eN) { nm = ''; }
      try { ix = PLb.roomNameIdx ? PLb.roomNameIdx(R, i) : R.nameIdx; } catch (eI) { ix = R.nameIdx; }
      if (ix != null && TB.length) { var rw = TB[(((ix | 0) % TB.length) + TB.length) % TB.length]; key = rw ? String(rw.i18n || '') : ''; }
      M.r.push(R);
      proj.rooms.push({ id: 'r' + (M.r.length - 1), name: nm, type: R.closet ? 'closet' : (IMP_CHG_REV[key] || 'other'), x0: X(bx.x0), y0: Y(bx.z0), x1: X(bx.x1), y1: Y(bx.z1),
                        area: _impR(R.area || ((bx.x1 - bx.x0) * (bx.z1 - bx.z0))) });
    }
    var FL = (H.fixturesRef && H.fixturesRef()) || [];
    for (i = 0; i < FL.length; i++) {
      var fx = FL[i];
      if (!fx || (fx.st != null && (fx.st | 0) !== st)) continue;
      var inR = '';
      for (k = 0; k < M.r.length; k++) {
        var RR = M.r[k], j;
        for (j = 0; j < (RR.rects || []).length; j++) { var r2 = RR.rects[j]; if (fx.x >= r2.x0 && fx.x <= r2.x1 && fx.z >= r2.z0 && fx.z <= r2.z1) { inR = 'r' + k; break; } }
        if (inR) break;
      }
      M.f.push(fx);
      proj.fixtures.push({ id: 'f' + (M.f.length - 1), type: String(fx.kind || ''), room: inR, x: X(fx.x), y: Y(fx.z) });
    }
    /* THE TERRACE (v124): the wall it lies against, its middle along that
       wall, its length and its depth, as the designer changes them */
    var T = S.product && S.product.terrace;
    if (T && T.on && T.sx > 0 && T.sz > 0) {
      var sdT = IMP_CHG_OVL[T.side] || '', nsT = (sdT === 'N' || sdT === 'S');
      proj.outdoor.push({ type: (T.surface === 'pavers') ? 'patio' : 'deck', side: sdT,
                          at: sdT ? (nsT ? X(T.x0 + T.sx / 2) : Y(T.z0 + T.sz / 2)) : 0,
                          w: _impR(nsT ? T.sx : T.sz), d: _impR(nsT ? T.sz : T.sx),
                          x0: X(T.x0), y0: Y(T.z0), x1: X(T.x0 + T.sx), y1: Y(T.z0 + T.sz) });
    }
    /* THE ROOF (v124): its type, pitch and reach past each wall, and what
       this product offers -- a flat roof has its parapet and no reach */
    var Rf = S.roof || {}, OV = null;
    try { OV = (typeof C.roofOver === 'function') ? C.roofOver() : Rf.over; } catch (eO) { OV = Rf.over; }
    OV = OV || {};
    var flatR = (Rf.type === 'flat');
    proj.roof = { type: Rf.type || 'gable', pitch: Math.round(Number(Rf.pitch) || 0),
                  over: { N: flatR ? 0 : _impR(OV.z0 || 0), E: flatR ? 0 : _impR(OV.x1 || 0), S: flatR ? 0 : _impR(OV.z1 || 0), W: flatR ? 0 : _impR(OV.x0 || 0) },
                  offers: _impRoofOffer().list };
    return { proj: proj, M: M };
  }
  /* the slanted wall's door kinds, as the designer names them and back (v126) */
  var IMP_DIAG_KIND_OUT = { hinged: 'hinged', pair: 'double', bifold: 'bifold', bypass: 'bypass', slide: 'sliding', pass: 'opening' };
  var IMP_DIAG_KIND_IN = { hinged: 'hinged', double: 'pair', bifold: 'bifold', bypass: 'bypass', sliding: 'slide', pocket: 'slide', opening: 'pass' };
  /* a door into a slanted wall, or a door of one changed: its kind, a width
     the catalogue sells and the wall can hold, clear of the other doors on
     the wall -- moved along it to the nearest free stretch, as a door in a
     square partition is. Returns why not, or an empty string. */
  function _impDiagDoorPut(g, dr, kindAsked, wAsked, uAsked, metric) {
    var L = Math.hypot(g.b[0] - g.a[0], g.b[1] - g.a[1]), cap = Math.max(0.6, L - 0.2), kd = IMP_DIAG_KIND_IN[kindAsked] || '', set = null, w, k;
    if (!kd) return 'no door of kind ' + (kindAsked || '?');
    if (kd === 'pair' || kd === 'bifold' || kd === 'bypass') set = _closetKindWidths(kd, metric);
    else if (kd !== 'pass') set = _doorSet();
    w = wAsked > 0 ? wAsked : (dr.w || 0.8);
    if (kd === 'pass') w = Math.max(0.6, _impSnap(w));
    else if (set && set.length) {
      var bw = null, bd = 1e9;
      for (k = 0; k < set.length; k++) if (set[k] <= cap + 0.005 && Math.abs(set[k] - w) < bd) { bd = Math.abs(set[k] - w); bw = set[k]; }
      if (bw == null) return 'the catalogue has no ' + kd + ' narrow enough for a ' + _impN(L) + ' m wall';
      w = bw;
    }
    if (w > cap + 0.005) return 'a ' + _impN(w) + ' m door does not fit in a ' + _impN(L) + ' m slanted wall';
    /* the free stretches: the wall less a jamb at each end and the other doors */
    var busy = [], free = [], cur = 0.05;
    for (k = 0; k < (g.doors || []).length; k++) if (g.doors[k] !== dr) busy.push([g.doors[k].u - g.doors[k].w / 2 - DOOR_REVEAL, g.doors[k].u + g.doors[k].w / 2 + DOOR_REVEAL]);
    busy.sort(function (a, b) { return a[0] - b[0]; });
    for (k = 0; k < busy.length; k++) { if (busy[k][0] > cur) free.push([cur, busy[k][0]]); cur = Math.max(cur, busy[k][1]); }
    if (L - 0.05 > cur) free.push([cur, L - 0.05]);
    var u0 = (uAsked != null) ? uAsked : dr.u, best = null, bdd = 1e9;
    for (k = 0; k < free.length; k++) {
      var lo = free[k][0] + w / 2, hi = free[k][1] - w / 2;
      if (hi < lo) continue;
      var uu = Math.max(lo, Math.min(hi, u0)), dd = Math.abs(uu - u0);
      if (dd < bdd) { bdd = dd; best = uu; }
    }
    if (best == null) return 'no free stretch of ' + _impN(w) + ' m on that slanted wall';
    dr.u = _impR(best); dr.w = _impR(w); dr.kind = kd;
    if (kd !== 'hinged') dr.sw = 0; else dr.sw = dr.sw | 0;
    return '';
  }
  /* THE ROOF TILE NEEDS NO DESIGNER (v184, Marek: at se neuctuje zmena
     strechy, ktera je u nas jedno kliknuti tlacitka v edit drawer). The
     idea in the bubble -- "Change the roof to hip?" -- and the same few
     words typed went to the designer: Opus, 0.03 USD, a change off the
     buyer's free three. Words that ask for a roof type and nothing else
     are that tile, done here: no designer, no token, no cost. Anything
     more ("a hip roof and a bigger bedroom") goes to the designer as
     before. English and Czech, with or without the accents. */
  var IMP_ROOF_WORD = [[/^(gable|gabled|sedlov)/, 'gable'], [/^(hip|hipped|valbov|valb)/, 'hip'], [/^(mono|monopitch|mono-pitch|shed|lean-to|pultov)/, 'mono'], [/^(flat|ploch)/, 'flat']];
  function _impLocalRoof(t) {
    var a = String(t || '').toLowerCase();
    try { a = a.normalize('NFD').replace(/[\u0300-\u036f]/g, ''); } catch (eN) {}
    a = a.replace(/[?.!,;:"']/g, ' ').replace(/\s+/g, ' ').trim();
    if (!a || a.length > 70) return '';
    var w = a.split(' '), kind = '', i, k, rest = [];
    var FILL = /^(please|change|switch|turn|set|make|use|give|put|it|the|house|roof|roofs|a|an|to|into|as|with|instead|zmen|zmenit|zmente|prehod|udelej|dej|nastav|chci|strechu|strecha|strechy|na|prosim|it's|its)$/;
    for (i = 0; i < w.length; i++) {
      var hit = '';
      for (k = 0; k < IMP_ROOF_WORD.length && !hit; k++) if (IMP_ROOF_WORD[k][0].test(w[i])) hit = IMP_ROOF_WORD[k][1];
      if (hit) { if (kind && kind !== hit) return ''; kind = hit; continue; }
      if (!FILL.test(w[i])) rest.push(w[i]);
    }
    /* the roof named, a type named, and no other word */
    if (!kind || rest.length || !/roof|strech/.test(a)) return '';
    return kind;
  }
  function _impChange(text, opts) {
    opts = opts || {};
    var t = String(text == null ? '' : text).replace(/\s+/g, ' ').trim();
    if (!t) return false;
    if (t.length > IMP_TEXT_MAX) t = t.slice(0, IMP_TEXT_MAX);
    if (_imp.user) _impUserClock();
    var PJ = null;
    try { PJ = _impChgProject(); } catch (eP) { PJ = null; _impSay('PLAN change: describing the project THREW ' + ((eP && eP.message) || eP)); }
    var C = _impCore(), S = C ? C.STATE : null;
    var prod = String(opts.product || _impProdName() || '').slice(0, 30);
    var R = _imp.rd = { st: 'ticket', name: t.length > 48 ? (t.slice(0, 48) + '...') : t, text: t, change: true, model: _impModel(), t0: Date.now(),
                        prod: prod, hist: IMP_CHG_HIST.slice(), map: PJ ? PJ.M : null, proj: PJ ? PJ.proj : null };
    _imp.prev = null;
    if (!PJ) { _impReadFail(R, 'there is no plan here to change'); return true; }
    /* v184: the roof tile, done here */
    var LR = _impLocalRoof(t);
    if (LR) {
      R.st = 'done'; R.t1 = R.t2 = Date.now(); R.local = true; R.model = 'local';
      R.chg = { ops: [{ op: 'roof_type', kind: LR }], question: '', summary: 'The roof set to ' + LR + ' (the Roof & Base tile, read on this page)' };
      R.res = { ok: true, mode: 'change', local: true, cost: 0, change: R.chg };
      _impChgHist(R.text);
      _imp.applied = false; _imp.restored = false; _imp.undone = false; _imp.applyLines = null;
      _impSay('PLAN change (v184): "' + t + '" is the roof tile -- ' + LR + ', done on this page: no designer, no token, no cost');
      _impRefresh();
      return true;
    }
    var units = S ? (S.isMetric === false ? 'imperial' : 'metric') : '';
    _impSay('PLAN change: the project told as ' + PJ.proj.walls.length + ' wall(s), ' + PJ.proj.openings.length + ' opening(s), ' + PJ.proj.partitions.length +
            ' partition(s)' + (PJ.M.nG ? (' (' + PJ.M.nG + ' of them slanted)') : '') + ', ' + PJ.proj.doors.length + ' door(s), ' + PJ.proj.rooms.length + ' room(s), ' + PJ.proj.fixtures.length + ' piece(s)' +
            (PJ.proj.outdoor.length ? ', a terrace on ' + (PJ.proj.outdoor[0].side || 'no wall') : '') +
            ', a ' + PJ.proj.roof.type + ' roof at ' + PJ.proj.roof.pitch + ' deg reaching N ' + _impN(PJ.proj.roof.over.N) + ' E ' + _impN(PJ.proj.roof.over.E) +
            ' S ' + _impN(PJ.proj.roof.over.S) + ' W ' + _impN(PJ.proj.roof.over.W) + ' (offers ' + PJ.proj.roof.offers.join('/') + ', from ' + _impRoofOffer().from + ')' +
            ' -- ' + JSON.stringify(PJ.proj).length + ' characters');
    /* v153: beside -- this page writes the roof and the terrace that come
       beside a redesign (worker w2.08); an older page is sent the redesign alone */
    _impAsk(R, function (ticket) { return { ticket: ticket, mode: 'change', text: t, project: PJ.proj, history: R.hist, units: units, product: prod, model: _impModel(), beside: true }; });
    return true;
  }
  /* THE OPERATIONS, WRITTEN. One Undo step for all of them; each says what
     it did or why it did not. */
  function _impChgApply(R) {
    var C = _impCore(), H = _PH, M = R.map, ops = (R.chg && R.chg.ops) || [], lines = [], done = 0, i;
    if (!C || !C.STATE || !H || !M) { _imp.applyLines = ['PLAN change: nothing written -- no house core or no project map']; _impTickAny(); return; }
    /* v153: the short link before the change -- the library waits for the next one */
    R.pBefore = _impShareId();
    var S = C.STATE, F = M.F, metric = S.isMetric !== false;
    /* REMOVALS FIRST, THEN CHANGES, THEN ADDITIONS: a window that becomes a
       glazed door is a remove and an add at the same place, and the add must
       not find the old window still there -- whatever order they came in */
    /* v124: the roof's type before its reach, a terrace taken away before one is put */
    var PRI = { opening_remove: 0, door_remove: 0, partition_remove: 0, fixture_remove: 0, terrace_remove: 0,
                opening_change: 1, door_change: 1, partition_move: 1, room_rename: 1, roof_type: 1 };
    ops = ops.map(function (o, n) { return { o: o, n: n }; }).sort(function (a, b) {
      var pa = PRI.hasOwnProperty(a.o.op) ? PRI[a.o.op] : 2, pb = PRI.hasOwnProperty(b.o.op) ? PRI[b.o.op] : 2;
      return (pa - pb) || (a.n - b.n);
    }).map(function (q) { return q.o; });
    try { if (C.undoPush) C.undoPush(); } catch (eU) {}
    function WX(x) { return F.x0 + x; }
    function WZ(y) { return F.z0 + y; }
    function idx(ref, ch, L) { var m = new RegExp('^' + ch + '(\\d+)$').exec(String(ref || '')); if (!m) return null; var o = L[m[1] | 0]; return o || null; }
    function nearest(set, w) { var b = null, bd = 1e9, k; for (k = 0; k < set.length; k++) if (Math.abs(set[k] - w) < bd) { bd = Math.abs(set[k] - w); b = set[k]; } return b; }
    function segOf(side) { return M.side[side] || null; }
    function atIn(sg, a) { var a2 = a - (sg.__o || 0); return sg.__flip ? (sg.len - a2) : a2; }
    function clashO(rec, except) {
      var L = S.openings || [], k;
      for (k = 0; k < L.length; k++) {
        if (L[k] === except || L[k].seg !== rec.seg) continue;
        if (Math.abs(L[k].at - rec.at) < (L[k].w + rec.w) / 2 + 0.12) return L[k];
      }
      return null;
    }
    function placeOpening(rec, sg, except) {
      try { if (C.snapOpeningAt) rec.at = C.snapOpeningAt(rec, rec.at); } catch (eS) {}
      if (sg.h && rec.sill + rec.h > sg.h) rec.h = Math.max(0.3, sg.h - rec.sill - 0.05);
      if (rec.at - rec.w / 2 < 0.25 || rec.at + rec.w / 2 > sg.len - 0.25) return 'does not fit in the ' + _impN(sg.len) + ' m wall';
      var cl = clashO(rec, except);
      return cl ? ('would overlap ' + (cl.code || cl.kind) + ' at ' + _impN(atIn(sg, cl.at))) : '';
    }
    function doorRec(d, kind, w) {
      delete d.pass; delete d.slide; delete d.kind; d.sw = d.sw | 0;
      var set;
      if (kind === 'opening') { d.pass = true; d.w = _impR(Math.max(0.6, w || 0.9)); return; }
      if (kind === 'double' || kind === 'bifold' || kind === 'bypass') {
        d.kind = (kind === 'double') ? 'pair' : kind; set = _closetKindWidths(d.kind, metric);
      } else { set = _doorSet(); if (kind === 'sliding' || kind === 'pocket') d.slide = 1; }
      d.w = _impR((set && set.length) ? nearest(set, w || 0.8) : (w || 0.8));
    }
    function fitDoor(d, part, span, except) {
      var free = _doorFree(part.id, span, except), k, w = d.w, best = null, bd = 1e9;
      for (k = 0; k < free.length; k++) {
        var lo = free[k][0] + w / 2 + DOOR_REVEAL, hi = free[k][1] - w / 2 - DOOR_REVEAL;
        if (hi < lo) continue;
        var u = Math.max(lo, Math.min(hi, d.u)), dd = Math.abs(u - d.u);
        if (dd < bd) { bd = dd; best = u; }
      }
      if (best == null) return 'no free stretch of ' + _impN(w + 2 * DOOR_REVEAL) + ' m on that partition';
      if (bd > 0.01) lines.push('   the door moved ' + _impN(bd) + ' m along its partition to stand free');
      d.u = _impR(best);
      return '';
    }
    /* v131: a glazed door asked for in words that say it slides is a slider --
       for a designer that does not name sliding_door yet */
    var SLIDE_WORDS = /slid|patio door|posuv|schiebe|desliz|corredi|scorrevol|coulissant|skjut|skyve|liukuov|przesuw|tol\u00f3ajt|kayar|schuif/i;
    var askedSlide = SLIDE_WORDS.test(String(R.text || ''));
    for (i = 0; i < ops.length; i++) {
      var o = ops[i], why = '', said = _impChgOpTxt(o), sg, rec, u2, d, p, sp, k;
      if ((o.op === 'opening_add' || o.op === 'opening_change') && o.kind === 'glass_door' && askedSlide) {
        o = JSON.parse(JSON.stringify(o)); o.kind = 'sliding_door';
        lines.push('   glass_door asked for in words that say it slides -- made a sliding door (v131)');
      }
      try {
        if (o.op === 'opening_add') {
          sg = segOf(o.wall);
          if (!sg) why = 'no wall ' + o.wall;
          else {
            u2 = _impChgUnit(o.kind || 'window', o.w || 1.2);
            if (!u2) why = 'the catalogue has no ' + (o.kind || 'window');
            else {
              rec = { seg: sg.id, at: atIn(sg, o.at), w: u2.w, h: u2.h, sill: u2.sill, kind: u2.kind, code: u2.code, hand: u2.hand,
                      fullW: null, halfW: null, rake: null, leaves: u2.leaves || Math.max(1, Math.round(u2.w / ((u2.kind === 'french') ? 1.0 : 1.4))) };
              why = placeOpening(rec, sg, null);
              if (!why) { S.openings = S.openings || []; S.openings.push(rec); said += ' -> ' + rec.code + ' ' + _impN(rec.w) + ' m'; }
            }
          }
        } else if (o.op === 'opening_change' || o.op === 'opening_remove') {
          rec = idx(o.ref, 'o', M.o);
          if (!rec || (S.openings || []).indexOf(rec) < 0) why = 'no opening ' + o.ref + ' any more';
          else if (o.op === 'opening_remove') { S.openings.splice(S.openings.indexOf(rec), 1); }
          else {
            sg = M.seg[rec.seg] ? M.seg[rec.seg].sg : null;
            var keep = JSON.stringify(rec), kd = o.kind || (rec.kind === 'french' ? 'glass_door' : rec.kind);
            if (o.w || o.kind) {
              u2 = _impChgUnit(kd, o.w || rec.w);
              /* v131: the leaves of the new unit, not the ones of the window it replaces */
              if (u2) { rec.w = u2.w; rec.h = u2.h; rec.sill = u2.sill; rec.kind = u2.kind; rec.code = u2.code; rec.hand = u2.hand;
                        rec.leaves = u2.leaves || Math.max(1, Math.round(u2.w / ((u2.kind === 'french') ? 1.0 : 1.4))); }
            }
            if (o.at && sg) rec.at = atIn(sg, o.at);
            why = sg ? placeOpening(rec, sg, rec) : 'its wall is not on this storey';
            if (why) { var back = JSON.parse(keep); for (k in back) if (back.hasOwnProperty(k)) rec[k] = back[k]; }
            else said += ' -> ' + rec.code + ' ' + _impN(rec.w) + ' m';
          }
        } else if (o.op === 'door_add' && (p = idx(o.ref, 'p', M.p)) && p.__diag) {
          /* A DOOR INTO A SLANTED WALL (v126) */
          var gA = p.__diag, PRa = S.product || {};
          if ((PRa.diagWalls || []).indexOf(gA) < 0) why = 'no slanted wall ' + o.ref + ' any more';
          else {
            var nd = { u: 0, w: 0.8, kind: 'hinged', sw: 0 };
            var LgA = Math.hypot(gA.b[0] - gA.a[0], gA.b[1] - gA.a[1]);
            why = _impDiagDoorPut(gA, nd, o.kind || 'hinged', o.w, (o.at > 0) ? o.at : LgA / 2, metric);
            if (!why) { gA.doors = gA.doors || []; gA.doors.push(nd); said += ' -> ' + _impN(nd.w) + ' m ' + nd.kind + ' in slanted wall ' + gA.id + ' at ' + _impN(nd.u); }
          }
        } else if ((o.op === 'door_change' || o.op === 'door_remove') && (d = idx(o.ref, 'd', M.d)) && d.__diagDoor) {
          /* A DOOR OF A SLANTED WALL (v126): changed in place, or taken out */
          var gC = d.__diag, dC = d.__diagDoor, PRc = S.product || {};
          if ((PRc.diagWalls || []).indexOf(gC) < 0 || (gC.doors || []).indexOf(dC) < 0) why = 'no door ' + o.ref + ' any more';
          else if (o.op === 'door_remove') { gC.doors.splice(gC.doors.indexOf(dC), 1); said += ' (in slanted wall ' + gC.id + ')'; }
          else {
            var keepC = JSON.stringify(dC);
            why = _impDiagDoorPut(gC, dC, o.kind || IMP_DIAG_KIND_OUT[dC.kind] || 'hinged', o.w, null, metric);
            if (why) { var bC = JSON.parse(keepC); for (k in dC) if (dC.hasOwnProperty(k)) delete dC[k]; for (k in bC) if (bC.hasOwnProperty(k)) dC[k] = bC[k]; }
            else said += ' -> ' + _impN(dC.w) + ' m ' + dC.kind + ' in slanted wall ' + gC.id;
          }
        } else if ((o.op === 'partition_move' || o.op === 'partition_remove') && (p = idx(o.ref, 'p', M.p)) && p.__diag) {
          /* A SLANTED WALL (v126) is taken out with its doors; it is not moved */
          var gR = p.__diag, PRr = S.product || {};
          if ((PRr.diagWalls || []).indexOf(gR) < 0) why = 'no slanted wall ' + o.ref + ' any more';
          else if (o.op === 'partition_move') why = 'a slanted wall stays where the plan drew it -- it can be taken out, not moved';
          else {
            PRr.diagWalls.splice(PRr.diagWalls.indexOf(gR), 1);
            if (!PRr.diagWalls.length) delete PRr.diagWalls;
            said += ' (slanted wall ' + gR.id + ((gR.doors || []).length ? (' and its ' + gR.doors.length + ' door(s)') : '') + ')';
          }
        } else if (o.op === 'door_add') {
          p = idx(o.ref, 'p', M.p); k = p ? M.p.indexOf(p) : -1;
          if (!p || (S.parts || []).indexOf(p) < 0) why = 'no partition ' + o.ref + ' any more';
          else {
            sp = _partSpan(p) || M.span[k];
            d = { pid: p.id, u: _impR(sp[0] + (o.at || (sp[1] - sp[0]) / 2)), w: 0.8, sw: 0 };
            doorRec(d, o.kind || 'hinged', o.w);
            why = fitDoor(d, p, sp, null);
            if (!why) { S.doors = S.doors || []; S.doors.push(d); said += ' -> ' + _impN(d.w) + ' m' + _impDoorKind(d); }
          }
        } else if (o.op === 'door_change' || o.op === 'door_remove') {
          d = idx(o.ref, 'd', M.d);
          if (!d || (S.doors || []).indexOf(d) < 0) why = 'no door ' + o.ref + ' any more';
          else if (o.op === 'door_remove') S.doors.splice(S.doors.indexOf(d), 1);
          else {
            var keepD = JSON.stringify(d), pD = null;
            for (k = 0; k < (S.parts || []).length; k++) if (S.parts[k].id === d.pid) { pD = S.parts[k]; break; }
            var kdD = o.kind || (d.pass ? 'opening' : (d.kind === 'pair' ? 'double' : (d.kind || (d.slide ? 'sliding' : 'hinged'))));
            doorRec(d, kdD, o.w || d.w);
            why = pD ? fitDoor(d, pD, _partSpan(pD) || [d.u - 1, d.u + 1], d) : 'its partition is gone';
            if (why) { var backD = JSON.parse(keepD); for (k in d) if (d.hasOwnProperty(k)) delete d[k]; for (k in backD) if (backD.hasOwnProperty(k)) d[k] = backD[k]; }
            else said += ' -> ' + _impN(d.w) + ' m' + _impDoorKind(d);
          }
        } else if (o.op === 'partition_add') {
          var vert = Math.abs(o.ax - o.bx) < 0.06, hz = Math.abs(o.ay - o.by) < 0.06;
          if (vert === hz) why = 'not square to the outline';
          else {
            var nid = 1;
            for (k = 0; k < (S.parts || []).length; k++) nid = Math.max(nid, (S.parts[k].id | 0) + 1);
            var c = vert ? WX((o.ax + o.bx) / 2) : WZ((o.ay + o.by) / 2);
            var s0 = vert ? WZ(Math.min(o.ay, o.by)) : WX(Math.min(o.ax, o.bx)), e0 = vert ? WZ(Math.max(o.ay, o.by)) : WX(Math.max(o.ax, o.bx));
            if (vert ? (c < F.x0 + 0.5 || c > F.x1 - 0.5) : (c < F.z0 + 0.5 || c > F.z1 - 0.5)) why = 'too close to the outer wall';
            else {
              p = { id: nid, o: vert ? 'v' : 'h', dir: vert ? 'v' : 'h', c: _impR(c), at: _impR(c), st: M.st, s: _impR(s0), e: _impR(e0) };
              /* an end on another partition is tied to it */
              for (k = 0; k < (S.parts || []).length; k++) {
                var q = S.parts[k], spq = null;
                if ((q.st | 0) !== M.st || q.o === p.o) continue;
                try { spq = _partSpan(q); } catch (eQ) { spq = null; }
                if (!spq || p.c < spq[0] - 0.05 || p.c > spq[1] + 0.05) continue;
                if (Math.abs(q.c - p.s) < 0.15) p.sTo = q.id;
                if (Math.abs(q.c - p.e) < 0.15) p.eTo = q.id;
              }
              S.parts = S.parts || [];
              S.parts.push(p);
              said += ' -> #' + p.id + ' ' + p.o + ' at ' + _impN(p.c);
            }
          }
        } else if (o.op === 'partition_move' || o.op === 'partition_remove') {
          p = idx(o.ref, 'p', M.p);
          if (!p || (S.parts || []).indexOf(p) < 0) why = 'no partition ' + o.ref + ' any more';
          else if (o.op === 'partition_remove') {
            S.parts.splice(S.parts.indexOf(p), 1);
            var nD = 0;
            for (k = (S.doors || []).length - 1; k >= 0; k--) if (S.doors[k].pid === p.id) { S.doors.splice(k, 1); nD++; }
            for (k = 0; k < S.parts.length; k++) { if (S.parts[k].sTo === p.id) delete S.parts[k].sTo; if (S.parts[k].eTo === p.id) delete S.parts[k].eTo; }
            if (nD) said += ' (and its ' + nD + ' door(s))';
          } else {
            var nc = (p.o === 'v') ? WX(o.at) : WZ(o.at);
            if ((p.o === 'v') ? (nc < F.x0 + 0.5 || nc > F.x1 - 0.5) : (nc < F.z0 + 0.5 || nc > F.z1 - 0.5)) why = 'that is too close to the outer wall';
            else { said += ' (from ' + _impN((p.o === 'v') ? (p.c - F.x0) : (p.c - F.z0)) + ')'; p.c = _impR(nc); p.at = p.c; }
          }
        } else if (o.op === 'room_rename') {
          var Rm = idx(o.ref, 'r', M.r);
          if (!Rm) why = 'no room ' + o.ref;
          else {
            var kk = _impKey(o.name || '', o.kind ? (IMP_TYPE_KEY[o.kind] || []) : []), TB2 = DRAW.roomTable(), ix2 = -1;
            for (k = 0; k < TB2.length; k++) if (TB2[k] && TB2[k].i18n === kk.key) { ix2 = k; break; }
            if (!kk.key || ix2 < 0) why = 'this product has no room called "' + (o.name || o.kind) + '"';
            else {
              var pins = S.roomNames = S.roomNames || [], pin = null, rcx = 0, rcz = 0, rA = 0;
              for (k = 0; k < (Rm.rects || []).length; k++) { var rr = Rm.rects[k], aa = (rr.x1 - rr.x0) * (rr.z1 - rr.z0); if (aa > rA) { rA = aa; rcx = (rr.x0 + rr.x1) / 2; rcz = (rr.z0 + rr.z1) / 2; } }
              for (k = 0; k < pins.length; k++) {
                var pn = pins[k], inside = false, jj;
                for (jj = 0; jj < (Rm.rects || []).length; jj++) { var r3 = Rm.rects[jj]; if (pn.x >= r3.x0 && pn.x <= r3.x1 && pn.z >= r3.z0 && pn.z <= r3.z1) { inside = true; break; } }
                if (inside) { pin = pn; break; }
              }
              if (!pin) { pin = { x: _impR(rcx), z: _impR(rcz), n: ix2, k: kk.key }; pins.push(pin); }
              else { pin.n = ix2; pin.k = kk.key; }
              said += ' -> ' + kk.key + (kk.note ? (' (' + kk.note + ')') : '');
            }
          }
        } else if (o.op === 'fixture_add') {
          var Rf = idx(o.ref, 'r', M.r), SZ = IMP_CHG_FIXSZ[o.kind] || null;
          if (!Rf) why = 'no room ' + o.ref;
          else if (!SZ) why = 'no piece of type ' + o.kind;
          else {
            var catK = _impFixKind(o.kind, SZ[0], SZ[1], ''), Df = catK ? _fixDef(catK) : null;
            if (!Df) why = 'this product has no ' + o.kind;
            else {
              var fcx = 0, fcz = 0, fA = 0;
              for (k = 0; k < (Rf.rects || []).length; k++) { var r4 = Rf.rects[k], a4 = (r4.x1 - r4.x0) * (r4.z1 - r4.z0); if (a4 > fA) { fA = a4; fcx = (r4.x0 + r4.x1) / 2; fcz = (r4.z0 + r4.z1) / 2; } }
              var pl = _fitPlace({ kind: catK, room: { x: fcx, z: fcz }, wall: IMP_CHG_SIDE[o.wall] || 'auto', align: 'auto', st: M.st });
              if ((!pl || !pl.ok) && IMP_CHG_SIDE[o.wall]) pl = _fitPlace({ kind: catK, room: { x: fcx, z: fcz }, wall: 'auto', align: 'auto', st: M.st });
              if (!pl || !pl.ok) why = 'no free place for ' + catK + ' in that room' + (pl ? (' (' + pl.reason + (pl.detail ? ' ' + pl.detail : '') + ')') : '');
              else { var nf = _fitCommit(pl); said += ' -> ' + catK + ' at ' + _impN(nf.x) + ',' + _impN(nf.z) + ' against the ' + (pl.wall || '?') + ' wall'; }
            }
          }
        } else if (o.op === 'fixture_remove') {
          var fr = idx(o.ref, 'f', M.f), FL2 = (H.fixturesRef && H.fixturesRef()) || [];
          if (!fr || FL2.indexOf(fr) < 0) why = 'no piece ' + o.ref + ' any more';
          else FL2.splice(FL2.indexOf(fr), 1);
        } else if (o.op === 'roof_type') {
          /* THE ROOF'S TYPE (v124), the way its tile on Roof & Base sets it:
             a flat roof with its parapet and no eave, a pitched one with its
             eave back and a pitch of its own range; the hip at both ends */
          var RO = _impRoofOffer().list, kdR = String(o.kind || ''), Rr = S.roof = S.roof || {}, wasR = (Rr.type || '?') + ' ' + Math.round(Rr.pitch || 0) + ' deg';
          if (RO.indexOf(kdR) < 0) why = 'this product has no ' + (kdR || '?') + ' roof (it offers ' + RO.join(', ') + ')';
          else {
            var acrossR = (Rr.axis === 'l') ? S.footprint.w : S.footprint.l, spanR = 0;
            try { spanR = (typeof C.spanFor === 'function') ? (Number(C.spanFor(kdR)) || 0) : 0; } catch (eSp) { spanR = 0; }
            if (spanR > 0 && acrossR > spanR + 1e-6) why = 'a ' + kdR + ' roof spans at most ' + _impN(spanR) + ' m and the house is ' + _impN(acrossR) + ' m across -- the roof stays ' + Rr.type;
            else {
              var fw0 = S.footprint.w, fl0 = S.footprint.l, eavR = (Rr.eave > 0) ? Rr.eave : 0.45;
              if (kdR === 'flat') {
                Rr.parapet = true; Rr.eave = 0; Rr.pitch = 0;
                C.setRoofType('flat');
              } else if (kdR === 'mono') {
                Rr.parapet = false; Rr.eave = eavR;
                Rr.pitch = (o.pitch >= 2 && o.pitch <= 15) ? o.pitch : ((Rr.pitch >= 2 && Rr.pitch <= 15) ? Rr.pitch : 8);
                C.setRoofType('mono');
              } else {
                Rr.parapet = false; Rr.eave = eavR;
                Rr.pitch = (o.pitch >= 15 && o.pitch <= 45) ? o.pitch : ((Rr.pitch >= 15 && Rr.pitch <= 35) ? Rr.pitch : 26);
                if (typeof C.setPitchedStage === 'function' && typeof C.roofStageCount === 'function') C.setPitchedStage(kdR === 'hip' ? (C.roofStageCount() - 1) : 0);
                else C.setRoofType(kdR);
              }
              said += ' -> ' + S.roof.type + (S.roof.parapet ? ' with its parapet' : '') + ' ' + Math.round(S.roof.pitch || 0) + ' deg (was ' + wasR + ')';
              if (S.footprint.w !== fw0 || S.footprint.l !== fl0) said += ' -- THE HOUSE SHRANK to ' + _impN(S.footprint.w) + ' x ' + _impN(S.footprint.l) + ' m: this roof spans no more';
            }
          }
        } else if (o.op === 'roof_overhang') {
          /* HOW FAR THE ROOF REACHES PAST A WALL (v124): written per side and
             held to the core's own limits (roofOver) */
          var Ro = S.roof = S.roof || {}, kO = o.wall ? [IMP_CHG_OVK[o.wall]] : ['z0', 'x1', 'z1', 'x0'];
          if (Ro.type === 'flat') why = 'a flat roof with a parapet has no overhang';
          else if (!(o.d > 0)) why = 'no reach given';
          else if (!kO[0]) why = 'no wall ' + o.wall;
          else {
            var e0R = Ro.eave || 0.45, dO = Math.max(0.1, Math.min(3.0, o.d));
            if (!Ro.over) Ro.over = { x0: e0R, x1: e0R, z0: e0R, z1: e0R };
            for (k = 0; k < kO.length; k++) Ro.over[kO[k]] = _impR(dO);
            try { if (typeof C.roofOver === 'function') C.roofOver(); } catch (eRo) {}
            said += ' -> ' + kO.map(function (q) { return IMP_CHG_OVL[q] + ' ' + _impN(Ro.over[q]) + (Math.abs(Ro.over[q] - dO) > 0.005 ? ' (held to the roof\'s limit)' : ''); }).join(', ');
          }
        } else if (o.op === 'terrace_set') {
          /* THE TERRACE (v124): against the wall named, its middle where the
             designer says, its size kept or the usual 4 x 3, pavers in whole
             tiles -- the same record the Exterior step drags */
          var PRt = S.product = S.product || {};
          var Tt = PRt.terrace = PRt.terrace || { on: false, side: 'z1', w: 4.0, d: 3.0, off: 0, surface: 'deck', pergola: 'none' };
          var hadT = !!(Tt.on && Tt.sx > 0 && Tt.sz > 0), sdO = hadT ? Tt.side : null;
          var sdT2 = IMP_CHG_OVK[o.wall] || sdO || 'z1', nsT2 = (sdT2 === 'z0' || sdT2 === 'z1');
          var LwT = nsT2 ? (F.x1 - F.x0) : (F.z1 - F.z0), sameT = hadT && sdO === sdT2;
          var twT = (o.w > 0) ? o.w : (sameT ? (nsT2 ? Tt.sx : Tt.sz) : Math.min(4.0, LwT));
          var tdT = (o.d > 0) ? o.d : (sameT ? (nsT2 ? Tt.sz : Tt.sx) : 3.0);
          twT = Math.max(1.2, Math.min(20, twT)); tdT = Math.max(1.0, Math.min(20, tdT));
          var midT = (o.at > 0) ? o.at : (sameT ? (nsT2 ? (Tt.x0 + Tt.sx / 2 - F.x0) : (Tt.z0 + Tt.sz / 2 - F.z0)) : LwT / 2);
          var sfT = (o.kind === 'patio') ? 'pavers' : ((o.kind === 'deck') ? 'deck' : (hadT ? (Tt.surface || 'deck') : 'deck'));
          if (sfT === 'pavers') { var tlT = (S.isMetric === false) ? 0.508 : 0.5; twT = Math.max(2 * tlT, Math.round(twT / tlT) * tlT); tdT = Math.max(2 * tlT, Math.round(tdT / tlT) * tlT); }
          var loT = (nsT2 ? F.x0 : F.z0) + midT - twT / 2;
          Tt.on = true; Tt.side = sdT2; Tt.surface = sfT; if (!Tt.pergola) Tt.pergola = 'none';
          if (sdT2 === 'z1') { Tt.x0 = loT; Tt.z0 = F.z1; Tt.sx = twT; Tt.sz = tdT; }
          else if (sdT2 === 'z0') { Tt.x0 = loT; Tt.z0 = F.z0 - tdT; Tt.sx = twT; Tt.sz = tdT; }
          else if (sdT2 === 'x1') { Tt.x0 = F.x1; Tt.z0 = loT; Tt.sx = tdT; Tt.sz = twT; }
          else { Tt.x0 = F.x0 - tdT; Tt.z0 = loT; Tt.sx = tdT; Tt.sz = twT; }
          Tt.x0 = _impR(Tt.x0); Tt.z0 = _impR(Tt.z0); Tt.sx = _impR(Tt.sx); Tt.sz = _impR(Tt.sz);
          Tt.w = _impR(twT); Tt.d = _impR(tdT); Tt.off = _impR(loT - (nsT2 ? F.x0 : F.z0));
          said += ' -> ' + (hadT ? (sameT ? 'resized' : 'moved') : 'new') + ' on ' + IMP_CHG_OVL[sdT2] + ', ' + _impN(twT) + ' x ' + _impN(tdT) + ' m of ' + sfT + ', its middle ' + _impN(midT) + ' m along the wall';
        } else if (o.op === 'terrace_remove') {
          var Tr = S.product && S.product.terrace;
          if (!Tr || !Tr.on) why = 'there is no terrace';
          else Tr.on = false;
        } else why = 'not an operation this asset writes';
      } catch (eO) { why = 'THREW ' + ((eO && eO.message) || eO); }
      if (why) lines.push('PLAN change REFUSED: ' + said + ' -- ' + why);
      else { lines.push('PLAN change done: ' + said); done++; }
    }
    try { C.savePref(); C.rebuild(); } catch (eB) { lines.push('PLAN change: the rebuild THREW ' + ((eB && eB.message) || eB)); }
    try { if (C.undoPush) C.undoPush(); } catch (eU2) {}
    /* v153: the plan checked again as it stands now -- the library goes by it */
    try { _imp.audit = _impAudit(S); } catch (eAu) { _imp.audit = { ok: false, problems: ['the check THREW ' + ((eAu && eAu.message) || eAu)] }; }
    lines.push('PLAN change: the plan checked as it stands -- ' + (_imp.audit && _imp.audit.ok ? 'clean' : ('NOT clean: ' + ((_imp.audit && _imp.audit.problems) || []).join(', '))));
    lines.unshift('PLAN change written: ' + done + ' of ' + ops.length + ' operation(s)' + ((R.chg && R.chg.summary) ? (' -- "' + R.chg.summary + '"') : '') + ' | Undo takes it all back');
    _imp.applyLines = lines;
    _imp.applied = done > 0;
    _imp.restored = false; _imp.undone = false;
    _impTickAny();
    try { _fitShow(); } catch (e3) {}
  }
  /* ---------- THE SLANTED WALLS, BUILT (v125) -----------------------------
     Marek: zkusime to postavit? Jen pri prevodu pudorysu, ne v edit mode --
     a dvere v sikme stene rovnou s kridlem. A wall the reader draws at an
     angle (the 45 deg wall at the door of a bathroom) was kept aside, drawn
     dashed in the preview and left out of the house. It goes with the
     project now, in product.diagWalls, each as
       { id, st, a: [x, z], b: [x, z], doors: [{ u, w, kind, sw }] }
     a and b on the centre line of the wall, u the distance from a to the
     middle of a door along it, kind hinged | pair | bifold | bypass | slide
     | pass, sw the swing the way the other doors keep it (bit 0 the leaf
     hangs on the end towards b, bit 1 it opens to the left of a -> b). Its
     ends are taken to the walls it nearly meets. The plan draws it as a
     partition with its doors; the product builds it (the product's own
     step). It is not edited by hand: a new plan, Undo or RESTORE are what
     change it. */
  var IMP_DIAG_REACH = 0.25;
  /* the slanted wall a door stands in, when one is nearer than any square
     wall: its index and how far along it the door's middle is */
  function _impDiagDoorAt(diags, p, nearSq) {
    var best = null, bd = Math.min(IMP_DIAG_REACH, nearSq == null ? 1e9 : nearSq), k;
    for (k = 0; diags && k < diags.length; k++) {
      var g = diags[k], dx = g.b[0] - g.a[0], dz = g.b[1] - g.a[1], L = Math.hypot(dx, dz);
      if (!(L > 0.3)) continue;
      var ux = dx / L, uz = dz / L, rx = p[0] - g.a[0], rz = p[1] - g.a[1];
      var al = rx * ux + rz * uz, pe = Math.abs(rx * uz - rz * ux);
      if (al < 0.1 || al > L - 0.1) continue;
      if (pe < bd) { bd = pe; best = { k: k, u: al, d: pe }; }
    }
    return best;
  }
  /* the door, of a kind and a width the catalogue has, no wider than its wall */
  function _impDiagDoorAdd(g, u, w, kind, hp, op, label, SET, metric) {
    var L = Math.hypot(g.b[0] - g.a[0], g.b[1] - g.a[1]), cap = Math.max(0.6, L - 0.1), k, bw = w, bd = 1e9;
    var kd = String(kind || 'hinged');
    var ck = (kd === 'double') ? 'pair' : ((kd === 'bifold' || kd === 'bypass') ? kd : '');
    var out = { u: 0, w: 0, kind: 'hinged', hp: hp || null, op: op || null, label: String(label || '') };
    if (ck) {
      var KW = _closetKindWidths(ck, metric) || [], kb = -1;
      for (k = 0; k < KW.length; k++) if (KW[k] <= cap + 0.005 && Math.abs(KW[k] - w) < bd) { bd = Math.abs(KW[k] - w); kb = k; }
      if (kb >= 0) { bw = KW[kb]; out.kind = ck; } else ck = '';
    }
    if (!ck) {
      if (kd === 'opening') { bw = Math.max(0.6, _impSnap(w)); out.kind = 'pass'; }
      else if (kd === 'sliding' || kd === 'pocket') { bw = _impSnap(w); out.kind = 'slide'; }
      else { bw = SET && SET.length ? SET[0] : 0.8; for (k = 0; SET && k < SET.length; k++) if (Math.abs(SET[k] - w) < bd) { bd = Math.abs(SET[k] - w); bw = SET[k]; } }
    }
    bw = Math.min(bw, cap);
    out.w = _impR(bw);
    out.u = _impR(Math.max(bw / 2 + 0.05, Math.min(L - bw / 2 - 0.05, u)));
    g.doors = g.doors || [];
    g.doors.push(out);
  }
  /* the slanted walls into the project, once the plan is written: their ends
     taken to the walls they nearly meet, each door's swing worked out */
  function _impDiagWrite(f) {
    var C = _impCore(), H = _PH;
    if (!C || !C.STATE || !f) return;
    var S = C.STATE, PR = S.product = S.product || {}, L = f.diag || [], i, k, out = [], said = [];
    if (!L.length) {
      if (PR.diagWalls && PR.diagWalls.length) { delete PR.diagWalls; _impSay('PLAN slanted walls: none in this plan -- the old ones went with the old plan'); }
      return;
    }
    var P = (H && H.poly) ? H.poly() : null, wt = (H && H.wallThk) ? H.wallThk() : 0.15, bx0 = 1e9, bx1 = -1e9, bz0 = 1e9, bz1 = -1e9;
    for (i = 0; P && i < P.length; i++) { bx0 = Math.min(bx0, P[i][0]); bx1 = Math.max(bx1, P[i][0]); bz0 = Math.min(bz0, P[i][1]); bz1 = Math.max(bz1, P[i][1]); }
    /* the lines an end may be taken to: the partitions' centre lines over
       their spans, and the inner faces of the outer walls */
    var LN = [];
    for (i = 0; i < (S.parts || []).length; i++) {
      var p = S.parts[i], sp = null;
      if (!p || p.sep || (p.st | 0) !== (f.storey | 0)) continue;
      try { sp = _partSpan(p); } catch (eS) { sp = null; }
      if (sp) LN.push({ o: p.o, c: p.c, lo: sp[0], hi: sp[1] });
    }
    if (P && P.length) {
      LN.push({ o: 'v', c: bx0 + wt, lo: bz0, hi: bz1 }); LN.push({ o: 'v', c: bx1 - wt, lo: bz0, hi: bz1 });
      LN.push({ o: 'h', c: bz0 + wt, lo: bx0, hi: bx1 }); LN.push({ o: 'h', c: bz1 - wt, lo: bx0, hi: bx1 });
    }
    function toWall(E, O) {
      var dx = E[0] - O[0], dz = E[1] - O[1], Ln = Math.hypot(dx, dz), best = null, bd = 0.40, q;
      if (!(Ln > 0.01)) return { p: E, how: 'as read' };
      var ux = dx / Ln, uz = dz / Ln;
      for (q = 0; q < LN.length; q++) {
        var ln = LN[q], t;
        if (ln.o === 'v') { if (Math.abs(ux) < 0.2) continue; t = (ln.c - O[0]) / ux; }
        else { if (Math.abs(uz) < 0.2) continue; t = (ln.c - O[1]) / uz; }
        var X2 = O[0] + ux * t, Z2 = O[1] + uz * t, al = (ln.o === 'v') ? Z2 : X2;
        if (al < ln.lo - 0.1 || al > ln.hi + 0.1) continue;
        var dd = Math.abs(t - Ln);
        if (t > 0.2 && dd < bd) { bd = dd; best = [X2, Z2]; }
      }
      return best ? { p: best, how: (bd < 0.005 ? 'on a wall' : ('taken ' + _impN(bd) + ' m to a wall')) } : { p: E, how: 'meets no wall -- left as read' };
    }
    for (i = 0; i < L.length; i++) {
      var g = L[i], A0 = g.a, B0 = g.b;
      var eA = toWall(A0, B0), eB = toWall(B0, A0), A = eA.p, B = eB.p;
      var dx = B[0] - A[0], dz = B[1] - A[1], Ln = Math.hypot(dx, dz);
      if (!(Ln > 0.3)) { said.push('one ' + _impN(Ln) + ' m long left out'); continue; }
      var ux = dx / Ln, uz = dz / Ln, DR = [];
      var ox = A0[0] - A[0], oz = A0[1] - A[1], shift = ox * ux + oz * uz;
      for (k = 0; g.doors && k < g.doors.length; k++) {
        var d = g.doors[k], u = _impR(Math.max(d.w / 2 + 0.05, Math.min(Ln - d.w / 2 - 0.05, d.u + shift))), sw = 0;
        var cx = A[0] + ux * u, cz = A[1] + uz * u;
        if (d.kind !== 'pass' && d.kind !== 'slide' && d.kind !== 'bypass') {
          if (d.hp && ((d.hp[0] - cx) * ux + (d.hp[1] - cz) * uz) > 0.02) sw |= 1;
          if (d.op && ((d.op[0] - cx) * (-uz) + (d.op[1] - cz) * ux) > 0.02) sw |= 2;
        }
        DR.push({ u: u, w: d.w, kind: d.kind, sw: sw });
      }
      out.push({ id: 'g' + (i + 1), st: f.storey | 0, a: [_impR(A[0]), _impR(A[1])], b: [_impR(B[0]), _impR(B[1])], doors: DR });
      said.push(_impN(Ln) + ' m at ' + Math.round(Math.atan2(Math.abs(dz), Math.abs(dx)) * 180 / Math.PI) + ' deg (' + eA.how + ' / ' + eB.how + ')' +
                (DR.length ? (' with ' + DR.map(function (q) { return q.kind + ' ' + _impN(q.w); }).join(', ')) : ', no door'));
    }
    if (out.length) PR.diagWalls = out; else delete PR.diagWalls;
    _impSay('PLAN slanted walls written: ' + out.length + ' -- ' + said.join(' | ') + ' -- drawn on the plan, built by the product, not edited by hand');
  }
  /* the project's slanted walls on a storey, for the product that builds them
     and for the drawing: each with its direction and length worked out */
  function _impDiags(st) {
    var C = _impCore(), L = (C && C.STATE && C.STATE.product && C.STATE.product.diagWalls) || [], out = [], i;
    for (i = 0; i < L.length; i++) {
      var g = L[i];
      if (!g || !g.a || !g.b) continue;
      if (st != null && (g.st | 0) !== (st | 0)) continue;
      var dx = g.b[0] - g.a[0], dz = g.b[1] - g.a[1], Ln = Math.hypot(dx, dz);
      if (!(Ln > 0.3)) continue;
      out.push({ id: g.id, st: g.st | 0, a: g.a, b: g.b, len: Ln, ux: dx / Ln, uz: dz / Ln, ang: Math.atan2(dz, dx), doors: (g.doors || []).slice() });
    }
    return out;
  }
  /* drawn as a partition is: the partition's grey at its thickness, broken
     at each door, and the door's own symbol turned to the wall */
  function _impDiagDraw(ctx, X, Y, ppm) {
    var H = _PH, C = _impCore();
    if (!H || !C || !C.STATE) return;
    var L = _impDiags(C.STATE.planStorey | 0), i, k;
    if (!L.length) return;
    var t = (H.partThk ? H.partThk() : 0.1), Dw = DRAW;
    ctx.save();
    if (ctx.setLineDash) ctx.setLineDash([]);
    ctx.fillStyle = '#374151';
    for (i = 0; i < L.length; i++) {
      var g = L[i], nx = -g.uz * t / 2, nz = g.ux * t / 2, cuts = [], cur = 0;
      for (k = 0; k < g.doors.length; k++) cuts.push([g.doors[k].u - g.doors[k].w / 2, g.doors[k].u + g.doors[k].w / 2]);
      cuts.sort(function (p, q) { return p[0] - q[0]; });
      var slab = function (s0, s1) {
        if (s1 - s0 < 0.01) return;
        var ax = g.a[0] + g.ux * s0, az = g.a[1] + g.uz * s0, bx = g.a[0] + g.ux * s1, bz = g.a[1] + g.uz * s1;
        ctx.beginPath();
        ctx.moveTo(X(ax + nx), Y(az + nz)); ctx.lineTo(X(bx + nx), Y(bz + nz));
        ctx.lineTo(X(bx - nx), Y(bz - nz)); ctx.lineTo(X(ax - nx), Y(az - nz));
        ctx.closePath(); ctx.fill();
      };
      for (k = 0; k < cuts.length; k++) { slab(cur, cuts[k][0]); cur = Math.max(cur, cuts[k][1]); }
      slab(cur, g.len);
      for (k = 0; Dw && k < g.doors.length; k++) {
        var d = g.doors[k], cx = g.a[0] + g.ux * d.u, cz = g.a[1] + g.uz * d.u, wpx = d.w * ppm, tpx = t * ppm + 2;
        ctx.save();
        ctx.translate(X(cx), Y(cz));
        ctx.rotate(g.ang);
        try {
          if (d.kind === 'pass') Dw.opening(ctx, 0, 0, wpx, tpx, Dw.SYM.PASS, false, 1);
          else if (d.kind === 'pair' && Dw.door2) Dw.door2(ctx, 0, 0, wpx, tpx, d.sw | 0, false);
          else if (d.kind === 'bifold' && Dw.bifold) Dw.bifold(ctx, 0, 0, wpx, tpx, (d.sw | 0) | ((d.w > 1.1) ? 4 : 0), false);
          else if (d.kind === 'bypass' && Dw.bypass) Dw.bypass(ctx, 0, 0, wpx, tpx, false);
          else if (d.kind === 'slide') Dw.opening(ctx, 0, 0, wpx, tpx, Dw.SYM.SLIDE, false, 1, 0);
          else Dw.door(ctx, 0, 0, wpx, tpx, d.sw | 0, false);
        } catch (eD) {}
        ctx.restore();
      }
    }
    ctx.restore();
  }
  /* ---------- THE CORNER PAST A SLANTED WALL (v129) ----------------------
     The rooms are cut at the square partitions only, so the triangle a
     slanted wall cuts off a corner stays in the rectangle of the room
     BEHIND the wall: the V of two doors off a small hall was drawn in the
     bathroom's and the bedroom's colours, the hall measured without it, and
     a dashed room line ran down the middle of it (Marek: 2D nesouhlasi s
     3D). A rectangle a slanted wall runs right across -- both its ends on
     the rectangle's edges -- is cut along the wall's line, and a piece on
     the far side of the wall from the middle of its room is a corner. It
     belongs to the room on the other side of the room line it is open
     across; a corner closed by real walls all round keeps its room, as the
     ADU builds it. Each: { from, rect, to, poly, area } -- to is -1 where
     the corner stays with its own room. */
  function _impWedges(R) {
    var H = _PH, C = _impCore();
    if (!H || !C || !C.STATE || !R || !R.length) return [];
    var G = _impDiags(C.STATE.planStorey | 0), out = [], i, k, w, q, pass;
    if (!G.length) return [];
    var PS = (H.partitions ? H.partitions() : null) || [];
    function chord(g, r) {
      var t0 = -1e9, t1 = 1e9, ta, tb;
      if (Math.abs(g.ux) > 1e-9) { ta = (r.x0 - g.a[0]) / g.ux; tb = (r.x1 - g.a[0]) / g.ux; t0 = Math.max(t0, Math.min(ta, tb)); t1 = Math.min(t1, Math.max(ta, tb)); }
      else if (g.a[0] < r.x0 || g.a[0] > r.x1) return null;
      if (Math.abs(g.uz) > 1e-9) { ta = (r.z0 - g.a[1]) / g.uz; tb = (r.z1 - g.a[1]) / g.uz; t0 = Math.max(t0, Math.min(ta, tb)); t1 = Math.min(t1, Math.max(ta, tb)); }
      else if (g.a[1] < r.z0 || g.a[1] > r.z1) return null;
      return (t1 > t0) ? [t0, t1] : null;
    }
    function side(g, x, z) { return (x - g.a[0]) * (-g.uz) + (z - g.a[1]) * g.ux; }
    function half(Pg, g, sg) {
      var o = [], n = Pg.length, j;
      for (j = 0; j < n; j++) {
        var A = Pg[j], B = Pg[(j + 1) % n], dA = sg * side(g, A[0], A[1]), dB = sg * side(g, B[0], B[1]);
        if (dA >= 0) o.push(A);
        if ((dA >= 0) !== (dB >= 0)) { var t = dA / (dA - dB); o.push([A[0] + (B[0] - A[0]) * t, A[1] + (B[1] - A[1]) * t]); }
      }
      return o;
    }
    function area(Pg) { var a = 0, j; for (j = 0; j < Pg.length; j++) { var A = Pg[j], B = Pg[(j + 1) % Pg.length]; a += A[0] * B[1] - B[0] * A[1]; } return Math.abs(a) / 2; }
    for (i = 0; i < R.length; i++) {
      var RS = R[i].rects || [], ax = 0, az = 0, aa = 0;
      for (k = 0; k < RS.length; k++) { var r0 = RS[k], a0 = (r0.x1 - r0.x0) * (r0.z1 - r0.z0); ax += a0 * (r0.x0 + r0.x1) / 2; az += a0 * (r0.z0 + r0.z1) / 2; aa += a0; }
      if (!(aa > 0)) continue;
      var mx = ax / aa, mz = az / aa;
      for (k = 0; k < RS.length; k++) {
        var rc = RS[k], pcs = [{ p: [[rc.x0, rc.z0], [rc.x1, rc.z0], [rc.x1, rc.z1], [rc.x0, rc.z1]], far: false }], cut = false;
        for (w = 0; w < G.length; w++) {
          var g = G[w], ch = chord(g, rc);
          if (!ch || ch[1] - ch[0] < 0.3 || ch[0] < -0.25 || ch[1] > g.len + 0.25) continue;
          var sm = side(g, mx, mz);
          if (Math.abs(sm) < 0.05) continue;
          var nx = [];
          for (q = 0; q < pcs.length; q++) {
            var hp = half(pcs[q].p, g, 1), hm = half(pcs[q].p, g, -1);
            if (hp.length > 2 && area(hp) > 0.005) nx.push({ p: hp, far: pcs[q].far || sm < 0 });
            if (hm.length > 2 && area(hm) > 0.005) nx.push({ p: hm, far: pcs[q].far || sm > 0 });
          }
          pcs = nx; cut = true;
        }
        if (!cut) continue;
        for (q = 0; q < pcs.length; q++) if (pcs[q].far && area(pcs[q].p) >= 0.03) out.push({ from: i, rect: k, to: -1, poly: pcs[q].p, area: area(pcs[q].p) });
      }
    }
    if (!out.length) return out;
    /* whose it is: the room across the room line it is open over -- the
       longest such line wins; across a real wall or the shell is nobody's.
       A corner open only onto another corner takes that one's room once it
       is known, so it is asked again */
    function onSep(o, c, lo, hi) {
      var j, mid = (lo + hi) / 2;
      for (j = 0; j < PS.length; j++) {
        var p = PS[j];
        if (!p || !p.sep || p.o !== o || Math.abs(p.c - c) > 0.04) continue;
        var sp = H.partSpan ? H.partSpan(p) : null;
        if (sp && mid >= sp[0] - 0.02 && mid <= sp[1] + 0.02) return true;
      }
      return false;
    }
    for (pass = 0; pass < 3; pass++) {
      var changed = false;
      for (k = 0; k < out.length; k++) {
        var W = out[k], Pw = W.poly, n = Pw.length, best = -1, bestL = 0, cx = 0, cz = 0, e;
        if (W.to >= 0) continue;
        for (e = 0; e < n; e++) { cx += Pw[e][0] / n; cz += Pw[e][1] / n; }
        for (e = 0; e < n; e++) {
          var A = Pw[e], B = Pw[(e + 1) % n], hz = Math.abs(A[1] - B[1]) < 1e-6, vt = Math.abs(A[0] - B[0]) < 1e-6;
          if (!hz && !vt) continue;
          var L = hz ? Math.abs(B[0] - A[0]) : Math.abs(B[1] - A[1]);
          if (L < 0.1) continue;
          var emx = (A[0] + B[0]) / 2, emz = (A[1] + B[1]) / 2;
          if (!onSep(hz ? 'h' : 'v', hz ? emz : emx, hz ? Math.min(A[0], B[0]) : Math.min(A[1], B[1]), hz ? Math.max(A[0], B[0]) : Math.max(A[1], B[1]))) continue;
          var px = hz ? emx : emx + ((emx > cx) ? 0.06 : -0.06), pz = hz ? emz + ((emz > cz) ? 0.06 : -0.06) : emz;
          var own = _impRoomAtW(R, out, px, pz, k);
          if (own < 0 || own === W.from) continue;
          if (L > bestL) { bestL = L; best = own; }
        }
        if (best >= 0) { W.to = best; changed = true; }
      }
      if (!changed) break;
    }
    return out;
  }
  function _impWedgeHas(Pg, x, z) {
    var c = false, j, n = Pg.length;
    for (j = 0; j < n; j++) {
      var A = Pg[j], B = Pg[(j + n - 1) % n];
      if (((A[1] > z) !== (B[1] > z)) && (x < (B[0] - A[0]) * (z - A[1]) / (B[1] - A[1]) + A[0])) c = !c;
    }
    return c;
  }
  /* the room a point is in, a corner past a slanted wall counted as the room
     it belongs to; -1 for a corner not yet known, and outside every room.
     skip: a corner not to ask (the one asking) */
  function _impRoomAtW(R, W, x, z, skip) {
    var i, k;
    for (k = 0; W && k < W.length; k++) {
      if (k === skip) continue;
      if (_impWedgeHas(W[k].poly, x, z)) return W[k].to;
    }
    for (i = 0; i < R.length; i++) {
      var RS = R[i].rects || [];
      for (k = 0; k < RS.length; k++) if (x > RS[k].x0 && x < RS[k].x1 && z > RS[k].z0 && z < RS[k].z1) return i;
    }
    return -1;
  }
  /* how much floor a room gains (corners past slanted walls that are its)
     and loses (its own corners that are another's) */
  /* THE ROOM'S OWN FLOOR (v143, Marek: u nekterych mist nemame room name,
     ale mela by tam byt spon vymera -- a vymera nad floorplanem musi sedet za
     vsechny mistnosti). A room's cells run from the outline's outer face to
     the middle of each partition, so the figure in a room was gross: it took
     in the outer wall and half of every partition, and the rooms summed to
     more than the floor area over the plan, which takes the walls out. Each
     cell is pulled in now by the wall's thickness where it lies on the
     outline, and by half a partition where a partition stands along it -- a
     room line has no wall and pulls in nothing -- with the corners past the
     slanted walls added and taken as before. These are the figures in the
     rooms; the figure over the plan is their sum, each taken as it is
     written (0.1 m2, or a whole sqft), so the rooms add up to it to the last
     digit. */
  function _impRoomNet(r, ps, P, WT, PT) {
    var H = _PH, rs = (r && r.rects) || [], a = 0, j;
    function cover(o, c, lo, hi) {
      var got = [], q, Lc = 0, cur = null;
      for (q = 0; q < ps.length; q++) {
        var p = ps[q];
        if (!p || p.sep || p.o !== o || Math.abs(p.c - c) > 0.03) continue;
        var sp = null;
        try { sp = H.partSpan ? H.partSpan(p) : _partSpan(p); } catch (eS) { sp = null; }
        if (!sp) continue;
        var a0 = Math.max(lo, Math.min(sp[0], sp[1])), a1 = Math.min(hi, Math.max(sp[0], sp[1]));
        if (a1 > a0) got.push([a0, a1]);
      }
      got.sort(function (x, y) { return x[0] - y[0]; });
      for (q = 0; q < got.length; q++) {
        if (!cur || got[q][0] > cur[1]) { if (cur) Lc += cur[1] - cur[0]; cur = [got[q][0], got[q][1]]; }
        else cur[1] = Math.max(cur[1], got[q][1]);
      }
      if (cur) Lc += cur[1] - cur[0];
      return (hi > lo) ? Math.min(1, Lc / (hi - lo)) : 0;
    }
    function outer(o, c, lo, hi, side) {
      var n = 0, q;
      for (q = 0; q < 5; q++) {
        var t = lo + (hi - lo) * (q + 0.5) / 5;
        if (!H.inPoly(o === 'v' ? c + side * 0.03 : t, o === 'v' ? t : c + side * 0.03, P)) n++;
      }
      return n / 5;
    }
    function inset(o, c, lo, hi, side) {
      var fo = outer(o, c, lo, hi, side);
      return fo * WT + (1 - fo) * cover(o, c, lo, hi) * PT / 2;
    }
    for (j = 0; j < rs.length; j++) {
      var cl = rs[j], w = cl.x1 - cl.x0, h = cl.z1 - cl.z0;
      if (!(w > 0) || !(h > 0)) continue;
      var iL = inset('v', cl.x0, cl.z0, cl.z1, -1), iR = inset('v', cl.x1, cl.z0, cl.z1, 1);
      var iT = inset('h', cl.z0, cl.x0, cl.x1, -1), iB = inset('h', cl.z1, cl.x0, cl.x1, 1);
      a += Math.max(0, w - iL - iR) * Math.max(0, h - iT - iB);
    }
    return a;
  }
  /* each room's own floor, the corners past slanted walls in or out */
  function _impNetAreas(R, WG) {
    var H = _PH, out = [], i;
    if (!H || !R) return out;
    var ps = H.partitions() || [], P = H.poly(), WT = H.wallThk ? H.wallThk() : 0.16, PT = H.partThk ? H.partThk() : 0.1;
    for (i = 0; i < R.length; i++) {
      var g = 0;
      try { g = _impRoomNet(R[i], ps, P, WT, PT); } catch (eN) { g = R[i].area || 0; }
      out.push(Math.max(0, g + _impWedgeDelta(WG, i)));
    }
    return out;
  }
  /* an area as the plan writes it (fmtArea: 0.1 m2, or a whole sqft), back
     in m2 -- what the rooms are summed in */
  function _impAreaShown(a) {
    var H = _PH, met = true;
    try { met = H && H.isMetric ? !!H.isMetric() : true; } catch (eM) { met = true; }
    return met ? Number((+a || 0).toFixed(1)) : Math.round((+a || 0) * 10.7639) / 10.7639;
  }
  /* for the product: every room's own floor and their sum as written */
  function _impNetAreasNow() {
    var H = _PH;
    if (!H || !H.rooms) return null;
    var R = H.rooms() || [], WG = [], tot = 0, i;
    try { WG = _impWedges(R) || []; } catch (eW) { WG = []; }
    var A = _impNetAreas(R, WG);
    for (i = 0; i < A.length; i++) tot += _impAreaShown(A[i]);
    return { rooms: A, total: Math.round(tot * 10000) / 10000, v: 143 };
  }
  /* A CLOSET, OR A ROOM TOO SMALL FOR ITS NAME, BY ITS AREA ALONE (v143): in
     its biggest cell, the size of the figure in a room, smaller down to 8 px
     when it does not fit, turned up a narrow cell, and left out only when it
     fits neither way -- it comes back as the plan is zoomed in. A closet's
     door has its width written on a leader into the closet, so the figure
     keeps off those: the middle of the cell first, then further along and
     across it, the first place that stays clear. */
  function _impNoteBoxes(ctx, X, Y) {
    var H = _PH, out = [], DW = [], q;
    try { DW = H.doorsLive ? (H.doorsLive() || []) : []; } catch (eD) { DW = []; }
    ctx.save();
    ctx.font = '600 12px Montserrat, Arial';
    for (q = 0; q < DW.length; q++) {
      var d = DW[q].d, p = DW[q].p;
      if (!d || !p) continue;
      var v = (p.o === 'v'), cx = v ? p.c : d.u, cz = v ? d.u : p.c, os = ((d.sw | 0) & 2) ? -1 : 1;
      var lx = X(v ? (cx - os * 0.55) : cx), lz = Y(v ? cz : (cz + os * 0.55)), tw = 24;
      try { tw = ctx.measureText(DRAW.fmtDoor(d.w || (H.doorW ? H.doorW() : 0.8), H.isMetric())).width; } catch (eT) { tw = 24; }
      out.push([lx - tw / 2 - 3, lz - 9, lx + tw / 2 + 3, lz + 9]);
      out.push([X(cx) - 5, Y(cz) - 5, X(cx) + 5, Y(cz) + 5]);
      /* and the leader between them */
      out.push([Math.min(X(cx), lx) - 3, Math.min(Y(cz), lz) - 3, Math.max(X(cx), lx) + 3, Math.max(Y(cz), lz) + 3]);
    }
    ctx.restore();
    return out;
  }
  /* v186: does a label w x h px take no more than 80 % of the room where it
     stands -- the piece of the room under its middle, else its biggest.
     v195 (Marek: muzou se zobrazit ve chvili, kdy napisy nepretekaji do
     sousednich mistnosti): the whole room -- the name shows as soon as it
     runs into no other room */
  var IMP_LABEL_FIT = 1.0;
  /* v187 (Marek: nazvy mistnosti nejsou videt ani pri velkem priblizeni u
     nekterych mistnosti): a room of several pieces -- a bedroom run into a
     hall, an L -- has its middle in a narrow piece, and its name never fit
     there however far it was zoomed. Where it stands first; when it does
     not fit there, the middle of the room's biggest piece, where it does.
     Returns the spot in plan metres, or null. */
  function _impLabelSpot(r, wPx, hPx, ppm) {
    var rs = (r && r.rects) || [], k, f = IMP_LABEL_FIT || 0.8;
    if (!rs.length) return { x: r.cx, z: r.cz };
    /* v191 (Marek: neobjevuje se vubec living room name): a room cut into
       strips -- an open living room round a closet -- has no single piece
       wide and tall enough, though the name fits it easily. The label's box
       (grown so the text is 80 % of it) must lie in the room, over as many
       pieces as it takes; the middle first, then the middles of the pieces,
       the biggest first, then their quarters */
    var hw = wPx / (2 * ppm * f), hh = hPx / (2 * ppm * f);
    function inRoom(x, z) {
      for (var q = 0; q < rs.length; q++) if (x >= rs[q].x0 - 1e-6 && x <= rs[q].x1 + 1e-6 && z >= rs[q].z0 - 1e-6 && z <= rs[q].z1 + 1e-6) return true;
      return false;
    }
    function boxIn(cx, cz) {
      var a, b;
      for (a = -2; a <= 2; a++) for (b = -2; b <= 2; b++) if (!inRoom(cx + hw * a / 2, cz + hh * b / 2)) return false;
      return true;
    }
    if (boxIn(r.cx, r.cz)) return { x: r.cx, z: r.cz };
    var S = rs.slice().sort(function (p, q) { return (q.x1 - q.x0) * (q.z1 - q.z0) - (p.x1 - p.x0) * (p.z1 - p.z0); }), t;
    for (k = 0; k < S.length; k++) {
      var Q = S[k], mx = (Q.x0 + Q.x1) / 2, mz = (Q.z0 + Q.z1) / 2;
      if (boxIn(mx, mz)) return { x: mx, z: mz };
    }
    for (k = 0; k < Math.min(3, S.length); k++) {
      var Q2 = S[k];
      for (t = 0.25; t <= 0.75; t += 0.5) {
        var px = Q2.x0 + (Q2.x1 - Q2.x0) * t, pz = Q2.z0 + (Q2.z1 - Q2.z0) * t, mx2 = (Q2.x0 + Q2.x1) / 2, mz2 = (Q2.z0 + Q2.z1) / 2;
        if (boxIn(px, mz2)) return { x: px, z: mz2 };
        if (boxIn(mx2, pz)) return { x: mx2, z: pz };
      }
    }
    return null;
  }
  function _impLabelFits(r, wPx, hPx, ppm) { return !!_impLabelSpot(r, wPx, hPx, ppm); }
  function _impAreaOnly(ctx, r, a, X, Y, ppm) {
    var H = _PH;
    if (!H || !r || !(a > 0.05)) return;
    var rs = r.rects || [], big = null, bA = -1, k;
    for (k = 0; k < rs.length; k++) { var aa = (rs[k].x1 - rs[k].x0) * (rs[k].z1 - rs[k].z0); if (aa > bA) { bA = aa; big = rs[k]; } }
    if (!big) return;
    var txt = H.fmtArea(a), wP = (big.x1 - big.x0) * ppm, hP = (big.z1 - big.z0) * ppm, fs, tw = 0, rot = false, ok = false;
    for (fs = Math.max(10, Math.round(ppm * 0.14)); fs >= 8; fs--) {
      ctx.font = '600 ' + fs + 'px Montserrat, Arial';
      tw = ctx.measureText(txt).width;
      if (tw + 6 <= wP && fs + 4 <= hP) { ok = true; break; }
      if (tw + 6 <= hP && fs + 4 <= wP) { ok = true; rot = true; break; }
    }
    if (!ok) return;
    var NB = [];
    try { NB = _impNoteBoxes(ctx, X, Y); } catch (eNB) { NB = []; }
    var cx0 = X((big.x0 + big.x1) / 2), cy0 = Y((big.z0 + big.z1) / 2), hw = (rot ? fs + 4 : tw + 6) / 2, hh = (rot ? tw + 6 : fs + 4) / 2;
    var longX = wP >= hP, AL = [0, -0.25, 0.25, -0.38, 0.38], AC = [0, -0.25, 0.25], best = null, ia, ic, q;
    for (ia = 0; ia < AL.length && !best; ia++) for (ic = 0; ic < AC.length && !best; ic++) {
      var px = cx0 + (longX ? AL[ia] * wP : AC[ic] * wP), py = cy0 + (longX ? AC[ic] * hP : AL[ia] * hP);
      if (px - hw < cx0 - wP / 2 || px + hw > cx0 + wP / 2 || py - hh < cy0 - hP / 2 || py + hh > cy0 + hP / 2) continue;
      var hit = false;
      for (q = 0; q < NB.length && !hit; q++) hit = (px - hw < NB[q][2] && px + hw > NB[q][0] && py - hh < NB[q][3] && py + hh > NB[q][1]);
      if (!hit) best = [px, py];
    }
    if (!best) best = [cx0, cy0];
    ctx.save();
    ctx.translate(best[0], best[1]);
    if (rot) ctx.rotate(-Math.PI / 2);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#111827';
    ctx.fillText(txt, 0, 0);
    ctx.restore();
  }
  function _impWedgeDelta(W, i) {
    var d = 0, k;
    for (k = 0; W && k < W.length; k++) {
      if (W[k].to < 0) continue;
      if (W[k].to === i) d += W[k].area;
      if (W[k].from === i) d -= W[k].area;
    }
    return d;
  }
  /* a room line with the same room on both sides along it -- the line the
     plan needed to cut the corner off the room behind the wall -- parts
     nothing once the corner is the room's own, and is not drawn */
  function _impSepInside(p, sp, R, W) {
    var any = false, k, f;
    for (k = 0; W && k < W.length; k++) if (W[k].to >= 0) any = true;
    if (!any || !sp) return false;
    for (f = 1; f <= 3; f++) {
      var at = sp[0] + (sp[1] - sp[0]) * f / 4, a, b;
      if (p.o === 'v') { a = _impRoomAtW(R, W, p.c - 0.06, at, -1); b = _impRoomAtW(R, W, p.c + 0.06, at, -1); }
      else { a = _impRoomAtW(R, W, at, p.c - 0.06, -1); b = _impRoomAtW(R, W, at, p.c + 0.06, -1); }
      if (a < 0 || a !== b) return false;
    }
    return true;
  }
  /* for the log: which corner went to which room */
  function _impWedgeSay() {
    var H = _PH;
    if (!H || !H.rooms) return '';
    var R = H.rooms() || [], W = _impWedges(R), o = [], k;
    if (!W.length) return '';
    function nm(i) { try { return (R[i] && R[i].closet) ? 'a closet' : (H.roomName ? H.roomName(R[i], i) : ('room ' + (i + 1))); } catch (e) { return 'room ' + (i + 1); } }
    for (k = 0; k < W.length; k++) {
      var Wk = W[k], a = Math.round(Wk.area * 100) / 100;
      if (Wk.to >= 0) o.push(a + ' m2 past ' + nm(Wk.from) + '\'s slanted wall is ' + nm(Wk.to) + '\'s floor');
      else o.push(a + ' m2 past ' + nm(Wk.from) + '\'s slanted wall stays its floor -- open to no other room across a room line');
    }
    return 'DIAG corners on the plan (v129): ' + o.join('; ');
  }
  /* THE ROOF ASKED FOR WITH A NEW LAYOUT (v153, Marek: agent nereagoval na
     pozadavek upravy s pultovou strechou, zustala valba). Words that change
     the layout go to the designer, and the designer draws rooms -- the roof
     in the same words was dropped by the worker, which kept the redesign
     alone. The worker (w2.08) now keeps the roof and the terrace beside it,
     and here they are written once the new plan is, by the change's own
     code: one more Undo step, each said done or refused with why. */
  function _impAfterOps(R0) {
    var A = R0.after || [], keepL = (_imp.applyLines || []).slice(), keepA = _imp.applied, PJ = null,
        tag = R0.lib ? 'PLAN with the library plan' : (R0.redesign ? 'PLAN with the redesign' : 'PLAN with the plan from words');
    if (!A.length) return;
    /* v156: the roof over the terrace -- the wall the terrace lies against
       and its depth (3 m at most), as the plan was written; no terrace, or
       one standing free, and there is nothing to cover */
    var pre = [], CT = _impCore(), TT = CT && CT.STATE && CT.STATE.product && CT.STATE.product.terrace;
    A = A.map(function (o) {
      if (!(o.op === 'roof_overhang' && o.ref === 'terrace' && !o.wall)) return o;
      var opn = (o.kind === 'open'), wsd = opn ? 'an open terrace' : 'a roof over the terrace';
      if (!TT || !TT.on || !(TT.sx > 0) || !(TT.sz > 0)) { pre.push(tag + ': ' + wsd + ' was asked for, and the plan has no terrace -- nothing to do (v156)'); return null; }
      var wl = IMP_CHG_OVL[TT.side] || '';
      if (!wl) { pre.push(tag + ': ' + wsd + ' was asked for, and the terrace stands free of the house -- nothing to do (v156)'); return null; }
      var dp = (wl === 'N' || wl === 'S') ? TT.sz : TT.sx;
      var o2 = JSON.parse(JSON.stringify(o));
      o2.ref = ''; o2.kind = ''; o2.wall = wl;
      if (opn) {
        /* v157: the roof's own eave over that wall, as on the other sides */
        var RR = (CT && CT.STATE && CT.STATE.roof) || {}, opp = { z0: 'z1', z1: 'z0', x0: 'x1', x1: 'x0' }[TT.side];
        var ev = (RR.over && RR.over[opp] > 0) ? RR.over[opp] : ((RR.eave > 0) ? RR.eave : 0.45);
        if (RR.type === 'flat') { pre.push(tag + ': an open terrace was asked for -- the roof is flat and reaches over nothing, the terrace is open as it is (v157)'); return null; }
        o2.d = Math.max(0.3, Math.min(1.0, Math.round(ev * 100) / 100));
        pre.push(tag + ': the terrace stays open -- against the ' + wl + ' wall, ' + _impN(dp) + ' m deep; the roof reaches past that wall as past the one across, ' + _impN(o2.d) + ' m (v157)');
        return o2;
      }
      o2.d = Math.max(0.3, Math.min(3, Math.round(dp * 100) / 100));
      pre.push(tag + ': the roof over the terrace -- the terrace lies against the ' + wl + ' wall, ' + _impN(dp) + ' m deep, so the roof reaches ' + _impN(o2.d) + ' m past that wall (v156)');
      return o2;
    }).filter(function (o) { return !!o; });
    if (!A.length) { _imp.applyLines = keepL.concat(pre); _impRefresh(); return; }
    keepL = keepL.concat(pre);
    try { PJ = _impChgProject(); } catch (eP) { PJ = null; }
    if (!PJ) { _imp.applyLines = keepL.concat([tag + ': ' + A.map(_impChgOpTxt).join(', ') + ' NOT written -- the project could not be described']); _impTickAny(); return; }
    _impChgApply({ chg: { ops: A, question: '', summary: '' }, map: PJ.M, text: R0.afterText || R0.text || '', change: true });
    _imp.applyLines = keepL.concat((_imp.applyLines || []).map(function (t) { return t.replace(/^PLAN change/, tag); }));
    _imp.applied = keepA;
    /* v155: the box as well as the lines -- the library road saves nothing
       after this, so nothing else would show it */
    _impRefresh();
  }
  var IMP_USER = {
    start: function (cb) { _impUserBegin(cb); _impPick(); },
    readFile: function (file, cb) { if (!file) return false; _impUserBegin(cb); _impReadFile(file); _impUserClock(); return true; },
    /* v123: a change to the plan there is -- the operations the designer
       answers are written in one Undo step; a question comes back in the
       state; a new house is handed on to describe() */
    change: function (text, cb, opts) {
      if (!String(text == null ? '' : text).trim()) return false;
      _impUserBegin(cb);
      var ok = _impChange(text, opts);
      _impUserClock();
      return ok;
    },
    /* v120: a description instead of a picture; opts.product names the
       configurator for the designer (the project's own name otherwise) */
    describe: function (text, cb, opts) {
      if (!String(text == null ? '' : text).trim()) return false;
      _impUserBegin(cb);
      /* v154: a ready plan from the library first, unless asked not to */
      var ok = (opts && opts.noMatch) ? _impDescribe(text, opts) : _impMatchThenDescribe(text, opts);
      _impUserClock();
      return ok;
    },
    /* v136: the plan library -- the list for the drawer (cb(err, answer)),
       one plan taken out onto the road of a design, one of one's own taken
       out of the library again */
    library: function (cb, opts) { return _impLibList(cb, opts); },
    fromLibrary: function (id, cb, opts) {
      if (!_impLibIdOk(id)) return false;
      _impUserBegin(cb);
      /* v199: { mirror: true } -- the plan turned over left to right */
      var ok = _impLibLoad(String(id), (opts && opts.mirror) ? { mirror: true } : undefined);
      _impUserClock();
      return ok;
    },
    libraryDrop: function (id, cb) { return _impLibDrop(id, cb); },
    /* v199: the plan in this project turned over left to right, built again
       and handed on as a plan from the library is (the listener hears it
       ready; apply() writes it, one Undo step) */
    mirror: function (cb) {
      var M = _impStateMirror();
      if (M.ok) {
        _impSay('PLAN mirror (v199): the project turned over left to right as it stands -- ' + M.said + ' (Undo takes it back)');
        try { _impRefresh(); } catch (eR) {}
        return 'state';
      }
      _impSay('PLAN mirror (v199): ' + M.why + ' -- the plan is built again turned over instead');
      _impUserBegin(cb);
      return _impMirrorNow() ? 'plan' : false;
    },
    /* v152: the plan read on the homepage, by the id the worker kept it under */
    fromHand: function (id, cb) {
      if (!_impHandIdOk(id)) return false;
      _impUserBegin(cb);
      var ok = _impHandLoad(String(id));
      _impUserClock();
      return ok;
    },
    on: function (cb) { _impUserCb = cb || null; },
    state: _impUserState,
    apply: function () {
      var Rc = _imp.rd;
      if (_imp.user && Rc && Rc.change) {
        if (_imp.applied || Rc.st !== 'done' || !Rc.chg || !Rc.chg.ops.length || Rc.chg.question) return false;
        _impChgApply(Rc);
        /* v153: the plan as it stands after the change, into the library */
        if (_imp.applied) { try { _impLibSave(Rc); } catch (eLc) { _impSay('PLAN library: saving the changed plan THREW ' + ((eLc && eLc.message) || eLc)); } }
        return !!_imp.applied;
      }
      if (!_imp.user || !_imp.fit || !_imp.fit.ok || _imp.applied) return false;
      /* A REDESIGN IS A CHANGE (v124): RESTORE and the bar's reset still go
         back to the house before the FIRST plan, not to the plan it redrew */
      var R0 = _imp.rd, KB = (R0 && R0.redesign) ? { before: _imp.before || null, dig: _imp.digBefore, kept: _impKept() } : null;
      _impDoApply();
      if (_imp.applied && KB) _impKeepFirst(KB);
      /* v153: the roof and the terrace the words asked for beside the redesign */
      if (_imp.applied && R0 && R0.after && R0.after.length) { try { _impAfterOps(R0); } catch (eAf) { _impSay('PLAN the roof and the terrace with the plan THREW ' + ((eAf && eAf.message) || eAf)); } }
      /* the designer's ideas go with the plan written (v124) */
      if (_imp.applied && R0 && !R0.change) _impIdeasSet(R0.ideas || [], R0.redesign ? 'the redesigned plan' : (R0.text ? 'the plan from words' : 'the plan read'));
      /* v136: a plan built clean goes into the plan library */
      if (_imp.applied && R0 && !R0.change) { try { _impLibSave(R0); } catch (eLs) { _impSay('PLAN library: saving THREW ' + ((eLs && eLs.message) || eLs)); } }
      /* v152: built, to the worker's record of the reading */
      if (_imp.applied && R0 && R0.rec && !R0.change) _impNote(R0, 'built', (_imp.fit && _imp.fit.scaled) ? _impScaledTxt(_imp.fit.scaled) : '');
      /* a described plan applied over one read from a picture (v120): the
         old picture no longer lies under anything it shows */
      var R = _imp.rd, T0 = _imp.traceOf;
      if (_imp.applied && R && (R.text || R.lib) && T0 && T0 !== R) {
        try { if (TRACE.img && TRACE.img === T0.imgEl) { TRACE.img = null; TRACE.armed = false; OVERLAY.repaint(); } } catch (eT) {}
        _imp.traceOf = null;
      }
      return !!_imp.applied;
    },
    /* v171: the plan too big, built smaller on the buyer's word -- true when
       it fits that way (the listener then hears it ready, as any plan) */
    squeeze: function (opts) {
      var R = _imp.rd;
      if (!R || R.st !== 'done' || !R.n || !R.n.ok || R.change || _imp.applied) return false;
      _imp.squeeze = IMP_SQUEEZE_MAX;
      /* v175: lib -- no product builds it as drawn, so the library and the
         map take it as built here */
      _imp.squeezeLib = !!(opts && opts.lib);
      _impSay('PLAN squeeze (v171): the buyer asked to build it smaller than drawn -- up to ' + Math.round(IMP_SQUEEZE_MAX * 100) + ' % over the largest size, the living rooms, the kitchen and the bedrooms giving it first');
      _impUserTick();
      var ok = !!(_imp.fit && _imp.fit.ok);
      if (!ok) { _imp.squeeze = 0; _impSay('PLAN squeeze (v171): it does not fit even so -- ' + ((_imp.fit && _imp.fit.why) || '?')); }
      return ok;
    },
    /* v171: the reading handed to another product on this site (?read=id),
       kept on this device -- nothing read or charged again there */
    handOff: function (to) { return _impHandLocalPut(to); },
    /* v175: the reading handed to the product that builds it as drawn, for
       the admin's REF there to build, check and publish (worker w2.22) */
    queueFor: function (to, place) { return _impQueueFor(to, place); },
    /* the reading and its violet plan go; the picture under the plan with them */
    cancel: function () {
      var R = _imp.rd;
      _imp.squeeze = 0; _imp.squeezeLib = false;
      _imp.rd = null; _imp.prev = null; _imp.fit = null; _imp.n2 = null; _imp.applyLines = null;
      try { if (R && R.traceKey && TRACE.img === R.imgEl) { TRACE.img = null; TRACE.armed = false; OVERLAY.repaint(); } } catch (eT) {}
      _impUserTick();
      return true;
    },
    restore: function () { _impDoRestore(); return !!_imp.restored; },
    lines: function () { return (_imp.lines || []).slice(); },
    /* v124: the designer's suggestions, and one taken off when it was used
       or dismissed (by its text or its place in the list) */
    ideas: _impIdeasNow,
    ideaDone: function (which, why) {
      var L = _impIdeasNow(), i = (typeof which === 'number') ? which : L.indexOf(String(which == null ? '' : which).replace(/\s+/g, ' ').trim());
      if (i < 0 || i >= L.length) return false;
      L.splice(i, 1);
      _impIdeasSet(L, why || 'one taken off');
      return true;
    }
  };
  /* the house before the first plan stays what RESTORE goes back to (v124) */
  function _impKeepFirst(KB) {
    var how = '';
    if (KB.kept) { KB.kept.digAfter = _imp.digAfter; _impKeepWrite(KB.kept); how = 'the copy on this device'; }
    else if (KB.before) {
      var kn = _impKept();
      if (kn) { kn.before = KB.before; kn.dig = KB.dig; kn.digAfter = _imp.digAfter; _impKeepWrite(kn); }
      how = 'the copy in this page';
    }
    if (KB.before) { _imp.before = KB.before; _imp.digBefore = KB.dig; }
    else if (KB.kept) { _imp.before = null; _imp.digBefore = KB.kept.dig; }
    _impSay('PLAN redesign written -- RESTORE ' + (how ? ('still goes back to the house before the first plan (' + how + ')') : 'goes back to the plan before the redesign: no copy of the house before the first plan was kept'));
  }

  var IMPORTER = {
    /* the buyer's road (v119) */
    user: IMP_USER,
    normalize: _impNormalize,
    lines: _impLines,
    /* a plan read (centred on the house for comparison) or a plan fitted
       (drawn where it will land); null takes it away */
    preview: function (n) { _imp.prev = (n && n.ok) ? n : null; },
    fit: _impFit,
    fitLines: _impFitLines,
    apply: _impApply,
    applyLines: _impApplyLines,
    restore: _impRestore,
    sample: _impSampleRaw,
    sampleRect: _impSampleRect,
    /* part 3: what the model returns, made into a plan the importer reads */
    fromModel: _impFromModel,
    dimText: _impDimText,
    /* v133: the reading in hand, for the test bench */
    reading: function () { return _imp.rd || null; },
    /* v136: the plan as built, in the designer's shape -- what the library keeps */
    libExport: function () { return _impLibExport(); },
    /* v137: the library's check of the plan in hand, and the drawer's picture of it */
    libGrade: function () { return _impLibGrade(_imp.rd); },
    libThumb: function () { return _impLibThumb(); },
    /* v152: the picture made ready for the reader -- shrunk, its walls traced,
       its fingerprint -- for the homepage banner, which reads on the same
       road: cb(err, { url, b64, w, h, w0, h0, walls, fp }) */
    shrink: function (file, cb) { _impShrink(file, cb); },
    /* v125: the project's slanted walls, for the product that builds them */
    /* v158: what the configurator found coming back to the page, into the box */
    tabSay: function (t) { _imp.tab = String(t == null ? '' : t).slice(0, 400); try { _impRefresh(); } catch (e) {} },
    /* v163: the reader's last answer, as RAW copies it -- '' when there is none */
    rawText: function () { return _impRawJson() || _impLastKept(); },
    diags: _impDiags,
    /* v199: LEVA's plans into the library (admin), the test bench, the
       mirror for a plan on a sheet */
    seed: function (from) { return _impSeedRun(from); },
    flatTest: _impFlatTest,
    flatSheet: _impFlatSheet,
    modelMirror: _impModelMirror
  };
  /* v199: ?planseed=N starts LEVA's plans into the library from the Nth,
     once the house is up */
  (function () {
    try {
      /* v207 (Marek 2026-10-12, typed with caps lock on: ?PLANSEED=29 started
         nothing): the parameter read in any case */
      var mS = /[?&]planseed=(\d+)(?:-(\d+))?/i.exec(String((global.location && global.location.search) || ''));
      if (!mS || typeof global.setInterval !== 'function') return;
      /* ONE RUN PER LINK (v201, Marek: pri refresh stranky se porad
         prestavuji domy). The address kept ?planseed=N, and the save after
         every plan kept it too, so each refresh of the tab started the whole
         seed again -- plan after plan built and written. It comes off the
         address the moment it is read: this run goes on, a refresh is an
         ordinary page. */
      try {
        if (global.history && typeof global.history.replaceState === 'function') {
          var u0 = String(global.location.href || '');
          var u1 = u0.replace(/([?&])planseed=[\d-]+(&?)/i, function (m, a, b) { return b ? a : ''; }).replace(/\?(#|$)/, '$1');
          if (u1 !== u0) { global.history.replaceState(global.history.state, '', u1); _impSay('PLAN SEED (v201): ?planseed taken off the address -- a refresh does not start it again'); }
        }
      } catch (eU) {}
      var tries = 0, tS = global.setInterval(function () {
        tries++;
        var ok = false;
        try { ok = !!(_impCore() && _PH && _PH.poly && (_PH.poly() || []).length >= 3); } catch (eP) { ok = false; }
        if (ok) { global.clearInterval(tS); global.setTimeout(function () { _impSeedRun(Math.max(0, parseInt(mS[1], 10) - 1), mS[2] ? (parseInt(mS[2], 10) - 1) : null); }, 4000); }
        else if (tries > 90) { global.clearInterval(tS); _impSay('PLAN SEED (v199): the house never came up -- not started'); }
      }, 1000);
    } catch (eS) {}
  })();

  var FIXTURES = {
    confine: _fixConfine,
    backAtWall: _fixBackAtWall,
    catalog: function () { return FIX; },
    order: function () { return FIX_ORDER.slice(); },
    glyph: function (kind) { return _fixGlyph(kind).svg; },
    glyphVb: function (kind) { return _fixGlyph(kind).vb || '24 24'; },
    def: _fixDef, dims: _fixDims, size: _fixWD, height: _fixHt, rect: _fixRect, at: _fixtureAt, hooks: _fixSetHooks, on: _fixOn,
    add: fixtureAdd, moveTo: fixtureMoveTo, rotate: fixtureRotate, mirror: fixtureMirror, remove: fixtureRemove,
    beginDrag: fixtureBeginDrag, dragTo: fixtureDragTo, endDrag: fixtureEndDrag,
    /* the placer (v89): answer, commit, and what it reads */
    place: _fitPlace, commit: _fitCommit, zones: _fitZones, roomsInfo: _fitRoomsInfo,
    test: function () { return _fitRunTest().lines; }
  };

  var _voidHook = null;
  function _setVoids(fn) { _voidHook = (typeof fn === 'function') ? fn : null; }
  var _nameBoxes = [];
  function roomNameHit(x, z) {
    var i;
    for (i = 0; i < _nameBoxes.length; i++) {
      var b = _nameBoxes[i];
      if (x >= b.x0 && x <= b.x1 && z >= b.z0 && z <= b.z1) return true;
    }
    return false;
  }
  var PLAN = {
    /* four kinds of answer: the geometry, the contents, the sizes, the units */
    init: function (host) { _PH = host || null; },
    host: function () { return _PH; },
    drawFloor: drawFloor,
    /* the rooms, for a product that wants to ask rather than work it out */
    rooms: _roomsTracked,
    roomName: _roomName,
    roomNameIdx: _roomNameIdx,
    voids: _setVoids,
    flights: _setFlights,   /* v210 */
    cycleRoomNameAt: cycleRoomNameAt,
    /* the partitions */
    MIN_ROOM: MIN_ROOM,
    partSpan: _partSpan,
    partSegs: _partSegs,
    partAt: _partAt,
    partId: _partId,
    cutPartSeg: cutPartSeg,
    /* the room line (v105) */
    toggleSep: toggleSep,
    isSep: function (p) { return !!(p && p.sep); },
    movePartTo: movePartTo,
    footObstacles: _footObstacles,
    /* the doors */
    DOOR_W: DOOR_W, DOOR_H: DOOR_H,
    doors: _doors,
    doorsLive: _doorsLive,
    doorGaps: _doorGaps,
    doorAt: _doorAt,
    doorSeg: _doorSeg,
    doorFree: _doorFree,
    stepDoor: stepDoor,
    addDoorAt: addDoorAt,
    doorTapAt: doorTapAt,
    moveDoorTo: moveDoorTo,
    pushDoors: _pushDoors,
    /* the doorway with no leaf, and the ends it is set by */
    PASS_MIN: PASS_MIN,
    isPass: function (d) { return !!(d && d.pass); },
    /* the closet doors (v106) */
    closetDoors: CLOSET_DOORS,
    closetKindWidths: _closetKindWidths,
    doorClosetSide: _doorClosetSide,
    closetSwing: _closetSwing,
    doorEndAt: doorEndAt,
    resizeDoorTo: resizeDoorTo,
    /* the fixtures: catalogue, drawing and the verbs the pointer calls */
    fixtures: FIXTURES,
    fixtureAt: _fixtureAt,
    roomNameHit: roomNameHit,
    /* the inner faces of the room under a point -- the box the services and
       the fitouts work inside */
    roomFaces: _roomFaces,
    /* the switch, and the buttons it governs */
    handles: HANDLES,
    icons: planIcons,
    /* the plan import (v94): straighten a plan read off a picture, show it */
    /* v125: the project's slanted walls, for the product that builds them */
    diags: _impDiags,
    /* v129: the corners past slanted walls and whose floor each is */
    wedges: function () { var H9 = _PH; try { return _impWedges(H9 ? H9.rooms() : []); } catch (eW9) { return []; } },
    /* v143: each room's own floor, the walls out, and their sum as written --
       the figure over the plan */
    netAreas: function () { try { return _impNetAreasNow(); } catch (eNA) { return null; } },
    importer: IMPORTER,
    /* the canvas and the mapping */
    overlay: OVERLAY
  };

  /* THE VERSION, bumped on every change; it lives here so a delivery from
     the zone banner down carries it */
  VERSION = 'floorplan-lib-v213-own';

  global.FloorplanLib = {
    VERSION: VERSION,
    draw: DRAW,
    /* ONE DOOR, WITH A NAME. Its own opener is _onIconClick, which is fine for
       the icon that calls it but is not something another product should have
       to know -- guessing at private names is exactly what cost us three
       rounds. underlay.open() is the contract; everything behind it is the
       object's own business (Marek). */
    /* the tracing image: state, drawing and hit-testing */
    trace: TRACE,
    /* and the look of the button that drives it, shared by every product */
    ui: UI,
    plan: PLAN,
    underlay: {
      obj:        _Floorplan,
      open:       function () { try { _Floorplan._onIconClick(); } catch (e) { _ulLog('open: ' + (e && e.message)); } },
      close:      function () { try { _Floorplan._hidePanel(); } catch (e) {} },
      pickFile:   function () { try { _Floorplan._pickFile(); } catch (e) {} },
      remove:     function () { try { _Floorplan.remove(); } catch (e) {} },
      setOpacity: _setOverlayOpacity,
      setRotation: _setOverlayRotation,
      nudgeRotation: _nudgeOverlayRotation
    },
    init:    init,
    open:    open,
    close:   close,
    apply:   _applyFloorplan,
    remove:  _removeFloorplan,
    undo:    _undo,
    setOpacity: _setOverlayOpacity,
    setRotation: _setOverlayRotation,
    getRotation: _getOverlayRotation,
    nudgeRotation: _nudgeOverlayRotation,
    getSelected: function () { return _selectedHandle; },
    /* the shared fitout catalogue */
    fitoutCategories: fitoutCategories,
    fitouts: fitoutsFor,
    fitoutItem: fitoutItem,
    fitoutFind: fitoutFind,
    /* Internal -- invoked by BaseEngine animation loop. */
    _onFrame: _onFrame
  };

})(window);